// Procedurally drawn sprites. There is no art pipeline: every tile, item and
// creature is painted with canvas primitives into an offscreen cache the
// first time it is needed, so the game ships with zero image assets.

import { Tile, TILE_COUNT } from "./types";
import { partyTreeFlat } from "./partytree";
import { shipFlat } from "./ship";
import { keptContext } from "./paper";

export const TILE = 32;
export const VARIANTS = 4;

/**
 * Which part of a drawbridge's span a tile is. A bridge is three tiles in
 * a row and has to read as one, so the variant — which every other tile
 * uses for a sprinkle of variety — carries the part instead: the renderer
 * works it out from the tiles around it (see variantAt) and the two outer
 * ones are the only ones that draw an end.
 *
 * The first four are a span laid east to west, which is what the gates
 * north and south of the keep need. Add SPAN_TURNED to any of them for the
 * same part of a span laid north to south, which is what the east gate's
 * bridge is: the picture is drawn once and turned a quarter turn, so the
 * two can never drift apart.
 */
export const SPAN_ONE = 0;
export const SPAN_WEST = 1;
export const SPAN_MID = 2;
export const SPAN_EAST = 3;
export const SPAN_TURNED = 4;
/** How many span parts there are: the four above, each way about. */
export const SPANS = 8;

type Ctx = CanvasRenderingContext2D;

function rng(seed: number): () => number {
  let s = seed >>> 0 || 1;
  return () => {
    s ^= s << 13;
    s ^= s >>> 17;
    s ^= s << 5;
    return ((s >>> 0) % 10000) / 10000;
  };
}

function canvas(w: number, h: number): [HTMLCanvasElement, Ctx] {
  const c = document.createElement("canvas");
  c.width = w;
  c.height = h;
  const ctx = keptContext(c);
  ctx.imageSmoothingEnabled = false;
  return [c, ctx];
}

function speckle(ctx: Ctx, r: () => number, n: number, colors: string[], size = 2) {
  for (let i = 0; i < n; i++) {
    ctx.fillStyle = colors[Math.floor(r() * colors.length)];
    ctx.fillRect(Math.floor(r() * TILE), Math.floor(r() * TILE), size, size);
  }
}

/*
 * MOUNTAINS AS ONE MASS. Asked for on 2026-10-26: "Fix the tiled mountains
 * so they look like filled in walls with rough edges". Every tile of rock
 * was the same little peak, and a range was a grid of them. Now the rock is
 * one picture ROCK_SPAN tiles each way that wraps round on itself, and a
 * tile of rock is the cut of it where the tile lies (rockCell), so the
 * rock runs on across its tiles' edges and only repeats every ROCK_SPAN
 * tiles, where the picture meets itself without a seam.
 */
/** How many tiles across the rock's one picture is, each way. */
export const ROCK_SPAN = 4;
/** Which cut of the rock's picture the tile (x, y) is: the variant rock is drawn at. */
export function rockCell(x: number, y: number): number {
  return (x & (ROCK_SPAN - 1)) + ROCK_SPAN * (y & (ROCK_SPAN - 1));
}
let rockPicture: HTMLCanvasElement | undefined;
/**
 * The rock's picture, made once: slabs and shards of grey stone split by
 * dark cracks, each slab a plane of its own tipped some way to the light,
 * with a grain over the lot. The slabs are the cells round a scatter of
 * points, measured round the picture's edges, so a slab cut by an edge
 * carries on from the other side; the grain is waves that fit the picture
 * a whole number of times. Both wrap, so the picture meets itself without
 * a seam.
 */
export function rockSheet(): HTMLCanvasElement {
  if (rockPicture) return rockPicture;
  const N = TILE * ROCK_SPAN;
  const r = rng(5150);
  const SLABS = 30;
  const px: number[] = [], py: number[] = [], lit: number[] = [], gx: number[] = [], gy: number[] = [];
  // No two points too close together, or the slab between them is a pit.
  const wrap = (d: number) => Math.min(Math.abs(d) % N, N - (Math.abs(d) % N));
  for (let i = 0; i < SLABS; i++) {
    let x = 0, y = 0;
    do {
      x = r() * N;
      y = r() * N;
    } while (px.some((qx, j) => Math.hypot(wrap(x - qx), wrap(y - py[j])) < 12));
    px.push(x);
    py.push(y);
    lit.push((r() - 0.5) * 22);
    gx.push((r() - 0.5) * 1.6);
    gy.push((r() - 0.5) * 1.6);
  }
  const waves: [number, number, number, number][] = [];
  while (waves.length < 30) {
    const kx = Math.floor(r() * 13) - 6, ky = Math.floor(r() * 13) - 6;
    if (kx || ky) waves.push([kx, ky, 1 / Math.hypot(kx, ky), r() * Math.PI * 2]);
  }
  const grain = new Float32Array(N * N);
  let most = 0;
  for (let y = 0; y < N; y++) {
    for (let x = 0; x < N; x++) {
      let v = 0;
      for (const [kx, ky, a, phase] of waves) v += a * Math.cos((2 * Math.PI * (kx * x + ky * y)) / N + phase);
      grain[y * N + x] = v;
      most = Math.max(most, Math.abs(v));
    }
  }
  const [cv, ctx] = canvas(N, N);
  const img = ctx.createImageData(N, N);
  for (let y = 0; y < N; y++) {
    for (let x = 0; x < N; x++) {
      // The nearest point and the next nearest, by a measure a little
      // squarer than a circle's, which gives the slabs straighter sides.
      let d1 = Infinity, d2 = Infinity, c = 0;
      for (let i = 0; i < SLABS; i++) {
        const dx = wrap(x - px[i]), dy = wrap(y - py[i]);
        const d = Math.max(dx * 0.8, dy) + 0.5 * Math.min(dx, dy);
        if (d < d1) [d2, d1, c] = [d1, d, i];
        else if (d < d2) d2 = d;
      }
      const ox = ((x - px[c] + N * 1.5) % N) - N / 2, oy = ((y - py[c] + N * 1.5) % N) - N / 2;
      let k = Math.max(-22, Math.min(22, lit[c] + gx[c] * ox + gy[c] * oy));
      if (d2 - d1 < 1) k -= 26; // the crack between two slabs
      k += (grain[y * N + x] / most) * 14 + (r() - 0.5) * 10;
      const i = (y * N + x) * 4;
      img.data[i] = 102 + k;
      img.data[i + 1] = 97 + k;
      img.data[i + 2] = 90 + k;
      img.data[i + 3] = 255;
    }
  }
  ctx.putImageData(img, 0, 0);
  return (rockPicture = cv);
}

/**
 * The worn rock of a slope over the part of ctx's tile from (x, y), w by
 * h, climbing north: the mountain's own rock (see rockSheet), its cracks
 * worn down, darker at the foot and lighter at the head, or going down to
 * the south for the head of a slope (`down`), with scuffs running up and
 * down it where feet have gone.
 */
function wornRock(ctx: Ctx, x: number, y: number, w: number, h: number, down = false) {
  ctx.drawImage(rockSheet(), x, y, w, h, x, y, w, h);
  ctx.fillStyle = "rgba(150,142,130,0.45)";
  ctx.fillRect(x, y, w, h);
  const g = ctx.createLinearGradient(0, down ? y : y + h, 0, down ? y + h : y);
  g.addColorStop(0, "rgba(0,0,0,0.28)");
  g.addColorStop(1, "rgba(255,250,240,0.12)");
  ctx.fillStyle = g;
  ctx.fillRect(x, y, w, h);
  const r = rng(4243);
  ctx.fillStyle = "rgba(40,36,32,0.25)";
  for (let sx = x + 3; sx < x + w - 2; sx += 4) {
    const top = y + Math.floor(r() * (h / 2));
    ctx.fillRect(Math.floor(sx + r() * 2), top, 1, Math.min(y + h - top, 8 + Math.floor(r() * 8)));
  }
}

function grassBase(ctx: Ctx, r: () => number) {
  ctx.fillStyle = "#3d7a2b";
  ctx.fillRect(0, 0, TILE, TILE);
  speckle(ctx, r, 40, ["#478a33", "#356b24", "#4f9438", "#2f6120"]);
  // A few blades.
  ctx.strokeStyle = "#5aa040";
  for (let i = 0; i < 6; i++) {
    const x = r() * TILE, y = r() * TILE;
    ctx.beginPath();
    ctx.moveTo(x, y + 3);
    ctx.lineTo(x + (r() - 0.5) * 3, y - 2);
    ctx.stroke();
  }
}

/**
 * Draw a tile that has a way round — a ramp or a slope, or the head of one —
 * turned to face the way it faces. The drawing is made the one way round
 * (the foot climbing north, the head going down to the south) and turned
 * about the tile's middle by a quarter turn for each step of dir, which is
 * the direction index north, east, south, west (see rampDir in render.ts).
 */
function facing(ctx: Ctx, dir: number, draw: () => void) {
  ctx.save();
  ctx.translate(TILE / 2, TILE / 2);
  ctx.rotate(((dir & 3) * Math.PI) / 2);
  ctx.translate(-TILE / 2, -TILE / 2);
  draw();
  ctx.restore();
}

/** The black drop of a hole: the ragged edge of the pit, inside its heaped lip. */
const HOLE_LIP: [number, number][] = [
  [6, 12], [11, 8], [17, 9], [23, 7], [28, 12], [26, 17],
  [28, 23], [22, 27], [15, 25], [9, 27], [4, 21], [7, 16],
];

function drawTile(ctx: Ctx, t: Tile, variant: number, frame: number, under?: Tile) {
  const r = rng(t * 977 + variant * 131 + 17);
  switch (t) {
    case Tile.Grass:
      grassBase(ctx, r);
      break;
    case Tile.Dirt:
      ctx.fillStyle = "#7a5a3a";
      ctx.fillRect(0, 0, TILE, TILE);
      speckle(ctx, r, 35, ["#8a6a48", "#6a4c2f", "#93744f", "#5e4227"]);
      break;
    case Tile.Sand:
      ctx.fillStyle = "#d6c07a";
      ctx.fillRect(0, 0, TILE, TILE);
      speckle(ctx, r, 30, ["#e2cd8c", "#c9b06a", "#d9c47f"], 1);
      break;
    case Tile.Stone:
      ctx.fillStyle = "#8c8c8c";
      ctx.fillRect(0, 0, TILE, TILE);
      ctx.strokeStyle = "#6d6d6d";
      ctx.strokeRect(0.5, 0.5, TILE - 1, TILE - 1);
      speckle(ctx, r, 20, ["#979797", "#808080"], 2);
      break;
    case Tile.Temple:
      ctx.fillStyle = "#cbbd94";
      ctx.fillRect(0, 0, TILE, TILE);
      ctx.strokeStyle = "#a89a72";
      ctx.strokeRect(0.5, 0.5, TILE - 1, TILE - 1);
      ctx.strokeStyle = "#dccfa8";
      ctx.beginPath();
      ctx.moveTo(4, 28);
      ctx.lineTo(28, 4);
      ctx.stroke();
      break;
    case Tile.Cobble:
      ctx.fillStyle = "#6a655d";
      ctx.fillRect(0, 0, TILE, TILE);
      for (let i = 0; i < 9; i++) {
        ctx.fillStyle = ["#7d776e", "#736d64", "#857f76"][Math.floor(r() * 3)];
        ctx.beginPath();
        ctx.ellipse(4 + (i % 3) * 11 + r() * 3, 4 + Math.floor(i / 3) * 11 + r() * 3, 4.5, 3.5, 0, 0, Math.PI * 2);
        ctx.fill();
      }
      break;
    case Tile.Water: {
      ctx.fillStyle = "#2b5ea8";
      ctx.fillRect(0, 0, TILE, TILE);
      speckle(ctx, r, 20, ["#2f68b8", "#26559a"], 3);
      ctx.strokeStyle = "#6fa0e0";
      ctx.lineWidth = 1;
      for (let i = 0; i < 3; i++) {
        const y = 6 + i * 10 + (frame ? 3 : 0);
        const x = r() * 12;
        ctx.beginPath();
        ctx.moveTo(x, y);
        ctx.quadraticCurveTo(x + 4, y - 2, x + 8, y);
        ctx.quadraticCurveTo(x + 12, y + 2, x + 16, y);
        ctx.stroke();
      }
      break;
    }
    case Tile.Bridge: {
      // The drawbridge, down: three tiles of it in a row over the moat,
      // which have to read as one span rather than as three little
      // bridges. Each is told which part of the span it is — the variant
      // carries it, see SPAN_* — and only the two outer ones draw an end.
      // Everything else runs clean off the tile edge and meets its
      // neighbour: planks, seams, kerbs and all.
      //
      // The keep has two spans laid east to west, in front of the north
      // and south gates, and one laid north to south in front of the east
      // gate. The timber is drawn once, for a span lying east to west, and
      // the other one is the same picture turned a quarter turn: a bridge
      // is a bridge whichever way the water runs under it, and two copies
      // of this that had to be kept looking alike would not stay alike.
      const part = variant % SPANS;
      if (part >= SPAN_TURNED) {
        ctx.save();
        ctx.translate(TILE, 0);
        ctx.rotate(Math.PI / 2);
        drawTile(ctx, t, part - SPAN_TURNED, frame, under);
        ctx.restore();
        break;
      }
      const west = part === SPAN_WEST || part === SPAN_ONE;
      const east = part === SPAN_EAST || part === SPAN_ONE;
      // The moat shows past the ends of the span, and nowhere else: the
      // north and south edges are where the road meets it.
      ctx.fillStyle = "#2b5ea8";
      ctx.fillRect(0, 0, TILE, TILE);
      if (west || east) {
        ctx.strokeStyle = "#6fa0e0";
        ctx.lineWidth = 1;
        for (const x of west ? [1] : [TILE - 2]) {
          ctx.beginPath();
          ctx.moveTo(x, r() * 6);
          ctx.quadraticCurveTo(x + 2, 10, x, 18);
          ctx.quadraticCurveTo(x - 2, 26, x, TILE);
          ctx.stroke();
        }
      }
      // Planks: laid across the way the road runs, so they read as boards
      // underfoot rather than as rails. They span the whole tile from north
      // to south and are cut only where the span ends.
      const x0 = west ? 3 : 0;
      const x1 = east ? TILE - 3 : TILE;
      for (let i = 0; i < 5; i++) {
        const y = 1 + i * 6;
        ctx.fillStyle = ["#8a6234", "#7d5730", "#946b3b"][Math.floor(r() * 3)];
        ctx.fillRect(x0, y, x1 - x0, 5);
        ctx.fillStyle = "rgba(60,36,16,0.55)";
        ctx.fillRect(x0, y + 5, x1 - x0, 1);
        // A knot or two, so the timber is not flat colour.
        if (r() > 0.6) {
          ctx.fillStyle = "#6a4526";
          ctx.fillRect(x0 + Math.floor(r() * Math.max(1, x1 - x0 - 4)) + 1, y + 1, 2, 2);
        }
      }
      // The kerbs run the length of the span, north and south, and are
      // what makes three tiles one bridge: they never stop at a seam.
      ctx.fillStyle = "#5b5550";
      ctx.fillRect(x0, 0, x1 - x0, 2);
      ctx.fillRect(x0, TILE - 2, x1 - x0, 2);
      // The ends: iron banding across the span's outer edges, with the
      // chains that raise it anchored at the corners.
      ctx.fillStyle = "#5b5550";
      if (west) ctx.fillRect(0, 0, 3, TILE);
      if (east) ctx.fillRect(TILE - 3, 0, 3, TILE);
      ctx.fillStyle = "#7c746c";
      if (west) {
        ctx.fillRect(1, 5, 1, 2);
        ctx.fillRect(1, TILE - 7, 1, 2);
      }
      if (east) {
        ctx.fillRect(TILE - 2, 5, 1, 2);
        ctx.fillRect(TILE - 2, TILE - 7, 1, 2);
      }
      break;
    }
    case Tile.Hilltop: {
      // The crown of a hill or a mountain's shelf: bare rock, scoured of
      // everything the country below still manages to grow. Grey stone
      // with a dry crack or two and the odd tuft of dead scrub clinging on.
      ctx.fillStyle = "#6b6560";
      ctx.fillRect(0, 0, TILE, TILE);
      speckle(ctx, r, 26, ["#7b746e", "#5c5651", "#847c75"], 3);
      ctx.strokeStyle = "#4a4541";
      ctx.lineWidth = 1;
      for (let i = 0; i < 2; i++) {
        const x = 4 + r() * 20;
        const y = 3 + i * 14;
        ctx.beginPath();
        ctx.moveTo(x, y);
        ctx.lineTo(x + 5 - r() * 3, y + 6);
        ctx.lineTo(x + 2 + r() * 6, y + 12);
        ctx.stroke();
      }
      // Dead scrub: a few brittle stalks, the only thing rooted this high.
      ctx.strokeStyle = "#6e6338";
      for (let i = 0; i < 3; i++) {
        const x = 3 + r() * 26;
        const y = 22 + r() * 8;
        ctx.beginPath();
        ctx.moveTo(x, y);
        ctx.lineTo(x + 2 - r() * 4, y - 4 - r() * 3);
        ctx.stroke();
      }
      break;
    }
    case Tile.Wall:
      ctx.fillStyle = "#4a4442";
      ctx.fillRect(0, 0, TILE, TILE);
      for (let row = 0; row < 4; row++) {
        const off = row % 2 ? 8 : 0;
        for (let col = -1; col < 3; col++) {
          ctx.fillStyle = ["#7a726e", "#6f6764", "#847b76"][Math.floor(r() * 3)];
          ctx.fillRect(col * 16 + off + 1, row * 8 + 1, 14, 6);
        }
      }
      // Top highlight to suggest height.
      ctx.fillStyle = "#9a918c";
      ctx.fillRect(0, 0, TILE, 2);
      break;
    case Tile.Palisade: {
      // The orc camp's wall: split logs driven upright, shoulder to
      // shoulder, each sharpened to a point. Read as a stockade rather
      // than a fence — the gaps are dark, so a run of it reads as solid
      // from across the field, which is what it is.
      ctx.fillStyle = "#241a12"; // the dark between the logs
      ctx.fillRect(0, 0, TILE, TILE);
      const logs = [
        ["#6b4a2a", "#7d5832"],
        ["#5d4024", "#6f4e2c"],
        ["#74512e", "#866036"],
      ];
      for (let i = 0; i < 5; i++) {
        const x = i * 6 + 1;
        const [body, lit] = logs[Math.floor(r() * logs.length)];
        ctx.fillStyle = body;
        ctx.fillRect(x, 0, 5, TILE);
        ctx.fillStyle = lit; // the round of the log, catching the light
        ctx.fillRect(x + 1, 0, 2, TILE);
        // The sharpened head: a notch out of each side of the top.
        ctx.fillStyle = "#241a12";
        ctx.fillRect(x, 0, 1, 3);
        ctx.fillRect(x + 4, 0, 1, 3);
        ctx.fillStyle = "#9a7448"; // the pale cut of the point
        ctx.fillRect(x + 1, 1, 3, 2);
        // A binding lashed across, a third of the way down.
        ctx.fillStyle = "#3e2d1c";
        ctx.fillRect(x, 11 + Math.floor(r() * 2), 5, 2);
      }
      break;
    }
    case Tile.Rampart:
    case Tile.Tower: {
      // The walk along the top of the palisade, seen from above: a plank
      // floor with a breastwork of logs along it. The tower is the same
      // thing built wider, so it is the same drawing in a lighter timber —
      // a corner should read as a corner without reading as a different
      // material.
      const tower = t === Tile.Tower;
      ctx.fillStyle = tower ? "#8a6a45" : "#78583a";
      ctx.fillRect(0, 0, TILE, TILE);
      // Planks running across, with the joints between them dark.
      for (let i = 0; i < 4; i++) {
        ctx.fillStyle = tower
          ? ["#9c7a52", "#8f6f4a", "#a8855c"][Math.floor(r() * 3)]
          : ["#82603f", "#775839", "#8d6a46"][Math.floor(r() * 3)];
        ctx.fillRect(0, i * 8 + 1, TILE, 6);
      }
      ctx.fillStyle = "#4a3520"; // the joints, dark between the planks
      for (let i = 0; i < 4; i++) ctx.fillRect(0, i * 8, TILE, 1);
      // The breastwork: a log rail along the outer edge, and the pegs that
      // hold it. On a tower it runs right round, which is what makes the
      // corner read as a platform rather than a wider piece of wall.
      ctx.fillStyle = "#5a4028";
      ctx.fillRect(0, 0, TILE, 4);
      if (tower) {
        ctx.fillRect(0, TILE - 4, TILE, 4);
        ctx.fillRect(0, 0, 4, TILE);
        ctx.fillRect(TILE - 4, 0, 4, TILE);
      }
      ctx.fillStyle = "#6e5033";
      for (let i = 0; i < 4; i++) ctx.fillRect(i * 8 + 2, 1, 4, 2);
      break;
    }
    case Tile.Tree: {
      grassBase(ctx, r);
      ctx.fillStyle = "#5a3a1e";
      ctx.fillRect(13, 18, 6, 12);
      ctx.fillStyle = "#1f5a1f";
      ctx.beginPath();
      ctx.arc(16, 13, 12, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = "#2d7a2d";
      for (let i = 0; i < 5; i++) {
        ctx.beginPath();
        ctx.arc(9 + r() * 14, 6 + r() * 12, 4, 0, Math.PI * 2);
        ctx.fill();
      }
      break;
    }
    case Tile.Flowers:
      grassBase(ctx, r);
      for (let i = 0; i < 5; i++) {
        ctx.fillStyle = ["#e8e050", "#e05070", "#f0f0f0", "#a070e0"][Math.floor(r() * 4)];
        const x = 3 + r() * 26, y = 3 + r() * 26;
        ctx.fillRect(x - 1, y - 1, 3, 3);
      }
      break;
    case Tile.Rock:
      // One cut of the mountain's single picture (see rockSheet), so a
      // range is one mass of rock with no seam at its tiles' edges.
      ctx.drawImage(rockSheet(), -(variant % ROCK_SPAN) * TILE, -Math.floor(variant / ROCK_SPAN) * TILE);
      break;
    case Tile.Stair: {
      // Stone steps climbing north: each tread lighter than the riser
      // under it, so they read as rising away from the door.
      ctx.fillStyle = "#6d6d6d";
      ctx.fillRect(0, 0, TILE, TILE);
      for (let i = 0; i < 4; i++) {
        const y = 24 - i * 8;
        ctx.fillStyle = ["#9a9a9a", "#a4a4a4", "#aeaeae", "#b8b8b8"][i];
        ctx.fillRect(2, y, TILE - 4, 6); // tread
        ctx.fillStyle = "#555";
        ctx.fillRect(2, y + 6, TILE - 4, 2); // riser shadow
      }
      ctx.fillStyle = "#4a4a4a";
      ctx.fillRect(0, 0, 2, TILE); // low walls either side
      ctx.fillRect(TILE - 2, 0, 2, TILE);
      break;
    }
    case Tile.Ramp: {
      // A goat track up the rock: the mountain's own grey either side, and
      // a worn earthen slope between with steps kicked into it. No dressed
      // stone and no walls, which is what tells it from the stairs.
      //
      // It is drawn climbing north and turned to climb whichever way it
      // really does: the variant is the direction uphill (see rampDir and
      // variantAt in render.ts). Until 2026-09-27 nothing in it pointed any
      // way at all, and a ramp up an east or west face drew its steps
      // running across the climb instead of up it ("fix stairs so they
      // render properly when a west/east ramp").
      facing(ctx, variant, () => {
        ctx.fillStyle = "#6f6a64";
        ctx.fillRect(0, 0, TILE, TILE);
        ctx.fillStyle = "#957551";
        ctx.fillRect(5, 0, TILE - 10, TILE);
        speckle(ctx, r, 8, ["#5d5853", "#817b74"], 2);
        // Each step a lighter tread with the lip of the next one's shadow
        // under it, lightening as it climbs, so which end is the top reads
        // at a glance whichever way round it lies.
        for (let i = 0; i < 4; i++) {
          const y = 24 - i * 8;
          ctx.fillStyle = ["#8a6c4b", "#957551", "#a07f5a", "#ab8963"][i];
          ctx.fillRect(5, y, TILE - 10, 6);
          ctx.fillStyle = "rgba(0,0,0,0.3)";
          ctx.fillRect(5, y + 6, TILE - 10, 2); // the lip of each step
        }
      });
      break;
    }
    case Tile.RampTop: {
      // The head of the track, seen from the top: hilltop stone with the
      // worn earth of the path running off its edge, drawn going down to
      // the south and turned to go down whichever way it really does.
      facing(ctx, variant, () => {
        ctx.fillStyle = "#7d7466";
        ctx.fillRect(0, 0, TILE, TILE);
        speckle(ctx, r, 6, ["#6d6558", "#8a8172"], 2);
        ctx.fillStyle = "#a0805e";
        ctx.fillRect(5, 8, TILE - 10, TILE - 8);
        ctx.fillStyle = "rgba(0,0,0,0.25)";
        ctx.fillRect(5, 22, TILE - 10, 2); // where the first step goes down
      });
      break;
    }
    case Tile.Slope: {
      // A ramp with no steps: bare rock worn smooth running up between
      // shoulders of rougher rock, darker at its foot and lighter at its
      // head. Asked for on 2026-09-27: "add actual ramps which don't have
      // steps, both north/south and west/east varieties, use those where
      // stairs aren't appropriate"; turf until 2026-10-26: "Ramps with
      // grass should be rock". Drawn climbing north and turned, like the
      // ramp.
      facing(ctx, variant, () => {
        ctx.fillStyle = "#6f6a64";
        ctx.fillRect(0, 0, TILE, TILE);
        speckle(ctx, r, 8, ["#5d5853", "#817b74"], 2);
        wornRock(ctx, 4, 0, TILE - 8, TILE);
        // The shoulders: a soft shadow down each side where the worn rock
        // meets the rough.
        ctx.fillStyle = "rgba(0,0,0,0.25)";
        ctx.fillRect(4, 0, 2, TILE);
        ctx.fillRect(TILE - 6, 0, 2, TILE);
      });
      break;
    }
    case Tile.SlopeTop: {
      // The head of a slope: hilltop stone, and the worn rock of the
      // incline running off its edge, going down to the south and turned like the
      // ramp's head.
      facing(ctx, variant, () => {
        ctx.fillStyle = "#7d7466";
        ctx.fillRect(0, 0, TILE, TILE);
        speckle(ctx, r, 6, ["#6d6558", "#8a8172"], 2);
        wornRock(ctx, 4, 10, TILE - 8, TILE - 10, true);
        ctx.fillStyle = "rgba(0,0,0,0.2)";
        ctx.fillRect(4, 10, TILE - 8, 1); // the brow
      });
      break;
    }
    case Tile.Roof: {
      // Slates: dark rows offset by half a slate, with a faint sheen.
      ctx.fillStyle = "#4e4a55";
      ctx.fillRect(0, 0, TILE, TILE);
      ctx.strokeStyle = "#3a3740";
      ctx.lineWidth = 1;
      for (let row = 0; row < 4; row++) {
        const y = row * 8 + 0.5;
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(TILE, y);
        ctx.stroke();
        for (let col = 0; col < 4; col++) {
          const x = col * 8 + (row & 1 ? 4 : 0) + 0.5;
          ctx.beginPath();
          ctx.moveTo(x, y);
          ctx.lineTo(x, y + 8);
          ctx.stroke();
        }
      }
      speckle(ctx, r, 10, ["#5a5662", "#605c68"], 2);
      break;
    }
    case Tile.StairTop: {
      // The head of the stairs, seen from above: the top treads and a dark
      // drop where they descend to the south.
      ctx.fillStyle = "#4e4a55";
      ctx.fillRect(0, 0, TILE, TILE);
      ctx.fillStyle = "#2a2830";
      ctx.fillRect(2, 8, TILE - 4, TILE - 8); // the stairwell opening
      for (let i = 0; i < 3; i++) {
        const y = 8 + i * 8;
        ctx.fillStyle = ["#b8b8b8", "#9a9a9a", "#7a7a7a"][i];
        ctx.fillRect(4, y, TILE - 8, 5); // treads sinking into the dark
        ctx.fillStyle = "#3a3a3a";
        ctx.fillRect(4, y + 5, TILE - 8, 2);
      }
      ctx.fillStyle = "#6a6672";
      ctx.fillRect(0, 0, TILE, 6); // the landing you step off
      break;
    }
    case Tile.Depot: {
      // Wooden floorboards, so the depot reads as its own room.
      ctx.fillStyle = "#7a5c38";
      ctx.fillRect(0, 0, TILE, TILE);
      ctx.strokeStyle = "#5e4527";
      ctx.lineWidth = 1;
      for (let i = 0; i < 4; i++) {
        const y = i * 8 + 0.5;
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(TILE, y);
        ctx.stroke();
        ctx.beginPath();
        const x = ((i * 13) % TILE) + 0.5;
        ctx.moveTo(x, y);
        ctx.lineTo(x, y + 8);
        ctx.stroke();
      }
      speckle(ctx, r, 8, ["#8a6a44", "#6e522f"], 2);
      break;
    }
    case Tile.Hole: {
      // A ragged opening torn in the dirt, the way Tibia digs them: bare
      // earth thrown up around a jagged black drop, the far wall just
      // catching the light under the near lip.
      ctx.fillStyle = "#7a5a3a";
      ctx.fillRect(0, 0, TILE, TILE);
      speckle(ctx, r, 20, ["#8a6a48", "#6a4c2f", "#5e4227"]);
      const lip = HOLE_LIP;
      // Loose soil heaped around the rim, wider than the drop itself.
      ctx.fillStyle = "#5e4227";
      ctx.beginPath();
      for (const [x, y] of lip) ctx.lineTo(16 + (x - 16) * 1.3, 17 + (y - 17) * 1.3);
      ctx.closePath();
      ctx.fill();
      ctx.fillStyle = "#0d0a06";
      ctx.beginPath();
      for (const [x, y] of lip) ctx.lineTo(x, y);
      ctx.closePath();
      ctx.fill();
      ctx.fillStyle = "#3a2c1a"; // far wall of the shaft, lit from above
      ctx.beginPath();
      ctx.moveTo(8, 13);
      ctx.lineTo(11, 9);
      ctx.lineTo(17, 10);
      ctx.lineTo(23, 8);
      ctx.lineTo(27, 12);
      ctx.lineTo(24, 15);
      ctx.lineTo(16, 13);
      ctx.lineTo(10, 15);
      ctx.closePath();
      ctx.fill();
      ctx.fillStyle = "#93744f"; // fresh dirt crumbs on the near lip
      for (const [x, y] of [[5, 23], [10, 28], [17, 27], [23, 28], [28, 25], [3, 17]]) {
        ctx.fillRect(x, y, 2, 2);
      }
      break;
    }
    case Tile.Ladder: {
      // A ladder going up, seen from above: it stands on the floor it is
      // on, so the floor is drawn under it. Which floor that is, the tile
      // itself cannot say — a ladder tile replaces the ground it was cut
      // into — so the renderer works it out from the neighbours and hands
      // it in as `under`. It used to be drawn on a black square, which
      // said the opposite of what a ladder going up says: that there was
      // a hole there.
      drawTile(ctx, under ?? Tile.Dirt, variant, frame);
      drawLadder(ctx);
      break;
    }
    case Tile.LadderTop: {
      // The head of that ladder, a storey up: the way back down. Here the
      // dark is the point — what is under the hatch is the shaft — so the
      // rungs are drawn against black, and a rim of boards round it says
      // the floor has been cut through rather than simply ending.
      ctx.fillStyle = "#181410";
      ctx.fillRect(0, 0, TILE, TILE);
      ctx.strokeStyle = "#6f5a3e";
      ctx.lineWidth = 2;
      ctx.strokeRect(1, 1, TILE - 2, TILE - 2);
      drawLadder(ctx);
      break;
    }
    case Tile.Forge: {
      // The forge yard's ground: cinder trodden into soot-stained flag,
      // scorched by years of sparks coming off the anvils standing on it.
      //
      // Everything here is chosen against one failure. The yard used to
      // borrow the depot's boards, and boards are drawn as a joint every
      // eight pixels with a stagger — which is running-bond stretcher, the
      // same bond the keep's walls use. Five by five of it out in the open
      // read as a brick wall the camera had fallen over, and the yard read
      // as part of the castle it had just been moved ten tiles away from.
      //
      // So this tile has no course and no bond. It is a dark base with
      // wear scattered over it by the tile's own seed, which means the
      // structure a player's eye can find in it is the scatter rather than
      // a grid — and scatter does not line up across a tile boundary, so
      // twenty-five of these read as one dirty yard rather than as
      // twenty-five of anything. The few seams that are drawn are short,
      // unequal and off any regular line: enough to say the ground was laid
      // rather than poured, not enough to become a course.
      //
      // The palette is the anvils' own. It is the dark end of the castle's
      // greys warmed towards the coal in the fire, so the yard sits under
      // the ironwork instead of competing with it.
      ctx.fillStyle = "#4a443e";
      ctx.fillRect(0, 0, TILE, TILE);
      // Trodden cinder: grit and ash, in warm darks and one cold grey so
      // the ground reads as stone under the soot rather than as mud.
      speckle(ctx, r, 34, ["#565049", "#3e3934", "#5f5852", "#332e2a"]);
      // Scorch: a few soft dark patches where the sparks keep landing. Drawn
      // as blobs rather than marks, because a burn spreads.
      ctx.globalAlpha = 0.5;
      ctx.fillStyle = "#231f1c";
      for (let i = 0; i < 3; i++) {
        ctx.beginPath();
        ctx.ellipse(r() * TILE, r() * TILE, 3 + r() * 4, 2 + r() * 3, r() * 3, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.globalAlpha = 1;
      // Seams between the flags: three short strokes at seeded angles, each
      // well under the width of the tile so none of them can read as a
      // course line running through the yard.
      ctx.strokeStyle = "#39342f";
      ctx.lineWidth = 1;
      for (let i = 0; i < 3; i++) {
        const x = r() * TILE, y = r() * TILE, a = r() * Math.PI, len = 5 + r() * 7;
        ctx.beginPath();
        ctx.moveTo(x, y);
        ctx.lineTo(x + Math.cos(a) * len, y + Math.sin(a) * len);
        ctx.stroke();
      }
      // A couple of cinders still warm. They are the only colour on the tile
      // and there are two of them, which is what keeps the yard reading as a
      // place where iron is worked rather than as dirty paving.
      ctx.fillStyle = "#a8552a";
      for (let i = 0; i < 2; i++) ctx.fillRect(Math.floor(r() * TILE), Math.floor(r() * TILE), 1, 1);
      break;
    }
    case Tile.PitProp: {
      // A pit prop: two legs and a cap, timber holding the roof of a
      // heading up.
      //
      // The cave floor is drawn first and the timber over it, because the
      // prop is scenery standing on ground rather than a floor of its own —
      // it is walkable, and a tile that reads as furniture the player can
      // cross has to show the ground it is standing on.
      //
      // It is drawn against the top of the tile rather than centred. The
      // props are set on the flanks of the spine and a timber hard against
      // the wall reads as bracing; one in the middle of its tile reads as a
      // pillar, which is the thing this deliberately is not.
      //
      // This is the flat picture, for wherever the world is drawn flat:
      // the map, the zoomed-out view, a look at another floor. The close
      // view stands the same set up at the north side of its tile over
      // plain cave floor (2026-09-28: "render the pit prop oblique"; see
      // propFacets in oblique.ts).
      ctx.fillStyle = "#3d352c";
      ctx.fillRect(0, 0, TILE, TILE);
      speckle(ctx, r, 18, ["#463d32", "#332b23", "#4d443a", "#2a231c"]);
      // The cap across the top, and its lit edge where the lamp catches it.
      ctx.fillStyle = "#5a4326";
      ctx.fillRect(2, 2, TILE - 4, 4);
      ctx.fillStyle = "#74572f";
      ctx.fillRect(2, 2, TILE - 4, 1.5);
      // The two legs, with a shadow down the inside of each so they read as
      // round timber rather than as painted stripes.
      for (const x of [4, TILE - 9]) {
        ctx.fillStyle = "#4e3a22";
        ctx.fillRect(x, 5, 5, TILE - 7);
        ctx.fillStyle = "#684e2c";
        ctx.fillRect(x, 5, 2, TILE - 7);
        ctx.fillStyle = "#332417";
        ctx.fillRect(x + 4, 5, 1, TILE - 7);
      }
      // The saw marks and the split in the cap: old timber under load.
      ctx.fillStyle = "#2b1f13";
      ctx.fillRect(9, 3.5, TILE - 18, 1);
      break;
    }
    case Tile.Cave: {
      // Packed cavern earth, darker than any surface ground.
      ctx.fillStyle = "#3d352c";
      ctx.fillRect(0, 0, TILE, TILE);
      speckle(ctx, r, 26, ["#463d32", "#332b23", "#4d443a", "#2a231c"]);
      break;
    }
    case Tile.CaveWall: {
      // Rough rock pressing in on the dark.
      ctx.fillStyle = "#211c16";
      ctx.fillRect(0, 0, TILE, TILE);
      ctx.fillStyle = "#38302a";
      ctx.beginPath();
      ctx.moveTo(2, 28);
      ctx.lineTo(9, 10);
      ctx.lineTo(17, 20);
      ctx.lineTo(23, 6);
      ctx.lineTo(30, 26);
      ctx.closePath();
      ctx.fill();
      ctx.fillStyle = "#4a4038";
      ctx.beginPath();
      ctx.moveTo(9, 10);
      ctx.lineTo(17, 20);
      ctx.lineTo(11, 24);
      ctx.closePath();
      ctx.fill();
      break;
    }
    case Tile.Swamp:
      ctx.fillStyle = "#4a5a2a";
      ctx.fillRect(0, 0, TILE, TILE);
      speckle(ctx, r, 30, ["#3a4a20", "#566a30", "#2e3a18"], 3);
      ctx.fillStyle = "#6b7d3a";
      for (let i = 0; i < 3; i++) {
        ctx.beginPath();
        ctx.ellipse(r() * TILE, r() * TILE, 3, 1.5, 0, 0, Math.PI * 2);
        ctx.fill();
      }
      break;
    case Tile.Snow: {
      // The High Pass's shelves and crest under snow (asked for on
      // 2026-09-28: "put snow as the ground on upper levels of the pass at
      // rivendell. movement is slower"). Near-white with a cold blue-grey
      // in its hollows, a glint here and there where the light catches a
      // crust, a drift or two, and the odd stone of the hilltop under it
      // showing through, so a field of it reads as ground rather than as
      // a blank sheet.
      ctx.fillStyle = "#e4ecf2";
      ctx.fillRect(0, 0, TILE, TILE);
      speckle(ctx, r, 30, ["#d3dde6", "#c7d3de", "#eef3f7"], 3);
      speckle(ctx, r, 10, ["#ffffff"], 1);
      // Drifts: a soft shadow along the lee of each.
      ctx.strokeStyle = "#b9c7d4";
      ctx.lineWidth = 1;
      for (let i = 0; i < 2; i++) {
        const x = 3 + r() * 16;
        const y = 8 + i * 13 + r() * 4;
        ctx.beginPath();
        ctx.moveTo(x, y);
        ctx.quadraticCurveTo(x + 5, y - 3, x + 11, y);
        ctx.stroke();
      }
      // A stone of the hill under it.
      if (r() > 0.5) {
        ctx.fillStyle = "#7b746e";
        ctx.fillRect(4 + Math.floor(r() * 22), 4 + Math.floor(r() * 22), 2, 1);
      }
      break;
    }
    case Tile.FrozenWaste: {
      // Forodwaith, the frozen waste along the north edge (2026-10-08:
      // "spiky looking ice mountains that are impassable"). This is the
      // ice the mass is made of, its top and its faces (see oblique.ts):
      // pale blue, colder than the snow, heaved into shards that catch the
      // light on one side and are blue in the shadow of the other, with
      // dark cracks between them. In the oblique view spires of it stand up
      // off the top as well (see icespire.ts).
      ctx.fillStyle = "#b9d3e6";
      ctx.fillRect(0, 0, TILE, TILE);
      speckle(ctx, r, 24, ["#a7c5db", "#cfe1ee", "#9dbbd2"], 2);
      for (let i = 0; i < 4; i++) {
        const x = 2 + r() * 24, y = 10 + r() * 20, w = 4 + r() * 5, h = 7 + r() * 9;
        ctx.fillStyle = "#e9f3fa";
        ctx.beginPath();
        ctx.moveTo(x, y);
        ctx.lineTo(x + w / 2, y - h);
        ctx.lineTo(x + w / 2, y);
        ctx.closePath();
        ctx.fill();
        ctx.fillStyle = "#86a9c3";
        ctx.beginPath();
        ctx.moveTo(x + w / 2, y);
        ctx.lineTo(x + w / 2, y - h);
        ctx.lineTo(x + w, y);
        ctx.closePath();
        ctx.fill();
      }
      // Cracks in the ice.
      ctx.strokeStyle = "#5f84a0";
      ctx.lineWidth = 1;
      for (let i = 0; i < 2; i++) {
        let x = r() * TILE, y = r() * TILE;
        ctx.beginPath();
        ctx.moveTo(x, y);
        for (let j = 0; j < 3; j++) {
          x += -5 + r() * 10;
          y += 3 + r() * 5;
          ctx.lineTo(x, y);
        }
        ctx.stroke();
      }
      speckle(ctx, r, 6, ["#ffffff"], 1);
      break;
    }
    // The Shire's own ground (2026-10-01: "Remove the forge yard, but add
    // the dungeon under the mathom house", answering the Hobbiton mockup).
    case Tile.Hedge: {
      // A clipped garden hedge seen from above: a dark rounded mass with
      // lighter leaves catching the light, on the grass it grows from.
      grassBase(ctx, r);
      ctx.fillStyle = "#24561d";
      ctx.beginPath();
      ctx.roundRect(1, 3, TILE - 2, TILE - 6, 9);
      ctx.fill();
      speckle(ctx, r, 26, ["#2f6a26", "#3a7d2e", "#1d4a17"], 3);
      speckle(ctx, r, 8, ["#4f9a3a"], 2);
      break;
    }
    case Tile.Hillside:
    case Tile.HobbitDoor: {
      // Turf over a hobbit's hill: a deep green, tufted, with the odd
      // daisy. A round door is the same turf with a painted door set in
      // it, a stone surround and a brass knob in the middle; its colour
      // is the variant's, so a row of holes is not one door repeated.
      ctx.fillStyle = "#5f9c40";
      ctx.fillRect(0, 0, TILE, TILE);
      speckle(ctx, r, 24, ["#6aa84a", "#548f37", "#73b053"], 3);
      speckle(ctx, r, 3, ["#f2f2e8", "#f2d24b"], 1);
      if (t === Tile.HobbitDoor) {
        const paint = ["#2e7d32", "#b23b2e", "#2b5fa8", "#d08a2a"][variant % 4];
        ctx.fillStyle = "#d9c08a";
        ctx.beginPath();
        ctx.arc(16, 17, 12, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = paint;
        ctx.beginPath();
        ctx.arc(16, 17, 9.5, 0, Math.PI * 2);
        ctx.fill();
        ctx.strokeStyle = "rgba(0,0,0,0.25)";
        ctx.lineWidth = 1;
        for (const dx of [-5, 0, 5]) {
          ctx.beginPath();
          ctx.moveTo(16 + dx, 9);
          ctx.lineTo(16 + dx, 25);
          ctx.stroke();
        }
        ctx.fillStyle = "#f2d24b";
        ctx.beginPath();
        ctx.arc(16, 17, 2.2, 0, Math.PI * 2);
        ctx.fill();
      }
      break;
    }
    case Tile.Thatch: {
      // Straw laid in courses: a warm gold with darker rows where each
      // course overlaps the one below, and stray straws.
      ctx.fillStyle = "#c9a35a";
      ctx.fillRect(0, 0, TILE, TILE);
      ctx.strokeStyle = "#a9843f";
      ctx.lineWidth = 2;
      for (let row = 0; row < 4; row++) {
        const y = row * 8 + 7;
        ctx.beginPath();
        ctx.moveTo(0, y);
        for (let x = 0; x <= TILE; x += 8) ctx.quadraticCurveTo(x + 4, y + 2, x + 8, y);
        ctx.stroke();
      }
      speckle(ctx, r, 18, ["#d8b56c", "#b8913f"], 2);
      break;
    }
    case Tile.MillWheel: {
      // Sandyman's wheel, standing in the Water: the water under it, the
      // timber rim and its spokes, and a paddle at each end of a spoke.
      ctx.fillStyle = "#2b5ea8";
      ctx.fillRect(0, 0, TILE, TILE);
      speckle(ctx, r, 14, ["#2f68b8", "#26559a"], 3);
      ctx.strokeStyle = "#5a3f22";
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.arc(16, 16, 12, 0, Math.PI * 2);
      ctx.stroke();
      ctx.lineWidth = 2;
      for (let i = 0; i < 8; i++) {
        const a = (i / 8) * Math.PI * 2 + (frame ? 0.2 : 0);
        ctx.beginPath();
        ctx.moveTo(16, 16);
        ctx.lineTo(16 + Math.cos(a) * 14, 16 + Math.sin(a) * 14);
        ctx.stroke();
      }
      ctx.fillStyle = "#3a2814";
      ctx.beginPath();
      ctx.arc(16, 16, 3, 0, Math.PI * 2);
      ctx.fill();
      break;
    }
    case Tile.PartyTree:
      // The Party Tree from above, golden over the grass (2026-10-01:
      // "Move the respawn under the tree. Make the tree look special"):
      // see partytree.ts, which stands it up as well. It stood in temple
      // flagstones until they were taken up the same day ("Remove the
      // temple floor and shrine at the mathom house and around the foot
      // of the party tree").
      grassBase(ctx, r);
      partyTreeFlat(ctx, TILE);
      break;
    case Tile.Ship:
      // A ship at its jetty, from above, on the water (2026-10-06): see
      // ship.ts, which stands it up as well.
      drawTile(ctx, Tile.Water, variant, frame);
      shipFlat(ctx, TILE);
      break;
  }
}

const tileCache: HTMLCanvasElement[][][] = [];

/** Returns the sprite for a tile; variant chosen by position, frame for animation. */
/** The rails and rungs both ends of a ladder share. */
function drawLadder(ctx: Ctx) {
  ctx.strokeStyle = "#a97245";
  ctx.lineWidth = 3;
  ctx.beginPath();
  ctx.moveTo(10, 2);
  ctx.lineTo(10, 30);
  ctx.moveTo(22, 2);
  ctx.lineTo(22, 30);
  ctx.stroke();
  ctx.lineWidth = 2;
  ctx.strokeStyle = "#8a5a32";
  ctx.beginPath();
  for (let y = 5; y < 30; y += 6) {
    ctx.moveTo(9, y);
    ctx.lineTo(23, y);
  }
  ctx.stroke();
}

/**
 * The tile's sprite, cached. `under` is the floor a ladder is standing on:
 * it is part of the key, since the same ladder over boards and over cave
 * floor are two different pictures.
 */
export function tileSprite(t: Tile, variant: number, frame: number, under?: Tile): HTMLCanvasElement {
  const f = t === Tile.Water ? frame & 1 : 0;
  const key = t === Tile.Ladder ? TILE_COUNT + (under ?? Tile.Dirt) : t;
  // A bridge has more of them than anything else: its variant is which
  // part of a span it is rather than a sprinkle of variety, and there are
  // two ways a span can lie.
  const v = variant % (t === Tile.Bridge ? SPANS : t === Tile.Rock ? ROCK_SPAN * ROCK_SPAN : VARIANTS);
  tileCache[key] ??= [];
  tileCache[key][v] ??= [];
  let c = tileCache[key][v][f];
  if (!c) {
    const [cv, ctx] = canvas(TILE, TILE);
    drawTile(ctx, t, v, f, under);
    c = tileCache[key][v][f] = cv;
  }
  return c;
}

/**
 * The surface of a ramp or a slope as the oblique view lays it on its
 * incline (see inclineOf in oblique.ts): drawn climbing north, its top row
 * the uphill edge, and the whole tile wide. Asked for on 2026-09-28: "fix
 * ramps looking like they're inside a weird box of a similar type of the
 * neighbouring terrain. also render them oblique". The flat pictures above
 * keep their grey shoulders, which stand for the rock either side of the
 * cut where the world is drawn flat — the map, the zoomed-out view, a look
 * at another floor; on the incline the rock either side is the wedge's
 * own faces, and shoulders painted on the surface as well were the box.
 *
 * `t` is the foot, Tile.Ramp or Tile.Slope; anything else is drawn as the
 * slope. Cached per tile.
 */
const inclineCache: HTMLCanvasElement[] = [];
export function inclineSprite(t: Tile): HTMLCanvasElement {
  const steps = t === Tile.Ramp;
  const key = steps ? 0 : 1;
  let c = inclineCache[key];
  if (c) return c;
  const [cv, ctx] = canvas(TILE, TILE);
  const r = rng(t * 977 + 4242);
  if (steps) {
    // The goat track: worn earth, each step a lighter tread with the
    // shadow of its lip under it, lightening as it climbs.
    ctx.fillStyle = "#957551";
    ctx.fillRect(0, 0, TILE, TILE);
    for (let i = 0; i < 4; i++) {
      const y = 24 - i * 8;
      ctx.fillStyle = ["#8a6c4b", "#957551", "#a07f5a", "#ab8963"][i];
      ctx.fillRect(0, y, TILE, 6);
      ctx.fillStyle = "rgba(0,0,0,0.3)";
      ctx.fillRect(0, y + 6, TILE, 2); // the lip of each step
    }
    speckle(ctx, r, 10, ["#7d6044", "#b0916b"], 1);
  } else {
    // Bare rock worn smooth, darker at its foot and lighter at its head
    // (2026-10-26: "Ramps with grass should be rock").
    wornRock(ctx, 0, 0, TILE, TILE);
  }
  // Where the surface meets the rock either side: a line of shadow.
  ctx.fillStyle = "rgba(0,0,0,0.22)";
  ctx.fillRect(0, 0, 1, TILE);
  ctx.fillRect(TILE - 1, 0, 1, TILE);
  c = inclineCache[key] = cv;
  return c;
}

/*
 * LADDERS IN THE OBLIQUE VIEW. Asked for on 2026-09-28: "render the ladder
 * oblique". The flat pictures above — a ladder lying on its floor, and one
 * against the black of a hatch — stay for wherever the world is drawn flat:
 * the map, the zoomed-out view, a look at another floor. The close view
 * stands the ladder up instead (see ladderAt in oblique.ts) and draws these.
 */

/** Rails and rungs, both ends of a ladder, as the flat pictures and these share them. */
const LADDER_RAIL = "#a97245";
const LADDER_RUNG = "#8a5a32";

/**
 * A ladder standing up, as a picture `w` wide and a tile tall, drawn
 * upright: faceTransform squashes it to a storey on the face it stands
 * on. Its rails, and rungs every eight pixels, which is four to a storey.
 */
const ladderCache = new Map<number, HTMLCanvasElement>();
export function ladderSprite(w: number): HTMLCanvasElement {
  let c = ladderCache.get(w);
  if (c) return c;
  const [cv, ctx] = canvas(w, TILE);
  ctx.fillStyle = LADDER_RUNG;
  for (let y = 3; y < TILE; y += 8) ctx.fillRect(2, y, w - 4, 3);
  ctx.fillStyle = LADDER_RAIL;
  ctx.fillRect(0, 0, 3, TILE);
  ctx.fillRect(w - 3, 0, 3, TILE);
  // The rails' shadowed side, so they read as round timber rather than as
  // two stripes painted on the wall behind.
  ctx.fillStyle = "rgba(0,0,0,0.3)";
  ctx.fillRect(2, 0, 1, TILE);
  ctx.fillRect(w - 1, 0, 1, TILE);
  ladderCache.set(w, (c = cv));
  return c;
}

/**
 * The head of a ladder with its foot in view: the hatch, a rim of boards
 * round an opening that is left empty, so the ladder under it and the
 * floor it stands on show through.
 */
let hatchCache: HTMLCanvasElement | undefined;
export function hatchSprite(): HTMLCanvasElement {
  if (hatchCache) return hatchCache;
  const [cv, ctx] = canvas(TILE, TILE);
  ctx.fillStyle = "#6f5a3e";
  ctx.fillRect(0, 0, TILE, 4);
  ctx.fillRect(0, TILE - 4, TILE, 4);
  ctx.fillRect(0, 4, 4, TILE - 8);
  ctx.fillRect(TILE - 4, 4, 4, TILE - 8);
  ctx.strokeStyle = "#4e3f2b";
  ctx.lineWidth = 1;
  ctx.strokeRect(3.5, 3.5, TILE - 7, TILE - 7);
  return (hatchCache = cv);
}

/**
 * The head of a ladder, or a hole with a ladder under it, looked down into
 * from above with the level below out of view: the shaft, and the top of
 * the ladder standing against its north side going down into the dark.
 * Seen from the south-east, the far walls of the shaft show, north and
 * west, a storey deep, and the ladder is drawn as it stands — LADDER_BACK
 * in from the north edge, which is none since 2026-10-19 ("put the ladder
 * flush with the north end of the tile"), against the shaft's north wall,
 * sheared down and to the right as it goes down,
 * the way everything a storey below is — and fading as it goes.
 * Everything is inside the tile's square, so it is laid with the ground.
 * `hole` is the hole's picture with the ladder in its pit; otherwise it is
 * the hatch, a rim of boards round the square opening.
 */
const shaftCache: HTMLCanvasElement[] = [];
export function ladderShaftSprite(hole: boolean): HTMLCanvasElement {
  const key = hole ? 1 : 0;
  let c = shaftCache[key];
  if (c) return c;
  const [cv, ctx] = canvas(TILE, TILE);
  const S = TILE / 2, back = 0, w = 16, lx = (TILE - w) / 2;
  if (hole) {
    drawTile(ctx, Tile.Hole, 0, 0);
    ctx.save();
    ctx.beginPath();
    for (const [x, y] of HOLE_LIP) ctx.lineTo(x, y);
    ctx.closePath();
    ctx.clip();
  } else {
    ctx.fillStyle = "#181410";
    ctx.fillRect(0, 0, TILE, TILE);
    // The far walls of the shaft: the north one lit a little from above,
    // the west one in shadow.
    ctx.fillStyle = "#2c2318";
    ctx.beginPath();
    ctx.moveTo(0, 0);
    ctx.lineTo(TILE, 0);
    ctx.lineTo(TILE + S, S);
    ctx.lineTo(S, S);
    ctx.closePath();
    ctx.fill();
    ctx.fillStyle = "#211a12";
    ctx.beginPath();
    ctx.moveTo(0, 0);
    ctx.lineTo(S, S);
    ctx.lineTo(S, TILE);
    ctx.lineTo(0, TILE);
    ctx.closePath();
    ctx.fill();
    ctx.save();
  }
  ctx.save();
  ctx.transform(1, 0, 0.5, 0.5, lx, back);
  ctx.drawImage(ladderSprite(w), 0, 0);
  ctx.restore();
  // Darker the further down it goes: a storey down is the bottom of the
  // picture's reach, and there it is nearly black.
  const g = ctx.createLinearGradient(0, back, 0, back + S);
  g.addColorStop(0, "rgba(10,8,6,0)");
  g.addColorStop(1, "rgba(10,8,6,0.6)");
  ctx.fillStyle = g;
  ctx.fillRect(0, back, TILE, TILE);
  ctx.restore();
  if (!hole) {
    ctx.strokeStyle = "#6f5a3e";
    ctx.lineWidth = 2;
    ctx.strokeRect(1, 1, TILE - 2, TILE - 2);
  }
  shaftCache[key] = c = cv;
  return c;
}

export function prewarmTiles() {
  for (let t = 0; t < TILE_COUNT; t++) {
    const variants = t === Tile.Bridge ? SPANS : t === Tile.Rock ? ROCK_SPAN * ROCK_SPAN : VARIANTS;
    for (let v = 0; v < variants; v++) for (let f = 0; f < 2; f++) tileSprite(t as Tile, v, f);
  }
}

/** Minimap colour per tile. */

/**
 * The flat colours the ground is painted in when a tile is too small to
 * draw. They were the minimap's and they came back with it (asked for on
 * 2026-09-20: "Allow zooming out to the point it looks like the old
 * minimap, where indistinguishable tiles use the appropriate colouring
 * they had"): under FLAT_BELOW pixels a tile a 32-pixel sprite is mush
 * that costs as much to draw as the real thing, and these read better at
 * that size, which is the argument the minimap always made for itself.
 *
 * One entry per tile, in the server's own order. A tile with no entry
 * comes out magenta, which is a bug wearing a colour rather than a colour
 * in the scheme.
 */
/**
 * Every way between levels, painted flat, in one colour.
 *
 * Stairs, the head of stairs, a hole and a ladder at either end used to be
 * drawn as what they are made of — timber for the ladders, grey stone for
 * the steps, near-black for the hole — which is right in the world, where
 * you are looking at the thing itself and it is a foot away. On a map at
 * three pixels a tile it is useless twice over. Each of them disappeared
 * into whatever it was standing on: grey steps into a grey castle floor, a
 * black hole into the dark of a cave map, ladder timber into the brown of
 * the plains. And even found, they did not read as one another, so the
 * fact that matters most about a map — where can I get off this floor —
 * was five different marks none of which stood out.
 *
 * So: one colour for all of them, and a colour that is in the palette
 * nowhere else. The ground is greens, browns, greys and one navy; the
 * dots over it are red for a monster, cyan for another player and white
 * for you. A violet belongs to none of that, and it is the only hue on
 * the map that cannot be mistaken for terrain at a glance.
 *
 * It is deliberately not magenta, which is what an id with no colour at
 * all is drawn as — a way down and a bug should not look alike.
 */
export const PASSAGE_FLAT = "#b44cff";

export const TILE_COLORS: string[] = [
  "#3d7a2b", "#7a5a3a", "#d6c07a", "#8c8c8c", "#2b5ea8", "#3a3634", "#1f5a1f", "#4a8a35", "#8c867c", "#cbbd94", "#5c5751", "#4a5a2a",
  PASSAGE_FLAT, // stairs up
  "#4e4a55", // a rooftop
  PASSAGE_FLAT, // the head of the stairs, going back down
  "#7a5c38", // the depot's floor
  PASSAGE_FLAT, // a hole down
  PASSAGE_FLAT, // a ladder up
  "#3d352c", // cave floor
  "#211c16", // cave wall
  "#8a6234", // the drawbridge: timber, so the crossing shows against the moat
  "#6b6560", // the hilltop: bare rock, a storey above the blight
  "#5a3f22", // the palisade: dark timber, so the camp reads as a ring
  "#8a6a45", // the rampart: pale planks, a storey above the yard
  "#a07c52", // the towers: the same timber, lighter, so corners read as corners
  PASSAGE_FLAT, // a ladder's head, going back down
  "#4a443e", // the forge yard: soot-dark, so the yard reads as a mark on the road south
  "#5a4326", // a pit prop: timber, so the mine's trunk reads as a line of props underground
  PASSAGE_FLAT, // a ramp up a mountainside
  PASSAGE_FLAT, // the top of a ramp, going back down
  PASSAGE_FLAT, // a slope up a gentle face
  PASSAGE_FLAT, // the top of a slope, going back down
  "#dfe7ee", // snow on the High Pass: near-white, a cold blue-grey
  "#2f6a26", // a hedge: dark, so a garden's edge reads as a line
  "#5f9c40", // the hillside: turf, a shade darker than the grass round it
  "#2e7d32", // a round door: Bag End's green
  "#c9a35a", // thatch: straw
  "#7a5a3a", // the mill wheel: timber in the water
  "#e8b830", // the Party Tree: its golden crown, so the respawn is found at a glance
  "#e8e4da", // a ship: its pale sail on the water, so the two jetties are found at a glance
  "#a9c8de", // the frozen waste: ice, a colder blue than the snow, so the north edge reads as a wall of it
];

// --- items -------------------------------------------------------------------

/** A cuirass outline shared by the three armors; detail() adds the material. */
function drawArmor(ctx: Ctx, body: string, shade: string, light: string, detail: (ctx: Ctx) => void) {
  ctx.fillStyle = body;
  ctx.beginPath();
  ctx.moveTo(10, 8);
  ctx.lineTo(22, 8);
  ctx.lineTo(25, 13);
  ctx.lineTo(23, 24);
  ctx.quadraticCurveTo(16, 28, 9, 24);
  ctx.lineTo(7, 13);
  ctx.closePath();
  ctx.fill();
  detail(ctx);
  // Neck opening and a highlight down one side, so the shape reads as worn.
  ctx.fillStyle = shade;
  ctx.beginPath();
  ctx.moveTo(13, 8);
  ctx.lineTo(19, 8);
  ctx.lineTo(16, 14);
  ctx.closePath();
  ctx.fill();
  ctx.fillStyle = light;
  ctx.fillRect(11, 13, 2, 10);
}

/**
 * A cloak on the ground or in the bag, as a cloak (2026-10-10: "Make the
 * spidersilk cloak look more like a cloak", then for the elven cloak
 * "Update the elven cloak to match"). Both were drawArmor's tunic, which
 * read as a shirt. Now each hangs from its clasp with its hood lying back,
 * round over the shoulders and flaring wide to the hem, and open down the
 * front with the dark lining showing in the gap: a wedge of shadow under
 * a clasp is most of what says "cloak" rather than "coat" at this size.
 * The folds run down from the neck and spread with the cloth.
 *
 * ragged gives the hem in points like loose threads, the silk's; the
 * elven cloak's falls in one even curve. detail draws what each has of
 * its own on top, the clasp at the throat among it.
 */
function drawCape(
  ctx: Ctx,
  pal: { body: string; lining: string; fold: string; light: string },
  ragged: boolean,
  detail: (ctx: Ctx) => void,
) {
  // The body: round over the shoulders, then falling and flaring.
  ctx.fillStyle = pal.body;
  ctx.beginPath();
  ctx.moveTo(13, 7);
  ctx.quadraticCurveTo(8, 7, 7, 11);
  ctx.quadraticCurveTo(5.5, 19, 4, 26);
  if (ragged) {
    // The hem in points, a thread hanging at each.
    const n = 6, step = 24 / n;
    for (let i = 1; i <= n; i++) {
      const x = 4 + step * i;
      ctx.lineTo(x - step / 2, (i & 1 ? 29 : 28) - Math.abs(x - 16) / 8);
      ctx.lineTo(x, 26.5 - Math.abs(x - 16) / 8);
    }
  } else {
    ctx.quadraticCurveTo(16, 31.5, 28, 26);
  }
  ctx.quadraticCurveTo(26.5, 19, 25, 11);
  ctx.quadraticCurveTo(24, 7, 19, 7);
  ctx.closePath();
  ctx.fill();
  // The hood lying back over the shoulders, its inside dark.
  ctx.beginPath();
  ctx.ellipse(16, 7, 6.5, 4, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = pal.lining;
  ctx.beginPath();
  ctx.ellipse(16, 6.5, 4, 2.3, 0, 0, Math.PI * 2);
  ctx.fill();
  // The lining in the opening, from the clasp to the hem.
  ctx.beginPath();
  ctx.moveTo(15.2, 10);
  ctx.lineTo(16.8, 10);
  ctx.lineTo(20, 27.5);
  ctx.lineTo(16, 28.7);
  ctx.lineTo(12, 27.5);
  ctx.closePath();
  ctx.fill();
  // Folds, spreading from the neck with the cloth.
  ctx.strokeStyle = pal.fold;
  ctx.lineWidth = 1;
  ctx.beginPath();
  for (const [x0, x1] of [[11, 7.5], [13.5, 11], [18.5, 21], [21, 24.5]]) {
    ctx.moveTo(x0, 11);
    ctx.quadraticCurveTo((x0 + x1) / 2 + (x1 < 16 ? -1 : 1), 17, x1, 26.5);
  }
  ctx.stroke();
  // The light along the near edge.
  ctx.strokeStyle = pal.light;
  ctx.beginPath();
  ctx.moveTo(8, 11);
  ctx.quadraticCurveTo(6.5, 18, 5, 25);
  ctx.stroke();
  // The rim of the hood.
  ctx.strokeStyle = pal.fold;
  ctx.beginPath();
  ctx.ellipse(16, 7, 5.5, 3.2, 0, 0.15, Math.PI - 0.15);
  ctx.stroke();
  detail(ctx);
}

/**
 * What a haunch is made of. Three tones of meat rather than one, because
 * a single flat fill is what made the old one read as a brown lump: the
 * lean is the body of it, the shadow is the underside no light reaches,
 * and the lit shoulder is where it does. The fat is the rind along the
 * top and the collar where the bone comes out — that pale band is most of
 * what says "meat" rather than "stone" at this size — and the seam is the
 * dark line drawn between the bone and the flesh, which the outline pass
 * cannot do for us: it stamps the silhouette and knows nothing about the
 * edges inside it.
 */
const MEAT = {
  lean: "#8e3627",
  shadow: "#5c1d13",
  lit: "#b34e35",
  fleck: "#c96a4c",
  fat: "#e0c4a6",
  bone: "#f3ead8",
  boneShade: "#c9b99b",
  seam: "#4a1610",
};

/**
 * Dragon ham (2026-10-23: "it should look greenish"): the haunch drawn
 * the same way, its flesh a sickly green with a darker underside and a
 * pale, yellowish rind, and a row of scales along the top where the hide
 * was left on (see drawDragonHam). The bone is the meat's bone.
 */
const DRAGON_HAM = {
  lean: "#4f7a2c",
  shadow: "#2c4a17",
  lit: "#77a540",
  fleck: "#a6cf62",
  fat: "#d6dc9a",
  bone: MEAT.bone,
  boneShade: MEAT.boneShade,
  seam: "#1e3310",
};

/** Dragon ham: the meat's haunch in DRAGON_HAM's greens, scaled along the top. */
function drawDragonHam(ctx: Ctx) {
  drawMeat(ctx, DRAGON_HAM);
  // The hide left on along the rind: four little scales, dark green with
  // a lit edge, overlapping down the shoulder.
  for (const [x, y] of [[8.2, 16.4], [11.4, 14.6], [14.8, 13.8], [18, 14.4], [10, 19], [13.4, 17.4]]) {
    ctx.fillStyle = "#1e3612";
    ctx.beginPath();
    ctx.arc(x, y, 2, 0, Math.PI);
    ctx.fill();
    ctx.strokeStyle = "#b8dc72";
    ctx.lineWidth = 0.8;
    ctx.beginPath();
    ctx.arc(x, y, 2, Math.PI, 0);
    ctx.stroke();
  }
}

/**
 * Meat: a haunch on the bone, the fat end down and the knuckle up in the
 * corner. A better one was asked for on 2026-09-19, and this picked out of
 * three; what it replaces was a flat brown ellipse with a white rectangle
 * floating beside it that never touched the meat, which at a tile's size
 * read as a lump with a speck.
 *
 * Two things are worth keeping about the shape. The bone is drawn first
 * so the haunch closes over the end of it, which is what makes them one
 * object rather than two things near each other. And the meat is most of
 * the tile with the bone as a handle in the corner, rather than the even
 * split a drumstick would have: this is the thing you eat, so the part
 * you eat is the part that is drawn.
 */
function drawMeat(ctx: Ctx, pal: typeof MEAT = MEAT) {
  // The bone: a shaft up to the corner with two knuckles on the end of
  // it, the far half of each in shadow so the pair reads as a joint.
  ctx.strokeStyle = pal.bone;
  ctx.lineWidth = 4;
  ctx.lineCap = "round";
  ctx.beginPath();
  ctx.moveTo(19, 13);
  ctx.lineTo(24.5, 7.5);
  ctx.stroke();
  ctx.fillStyle = pal.bone;
  ctx.beginPath();
  ctx.arc(23.5, 6.5, 3, 0, Math.PI * 2);
  ctx.fill();
  ctx.beginPath();
  ctx.arc(26.5, 9.5, 2.6, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = pal.boneShade;
  ctx.beginPath();
  ctx.arc(25, 8.5, 2, 0, Math.PI * 2);
  ctx.fill();
  ctx.beginPath();
  ctx.arc(27.4, 10.6, 1.5, 0, Math.PI * 2);
  ctx.fill();
  // The haunch, closing over the foot of the bone.
  ctx.fillStyle = pal.lean;
  ctx.beginPath();
  ctx.moveTo(20.5, 13.5);
  ctx.bezierCurveTo(24, 19, 21, 27, 13.5, 28);
  ctx.bezierCurveTo(6, 29, 2.5, 22, 5.5, 17);
  ctx.bezierCurveTo(9, 12, 17, 10.5, 20.5, 13.5);
  ctx.closePath();
  ctx.fill();
  ctx.fillStyle = pal.shadow; // the underside, where no light gets
  ctx.beginPath();
  ctx.moveTo(3.5, 21);
  ctx.bezierCurveTo(7, 29, 19, 30, 22, 21.5);
  ctx.bezierCurveTo(17, 26.5, 8, 27, 3.5, 21);
  ctx.closePath();
  ctx.fill();
  ctx.fillStyle = pal.lit; // and the shoulder, where it does
  ctx.beginPath();
  ctx.moveTo(6.5, 16.5);
  ctx.bezierCurveTo(11, 12.5, 17, 12, 19.5, 14.5);
  ctx.bezierCurveTo(15, 15, 10, 16.5, 7.5, 19);
  ctx.closePath();
  ctx.fill();
  // The rind along the top, and the seam where the bone goes in.
  ctx.strokeStyle = pal.fat;
  ctx.lineWidth = 1.6;
  ctx.beginPath();
  ctx.moveTo(7, 15.5);
  ctx.bezierCurveTo(11, 11.6, 17, 11.2, 20, 13.6);
  ctx.stroke();
  ctx.strokeStyle = pal.seam;
  ctx.lineWidth = 1.4;
  ctx.beginPath();
  ctx.moveTo(17.5, 11.8);
  ctx.lineTo(21.5, 16);
  ctx.stroke();
  ctx.fillStyle = pal.fleck; // one bright fleck, so it is not flat
  ctx.beginPath();
  ctx.ellipse(11, 17.5, 2.6, 1.3, -0.5, 0, Math.PI * 2);
  ctx.fill();
}

/**
 * What a mushroom is made of. The same palette discipline as the haunch
 * beside it: two tones of cap so the dome is a dome rather than a disc,
 * a dark underside that is neither of them, and a stalk pale enough to
 * read against both. The gill is the darkest thing in the item and the
 * stem the palest, on purpose — at half a tile the only thing left of a
 * drawing is its values, and cap-over-dark-over-pale is what still says
 * "mushroom" when the shapes have gone.
 */
const MUSHROOM = {
  cap: "#8c4a2f",
  capShade: "#5e2c19",
  capLit: "#b8704b",
  fleck: "#e8d6b8",
  gill: "#3a1c10",
  stem: "#efe2c6",
  stemShade: "#bfae8e",
};

/**
 * A mushroom: a cap, the gills under it, and a stalk coming out of them.
 * one was asked for on 2026-09-21 — it is what dwarves eat, and what
 * they leave behind — and it is thirty seconds of food, a third of a
 * haunch.
 *
 * The stalk is drawn first and the cap and its gills close over the top
 * of it, which is the same trick the haunch plays on its bone: two parts
 * drawn in that order are one object, and drawn the other way round they
 * are a lump sitting on a stick. The cap takes two thirds of the height
 * because the cap is the whole of what tells a mushroom from a bone or a
 * bottle at a tile's size, and everything stays a pixel in from the edge
 * so the outline pass has somewhere to put the silhouette.
 */
function drawMushroom(ctx: Ctx) {
  // The stalk, slightly waisted and flared at the foot.
  ctx.fillStyle = MUSHROOM.stem;
  ctx.beginPath();
  ctx.moveTo(12.9, 15);
  ctx.bezierCurveTo(12.3, 21, 11.2, 25, 11.4, 28);
  ctx.lineTo(20.6, 28);
  ctx.bezierCurveTo(20.8, 25, 19.7, 21, 19.1, 15);
  ctx.closePath();
  ctx.fill();
  ctx.fillStyle = MUSHROOM.stemShade; // the side the light is not on
  ctx.beginPath();
  ctx.moveTo(16.8, 15);
  ctx.bezierCurveTo(17.4, 21, 18.6, 25, 18.4, 28);
  ctx.lineTo(20.6, 28);
  ctx.bezierCurveTo(20.8, 25, 19.7, 21, 19.1, 15);
  ctx.closePath();
  ctx.fill();
  // The gills, a dark band tucked under the rim. Narrower than the cap,
  // so the cap overhangs them rather than sitting flush on them.
  ctx.fillStyle = MUSHROOM.gill;
  ctx.beginPath();
  ctx.ellipse(16, 17.4, 10, 2.7, 0, 0, Math.PI * 2);
  ctx.fill();
  // The cap over the lot.
  ctx.fillStyle = MUSHROOM.cap;
  ctx.beginPath();
  ctx.moveTo(4.6, 17.4);
  ctx.bezierCurveTo(4.6, 8.6, 10.2, 5.6, 16, 5.6);
  ctx.bezierCurveTo(21.8, 5.6, 27.4, 8.6, 27.4, 17.4);
  ctx.closePath();
  ctx.fill();
  ctx.fillStyle = MUSHROOM.capShade; // the far side of the dome
  ctx.beginPath();
  ctx.moveTo(16, 5.6);
  ctx.bezierCurveTo(21.8, 5.6, 27.4, 8.6, 27.4, 17.4);
  ctx.lineTo(21, 17.4);
  ctx.bezierCurveTo(21, 10.5, 19, 6.6, 16, 5.6);
  ctx.closePath();
  ctx.fill();
  ctx.fillStyle = MUSHROOM.capLit; // and the near side, where it is lit
  ctx.beginPath();
  ctx.moveTo(6.6, 14);
  ctx.bezierCurveTo(7, 9.6, 11, 7.2, 14.6, 7);
  ctx.bezierCurveTo(11.4, 8.4, 9, 11, 8.4, 14.6);
  ctx.closePath();
  ctx.fill();
  ctx.fillStyle = MUSHROOM.fleck; // the specks a cap grown in the dark has
  for (const [x, y, rx, ry] of [[12, 10.6, 2.2, 1.7], [19.6, 9.4, 1.8, 1.4], [22.6, 13.6, 1.5, 1.2]] as const) {
    ctx.beginPath();
    ctx.ellipse(x, y, rx, ry, 0, 0, Math.PI * 2);
    ctx.fill();
  }
}

/**
 * A corked flask: a round belly of `body` with a `glint` where the light
 * catches it, and the same glass neck and cork whatever is inside. The
 * health potion's red was the only filling until 2026-09-27, when the mana
 * potion came in the same bottle ("add a mana potion that gives 50 mana and
 * costs 25 gp"), so the two read as the same kind of thing and the colour
 * says which bar it fills.
 */
function drawPotion(ctx: Ctx, body: string, glint: string) {
  ctx.fillStyle = body;
  ctx.beginPath();
  ctx.arc(16, 20, 7, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = glint;
  ctx.beginPath();
  ctx.arc(14, 18, 2.5, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = "#ccc";
  ctx.fillRect(13, 8, 6, 7);
  ctx.fillStyle = "#775";
  ctx.fillRect(12, 6, 8, 3);
}

/**
 * The chest's wood and metal, one look for the quest chests and the depot
 * boxes alike (2026-10-01: "use a wooden chest with a rounded top instead
 * of the tall iron chest we have. make sure the depot uses the same ones"):
 * the front, the east end in shadow, the lid from its lowest stave to its
 * lit crown, the dark plank seams, the iron bands, and the brass lock plate,
 * its keyhole and its hasp. The standing chest in chest.ts is drawn in the
 * same colours.
 */
export const CHEST_WOOD = {
  s: "#8b5a2b",
  e: "#6a4321",
  lidLow: "#93602f",
  lidHigh: "#b47b42",
  plank: "#3e2410",
  band: "#4f5358",
  lock: "#c9a227",
  keyhole: "#2a1a0c",
  hasp: "#a8871f",
};

/**
 * The flat picture of a chest, the quest chest's and the depot box's: a
 * low wooden body of two planks, a rounded lid on it lit at its crown,
 * two iron bands up the front and over the lid, the iron rim under the
 * lid, and a small brass lock plate with its hasp coming down from the lid.
 */
function drawChest(ctx: Ctx) {
  const W = CHEST_WOOD;
  const shape = () => {
    ctx.beginPath();
    ctx.moveTo(5, 26);
    ctx.lineTo(5, 16);
    ctx.ellipse(16, 16, 11, 7, 0, Math.PI, Math.PI * 2);
    ctx.lineTo(27, 26);
    ctx.closePath();
  };
  ctx.fillStyle = W.lidLow; // the lid, its lowest staves
  shape();
  ctx.fill();
  ctx.fillStyle = W.lidHigh; // and its crown, where the light catches it
  ctx.beginPath();
  ctx.ellipse(16, 15, 8, 5, 0, Math.PI, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = W.s; // the body
  ctx.fillRect(5, 16, 22, 10);
  ctx.save();
  shape();
  ctx.clip();
  ctx.strokeStyle = W.plank; // the plank seams: across the body, and the staves over the lid
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(5, 21.5);
  ctx.lineTo(27, 21.5);
  ctx.stroke();
  for (const r of [2.5, 5]) {
    ctx.beginPath();
    ctx.ellipse(16, 16, 11, r, 0, Math.PI, Math.PI * 2);
    ctx.stroke();
  }
  ctx.fillStyle = W.band; // the bands, up the front and over the lid
  ctx.fillRect(8, 8, 3, 18);
  ctx.fillRect(21, 8, 3, 18);
  ctx.fillRect(5, 15, 22, 2); // the rim under the lid
  ctx.restore();
  ctx.fillStyle = W.lock; // the lock plate
  ctx.fillRect(14, 17, 4, 5);
  ctx.fillStyle = W.hasp; // the hasp, down from the lid over the rim
  ctx.fillRect(15, 13, 2, 5);
  ctx.fillStyle = W.keyhole;
  ctx.fillRect(15, 19, 2, 2);
}

/**
 * The flat picture of a depot box, square-edged where the chest is rounded
 * (2026-10-10: "make depot boxes a distinct square-edged chest shape"):
 * the same wood, a body of two planks under a flat lid with square
 * corners, the lid's top lit, iron caps on its four corners and the
 * corners of the lid, an iron rim on the seam between them, and the brass
 * lock plate with its hasp down from the lid.
 */
function drawDepotBox(ctx: Ctx) {
  const W = CHEST_WOOD;
  ctx.fillStyle = W.s; // the body
  ctx.fillRect(5, 15, 22, 11);
  ctx.fillStyle = W.lidLow; // the lid's front
  ctx.fillRect(4, 10, 24, 5);
  ctx.fillStyle = W.lidHigh; // and its flat top, where the light catches it
  ctx.fillRect(4, 8, 24, 2);
  ctx.strokeStyle = W.plank; // the plank seam across the body
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(5, 20.5);
  ctx.lineTo(27, 20.5);
  ctx.stroke();
  ctx.fillStyle = W.band;
  ctx.fillRect(4, 14, 24, 2); // the rim on the seam under the lid
  for (const x of [5, 24]) ctx.fillRect(x, 16, 3, 10); // the body's corners
  for (const x of [4, 25]) ctx.fillRect(x, 8, 3, 6); // the lid's corners
  ctx.fillStyle = W.lock; // the lock plate
  ctx.fillRect(14, 17, 4, 5);
  ctx.fillStyle = W.hasp; // the hasp, down from the lid over the rim
  ctx.fillRect(15, 12, 2, 6);
  ctx.fillStyle = W.keyhole;
  ctx.fillRect(15, 19, 2, 2);
}

/** The mana potion's filling: the blue of the mana bar, as the soul orb is. */
const POTION_MANA = { body: "#1f56a8", glint: "#6fb0f0" };

/**
 * One item, drawn into a tile. `color` is the colour of whoever owns it,
 * which only the skull has and only the skull reads: it is threaded in as
 * an argument rather than looked up anywhere, because itemSprite's cache
 * key is built from exactly what is passed here and anything fetched
 * further down would be invisible to it.
 */
/**
 * The crossbow's colours (2026-09-27), stated once because it is drawn three
 * times over — on the ground, in somebody's hands and on their back — and
 * the three have to be the same thing: a stock of dark wood, a steel prod,
 * and a string the bow's own cream.
 */
const CROSSBOW = { stock: "#4a3220", stockLit: "#7a5634", prod: "#8d949c", prodLit: "#c3c9ce", iron: "#3a3d41", string: "#e8e0c8" };

/**
 * An arrow and a bolt, as they lie in a bundle. The arrow is long, thin and
 * pale-headed, fletched in white; the bolt is short, thick and dark, with a
 * squat iron head and leather vanes. They are the same colours they fly in
 * (see missiles.ts), so what you pick up is what you saw coming at you.
 */
interface Shaft { shaft: string; width: number; head: string; headLen: number; headHalf: number; fletch: string; fletchLen: number; band: string }
const ARROW_LOOK: Shaft = { shaft: "#8a5a2a", width: 1.4, head: "#d8dde1", headLen: 4, headHalf: 1.8, fletch: "#f2efe6", fletchLen: 5, band: "#c8a060" };
const BOLT_LOOK: Shaft = { shaft: "#3e2a1a", width: 2.4, head: "#8d949c", headLen: 4, headHalf: 2.6, fletch: "#7a5634", fletchLen: 3, band: "#b07a3a" };

/**
 * A bundle of `n` shafts `long` pixels long, laid along the tile's diagonal
 * side by side and tied round the middle: the ground and bag picture of
 * arrows and of bolts (2026-09-27, "implement arrows and bolts").
 */
function drawBundle(ctx: Ctx, n: number, long: number, look: Shaft) {
  const ux = Math.SQRT1_2, uy = -Math.SQRT1_2; // up and to the right
  const px = -uy, py = ux; // square across
  const gap = look.width + 1.4;
  ctx.lineCap = "butt";
  for (let i = 0; i < n; i++) {
    const off = (i - (n - 1) / 2) * gap;
    // Staggered a little along their length, as a handful is.
    const slide = (i % 2) * 1.5;
    const mx = 16 + px * off + ux * slide, my = 16 + py * off + uy * slide;
    const bx = mx - ux * long / 2, by = my - uy * long / 2; // the nock
    const tx = mx + ux * long / 2, ty = my + uy * long / 2; // the point
    ctx.strokeStyle = look.shaft;
    ctx.lineWidth = look.width;
    ctx.beginPath();
    ctx.moveTo(bx, by);
    ctx.lineTo(tx - ux * look.headLen, ty - uy * look.headLen);
    ctx.stroke();
    // The head, a point of metal on the end.
    ctx.fillStyle = look.head;
    ctx.beginPath();
    ctx.moveTo(tx, ty);
    ctx.lineTo(tx - ux * look.headLen + px * look.headHalf, ty - uy * look.headLen + py * look.headHalf);
    ctx.lineTo(tx - ux * look.headLen - px * look.headHalf, ty - uy * look.headLen - py * look.headHalf);
    ctx.closePath();
    ctx.fill();
    // The fletching, a feather either side of the nock.
    ctx.strokeStyle = look.fletch;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(bx + px * 1.6, by + py * 1.6);
    ctx.lineTo(bx + ux * look.fletchLen + px * 0.4, by + uy * look.fletchLen + py * 0.4);
    ctx.moveTo(bx - px * 1.6, by - py * 1.6);
    ctx.lineTo(bx + ux * look.fletchLen - px * 0.4, by + uy * look.fletchLen - py * 0.4);
    ctx.stroke();
  }
  // The cord round the middle of the bundle.
  const half = ((n - 1) / 2) * gap + look.width;
  ctx.strokeStyle = look.band;
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(16 - px * half, 16 - py * half);
  ctx.lineTo(16 + px * half, 16 + py * half);
  ctx.stroke();
}

/**
 * The White Hand of Saruman, painted on the Uruk-hai shield: a palm, four
 * fingers and a thumb, centred on (cx, cy) at scale k (2026-10-01).
 */
function drawWhiteHand(ctx: Ctx, cx: number, cy: number, k: number) {
  ctx.fillStyle = URUK.hand;
  ctx.beginPath();
  ctx.ellipse(cx, cy + 2 * k, 3.6 * k, 4 * k, 0, 0, Math.PI * 2);
  ctx.fill();
  for (const dx of [-2.6, -0.9, 0.9, 2.6]) ctx.fillRect(cx + dx * k - 0.7 * k, cy - 6 * k, 1.4 * k, 6 * k);
  ctx.fillRect(cx - 5.2 * k, cy - 0.5 * k, 2.2 * k, 1.4 * k);
}

/**
 * The phial of Galadriel: a crystal flask with a stopper of gold and a
 * star's light at its heart, its belly centred on (cx, cy) at scale k. It
 * is the bag's picture and the one in the hand (2026-10-01).
 */
function drawPhial(ctx: Ctx, cx: number, cy: number, k: number) {
  ctx.fillStyle = PHIAL.glass;
  ctx.beginPath();
  ctx.arc(cx, cy, 5 * k, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillRect(cx - 1.5 * k, cy - 10 * k, 3 * k, 6 * k); // the neck
  ctx.fillStyle = PHIAL.heart;
  ctx.beginPath();
  ctx.arc(cx, cy, 2.4 * k, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = PHIAL.stopper;
  ctx.fillRect(cx - 2 * k, cy - 12 * k, 4 * k, 2.5 * k);
}

/**
 * The Arkenstone (2026-11-01): a great white gem like a globe of a
 * thousand facets, centred on (cx, cy) at scale k. It is the bag's
 * picture, the glint in Smaug's hoard and the one in the hand.
 *
 * Its rainbow is laid on thick (2026-11-03: "exaggerate the rainbow colors
 * with the arkenstone"): every facet is its own colour of the spectrum,
 * blues and teals most, with magentas, violets and reds between, round a
 * heart burning white, and rays of colour shoot out of it through a halo
 * of blue light. As the Hobbit has it, the light that falls on it comes
 * back as white radiance shot with glints of the rainbow.
 */
const ARKENSTONE = {
  // The facets, going round: mostly blue and teal, the rest of the
  // spectrum between them, so no two neighbours are alike.
  facets: ["#3d7bff", "#ff3fb4", "#2fe0d0", "#8a4dff", "#ff4a5a", "#36b0ff", "#d040ff", "#24c8a0", "#5a5cff", "#ff8a3d", "#1ea0ff", "#e8389a"],
  rays: ["#7fd8ff", "#ff7ad0", "#9dffea", "#b48cff", "#ff8a8a", "#ffe07a", "#7fb4ff", "#c8ff8a"],
  edge: "rgba(255,255,255,0.75)",
};

function drawArkenstone(ctx: Ctx, cx: number, cy: number, k: number) {
  ctx.save();
  // The halo: blue-white light, strongest at the stone.
  const halo = ctx.createRadialGradient(cx, cy, 2 * k, cx, cy, 12 * k);
  halo.addColorStop(0, "rgba(200,230,255,0.75)");
  halo.addColorStop(0.45, "rgba(120,170,255,0.35)");
  halo.addColorStop(1, "rgba(150,110,255,0)");
  ctx.fillStyle = halo;
  ctx.beginPath();
  ctx.arc(cx, cy, 12 * k, 0, Math.PI * 2);
  ctx.fill();
  // Rays of colour out of it, long and short by turns.
  ctx.lineCap = "round";
  ARKENSTONE.rays.forEach((c, i) => {
    const a = -Math.PI / 2 + Math.PI / 16 + (i * 2 * Math.PI) / ARKENSTONE.rays.length;
    const len = (i % 2 ? 9.5 : 11.5) * k;
    ctx.strokeStyle = c;
    ctx.globalAlpha = 0.7;
    ctx.lineWidth = (i % 2 ? 0.6 : 0.9) * k;
    ctx.beginPath();
    ctx.moveTo(cx + Math.cos(a) * 7 * k, cy + Math.sin(a) * 7 * k);
    ctx.lineTo(cx + Math.cos(a) * len, cy + Math.sin(a) * len);
    ctx.stroke();
  });
  ctx.globalAlpha = 1;
  // The globe, cut in facets: an outer ring of twelve and an inner of six,
  // each a colour of its own.
  const r = 7.5 * k, ri = 3.8 * k, n = ARKENSTONE.facets.length;
  const at = (rad: number, i: number, of: number, turn = 0): [number, number] => {
    const a = -Math.PI / 2 + turn + (i * 2 * Math.PI) / of;
    return [cx + Math.cos(a) * rad, cy + Math.sin(a) * rad];
  };
  const tri = (p: [number, number][], fill: string) => {
    ctx.fillStyle = fill;
    ctx.beginPath();
    ctx.moveTo(p[0][0], p[0][1]);
    for (const q of p.slice(1)) ctx.lineTo(q[0], q[1]);
    ctx.closePath();
    ctx.fill();
  };
  for (let i = 0; i < n; i++) {
    // The outer facets, from two corners on the rim to one on the inner ring.
    tri([at(r, i, n), at(r, i + 1, n), at(ri, i / 2 + 0.5, n / 2, 0)], ARKENSTONE.facets[i]);
    // And the ones between, from the inner ring out to the rim, a shade lighter.
    tri([at(ri, Math.floor(i / 2), n / 2), at(ri, Math.floor(i / 2) + 1, n / 2), at(r, i + 1, n)], ARKENSTONE.facets[(i + 5) % n]);
  }
  ctx.fillStyle = "rgba(255,255,255,0.22)"; // the upper half catches more light
  ctx.beginPath();
  ctx.arc(cx, cy, r, Math.PI, Math.PI * 2);
  ctx.fill();
  // The facets' edges, white, out from the heart and round the rim.
  ctx.strokeStyle = ARKENSTONE.edge;
  ctx.lineWidth = 0.35 * k;
  ctx.beginPath();
  for (let i = 0; i < n; i++) {
    const [x, y] = at(r, i, n);
    const [ix, iy] = at(ri, Math.round(i / 2), n / 2);
    ctx.moveTo(ix, iy);
    ctx.lineTo(x, y);
  }
  ctx.stroke();
  // The white heart, a star burning out of the middle of it.
  const heart = ctx.createRadialGradient(cx, cy, 0, cx, cy, 2.8 * k);
  heart.addColorStop(0, "rgba(255,255,255,1)");
  heart.addColorStop(0.45, "rgba(255,255,255,0.85)");
  heart.addColorStop(1, "rgba(220,240,255,0)");
  ctx.fillStyle = heart;
  ctx.beginPath();
  ctx.arc(cx, cy, 2.8 * k, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = "#ffffff";
  ctx.beginPath();
  for (let i = 0; i < 8; i++) {
    const [x, y] = at(i % 2 ? 0.8 * k : 2.6 * k, i, 8);
    if (i === 0) ctx.moveTo(x, y);
    else ctx.lineTo(x, y);
  }
  ctx.closePath();
  ctx.fill();
  // A glint high on the rim.
  ctx.fillRect(cx - 4.2 * k, cy - 5 * k, 1.2 * k, 1.2 * k);
  ctx.restore();
}

/**
 * A torch (2026-10-16: "Implement torches"): a haft of pine, a head of
 * pitch-soaked rag bound round its top, and the flame over it, standing on
 * (x, y) at scale size and leaning by turn radians. The flame is clear of the
 * head and licks up one side, for the reason the light spell's torch glyph
 * gives (see spellicons.ts): joined to the head and tapering evenly it
 * reads as a spearhead. It is the bag's picture and the one in the hand.
 */
function drawTorch(ctx: Ctx, x: number, y: number, size: number, turn: number) {
  ctx.save();
  ctx.translate(x, y);
  ctx.rotate(turn);
  ctx.scale(size, size);
  ctx.fillStyle = TORCH.glow;
  ctx.beginPath();
  ctx.arc(0, -23, 7, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = TORCH.haft;
  ctx.fillRect(-1.4, -14, 2.8, 14);
  ctx.fillStyle = TORCH.rag;
  ctx.fillRect(-2.8, -19, 5.6, 5.5);
  ctx.fillStyle = TORCH.band;
  ctx.fillRect(-2.8, -17, 5.6, 1);
  ctx.fillRect(-2.8, -15, 5.6, 0.8);
  ctx.fillStyle = TORCH.flame;
  ctx.beginPath();
  ctx.moveTo(0, -30);
  ctx.quadraticCurveTo(-1, -27, -2.8, -24.5);
  ctx.quadraticCurveTo(-4, -22.5, -3, -21);
  ctx.quadraticCurveTo(0, -19.4, 3, -21);
  ctx.quadraticCurveTo(4, -23, 2.4, -25.5);
  ctx.quadraticCurveTo(1.6, -24.4, 1, -24.6);
  ctx.quadraticCurveTo(1.6, -27.4, 0, -30);
  ctx.closePath();
  ctx.fill();
  ctx.fillStyle = TORCH.heart;
  ctx.beginPath();
  ctx.ellipse(0, -22.4, 1.4, 2, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
}

// 11. The Variags of Khand (2026-10-16): the hide of their tents, the
// palms of the oasis, the merchant's strongbox and his dates.
const KHAND = {
  hide: "#3a2e26", hideDark: "#1e1814", hideLight: "#5a4a3c", pole: "#8a6a42", flap: "#120e0c", spear: "#c8c0b0",
  trunk: "#8a7a62", trunkRing: "#5e5040", frond: "#4a8a3a", frondDark: "#2e5e26", fruit: "#c87a2a",
  box: "#5a3a22", boxLid: "#6e4a2c", boxShade: "#2e1e12", brass: "#d8b040", seal: "#a82020",
  date: "#6a3418", dateLight: "#b0703a",
};

/** A hide tent of the Variags: a low ridge tent over poles, its flap tied back on the dark inside. */
function drawVariagTent(ctx: Ctx) {
  ctx.fillStyle = KHAND.hideDark; // the shadow on the sand
  ctx.beginPath();
  ctx.ellipse(16, 28, 14, 2.5, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = KHAND.hide; // the hide, down from the ridge-pole to the pegs
  ctx.beginPath();
  ctx.moveTo(2, 28);
  ctx.lineTo(9, 8);
  ctx.lineTo(23, 8);
  ctx.lineTo(30, 28);
  ctx.closePath();
  ctx.fill();
  ctx.fillStyle = KHAND.hideLight; // the sunlit end
  ctx.beginPath();
  ctx.moveTo(9, 8);
  ctx.lineTo(2, 28);
  ctx.lineTo(16, 28);
  ctx.closePath();
  ctx.fill();
  ctx.fillStyle = KHAND.flap; // the door, its flap tied back
  ctx.beginPath();
  ctx.moveTo(6.5, 28);
  ctx.lineTo(9, 17);
  ctx.lineTo(12, 28);
  ctx.closePath();
  ctx.fill();
  ctx.strokeStyle = KHAND.pole; // the ridge-pole, out past the hide at both ends
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  ctx.moveTo(7, 7.5);
  ctx.lineTo(25, 7.5);
  ctx.stroke();
  ctx.strokeStyle = KHAND.spear; // a spear stuck in the sand by it
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(28, 29);
  ctx.lineTo(29.5, 3);
  ctx.stroke();
}

/** A date palm: a ringed trunk leaning up from the sand to a crown of fronds round (16, 13). */
function drawPalm(ctx: Ctx) {
  ctx.strokeStyle = KHAND.trunk;
  ctx.lineWidth = 3.5;
  ctx.lineCap = "round";
  ctx.beginPath();
  ctx.moveTo(14, 30);
  ctx.quadraticCurveTo(13, 21, 16, 13);
  ctx.stroke();
  ctx.strokeStyle = KHAND.trunkRing;
  ctx.lineWidth = 1;
  for (const y of [27, 24, 21, 18, 15.5]) {
    ctx.beginPath();
    ctx.moveTo(12.5, y);
    ctx.lineTo(16, y - 0.6);
    ctx.stroke();
  }
  // The fronds, each a long bowed leaf out of the crown.
  const frond = (dx: number, dy: number, col: string) => {
    ctx.strokeStyle = col;
    ctx.lineWidth = 2.6;
    ctx.beginPath();
    ctx.moveTo(16, 12);
    ctx.quadraticCurveTo(16 + dx * 0.6, 12 + dy * 0.6 - 4, 16 + dx, 12 + dy);
    ctx.stroke();
  };
  for (const [dx, dy] of [[-13, 6], [13, 6], [-9, -6], [9, -6], [0, -10]]) frond(dx, dy, KHAND.frondDark);
  for (const [dx, dy] of [[-12, 1], [12, 1], [-5, -9], [5, -9], [-14, 9], [14, 9]]) frond(dx, dy, KHAND.frond);
  ctx.lineCap = "butt";
  ctx.fillStyle = KHAND.fruit; // the dates, hanging in a bunch under the crown
  for (const [x, y] of [[14.5, 15], [16.5, 15.5], [15.5, 16.8]]) {
    ctx.beginPath();
    ctx.arc(x, y, 1.2, 0, Math.PI * 2);
    ctx.fill();
  }
}
// end of 11. Khand

// --- Site 8: the gardens of the Entwives (2026-10-16) ---
/**
 * The items the oblique view stands up as trees, cut out the way a tree
 * tile is (treeCut): the withered apple trees of the Entwives' orchard and
 * the last one alive. Each is drawn in the tree tile's shape, a crown in
 * the disc round (16, 13) over a trunk in the post below it.
 */
export const STANDING_TREE_ITEMS: ReadonlySet<number> = new Set([
  122, 123,
  146, // the palms of the oasis in Khand (11. Khand, 2026-10-16)
]);

/** A withered apple tree: a split grey trunk and bare limbs, not a leaf. */
function drawWitheredTree(ctx: Ctx) {
  ctx.fillStyle = "#6d6258"; // the trunk, grey with age
  ctx.fillRect(14, 16, 5, 14);
  ctx.fillStyle = "#4a423b"; // the split down it
  ctx.fillRect(16, 19, 1, 9);
  ctx.strokeStyle = "#6d6258";
  ctx.lineCap = "round";
  const limbs: [number, number, number, number, number][] = [
    // from x, from y, to x, to y, width
    [16, 17, 7, 8, 2.5],
    [16, 17, 25, 7, 2.5],
    [16, 16, 15, 3, 2],
    [10, 11, 6, 13, 1.2],
    [11, 12, 9, 4, 1.2],
    [22, 10, 27, 13, 1.2],
    [21, 11, 21, 3, 1.2],
    [15, 8, 11, 5, 1],
    [15, 7, 19, 4, 1],
  ];
  for (const [x0, y0, x1, y1, wd] of limbs) {
    ctx.lineWidth = wd;
    ctx.beginPath();
    ctx.moveTo(x0, y0);
    ctx.lineTo(x1, y1);
    ctx.stroke();
  }
  ctx.fillStyle = "#8a8075"; // the lit side of the trunk
  ctx.fillRect(14, 17, 1, 12);
}

/** The last apple tree: a leaning trunk, a crown still green, a few apples. */
function drawLastAppleTree(ctx: Ctx) {
  ctx.fillStyle = "#5e4630";
  ctx.beginPath(); // the trunk, leaning a little east
  ctx.moveTo(13, 30);
  ctx.lineTo(18, 30);
  ctx.lineTo(19, 18);
  ctx.lineTo(15, 18);
  ctx.closePath();
  ctx.fill();
  ctx.fillStyle = "#3f6b2a"; // the crown, ragged rather than round
  for (const [x, y, rad] of [[16, 12, 10.5], [9, 13, 5.5], [23, 12, 5.5], [16, 5, 5.5], [12, 19, 4], [20, 19, 4]] as const) {
    ctx.beginPath();
    ctx.arc(x, y, rad, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.fillStyle = "#5f8f3a"; // the lit leaves
  for (const [x, y] of [[11, 9], [17, 6], [22, 11], [13, 16], [19, 15]] as const) {
    ctx.beginPath();
    ctx.arc(x, y, 2.6, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.fillStyle = "#c0302a"; // a few small red apples
  for (const [x, y] of [[8, 14], [22, 8], [19, 18], [14, 5], [25, 14], [12, 11]] as const) {
    ctx.beginPath();
    ctx.arc(x, y, 1.5, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.fillStyle = "#f08070";
  for (const [x, y] of [[8, 14], [22, 8], [19, 18], [14, 5], [25, 14], [12, 11]] as const) ctx.fillRect(x - 1, y - 1, 1, 1);
}

/** The Entwife's sickle: a crescent of grey stone on a long pale haft. */
function drawEntwifeSickle(ctx: Ctx) {
  ctx.strokeStyle = "#b39a6a"; // the haft, of a wood that has not rotted
  ctx.lineWidth = 3;
  ctx.lineCap = "round";
  ctx.beginPath();
  ctx.moveTo(7, 28);
  ctx.lineTo(15, 18);
  ctx.stroke();
  ctx.fillStyle = "#8f9188"; // the blade, a crescent
  ctx.beginPath();
  ctx.moveTo(14, 19);
  ctx.quadraticCurveTo(14, 4, 28, 6);
  ctx.quadraticCurveTo(18, 8, 17, 20);
  ctx.closePath();
  ctx.fill();
  ctx.strokeStyle = "#c4c6bc"; // its worn inner edge
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(17, 18);
  ctx.quadraticCurveTo(18, 9, 27, 6.5);
  ctx.stroke();
}

/** The draught of Wellinghall: a great stone jar, golden-brown at the brim. */
function drawWellinghallDraught(ctx: Ctx) {
  ctx.fillStyle = "#7d7a70";
  ctx.beginPath();
  ctx.moveTo(9, 10);
  ctx.lineTo(23, 10);
  ctx.quadraticCurveTo(28, 19, 23, 29);
  ctx.lineTo(9, 29);
  ctx.quadraticCurveTo(4, 19, 9, 10);
  ctx.closePath();
  ctx.fill();
  ctx.fillStyle = "#a29e92"; // the lit side of the stone
  ctx.fillRect(9, 14, 2, 11);
  ctx.fillStyle = "#5c594f"; // two bands round it, and the lip
  ctx.fillRect(7, 16, 18, 1.5);
  ctx.fillRect(7, 23, 18, 1.5);
  ctx.fillRect(8, 8, 16, 3);
  ctx.fillStyle = "#b08a3a"; // the draught, golden-brown
  ctx.beginPath();
  ctx.ellipse(16, 9, 7, 1.8, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = "#e0c070"; // light on it
  ctx.fillRect(13, 8, 3, 1);
}
// --- end site 8 ---

// --- Site 6: Henneth Annûn (2026-10-16) ---
/**
 * The falls of Henneth Annûn (item 143). The oblique view stands them up
 * against the cliff they pour over, a storey taller than a tile, and runs
 * the water down them (see WATERFALL_ITEM in render.ts); flat, they are
 * this picture lying on their tile.
 */
export const WATERFALL_ITEM = 143;

/** The falls: a curtain of white water over blue, foaming at its foot. */
function drawWaterfall(ctx: Ctx) {
  const g = ctx.createLinearGradient(0, 0, 0, 32);
  g.addColorStop(0, "#3d6f9a");
  g.addColorStop(0.7, "#6e9fc4");
  g.addColorStop(1, "#d8ecf6");
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, 32, 32);
  // The lip it comes over, darker, and the streaks of the fall.
  ctx.fillStyle = "#2c5578";
  ctx.fillRect(0, 0, 32, 2);
  for (const [x, w, a] of [[2, 2, 0.55], [7, 3, 0.8], [12, 1.5, 0.5], [16, 3, 0.85], [21, 2, 0.6], [25, 3, 0.75], [29, 1.5, 0.5]] as const) {
    ctx.fillStyle = `rgba(235, 246, 252, ${a})`;
    ctx.fillRect(x, 1, w, 27);
  }
  // Foam where it strikes the pool.
  ctx.fillStyle = "#f4fbff";
  for (const [x, y, r] of [[5, 29, 4], [13, 30, 5], [22, 29, 5], [29, 30, 3]] as const) {
    ctx.beginPath();
    ctx.ellipse(x, y, r, 2.4, 0, 0, Math.PI * 2);
    ctx.fill();
  }
}

/** The war-banner of Harad (item 144): a black serpent on scarlet, on a spear-shaft hung with gold rings. */
function drawHaradBanner(ctx: Ctx) {
  ctx.strokeStyle = "#5a3a1e"; // the shaft
  ctx.lineWidth = 2;
  ctx.lineCap = "round";
  ctx.beginPath();
  ctx.moveTo(7, 30);
  ctx.lineTo(7, 3);
  ctx.stroke();
  ctx.fillStyle = "#c8c8c0"; // its spear-head
  ctx.beginPath();
  ctx.moveTo(7, 0);
  ctx.lineTo(9, 4);
  ctx.lineTo(5, 4);
  ctx.closePath();
  ctx.fill();
  ctx.fillStyle = "#b3201c"; // the scarlet cloth, swallow-tailed
  ctx.beginPath();
  ctx.moveTo(8, 5);
  ctx.lineTo(27, 6);
  ctx.lineTo(24, 13);
  ctx.lineTo(28, 20);
  ctx.lineTo(8, 19);
  ctx.closePath();
  ctx.fill();
  ctx.strokeStyle = "#141010"; // the black serpent on it
  ctx.lineWidth = 1.6;
  ctx.beginPath();
  ctx.moveTo(11, 16);
  ctx.bezierCurveTo(14, 9, 17, 18, 20, 11);
  ctx.quadraticCurveTo(21, 8, 23, 9);
  ctx.stroke();
  ctx.fillStyle = "#141010";
  ctx.beginPath();
  ctx.ellipse(23.2, 9.2, 1.6, 1.1, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = "#e0b030"; // gold rings down the shaft
  for (const y of [21, 24, 27]) ctx.fillRect(5.5, y, 3, 1.2);
}
// --- end site 6 ---

function drawItem(ctx: Ctx, id: number, color?: string) {
  switch (id) {
    case 1: // gold
      for (let i = 0; i < 3; i++) {
        ctx.fillStyle = "#c9a227";
        ctx.beginPath();
        ctx.ellipse(16 + (i - 1) * 5, 20 - i * 2, 6, 4, 0, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = "#f0d060";
        ctx.beginPath();
        ctx.ellipse(16 + (i - 1) * 5, 19 - i * 2, 4, 2.5, 0, 0, Math.PI * 2);
        ctx.fill();
      }
      break;
    case 2: // health potion
      drawPotion(ctx, "#c22", "#e55");
      break;
    case 3: // meat
      drawMeat(ctx);
      break;
    case 4: // club
      ctx.strokeStyle = "#6b4a2a";
      ctx.lineWidth = 4;
      ctx.lineCap = "round";
      ctx.beginPath();
      ctx.moveTo(8, 26);
      ctx.lineTo(22, 10);
      ctx.stroke();
      ctx.lineWidth = 7;
      ctx.beginPath();
      ctx.moveTo(19, 13);
      ctx.lineTo(24, 8);
      ctx.stroke();
      break;
    case 5: // sword
      ctx.strokeStyle = "#cfd4d8";
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.moveTo(10, 24);
      ctx.lineTo(24, 8);
      ctx.stroke();
      ctx.strokeStyle = "#7a5a2a";
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.moveTo(6, 22);
      ctx.lineTo(13, 27);
      ctx.moveTo(6, 28);
      ctx.lineTo(10, 24);
      ctx.stroke();
      break;
    case 6: // axe: the same battle axe, laid across the tile
      ctx.strokeStyle = "#6b4a2a";
      ctx.lineWidth = 3;
      ctx.lineCap = "round";
      ctx.beginPath();
      ctx.moveTo(7, 26);
      ctx.lineTo(22, 10);
      ctx.stroke();
      ctx.strokeStyle = "#3b2a17";
      ctx.lineWidth = 4;
      ctx.beginPath();
      ctx.moveTo(9, 24);
      ctx.lineTo(13, 20);
      ctx.stroke();
      // The head, set out along and across the haft rather than in flat
      // coordinates, so both bits sit square on a haft laid at an angle.
      {
        const hxc = 20, hyc = 12;
        const ux = 0.68, uy = -0.73; // along the haft, toward the tip
        const px = 0.73, py = 0.68; // square across it
        const at = (a: number, b: number): [number, number] =>
          [hxc + ux * a + px * b, hyc + uy * a + py * b];
        ctx.fillStyle = "#b8bec4";
        for (const side of [1, -1]) {
          const pts = [at(-3, side * 2), at(3, side * 2), at(4.5, side * 8), at(-4.5, side * 8)];
          ctx.beginPath();
          ctx.moveTo(pts[0][0], pts[0][1]);
          for (const [x, y] of pts.slice(1)) ctx.lineTo(x, y);
          ctx.closePath();
          ctx.fill();
        }
        ctx.fillStyle = "#6b7076";
        const [sx, sy] = at(-2.5, -2.5);
        ctx.fillRect(sx, sy, 5, 5);
      }
      break;
    case 7: // skull
      ctx.fillStyle = "#e8e4d8";
      ctx.beginPath();
      ctx.arc(16, 15, 8, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillRect(11, 18, 10, 6);
      // The sockets carry the colour of whoever lost the head, so a glance
      // at a skull says whose it is. Undimmed and unmixed: they are four
      // pixels square, and anything blended toward the dark they used to be
      // takes two players' colours to the same muddy grey at this size,
      // which is the one thing they must not do. A skull nobody can name —
      // one dropped before skulls knew, or off a character since deleted —
      // keeps the plain dark it always had.
      ctx.fillStyle = color ?? "#222";
      ctx.fillRect(11, 13, 4, 4);
      ctx.fillRect(17, 13, 4, 4);
      // The nose and teeth stay dark whoever it was: they are shadow rather
      // than eyes, and colouring them would leave nothing for the colour to
      // stand out against.
      ctx.fillStyle = "#222";
      ctx.fillRect(13, 21, 1, 3);
      ctx.fillRect(16, 21, 1, 3);
      ctx.fillRect(19, 21, 1, 3);
      break;
    case 8: // leather armor
      drawArmor(ctx, "#8a5a32", "#5a3a1e", "#a97245", (c) => {
        c.strokeStyle = "#5a3a1e";
        c.lineWidth = 1;
        c.setLineDash([2, 2]);
        c.beginPath();
        c.moveTo(10, 12);
        c.lineTo(10, 23);
        c.moveTo(22, 12);
        c.lineTo(22, 23);
        c.stroke();
        c.setLineDash([]);
      });
      break;
    case 9: // chain mail
      drawArmor(ctx, "#8e949a", "#4d5257", "#b9c0c6", (c) => {
        c.fillStyle = "#5f666c";
        for (let y = 11; y < 25; y += 3) {
          for (let x = 9 + ((y / 3) & 1); x < 24; x += 3) c.fillRect(x, y, 1, 1);
        }
      });
      break;
    case 10: // plate armor
      drawArmor(ctx, "#b6bcc4", "#5a6068", "#e2e8ee", (c) => {
        c.fillStyle = "#8d949c";
        c.fillRect(7, 17, 18, 2); // waist band
        c.beginPath(); // pauldrons
        c.ellipse(9, 12, 4, 3, 0, 0, Math.PI * 2);
        c.ellipse(23, 12, 4, 3, 0, 0, Math.PI * 2);
        c.fill();
      });
      break;
    case 33: // stick: a bent branch with the bark still on
      ctx.strokeStyle = "#6b4a2a";
      ctx.lineWidth = 3;
      ctx.lineCap = "round";
      ctx.beginPath();
      ctx.moveTo(7, 25);
      ctx.quadraticCurveTo(16, 18, 25, 8);
      ctx.stroke();
      ctx.strokeStyle = "#8a6438";
      ctx.lineWidth = 1.5;
      ctx.beginPath(); // a stub where a twig came off
      ctx.moveTo(17, 16);
      ctx.lineTo(22, 17);
      ctx.stroke();
      break;
    case 34: // rock: a fist-sized lump, lit from above
      ctx.fillStyle = "#7d7a74";
      ctx.beginPath();
      ctx.moveTo(9, 22);
      ctx.lineTo(12, 12);
      ctx.lineTo(20, 10);
      ctx.lineTo(24, 17);
      ctx.lineTo(21, 23);
      ctx.closePath();
      ctx.fill();
      ctx.fillStyle = "#9a968e"; // the lit facet
      ctx.beginPath();
      ctx.moveTo(12, 12);
      ctx.lineTo(20, 10);
      ctx.lineTo(19, 16);
      ctx.closePath();
      ctx.fill();
      ctx.fillStyle = "#5e5b56"; // the shaded one
      ctx.beginPath();
      ctx.moveTo(9, 22);
      ctx.lineTo(19, 18);
      ctx.lineTo(21, 23);
      ctx.closePath();
      ctx.fill();
      break;
    case 78: // boulder: the hill troll's, a rock the size of a head
      // The rock's own three facets, drawn across most of the tile: nobody
      // ever sees one lying about, but the bag, the look and the tooltip
      // need something to show, and this is the rock grown up.
      ctx.fillStyle = "#7d7a74";
      ctx.beginPath();
      ctx.moveTo(4, 27);
      ctx.lineTo(8, 9);
      ctx.lineTo(22, 5);
      ctx.lineTo(29, 17);
      ctx.lineTo(24, 28);
      ctx.closePath();
      ctx.fill();
      ctx.fillStyle = "#9a968e"; // the lit facet
      ctx.beginPath();
      ctx.moveTo(8, 9);
      ctx.lineTo(22, 5);
      ctx.lineTo(20, 15);
      ctx.closePath();
      ctx.fill();
      ctx.fillStyle = "#5e5b56"; // the shaded one
      ctx.beginPath();
      ctx.moveTo(4, 27);
      ctx.lineTo(20, 21);
      ctx.lineTo(24, 28);
      ctx.closePath();
      ctx.fill();
      break;
    case 32: { // bright sword: a pale blade that keeps its own light
      // Hilt and blade are built on one axis: the guard square across the
      // blade and the grip straight back down it, rather than each drawn
      // at whatever angle looked right on its own. A grip a few degrees
      // off its blade reads as a sword bent at the guard, which is what
      // this one looked like.
      const bx = 10, by = 24, tx = 26, ty = 5; // the guard, and the point
      const len = Math.hypot(tx - bx, ty - by);
      const ux = (tx - bx) / len, uy = (ty - by) / len; // along the blade
      const px = -uy, py = ux; // square across it
      const at = (a: number, b: number): [number, number] => [bx + ux * a + px * b, by + uy * a + py * b];
      ctx.fillStyle = BRIGHT_GLOW;
      bladePath(ctx, bx, by, ...at(len + 1, 0), 2.2); // the glow, a little wider all round
      ctx.fill();
      ctx.fillStyle = BRIGHT_BLADE;
      bladePath(ctx, bx, by, tx, ty, 1.2); // the blade, brighter than its glow
      ctx.fill();
      ctx.strokeStyle = "#c9a227"; // a gold hilt, barrow goods
      ctx.lineWidth = 3;
      ctx.lineCap = "butt";
      ctx.beginPath();
      ctx.moveTo(...at(0, -5));
      ctx.lineTo(...at(0, 5)); // the guard, across the blade
      ctx.moveTo(...at(-1, 0));
      ctx.lineTo(...at(-7, 0)); // the grip, in line with it
      ctx.stroke();
      break;
    }
    case 42: {
      // fire sword: the bright sword's shape with fire along it instead of
      // light. Built on one axis the same way — the guard square across
      // the blade and the grip straight back down it — because a grip a
      // few degrees off its blade reads as a sword bent at the hilt.
      //
      // Three passes down the same line, widest and dullest first: a red
      // haze standing off the edges, orange inside that, and a pale core
      // where the steel still shows through. That is a flame seen from
      // above, and it is why the blade is drawn with no metal of its own.
      const bx = 10, by = 24, tx = 26, ty = 5;
      const len = Math.hypot(tx - bx, ty - by);
      const ux = (tx - bx) / len, uy = (ty - by) / len;
      const px = -uy, py = ux;
      const at = (a: number, b: number): [number, number] => [bx + ux * a + px * b, by + uy * a + py * b];
      for (const [half, color] of [[3, "#8c1f08"], [2.2, "#e0641a"], [1.4, "#ffc24a"], [0.7, "#fff0c0"]] as const) {
        ctx.fillStyle = color;
        bladePath(ctx, bx, by, ...at(len + (half > 2 ? 2 : 0), 0), half);
        ctx.fill();
      }
      ctx.strokeStyle = "#3a3330"; // a blackened hilt: it has been through this before
      ctx.lineWidth = 3;
      ctx.lineCap = "butt";
      ctx.beginPath();
      ctx.moveTo(...at(0, -5));
      ctx.lineTo(...at(0, 5)); // the guard
      ctx.moveTo(...at(-1, 0));
      ctx.lineTo(...at(-7, 0)); // the grip, in line with it
      ctx.stroke();
      break;
    }
    case 43: {
      // giant sword: the same construction as the other two — guard square
      // across the blade, grip straight back down it — but drawn corner to
      // corner of the tile and twice as wide in the blade, because the
      // only thing this sprite has to say is that it is too big. Where a
      // sword lies across the middle of its tile, this one runs out of
      // room.
      //
      // The numbers are GIANT_SWORD_LIE, stated once, because the ground
      // sprite is drawn from them twice over: once here, and once as the
      // box giantSwordLieBox works out from the same numbers so that
      // groundSprite can centre the whole sword on its tile.
      const { bx, by, tx, ty, edge, guard, grip, pommel, pommelR } = GIANT_SWORD_LIE;
      const { len, at } = lieFrame();
      ctx.fillStyle = "#6b7178"; // the dull edge, standing off the bright face
      bladePath(ctx, bx, by, tx, ty, edge);
      ctx.fill();
      ctx.fillStyle = "#c3cad1"; // grey steel, no shine of its own: it is a slab
      bladePath(ctx, bx, by, ...at(len - 1, 0), 2.2);
      ctx.fill();
      ctx.strokeStyle = "#e8edf2"; // one highlight down the fuller
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(...at(2, 0));
      ctx.lineTo(...at(len - 4, 0));
      ctx.stroke();
      ctx.strokeStyle = "#4a3a22"; // iron furniture, plain and heavy
      ctx.lineWidth = GIANT_SWORD_LIE.iron;
      ctx.lineCap = "butt";
      ctx.beginPath();
      ctx.moveTo(...at(0, -guard)); // a guard wider than a sword is long in the grip
      ctx.lineTo(...at(0, guard));
      ctx.moveTo(...at(-1, 0));
      ctx.lineTo(...at(-grip, 0)); // and a grip long enough for two hands
      ctx.stroke();
      ctx.fillStyle = "#4a3a22";
      ctx.beginPath(); // the pommel, a counterweight for all that blade
      ctx.arc(...at(-pommel, 0), pommelR, 0, Math.PI * 2);
      ctx.fill();
      break;
    }
    case 31: // spidersilk cloak: a cape in pale silk, ragged at the hem, a web on it
      drawCape(ctx, SILK_CLOAK, true, (c) => {
        // A web spun faintly over the far side: spokes from a hub, and a ring.
        c.strokeStyle = SILK_CLOAK.web;
        c.lineWidth = 0.6;
        c.beginPath();
        const hx = 22, hy = 19;
        for (let a = 0; a < 6; a++) {
          const t = (a / 6) * Math.PI * 2;
          c.moveTo(hx, hy);
          c.lineTo(hx + Math.cos(t) * 4, hy + Math.sin(t) * 4);
        }
        c.moveTo(hx + 2.5, hy);
        c.arc(hx, hy, 2.5, 0, Math.PI * 2);
        c.stroke();
        c.fillStyle = SILK_CLOAK.clasp; // the spider-green clasp at the throat
        c.fillRect(14.5, 9, 3, 2.5);
      });
      break;
    // 11 was the rusty key, gone with its quest on 2026-10-07 ("remove the
    // rusty key, and the related quest"); the id is held, and nothing is one.
    case 12: // chest: the quest chests
      drawChest(ctx);
      break;
    case 27: // depot box: a square-edged chest of its own (2026-10-10)
      drawDepotBox(ctx);
      break;
    case 16: // ominous pile of bones
      // A heap: short lengths jumbled at shallow angles, piled towards the
      // middle and settling wider at the base, with a skull half-buried in it.
      ctx.fillStyle = "#b9b2a0";
      for (const [x, y, len, rot] of [
        [9, 25, 13, 0.12],
        [20, 26, 11, -0.18],
        [7, 22, 10, -0.5],
        [24, 22, 9, 0.55],
        [13, 24, 14, -0.06],
      ] as [number, number, number, number][]) {
        ctx.save();
        ctx.translate(x, y);
        ctx.rotate(rot);
        ctx.fillRect(-len / 2, -1.5, len, 3);
        ctx.restore();
      }
      ctx.fillStyle = "#d9d2be";
      for (const [x, y, len, rot] of [
        [14, 21, 12, 0.3],
        [19, 19, 10, -0.42],
        [11, 18, 9, 0.62],
      ] as [number, number, number, number][]) {
        ctx.save();
        ctx.translate(x, y);
        ctx.rotate(rot);
        ctx.fillRect(-len / 2, -1.5, len, 3);
        // Knuckled ends, which is what makes a bone read as a bone.
        ctx.beginPath();
        ctx.arc(-len / 2, 0, 2, 0, Math.PI * 2);
        ctx.arc(len / 2, 0, 2, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
      }
      ctx.fillStyle = "#efe8d6";
      ctx.beginPath();
      ctx.arc(16, 14, 5, 0, Math.PI * 2); // skull sitting on the heap
      ctx.fill();
      ctx.fillRect(13, 16, 6, 3);
      // Something is still watching out of it: dark sockets with a red light
      // in them, and a faint glow so it carries at a glance.
      ctx.fillStyle = "#241c14";
      ctx.fillRect(13, 12, 3, 3);
      ctx.fillRect(17, 12, 3, 3);
      ctx.globalAlpha = 0.35;
      ctx.fillStyle = "#f00";
      for (const x of [14.5, 18.5]) {
        ctx.beginPath();
        ctx.arc(x, 13.5, 3, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.globalAlpha = 1;
      ctx.fillStyle = "#ff2b2b";
      ctx.fillRect(14, 13, 2, 2);
      ctx.fillRect(18, 13, 2, 2);
      break;
    case 21: // bow
      // The stave curves away from the string; an arrow lies nocked across
      // them so the thing reads as a weapon rather than a bracket.
      ctx.strokeStyle = "#8a5a2a";
      ctx.lineWidth = 3;
      ctx.lineCap = "round";
      ctx.beginPath();
      ctx.moveTo(9, 5);
      ctx.quadraticCurveTo(24, 16, 9, 27);
      ctx.stroke();
      ctx.strokeStyle = "#e8e0c8";
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(9, 5);
      ctx.lineTo(9, 27);
      ctx.stroke();
      ctx.strokeStyle = "#6b4a2a";
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.moveTo(6, 16);
      ctx.lineTo(26, 16);
      ctx.stroke();
      ctx.fillStyle = "#cfd4d8";
      ctx.beginPath();
      ctx.moveTo(29, 16);
      ctx.lineTo(24, 13.5);
      ctx.lineTo(24, 18.5);
      ctx.closePath();
      ctx.fill();
      ctx.fillStyle = "#c8c0a0";
      ctx.fillRect(6, 14, 3, 4);
      break;
    case 40: { // spear: a long ash shaft and a leaf of steel
      ctx.strokeStyle = "#8a6a46";
      ctx.lineWidth = 2.5;
      ctx.lineCap = "round";
      ctx.beginPath();
      ctx.moveTo(6, 27);
      ctx.lineTo(21, 10);
      ctx.stroke();
      ctx.strokeStyle = "#5c6166"; // the binding at the neck
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.moveTo(18, 14);
      ctx.lineTo(21.5, 17);
      ctx.moveTo(20, 11);
      ctx.lineTo(23.5, 14);
      ctx.stroke();
      ctx.fillStyle = "#cfd4d8";
      bladePath(ctx, 20, 11, 28, 3, 2.4); // the leaf
      ctx.fill();
      ctx.fillStyle = "#eef2f6";
      bladePath(ctx, 20.5, 11.5, 27, 5, 0.9);
      ctx.fill();
      break;
    }
    case 171: { // wizard's staff: a long pale-grey shaft, a knotted head with a glint in it
      ctx.strokeStyle = WIZARD_STAFF.wood;
      ctx.lineWidth = 2.5;
      ctx.lineCap = "round";
      ctx.beginPath();
      ctx.moveTo(5, 30);
      ctx.lineTo(24, 7);
      ctx.stroke();
      ctx.strokeStyle = WIZARD_STAFF.grain; // the worn grip
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(12, 23);
      ctx.lineTo(16, 18);
      ctx.stroke();
      ctx.fillStyle = WIZARD_STAFF.knot; // the knotted head
      ctx.beginPath();
      ctx.arc(25, 6, 3.4, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = WIZARD_STAFF.glint;
      ctx.beginPath();
      ctx.arc(25, 6, 1.4, 0, Math.PI * 2);
      ctx.fill();
      break;
    }
    case 170: { // Uruk-hai pike: a long dark shaft, iron-banded, and a narrow steel point
      ctx.strokeStyle = PIKE_SHAFT;
      ctx.lineWidth = 2.5;
      ctx.lineCap = "round";
      ctx.beginPath();
      ctx.moveTo(3, 30);
      ctx.lineTo(23, 9);
      ctx.stroke();
      ctx.strokeStyle = URUK.rim; // the iron bands down it
      ctx.lineWidth = 1.2;
      ctx.beginPath();
      for (const t of [0.08, 0.45, 0.82]) {
        const x = 3 + 20 * t, y = 30 - 21 * t;
        ctx.moveTo(x - 1.6, y - 1.5);
        ctx.lineTo(x + 1.6, y + 1.5);
      }
      ctx.stroke();
      ctx.strokeStyle = URUK.iron; // the lugs under the head
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.moveTo(20, 9);
      ctx.lineTo(24.5, 13.5);
      ctx.stroke();
      ctx.fillStyle = URUK.edge;
      bladePath(ctx, 22, 10, 30, 2, 1.4); // the point, long and narrow
      ctx.fill();
      break;
    }
    case 39: { // carapace: a spider's back plate, cut free and strapped on
      drawArmor(ctx, "#2f2a33", "#171419", "#6d5f72", (c) => {
        // The chitin's own banding, and the pale joins between plates.
        c.fillStyle = "#3d3644";
        c.fillRect(10, 12, 12, 2.5);
        c.fillRect(10, 17, 12, 2.5);
        c.fillStyle = "#8f7f96";
        c.fillRect(15, 9, 2, 16);
        // The hairs, which is the part nobody enjoys.
        c.strokeStyle = "#0f0d12";
        c.lineWidth = 1;
        c.beginPath();
        for (const [x, y] of [[11, 15], [21, 15], [12, 21], [20, 21]] as const) {
          c.moveTo(x, y);
          c.lineTo(x + (x < 16 ? -3 : 3), y - 3);
        }
        c.stroke();
      });
      break;
    }
    case 38: { // tower shield: a slab of oak, banded, nearly the tile's height
      ctx.fillStyle = "#7a5a34";
      ctx.beginPath();
      ctx.moveTo(9, 3);
      ctx.lineTo(23, 3);
      ctx.lineTo(23, 24);
      ctx.quadraticCurveTo(23, 29, 16, 30);
      ctx.quadraticCurveTo(9, 29, 9, 24);
      ctx.closePath();
      ctx.fill();
      ctx.strokeStyle = "#5a3a1e"; // the boards under the iron
      ctx.lineWidth = 1;
      ctx.beginPath();
      for (const x of [13, 19]) {
        ctx.moveTo(x, 4);
        ctx.lineTo(x, 28);
      }
      ctx.stroke();
      ctx.fillStyle = "#6f767e"; // the bands: two across, one down the spine
      ctx.fillRect(9, 7, 14, 3);
      ctx.fillRect(9, 20, 14, 3);
      ctx.fillRect(15, 3, 2, 26);
      ctx.strokeStyle = "#4a4f55";
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(9, 3);
      ctx.lineTo(23, 3);
      ctx.lineTo(23, 24);
      ctx.quadraticCurveTo(23, 29, 16, 30);
      ctx.quadraticCurveTo(9, 29, 9, 24);
      ctx.closePath();
      ctx.stroke();
      break;
    }
    case 37: { // buckler: a little round shield, hide over a wooden disc
      ctx.fillStyle = "#8a6a46";
      ctx.beginPath();
      ctx.arc(16, 17, 9, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = "#5a3a1e"; // the hide's stitched rim
      ctx.lineWidth = 2;
      ctx.stroke();
      ctx.strokeStyle = "#6e5336";
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.arc(16, 17, 5.5, 0, Math.PI * 2);
      ctx.stroke();
      ctx.fillStyle = "#8d949c"; // the iron boss, all the metal it has
      ctx.beginPath();
      ctx.arc(16, 17, 2.6, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = "#c3c9d0";
      ctx.fillRect(14, 15, 1.6, 1.6);
      break;
    }
    case 22: // wooden shield
      ctx.fillStyle = "#8a5a32";
      ctx.beginPath();
      ctx.moveTo(8, 6);
      ctx.lineTo(24, 6);
      ctx.lineTo(24, 18);
      ctx.quadraticCurveTo(24, 26, 16, 28);
      ctx.quadraticCurveTo(8, 26, 8, 18);
      ctx.closePath();
      ctx.fill();
      ctx.strokeStyle = "#5a3a1e";
      ctx.lineWidth = 1;
      ctx.beginPath(); // planks
      for (const x of [12, 16, 20]) {
        ctx.moveTo(x, 6);
        ctx.lineTo(x, 27);
      }
      ctx.stroke();
      ctx.strokeStyle = "#5c6166";
      ctx.lineWidth = 2;
      ctx.beginPath(); // iron rim
      ctx.moveTo(8, 6);
      ctx.lineTo(24, 6);
      ctx.lineTo(24, 18);
      ctx.quadraticCurveTo(24, 26, 16, 28);
      ctx.quadraticCurveTo(8, 26, 8, 18);
      ctx.closePath();
      ctx.stroke();
      ctx.fillStyle = "#8d949c";
      ctx.beginPath(); // boss
      ctx.arc(16, 16, 3.5, 0, Math.PI * 2);
      ctx.fill();
      break;
    case 23: // steel shield
      ctx.fillStyle = "#b6bcc4";
      ctx.beginPath(); // a kite: straight top, tapering to a point
      ctx.moveTo(7, 6);
      ctx.lineTo(25, 6);
      ctx.lineTo(25, 16);
      ctx.quadraticCurveTo(25, 25, 16, 29);
      ctx.quadraticCurveTo(7, 25, 7, 16);
      ctx.closePath();
      ctx.fill();
      ctx.fillStyle = "#e2e8ee";
      ctx.fillRect(10, 9, 3, 12); // a highlight down one side
      ctx.strokeStyle = "#5a6068";
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(7, 6);
      ctx.lineTo(25, 6);
      ctx.lineTo(25, 16);
      ctx.quadraticCurveTo(25, 25, 16, 29);
      ctx.quadraticCurveTo(7, 25, 7, 16);
      ctx.closePath();
      ctx.stroke();
      ctx.strokeStyle = "#8d949c";
      ctx.lineWidth = 1.5;
      ctx.beginPath(); // a cross of ribs
      ctx.moveTo(16, 8);
      ctx.lineTo(16, 26);
      ctx.moveTo(9, 14);
      ctx.lineTo(23, 14);
      ctx.stroke();
      break;
    case 35: { // anvil: the smith's, on its block, with the forge glowing behind
      ctx.fillStyle = "#3a2a18";
      ctx.fillRect(8, 23, 16, 6); // the stump it stands on
      ctx.fillStyle = "#4a4f55";
      ctx.beginPath(); // the body: waisted, with a flat face and a horn
      ctx.moveTo(9, 22);
      ctx.lineTo(12, 17);
      ctx.lineTo(20, 17);
      ctx.lineTo(23, 22);
      ctx.closePath();
      ctx.fill();
      ctx.fillStyle = "#63696f";
      ctx.fillRect(7, 12, 18, 5); // the face
      ctx.beginPath();
      ctx.moveTo(7, 12); // the horn, off the near end
      ctx.lineTo(2, 15);
      ctx.lineTo(7, 17);
      ctx.closePath();
      ctx.fill();
      ctx.fillStyle = "#ff8a3c"; // coals, seen past it
      ctx.fillRect(21, 8, 6, 3);
      ctx.fillStyle = "#ffd06a";
      ctx.fillRect(23, 9, 2, 1);
      break;
    }
    case 52: {
      // The undercroft's door, shut: a slab of dark oak filling its frame,
      // three iron bands across it and a ring to pull it by. Seen face on,
      // the way a door in the wall ahead of you is, and standing the full
      // tile so that it reads as something in the way rather than lying
      // on the floor.
      ctx.fillStyle = "#3a3a3a"; // the stone jambs either side
      ctx.fillRect(2, 2, 3, 28);
      ctx.fillRect(27, 2, 3, 28);
      ctx.fillRect(2, 2, 28, 3); // and the lintel
      ctx.fillStyle = "#4a3219"; // the oak
      ctx.fillRect(5, 5, 22, 25);
      ctx.fillStyle = "#3a2712"; // the planks' seams
      for (const x of [10, 16, 22]) ctx.fillRect(x, 5, 1, 25);
      ctx.fillStyle = "#6d7378"; // the iron bands
      for (const y of [8, 17, 26]) ctx.fillRect(5, y, 22, 3);
      ctx.fillStyle = "#4b5054"; // the rivets along them
      for (const y of [9, 18, 27]) for (const x of [7, 13, 19, 25]) ctx.fillRect(x, y, 1, 1);
      ctx.strokeStyle = "#8a8f94"; // the ring
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.arc(21, 14, 2.5, 0, Math.PI * 2);
      ctx.stroke();
      break;
    }
    case 53: {
      // The same door standing open: the frame with the dark of the
      // doorway through it, and the door itself swung back against the
      // left jamb, seen edge on — a narrow strip of oak with the ends of
      // its bands showing. Nothing stands the full width of the tile, so
      // it reads as a way through.
      ctx.fillStyle = "#3a3a3a"; // the jambs and lintel, as when shut
      ctx.fillRect(2, 2, 3, 28);
      ctx.fillRect(27, 2, 3, 28);
      ctx.fillRect(2, 2, 28, 3);
      ctx.fillStyle = "#15120e"; // the dark beyond the door
      ctx.fillRect(5, 5, 22, 25);
      ctx.fillStyle = "#4a3219"; // the door, swung back, edge on
      ctx.fillRect(5, 4, 5, 26);
      ctx.fillStyle = "#6d7378"; // the ends of its three bands
      for (const y of [7, 16, 25]) ctx.fillRect(5, y, 5, 3);
      ctx.fillStyle = "#2b1d0e"; // its shadow on the jamb
      ctx.fillRect(10, 5, 2, 25);
      break;
    }
    case 55: {
      // The Doors of Durin, shut: grey stone set flush in the mountain's
      // face, the seam down the middle all but invisible, and over them
      // the arch drawn in silver lines — the two pillars, the crown and
      // the star — that shine where light falls on them. Asked for on
      // 2026-09-25: "Implement the moria door".
      ctx.fillStyle = "#6f7275"; // the face of the rock round them
      ctx.fillRect(0, 0, 32, 32);
      ctx.fillStyle = "#5a5d60"; // the doors
      ctx.fillRect(4, 6, 24, 26);
      ctx.fillStyle = "#505356"; // the seam between the leaves
      ctx.fillRect(15.5, 8, 1, 24);
      ctx.strokeStyle = "#dfe8f2"; // ithildin: the arch and its pillars
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(6.5, 31);
      ctx.lineTo(6.5, 13);
      ctx.arc(16, 13, 9.5, Math.PI, 0);
      ctx.lineTo(25.5, 31);
      ctx.stroke();
      ctx.beginPath(); // the crown
      ctx.moveTo(12, 9);
      ctx.lineTo(13, 6);
      ctx.lineTo(16, 8);
      ctx.lineTo(19, 6);
      ctx.lineTo(20, 9);
      ctx.stroke();
      ctx.fillStyle = "#f4fbff"; // the star of the house of Fëanor
      ctx.fillRect(15, 17, 2, 2);
      ctx.fillRect(14, 17.5, 4, 1);
      ctx.fillRect(15.5, 16, 1, 4);
      break;
    }
    case 56: {
      // The same doors standing open: the leaves swung back to either
      // side of the doorway, and the dark of the stair hall between them.
      ctx.fillStyle = "#6f7275";
      ctx.fillRect(0, 0, 32, 32);
      ctx.fillStyle = "#0e0e10"; // the dark within
      ctx.fillRect(7, 6, 18, 26);
      ctx.fillStyle = "#5a5d60"; // the leaves, edge on
      ctx.fillRect(4, 6, 3, 26);
      ctx.fillRect(25, 6, 3, 26);
      ctx.strokeStyle = "#9aa3ac"; // the arch, dimmer with the doors apart
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.arc(16, 13, 9.5, Math.PI, 0);
      ctx.stroke();
      break;
    }
    case 41: {
      // weathered board: the bridge notice, out in the weather rather than
      // hung on a wall, so it stands on its own post and leans. Silvered
      // timber, not the painted oak of a town sign — nobody has been out
      // here to repaint it since the span went.
      ctx.fillStyle = "#6b6157";
      ctx.fillRect(15, 17, 2.5, 12); // the post, driven into the ground
      ctx.save();
      ctx.translate(16, 13);
      ctx.rotate(-0.09); // the lean of something nobody straightens
      ctx.fillStyle = "#8d8a80";
      ctx.fillRect(-12, -8, 24, 15);
      ctx.strokeStyle = "#5d5a52";
      ctx.lineWidth = 1.5;
      ctx.strokeRect(-11.25, -7.25, 22.5, 13.5);
      ctx.strokeStyle = "#6f6c64"; // the grain, and one split across it
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(-11, -2.5);
      ctx.lineTo(11, -3.5);
      ctx.moveTo(-11, 2.5);
      ctx.lineTo(11, 1.5);
      ctx.stroke();
      ctx.fillStyle = "#3f3b33"; // cut letters, deep enough to still read
      ctx.fillRect(-8, -5, 16, 2);
      ctx.fillRect(-8, 0, 10, 2);
      ctx.restore();
      break;
    }
    case 24: // fishing rod
      ctx.strokeStyle = "#a97245";
      ctx.lineWidth = 2.5;
      ctx.lineCap = "round";
      ctx.beginPath(); // the rod, thick at the grip and bending at the tip
      ctx.moveTo(6, 27);
      ctx.lineTo(20, 12);
      ctx.quadraticCurveTo(24, 8, 27, 7);
      ctx.stroke();
      ctx.strokeStyle = "#6b4a2a";
      ctx.lineWidth = 4;
      ctx.beginPath();
      ctx.moveTo(6, 27);
      ctx.lineTo(10, 23);
      ctx.stroke();
      ctx.strokeStyle = "#e8e8e8";
      ctx.lineWidth = 1;
      ctx.beginPath(); // the line, hanging from the tip
      ctx.moveTo(27, 7);
      ctx.lineTo(27, 21);
      ctx.stroke();
      ctx.strokeStyle = "#cfd4d8";
      ctx.lineWidth = 1.5;
      ctx.beginPath(); // the hook
      ctx.arc(25, 21, 2.5, -Math.PI / 2, Math.PI * 0.9);
      ctx.stroke();
      break;
    case 25: // fish
      ctx.fillStyle = "#7fa3c4";
      ctx.beginPath();
      ctx.ellipse(15, 17, 9, 5, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.beginPath(); // tail
      ctx.moveTo(23, 17);
      ctx.lineTo(28, 12);
      ctx.lineTo(28, 22);
      ctx.closePath();
      ctx.fill();
      ctx.fillStyle = "#c9dbe9";
      ctx.beginPath(); // pale belly
      ctx.ellipse(15, 19, 7, 2.5, 0, 0, Math.PI);
      ctx.fill();
      ctx.fillStyle = "#222";
      ctx.beginPath();
      ctx.arc(9, 16, 1.2, 0, Math.PI * 2);
      ctx.fill();
      break;
    case 54: // mushroom: what the dwarves eat, and thirty seconds of food
      drawMushroom(ctx);
      break;
    case 44: { // demon shield: the nastiest thing anybody ever got behind
      // A heater shield of black iron with the demon's own horns still on
      // the corners and its face beaten into the front. Every other shield
      // in the world is a flat thing you hide behind; this one is looking
      // back at whatever is swinging at it, which is what fourteen points
      // of block ought to feel like from the other side.
      const face = () => {
        ctx.beginPath();
        ctx.moveTo(7, 7);
        ctx.lineTo(25, 7);
        ctx.lineTo(25, 17);
        ctx.quadraticCurveTo(25, 26, 16, 30);
        ctx.quadraticCurveTo(7, 26, 7, 17);
        ctx.closePath();
      };
      demonHorn(ctx, 10, 9, -5, -8, 2.4); // the horns, under the rim they sit in
      demonHorn(ctx, 22, 9, 5, -8, 2.4);
      ctx.fillStyle = "#2b1013";
      face();
      ctx.fill();
      ctx.fillStyle = "#3f181c"; // the brow, raised out of the iron
      ctx.beginPath();
      ctx.moveTo(9, 11);
      ctx.lineTo(23, 11);
      ctx.lineTo(21, 17);
      ctx.lineTo(11, 17);
      ctx.closePath();
      ctx.fill();
      ctx.fillStyle = DEMON.fire; // and the fire behind the eyes
      ctx.fillRect(10, 13, 5, 3);
      ctx.fillRect(17, 13, 5, 3);
      ctx.fillStyle = DEMON.ember;
      ctx.fillRect(11, 14, 2, 1);
      ctx.fillRect(18, 14, 2, 1);
      ctx.fillStyle = "#150708";
      ctx.fillRect(10, 19, 12, 5); // the mouth
      ctx.fillStyle = DEMON.horn;
      for (const x of [10, 13, 16, 19]) ctx.fillRect(x, 19, 2, 5); // the teeth in it
      ctx.strokeStyle = "#6a564a"; // a rim of old iron over the lot
      ctx.lineWidth = 2;
      face();
      ctx.stroke();
      break;
    }
    case 45: { // soul orb: about forty points of mana (a hundred and twenty while the pools were tripled, 2026-09-29 to 2026-10-10)
      // A sphere lit from the inside, and nothing else — no neck, no
      // stopper, nothing to hold it by, since it is meant to be crushed
      // rather than poured. The health potion lying beside it in the bag is
      // a corked red flask; this is round, whole and the blue of the mana
      // bar it fills, so that reaching into a bag in a hurry can never
      // bring up the wrong one.
      const cx = 16, cy = 17;
      for (const [r, col] of [[8, "#12305e"], [6.8, "#1f56a8"], [5, "#3f9ae0"], [3, "#a6e2ff"], [1.5, "#f4fcff"]] as const) {
        ctx.fillStyle = col;
        ctx.beginPath();
        ctx.arc(cx, cy, r, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.strokeStyle = "#bce6ff"; // the thing caught in it, wound round the light
      ctx.lineWidth = 1.2;
      ctx.beginPath();
      ctx.moveTo(cx - 4.5, cy + 3.5);
      ctx.quadraticCurveTo(cx - 6, cy - 3, cx, cy - 5);
      ctx.quadraticCurveTo(cx + 6, cy - 3, cx + 4, cy + 3.5);
      ctx.stroke();
      ctx.fillStyle = "#eaf8ff";
      ctx.beginPath(); // the glint, where the light lands on the outside of it
      ctx.ellipse(cx - 3.5, cy - 4.5, 2.2, 1.3, -0.6, 0, Math.PI * 2);
      ctx.fill();
      break;
    }
    case 46: { // demon horn: five hundred gold, and good for nothing else
      // The same horn the creature wears, off the creature and drawn at the
      // size of a trophy: heavy at the cut end, ringed along its length, and
      // banded in gold by whoever last sold one. The gold is the point of
      // the sprite. Nothing about a horn on its own says money, and this is
      // the most valuable thing in the world — a player who has one should
      // know what it is worth from the square it is sitting in.
      // It curls, which is the whole of why it is drawn here rather than
      // borrowed from the creature: the one on the demon's head is small
      // enough that a slight bend is all that fits, and a straight horn laid
      // across a tile is a cone. So the shape is walked along a quarter
      // circle instead, thick at the cut end and tapering to nothing, with
      // the growth rings crossing it and a highlight down the outside edge.
      const CX = 9, CY = 9, R = 15;
      const half = (t: number) => 6 * Math.pow(1 - t, 1.15) + 0.4;
      // A strip of the horn between two points along it, from a fraction of
      // its thickness on the outside edge to a fraction on the inside: the
      // whole horn is one of these, and so is every ring and the gold.
      const band = (t0: number, t1: number, fo: number, fi: number) => {
        ctx.beginPath();
        for (let i = 0; i <= 12; i++) {
          const t = t0 + ((t1 - t0) * i) / 12, a = (Math.PI / 2) * (1 - t);
          ctx.lineTo(CX + Math.cos(a) * (R + half(t) * fo), CY + Math.sin(a) * (R + half(t) * fo));
        }
        for (let i = 12; i >= 0; i--) {
          const t = t0 + ((t1 - t0) * i) / 12, a = (Math.PI / 2) * (1 - t);
          ctx.lineTo(CX + Math.cos(a) * (R + half(t) * fi), CY + Math.sin(a) * (R + half(t) * fi));
        }
        ctx.closePath();
        ctx.fill();
      };
      ctx.fillStyle = "#c2b294";
      band(0, 1, 1, -1);
      ctx.fillStyle = "#8d8168"; // the underside, in shadow
      band(0, 1, -0.45, -1);
      ctx.fillStyle = "#ebe2c9"; // and the light along the top of the curve
      band(0, 1, 1, 0.4);
      ctx.fillStyle = "#6f6450"; // growth rings, closer together towards the tip
      for (const t of [0.3, 0.5, 0.66, 0.79]) band(t, t + 0.045, 1, -1);
      ctx.fillStyle = "#a8821c"; // the gold over the cut end
      band(0, 0.17, 1.12, -1.12);
      ctx.fillStyle = "#f0d060";
      band(0, 0.17, 1.12, 0.15);
      break;
    }
    case 47: { // demon helmet: the demon's own horns, and this time they are on you
      // A skull-cap of the same black iron as the demon shield, with the
      // horns rooted in it where they were rooted in the demon, so the
      // three things off it read as one set lying side by side in a bag.
      // The cap wants something on the front of it or it is a bowl: a
      // raised brow, a nasal bar down the middle and a cheek guard either
      // side, which are what make an iron dome read as a helmet at this
      // size. The only fire on it is at the roots of the horns. A helmet
      // on the ground has nobody behind it, so unlike the shield it has
      // no eyes to light.
      const cap = () => {
        ctx.beginPath();
        ctx.moveTo(7, 20);
        ctx.quadraticCurveTo(7, 8, 16, 8);
        ctx.quadraticCurveTo(25, 8, 25, 20);
        ctx.lineTo(25, 24);
        ctx.lineTo(21, 27); // the cheek guards, hanging below the rim
        ctx.lineTo(21, 22);
        ctx.lineTo(11, 22);
        ctx.lineTo(11, 27);
        ctx.lineTo(7, 24);
        ctx.closePath();
      };
      demonHorn(ctx, 10, 12, -6, -9, 2.4); // the horns, under the iron they sit in
      demonHorn(ctx, 22, 12, 6, -9, 2.4);
      ctx.fillStyle = "#2b1013";
      cap();
      ctx.fill();
      ctx.fillStyle = "#3f181c"; // the brow, raised out of the iron
      ctx.fillRect(9, 17, 14, 3);
      ctx.fillRect(15, 17, 2, 10); // and the nasal, down between where the eyes go
      ctx.fillStyle = DEMON.fire; // the fire, where the horns leave the iron
      ctx.fillRect(9, 11, 3, 2);
      ctx.fillRect(20, 11, 3, 2);
      ctx.fillStyle = DEMON.ember;
      ctx.fillRect(10, 11, 1, 1);
      ctx.fillRect(21, 11, 1, 1);
      ctx.strokeStyle = "#6a564a"; // a rim of old iron over the lot
      ctx.lineWidth = 2;
      cap();
      ctx.stroke();
      break;
    }
    case 48: { // forge: the lit hearth the yard is named for
      // The one thing in the yard that has to read at a glance, because it
      // is what tells you the place is a forge rather than a yard with
      // ironwork in it. So it is drawn as heat first and masonry second: a
      // low stone box, and inside it a bed of coals bright enough to throw
      // light back up the chimney breast above.
      ctx.fillStyle = "#4a4540"; // the chimney breast, rising off the back
      ctx.fillRect(9, 2, 14, 10);
      ctx.fillStyle = "#3a3630";
      ctx.fillRect(9, 2, 14, 2); // its shadowed top edge
      ctx.fillStyle = "#6b6259"; // the hearth itself: coursed stone
      ctx.fillRect(4, 12, 24, 16);
      ctx.strokeStyle = "#4e463e";
      ctx.lineWidth = 1;
      for (const y of [17.5, 22.5]) {
        ctx.beginPath();
        ctx.moveTo(4, y);
        ctx.lineTo(28, y);
        ctx.stroke();
      }
      ctx.fillStyle = "#2a2420"; // the fire hole, cut back into the stone
      ctx.fillRect(9, 13, 14, 9);
      // The coals: a bed of them, hottest in the middle, with the glow
      // washing up the breast above. Three bands rather than a gradient,
      // because the whole art style is flat colour and a gradient here
      // would be the only soft thing in the world.
      ctx.fillStyle = "#c2400f";
      ctx.fillRect(10, 15, 12, 6);
      ctx.fillStyle = "#ff7a1c";
      ctx.fillRect(11, 16, 10, 4);
      ctx.fillStyle = "#ffd87a";
      ctx.fillRect(13, 17, 6, 2);
      ctx.fillStyle = "rgba(255,150,40,0.28)"; // the light on the breast
      ctx.fillRect(10, 5, 12, 7);
      ctx.fillStyle = "#8a7f74"; // the hearth's lip, worn pale
      ctx.fillRect(4, 26, 24, 2);
      break;
    }
    case 49: { // slack tub: the water a hot piece is quenched in
      // A half-barrel, seen from slightly above so the water shows: staves
      // and two hoops below, a dark disc of water on top. What makes it
      // read as a smith's rather than as a rain butt is the char on the
      // rim, where hot iron has been rested while somebody got a grip.
      ctx.fillStyle = "#6b4a2a"; // the staves
      ctx.fillRect(6, 12, 20, 16);
      ctx.fillStyle = "#5a3d22"; // the shadowed side
      ctx.fillRect(21, 12, 5, 16);
      ctx.fillStyle = "#4b4f54"; // iron hoops
      ctx.fillRect(6, 15, 20, 2);
      ctx.fillRect(6, 24, 20, 2);
      ctx.fillStyle = "#8a6236"; // the rim, catching the light
      ctx.beginPath();
      ctx.ellipse(16, 12, 10, 4, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = "#1e2f3a"; // the water: black and still
      ctx.beginPath();
      ctx.ellipse(16, 12, 8, 3, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = "#3d5a6b"; // a single highlight on it
      ctx.fillRect(12, 11, 4, 1);
      ctx.fillStyle = "#2b2520"; // char on the rim, where iron has rested
      ctx.fillRect(20, 10, 6, 2);
      break;
    }
    case 50: { // rack of stock: bar and rod, waiting to be worked
      // A lean-to of iron against the yard's corner. Bar is square and rod
      // is round, and both are drawn as lengths standing at slightly
      // different angles, because a stack nobody has straightened in a
      // while is what "waiting to be worked" looks like.
      ctx.fillStyle = "#3a2a18"; // the timber frame it leans on
      ctx.fillRect(5, 24, 22, 3);
      ctx.fillRect(6, 10, 2.5, 16);
      for (const [x, top, w, tint] of [
        [10, 7, 3, "#5c6167"],
        [14, 5, 2, "#6b7076"],
        [17, 8, 3, "#50555b"],
        [21, 6, 2, "#646a70"],
        [24, 9, 3, "#585d63"],
      ] as [number, number, number, string][]) {
        ctx.save();
        ctx.translate(x, 25);
        ctx.rotate(((x - 16) / 16) * 0.12); // each length leaning its own way
        ctx.fillStyle = tint;
        ctx.fillRect(0, -(25 - top), w, 25 - top);
        ctx.restore();
      }
      ctx.fillStyle = "#7a4a28"; // rust, which is the point of the look
      ctx.fillRect(11, 16, 2, 4);
      ctx.fillRect(22, 13, 2, 3);
      break;
    }
    // The rare finds (2026-09-26, "Implement them all", on a proposal of
    // rare drops). Each is drawn in the colours it is worn or held in, so
    // what lies on the ground is what somebody is then seen in.
    case 60: // mithril shirt: rings finer than chain's, in a silver nothing else has
      drawArmor(ctx, MITHRIL.body, MITHRIL.shade, MITHRIL.light, (c) => {
        c.fillStyle = MITHRIL.ring;
        for (let y = 11; y < 25; y += 2) {
          for (let x = 9 + ((y >> 1) & 1); x < 24; x += 2) c.fillRect(x, y, 1, 1);
        }
        c.fillStyle = MITHRIL.sheen; // the sheen that says it is not iron
        c.fillRect(19, 12, 2, 9);
      });
      break;
    case 61: { // dwarven helm: a plain iron cap with a nasal, banded in bronze
      const cap = () => {
        ctx.beginPath();
        ctx.moveTo(7, 21);
        ctx.quadraticCurveTo(7, 8, 16, 8);
        ctx.quadraticCurveTo(25, 8, 25, 21);
        ctx.lineTo(25, 25);
        ctx.lineTo(21, 27); // the cheek guards
        ctx.lineTo(21, 22);
        ctx.lineTo(11, 22);
        ctx.lineTo(11, 27);
        ctx.lineTo(7, 25);
        ctx.closePath();
      };
      ctx.fillStyle = DWARF_HELM.iron;
      cap();
      ctx.fill();
      ctx.fillStyle = DWARF_HELM.lit; // the lit side of the dome
      ctx.beginPath();
      ctx.moveTo(10, 17);
      ctx.quadraticCurveTo(10, 11, 15, 10);
      ctx.lineTo(15, 12);
      ctx.quadraticCurveTo(12, 13, 12, 17);
      ctx.closePath();
      ctx.fill();
      ctx.fillStyle = DWARF_HELM.bronze; // the band round the brow, and the crest over the top
      ctx.fillRect(7, 18, 18, 3);
      ctx.fillRect(15, 8, 2, 10);
      ctx.fillStyle = DWARF_HELM.iron;
      ctx.fillRect(15, 21, 2, 6); // the nasal, down between where the eyes go
      ctx.strokeStyle = DWARF_HELM.rim;
      ctx.lineWidth = 1.5;
      cap();
      ctx.stroke();
      break;
    }
    case 62: // elven longbow: the bow's shape, taller, in dark gold-grained wood with recurved tips
      ctx.strokeStyle = LONGBOW.wood;
      ctx.lineWidth = 2.5;
      ctx.lineCap = "round";
      ctx.beginPath();
      ctx.moveTo(6, 2);
      ctx.quadraticCurveTo(10, 4, 11, 6); // the upper tip, turned back
      ctx.quadraticCurveTo(25, 16, 11, 26);
      ctx.quadraticCurveTo(10, 28, 6, 30); // and the lower
      ctx.stroke();
      ctx.strokeStyle = LONGBOW.string;
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(7, 3);
      ctx.lineTo(7, 29);
      ctx.stroke();
      ctx.fillStyle = LONGBOW.grip; // the grip, bound in green
      ctx.fillRect(16, 13, 3, 6);
      ctx.fillStyle = LONGBOW.leaf; // and a silver leaf inlaid above it
      ctx.fillRect(15, 10, 2, 2);
      break;
    // 63 was the wolfskin cloak, taken out of the game on 2026-10-07
    // ("remove the wolfskin cloak from the game."). The number is retired
    // on the server, so nothing is ever drawn by it.
    case 64: { // spider-fang dagger: a black fang on a short bound hilt, still wet
      ctx.strokeStyle = SPIDER_FANG.hilt;
      ctx.lineWidth = 3;
      ctx.lineCap = "round";
      ctx.beginPath();
      ctx.moveTo(7, 27);
      ctx.lineTo(12, 22); // the grip
      ctx.stroke();
      ctx.strokeStyle = SPIDER_FANG.guard;
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(10, 19);
      ctx.lineTo(15, 24); // the guard, square across it
      ctx.stroke();
      ctx.fillStyle = SPIDER_FANG.fang; // the fang, curving to its point
      ctx.beginPath();
      ctx.moveTo(11, 20);
      ctx.quadraticCurveTo(17, 11, 26, 7);
      ctx.quadraticCurveTo(21, 14, 15, 24);
      ctx.closePath();
      ctx.fill();
      ctx.fillStyle = SPIDER_FANG.gloss;
      ctx.fillRect(15, 17, 2, 1.5);
      ctx.fillStyle = SPIDER_FANG.venom; // and a drop at the tip
      ctx.beginPath();
      ctx.arc(26, 10, 1.6, 0, Math.PI * 2);
      ctx.fill();
      break;
    }
    case 65: { // Sting: a long elven knife, bright, with a blue light on the edges
      // The bright sword's construction, shorter and narrower: guard square
      // across the blade and the grip straight back down it, on one axis.
      const bx = 11, by = 22, tx = 25, ty = 6;
      const len = Math.hypot(tx - bx, ty - by);
      const ux = (tx - bx) / len, uy = (ty - by) / len;
      const px = -uy, py = ux;
      const at = (a: number, b: number): [number, number] => [bx + ux * a + px * b, by + uy * a + py * b];
      ctx.fillStyle = STING.glow;
      bladePath(ctx, bx, by, ...at(len + 1, 0), 1.9);
      ctx.fill();
      ctx.fillStyle = STING.blade;
      bladePath(ctx, bx, by, tx, ty, 1);
      ctx.fill();
      ctx.strokeStyle = STING.hilt;
      ctx.lineWidth = 2.5;
      ctx.lineCap = "butt";
      ctx.beginPath();
      ctx.moveTo(...at(0, -4));
      ctx.lineTo(...at(0, 4)); // the guard
      ctx.moveTo(...at(-1, 0));
      ctx.lineTo(...at(-7, 0)); // the grip
      ctx.stroke();
      break;
    }
    case 66: { // geomancer's staff: black stone, veined, with a lit stone at the head
      ctx.strokeStyle = GEO_STAFF.stone;
      ctx.lineWidth = 3;
      ctx.lineCap = "round";
      ctx.beginPath();
      ctx.moveTo(6, 28);
      ctx.lineTo(22, 9);
      ctx.stroke();
      ctx.strokeStyle = GEO_STAFF.vein; // the veins, humming
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(9, 25);
      ctx.lineTo(12, 22);
      ctx.moveTo(15, 18);
      ctx.lineTo(18, 14);
      ctx.stroke();
      ctx.fillStyle = GEO_STAFF.stone; // the knuckle of rock at the head
      ctx.beginPath();
      ctx.arc(23, 8, 4.5, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = GEO_STAFF.vein; // and the stone set in it
      ctx.beginPath();
      ctx.arc(23, 8, 2.2, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = GEO_STAFF.spark;
      ctx.fillRect(22, 7, 1, 1);
      break;
    }
    case 67: // dragon-scale mail: red scale riveted on in rows, rimmed in gold
      drawArmor(ctx, DRAGON_SCALE.body, DRAGON_SCALE.shade, DRAGON_SCALE.light, (c) => {
        c.strokeStyle = DRAGON_SCALE.edge;
        c.lineWidth = 1;
        for (let y = 12; y < 25; y += 3) {
          c.beginPath(); // a row of scales, each a little arc lapping the one below
          for (let x = 9 + ((y / 3) & 1) * 1.5; x < 23; x += 3) {
            c.moveTo(x, y);
            c.quadraticCurveTo(x + 1.5, y + 2.2, x + 3, y);
          }
          c.stroke();
        }
        c.fillStyle = DRAGON_SCALE.gold; // the rivets down the front
        for (const y of [12, 16, 20]) c.fillRect(21, y, 1.5, 1.5);
      });
      break;
    case 68: // mana potion: the health potion's flask, filled blue (2026-09-27)
      drawPotion(ctx, POTION_MANA.body, POTION_MANA.glint);
      break;
    case 69: { // crossbow: a dark stock laid corner to corner, a steel prod across its nose
      // The crossbow, the arrow and the bolt (2026-09-27: "add a crossbow
      // item ... implement arrows and bolts"). The crossbow is drawn along
      // the tile's diagonal so the stock has the length to read as a
      // stock, and the prod goes square across its nose, bowed forward the
      // way a spanned prod is, with the string drawn back to the nut.
      const bx = 5, by = 27; // the butt
      const nx = 24, ny = 8; // the nose
      const len = Math.hypot(nx - bx, ny - by);
      const ux = (nx - bx) / len, uy = (ny - by) / len; // along the stock
      const px = -uy, py = ux; // square across it
      const at = (a: number, b: number): [number, number] => [bx + ux * a + px * b, by + uy * a + py * b];
      const [t1x, t1y] = at(len - 3, -11), [t2x, t2y] = at(len - 3, 11);
      const [kx, ky] = at(len - 12, 0); // the nut the string is spanned back to
      // The stock: dark wood, thicker at the butt, with a lit edge.
      ctx.lineCap = "round";
      ctx.strokeStyle = CROSSBOW.stock;
      ctx.lineWidth = 3.5;
      ctx.beginPath();
      ctx.moveTo(bx, by);
      ctx.lineTo(nx, ny);
      ctx.stroke();
      ctx.strokeStyle = CROSSBOW.stockLit;
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(...at(2, -1.2));
      ctx.lineTo(...at(len - 2, -1.2));
      ctx.stroke();
      // The lever under the stock, behind the nut.
      ctx.strokeStyle = CROSSBOW.iron;
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.moveTo(...at(len - 17, 1.5));
      ctx.lineTo(...at(len - 21, 5));
      ctx.stroke();
      // The string, spanned from the prod's tips back to the nut: drawn
      // over the stock, because the V it makes is most of what tells a
      // crossbow from a pick at this size.
      ctx.strokeStyle = CROSSBOW.string;
      ctx.lineWidth = 1.2;
      ctx.beginPath();
      ctx.moveTo(t1x, t1y);
      ctx.lineTo(kx, ky);
      ctx.lineTo(t2x, t2y);
      ctx.stroke();
      // The prod: steel, bowed forward, tip to tip across the nose.
      const [cx, cy] = at(len + 3, 0);
      ctx.strokeStyle = CROSSBOW.prod;
      ctx.lineWidth = 2.5;
      ctx.beginPath();
      ctx.moveTo(t1x, t1y);
      ctx.quadraticCurveTo(cx, cy, t2x, t2y);
      ctx.stroke();
      ctx.strokeStyle = CROSSBOW.prodLit;
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(t1x, t1y);
      ctx.quadraticCurveTo(cx, cy, ...at(len, 0));
      ctx.stroke();
      ctx.lineCap = "butt";
      break;
    }
    case 70: // arrows: a bundle of four, heads up, tied at the middle
      drawBundle(ctx, 4, 21, ARROW_LOOK);
      break;
    case 71: // bolts: a bundle of three, shorter and thicker, heads of iron
      drawBundle(ctx, 3, 15, BOLT_LOOK);
      break;
    // The Shards of Narsil and Andúril (2026-09-28: "Add a quest to find the
    // Shards of Narsil. One in the Ettenmoors, one in Ered Luin, find some
    // other good places. Maybe the hilt, a few fragments, the crossbar").
    // Each piece is drawn as the part of one sword it is, on the tile's
    // diagonal where a sword lies, so the five in a bag read as a sword in
    // pieces: the hilt with a stub of blade, the guard across, and the
    // blade in three — its lower third broken at both ends, its middle
    // the longest, and its point.
    case 72: { // Narsil's hilt: leather grip wound with wire, a pommel, a stub of broken blade
      const { at } = narsilAxis();
      drawNarsilSteel(ctx, at, 1, 7, 2.1, true, false); // the stub, broken off
      ctx.strokeStyle = NARSIL.grip;
      ctx.lineWidth = 3;
      ctx.lineCap = "butt";
      ctx.beginPath();
      ctx.moveTo(...at(-1, 0));
      ctx.lineTo(...at(-8, 0)); // the grip
      ctx.stroke();
      ctx.strokeStyle = NARSIL.wire; // the wire wound round it
      ctx.lineWidth = 1;
      ctx.beginPath();
      for (const a of [-2.5, -4.5, -6.5]) {
        ctx.moveTo(...at(a, -1.6));
        ctx.lineTo(...at(a - 1, 1.6));
      }
      ctx.stroke();
      ctx.strokeStyle = NARSIL.guard; // the collar the guard sat on
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(...at(0, -2));
      ctx.lineTo(...at(0, 2));
      ctx.stroke();
      ctx.fillStyle = NARSIL.sun; // the pommel
      ctx.beginPath();
      ctx.arc(...at(-9.5, 0), 2.3, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = NARSIL.moon;
      ctx.fillRect(...at(-9.8, -0.6), 1.2, 1.2);
      break;
    }
    case 73: { // Narsil's crossguard: a bar with its ends turned up toward the blade, sun and moon on it
      ctx.lineCap = "round";
      ctx.strokeStyle = NARSIL.edge; // a dark line under the steel so it reads on stone
      ctx.lineWidth = 4;
      ctx.beginPath();
      ctx.moveTo(5, 13);
      ctx.quadraticCurveTo(7, 19, 16, 19);
      ctx.quadraticCurveTo(25, 19, 27, 13);
      ctx.stroke();
      ctx.strokeStyle = NARSIL.guard;
      ctx.lineWidth = 2.5;
      ctx.beginPath();
      ctx.moveTo(5, 13);
      ctx.quadraticCurveTo(7, 19, 16, 19);
      ctx.quadraticCurveTo(25, 19, 27, 13);
      ctx.stroke();
      ctx.strokeStyle = NARSIL.steel; // the light along its top edge
      ctx.lineWidth = 0.8;
      ctx.beginPath();
      ctx.moveTo(6, 13.5);
      ctx.quadraticCurveTo(8, 18, 16, 18);
      ctx.quadraticCurveTo(24, 18, 26, 13.5);
      ctx.stroke();
      ctx.fillStyle = NARSIL.grip; // the slot the blade went through
      ctx.fillRect(14.5, 17.5, 3, 3);
      ctx.fillStyle = NARSIL.sun; // the sun on one arm
      ctx.beginPath();
      ctx.arc(9, 17.5, 1.8, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = NARSIL.moon; // and the moon on the other, a crescent
      ctx.beginPath();
      ctx.arc(23, 17.5, 1.8, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = NARSIL.guard;
      ctx.beginPath();
      ctx.arc(23.8, 17, 1.4, 0, Math.PI * 2);
      ctx.fill();
      ctx.lineCap = "butt";
      break;
    }
    case 74: { // Narsil's lower blade: the third nearest the guard, broken at both ends
      const { at } = narsilAxis();
      drawNarsilSteel(ctx, at, 1, 16, 2.3, true, false);
      break;
    }
    case 75: { // Narsil's middle blade: the longest piece, broken at both ends
      const { at } = narsilAxis();
      drawNarsilSteel(ctx, at, -3, 21, 2.1, true, false);
      break;
    }
    case 76: { // Narsil's point: the last third, broken at its foot and whole at its tip
      const { at } = narsilAxis();
      drawNarsilSteel(ctx, at, 1, 22, 2.1, true, true);
      break;
    }
    case 77: { // Andúril: Narsil whole again, the blade alight, on a hilt of gold
      // The bright sword's construction — the guard square across the
      // blade and the grip straight back down it — with the light laid
      // under the blade in two: the moon's cold glow, and the sun's warm
      // line down one edge.
      const bx = 10, by = 24, tx = 27, ty = 4;
      const len = Math.hypot(tx - bx, ty - by);
      const ux = (tx - bx) / len, uy = (ty - by) / len;
      const px = -uy, py = ux;
      const at = (a: number, b: number): [number, number] => [bx + ux * a + px * b, by + uy * a + py * b];
      ctx.fillStyle = ANDURIL.glow; // the moon's light, round the whole blade
      bladePath(ctx, bx, by, ...at(len + 1.5, 0), 2.6);
      ctx.fill();
      ctx.fillStyle = ANDURIL.flame; // the sun's, down one edge
      bladePath(ctx, ...at(0, 0.6), ...at(len - 1, 0.6), 1.3);
      ctx.fill();
      ctx.fillStyle = ANDURIL.blade;
      bladePath(ctx, bx, by, tx, ty, 1.1);
      ctx.fill();
      ctx.strokeStyle = ANDURIL.hilt; // a guard of gold, its ends turned up
      ctx.lineWidth = 2.5;
      ctx.lineCap = "round";
      ctx.beginPath();
      ctx.moveTo(...at(1.5, -5));
      ctx.lineTo(...at(0, -4));
      ctx.lineTo(...at(0, 4));
      ctx.lineTo(...at(1.5, 5));
      ctx.stroke();
      ctx.strokeStyle = NARSIL.grip; // the grip
      ctx.lineWidth = 3;
      ctx.lineCap = "butt";
      ctx.beginPath();
      ctx.moveTo(...at(-1, 0));
      ctx.lineTo(...at(-7, 0));
      ctx.stroke();
      ctx.fillStyle = ANDURIL.hilt; // the pommel, with a red stone in it
      ctx.beginPath();
      ctx.arc(...at(-8.5, 0), 2.2, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = ANDURIL.gem;
      ctx.beginPath();
      ctx.arc(...at(-8.5, 0), 1, 0, Math.PI * 2);
      ctx.fill();
      break;
    }
    // The content proposal's items (2026-10-01, "Implement the entire
    // proposal in the background"), each in the colours it is worn or held
    // in: see GLAMDRING and the rest.
    case 79: { // Glamdring: a long blade on the sword's diagonal, blue light down it, a jewelled hilt
      const bx = 10, by = 24, tx = 27, ty = 5;
      const len = Math.hypot(tx - bx, ty - by);
      const ux = (tx - bx) / len, uy = (ty - by) / len;
      const px = -uy, py = ux;
      const at = (a: number, b: number): [number, number] => [bx + ux * a + px * b, by + uy * a + py * b];
      ctx.fillStyle = GLAMDRING.glow;
      bladePath(ctx, bx, by, ...at(len + 1.5, 0), 2.5);
      ctx.fill();
      ctx.fillStyle = GLAMDRING.blade;
      bladePath(ctx, bx, by, tx, ty, 1.1);
      ctx.fill();
      ctx.strokeStyle = GLAMDRING.hilt;
      ctx.lineWidth = 2.5;
      ctx.lineCap = "butt";
      ctx.beginPath();
      ctx.moveTo(...at(0, -5));
      ctx.lineTo(...at(0, 5)); // the guard
      ctx.stroke();
      ctx.strokeStyle = GLAMDRING.grip;
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.moveTo(...at(-1, 0));
      ctx.lineTo(...at(-7, 0)); // the grip
      ctx.stroke();
      ctx.fillStyle = GLAMDRING.gem; // a blue stone in the pommel
      ctx.beginPath();
      ctx.arc(...at(-8.5, 0), 1.8, 0, Math.PI * 2);
      ctx.fill();
      break;
    }
    case 80: { // barrow-blade: a long knife, its blade run with red-gold runes
      const bx = 11, by = 22, tx = 25, ty = 7;
      const len = Math.hypot(tx - bx, ty - by);
      const ux = (tx - bx) / len, uy = (ty - by) / len;
      const px = -uy, py = ux;
      const at = (a: number, b: number): [number, number] => [bx + ux * a + px * b, by + uy * a + py * b];
      ctx.fillStyle = BARROW_BLADE.edge;
      bladePath(ctx, bx, by, ...at(len + 0.5, 0), 1.9);
      ctx.fill();
      ctx.fillStyle = BARROW_BLADE.blade;
      bladePath(ctx, bx, by, tx, ty, 1.3);
      ctx.fill();
      ctx.fillStyle = BARROW_BLADE.rune; // the runes, down the middle of it
      for (const a of [3, 6, 9, 12]) ctx.fillRect(...at(a, -0.5), 1.2, 1.2);
      ctx.strokeStyle = BARROW_BLADE.guard;
      ctx.lineWidth = 2;
      ctx.lineCap = "butt";
      ctx.beginPath();
      ctx.moveTo(...at(0, -3.5));
      ctx.lineTo(...at(0, 3.5));
      ctx.stroke();
      ctx.strokeStyle = BARROW_BLADE.hilt;
      ctx.lineWidth = 2.5;
      ctx.beginPath();
      ctx.moveTo(...at(-1, 0));
      ctx.lineTo(...at(-6, 0));
      ctx.stroke();
      break;
    }
    case 81: { // Uruk-hai scimitar: broad, short and curved, black iron with a bright edge
      ctx.fillStyle = URUK.iron;
      ctx.beginPath();
      ctx.moveTo(11, 21);
      ctx.quadraticCurveTo(15, 9, 26, 5); // the back of the blade
      ctx.quadraticCurveTo(22, 14, 15, 24); // the edge, swelling towards the point
      ctx.closePath();
      ctx.fill();
      ctx.strokeStyle = URUK.edge;
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(26, 5);
      ctx.quadraticCurveTo(22, 14, 15, 24);
      ctx.stroke();
      ctx.strokeStyle = URUK.grip;
      ctx.lineWidth = 2.5;
      ctx.lineCap = "butt";
      ctx.beginPath();
      ctx.moveTo(9, 20);
      ctx.lineTo(15, 25); // the guard, a plain bar
      ctx.moveTo(12, 23);
      ctx.lineTo(7, 28); // the grip
      ctx.stroke();
      break;
    }
    case 82: { // Umbar cutlass: a curved blade on a brass guard, its grip wrapped in tarred blue
      ctx.fillStyle = CUTLASS.blade;
      ctx.beginPath();
      ctx.moveTo(12, 21);
      ctx.quadraticCurveTo(17, 10, 27, 5);
      ctx.quadraticCurveTo(21, 13, 15, 23);
      ctx.closePath();
      ctx.fill();
      ctx.strokeStyle = CUTLASS.edge;
      ctx.lineWidth = 0.8;
      ctx.beginPath();
      ctx.moveTo(13, 20);
      ctx.quadraticCurveTo(18, 10, 27, 5);
      ctx.stroke();
      ctx.strokeStyle = CUTLASS.guard; // the shell of the guard round the hand
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(11, 24, 4, -0.9, 2.2);
      ctx.stroke();
      ctx.strokeStyle = CUTLASS.wrap;
      ctx.lineWidth = 2.5;
      ctx.lineCap = "butt";
      ctx.beginPath();
      ctx.moveTo(12, 23);
      ctx.lineTo(7, 28);
      ctx.stroke();
      break;
    }
    case 83: { // Morgul-blade: a thin pale knife going to smoke at its point
      const bx = 12, by = 21, tx = 24, ty = 8;
      ctx.fillStyle = MORGUL.haze;
      bladePath(ctx, bx, by, tx + 1.5, ty - 1.5, 2);
      ctx.fill();
      ctx.fillStyle = MORGUL.blade;
      bladePath(ctx, bx, by, tx, ty, 0.9);
      ctx.fill();
      ctx.strokeStyle = MORGUL.hilt;
      ctx.lineWidth = 2.5;
      ctx.lineCap = "butt";
      ctx.beginPath();
      ctx.moveTo(9.5, 18.5);
      ctx.lineTo(14.5, 23.5); // the guard
      ctx.moveTo(11, 22);
      ctx.lineTo(7, 26); // the grip
      ctx.stroke();
      break;
    }
    case 84: // elven cloak: a cape in Lórien's grey-green, a gold leaf at the throat
      drawCape(ctx, { ...ELVEN_CLOAK, lining: ELVEN_CLOAK.shade }, false, (c) => {
        c.fillStyle = ELVEN_CLOAK.leaf; // the brooch, a leaf of Lórien
        c.beginPath();
        c.ellipse(16, 10.2, 2.4, 1.4, -0.5, 0, Math.PI * 2);
        c.fill();
      });
      break;
    case 85: // Easterling scale mail: red lacquered scale in rows, edged in gold
      drawArmor(ctx, EASTERLING.body, EASTERLING.shade, EASTERLING.light, (c) => {
        c.strokeStyle = EASTERLING.edge;
        c.lineWidth = 1;
        for (let y = 12; y < 25; y += 3) {
          c.beginPath();
          for (let x = 8 + ((y / 3) & 1) * 1.5; x < 24; x += 3) {
            c.moveTo(x, y);
            c.quadraticCurveTo(x + 1.5, y + 2, x + 3, y);
          }
          c.stroke();
        }
        c.fillStyle = EASTERLING.gold; // the gold edging down the front
        c.fillRect(15, 14, 2, 10);
      });
      break;
    case 86: { // Rohirric helm: a tall steel cap, gold-banded, a white horsetail off its crest
      drawHorsetail(ctx, 16, 5, 27, 4, 27, 26, 6); // the horsetail, streaming back
      ctx.fillStyle = ROHAN_HELM.steel;
      ctx.beginPath();
      ctx.moveTo(8, 23);
      ctx.quadraticCurveTo(8, 9, 16, 6);
      ctx.quadraticCurveTo(24, 9, 24, 23);
      ctx.closePath();
      ctx.fill();
      ctx.fillStyle = ROHAN_HELM.lit;
      ctx.fillRect(11, 12, 2, 8);
      ctx.fillStyle = ROHAN_HELM.gold; // the band at the brow and the crest-ridge
      ctx.fillRect(8, 20, 16, 2.5);
      ctx.fillRect(15, 6, 2, 14);
      ctx.strokeStyle = ROHAN_HELM.rim;
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(8, 23);
      ctx.quadraticCurveTo(8, 9, 16, 6);
      ctx.quadraticCurveTo(24, 9, 24, 23);
      ctx.closePath();
      ctx.stroke();
      break;
    }
    case 87: { // helm of Dol Amroth: a silver dome, a blue band, a swan's wing either side
      for (const side of [-1, 1]) {
        ctx.fillStyle = DOL_AMROTH.wing; // the wing, swept up and back
        ctx.beginPath();
        ctx.moveTo(16 + side * 7, 16);
        ctx.quadraticCurveTo(16 + side * 15, 12, 16 + side * 14, 3);
        ctx.quadraticCurveTo(16 + side * 11, 10, 16 + side * 6, 12);
        ctx.closePath();
        ctx.fill();
      }
      ctx.fillStyle = DOL_AMROTH.steel;
      ctx.beginPath();
      ctx.moveTo(8, 24);
      ctx.quadraticCurveTo(8, 9, 16, 9);
      ctx.quadraticCurveTo(24, 9, 24, 24);
      ctx.closePath();
      ctx.fill();
      ctx.fillStyle = DOL_AMROTH.lit;
      ctx.fillRect(11, 13, 2, 7);
      ctx.fillStyle = DOL_AMROTH.blue; // the band, and the nasal
      ctx.fillRect(8, 19, 16, 3);
      ctx.fillRect(15, 19, 2, 7);
      ctx.strokeStyle = DOL_AMROTH.rim;
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(8, 24);
      ctx.quadraticCurveTo(8, 9, 16, 9);
      ctx.quadraticCurveTo(24, 9, 24, 24);
      ctx.stroke();
      break;
    }
    case 88: { // Elendilmir: a fillet of mithril with a white star on it, alight
      ctx.fillStyle = ELENDILMIR.glow; // the light it gives
      ctx.beginPath();
      ctx.arc(16, 13, 6, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = ELENDILMIR.band;
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.ellipse(16, 18, 11, 6, 0, 0, Math.PI * 2);
      ctx.stroke();
      ctx.fillStyle = ELENDILMIR.gem; // the star, four-pointed
      ctx.beginPath();
      ctx.moveTo(16, 7);
      ctx.lineTo(17.4, 11.6);
      ctx.lineTo(22, 13);
      ctx.lineTo(17.4, 14.4);
      ctx.lineTo(16, 19);
      ctx.lineTo(14.6, 14.4);
      ctx.lineTo(10, 13);
      ctx.lineTo(14.6, 11.6);
      ctx.closePath();
      ctx.fill();
      break;
    }
    case 89: { // Uruk-hai shield: tall and black, with the White Hand on it
      const outline = () => {
        ctx.beginPath();
        ctx.moveTo(8, 4);
        ctx.lineTo(24, 4);
        ctx.lineTo(24, 20);
        ctx.quadraticCurveTo(24, 27, 16, 30);
        ctx.quadraticCurveTo(8, 27, 8, 20);
        ctx.closePath();
      };
      ctx.fillStyle = URUK.shield;
      outline();
      ctx.fill();
      drawWhiteHand(ctx, 16, 16, 1);
      ctx.strokeStyle = URUK.rim;
      ctx.lineWidth = 1.5;
      outline();
      ctx.stroke();
      break;
    }
    case 90: // phial of Galadriel: a crystal flask with a star's light in it
      ctx.fillStyle = PHIAL.glow;
      ctx.beginPath();
      ctx.arc(16, 19, 10, 0, Math.PI * 2);
      ctx.fill();
      drawPhial(ctx, 16, 19, 1);
      break;
    case 91: // lembas: pale waybread in a folded green leaf, tied
      ctx.fillStyle = "#5f8a3a"; // the leaf
      ctx.beginPath();
      ctx.ellipse(16, 19, 12, 7, -0.25, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = "#d8c690"; // the bread, showing where the leaf parts
      ctx.beginPath();
      ctx.moveTo(8, 17);
      ctx.lineTo(22, 13);
      ctx.lineTo(24, 19);
      ctx.lineTo(10, 23);
      ctx.closePath();
      ctx.fill();
      ctx.strokeStyle = "#b8a470"; // the score across it
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(15, 15);
      ctx.lineTo(17, 21);
      ctx.stroke();
      ctx.strokeStyle = "#8a6a3a"; // the cord
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.moveTo(19, 11);
      ctx.lineTo(21, 25);
      ctx.stroke();
      break;
    case 92: // honey cake: two round cakes, glazed
      for (const [x, y] of [[12, 20], [20, 17]] as const) {
        ctx.fillStyle = "#8a5a1a";
        ctx.beginPath();
        ctx.ellipse(x, y + 2, 7, 4, 0, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = "#c98a2a";
        ctx.beginPath();
        ctx.ellipse(x, y, 7, 4, 0, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = "#f0c040"; // the honey on top
        ctx.beginPath();
        ctx.ellipse(x - 1, y - 1, 4, 2, 0, 0, Math.PI * 2);
        ctx.fill();
      }
      break;
    case 93: // athelas: a sprig of long leaves, with small white flowers
      ctx.strokeStyle = "#3f7a32";
      ctx.lineWidth = 1.5;
      ctx.lineCap = "round";
      ctx.beginPath();
      ctx.moveTo(14, 28);
      ctx.quadraticCurveTo(15, 18, 18, 7);
      ctx.stroke();
      ctx.fillStyle = "#5fa84a";
      for (const [x, y, r] of [[13, 21, -0.9], [19, 18, 0.8], [13, 14, -0.7], [20, 11, 0.6]] as const) {
        ctx.beginPath();
        ctx.ellipse(x, y, 5, 2, r, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.fillStyle = "#f4f8f0";
      for (const [x, y] of [[18, 6], [16, 8], [20, 8]] as const) ctx.fillRect(x - 1, y - 1, 2, 2);
      break;
    case 94: // Ent-draught: a stone jar of the Entwash, green-brown at the brim
      ctx.fillStyle = "#8d8a80";
      ctx.beginPath();
      ctx.moveTo(10, 11);
      ctx.lineTo(22, 11);
      ctx.quadraticCurveTo(26, 19, 22, 28);
      ctx.lineTo(10, 28);
      ctx.quadraticCurveTo(6, 19, 10, 11);
      ctx.closePath();
      ctx.fill();
      ctx.fillStyle = "#b0ada2"; // the lit side of the stone
      ctx.fillRect(10, 15, 2, 9);
      ctx.fillStyle = "#6a675e"; // the lip
      ctx.fillRect(9, 9, 14, 3);
      ctx.fillStyle = "#7a9a4a"; // the draught in the mouth of it
      ctx.beginPath();
      ctx.ellipse(16, 10, 6, 1.6, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = "#4f7a3a"; // a leaf floating
      ctx.fillRect(17, 9, 3, 1.5);
      break;
    case 95: // warg pelt: a rank grey hide laid flat, legs out
      ctx.fillStyle = "#5c5650";
      ctx.beginPath();
      ctx.moveTo(9, 8);
      ctx.lineTo(23, 7);
      ctx.lineTo(28, 4); // a foreleg
      ctx.lineTo(25, 11);
      ctx.lineTo(26, 22);
      ctx.lineTo(29, 28); // a hind leg
      ctx.lineTo(22, 25);
      ctx.lineTo(10, 26);
      ctx.lineTo(4, 29);
      ctx.lineTo(7, 21);
      ctx.lineTo(6, 11);
      ctx.lineTo(3, 5);
      ctx.closePath();
      ctx.fill();
      ctx.fillStyle = "#857d74"; // the paler belly fur down the middle
      ctx.beginPath();
      ctx.ellipse(16, 16, 5, 8, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = "#3a3632"; // the ragged edge
      ctx.lineWidth = 1;
      ctx.beginPath();
      for (const [x, y] of [[9, 12], [22, 12], [10, 21], [21, 21]] as const) {
        ctx.moveTo(x, y);
        ctx.lineTo(x + (x < 16 ? -2 : 2), y + 1);
      }
      ctx.stroke();
      break;
    case 96: // mûmak tusk: a long curve of ivory, brown at the root
      ctx.strokeStyle = "#c9bd98";
      ctx.lineWidth = 6;
      ctx.lineCap = "round";
      ctx.beginPath();
      ctx.moveTo(5, 25);
      ctx.quadraticCurveTo(14, 26, 27, 6);
      ctx.stroke();
      ctx.strokeStyle = "#efe6cc";
      ctx.lineWidth = 4;
      ctx.beginPath();
      ctx.moveTo(5, 24);
      ctx.quadraticCurveTo(14, 25, 26.5, 6);
      ctx.stroke();
      ctx.strokeStyle = "#8a6a3a"; // the root, where it came out of the mûmak
      ctx.lineWidth = 6;
      ctx.lineCap = "butt";
      ctx.beginPath();
      ctx.moveTo(4, 25);
      ctx.lineTo(8, 25);
      ctx.stroke();
      break;
    case 97: // troll key: the old rusty key's shape, twice as heavy, in black iron
      ctx.strokeStyle = "#3a3a3a";
      ctx.lineWidth = 4;
      ctx.lineCap = "round";
      ctx.beginPath();
      ctx.arc(10, 11, 5, 0, Math.PI * 2);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(13, 15);
      ctx.lineTo(25, 27);
      ctx.stroke();
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.moveTo(20, 22);
      ctx.lineTo(16, 26);
      ctx.moveTo(24, 26);
      ctx.lineTo(21, 29);
      ctx.stroke();
      ctx.strokeStyle = "#6a6a6a"; // a glint along the shank
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(14, 15);
      ctx.lineTo(20, 21);
      ctx.stroke();
      break;
    case 98: // corsair's key: a brass key on a tarred cord
      ctx.strokeStyle = "#2a2420"; // the cord
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.moveTo(4, 4);
      ctx.quadraticCurveTo(4, 12, 10, 12);
      ctx.stroke();
      ctx.strokeStyle = "#b8902a";
      ctx.lineWidth = 2.5;
      ctx.lineCap = "round";
      ctx.beginPath();
      ctx.arc(12, 13, 3.5, 0, Math.PI * 2);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(14, 16);
      ctx.lineTo(24, 26);
      ctx.moveTo(20, 22);
      ctx.lineTo(17, 25);
      ctx.moveTo(23, 25);
      ctx.lineTo(20, 28);
      ctx.stroke();
      ctx.fillStyle = "#1c1a20"; // the black ship stamped on the bow
      ctx.fillRect(10.5, 12, 3, 1.5);
      break;
    case 99: // Gandalf's letter: folded parchment under a red seal with a G-rune
      ctx.fillStyle = "#e8dcc0";
      ctx.fillRect(5, 9, 22, 15);
      ctx.strokeStyle = "#a89a78"; // the folds
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(5, 9);
      ctx.lineTo(16, 18);
      ctx.lineTo(27, 9);
      ctx.stroke();
      ctx.fillStyle = "#b01818"; // the seal
      ctx.beginPath();
      ctx.arc(16, 18, 3.5, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = "#e8c070"; // the rune on it
      ctx.beginPath();
      ctx.moveTo(15, 16.5);
      ctx.lineTo(17, 18);
      ctx.lineTo(15, 19.5);
      ctx.stroke();
      break;
    case 100: // corsair captain's sea-chart: a chart half unrolled, coasts on it
      ctx.fillStyle = "#d9c79c";
      ctx.fillRect(7, 8, 18, 16);
      ctx.fillStyle = "#b8a476"; // the rolled ends
      ctx.beginPath();
      ctx.ellipse(7, 16, 2.5, 9, 0, 0, Math.PI * 2);
      ctx.ellipse(25, 16, 2.5, 9, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = "#6a8aa8"; // a coast and the sea off it
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(10, 11);
      ctx.quadraticCurveTo(14, 14, 12, 18);
      ctx.quadraticCurveTo(16, 20, 21, 21);
      ctx.stroke();
      ctx.fillStyle = "#8a1c1c"; // the harbours, marked
      ctx.fillRect(13, 13, 1.5, 1.5);
      ctx.fillRect(19, 19, 1.5, 1.5);
      break;
    case 101: { // Witch-king's crown: a faceless helm of black steel, a crown of blades fanned off its brow
      // The helm-crown of Minas Morgul (2026-10-07: "use the helm-crown of
      // minas morgul graphics", design 3 of the proposal). It was a ring
      // of five points over a green haze from 2026-10-04, when it came in
      // with the Witch-king ("Add more Nazgul and a boss Witch King"); now
      // crown and helm are one piece, as the item is a helm, the best one.
      // The blades first, fanned out from the brow, the outer ones leaning
      // furthest, each with a bright edge up its inside.
      for (const [bx, tx, ty, w] of [[9, 3, 6, 1.6], [12, 8, 2, 1.8], [16, 16, 0.5, 2], [20, 24, 2, 1.8], [23, 29, 6, 1.6]] as const) {
        ctx.fillStyle = WITCH_CROWN.lit;
        ctx.beginPath();
        ctx.moveTo(bx - w, 14);
        ctx.lineTo(tx, ty);
        ctx.lineTo(bx + w, 14);
        ctx.closePath();
        ctx.fill();
        ctx.strokeStyle = WITCH_CROWN.edge;
        ctx.lineWidth = 0.8;
        ctx.beginPath();
        ctx.moveTo(bx, 13);
        ctx.lineTo(tx + Math.sign(16 - bx) * 0.5, ty + 2);
        ctx.stroke();
      }
      // The mask: a shield shape, brow to a point at the chin.
      ctx.fillStyle = WITCH_CROWN.steel;
      ctx.beginPath();
      ctx.moveTo(8, 12);
      ctx.lineTo(24, 12);
      ctx.lineTo(23, 22);
      ctx.lineTo(16, 30);
      ctx.lineTo(9, 22);
      ctx.closePath();
      ctx.fill();
      ctx.fillStyle = WITCH_CROWN.lit; // the brow band
      ctx.fillRect(8, 12, 16, 2);
      // The eye-slits, a black T, and nothing behind them but two cold
      // points of the Witch-king's green.
      ctx.fillStyle = WITCH_CROWN.slit;
      ctx.fillRect(10, 16, 12, 2.5);
      ctx.fillRect(15, 16, 2, 9);
      ctx.fillStyle = WITCH_CROWN.eye;
      ctx.fillRect(12, 16.5, 1, 1);
      ctx.fillRect(19, 16.5, 1, 1);
      ctx.fillStyle = WITCH_CROWN.dark; // the shadow down the right cheek
      ctx.beginPath();
      ctx.moveTo(23, 14);
      ctx.lineTo(23, 22);
      ctx.lineTo(16.5, 29.3);
      ctx.lineTo(17, 26);
      ctx.lineTo(21, 21);
      ctx.lineTo(21, 14);
      ctx.closePath();
      ctx.fill();
      break;
    }
    // The rings (2026-10-05: "Implement the lesser, Seven, Nine and One
    // Ring"): each a band seen a little from above, in its own metal, with
    // whatever is set in it. None is drawn on the body: a ring is too small
    // to see on a finger at this size, and the bag outlines it in use.
    case 102: // ring of lore: silver, a blue stone
      drawRing(ctx, RINGS.lore);
      break;
    case 103: // ring of protection (the ring of quiet until 2026-11-03): grey silver, plain
      drawRing(ctx, RINGS.quiet);
      break;
    case 104: // ring of clean blood: pale gold, a green chip
      drawRing(ctx, RINGS.cleanBlood);
      break;
    case 105: // the Seven, numbered as they drop: the First is 105, the
    case 156: // Second to the Seventh 156 to 161 (2026-10-18), all alike
    case 157:
    case 158:
    case 159:
    case 160:
    case 161: // ring of the Seven: heavy red gold, a broad band, a red stone
      drawRing(ctx, RINGS.seven);
      break;
    case 106: // the Nine likewise: the First is 106, the Second to the
    case 162: // Ninth 162 to 169
    case 163:
    case 164:
    case 165:
    case 166:
    case 167:
    case 168:
    case 169: // ring of the Nine: cold pale metal, a grey stone
      // It had a grey haze round it until 2026-10-21 ("remove the existing
      // grey colour around the nine"): its Morgul outline went round the
      // haze and made it a green blob.
      drawRing(ctx, RINGS.nine);
      break;
    case 107: { // the One Ring: plain bright gold, letters of fire faint on it
      ctx.fillStyle = RINGS.oneGlow;
      ctx.beginPath();
      ctx.ellipse(16, 18, 12, 8, 0, 0, Math.PI * 2);
      ctx.fill();
      drawRing(ctx, RINGS.one);
      ctx.strokeStyle = RINGS.oneFire; // the fire-letters along the near side
      ctx.lineWidth = 0.8;
      ctx.setLineDash([1.5, 1]);
      ctx.beginPath();
      ctx.ellipse(16, 18, 8, 4.6, 0, 0.15 * Math.PI, 0.85 * Math.PI);
      ctx.stroke();
      ctx.setLineDash([]);
      break;
    }
    case 108: { // the Cracks of Doom: a jagged fissure of fire in black rock
      ctx.fillStyle = "#1a1412"; // the scorched lip
      ctx.beginPath();
      ctx.ellipse(16, 17, 14, 10, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = CRACKS.fire;
      ctx.beginPath();
      ctx.moveTo(4, 15);
      ctx.lineTo(10, 12);
      ctx.lineTo(14, 16);
      ctx.lineTo(20, 11);
      ctx.lineTo(28, 15);
      ctx.lineTo(21, 20);
      ctx.lineTo(15, 19);
      ctx.lineTo(9, 22);
      ctx.closePath();
      ctx.fill();
      ctx.fillStyle = CRACKS.heart; // the white heat at the bottom of it
      ctx.beginPath();
      ctx.moveTo(10, 16);
      ctx.lineTo(14, 17);
      ctx.lineTo(20, 14);
      ctx.lineTo(22, 16);
      ctx.lineTo(15, 18);
      ctx.closePath();
      ctx.fill();
      break;
    }
    // The Orcrist quest's two (2026-10-07: "do the Orcrist quest"): see
    // ORCRIST and ELVENKING_KEYS.
    case 109: { // the Elvenking's keys: three long keys of green bronze on a silver ring
      ctx.strokeStyle = ELVENKING_KEYS.ring;
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(11, 10, 5, 0, Math.PI * 2);
      ctx.stroke();
      ctx.strokeStyle = ELVENKING_KEYS.bronze;
      ctx.lineCap = "round";
      for (const [ex, ey, bit] of [[9, 28, -1], [16, 27, 1], [24, 23, 1]] as const) {
        const sx = 11 + (ex - 11) * 0.22, sy = 14; // where it hangs off the ring
        const along = (dy: number): number => sx + ((ex - sx) * (ey - dy - sy)) / (ey - sy);
        ctx.lineWidth = 2.5;
        ctx.beginPath();
        ctx.moveTo(sx, sy);
        ctx.lineTo(ex, ey); // the shank
        ctx.stroke();
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(along(1), ey - 1);
        ctx.lineTo(along(1) + 3 * bit, ey - 1); // the bit, in two wards
        ctx.moveTo(along(4), ey - 4);
        ctx.lineTo(along(4) + 2.5 * bit, ey - 4);
        ctx.stroke();
      }
      ctx.fillStyle = ELVENKING_KEYS.leaf; // a leaf of the Woodland Realm on the ring
      ctx.beginPath();
      ctx.ellipse(6, 6, 2.6, 1.4, -0.7, 0, Math.PI * 2);
      ctx.fill();
      break;
    }
    case 110: { // Orcrist: Glamdring's twin, broader in the blade, a down-swept guard and a bound grip
      // A two-hander (2026-10-17: "orcrist looks too small to be 2
      // handed"): drawn as long as Andúril, corner to corner of the slot,
      // broader in the blade than any sword but the giant one, and on a
      // grip long enough for two hands.
      const bx = 10, by = 22, tx = 28, ty = 3;
      const len = Math.hypot(tx - bx, ty - by);
      const ux = (tx - bx) / len, uy = (ty - by) / len;
      const px = -uy, py = ux;
      const at = (a: number, b: number): [number, number] => [bx + ux * a + px * b, by + uy * a + py * b];
      ctx.fillStyle = ORCRIST.glow;
      bladePath(ctx, bx, by, ...at(len + 1.5, 0), 3.6);
      ctx.fill();
      ctx.fillStyle = ORCRIST.blade;
      bladePath(ctx, bx, by, tx, ty, 2.2);
      ctx.fill();
      ctx.strokeStyle = ORCRIST.fuller; // the groove down the middle of a broad blade
      ctx.lineWidth = 0.8;
      ctx.beginPath();
      ctx.moveTo(...at(1.5, 0));
      ctx.lineTo(...at(len * 0.72, 0));
      ctx.stroke();
      ctx.strokeStyle = ORCRIST.hilt;
      ctx.lineWidth = 2.2;
      ctx.lineCap = "round";
      ctx.beginPath();
      ctx.moveTo(...at(-2, -6));
      ctx.lineTo(...at(0, -4));
      ctx.lineTo(...at(0, 4));
      ctx.lineTo(...at(-2, 6)); // the guard, its ends swept down to the hands
      ctx.stroke();
      ctx.strokeStyle = ORCRIST.grip;
      ctx.lineWidth = 3;
      ctx.lineCap = "butt";
      ctx.beginPath();
      ctx.moveTo(...at(-1, 0));
      ctx.lineTo(...at(-8.5, 0)); // the grip, room for both hands
      ctx.stroke();
      ctx.strokeStyle = ORCRIST.binding; // bound with silver wire
      ctx.lineWidth = 0.8;
      ctx.beginPath();
      for (const a of [-2.5, -4.5, -6.5]) {
        ctx.moveTo(...at(a, -1.6));
        ctx.lineTo(...at(a - 0.8, 1.6));
      }
      ctx.stroke();
      ctx.fillStyle = ORCRIST.hilt; // a silver pommel with a green stone in it
      ctx.beginPath();
      ctx.arc(...at(-10, 0), 2.2, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = ORCRIST.gem;
      ctx.beginPath();
      ctx.arc(...at(-10, 0), 1.1, 0, Math.PI * 2);
      ctx.fill();
      break;
    }
    // A bed in the room over the Prancing Pony (2026-10-08: "put barliman
    // butterbur in a small inn at bree, with an upstairs that's a single
    // room with beds"), seen from above, its head to the north: see BED.
    case 111: // a bed: a wooden frame, a bolster and a patched quilt
    case 177: { // the Green Dragon's beds upstairs (2026-10-19), the same bed
      ctx.fillStyle = BED.frame;
      ctx.fillRect(5, 2, 22, 28);
      ctx.fillStyle = BED.frameDark; // the headboard, and the foot rail
      ctx.fillRect(5, 2, 22, 3);
      ctx.fillRect(5, 28, 22, 2);
      ctx.fillStyle = BED.sheet;
      ctx.fillRect(7, 5, 18, 23);
      ctx.fillStyle = BED.bolster;
      ctx.fillRect(8, 6, 16, 5);
      ctx.fillStyle = BED.sheetShade; // the dent in the bolster
      ctx.fillRect(12, 8, 8, 2);
      const patches = [BED.quiltA, BED.quiltB, BED.quiltC, BED.quiltB, BED.quiltC, BED.quiltA];
      for (let i = 0; i < 6; i++) { // the quilt, turned down under the bolster, in six patches
        ctx.fillStyle = patches[i];
        ctx.fillRect(7 + (i % 2) * 9, 13 + Math.floor(i / 2) * 5, 9, 5);
      }
      ctx.fillStyle = BED.sheet; // the sheet turned back over the quilt's top edge
      ctx.fillRect(7, 12, 18, 2);
      ctx.strokeStyle = BED.seam;
      ctx.lineWidth = 0.6;
      ctx.beginPath();
      ctx.moveTo(16, 14);
      ctx.lineTo(16, 28);
      ctx.moveTo(7, 18.5);
      ctx.lineTo(25, 18.5);
      ctx.moveTo(7, 23.5);
      ctx.lineTo(25, 23.5);
      ctx.stroke();
      break;
    }
    // What the dead keep in the crypt under Sauron's temple (2026-10-08).
    case 112: { // helm of the King's Men: a high-crested helm, green with the sea
      ctx.fillStyle = KINGSMEN_HELM.steel;
      ctx.beginPath();
      ctx.moveTo(7, 22);
      ctx.quadraticCurveTo(7, 10, 16, 9);
      ctx.quadraticCurveTo(25, 10, 25, 22);
      ctx.closePath();
      ctx.fill();
      ctx.fillStyle = KINGSMEN_HELM.rim;
      ctx.fillRect(6, 21, 20, 3);
      ctx.fillRect(15, 22, 2, 5); // the nasal
      ctx.fillStyle = KINGSMEN_HELM.lit;
      ctx.fillRect(10, 13, 2, 4);
      ctx.fillStyle = KINGSMEN_HELM.crest; // the crest, high from brow to nape
      ctx.beginPath();
      ctx.moveTo(10, 10);
      ctx.quadraticCurveTo(16, 2, 22, 10);
      ctx.lineTo(20, 11);
      ctx.quadraticCurveTo(16, 6, 12, 11);
      ctx.closePath();
      ctx.fill();
      break;
    }
    case 113: { // black rod: black iron, the altar's fire in its head
      ctx.strokeStyle = BLACK_ROD.iron;
      ctx.lineWidth = 2.6;
      ctx.lineCap = "round";
      ctx.beginPath();
      ctx.moveTo(8, 27);
      ctx.lineTo(23, 8);
      ctx.stroke();
      ctx.fillStyle = BLACK_ROD.glow;
      ctx.beginPath();
      ctx.arc(24, 7, 4, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = BLACK_ROD.ember;
      ctx.beginPath();
      ctx.arc(24, 7, 2.6, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = BLACK_ROD.iron; // the claws that hold the fire
      ctx.fillRect(20, 9, 2, 2);
      ctx.fillRect(25, 10, 2, 2);
      break;
    }
    case 114: { // piece of black silver: a curved shard of the dome, black with smoke
      ctx.fillStyle = BLACK_SILVER.dark;
      ctx.beginPath();
      ctx.moveTo(9, 20);
      ctx.quadraticCurveTo(16, 11, 24, 14);
      ctx.lineTo(22, 21);
      ctx.quadraticCurveTo(16, 18, 12, 24);
      ctx.closePath();
      ctx.fill();
      ctx.strokeStyle = BLACK_SILVER.lit;
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(11, 19);
      ctx.quadraticCurveTo(16, 13, 22, 15);
      ctx.stroke();
      ctx.fillStyle = BLACK_SILVER.glint; // where the silver shows through
      ctx.fillRect(17, 15, 2, 1);
      break;
    }
    case 115: { // pearl: a sea-pearl, lit on one side
      ctx.fillStyle = PEARL.shade;
      ctx.beginPath();
      ctx.arc(16, 18, 5, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = PEARL.body;
      ctx.beginPath();
      ctx.arc(15.4, 17.4, 4.2, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = PEARL.lit;
      ctx.beginPath();
      ctx.arc(14, 16, 1.4, 0, Math.PI * 2);
      ctx.fill();
      break;
    }
    // A wild hobbit's (2026-10-09: "Implement Hobbit saucepan").
    case 116: { // hobbit saucepan: a copper pan, its long handle off to one side
      ctx.fillStyle = SAUCEPAN.handle;
      ctx.fillRect(19, 15, 10, 2.5);
      ctx.fillStyle = SAUCEPAN.dark;
      ctx.beginPath();
      ctx.ellipse(12, 15, 8, 2.5, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = SAUCEPAN.copper;
      ctx.beginPath();
      ctx.moveTo(4, 15);
      ctx.lineTo(5, 24);
      ctx.quadraticCurveTo(12, 27, 19, 24);
      ctx.lineTo(20, 15);
      ctx.quadraticCurveTo(12, 18, 4, 15);
      ctx.closePath();
      ctx.fill();
      ctx.fillStyle = SAUCEPAN.lit;
      ctx.fillRect(7, 18, 2, 5);
      break;
    }
    // Off a mûmak's war-tower (2026-10-09: "implement it").
    case 117: // Haradrim spiked shield: round, yellow and black, spiked
      drawHaradShield(ctx, 16, 17, 10);
      break;
    // Bullroarer Took's (2026-10-10: "always drops a saucepan, and a golf club").
    // 2026-10-16: "Implement torches".
    case 119: // torch: a pine haft, a head of pitch-soaked rag, alight
      drawTorch(ctx, 11, 30, 0.95, 0.35);
      break;
    // --- The Rangers' camp (empty-areas site 2, 2026-10-16) ---------------
    case 137: { // troll hide: a stiff grey slab, ragged at the edges, lichen on it
      ctx.fillStyle = HILL_TROLL_HIDE.shade;
      ctx.beginPath();
      ctx.moveTo(6, 7);
      ctx.lineTo(16, 5);
      ctx.lineTo(26, 7);
      ctx.lineTo(28, 15);
      ctx.lineTo(26, 25);
      ctx.lineTo(17, 27);
      ctx.lineTo(7, 26);
      ctx.lineTo(4, 17);
      ctx.closePath();
      ctx.fill();
      ctx.fillStyle = HILL_TROLL_HIDE.skin;
      ctx.beginPath();
      ctx.moveTo(8, 9);
      ctx.lineTo(16, 7.5);
      ctx.lineTo(24, 9);
      ctx.lineTo(25.5, 15);
      ctx.lineTo(24, 23);
      ctx.lineTo(17, 24.5);
      ctx.lineTo(9, 23.5);
      ctx.lineTo(6.5, 16.5);
      ctx.closePath();
      ctx.fill();
      ctx.fillStyle = HILL_TROLL_HIDE.mottle; // the lichen in it
      for (const [x, y, r] of [[12, 12, 2], [20, 14, 1.6], [14, 19, 1.8], [21, 20, 1.2]] as const) {
        ctx.beginPath();
        ctx.ellipse(x, y, r, r * 0.8, 0, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.strokeStyle = HILL_TROLL_HIDE.dark; // a crack where it was folded
      ctx.lineWidth = 0.8;
      ctx.beginPath();
      ctx.moveTo(10, 15);
      ctx.lineTo(16, 16);
      ctx.lineTo(23, 15.5);
      ctx.stroke();
      break;
    }
    case 138: { // troll chief's head: a grey lump of rock with an iron ring through one ear
      ctx.fillStyle = HILL_TROLL_HIDE.shade;
      ctx.beginPath();
      ctx.ellipse(16, 18.5, 10, 8.5, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = HILL_TROLL_HIDE.skin;
      ctx.beginPath();
      ctx.ellipse(16, 17, 9, 7.5, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.beginPath(); // the lumps of the skull
      ctx.ellipse(12, 11.5, 3.5, 3, 0, 0, Math.PI * 2);
      ctx.ellipse(19.5, 11, 3, 2.6, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = HILL_TROLL_HIDE.shade; // the ears
      ctx.beginPath();
      ctx.moveTo(7.5, 14);
      ctx.lineTo(2, 10);
      ctx.lineTo(6.5, 18);
      ctx.moveTo(24.5, 14);
      ctx.lineTo(30, 10);
      ctx.lineTo(25.5, 18);
      ctx.fill();
      ctx.fillStyle = HILL_TROLL_HIDE.dark; // shut eyes and the wide mouth
      ctx.fillRect(11, 16, 3.5, 1.2);
      ctx.fillRect(17.5, 16, 3.5, 1.2);
      ctx.fillRect(11.5, 21, 9, 1.4);
      ctx.fillStyle = HILL_TROLL_HIDE.tusk;
      ctx.fillRect(12.5, 19.6, 1.5, 1.4);
      ctx.fillRect(18, 19.6, 1.5, 1.4);
      ctx.strokeStyle = TROLL_CHIEF_RING; // the iron ring through the ear
      ctx.lineWidth = 1.4;
      ctx.beginPath();
      ctx.ellipse(4.6, 16.5, 2.2, 2.6, 0, 0, Math.PI * 2);
      ctx.stroke();
      break;
    }
    case 139: // star of the Dúnedain: a Ranger's grey cloak, a silver star at the throat
      drawCape(ctx, { ...DUNEDAIN_CLOAK, lining: DUNEDAIN_CLOAK.shade }, false, (c) => {
        drawDunedainStar(c, 16, 10.2, 2.6);
      });
      break;
    case 155: { // pick: an ash haft, a curved iron head pointed at one end and flat at the other
      // Laid corner to corner like the axe, the head square across the top
      // of the haft rather than hung off one side of it (2026-10-24: "Fix
      // the pick graphics").
      const len = Math.hypot(13, 20);
      const ux = 13 / len, uy = -20 / len;
      drawPick(ctx, (a, b) => [8 + ux * a - uy * b, 28 + uy * a + ux * b], len, 2.6, 1);
      break;
    }
    case 118: { // golf club: a hickory shaft, a leather grip, an iron head
      ctx.lineCap = "round";
      ctx.strokeStyle = GOLF_CLUB.shaft;
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(8, 27);
      ctx.lineTo(23, 8);
      ctx.stroke();
      ctx.strokeStyle = GOLF_CLUB.grip;
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.moveTo(7, 28);
      ctx.lineTo(11, 23);
      ctx.stroke();
      ctx.fillStyle = GOLF_CLUB.iron;
      ctx.beginPath();
      ctx.moveTo(21, 9);
      ctx.lineTo(24, 6);
      ctx.lineTo(29, 7);
      ctx.quadraticCurveTo(30, 10, 27, 11);
      ctx.closePath();
      ctx.fill();
      ctx.fillStyle = GOLF_CLUB.lit;
      ctx.fillRect(24, 7, 4, 1);
      break;
    }
    // Azanulbizar (2026-10-16, site 4 of the empty areas' proposal): see
    // NAIN_AXE and DURINS_STONE.
    case 120: { // Náin's axe: a dwarf war-axe, its haft charred, its edge hacked to a saw
      ctx.lineCap = "round";
      ctx.strokeStyle = NAIN_AXE.haft;
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.moveTo(7, 27);
      ctx.lineTo(21, 9);
      ctx.stroke();
      ctx.strokeStyle = NAIN_AXE.char; // burnt along its lower half
      ctx.beginPath();
      ctx.moveTo(7, 27);
      ctx.lineTo(12, 20.5);
      ctx.stroke();
      ctx.strokeStyle = NAIN_AXE.band; // two iron bands round the haft
      ctx.lineWidth = 3.6;
      ctx.lineCap = "butt";
      ctx.beginPath();
      ctx.moveTo(14.4, 17.6);
      ctx.lineTo(15.4, 16.3);
      ctx.moveTo(17.6, 13.5);
      ctx.lineTo(18.6, 12.2);
      ctx.stroke();
      // One broad bit, bearded, its edge nicked into teeth.
      ctx.fillStyle = NAIN_AXE.steel;
      ctx.beginPath();
      ctx.moveTo(18, 8);
      ctx.lineTo(23, 12);
      ctx.lineTo(27, 9);
      ctx.lineTo(28, 13);
      ctx.lineTo(27, 15);
      ctx.lineTo(28, 18);
      ctx.lineTo(25, 20);
      ctx.lineTo(21, 15);
      ctx.closePath();
      ctx.fill();
      ctx.fillStyle = NAIN_AXE.dark; // the nicks
      for (const [x, y] of [[27.2, 11.5], [27.4, 16.4]] as const) {
        ctx.beginPath();
        ctx.moveTo(x + 1, y - 1);
        ctx.lineTo(x - 1.5, y);
        ctx.lineTo(x + 1, y + 1);
        ctx.closePath();
        ctx.fill();
      }
      ctx.fillStyle = NAIN_AXE.rune; // a rune of Durin's house on the cheek
      ctx.fillRect(22.5, 12.5, 1, 3);
      ctx.fillRect(23.5, 13.5, 1.5, 1);
      break;
    }
    case 121: { // Durin's Stone: a broken pillar of grey stone, worn runes on its face
      ctx.fillStyle = DURINS_STONE.shadow;
      ctx.beginPath();
      ctx.ellipse(17, 28, 9, 3, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = DURINS_STONE.side;
      ctx.beginPath();
      ctx.moveTo(10, 28);
      ctx.lineTo(10, 8);
      ctx.lineTo(13, 5);
      ctx.lineTo(16, 7);
      ctx.lineTo(19, 3);
      ctx.lineTo(22, 6);
      ctx.lineTo(22, 28);
      ctx.closePath();
      ctx.fill();
      ctx.fillStyle = DURINS_STONE.face; // the lit face
      ctx.beginPath();
      ctx.moveTo(10, 28);
      ctx.lineTo(10, 8);
      ctx.lineTo(13, 5);
      ctx.lineTo(16, 7);
      ctx.lineTo(17, 28);
      ctx.closePath();
      ctx.fill();
      ctx.strokeStyle = DURINS_STONE.rune; // the runes, worn almost away
      ctx.lineWidth = 0.8;
      ctx.beginPath();
      ctx.moveTo(12, 12);
      ctx.lineTo(12, 16);
      ctx.moveTo(12, 13);
      ctx.lineTo(14, 12);
      ctx.moveTo(14.5, 15);
      ctx.lineTo(14.5, 19);
      ctx.moveTo(12, 21);
      ctx.lineTo(15, 21);
      ctx.moveTo(13.5, 21);
      ctx.lineTo(13.5, 24);
      ctx.stroke();
      ctx.fillStyle = DURINS_STONE.star; // and seven points over them, the crown
      for (let i = 0; i < 7; i++) {
        const a = -Math.PI / 2 + ((i - 3) * Math.PI) / 9;
        ctx.fillRect(13.5 + Math.cos(a) * 3 - 0.5, 10 + Math.sin(a) * 2 - 0.5, 1, 1);
      }
      break;
    }
    // --- Site 8: the gardens of the Entwives (2026-10-16) ---
    // The two trees are drawn in the tree tile's shape, a crown over a
    // trunk (see Tile.Tree), so the oblique view stands them up the way it
    // stands a tree (see treeCut and STANDING_TREE_ITEMS).
    case 122: // withered apple tree: a grey trunk and bare limbs
      drawWitheredTree(ctx);
      break;
    case 123: // last apple tree: leaning, in leaf, a few red apples
      drawLastAppleTree(ctx);
      break;
    case 124: // Entwife's sickle: a grey stone crescent on a pale haft
      drawEntwifeSickle(ctx);
      break;
    case 125: // draught of Wellinghall: a great stone jar, golden-brown at the brim
      drawWellinghallDraught(ctx);
      break;
    // --- end site 8 ---
    // 9. Dorwinion (2026-10-16): a cask of the Elvenking's wine, and the
    // casks racked in the cellars.
    case 126: // cask of Dorwinion: a small oak cask on its side, iron-hooped, a red stain at the bung
      drawCask(ctx, 16, 19, 1);
      break;
    case 127: // casks: two racked on a cradle and one over them
    case 154: // the Prancing Pony's barrels (2026-10-17), racked the same way
    case 176: // the Green Dragon's barrels behind its bar (2026-10-19), the same again
      ctx.fillStyle = CASK.cradle;
      ctx.fillRect(3, 26, 26, 3);
      ctx.fillRect(5, 22, 2, 5);
      ctx.fillRect(25, 22, 2, 5);
      drawCask(ctx, 10, 21, 0.85);
      drawCask(ctx, 22, 21, 0.85);
      drawCask(ctx, 16, 12, 0.85);
      break;
    // end of 9. Dorwinion
    // --- Site 3, the Lossoth of Forochel (2026-10-16): see LOSSOTH.
    case 128: { // winter-wolf pelt: a white hide laid out flat, legs splayed, grey down the back
      ctx.fillStyle = LOSSOTH.peltShade; // the legs and the tail, splayed
      for (const [x0, y0, x1, y1] of [[10, 12, 4, 8], [22, 12, 28, 8], [10, 22, 4, 27], [22, 22, 28, 27]]) {
        ctx.beginPath();
        ctx.moveTo(x0 - 2, y0);
        ctx.lineTo(x1, y1);
        ctx.lineTo(x0 + 2, y0 + 2);
        ctx.closePath();
        ctx.fill();
      }
      ctx.beginPath();
      ctx.moveTo(15, 25);
      ctx.quadraticCurveTo(17, 29, 15, 31);
      ctx.lineTo(18, 25);
      ctx.fill();
      ctx.fillStyle = LOSSOTH.pelt; // the hide
      ctx.beginPath();
      ctx.ellipse(16, 17, 8, 9, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.beginPath(); // the head, flat, ears out
      ctx.ellipse(16, 7.5, 4, 3.5, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = LOSSOTH.peltBack; // the grey down the spine
      ctx.beginPath();
      ctx.ellipse(16, 17, 2.4, 7.5, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = LOSSOTH.peltShade;
      ctx.fillRect(13, 4, 2, 2);
      ctx.fillRect(17, 4, 2, 2);
      break;
    }
    case 129: // fur cloak: a cape of white fur, ragged at the hem, a bone toggle at the throat
      drawCape(ctx, { body: FUR_CLOAK.body, lining: FUR_CLOAK.shade, fold: FUR_CLOAK.fold, light: FUR_CLOAK.light }, true, (c) => {
        c.fillStyle = FUR_CLOAK.toggle;
        c.fillRect(14.5, 9.5, 3, 1.5);
      });
      break;
    case 130: // the Ring of Barahir: two serpents of gold, their eyes emeralds
      drawRing(ctx, RINGS.barahir);
      ctx.fillStyle = RINGS.barahirEye; // the second serpent's eye, across the band
      ctx.beginPath();
      ctx.arc(16, 23.4, 1.4, 0, Math.PI * 2);
      ctx.fill();
      break;
    case 131: { // snow-house: a dome of cut snow blocks, a low dark door, a smoke hole
      ctx.fillStyle = LOSSOTH.snowShade;
      ctx.beginPath();
      ctx.ellipse(16, 27, 14, 3, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = LOSSOTH.snow;
      ctx.beginPath();
      ctx.moveTo(2, 27);
      ctx.bezierCurveTo(2, 4, 30, 4, 30, 27);
      ctx.closePath();
      ctx.fill();
      ctx.strokeStyle = LOSSOTH.snowShade; // the courses of blocks, and their joints
      ctx.lineWidth = 0.8;
      ctx.beginPath();
      for (const [y, half] of [[21, 13], [15, 11], [10.5, 7.5]]) {
        ctx.moveTo(16 - half, y);
        ctx.lineTo(16 + half, y);
      }
      for (const [x, y0, y1] of [[7, 21, 27], [12, 15, 21], [20, 15, 21], [25, 21, 27], [16, 10.5, 15]]) {
        ctx.moveTo(x, y0);
        ctx.lineTo(x, y1);
      }
      ctx.stroke();
      ctx.fillStyle = LOSSOTH.door; // the low door, on the lee side
      ctx.beginPath();
      ctx.moveTo(12.5, 27);
      ctx.lineTo(12.5, 23);
      ctx.arc(16, 23, 3.5, Math.PI, 0);
      ctx.lineTo(19.5, 27);
      ctx.closePath();
      ctx.fill();
      ctx.fillStyle = LOSSOTH.smoke; // the smoke hole at the crown
      ctx.beginPath();
      ctx.ellipse(16, 7.6, 2, 1, 0, 0, Math.PI * 2);
      ctx.fill();
      break;
    }
    case 132: { // ship's timbers: black sodden ribs of a hull, a broken plank, frost on them
      ctx.lineCap = "round";
      ctx.strokeStyle = LOSSOTH.timber;
      ctx.lineWidth = 3;
      ctx.beginPath(); // two ribs, bowed
      ctx.moveTo(7, 29);
      ctx.quadraticCurveTo(3, 15, 10, 4);
      ctx.moveTo(25, 29);
      ctx.quadraticCurveTo(29, 15, 22, 5);
      ctx.stroke();
      ctx.lineWidth = 4; // a plank across them, snapped
      ctx.beginPath();
      ctx.moveTo(4, 19);
      ctx.lineTo(15, 17);
      ctx.moveTo(18, 18.5);
      ctx.lineTo(28, 21);
      ctx.stroke();
      ctx.strokeStyle = LOSSOTH.frost;
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(4, 17.6);
      ctx.lineTo(15, 15.6);
      ctx.moveTo(18, 17);
      ctx.lineTo(28, 19.5);
      ctx.moveTo(9, 5);
      ctx.lineTo(6, 10);
      ctx.stroke();
      ctx.lineCap = "butt";
      break;
    }
    // --- end of site 3
    // --- Longbottom (empty-areas site 1, 2026-10-16) ---
    case 133: { // pipe: a briar bowl on a long stem
      ctx.lineCap = "round";
      ctx.strokeStyle = LONGBOTTOM.stem;
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(6, 12);
      ctx.quadraticCurveTo(13, 18, 19, 19);
      ctx.stroke();
      ctx.fillStyle = LONGBOTTOM.bowl;
      ctx.beginPath();
      ctx.moveTo(18, 14);
      ctx.lineTo(26, 14);
      ctx.lineTo(25, 22);
      ctx.quadraticCurveTo(22, 25, 19, 22);
      ctx.closePath();
      ctx.fill();
      ctx.fillStyle = LONGBOTTOM.rim;
      ctx.fillRect(18, 13, 8, 2);
      ctx.fillStyle = LONGBOTTOM.lit;
      ctx.fillRect(20, 16, 1.5, 4);
      ctx.fillStyle = LONGBOTTOM.bit;
      ctx.fillRect(5, 11, 2.5, 2);
      break;
    }
    case 134: { // pinch of Longbottom Leaf: a twist of cured brown leaf
      ctx.fillStyle = LONGBOTTOM.leafDark;
      ctx.beginPath();
      ctx.ellipse(16, 21, 8, 4, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = LONGBOTTOM.leaf;
      for (const [x, y, r] of [[12, 19, 3], [16, 18, 3.4], [20, 19.5, 3], [14, 22, 2.6], [18.5, 22, 2.6]]) {
        ctx.beginPath();
        ctx.arc(x, y, r, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.fillStyle = LONGBOTTOM.leafLit;
      ctx.fillRect(14, 17, 3, 1);
      ctx.fillRect(19, 18.5, 2, 1);
      break;
    }
    case 135: { // bundle of stolen leaf: a hand of leaves tied at the stems, gnawed
      ctx.fillStyle = LONGBOTTOM.stolen;
      ctx.beginPath();
      ctx.moveTo(16, 9);
      ctx.quadraticCurveTo(25, 16, 22, 27);
      ctx.lineTo(10, 27);
      ctx.quadraticCurveTo(7, 16, 16, 9);
      ctx.fill();
      ctx.strokeStyle = LONGBOTTOM.leafDark;
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(16, 11);
      ctx.lineTo(16, 26);
      ctx.moveTo(13, 14);
      ctx.lineTo(12, 26);
      ctx.moveTo(19, 14);
      ctx.lineTo(20, 26);
      ctx.stroke();
      ctx.fillStyle = LONGBOTTOM.twine; // tied at the stems
      ctx.fillRect(11, 23, 10, 2);
      ctx.fillStyle = LONGBOTTOM.bite; // and bitten out of one side
      ctx.beginPath();
      ctx.arc(22.5, 17, 2.2, 0, Math.PI * 2);
      ctx.fill();
      break;
    }
    // --- end Longbottom ---
    // 5. Rhosgobel (2026-10-16): Radagast's brown cloak.
    case 136: // brown cloak: a cape of undyed brown wool, an even hem, a feather at the throat
      drawCape(ctx, { body: BROWN_CLOAK.body, lining: BROWN_CLOAK.shade, fold: BROWN_CLOAK.fold, light: BROWN_CLOAK.light }, false, (c) => {
        c.fillStyle = BROWN_CLOAK.feather; // a bird's feather through the clasp
        c.beginPath();
        c.ellipse(16.5, 10, 0.9, 2.6, 0.6, 0, Math.PI * 2);
        c.fill();
        c.fillStyle = BROWN_CLOAK.clasp;
        c.fillRect(15, 9.5, 2, 1.5);
      });
      break;
    // end of 5. Rhosgobel
    // 10. Drúwaith Iaur (2026-10-16): see drawPukelMan.
    case 140: // Púkel-man: a squat stone man sitting cross-legged, the size of a sitting man
      drawPukelMan(ctx, 16, 29, 1, PUKEL.stone, PUKEL.shade);
      break;
    case 141: // faithful stone: a little Púkel-man, darker, with black chips for eyes
      drawPukelMan(ctx, 16, 27, 0.62, PUKEL.faithful, PUKEL.faithfulShade, PUKEL.chips);
      break;
    // end of 10. Drúwaith Iaur
    // --- 12. Belegost (2026-10-16): Azaghâl's war-mask.
    case 142: { // war-mask of Belegost: a grim iron face, eye-slits, a beard of iron rings
      ctx.fillStyle = BELEGOST_MASK.dark; // the cap behind the face
      ctx.beginPath();
      ctx.ellipse(16, 13, 10, 8, 0, Math.PI, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = BELEGOST_MASK.iron; // the face-plate
      ctx.beginPath();
      ctx.moveTo(8, 11);
      ctx.lineTo(24, 11);
      ctx.lineTo(23, 20);
      ctx.lineTo(16, 23);
      ctx.lineTo(9, 20);
      ctx.closePath();
      ctx.fill();
      ctx.fillStyle = BELEGOST_MASK.lit; // the brow-ridge, scowling
      ctx.fillRect(9, 11, 14, 2);
      ctx.fillStyle = BELEGOST_MASK.slit; // the eye-slits
      ctx.fillRect(10.5, 14, 4, 1.5);
      ctx.fillRect(17.5, 14, 4, 1.5);
      ctx.fillStyle = BELEGOST_MASK.ring; // the beard of iron rings
      for (let row = 0; row < 3; row++) {
        for (let i = 0; i < 4 - row; i++) {
          ctx.beginPath();
          ctx.arc(11.5 + row * 1.5 + i * 3, 22 + row * 2.5, 1.2, 0, Math.PI * 2);
          ctx.fill();
        }
      }
      ctx.fillStyle = BELEGOST_MASK.gold; // the Broadbeams' hammer on the brow
      ctx.fillRect(15, 7, 2, 4);
      ctx.fillRect(13.5, 7, 5, 1.5);
      break;
    }
    // --- end of 12. Belegost
    // --- Site 6: Henneth Annûn (2026-10-16) ---
    case 143: // waterfall: a curtain of white water, foaming at its foot (see WATERFALL_ITEM)
      drawWaterfall(ctx);
      break;
    case 144: // Harad banner: a black serpent on scarlet, on a spear-shaft
      drawHaradBanner(ctx);
      break;
    // --- end site 6 ---
    // 11. The Variags of Khand (2026-10-16): see KHAND.
    case 145: // hide tent: a low ridge tent of black goat-hide, the flap tied back
      drawVariagTent(ctx);
      break;
    case 146: // palm: a ringed grey trunk leaning up to a crown of fronds (see STANDING_TREE_ITEMS)
      drawPalm(ctx);
      break;
    case 147: { // trader's strongbox: dark wood bound in brass, a seal on the lid
      ctx.fillStyle = KHAND.boxShade;
      ctx.fillRect(7, 14, 18, 13);
      ctx.fillStyle = KHAND.box;
      ctx.fillRect(7, 11, 18, 14);
      ctx.fillStyle = KHAND.boxLid;
      ctx.fillRect(7, 11, 18, 4);
      ctx.fillStyle = KHAND.brass; // the bands and the lock-plate
      ctx.fillRect(10, 11, 2, 14);
      ctx.fillRect(20, 11, 2, 14);
      ctx.fillRect(7, 15, 18, 1);
      ctx.fillRect(14.5, 16, 3, 4);
      ctx.fillStyle = KHAND.seal; // the merchant house's seal, unbroken
      ctx.beginPath();
      ctx.arc(16, 13, 1.6, 0, Math.PI * 2);
      ctx.fill();
      break;
    }
    case 148: { // dates: a little heap of brown, wrinkled fruit, sticky-bright
      for (const [x, y, a] of [[11, 22, 0.5], [16, 23, -0.3], [21, 22, 0.2], [13.5, 18.5, -0.6], [18.5, 18.5, 0.4], [16, 15, 0]]) {
        ctx.fillStyle = KHAND.date;
        ctx.beginPath();
        ctx.ellipse(x, y, 3.6, 2.2, a, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = KHAND.dateLight;
        ctx.fillRect(x - 1.5, y - 1.2, 2, 0.8);
      }
      break;
    }
    // end of 11. Khand
    // Dunharrow and the Paths of the Dead (2026-10-16, site 7 of the empty
    // areas' proposal): see BALDOR_HELM, DEAD_CROWN, BALDOR_BONES,
    // STONE_DOOR and ERECH_STONE.
    case 149: { // Baldor's helm: a tall helm of the Riders, gold-banded and set with garnets, a grey horsetail
      ctx.strokeStyle = BALDOR_HELM.tail; // the horsetail, gone grey
      ctx.lineWidth = 2;
      ctx.lineCap = "round";
      ctx.beginPath();
      ctx.moveTo(16, 6);
      ctx.quadraticCurveTo(24, 6, 27, 16);
      ctx.moveTo(16, 7);
      ctx.quadraticCurveTo(22, 9, 24, 19);
      ctx.stroke();
      ctx.fillStyle = BALDOR_HELM.steel;
      ctx.beginPath();
      ctx.moveTo(8, 23);
      ctx.quadraticCurveTo(8, 9, 16, 6);
      ctx.quadraticCurveTo(24, 9, 24, 23);
      ctx.closePath();
      ctx.fill();
      ctx.fillStyle = BALDOR_HELM.lit;
      ctx.fillRect(11, 12, 2, 7);
      ctx.fillStyle = BALDOR_HELM.gold; // a broad band of gold at the brow, and the crest
      ctx.fillRect(8, 19, 16, 4);
      ctx.fillRect(15, 6, 2, 13);
      ctx.fillStyle = BALDOR_HELM.garnet; // garnets set in the band
      for (const x of [10, 14.5, 19]) ctx.fillRect(x, 20, 2, 2);
      ctx.strokeStyle = BALDOR_HELM.rim;
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(8, 23);
      ctx.quadraticCurveTo(8, 9, 16, 6);
      ctx.quadraticCurveTo(24, 9, 24, 23);
      ctx.closePath();
      ctx.stroke();
      break;
    }
    case 150: { // crown of the King of the Dead: a dim black crown of seven tall points
      ctx.fillStyle = DEAD_CROWN.shadow;
      ctx.beginPath();
      ctx.ellipse(16, 25, 11, 3, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = DEAD_CROWN.iron;
      for (let i = 0; i < 7; i++) {
        const x = 7 + i * 3;
        const up = i % 2 ? 6 : 10;
        ctx.beginPath();
        ctx.moveTo(x - 1.5, 19);
        ctx.lineTo(x, 19 - up);
        ctx.lineTo(x + 1.5, 19);
        ctx.closePath();
        ctx.fill();
      }
      ctx.fillRect(5, 18, 22, 6); // the band
      ctx.fillStyle = DEAD_CROWN.lit;
      ctx.fillRect(6, 19, 20, 1);
      ctx.fillStyle = DEAD_CROWN.glow; // a cold light in the stones
      for (const x of [9, 15.5, 22]) ctx.fillRect(x, 20.5, 1.5, 1.5);
      break;
    }
    case 151: { // Baldor's bones: a skeleton in mail fallen on its face, its hands at a door
      ctx.fillStyle = BALDOR_BONES.shadow;
      ctx.beginPath();
      ctx.ellipse(16, 18, 13, 6, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = BALDOR_BONES.mail; // the harness, whole still
      ctx.fillRect(8, 13, 11, 9);
      ctx.fillStyle = BALDOR_BONES.ring;
      for (let y = 14; y < 22; y += 2) for (let x = 9 + ((y & 2) ? 1 : 0); x < 19; x += 2) ctx.fillRect(x, y, 1, 1);
      ctx.fillStyle = BALDOR_BONES.bone;
      ctx.beginPath(); // the skull, face down, towards the door
      ctx.arc(23, 17, 3.5, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillRect(18, 11, 9, 1.5); // the arms, reaching for the door
      ctx.fillRect(18, 22, 9, 1.5);
      ctx.fillRect(27, 10, 2, 1); // the finger-bones in its cracks
      ctx.fillRect(27, 12, 2, 1);
      ctx.fillRect(27, 22, 2, 1);
      ctx.fillRect(3, 14, 6, 1.5); // the legs, behind
      ctx.fillRect(3, 19, 6, 1.5);
      ctx.fillStyle = BALDOR_BONES.belt; // a belt of gold and garnets
      ctx.fillRect(7, 16, 2, 3);
      ctx.fillStyle = BALDOR_BONES.garnet;
      ctx.fillRect(7.5, 17, 1, 1);
      break;
    }
    case 152: { // the closed door: a slab of stone in the passage wall, no handle, no keyhole
      ctx.fillStyle = STONE_DOOR.frame;
      ctx.fillRect(4, 1, 24, 30);
      ctx.fillStyle = STONE_DOOR.slab;
      ctx.fillRect(7, 4, 18, 27);
      ctx.fillStyle = STONE_DOOR.lit;
      ctx.fillRect(7, 4, 18, 1.5);
      ctx.fillRect(7, 4, 1.5, 27);
      ctx.strokeStyle = STONE_DOOR.crack; // the cracks a dying man's nails were in
      ctx.lineWidth = 0.8;
      ctx.beginPath();
      ctx.moveTo(16, 4);
      ctx.lineTo(16, 31);
      ctx.moveTo(11, 22);
      ctx.lineTo(13, 26);
      ctx.lineTo(12, 30);
      ctx.moveTo(20, 9);
      ctx.lineTo(22, 13);
      ctx.stroke();
      ctx.fillStyle = STONE_DOOR.scratch;
      for (const y of [24, 26, 28]) ctx.fillRect(14.5, y, 3, 0.6);
      break;
    }
    case 153: { // the Stone of Erech: a great black stone, round as a ball, half buried in its hill
      ctx.fillStyle = ERECH_STONE.shadow;
      ctx.beginPath();
      ctx.ellipse(16, 27, 14, 4, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = ERECH_STONE.stone;
      ctx.beginPath();
      ctx.arc(16, 17, 12, Math.PI * 0.95, Math.PI * 2.05);
      ctx.lineTo(28, 27);
      ctx.lineTo(4, 27);
      ctx.closePath();
      ctx.fill();
      ctx.fillStyle = ERECH_STONE.lit; // the curve of it, catching the light
      ctx.beginPath();
      ctx.arc(12, 12, 4, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = ERECH_STONE.earth; // the earth of the hill banked round its foot
      ctx.fillRect(3, 25, 26, 3);
      ctx.fillStyle = ERECH_STONE.earthDark;
      ctx.fillRect(5, 27, 22, 1);
      break;
    }
    // The Green Dragon's furniture (2026-10-19: "put some furniture in the
    // green dragon inn and an upstairs with some rooms"), seen from above as
    // the beds are: see INN.
    case 172: { // table: a round oak top, a mug left on it
      ctx.fillStyle = INN.oakDark; // its shadow edge
      ctx.beginPath();
      ctx.arc(16, 17, 12, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = INN.oak;
      ctx.beginPath();
      ctx.arc(16, 16, 11, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = INN.grain;
      ctx.lineWidth = 0.7;
      ctx.beginPath();
      for (const y of [11, 16, 21]) {
        ctx.moveTo(8, y);
        ctx.lineTo(24, y + 1);
      }
      ctx.stroke();
      ctx.strokeStyle = INN.ring; // the rings old mugs left
      ctx.beginPath();
      ctx.arc(11, 19, 2.5, 0, Math.PI * 2);
      ctx.stroke();
      drawMug(ctx, 20, 12);
      break;
    }
    case 173: { // stool: a small round seat on three legs
      ctx.fillStyle = INN.oakDark;
      for (const [x, y] of [[10, 23], [22, 23], [16, 11]]) ctx.fillRect(x - 1, y - 1, 3, 3);
      ctx.beginPath();
      ctx.arc(16, 18, 7, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = INN.oak;
      ctx.beginPath();
      ctx.arc(16, 17, 6, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = INN.oakLit;
      ctx.beginPath();
      ctx.arc(14.5, 15.5, 2.5, 0, Math.PI * 2);
      ctx.fill();
      break;
    }
    case 174: { // bar: a thick oak counter right across the tile, so two side by side are one bar
      ctx.fillStyle = INN.oakDark; // its front, towards the room
      ctx.fillRect(0, 20, 32, 8);
      ctx.fillStyle = INN.oak; // the top
      ctx.fillRect(0, 8, 32, 13);
      ctx.fillStyle = INN.oakLit; // polished by elbows along the front edge
      ctx.fillRect(0, 17, 32, 3);
      ctx.strokeStyle = INN.grain;
      ctx.lineWidth = 0.7;
      ctx.beginPath();
      ctx.moveTo(0, 12.5);
      ctx.lineTo(32, 12.5);
      ctx.stroke();
      drawMug(ctx, 11, 11);
      break;
    }
    case 175: { // hearth: a stone surround, the fire in it, a kettle on the hob
      ctx.fillStyle = INN.stoneDark;
      ctx.fillRect(3, 4, 26, 25);
      ctx.fillStyle = INN.stone;
      for (const [x, y, w, h] of [[3, 4, 9, 6], [12, 4, 8, 6], [20, 4, 9, 6], [3, 10, 5, 8], [24, 10, 5, 8], [3, 18, 5, 11], [24, 18, 5, 11]]) ctx.fillRect(x + 0.5, y + 0.5, w - 1, h - 1);
      ctx.fillStyle = INN.soot; // the firebox
      ctx.fillRect(8, 10, 16, 19);
      ctx.fillStyle = INN.ember; // the logs and the fire on them
      ctx.fillRect(10, 24, 12, 3);
      ctx.fillStyle = INN.flame;
      ctx.beginPath();
      ctx.moveTo(10, 25);
      ctx.quadraticCurveTo(12, 15, 14, 21);
      ctx.quadraticCurveTo(16, 11, 18, 21);
      ctx.quadraticCurveTo(20, 15, 22, 25);
      ctx.closePath();
      ctx.fill();
      ctx.fillStyle = INN.flameCore;
      ctx.beginPath();
      ctx.moveTo(13, 25);
      ctx.quadraticCurveTo(16, 16, 19, 25);
      ctx.closePath();
      ctx.fill();
      ctx.fillStyle = INN.kettle; // the kettle on the hob, to the right
      ctx.beginPath();
      ctx.arc(26.5, 8, 2.5, 0, Math.PI * 2);
      ctx.fill();
      break;
    }
    // The Mathom-house's museum (2026-10-19: "make an upper level to the
    // mathom house, behind inaccessible areas have "museum items" and
    // showcase some cool stuff"): the cord that keeps visitors back, and
    // the stands the exhibits are on show on.
    case 178: { // red cord: a brass post at the left, the cord slung from it across the tile
      ctx.strokeStyle = MUSEUM.cord;
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(3, 13);
      ctx.quadraticCurveTo(17, 21, 32, 13);
      ctx.stroke();
      ctx.fillStyle = MUSEUM.brassDark; // the post's foot
      ctx.beginPath();
      ctx.ellipse(3, 26, 3.5, 1.8, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = MUSEUM.brass;
      ctx.fillRect(2, 11, 2.5, 15);
      ctx.beginPath();
      ctx.arc(3.25, 11, 2.2, 0, Math.PI * 2);
      ctx.fill();
      break;
    }
    // A display stand (2026-10-20: "put them on top of the display stands,
    // which raise the items up a little bit, like boxes do in tibia if you
    // stand on them"): a block of pale stone, its top and its front with the
    // card on it. What lies on it is drawn 8 pixels up (its raise), so it
    // sits on the top.
    case 179: {
      ctx.fillStyle = MUSEUM.plinthDark; // the front
      ctx.fillRect(5, 20, 22, 10);
      ctx.fillStyle = MUSEUM.plinth; // the top
      ctx.fillRect(5, 11, 22, 9);
      ctx.fillStyle = MUSEUM.plinthLit; // the top's front edge, catching the light
      ctx.fillRect(5, 19, 22, 1);
      ctx.fillStyle = MUSEUM.plinthShade; // the foot
      ctx.fillRect(4, 29, 24, 2);
      ctx.fillStyle = MUSEUM.card; // the card on its front
      ctx.fillRect(12, 23, 8, 4);
      ctx.fillStyle = MUSEUM.ink;
      ctx.fillRect(13, 24, 6, 0.6);
      ctx.fillRect(13, 25.4, 4, 0.6);
      break;
    }
    // The Seats of Seeing and Hearing on Amon Hen and Amon Lhaw
    // (2026-10-21): a high seat of grey stone on four pillars, its back to
    // the north, with the hill's own sign cut in the back, an eye for Amon
    // Hen and the rings of a sound for Amon Lhaw.
    case 180:
    case 181: {
      drawHighSeat(ctx);
      if (id === 180) {
        ctx.fillStyle = HIGH_SEAT.carve; // an eye
        ctx.beginPath();
        ctx.moveTo(10.5, 7.5);
        ctx.quadraticCurveTo(16, 3, 21.5, 7.5);
        ctx.quadraticCurveTo(16, 12, 10.5, 7.5);
        ctx.fill();
        ctx.fillStyle = HIGH_SEAT.mark;
        ctx.beginPath();
        ctx.arc(16, 7.5, 1.8, 0, Math.PI * 2);
        ctx.fill();
      } else {
        ctx.strokeStyle = HIGH_SEAT.mark; // the rings of a sound, going out
        ctx.lineWidth = 1;
        for (const r of [1.5, 3.5, 5.5]) {
          ctx.beginPath();
          ctx.arc(16, 9.5, r, Math.PI * 1.15, Math.PI * 1.85);
          ctx.stroke();
        }
      }
      break;
    }
    // The Horn of Helm Hammerhand at the back of Helm's Deep (2026-10-22):
    // a great war-horn, pale and banded with silver, slung on a stand of
    // stone with its bell to the east.
    case 182: {
      ctx.fillStyle = HELM_HORN.shade; // the stand's shadow
      ctx.fillRect(4, 28, 25, 3);
      ctx.fillStyle = HELM_HORN.stoneDark; // the stand: a low block of the Deep's rock
      ctx.fillRect(5, 22, 23, 7);
      ctx.fillStyle = HELM_HORN.stone;
      ctx.fillRect(5, 22, 23, 4);
      ctx.fillStyle = HELM_HORN.stoneLit;
      ctx.fillRect(5, 22, 23, 1);
      // The horn, from its mouthpiece low on the west to its bell high on
      // the east, the curve of a great ox's horn: an outline, then its body.
      const hornPath = (w: number) => {
        ctx.beginPath();
        ctx.moveTo(2, 19 - w * 0.3);
        ctx.quadraticCurveTo(16, 20 - w, 24, 4 - w * 0.5);
        ctx.lineTo(31, 9 + w * 0.5);
        ctx.quadraticCurveTo(18, 25 + w * 0.6, 2, 21 + w * 0.3);
        ctx.closePath();
        ctx.fill();
      };
      ctx.fillStyle = HELM_HORN.hornDark;
      hornPath(1);
      ctx.fillStyle = HELM_HORN.horn;
      hornPath(0);
      ctx.fillStyle = HELM_HORN.hornLit; // the light along its back
      ctx.beginPath();
      ctx.moveTo(4, 19);
      ctx.quadraticCurveTo(16, 19, 23.5, 6);
      ctx.lineTo(25, 7);
      ctx.quadraticCurveTo(17, 20.5, 4, 20.2);
      ctx.closePath();
      ctx.fill();
      ctx.fillStyle = HELM_HORN.silver; // the silver mouthpiece and bands
      ctx.fillRect(1, 18.5, 3, 3.5);
      ctx.save();
      ctx.translate(11, 19.5);
      ctx.rotate(-0.25);
      ctx.fillRect(-1, -3, 2, 6);
      ctx.restore();
      ctx.save();
      ctx.translate(19, 14.5);
      ctx.rotate(-0.7);
      ctx.fillRect(-1, -4, 2, 8);
      ctx.restore();
      ctx.strokeStyle = HELM_HORN.silver; // and the rim of the bell
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(24, 3.5);
      ctx.lineTo(31.5, 9.5);
      ctx.stroke();
      ctx.fillStyle = HELM_HORN.mouth; // the dark of the bell's mouth
      ctx.beginPath();
      ctx.moveTo(25.5, 5.5);
      ctx.lineTo(29.5, 8.8);
      ctx.lineTo(27, 9);
      ctx.closePath();
      ctx.fill();
      break;
    }
    case 183: { // Herugrim: a long blade of the Mark on the sword's diagonal, a hilt of gold, a green grip
      const bx = 10, by = 24, tx = 27, ty = 5;
      const len = Math.hypot(tx - bx, ty - by);
      const ux = (tx - bx) / len, uy = (ty - by) / len;
      const px = -uy, py = ux;
      const at = (a: number, b: number): [number, number] => [bx + ux * a + px * b, by + uy * a + py * b];
      ctx.fillStyle = HERUGRIM.edge;
      bladePath(ctx, bx, by, ...at(len + 0.8, 0), 2.1);
      ctx.fill();
      ctx.fillStyle = HERUGRIM.blade;
      bladePath(ctx, bx, by, tx, ty, 1.2);
      ctx.fill();
      ctx.strokeStyle = HERUGRIM.gold;
      ctx.lineWidth = 2.5;
      ctx.lineCap = "butt";
      ctx.beginPath();
      ctx.moveTo(...at(0, -5.5));
      ctx.lineTo(...at(0, 5.5)); // the guard
      ctx.stroke();
      ctx.strokeStyle = HERUGRIM.grip;
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.moveTo(...at(-1, 0));
      ctx.lineTo(...at(-7, 0)); // the grip, bound in the Mark's green
      ctx.stroke();
      ctx.fillStyle = HERUGRIM.gold; // a gold pommel
      ctx.beginPath();
      ctx.arc(...at(-8.5, 0), 2, 0, Math.PI * 2);
      ctx.fill();
      break;
    }
    case 184: // the shield of Arthedain: a blue kite, rimmed in silver, the seven stars on it
      drawArthedainShield(ctx, 16, 3, 29, 8);
      break;
    // The mithril pieces (2026-10-27), in the mithril shirt's silver.
    case 191: // mithril shield: a silver kite with Durin's anvil and seven stars
      drawMithrilShield(ctx, 16, 3, 29, 9);
      break;
    case 192: // mithril plate: plate's harness in mithril, and the sheen
      drawArmor(ctx, MITHRIL.body, MITHRIL.shade, MITHRIL.light, (c) => {
        c.fillStyle = MITHRIL.ring;
        c.fillRect(7, 17, 18, 2); // waist band
        c.fillRect(15, 10, 2, 7); // the join down the breastplate
        c.beginPath(); // pauldrons
        c.ellipse(9, 12, 4, 3, 0, 0, Math.PI * 2);
        c.ellipse(23, 12, 4, 3, 0, 0, Math.PI * 2);
        c.fill();
        c.fillStyle = MITHRIL.sheen;
        c.fillRect(19, 12, 2, 4);
      });
      break;
    // --- The northern spots (2026-11-01): the Troll-stones, Scatha's lair
    // and the Wolf-watch.
    case 199: { // troll-stone: a hunched troll turned to grey rock, moss in its cracks
      ctx.save();
      ctx.translate(16, 31);
      ctx.scale(0.95, 0.95);
      ctx.translate(-16, -31);
      drawTroll(ctx, 2, 0, 31, TROLL_STONE_HIDE);
      ctx.restore();
      ctx.fillStyle = OLD_TROLL_MOSS; // moss where the weather sits
      for (const [x, y, r] of [[11, 9, 1.6], [20, 12, 1.3], [14, 19, 1.4], [22, 22, 1.1]] as const) {
        ctx.beginPath();
        ctx.ellipse(x, y, r, r * 0.7, 0, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.strokeStyle = "#3c3e3a"; // a crack down its middle
      ctx.lineWidth = 0.7;
      ctx.beginPath();
      ctx.moveTo(16, 6);
      ctx.lineTo(15, 12);
      ctx.lineTo(17, 17);
      ctx.lineTo(15.5, 24);
      ctx.stroke();
      break;
    }
    case 200: // Ranger's signet: a plain silver band with the Dúnedain's star on its face
      drawRing(ctx, RINGS.signet);
      drawDunedainStar(ctx, 16, 10.5, 2.2);
      break;
    case 201: { // Scatha's teeth: a string of long yellow fangs on a thong
      ctx.strokeStyle = "#5a4430";
      ctx.lineWidth = 1.2;
      ctx.beginPath();
      ctx.moveTo(4, 8);
      ctx.quadraticCurveTo(16, 20, 28, 8);
      ctx.stroke();
      for (let i = 0; i < 6; i++) {
        const t = (i + 0.5) / 6;
        const x = 4 + 24 * t;
        const y = 8 + 12 * 2 * t * (1 - t) * 1.0 + 1;
        const len = 9 - Math.abs(i - 2.5) * 1.4;
        ctx.fillStyle = "#e8dcae";
        ctx.beginPath();
        ctx.moveTo(x - 1.6, y);
        ctx.lineTo(x + 1.6, y);
        ctx.lineTo(x + 0.3, y + len);
        ctx.closePath();
        ctx.fill();
        ctx.fillStyle = "#b6a576";
        ctx.fillRect(x - 1.6, y, 3.2, 1);
      }
      break;
    }
    case 202: { // Fram's spear: an ash shaft and a blackened, notched leaf
      ctx.strokeStyle = "#8a6a46";
      ctx.lineWidth = 2.5;
      ctx.lineCap = "round";
      ctx.beginPath();
      ctx.moveTo(6, 27);
      ctx.lineTo(20, 11);
      ctx.stroke();
      ctx.strokeStyle = "#c9a227"; // the gold binding of the Éothéod at the neck
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.moveTo(17, 13);
      ctx.lineTo(20.5, 16);
      ctx.stroke();
      ctx.fillStyle = FRAM_SPEAR.blade;
      bladePath(ctx, 19, 12, 28, 3, 2.6);
      ctx.fill();
      ctx.fillStyle = FRAM_SPEAR.char;
      bladePath(ctx, 19.5, 12.5, 26, 5, 1);
      ctx.fill();
      break;
    }
    case 203: { // white wolf's pelt: the winter-wolf pelt bigger and whiter, long-furred
      ctx.save();
      ctx.translate(16, 17);
      ctx.scale(1.12, 1.12);
      ctx.translate(-16, -17);
      ctx.fillStyle = WHITE_WOLF.shade;
      for (const [x0, y0, x1, y1] of [[10, 12, 4, 8], [22, 12, 28, 8], [10, 22, 4, 27], [22, 22, 28, 27]]) {
        ctx.beginPath();
        ctx.moveTo(x0 - 2, y0);
        ctx.lineTo(x1, y1);
        ctx.lineTo(x0 + 2, y0 + 2);
        ctx.closePath();
        ctx.fill();
      }
      ctx.fillStyle = WHITE_WOLF.fur;
      ctx.beginPath();
      ctx.ellipse(16, 17, 8.5, 9.5, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.beginPath();
      ctx.ellipse(16, 7.5, 4, 3.5, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = WHITE_WOLF.shade; // the long fur, combed down
      ctx.lineWidth = 0.6;
      ctx.beginPath();
      for (const x of [11, 14, 18, 21]) {
        ctx.moveTo(x, 12);
        ctx.lineTo(x + 0.5, 22);
      }
      ctx.stroke();
      ctx.fillStyle = WHITE_WOLF.eye;
      ctx.fillRect(14, 7, 1.2, 1);
      ctx.fillRect(17, 7, 1.2, 1);
      ctx.restore();
      break;
    }
    case 204: { // Iron Hills mattock: a long haft, a heavy iron head, pick one side and blade the other
      const len = Math.hypot(14, 21);
      const ux = 14 / len, uy = -21 / len;
      drawPick(ctx, (a, b) => [7 + ux * a - uy * b, 29 + uy * a + ux * b], len, 3, 1.25);
      break;
    }
    case 210: // the Arkenstone (2026-11-01)
      drawArkenstone(ctx, 16, 18, 1);
      break;
    case 211: { // Smaug's hoard: gold heaped in drifts, a cup and a helm in it, and the Arkenstone's white light deep in it
      const gold = ["#8a6418", "#c8961e", "#e8bd3a"];
      for (const [x, y, rx, ry] of [[10, 23, 9, 6], [22, 22, 9, 7], [16, 18, 8, 6]] as const) {
        ctx.fillStyle = gold[0];
        ctx.beginPath();
        ctx.ellipse(x, y, rx, ry, 0, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = gold[1];
        ctx.beginPath();
        ctx.ellipse(x - 1, y - 1, rx - 2, ry - 2, 0, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.fillStyle = gold[2]; // coins catching the light
      for (const [x, y] of [[7, 21], [12, 24], [19, 20], [24, 23], [15, 15], [27, 20], [9, 26]]) ctx.fillRect(x, y, 2, 1.2);
      ctx.fillStyle = "#b07a14"; // a cup on its side
      ctx.fillRect(22, 14, 5, 4);
      ctx.fillRect(23.5, 18, 2, 2);
      ctx.fillStyle = "#7b8794"; // a helm half sunk
      ctx.beginPath();
      ctx.arc(8, 19, 3, Math.PI, 0);
      ctx.fill();
      ctx.fillStyle = "rgba(240,246,255,0.55)"; // the white light under it
      ctx.beginPath();
      ctx.arc(15, 21, 4, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(14, 20, 2, 2);
      break;
    }
    // --- end of the northern spots
    case 193: { // mithril spear: a dark shaft and a long bright leaf
      ctx.strokeStyle = MITHRIL_SPEAR.shaft;
      ctx.lineWidth = 2.5;
      ctx.lineCap = "round";
      ctx.beginPath();
      ctx.moveTo(6, 27);
      ctx.lineTo(20, 11);
      ctx.stroke();
      ctx.strokeStyle = MITHRIL.shade; // the binding at the neck
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.moveTo(17, 13);
      ctx.lineTo(20.5, 16);
      ctx.stroke();
      ctx.fillStyle = MITHRIL.body;
      bladePath(ctx, 19, 12, 29, 2, 2.6); // the leaf, longer than steel's
      ctx.fill();
      ctx.fillStyle = MITHRIL.light;
      bladePath(ctx, 19.5, 12.5, 28, 3.5, 1);
      ctx.fill();
      break;
    }
    // The belts of Lórien (2026-10-22): the belt coiled loose on the
    // ground, its tail end lying out of the coil, and its clasp on top.
    case 185:
    case 186: {
      const pal = BELTS[id];
      ctx.lineCap = "round";
      ctx.strokeStyle = pal.shade;
      ctx.lineWidth = 4;
      ctx.beginPath();
      ctx.ellipse(16, 19, 9, 6, 0, 0, Math.PI * 2);
      ctx.stroke();
      ctx.strokeStyle = pal.band;
      ctx.lineWidth = 2.5;
      ctx.beginPath();
      ctx.ellipse(16, 18.5, 9, 6, 0, 0, Math.PI * 2);
      ctx.stroke();
      ctx.beginPath(); // the tail, out of the coil to the right
      ctx.moveTo(23, 22);
      ctx.quadraticCurveTo(27, 25, 29, 24);
      ctx.stroke();
      ctx.lineCap = "butt";
      drawBeltClasp(ctx, id, 16, 12.5, 1.6);
      break;
    }
    // The Glittering Caves under Helm's Deep (2026-10-22): a column of
    // crystal, a cluster of tall points grown up from the floor, white and
    // rose and pale green, each lit down one face and sparkling.
    case 187: {
      ctx.fillStyle = AGLAROND.shade; // where it stands
      ctx.beginPath();
      ctx.ellipse(16, 29, 12, 2.5, 0, 0, Math.PI * 2);
      ctx.fill();
      // Back to front: [x of the base's middle, half its width, height of the point, colour].
      const points: [number, number, number, keyof typeof AGLAROND.glass][] = [
        [10, 3, 15, "green"],
        [22, 3, 13, "rose"],
        [16, 4.5, 2, "white"],
        [7, 2.5, 20, "rose"],
        [25, 2.5, 19, "white"],
        [13, 2.5, 11, "green"],
        [19.5, 2.5, 9, "white"],
      ];
      for (const [x, hw, top, col] of points) {
        const g = AGLAROND.glass[col];
        ctx.fillStyle = g.dark; // the shadowed face
        ctx.beginPath();
        ctx.moveTo(x - hw, 30);
        ctx.lineTo(x - hw, top + hw * 1.6);
        ctx.lineTo(x, top);
        ctx.lineTo(x + hw, top + hw * 1.6);
        ctx.lineTo(x + hw, 30);
        ctx.closePath();
        ctx.fill();
        ctx.fillStyle = g.body; // the face towards the light
        ctx.beginPath();
        ctx.moveTo(x - hw * 0.4, 30);
        ctx.lineTo(x - hw * 0.4, top + hw * 1.2);
        ctx.lineTo(x, top);
        ctx.lineTo(x + hw, top + hw * 1.6);
        ctx.lineTo(x + hw, 30);
        ctx.closePath();
        ctx.fill();
        ctx.fillStyle = g.lit; // and the edge that catches it
        ctx.fillRect(x - hw * 0.4, top + hw * 1.2, 1, 30 - top - hw * 1.2);
      }
      ctx.fillStyle = AGLAROND.spark; // the glitter
      for (const [x, y] of [[16, 5], [7.5, 22], [25, 21], [12, 16], [21, 12]]) {
        ctx.fillRect(x - 0.5, y - 2, 1, 4);
        ctx.fillRect(x - 2, y - 0.5, 4, 1);
      }
      break;
    }
    // The heart-crystal of Aglarond (2026-10-22): one great clear crystal
    // lying on its side, a light deep in it.
    case 188: {
      ctx.fillStyle = AGLAROND.shade;
      ctx.beginPath();
      ctx.ellipse(16, 25, 12, 2.5, 0, 0, Math.PI * 2);
      ctx.fill();
      const g = AGLAROND.glass.white;
      ctx.fillStyle = g.dark;
      ctx.beginPath();
      ctx.moveTo(4, 19);
      ctx.lineTo(9, 13);
      ctx.lineTo(23, 11);
      ctx.lineTo(29, 17);
      ctx.lineTo(23, 24);
      ctx.lineTo(9, 25);
      ctx.closePath();
      ctx.fill();
      ctx.fillStyle = g.body;
      ctx.beginPath();
      ctx.moveTo(5, 18);
      ctx.lineTo(9, 13);
      ctx.lineTo(23, 11);
      ctx.lineTo(28, 17);
      ctx.lineTo(9, 19);
      ctx.closePath();
      ctx.fill();
      ctx.fillStyle = g.lit;
      ctx.fillRect(9, 13, 14, 1);
      const glow = ctx.createRadialGradient(16, 18, 0, 16, 18, 6); // the star caught in it
      glow.addColorStop(0, AGLAROND.heart);
      glow.addColorStop(1, "rgba(255,255,255,0)");
      ctx.fillStyle = glow;
      ctx.fillRect(10, 12, 12, 12);
      ctx.fillStyle = AGLAROND.spark;
      ctx.fillRect(15.5, 15, 1, 6);
      ctx.fillRect(13, 17.5, 6, 1);
      break;
    }
    case 189: // dragon ham: the haunch, greenish and scaled (2026-10-23)
      drawDragonHam(ctx);
      break;
    case 190: {
      // The experience door under Durin's Bane (2026-10-27): a slab of
      // black stone polished like still water in a frame of grey, with no
      // handle, and a band of silver dwarf-runes across it.
      ctx.fillStyle = "#4a4c50"; // the frame
      ctx.fillRect(2, 2, 28, 30);
      ctx.fillStyle = "#16171b"; // the slab
      ctx.fillRect(5, 5, 22, 27);
      ctx.fillStyle = "#2a2d35"; // the shine down one side of it
      ctx.fillRect(7, 7, 2, 22);
      ctx.fillStyle = "#c9d3dc"; // the runes: upright strokes and tags
      for (const x of [9, 13, 17, 21]) ctx.fillRect(x, 13, 1, 5);
      ctx.fillRect(10, 13, 2, 1);
      ctx.fillRect(14, 15, 2, 1);
      ctx.fillRect(18, 17, 2, 1);
      ctx.fillRect(22, 13, 2, 1);
      ctx.fillStyle = "#8a96a2"; // the lines ruled above and below them
      ctx.fillRect(7, 11, 18, 1);
      ctx.fillRect(7, 20, 18, 1);
      break;
    }
    case 194: {
      // The board by the stair down to Smaug's lair (2026-10-27): the
      // bridge notice's shape on a post, charred black round its edges,
      // with SMAUG cut across it in red.
      ctx.fillStyle = "#4a3a2c";
      ctx.fillRect(15, 17, 2.5, 12); // the post
      ctx.save();
      ctx.translate(16, 13);
      ctx.rotate(0.07);
      ctx.fillStyle = "#1c1612"; // the char round the edges
      ctx.fillRect(-13, -9, 26, 17);
      ctx.fillStyle = "#7a6248"; // the board left unburnt
      ctx.fillRect(-11, -7, 22, 13);
      ctx.fillStyle = "#3a2a20"; // a bite burnt out of one corner
      ctx.fillRect(7, -7, 4, 3);
      ctx.fillStyle = "#c0281c"; // the letters
      ctx.font = "bold 7px sans-serif";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText("SMAUG", 0, 0);
      ctx.restore();
      break;
    }
    // The Blue Tower of Rhûn (2026-11-01).
    case 195: { // staff of the Ithryn Luin: pale wood shod in blue-black iron, blue fire at its head
      ctx.strokeStyle = BLUE_STAFF.wood;
      ctx.lineWidth = 2.4;
      ctx.lineCap = "round";
      ctx.beginPath();
      ctx.moveTo(8, 27);
      ctx.lineTo(23, 8);
      ctx.stroke();
      ctx.strokeStyle = BLUE_STAFF.iron; // the shoe and the collar
      ctx.beginPath();
      ctx.moveTo(8, 27);
      ctx.lineTo(10, 24.5);
      ctx.moveTo(21, 10.5);
      ctx.lineTo(22.2, 9);
      ctx.stroke();
      ctx.fillStyle = BLUE_STAFF.glow;
      ctx.beginPath();
      ctx.arc(24, 7, 4, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = BLUE_STAFF.fire;
      ctx.beginPath();
      ctx.arc(24, 7, 2.4, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = BLUE_STAFF.heart;
      ctx.fillRect(23.4, 6.4, 1.2, 1.2);
      break;
    }
    case 196: // blue robe: a cape of deep sea-blue, faded, a silver clasp
      drawCape(ctx, { body: BLUE_ROBE.body, lining: BLUE_ROBE.shade, fold: BLUE_ROBE.fold, light: BLUE_ROBE.light }, false, (c) => {
        c.fillStyle = BLUE_ROBE.clasp;
        c.fillRect(15, 7, 2, 2);
      });
      break;
    case 197: { // blue-glass token: a pendant of blue glass on a thong
      ctx.strokeStyle = "#6a4a2a"; // the thong
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(10, 8);
      ctx.quadraticCurveTo(16, 16, 22, 8);
      ctx.stroke();
      ctx.fillStyle = "#2a5aa8";
      ctx.beginPath();
      ctx.arc(16, 19, 5, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = "#6aa8ff";
      ctx.beginPath();
      ctx.arc(15, 18, 3, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = "#dceaff"; // the sign pressed into it
      ctx.beginPath();
      ctx.moveTo(14, 21);
      ctx.lineTo(18, 17);
      ctx.moveTo(18, 21);
      ctx.lineTo(14, 17);
      ctx.stroke();
      break;
    }
    case 198: { // wizard's journal: a book bound in blue leather, swollen with damp
      ctx.fillStyle = "#e8e0c8"; // the pages' edge
      ctx.fillRect(9, 9, 15, 16);
      ctx.fillStyle = "#1e3a6a"; // the cover
      ctx.fillRect(8, 8, 15, 16);
      ctx.fillStyle = "#14284c"; // the spine
      ctx.fillRect(8, 8, 3, 16);
      ctx.strokeStyle = "#8ab4e8"; // the sign on the cover
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(13, 20);
      ctx.lineTo(20, 12);
      ctx.moveTo(20, 20);
      ctx.lineTo(13, 12);
      ctx.stroke();
      ctx.fillStyle = "rgba(60,70,60,0.35)"; // the damp, a stain at one corner
      ctx.fillRect(18, 19, 5, 5);
      break;
    }
    // end of the Blue Tower
    // The Pits of Dol Guldur (2026-11-01).
    case 205: { // keys of Dol Guldur: a ring of black iron keys
      ctx.strokeStyle = "#2a2a30";
      ctx.lineWidth = 1.6;
      ctx.beginPath();
      ctx.arc(13, 12, 5, 0, Math.PI * 2);
      ctx.stroke();
      ctx.fillStyle = "#3a3a42";
      for (const [x, y, r] of [[16, 15, 0.5], [12, 17, -0.2], [9, 15, -0.7]] as const) {
        ctx.save();
        ctx.translate(x, y);
        ctx.rotate(r);
        ctx.fillRect(-1, 0, 2, 10); // the shank
        ctx.fillRect(1, 7, 3, 1.5); // the bit
        ctx.fillRect(1, 9.5, 2, 1.5);
        ctx.restore();
      }
      break;
    }
    case 206: { // Thrór's map: a sheet of old parchment, the Mountain drawn on it, a red hand pointing
      ctx.fillStyle = "#c8b48a";
      ctx.fillRect(6, 7, 20, 17);
      ctx.fillStyle = "#a8946a"; // its edges, worn
      ctx.fillRect(6, 7, 20, 1);
      ctx.fillRect(6, 23, 20, 1);
      ctx.strokeStyle = "#4a3a24"; // the Mountain
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(9, 20);
      ctx.lineTo(16, 10);
      ctx.lineTo(23, 20);
      ctx.moveTo(12, 20);
      ctx.lineTo(16, 14);
      ctx.lineTo(20, 20);
      ctx.stroke();
      ctx.fillStyle = "#a8281c"; // the hand, at the side door
      ctx.fillRect(9, 12, 3, 2);
      ctx.fillStyle = "#6a5a40"; // runes along the foot
      for (const x of [9, 12, 15, 18, 21]) ctx.fillRect(x, 21.5, 1.5, 1);
      break;
    }
    case 207: { // Thrór's key: silver, a long barrel and intricate wards
      ctx.save();
      ctx.translate(16, 16);
      ctx.rotate(-0.7);
      ctx.strokeStyle = "#d8dce4";
      ctx.lineWidth = 1.6;
      ctx.beginPath();
      ctx.arc(0, -7, 3, 0, Math.PI * 2); // the bow
      ctx.stroke();
      ctx.fillStyle = "#c8ccd4";
      ctx.fillRect(-1, -4, 2, 13); // the barrel
      ctx.fillRect(1, 5, 3, 1.5); // the wards
      ctx.fillRect(1, 7.5, 4, 1.5);
      ctx.fillStyle = "#f4f6fa";
      ctx.fillRect(-0.5, -3, 1, 4);
      ctx.restore();
      break;
    }
    case 208: // broken tomb: a door of grey stone cut into the rock, its iron bands burst, black inside
    case 209: { // the Witch-king's tomb: the same, wider and taller, a crown carved over it
      const wk = id === 209;
      const w = wk ? 24 : 18, h = wk ? 27 : 22, x0 = 16 - w / 2, y0 = 30 - h;
      ctx.fillStyle = "#4a4c52"; // the rock it is cut into
      ctx.fillRect(x0 - 2, y0 - 2, w + 4, h + 2);
      ctx.fillStyle = "#08080a"; // the dark of the tomb
      ctx.fillRect(x0 + 2, y0 + 3, w - 4, h - 3);
      ctx.fillStyle = "#6a6c72"; // the slab, thrown back askew
      ctx.save();
      ctx.translate(x0 + 2, y0 + 3);
      ctx.rotate(-0.25);
      ctx.fillRect(0, 0, 4, h - 6);
      ctx.restore();
      ctx.strokeStyle = "#5a3a2a"; // the bands, burst outward
      ctx.lineWidth = 1.2;
      ctx.beginPath();
      ctx.moveTo(x0 + 2, y0 + 8);
      ctx.lineTo(x0 - 1, y0 + 6);
      ctx.moveTo(x0 + w - 2, y0 + 8);
      ctx.lineTo(x0 + w + 1, y0 + 5);
      ctx.moveTo(x0 + 2, y0 + h - 6);
      ctx.lineTo(x0 - 1, y0 + h - 4);
      ctx.moveTo(x0 + w - 2, y0 + h - 6);
      ctx.lineTo(x0 + w + 1, y0 + h - 8);
      ctx.stroke();
      if (wk) {
        ctx.fillStyle = "#8a8c92"; // the crown carved over the door
        ctx.fillRect(12, y0, 8, 2);
        for (const x of [12, 15, 18]) ctx.fillRect(x, y0 - 2, 2, 2);
      }
      break;
    }
    // end of Dol Guldur
    default:
      ctx.fillStyle = "#f0f";
      ctx.fillRect(10, 10, 12, 12);
  }
}

/** The crystals of the Glittering Caves (2026-10-22): white, rose and pale green glass, and their glitter. */
const AGLAROND = {
  glass: {
    white: { body: "#dfeaf2", lit: "#ffffff", dark: "#8fa4b4" },
    rose: { body: "#efc6d2", lit: "#fff0f4", dark: "#a07686" },
    green: { body: "#c8ecd8", lit: "#f0fff6", dark: "#729c86" },
  },
  spark: "#ffffff",
  heart: "rgba(255,246,214,0.95)",
  shade: "rgba(10,12,16,0.35)",
};

/** The shield of Arthedain (2026-10-22): deep blue, rimmed in silver, with the seven stars of Elendil. */
const ARTHEDAIN = { field: "#1f3566", fieldLit: "#2c4a86", rim: "#c9d2dc", star: "#f2f5f8" };

/**
 * The shield of Arthedain, out of Carn Dûm, from top to bottom about cx
 * with half width w: a kite shield, the steel shield's shape, its blue
 * field lit down the left, a silver rim, and the seven stars of Elendil
 * in a ring over the middle.
 */
function drawArthedainShield(ctx: Ctx, cx: number, top: number, bottom: number, w: number) {
  const h = bottom - top;
  const outline = () => {
    ctx.beginPath();
    ctx.moveTo(cx - w, top);
    ctx.lineTo(cx + w, top);
    ctx.lineTo(cx + w, top + h * 0.4);
    ctx.quadraticCurveTo(cx + w, bottom - 1, cx, bottom);
    ctx.quadraticCurveTo(cx - w, bottom - 1, cx - w, top + h * 0.4);
    ctx.closePath();
  };
  ctx.fillStyle = ARTHEDAIN.field;
  outline();
  ctx.fill();
  ctx.fillStyle = ARTHEDAIN.fieldLit;
  ctx.fillRect(cx - w + 1, top + 1, w * 0.6, h * 0.45);
  ctx.fillStyle = ARTHEDAIN.star;
  const r = Math.max(0.5, w * 0.09);
  for (let i = 0; i < 7; i++) {
    const a = -Math.PI / 2 + (2 * Math.PI * i) / 7;
    const x = cx + Math.cos(a) * w * 0.5;
    const y = top + h * 0.4 + Math.sin(a) * w * 0.5;
    ctx.fillRect(x - r, y - r, r * 2, r * 2);
  }
  ctx.strokeStyle = ARTHEDAIN.rim;
  ctx.lineWidth = w > 6 ? 1.5 : 1;
  outline();
  ctx.stroke();
}

/** The mithril spear's shaft (2026-10-27): dark wood under the bright leaf. */
const MITHRIL_SPEAR = { shaft: "#4a3626" };

/**
 * The mithril shield (2026-10-27), from top to bottom about cx with half
 * width w: the steel shield's kite in the mithril shirt's silver, lit
 * down the left, and Durin's emblem in its face, as on the Doors of
 * Durin: an anvil under an arc of seven stars.
 */
function drawMithrilShield(ctx: Ctx, cx: number, top: number, bottom: number, w: number) {
  const h = bottom - top;
  const outline = () => {
    ctx.beginPath();
    ctx.moveTo(cx - w, top);
    ctx.lineTo(cx + w, top);
    ctx.lineTo(cx + w, top + h * 0.4);
    ctx.quadraticCurveTo(cx + w, bottom - 1, cx, bottom);
    ctx.quadraticCurveTo(cx - w, bottom - 1, cx - w, top + h * 0.4);
    ctx.closePath();
  };
  ctx.fillStyle = MITHRIL.ring; // a deeper silver, for the stars to show on
  outline();
  ctx.fill();
  ctx.fillStyle = MITHRIL.body;
  ctx.fillRect(cx - w + 1.5, top + 1.5, w * 0.3, h * 0.45);
  // The anvil: a broad face on a narrow waist and a foot.
  const ay = top + h * 0.5, u = w / 9;
  ctx.fillStyle = MITHRIL.shade;
  ctx.fillRect(cx - 3.5 * u, ay, 7 * u, 1.6 * u);
  ctx.fillRect(cx - 1.2 * u, ay + 1.6 * u, 2.4 * u, 1.8 * u);
  ctx.fillRect(cx - 2.6 * u, ay + 3.4 * u, 5.2 * u, 1.2 * u);
  // The seven stars in an arc over it.
  ctx.fillStyle = "#ffffff";
  const r = Math.max(0.5, w * 0.08);
  for (let i = 0; i < 7; i++) {
    const a = Math.PI + (Math.PI * i) / 6;
    const x = cx + Math.cos(a) * w * 0.55;
    const y = ay - 1 + Math.sin(a) * w * 0.45;
    ctx.fillRect(x - r, y - r, r * 2, r * 2);
  }
  ctx.strokeStyle = MITHRIL.ring;
  ctx.lineWidth = w > 6 ? 1.5 : 1;
  outline();
  ctx.stroke();
}

/** Herugrim, Théoden's sword (2026-10-22): bright steel, a hilt of gold, a grip bound in the Mark's green. */
const HERUGRIM = { blade: "#eef1f4", edge: "#8f989f", gold: "#e0b23a", grip: "#2f5a2a" };
/** The Horn of Helm Hammerhand (2026-10-22): pale horn banded with silver, on the Deep's grey rock. */
const HELM_HORN = { horn: "#d9c9a0", hornLit: "#f1e6c8", hornDark: "#6e5a36", mouth: "#3a2e1c", silver: "#dfe6ec", stone: "#8e8a80", stoneLit: "#aca89c", stoneDark: "#5c5952", shade: "rgba(20,18,14,0.35)" };

/** Náin's axe (item 120): a dwarf war-axe off the field of Azanulbizar. */
const NAIN_AXE = {
  haft: "#6a4a2c",
  char: "#2a1d14",
  band: "#555b61",
  steel: "#a9b0b6",
  dark: "#3d4248",
  rune: "#d8c27a",
};

/**
 * 10. Drúwaith Iaur (2026-10-16): the Púkel-men (item 133), squat grey
 * stone men sitting cross-legged at the corners of the camps, and the
 * faithful stone (item 134), a little one of a darker stone with chips of
 * black for eyes.
 */
const PUKEL = {
  stone: "#8e8a80", shade: "#625e56", line: "#4a4740", shadow: "rgba(0, 0, 0, 0.3)",
  faithful: "#7a6e60", faithfulShade: "#544a40", chips: "#121010",
};

/**
 * A Púkel-man sitting on the ground at (cx, base), scale 1 the size of a
 * tile: crossed legs for a base, a pot belly, fists on the knees, a round
 * head under a flat cap, and a wide blunt grin. eyes, if given, are drawn
 * as two chips of that colour; a plain one has hollows the shade of the
 * stone.
 */
function drawPukelMan(ctx: Ctx, cx: number, base: number, s: number, stone: string, shade: string, eyes?: string) {
  ctx.save();
  ctx.translate(cx, base);
  ctx.scale(s, s);
  ctx.fillStyle = PUKEL.shadow;
  ctx.beginPath();
  ctx.ellipse(0, -1, 12, 3, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = shade; // the crossed legs, a flat lump for a base
  ctx.beginPath();
  ctx.ellipse(0, -4, 11, 4.2, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = stone; // the pot belly and the chest
  ctx.beginPath();
  ctx.ellipse(0, -11, 7.5, 7.5, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = shade; // the arms down to fists on the knees
  ctx.beginPath();
  ctx.ellipse(-7.5, -8, 2.4, 4.4, 0.35, 0, Math.PI * 2);
  ctx.ellipse(7.5, -8, 2.4, 4.4, -0.35, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = stone;
  ctx.beginPath();
  ctx.arc(-8.5, -5, 2.2, 0, Math.PI * 2);
  ctx.arc(8.5, -5, 2.2, 0, Math.PI * 2);
  ctx.fill();
  ctx.beginPath(); // the head, sunk into the shoulders
  ctx.ellipse(0, -21, 5.6, 5, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = shade; // the flat round cap
  ctx.beginPath();
  ctx.ellipse(0, -25, 5.8, 2.2, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = eyes ?? PUKEL.line; // the eyes, and the grin under them
  ctx.fillRect(-3.2, -22.5, 2, 1.6);
  ctx.fillRect(1.2, -22.5, 2, 1.6);
  ctx.strokeStyle = PUKEL.line;
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(-3, -18.8);
  ctx.quadraticCurveTo(0, -17, 3, -18.8);
  ctx.moveTo(-4, -11); // the belly's crease
  ctx.quadraticCurveTo(0, -8.5, 4, -11);
  ctx.stroke();
  ctx.restore();
}

/** Durin's Stone (item 121), the broken pillar by the Mirrormere. */
const DURINS_STONE = {
  shadow: "rgba(0, 0, 0, 0.3)",
  side: "#6f7479",
  face: "#9aa0a5",
  rune: "#5a5f64",
  star: "#e8f0ff",
};

// Dunharrow and the Paths of the Dead (2026-10-16, site 7 of the empty
// areas' proposal).
/** Baldor's helm (item 149): a Rohirric helm made a king's son's. */
const BALDOR_HELM = { steel: "#9da4ab", lit: "#d0d6db", rim: "#565b60", gold: "#d4a82a", garnet: "#8a1626", tail: "#b8b4aa" };
/** The crown of the King of the Dead (item 150): black, dim, cold. */
const DEAD_CROWN = { shadow: "rgba(0, 0, 0, 0.3)", iron: "#2c3034", lit: "#5a6268", glow: "#bfe0d8" };
/** Baldor's bones (item 151), fallen before the closed door. */
const BALDOR_BONES = { shadow: "rgba(0, 0, 0, 0.25)", mail: "#5c6064", ring: "#8a9096", bone: "#ddd3bb", belt: "#c9a227", garnet: "#8a1626" };
/** The closed door in the Paths of the Dead (item 152). */
const STONE_DOOR = { frame: "#3a3632", slab: "#5e5852", lit: "#7a746c", crack: "#2a2622", scratch: "#9a9288" };
/** The Stone of Erech (item 153). */
const ERECH_STONE = { shadow: "rgba(0, 0, 0, 0.35)", stone: "#1e1e22", lit: "#3a3a42", earth: "#7a6248", earthDark: "#5a4632" };

/** What each kind of blood is made of, for mixing pools on one tile. */
export const BLOOD_RGB: Record<number, [number, number, number]> = {
  // Bright enough to read against dark dirt and swamp, which is where most of
  // it gets spilled; the rim drawn under each pool supplies the darkness.
  13: [190, 24, 24], // red
  14: [96, 168, 48], // troll green
  15: [128, 28, 40], // orc dark
};

/**
 * The lobes of a pool. Loot and creatures are drawn centred, so a pool kept to
 * the middle would simply vanish under them; these reach out towards the edges
 * so the mess stays readable with a sword lying in it. Later lobes only appear
 * once there is enough blood to justify them.
 */
const POOL_LOBES: [number, number, number, number][] = [
  // x, y, radius, the amount of blood at which it appears
  [16, 17, 7, 1],
  [9, 14, 5.5, 1],
  [23, 19, 5.5, 2],
  [12, 22, 5, 3],
  [21, 11, 5, 4],
  [6, 20, 4.5, 6],
  [26, 13, 4.5, 8],
  [16, 26, 4.5, 10],
];

const bloodCache = new Map<string, HTMLCanvasElement>();

/**
 * A pool of the given colour and volume. Cached per colour and amount, since
 * a tile's mix only changes when something else dies on it.
 */
export function bloodSprite(rgb: [number, number, number], amount: number): HTMLCanvasElement {
  const n = Math.max(1, Math.min(12, Math.round(amount)));
  const key = `${rgb[0]},${rgb[1]},${rgb[2]}|${n}`;
  let c = bloodCache.get(key);
  if (c) return c;
  const [cv, ctx] = canvas(TILE, TILE);
  const lobes = POOL_LOBES.filter(([, , , at]) => n >= at);
  const grow = (at: number) => 1 + Math.min(0.35, (n - at) * 0.05);
  // A darker rim first, one pixel larger, so the pool has an edge whatever
  // the ground colour: on bright grass it is the liquid that shows, on dark
  // swamp it is the outline. Drawn as one path so overlapping lobes do not
  // stack their alpha into blotches.
  ctx.fillStyle = `rgb(${Math.round(rgb[0] * 0.45)}, ${Math.round(rgb[1] * 0.45)}, ${Math.round(rgb[2] * 0.45)})`;
  ctx.globalAlpha = 0.85;
  ctx.beginPath();
  for (const [x, y, r, at] of lobes) {
    ctx.moveTo(x + r * grow(at) + 1, y);
    ctx.ellipse(x, y, r * grow(at) + 1, r * grow(at) * 0.68 + 1, (x + y) % 3, 0, Math.PI * 2);
  }
  ctx.fill();
  // Then the liquid itself, opaque enough that a big pool is unmistakable.
  ctx.fillStyle = `rgb(${rgb[0]}, ${rgb[1]}, ${rgb[2]})`;
  ctx.globalAlpha = 0.8 + 0.15 * Math.min(1, n / 6);
  ctx.beginPath();
  for (const [x, y, r, at] of lobes) {
    ctx.moveTo(x + r * grow(at), y);
    ctx.ellipse(x, y, r * grow(at), r * grow(at) * 0.68, (x + y) % 3, 0, Math.PI * 2);
  }
  ctx.fill();
  // A wet highlight on the biggest lobe, which is what says liquid rather
  // than paint.
  ctx.globalAlpha = 0.28;
  ctx.fillStyle = "#fff";
  ctx.beginPath();
  ctx.ellipse(14, 14, 3, 1.6, -0.5, 0, Math.PI * 2);
  ctx.fill();
  ctx.globalAlpha = 1;
  bloodCache.set(key, cv);
  return cv;
}

/** What each spatter is made of, matching the pool of the same blood. */
export const SPATTER_RGB: Record<number, [number, number, number]> = {
  17: [190, 24, 24],
  18: [96, 168, 48],
  19: [128, 28, 40],
  20: [226, 219, 199], // skeleton bone chips
};

const spatterCache = new Map<string, HTMLCanvasElement>();

/**
 * Thrown droplets rather than a body of liquid: small, scattered wide, and
 * elongated along the direction they flew. This is what a wound leaves; a
 * pool only forms where something died.
 */
export function spatterSprite(rgb: [number, number, number], amount: number): HTMLCanvasElement {
  const n = Math.max(1, Math.min(12, Math.round(amount)));
  const key = `${rgb[0]},${rgb[1]},${rgb[2]}|${n}`;
  let c = spatterCache.get(key);
  if (c) return c;
  const [cv, ctx] = canvas(TILE, TILE);
  ctx.fillStyle = `rgb(${rgb[0]}, ${rgb[1]}, ${rgb[2]})`;
  const drops = 5 + n * 3;
  for (let i = 0; i < drops; i++) {
    // Golden-angle scatter: even coverage without clumping or a visible grid.
    const a = i * 2.399;
    const rad = 3 + ((i * 7) % 13) + (i % 3);
    const x = 16 + Math.cos(a) * rad;
    const y = 16 + Math.sin(a) * rad * 0.9;
    if (x < 1 || x > TILE - 1 || y < 1 || y > TILE - 1) continue;
    const len = 1 + ((i * 5) % 3) * 0.6;
    ctx.globalAlpha = 0.7 + ((i * 3) % 4) * 0.1;
    ctx.beginPath();
    ctx.ellipse(x, y, len, len * 0.55, a, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.globalAlpha = 1;
  spatterCache.set(key, cv);
  return cv;
}

const itemCache = new Map<string, HTMLCanvasElement>();

/**
 * Items and creatures are drawn with the same flat fills as the ground under
 * them, so on their own they sink into it: a steel axe on the castle's grey
 * flagstones all but disappears. Each sprite is finished with a dark
 * one-pixel outline around its silhouette, and a soft shadow if asked for,
 * which lifts it off any tile without every drawing case needing to know
 * what it will sit on. Creatures get no shadow: their feet touch the bottom
 * of the canvas, so there is nowhere for one to fall.
 */
function outlined(raw: HTMLCanvasElement, w: number, h: number, shadow: boolean, edge?: string): HTMLCanvasElement {
  // The silhouette: the sprite's alpha filled with a single dark colour.
  const silhouette = (fill: string) => {
    const [sil, sctx] = canvas(w, h);
    sctx.drawImage(raw, 0, 0);
    sctx.globalCompositeOperation = "source-in";
    sctx.fillStyle = fill;
    sctx.fillRect(0, 0, w, h);
    return sil;
  };
  const sil = silhouette("rgba(0,0,0,0.8)");

  const [cv, ctx] = canvas(w, h);
  if (shadow) {
    // Shadow: the silhouette pushed down a touch and faded.
    ctx.globalAlpha = 0.35;
    ctx.drawImage(sil, 0, 2);
    ctx.globalAlpha = 1;
  }
  // Outline: the silhouette stamped one pixel out in each direction, in
  // the colour of the thing's make if it has one (2026-10-21: "can we put
  // an outline around the object of the color? a bit like it's done in
  // castle of the winds?"), and dark for everything else.
  const line = edge ? silhouette(edge) : sil;
  for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1]]) {
    ctx.drawImage(line, dx, dy);
  }
  ctx.drawImage(raw, 0, 0);
  return cv;
}

/**
 * The colour of the outline round a thing of each make (see make.go on the
 * server): gold for dwarf-wrought, blue for Gondolin, cyan for elven,
 * violet for Arnor-forged (white was lost against pale steel), rust for
 * orc-made, green for Morgul and red for dragon-gilded.
 */
export const MAKE_EDGE: Record<string, string> = {
  dwarf: "#f0c040",
  gondolin: "#1e6bff",
  elven: "#4fd8e8",
  arnor: "#b36bff",
  orc: "#c8641e",
  morgul: "#7fe05a",
  gilded: "#ff3b3b",
};

/** The outline colour for a thing of make m, or undefined for the plain dark one. */
export function makeEdge(m?: string): string | undefined {
  return m ? MAKE_EDGE[m] : undefined;
}

/**
 * An item's sprite, cached. The colour is part of the key and not merely
 * part of the drawing: a cache keyed on the id alone hands the first skull
 * drawn to every skull after it, so a battlefield of four players' heads
 * would come out all one colour with nothing anywhere to say so. That is
 * the shape of bug this project has shipped twice, so the colour goes in
 * the key beside the id and the two are the only things drawItem is given.
 */
export function itemSprite(id: number, color?: string, make?: string): HTMLCanvasElement {
  const edge = makeEdge(make);
  const key = `${id}|${color ?? ""}|${edge ?? ""}`;
  let c = itemCache.get(key);
  if (!c) {
    const [raw, ctx] = canvas(TILE, TILE);
    if (mirrored(id)) mirrorAbout(ctx, TILE / 2);
    drawItem(ctx, id, color);
    c = outlined(raw, TILE, TILE, true, edge);
    itemCache.set(key, c);
  }
  return c;
}

/**
 * The giant sword as it lies on the ground: the blade corner to corner of
 * the tile, the guard across it, the grip and pommel back off the other
 * corner. One statement of the numbers, read by two things — the drawing
 * in drawItem, and giantSwordLieBox, which works out how much of the tile
 * the drawing actually covers so that groundSprite can centre it. edge is
 * the blade's half-width at the guard, iron the stroke of the guard and
 * grip, and the rest are lengths along the blade's own line from the
 * guard: positive towards the point, negative down the grip.
 */
export const GIANT_SWORD_LIE = { bx: 7, by: 27, tx: 28, ty: 4, edge: 3.2, guard: 7, grip: 8, pommel: 9, pommelR: 2.4, iron: 3 };

/** The sword's own frame: its length, and a point so far along it and so far across it. */
function lieFrame() {
  const { bx, by, tx, ty } = GIANT_SWORD_LIE;
  const len = Math.hypot(tx - bx, ty - by);
  const ux = (tx - bx) / len, uy = (ty - by) / len;
  const px = -uy, py = ux;
  const at = (a: number, b: number): [number, number] => [bx + ux * a + px * b, by + uy * a + py * b];
  return { len, at };
}

/**
 * The box the giant sword's ground drawing covers, in tile pixels: the
 * point, the two edges of the blade, the four corners of the guard's
 * stroke, and the pommel's circle. Nothing else it draws reaches past
 * these. It is worked out from GIANT_SWORD_LIE rather than written down
 * so that moving the sword moves the box with it.
 */
export function giantSwordLieBox(): { x0: number; y0: number; x1: number; y1: number } {
  const { edge, guard, pommel, pommelR, iron } = GIANT_SWORD_LIE;
  const { len, at } = lieFrame();
  const half = iron / 2;
  const pts: [number, number][] = [
    at(len, 0), at(len, edge), at(len, -edge),
    at(half, guard), at(-half, guard), at(half, -guard), at(-half, -guard),
    at(0, guard), at(0, -guard),
  ];
  const [cx, cy] = at(-pommel, 0);
  pts.push([cx - pommelR, cy - pommelR], [cx + pommelR, cy + pommelR]);
  let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
  for (const [x, y] of pts) {
    x0 = Math.min(x0, x); y0 = Math.min(y0, y); x1 = Math.max(x1, x); y1 = Math.max(y1, y);
  }
  return { x0, y0, x1, y1 };
}

/**
 * How far to move a drawing so that a box it covers sits in the middle of
 * a square canvas of the given size, in whole pixels so nothing lands
 * between two of them and blurs.
 */
export function centringShift(size: number, box: { x0: number; y0: number; x1: number; y1: number }): [number, number] {
  return [Math.round(size / 2 - (box.x0 + box.x1) / 2), Math.round(size / 2 - (box.y0 + box.y1) / 2)];
}

/**
 * The margin a ground sprite may run over its tile by on each side, and
 * so the size of the canvas an oversize one is drawn on. Four is what the
 * giant sword needs: its box is a shade over the tile in both directions
 * once it is centred, and the outline and the shadow want a pixel or two
 * past that. giantSwordLieBox and the test on it are what keep this
 * honest if the sword is ever redrawn.
 */
export const GROUND_MARGIN = 4;

/**
 * An item's sprite as it lies on the ground. For everything but the giant
 * sword that is itemSprite, a tile's worth of picture. The giant sword is
 * drawn corner to corner of its tile and does not fit — the pommel and the
 * guard's ends ran off the bottom-left edge and were cut off there, so a
 * thing whose whole point is being too big was drawn missing a piece. On
 * the ground it gets a canvas GROUND_MARGIN wider than a tile on every
 * side and is drawn whole, moved so that the box it covers is centred on
 * the tile: what sticks out sticks out equally on every side, and by as
 * little as it can, since a centred box overhangs by half its excess and
 * an off-centre one by all of it on one side.
 *
 * The canvas is bigger than a tile, so the caller centres it by its own
 * size (render.ts does, at both ground draws). The panels keep drawing
 * itemSprite: a slot is a slot, and a sword clipped to it is the panel's
 * honest picture of a thing that does not fit in a slot.
 */
/**
 * A door as it stands in a wall in the oblique view (asked for on
 * 2026-09-27: "fix the door so it fits the oblique style"): the picture
 * the door face shows, laid onto the wall's south or east face by the
 * renderer (see faceTransform). The shut doors are their own pictures,
 * face on already. The open undercroft door is its frame and its leaf
 * swung back, with nothing between them: standing in the wall, the
 * doorway is a way through to the floor beyond rather than a patch of
 * dark. The open Doors of Durin keep their dark, which is the stair hall
 * inside the mountain.
 */
const doorFaceCache = new Map<number, HTMLCanvasElement>();
export function doorFace(id: number): HTMLCanvasElement {
  let c = doorFaceCache.get(id);
  if (!c) {
    const [raw, ctx] = canvas(TILE, TILE);
    if (id === 53) {
      ctx.fillStyle = "#3a3a3a"; // the jambs and lintel
      ctx.fillRect(0, 0, 4, 32);
      ctx.fillRect(28, 0, 4, 32);
      ctx.fillRect(0, 0, 32, 4);
      ctx.fillStyle = "#4a3219"; // the door, swung back against the left jamb
      ctx.fillRect(4, 3, 6, 29);
      ctx.fillStyle = "#6d7378"; // the ends of its bands
      for (const y of [7, 16, 25]) ctx.fillRect(4, y, 6, 3);
    } else {
      drawItem(ctx, id);
    }
    c = raw;
    doorFaceCache.set(id, c);
  }
  return c;
}

export function groundSprite(id: number, color?: string, make?: string): HTMLCanvasElement {
  if (id !== GIANT_SWORD) return itemSprite(id, color, make);
  const edge = makeEdge(make);
  const key = `ground|${id}|${color ?? ""}|${edge ?? ""}`;
  let c = itemCache.get(key);
  if (!c) {
    const size = TILE + 2 * GROUND_MARGIN;
    const [raw, ctx] = canvas(size, size);
    const [dx, dy] = centringShift(size, giantSwordLieBox());
    if (mirrored(id)) mirrorAbout(ctx, size / 2);
    ctx.translate(dx, dy);
    drawItem(ctx, id, color);
    c = outlined(raw, size, size, true, edge);
    itemCache.set(key, c);
  }
  return c;
}

// --- creatures ---------------------------------------------------------------

/**
 * Where a monster's weapon hangs and how big it is in its hands, by outfit:
 * the x on the viewer's left, the x on the viewer's right, how far up from
 * the feet, and the size of the weapon against the one a player carries.
 * Only the kinds that fight with something are listed — a wolf has nothing
 * to hold one with — and the numbers follow each body, so a cyclops swings
 * from higher up than a kobold does.
 *
 * The scale is there because the weapon sprites are drawn to a person's
 * hand, and not everything that picks one up is a person. It scales about
 * the grip, so a resized weapon stays in the fist holding it.
 */
interface MonsterHold {
  left: number;
  right: number;
  y: number;
  /** 1 is the size a player carries it at. */
  scale: number;
}

const DWARVES: Record<number, {
  coat: string; boot: string; skin: string; beard: string;
  helm: string; helmLit: string; eye: string; crown: number;
  peaks: number; jewels: string; ember?: string;
  trim?: string; plume?: string; quilt?: string; mask?: string;
}> = {
  // The common dwarf: iron helm, leather coat, black beard.
  27: { coat: "#6b4a2a", boot: "#3b2a17", skin: "#c99a70", beard: "#2e2723", helm: "#5c6166", helmLit: "#8a9298", eye: "#ffd24a", crown: 0, peaks: 0, jewels: "" },
  // The guard: the royal pair's own coat and the royal pair's own gold,
  // so it reads as their household and not as a sixth kind of dwarf, with
  // a black beard rather than their white one because it is a soldier and
  // not an elder. What tells it from the pair is what is laid over the
  // coat and what stands off the helm: gold at the throat, the shoulders
  // and the hem, and a red crest where they have a crown. Ornate is the
  // brief and ornate is metal-on-cloth plus a thing standing up — a crown
  // says "I rule here", a crest says "I am his".
  32: {
    coat: "#3c2a5a", boot: "#241a33", skin: "#c99a70", beard: "#2e2723",
    helm: "#c9a227", helmLit: "#f0d060", eye: "#ffd24a", crown: 1,
    peaks: 0, jewels: "", trim: "#e6c454", plume: "#c02030",
  },
  // The king: gold and a deep coat, a white beard, and a crown of a helm
  // that stands well clear of the others. His crown is plain — a flat
  // gold rim with nothing set in it — so that hers is the one that tells.
  29: { coat: "#3c2a5a", boot: "#241a33", skin: "#c99a70", beard: "#d8d2c4", helm: "#c9a227", helmLit: "#f0d060", eye: "#ffd24a", crown: 4, peaks: 0, jewels: "" },
  // The queen: the king, to the colour. Same coat, same boots, same white
  // beard, same gold, same height of crown. The crown comes up to three
  // points instead of a flat rim and has two small rubies in its band, and
  // that is the whole of the difference, on purpose: every dwarf has a
  // beard and nobody asks, and the joke only lands if the two are the same
  // creature to anyone who is not looking at the crown.
  30: { coat: "#3c2a5a", boot: "#241a33", skin: "#c99a70", beard: "#d8d2c4", helm: "#c9a227", helmLit: "#f0d060", eye: "#ffd24a", crown: 4, peaks: 3, jewels: "#d8202c" },
  // The geomancer: a coat the colour of cooled slag, an ash-grey beard, a
  // dark helm with a band of heat along its rim, and eyes the fire's own
  // orange. What is in its hand is what tells it apart from across the
  // hall — a lump of molten rock, lit from inside.
  31: { coat: "#4a2a26", boot: "#2a1b17", skin: "#c99a70", beard: "#8d8378", helm: "#3a3a40", helmLit: "#ff7a1e", eye: "#ff8a3c", crown: 0, peaks: 0, jewels: "", ember: "#ff7a1e" },
  // The soldier (2026-09-27: "add a dwarf soldier creature ... it carries
  // a crossbow"): the common dwarf's build and beard, dressed for standing
  // a post rather than swinging an axe. A padded coat the colour of
  // undyed wool, quilted in rows — the quilting is what says gambeson
  // rather than cloth, and it is the soldier's one mark on the body — and
  // a cap of dark iron sitting low, darker than the axeman's plain helm so
  // the two read apart across a gallery. The beard is a dark brown. What
  // tells it from everything else in the hold at a distance is in its
  // hands: the crossbow, levelled the way it faces.
  28: { coat: "#9a8660", boot: "#3b2a17", skin: "#c99a70", beard: "#4a3222", helm: "#34383d", helmLit: "#5a6168", eye: "#ffd24a", crown: 0, peaks: 0, jewels: "", quilt: "#6f5f40" },
  // Dáin Ironfoot, lord of the Iron Hills (2026-10-16), who sells the
  // plate they make: steel where the royal pair have gold, over a coat of
  // iron blue, a rust-red beard that stands out against all that steel,
  // and a lord's rim of a crown on his helm, lower than a king's. The steel
  // laid over the coat is the plate he sells.
  90: {
    coat: "#33475e", boot: "#1f2730", skin: "#c99a70", beard: "#9c4a2a",
    helm: "#8c96a0", helmLit: "#d4dce4", eye: "#ffd24a", crown: 2,
    peaks: 0, jewels: "", trim: "#b8c2cc",
  },
  // --- 12. Belegost (2026-10-16): the Broadbeams' dead. A dwarf shade is
  // the common dwarf gone grey as smoke, in the mail he died in, with
  // eyes the cold blue of the deeps; Azaghâl is a lord's helm of black iron
  // over his war-mask, the same grey, and drawn the same see-through (see
  // SHADE_DWARVES).
  156: {
    coat: "#6f7780", boot: "#454a50", skin: "#a9b2b8", beard: "#c8ced2",
    helm: "#596068", helmLit: "#8d959c", eye: "#9fe0ff", crown: 0,
    peaks: 0, jewels: "", trim: "#8d959c",
  },
  157: {
    coat: "#4f565e", boot: "#2f3338", skin: "#a9b2b8", beard: "#d8dde0",
    helm: "#2c2f33", helmLit: "#c9a227", eye: "#bff0ff", crown: 3,
    peaks: 0, jewels: "", trim: "#c9a227", mask: "#4a4d52",
  },
  // --- end of 12. Belegost
  // Hánar of the Iron Hills, the stonewright in the Glittering Caves
  // (2026-10-22): a working dwarf, a leather coat the colour of the caves'
  // clay, a grey-streaked brown beard and a plain iron cap, trimmed in the
  // rose of the crystal he loves.
  161: {
    coat: "#7a5638", boot: "#3b2a17", skin: "#c99a70", beard: "#6e5a48",
    helm: "#4c5258", helmLit: "#7c848c", eye: "#ffd24a", crown: 0,
    peaks: 0, jewels: "", trim: "#efc6d2",
  },
  // The warden of the Wolf-watch (2026-11-01), one of Dáin's: a hauberk
  // of iron blue as his lord's, a fur trim against the north's cold, a
  // grizzled grey beard and a plain iron cap.
  181: {
    coat: "#3c4e60", boot: "#2a2016", skin: "#c99a70", beard: "#8e8a84",
    helm: "#5a626a", helmLit: "#9aa4ae", eye: "#ffd24a", crown: 0,
    peaks: 0, jewels: "", trim: "#e8e4dc",
  },
};

/** The dwarves drawn as shades, see-through (site 12, Belegost). */
const SHADE_DWARVES = new Set([156, 157]);
const SHADE_ALPHA = 0.7;

const MONSTER_GRIP: Record<number, MonsterHold> = {
  // The troll's hands hang to its knees since it was redrawn hunched and
  // long in the arm (asked for on 2026-09-25: "improve the troll
  // graphics"), so its grip is further out and lower than it was at
  // 4.5, 27.5 and 20: the club and the shield stay in the fists that
  // drawTroll puts there.
  12: { left: 3, right: 29, y: 12, scale: 1 }, // forest troll (the troll until 2026-09-28)
  // The hill troll is the troll drawn a fifth as big again about its feet
  // (2026-09-28), so its hands are the troll's scaled the same way — 16 +
  // 1.2 × (3 − 16) and 16 + 1.2 × (29 − 16), 1.2 × 12 up — and the club in
  // them is a size up again: a young tree rather than a stick.
  38: { left: 0.4, right: 31.6, y: 14.4, scale: 1.3 }, // hill troll
  // The troll chief of the Ettenmoors (2026-10-16) is the troll drawn 1.4
  // times as big about its feet: 16 + 1.4 × (3 − 16), 16 + 1.4 × (29 −
  // 16), 1.4 × 12 up, and its club a size over the hill troll's.
  106: { left: -2.2, right: 34.2, y: 16.8, scale: 1.45 }, // troll chief
  // An old stone troll of the Troll-stones (2026-11-01) is the troll drawn
  // 1.3 times as big about its feet: 16 + 1.3 × (3 − 16), 16 + 1.3 × (29 −
  // 16), 1.3 × 12 up.
  177: { left: -0.9, right: 32.9, y: 15.6, scale: 1.35 }, // old stone troll
  // The cave troll is the troll drawn 1.3 times as big about its feet
  // (2026-09-28: "rename our current trolls as forest trolls, add cave
  // trolls and hill trolls"), so its hands are the troll's scaled the same
  // way — 16 + 1.3 × (3 − 16) and 16 + 1.3 × (29 − 16), 1.3 × 12 up — and
  // its club is bigger again than the hill troll's: a felled tree.
  40: { left: -0.9, right: 32.9, y: 15.6, scale: 1.5 }, // cave troll (39 is the hobbit, merged the same day)
  13: { left: 5.5, right: 26.5, y: 16, scale: 1 }, // orc
  34: { left: 6.5, right: 25.5, y: 16, scale: 1 }, // gnoll: the orc's grip, a shade narrower for the leaner build
  // The bandit (2026-10-01): a man's hands at the ends of his sleeves, just
  // outside the cloak, and a man's sword in them.
  42: { left: 6, right: 26, y: 15, scale: 1 }, // bandit
  // The content proposal's armed kinds (2026-10-01: "Implement the entire
  // proposal in the background"). The four men are drawn on the bandit's
  // frame (drawMan) and hold things where he does. The snaga is a small
  // orc and its club is a shade under a player's. The Uruk-hai is the orc
  // warrior drawn 1.12 times as big about its feet, so its hands are the
  // orc's scaled the same way — 16 + 1.12 × (5.5 − 16) and 16 + 1.12 ×
  // (26.5 − 16), 1.12 × 16 up — and the stone troll the forest troll's at
  // 1.1. The Nazgûl's sleeves are long and its hands hang a little lower.
  44: { left: 6, right: 26, y: 15, scale: 0.85 }, // snaga
  46: { left: 6, right: 26, y: 15, scale: 1 }, // Southron archer
  47: { left: 6, right: 26, y: 15, scale: 1 }, // Dunlending
  48: { left: 6, right: 26, y: 15, scale: 1 }, // corsair
  49: { left: 6, right: 26, y: 15, scale: 1 }, // Easterling
  50: { left: 4.2, right: 27.8, y: 17.9, scale: 1.1 }, // Uruk-hai
  // The Black Orc (2026-10-04: "Add tougher Black Orcs in Mordor") is the
  // orc warrior at 1.18, so its hands are the orc's scaled that way:
  // 16 + 1.18 × (5.5 − 16), 16 + 1.18 × (26.5 − 16) and 1.18 × 16 up.
  68: { left: 3.6, right: 28.4, y: 18.9, scale: 1.15 }, // Black Orc
  // Gundabad's orcs (2026-10-15) are the orc warrior at 1.14, 1.26 and
  // 1.34, their hands the orc's scaled the same way.
  95: { left: 4, right: 28, y: 18.2, scale: 1.12 }, // Gundabad orc
  96: { left: 2.8, right: 29.2, y: 20.2, scale: 1.2 }, // Bolg's bodyguard
  97: { left: 1.9, right: 30.1, y: 21.4, scale: 1.25 }, // Bolg
  51: { left: 1.7, right: 30.3, y: 13.2, scale: 1.15 }, // stone troll
  53: { left: 6.5, right: 25.5, y: 16, scale: 1 }, // Nazgûl
  // The Witch-king (2026-10-04: "Add more Nazgul and a boss Witch King") is
  // the Nazgûl drawn 1.2 times as big about its feet, so his hands are the
  // Nazgûl's scaled the same way — 16 + 1.2 × (6.5 − 16) and 16 + 1.2 ×
  // (25.5 − 16), 1.2 × 16 up — and his blade a fifth over a Nazgûl's.
  67: { left: 4.6, right: 27.4, y: 19.2, scale: 1.2 }, // Witch-king
  // The Wood-elves and their king (2026-10-07: "propose some pathways
  // through the mirkwood forest, and the elf-kings halls, with hostile elf
  // monsters", then "Implement it") are drawn on the people's frame
  // (drawFolk), whose hands are at the ends of its sleeves where the
  // bandit's and the men's are, and hold things where they do.
  71: { left: 6, right: 26, y: 15, scale: 1 }, // Wood-elf archer
  72: { left: 6, right: 26, y: 15, scale: 1 }, // Wood-elf warden
  73: { left: 6, right: 26, y: 15, scale: 1 }, // Guard of the Elvenking
  74: { left: 6, right: 26, y: 15, scale: 1 }, // Thranduil
  // The crypt's armed dead (2026-10-08) are drawn on the same frame.
  80: { left: 6, right: 26, y: 15, scale: 1 }, // Drowned King's Man
  83: { left: 6, right: 26, y: 15, scale: 1 }, // High Priest of Melkor
  173: { left: 6, right: 26, y: 15, scale: 1 }, // the Blue Wizard (2026-11-01), on the same frame
  111: { left: 6, right: 26, y: 15, scale: 1 }, // Drowned Dúnadan (site 3, 2026-10-16): the King's Man's frame
  146: { left: 5, right: 27, y: 16, scale: 1 }, // Wose (site 10, 2026-10-16): the bow in a broad man's fist
  // 11. The Variags of Khand (2026-10-16): the men's frame (drawMan), and
  // the chieftain drawn on it 1.15 times as big about his feet, so his
  // hands are its hands scaled the same way.
  151: { left: 6, right: 26, y: 15, scale: 1 }, // Variag axeman
  152: { left: 6, right: 26, y: 15, scale: 1 }, // Variag horse-archer
  153: { left: 4.5, right: 27.5, y: 17.25, scale: 1.15 }, // chieftain of the Variags
  // The Dead of Dunharrow (2026-10-16) are on the same frame; their King
  // is it drawn 1.2 times as big about its feet, so his hands are the
  // frame's scaled the same way — 16 + 1.2 × (6 − 16), 16 + 1.2 × (26 −
  // 16), 1.2 × 15 up — and his sword a fifth over a man's.
  130: { left: 6, right: 26, y: 15, scale: 1 }, // Dead Man of Dunharrow
  131: { left: 4, right: 28, y: 18, scale: 1.2 }, // King of the Dead
  // A cyclops is twice the size of the things it fights, and the great axe
  // in its fist should look like one: a player-sized haft in that hand
  // reads as a hatchet.
  14: { left: 2, right: 30, y: 22, scale: 1.4 }, // cyclops: club fists hang to the knee
  15: { left: 10.5, right: 22.5, y: 14, scale: 1 }, // skeleton
  // A kobold is a small thing, and a full-sized club in its fist reads as
  // the club carrying the kobold rather than the other way about. Its arms
  // are short with it: the grips sit close in against that little barrel of
  // a body — the troll's reach would leave the club and the buckler hanging
  // in the air a clear tile's width from anything holding them.
  21: { left: 8, right: 24, y: 17, scale: 0.8 }, // kobold
  102: { left: 8, right: 24, y: 17, scale: 0.8 }, // Longbottom's shed kobold, the kobold's (empty-areas site 1)
  // The hobbit (2026-09-28: "add a hobbit creature with a stick in one hand
  // and a rock in the other"): the fists just outside Grubb's short arms,
  // so the stick stands clear of the sleeve rather than lying along it,
  // and both carried a shade under a player's size.
  39: { left: 4, right: 28, y: 15, scale: 0.9 }, // hobbit (38 is the hill troll, merged the same day)
  // Bullroarer Took (2026-10-10) is drawn as a player is, who holds things
  // in their own hands, so this is never read for where; it is here so that
  // his golf club gets an armed sprite's room (see creatureSprite).
  86: { left: 4, right: 28, y: 15, scale: 1 }, // Bullroarer Took
  23: { left: 6.5, right: 25.5, y: 17, scale: 1 }, // barrow wight
  // The Wight-lord of Carn Dûm (2026-10-15) is the wight drawn 1.25 times
  // as big about its feet, so its hands are the wight's scaled the same way
  // — 16 + 1.25 × (6.5 − 16), 16 + 1.25 × (25.5 − 16), 1.25 × 17 up — and
  // its blade a quarter over a wight's.
  89: { left: 4.1, right: 27.9, y: 21.3, scale: 1.25 }, // Wight-lord
  // A berserker is broader than an orc and swings a heavier axe: the grips
  // sit further out, and the axe is drawn a shade over a player's.
  25: { left: 3.5, right: 28.5, y: 13, scale: 1.15 }, // orc berserker
  // The dwarves are short and their arms are short with it: the grips sit
  // close in against bodies that are wider than they are tall. The iron in
  // their fists is a player's size, as it is in a dwarf player's
  // (2026-11-02: "don't scale items when carried by dwarves or hobbits");
  // it was drawn a shade under until then.
  27: { left: 6, right: 26, y: 15, scale: 1 }, // dwarf
  28: { left: 6, right: 26, y: 15, scale: 1 }, // dwarf soldier: the dwarf's grip, holding a crossbow (2026-09-27)
  32: { left: 6, right: 26, y: 15, scale: 1 }, // dwarf guard: the royal grip, it carries the royal sword
  29: { left: 6, right: 26, y: 15, scale: 1 }, // dwarf king
  30: { left: 6, right: 26, y: 15, scale: 1 }, // dwarf queen: the king's grip, as everything else of hers is the king's
  // --- 12. Belegost (2026-10-16): the shades are dwarves, and hold their
  // axes where a dwarf does.
  156: { left: 6, right: 26, y: 15, scale: 1 }, // dwarf shade
  157: { left: 6, right: 26, y: 15, scale: 1 }, // the Shade of Azaghâl
  // --- end of 12. Belegost
};

/**
 * The bright sword's own light: a pale ice-blue blade, and a deeper blue
 * glow laid under it. It was a white with only a breath of blue in it
 * until 2026-09-28 ("give the bright sword more of a blue tinge"): now the
 * blue is the first thing you see of it, still cold steel rather than
 * fire, and still told at a glance from Andúril's near-white blade with
 * its line of gold.
 */
const BRIGHT_BLADE = "#cfe2ff";
const BRIGHT_GLOW = "#6fa3ec";

/**
 * The rare finds' colours (2026-09-26, "Implement them all", on a proposal
 * of rare drops), stated once each because every piece is drawn twice: on
 * the ground and on whoever has it, and the two have to be the same thing.
 */
const MITHRIL = { body: "#c9d3dc", shade: "#6f7b86", light: "#f4f8fb", ring: "#98a5b1", sheen: "#e6eef6" };
const DWARF_HELM = { iron: "#7c838b", lit: "#a9b0b7", rim: "#4a4f55", bronze: "#b07a3a" };
/**
 * The longbow's wood was a pale #d9c89a until 2026-10-01 ("in the
 * background, make sure the elven bow shaft contrasts with the skin of the
 * player races"): every race's skin is a pale warm tan, and against them it
 * came to a contrast of 1.1 to 1.3, so the stave all but vanished where it
 * crossed the hand. It is a dark gold-grained wood now, at least 3 to 1
 * against every skin (WCAG's least for a shape against what is beside it),
 * and an olive gold rather than the hunting bow's red-brown, so the two
 * bows are still told apart by more than their size.
 */
const LONGBOW = { wood: "#66562a", string: "#f4f0e0", grip: "#4f7a4a", leaf: "#e8edf2" };
const SPIDER_FANG = { fang: "#241f28", gloss: "#6d5f72", hilt: "#5a3a1e", guard: "#3d3644", venom: "#7ed957" };
/** Sting's blue is its own: the bright sword's glow is pale, and this one is a warning. */
const STING = { blade: "#eef6ff", glow: "#6fb6ff", hilt: "#3b3f5c" };
const GEO_STAFF = { stone: "#34323a", vein: "#e0a040", spark: "#fff0c0" };
const DRAGON_SCALE = { body: "#8c2a1c", shade: "#4a120b", light: "#c9573a", edge: "#5a160c", gold: "#d8a93a" };

/**
 * The Shards of Narsil's colours and Andúril's (2026-09-28, "Add a quest to
 * find the Shards of Narsil"). The pieces are old steel, a shade warmer and
 * duller than the bright sword's, with a sun of gold and a moon of silver on
 * the hilt and guard; Andúril is that steel alight, with the moon's glow
 * round the blade and the sun's line down it.
 */
const NARSIL = {
  steel: "#d9e0e6", edge: "#8e979f", bright: "#f7fbff",
  grip: "#4b3322", wire: "#b98b3e", guard: "#a4adb5", sun: "#e8b93a", moon: "#cfe0f2",
};
const ANDURIL = { blade: "#f4f8ff", glow: "#9fd0ff", flame: "#ffcf6b", hilt: "#e8b93a", gem: "#d23b2e" };

/**
 * The content proposal's items' colours (2026-10-01, "Implement the entire
 * proposal in the background"), stated once each for the reason the rare
 * finds' are: every piece of gear is drawn twice, on the ground and on
 * whoever has it, and the two have to be the same thing.
 *
 * Glamdring is Gondolin's steel with a blue light down it, bluer and paler
 * than Sting's; the barrow-blade bright steel with red-gold runes; the
 * Uruk-hai's iron black and the White Hand white; the corsair's cutlass on
 * a brass guard with a blue tarred wrap; the Morgul-blade a sick pale green
 * going to smoke. The elven cloak is Lórien's grey-green with a gold leaf
 * at the throat, the Easterling's scale lacquered red edged in gold, the
 * Rohirric helm steel and gold with a white horsetail, the helm of Dol
 * Amroth silver with the swan-knights' blue and white wings, and the
 * Elendilmir a white star on a fillet of mithril.
 */
const GLAMDRING = { blade: "#e9f2ff", glow: "#8fd0ff", hilt: "#9aa8c8", grip: "#2d3352", gem: "#2f6fd0" };
const BARROW_BLADE = { blade: "#d9dde2", edge: "#8e979f", rune: "#c9733a", hilt: "#6b4a2a", guard: "#a07a3a" };
/**
 * How far the Uruk-hai pike's point stands over the hands that hold it
 * (2026-10-26: "The pike should be held more upright and longer"): half
 * again what the spear's does, which takes it a hand's breadth over the
 * head of whoever holds it. The sprite's box is grown to fit it.
 */
const PIKE_RISE = 46;
const PIKE_ID = 170;
/** The Uruk-hai pike's shaft: a dark wood, so it shows against their black. */
const PIKE_SHAFT = "#5a4632";
const URUK = { iron: "#4b4f55", edge: "#9aa0a6", grip: "#2a2420", shield: "#1e1c1e", hand: "#f4f4f0", rim: "#5c6166" };
const CUTLASS = { blade: "#cfd4d8", edge: "#f2f5f7", guard: "#c9a227", wrap: "#2e5c7a" };
const MORGUL = { blade: "#9fb6a0", haze: "#4a5a4c", hilt: "#1c1a20" };
/**
 * The spidersilk cloak's colours, read by its ground sprite and by
 * drawWornCloak: silk pale blue-grey, a darker lining, folds and a web a
 * shade lighter than the cloth, and the green clasp.
 */
const SILK_CLOAK = { body: "#a8b2c8", lining: "#4c5468", fold: "#cdd6ea", light: "#eef2fc", web: "#e6ecfa", clasp: "#8f8" };
/**
 * Site 3, the Lossoth of Forochel (2026-10-16). The fur cloak their
 * headman sells is winter-wolf white with a bone toggle, ragged at the hem
 * where the fur lies; LOSSOTH is the rest of the site's colours: the pelt,
 * the snow-houses and the timbers of Arvedui's ship.
 */
const FUR_CLOAK = { body: "#e6e3da", shade: "#a6a296", fold: "#c8c4b8", light: "#faf8f2", toggle: "#d8c8a0" };
const LOSSOTH = {
  pelt: "#ecebe4", peltBack: "#b8b6ae", peltShade: "#c9c6bc",
  snow: "#eef3f6", snowShade: "#b9c8d2", door: "#2a3038", smoke: "#4a5058",
  timber: "#3a2e24", frost: "#b8ccd6",
};
const ELVEN_CLOAK = { body: "#6f8a6a", shade: "#3f523c", light: "#9ab894", fold: "#87a481", leaf: "#c9b45a" };
// 5. Rhosgobel (2026-10-16): Radagast's brown cloak, undyed wool the
// colour of earth, a wooden clasp and a thrush's feather through it.
const BROWN_CLOAK = { body: "#7a5a36", shade: "#4e3920", light: "#9c7a50", fold: "#8e6c44", clasp: "#5a3a1a", feather: "#c8b48a" };
// end of 5. Rhosgobel
/**
 * The star of the Dúnedain (2026-10-16, the Rangers' camp): a Ranger's
 * cloak, weathered grey with a green cast, darker than Lórien's, and the
 * silver star that pins it.
 */
const DUNEDAIN_CLOAK = { body: "#5e665c", shade: "#383e37", light: "#848c80", fold: "#717a6e", star: "#e4e8ee", starEdge: "#8a929c" };
/** The iron ring through the troll chief's ear. */
const TROLL_CHIEF_RING = "#3c3f44";
/**
 * The star of the Dúnedain at x, y, r across: six rays in silver about a
 * bright middle, the brooch that pins a Ranger's cloak.
 */
function drawDunedainStar(ctx: Ctx, x: number, y: number, r: number) {
  const ray = (rr: number, w: number, col: string) => {
    ctx.fillStyle = col;
    ctx.beginPath();
    for (let i = 0; i < 12; i++) {
      const a = (Math.PI / 6) * i - Math.PI / 2;
      const d = i % 2 === 0 ? rr : rr * w;
      if (i === 0) ctx.moveTo(x + Math.cos(a) * d, y + Math.sin(a) * d);
      else ctx.lineTo(x + Math.cos(a) * d, y + Math.sin(a) * d);
    }
    ctx.closePath();
    ctx.fill();
  };
  ray(r + 0.6, 0.45, DUNEDAIN_CLOAK.starEdge);
  ray(r, 0.4, DUNEDAIN_CLOAK.star);
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(x - 0.5, y - 0.5, 1, 1);
}
const EASTERLING = { body: "#8a3a2a", shade: "#4a1a12", light: "#b8604a", edge: "#5a2216", gold: "#c9a227" };
const ROHAN_HELM = {
  steel: "#a9b0b7", lit: "#d8dde2", rim: "#5c6166", gold: "#c9a227",
  tail: "#fbf8ee", strand: "#cfc6b0", edge: "#4a4438",
};

/**
 * The Rohirric helm's horsetail, on the ground and worn (2026-10-16: "Make
 * the rohirric helm horse hair more distinctive"): a thick plume that
 * swells from the crest and thins to a tip, cream white with a dark edge so
 * it stands off the grey steel and off grass alike, and a strand or two
 * down it so it reads as hair. It runs from (x0,y0) to (x1,y1), bowed
 * through (qx,qy), at its widest w across.
 */
function drawHorsetail(ctx: Ctx, x0: number, y0: number, qx: number, qy: number, x1: number, y1: number, w: number) {
  const dx = x1 - x0, dy = y1 - y0, len = Math.hypot(dx, dy) || 1;
  const nx = -dy / len, ny = dx / len; // across the tail
  const h = w / 2, r = Math.min(1, h / 2); // half its width at the bow, and at the root
  ctx.beginPath();
  ctx.moveTo(x0 + nx * r, y0 + ny * r);
  ctx.quadraticCurveTo(qx + nx * h * 2, qy + ny * h * 2, x1, y1);
  ctx.quadraticCurveTo(qx - nx * h * 2, qy - ny * h * 2, x0 - nx * r, y0 - ny * r);
  ctx.closePath();
  ctx.lineJoin = "round";
  ctx.strokeStyle = ROHAN_HELM.edge;
  ctx.lineWidth = 2;
  ctx.stroke();
  ctx.fillStyle = ROHAN_HELM.tail;
  ctx.fill();
  ctx.strokeStyle = ROHAN_HELM.strand;
  ctx.lineWidth = 0.75;
  ctx.lineCap = "round";
  ctx.beginPath();
  for (const k of w >= 4 ? [-0.6, 0.6] : [0]) {
    ctx.moveTo(x0, y0);
    ctx.quadraticCurveTo(qx + nx * h * k, qy + ny * h * k, x1 - (nx * h * k) / 2, y1 - (ny * h * k) / 2);
  }
  ctx.stroke();
}
const DOL_AMROTH = { steel: "#c9d3dc", lit: "#eef3f8", rim: "#5a6068", blue: "#5878b8", wing: "#f4f8fb" };
const ELENDILMIR = { band: "#c9d3dc", gem: "#f8fbff", glow: "#bfe4ff" };
/**
 * The Witch-king's crown, the helm-crown of Minas Morgul (2026-10-07: "use
 * the helm-crown of minas morgul graphics"): a dark steel for the helm, a
 * paler one for the blades and the brow band and a near-white for their
 * edges, a near-black in the shade of the cheek, black for the slits, and
 * in them the cold green the Witch-king's eyes are drawn in.
 */
const WITCH_CROWN = { steel: "#4a4e57", lit: "#9198a3", edge: "#d6dbe2", dark: "#121116", slit: "#030305", eye: "#b8ffc8" };

/**
 * The blades of the Witch-king's crown as they stand off a brow at baseY,
 * worn or on the brow of his hood: each [foot, tip, height] across from
 * cx, a narrow blade halfW either side of its foot leaning out to its tip.
 */
function witchCrownBlades(ctx: Ctx, cx: number, baseY: number, halfW: number, blades: readonly (readonly [number, number, number])[]) {
  ctx.fillStyle = WITCH_CROWN.lit;
  for (const [foot, tip, h] of blades) {
    ctx.beginPath();
    ctx.moveTo(cx + foot - halfW, baseY);
    ctx.lineTo(cx + tip, baseY - h);
    ctx.lineTo(cx + foot + halfW, baseY);
    ctx.closePath();
    ctx.fill();
  }
}
/**
 * The rings (2026-10-05, "Implement the lesser, Seven, Nine and One Ring"):
 * each one's metal, the lit edge of it, and the stone set in it, if any.
 * The lesser three are Eregion's practice pieces, silver and pale gold; the
 * Seven's is the dwarves' heavy red gold; the Nine's a cold metal nobody can
 * name, with a grey haze round it; and the One plain gold, the brightest of
 * them, with the fire-letters showing.
 */
type RingLook = { metal: string; lit: string; stone?: string; width: number };
const RINGS = {
  lore: { metal: "#b8c2cc", lit: "#eef3f8", stone: "#3f6fd8", width: 2 } as RingLook,
  quiet: { metal: "#8e979f", lit: "#c9d0d6", width: 2 } as RingLook,
  cleanBlood: { metal: "#d8c27a", lit: "#f4e6b0", stone: "#4fb86a", width: 2 } as RingLook,
  seven: { metal: "#c0782a", lit: "#f0b060", stone: "#c0202a", width: 3 } as RingLook,
  nine: { metal: "#a6b4b0", lit: "#e2ece8", stone: "#5c6460", width: 2 } as RingLook,
  one: { metal: "#e8b830", lit: "#fff0a0", width: 2.5 } as RingLook,
  oneGlow: "rgba(255,200,60,0.22)",
  oneFire: "#ff6a20",
  // The Ring of Barahir (site 3, 2026-10-16): gold serpents, an emerald
  // eye on the near side and one across the band.
  barahir: { metal: "#d8a838", lit: "#f8dc80", stone: "#1fa85a", width: 2.5 } as RingLook,
  barahirEye: "#1fa85a",
  // The Ranger's signet out of the trolls' hoard (2026-11-01): plain
  // silver, the star cut in its face drawn over it.
  signet: { metal: "#b4bcc4", lit: "#eef2f6", width: 2.5 } as RingLook,
};
/** The wizard's staff (2026-10-19): grey ash, a darker knot at its head, and a white glint in the knot. */
const WIZARD_STAFF = { wood: "#a9a291", grain: "#7d766a", knot: "#6b6255", glint: "#f4f7ff" };
/** The Cracks of Doom: the fire in the fissure and the heat at its heart. */
const CRACKS = { fire: "#e0501a", heart: "#ffe08a" };

/**
 * The Orcrist quest's colours (2026-10-07: "do the Orcrist quest"), stated
 * once for the reason Glamdring's are: Orcrist is drawn on the ground and
 * in the hand, and the two have to be the same sword.
 *
 * Orcrist is Glamdring's twin, so its light is the same blue family, a
 * little greener; what tells the two apart at a glance is the shape — a
 * broader blade with a groove down it, a guard swept down towards the hand,
 * a dark grip bound in silver wire and a green stone in a silver pommel,
 * where Glamdring has a straight guard and a blue stone. The Elvenking's
 * keys are green bronze on a silver ring, with a leaf of his realm on it.
 */
// 9. Dorwinion (2026-10-16).
/** A cask's colours: oak staves, iron hoops, the wine at its bung. */
const CASK = { stave: "#8a5a30", shade: "#6a4222", lit: "#a87444", hoop: "#3e3e42", head: "#9a6a3a", wine: "#6a1424", cradle: "#4a3018" };

/** A cask lying on its side, seen from the front, centred on (cx, cy), at scale k. */
function drawCask(ctx: Ctx, cx: number, cy: number, k: number) {
  const w = 22 * k, h = 14 * k;
  ctx.fillStyle = CASK.stave; // the belly, bulging at the middle
  ctx.beginPath();
  ctx.ellipse(cx, cy, w / 2, h / 2, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = CASK.shade; // the shadowed underside
  ctx.beginPath();
  ctx.ellipse(cx, cy + h * 0.18, w / 2 - 1, h / 3, 0, 0, Math.PI);
  ctx.fill();
  ctx.fillStyle = CASK.lit; // the light along the top stave
  ctx.fillRect(cx - w * 0.3, cy - h * 0.38, w * 0.6, 1.2 * k);
  ctx.fillStyle = CASK.hoop; // two iron hoops round the belly
  ctx.fillRect(cx - w * 0.3, cy - h / 2 + 1, 2 * k, h - 2);
  ctx.fillRect(cx + w * 0.3 - 2 * k, cy - h / 2 + 1, 2 * k, h - 2);
  ctx.fillStyle = CASK.head; // the head of the cask at the right end
  ctx.beginPath();
  ctx.ellipse(cx + w / 2 - 2 * k, cy, 2.5 * k, h / 2 - 1, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = CASK.wine; // the bung, stained red
  ctx.fillRect(cx - 1.5 * k, cy - h / 2 + 0.5, 3 * k, 2 * k);
}
// end of 9. Dorwinion

/** A bed's colours: an oak frame, linen, and a quilt of three patches. */
const BED = { frame: "#8a5a32", frameDark: "#5e3b1f", sheet: "#ece6d6", sheetShade: "#cfc6b0", bolster: "#f6f2e6", quiltA: "#a8423a", quiltB: "#4f6e3a", quiltC: "#c79a4a", seam: "rgba(40,24,12,0.45)" };

/** The Green Dragon's furniture (2026-10-19): oak, stone, and a good fire. */
const INN = { oak: "#9a6a3e", oakLit: "#b88552", oakDark: "#5e3b1f", grain: "rgba(60,36,16,0.35)", ring: "rgba(60,36,16,0.3)", stone: "#8d8a82", stoneDark: "#5a5852", soot: "#1c1814", ember: "#7a2e12", flame: "#e8742a", flameCore: "#ffd36a", kettle: "#2c2c30", pewter: "#a8acb0", pewterDark: "#6c7074", ale: "#d8a840" };
/** The Mathom-house's museum (2026-10-19): a red cord on brass posts, and stone display stands. */
const MUSEUM = { cord: "#a8202a", brass: "#d4aa3c", brassDark: "#8a6a1e", plinth: "#c9c2b2", plinthLit: "#ddd7c9", plinthDark: "#8e877a", plinthShade: "#5e594f", card: "#f4efe2", ink: "#6a6458" };

/** The high seats on Amon Hen and Amon Lhaw (2026-10-21): weathered grey stone. */
const HIGH_SEAT = { stone: "#9a978e", stoneLit: "#b8b5ab", stoneDark: "#5f5d57", shade: "rgba(20,18,14,0.35)", carve: "#d8d4c6", mark: "#4a4842" };

/** A high seat of grey stone, from above and in front: four pillars at its
 *  corners, the seat between them, and a tall back to the north. */
function drawHighSeat(ctx: Ctx) {
  ctx.fillStyle = HIGH_SEAT.shade; // its shadow on the hilltop
  ctx.fillRect(6, 27, 22, 3);
  ctx.fillStyle = HIGH_SEAT.stoneDark; // the four pillars it stands on
  for (const x of [5, 24]) for (const y of [13, 23]) ctx.fillRect(x, y, 4, 6);
  ctx.fillStyle = HIGH_SEAT.stoneDark; // the back's side, in shadow
  ctx.fillRect(7, 2, 18, 13);
  ctx.fillStyle = HIGH_SEAT.stone; // the back's face
  ctx.fillRect(8, 2, 16, 12);
  ctx.fillStyle = HIGH_SEAT.stoneLit; // its top edge
  ctx.fillRect(8, 2, 16, 1.5);
  ctx.fillStyle = HIGH_SEAT.stoneDark; // the seat's front
  ctx.fillRect(6, 21, 20, 4);
  ctx.fillStyle = HIGH_SEAT.stone; // the seat
  ctx.fillRect(6, 14, 20, 7);
  ctx.fillStyle = HIGH_SEAT.stoneLit; // and its front edge, worn by sitting
  ctx.fillRect(6, 20, 20, 1);
  ctx.fillStyle = HIGH_SEAT.stoneDark; // the arms, a step up either side
  ctx.fillRect(4, 12, 4, 10);
  ctx.fillRect(24, 12, 4, 10);
  ctx.fillStyle = HIGH_SEAT.stoneLit;
  ctx.fillRect(4, 12, 4, 1.5);
  ctx.fillRect(24, 12, 4, 1.5);
}

/** A pewter mug of ale, from above, its handle to the right, centred on (x, y). */
function drawMug(ctx: Ctx, x: number, y: number) {
  ctx.fillStyle = INN.pewterDark;
  ctx.fillRect(x + 2.5, y - 1, 2.5, 2);
  ctx.beginPath();
  ctx.arc(x, y, 3, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = INN.pewter;
  ctx.beginPath();
  ctx.arc(x, y, 2.4, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = INN.ale;
  ctx.beginPath();
  ctx.arc(x, y, 1.7, 0, Math.PI * 2);
  ctx.fill();
}

const ORCRIST = { blade: "#f1f6fb", glow: "#7fc4e8", fuller: "#b9c6d4", hilt: "#c3cbd4", grip: "#4a2e1e", binding: "#dfe6ee", gem: "#3fae9a" };
const ELVENKING_KEYS = { ring: "#b8c4cc", bronze: "#6f8a4a", leaf: "#9bc45a" };
/**
 * What the dead keep in the crypt under Sauron's temple (2026-10-08): the
 * helm of the King's Men green with the sea; the black rod's iron with
 * the altar's fire in its head; the black silver of the temple's dome; and
 * a pearl.
 */
const KINGSMEN_HELM = { steel: "#7a8e84", lit: "#a8bcb0", rim: "#3a4a44", crest: "#2e5a4a" };
/** The war-mask of Belegost (item 142, site 12): black iron, a gold hammer. */
const BELEGOST_MASK = { iron: "#4a4d52", lit: "#7c8187", dark: "#26282c", slit: "#0c0c0e", ring: "#5c6066", gold: "#c9a227" };
const BLACK_ROD = { iron: "#1e1a1c", ember: "#d8501a", glow: "#ffb050" };
/** The Blue Wizard's staff and robe (2026-11-01): pale wood, blue-black iron, blue fire; deep sea-blue wool. */
const BLUE_STAFF = { wood: "#c8bc9a", iron: "#1e2638", fire: "#4a8aff", heart: "#e0f0ff", glow: "rgba(90,150,255,0.55)" };
const BLUE_ROBE = { body: "#1e3e78", shade: "#132a56", light: "#3a5a98", fold: "#2a4a86", clasp: "#d8dce4" };
const BLACK_SILVER = { dark: "#2a2a30", lit: "#6a6a74", glint: "#b8b8c4" };
const PEARL = { body: "#efe9dc", shade: "#c8bfae", lit: "#ffffff" };
const SAUCEPAN = { copper: "#b8693a", lit: "#e09a62", dark: "#7a3f1e", handle: "#3a2a1e" };
/** The dwarf's pick (item 155, 2026-10-17): ash, and iron gone dark with stone dust. */
const PICK = { haft: "#9a7650", iron: "#7d838a", lit: "#c4c9ce", dark: "#3a3e43" };

/**
 * A pick on a haft running from along(0, 0) to along(len, 0), its head
 * square across the top: a curved iron bar, bowed down at both ends,
 * drawn out to a point the b axis's own way and to a short flat chisel
 * the other. scale shrinks the head for a pick held in the hand.
 */
function drawPick(
  ctx: Ctx,
  along: (a: number, b: number) => [number, number],
  len: number,
  haft: number,
  scale: number,
) {
  ctx.lineCap = "round";
  ctx.strokeStyle = PICK.dark;
  ctx.lineWidth = haft + 1;
  ctx.beginPath();
  ctx.moveTo(...along(0, 0));
  ctx.lineTo(...along(len - 1, 0));
  ctx.stroke();
  ctx.strokeStyle = PICK.haft;
  ctx.lineWidth = haft;
  ctx.beginPath();
  ctx.moveTo(...along(0.5, 0));
  ctx.lineTo(...along(len - 1, 0));
  ctx.stroke();
  const at = (a: number, b: number) => along(len + a * scale, b * scale);
  ctx.fillStyle = PICK.dark;
  ctx.beginPath(); // the head, outlined a shade darker
  ctx.moveTo(...at(-4.8, 11.5));
  ctx.quadraticCurveTo(...at(2.6, 6), ...at(2.3, 0));
  ctx.quadraticCurveTo(...at(2.6, -5), ...at(-2.4, -10.2));
  ctx.lineTo(...at(-5.4, -9.2));
  ctx.quadraticCurveTo(...at(-1.2, -5), ...at(-2.3, 0));
  ctx.quadraticCurveTo(...at(-1.5, 6), ...at(-4.8, 11.5));
  ctx.closePath();
  ctx.fill();
  ctx.fillStyle = PICK.iron;
  ctx.beginPath();
  ctx.moveTo(...at(-4.4, 10.2));
  ctx.quadraticCurveTo(...at(1.6, 5.5), ...at(1.4, 0));
  ctx.quadraticCurveTo(...at(1.7, -4.4), ...at(-2.6, -9.2));
  ctx.lineTo(...at(-4.4, -8.6));
  ctx.quadraticCurveTo(...at(-0.5, -4.4), ...at(-1.4, 0));
  ctx.quadraticCurveTo(...at(-0.8, 5.4), ...at(-4.4, 10.2));
  ctx.closePath();
  ctx.fill();
  ctx.strokeStyle = PICK.lit; // light along the top of the bar
  ctx.lineWidth = Math.max(0.8, scale);
  ctx.beginPath();
  ctx.moveTo(...at(0.6, 4));
  ctx.quadraticCurveTo(...at(1.3, 0), ...at(0.8, -3));
  ctx.stroke();
}

/** Bullroarer Took's golf club: hickory, a leather wrap, an iron head. */
/** The torch's colours: pine, pitch-black rag and its binding, the fire. */
const TORCH = { haft: "#8a5a32", rag: "#4a3524", band: "#2a1c12", flame: "#ff8c26", heart: "#ffe27a", glow: "rgba(255,150,50,0.35)" };
const GOLF_CLUB = { shaft: "#a07a4a", grip: "#3a2418", iron: "#8e939a", lit: "#d0d4d8" };
const HARAD_SHIELD = { yellow: "#d8a826", black: "#1e1a16", rim: "#5a4a2a", spike: "#c8ccd2" };
/** Longbottom's (empty-areas site 1, 2026-10-16): Tobold's briar pipe, his cured leaf, and the leaf the vermin dragged off. */
const LONGBOTTOM = {
  stem: "#6a4a2c", bowl: "#7a4426", rim: "#4a2814", lit: "#b0703e", bit: "#2a1c12",
  leaf: "#8a6232", leafDark: "#5a3c1c", leafLit: "#c09050",
  stolen: "#7a6a3a", twine: "#c8b48a", bite: "#3a2c1a",
};

/**
 * The round shield of the Haradrim, "yellow and black with great spikes",
 * centred on (cx, cy) with radius r: a black disc with a yellow ring and
 * a yellow boss, a spike off the boss and spikes round the rim.
 */
function drawHaradShield(ctx: Ctx, cx: number, cy: number, r: number) {
  ctx.fillStyle = HARAD_SHIELD.spike;
  for (let i = 0; i < 8; i++) {
    const a = (i * Math.PI) / 4;
    const c = Math.cos(a), s = Math.sin(a);
    ctx.beginPath();
    ctx.moveTo(cx + c * (r + r * 0.35), cy + s * (r + r * 0.35));
    ctx.lineTo(cx + c * r * 0.85 - s * r * 0.18, cy + s * r * 0.85 + c * r * 0.18);
    ctx.lineTo(cx + c * r * 0.85 + s * r * 0.18, cy + s * r * 0.85 - c * r * 0.18);
    ctx.closePath();
    ctx.fill();
  }
  ctx.fillStyle = HARAD_SHIELD.black;
  ctx.beginPath();
  ctx.arc(cx, cy, r, 0, Math.PI * 2);
  ctx.fill();
  ctx.strokeStyle = HARAD_SHIELD.yellow;
  ctx.lineWidth = Math.max(1, r * 0.22);
  ctx.beginPath();
  ctx.arc(cx, cy, r * 0.62, 0, Math.PI * 2);
  ctx.stroke();
  ctx.fillStyle = HARAD_SHIELD.yellow;
  ctx.beginPath();
  ctx.arc(cx, cy, r * 0.3, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = HARAD_SHIELD.spike;
  ctx.beginPath();
  ctx.arc(cx, cy, r * 0.12, 0, Math.PI * 2);
  ctx.fill();
  ctx.strokeStyle = HARAD_SHIELD.rim;
  ctx.lineWidth = Math.max(1, r * 0.12);
  ctx.beginPath();
  ctx.arc(cx, cy, r, 0, Math.PI * 2);
  ctx.stroke();
}

/** A ring lying in a tile: a band seen a little from above, and its stone. */
function drawRing(ctx: Ctx, r: RingLook) {
  ctx.strokeStyle = r.metal;
  ctx.lineWidth = r.width;
  ctx.beginPath();
  ctx.ellipse(16, 18, 8, 5, 0, 0, Math.PI * 2);
  ctx.stroke();
  ctx.strokeStyle = r.lit; // the light along the far rim
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.ellipse(16, 18, 8, 5, 0, 1.15 * Math.PI, 1.85 * Math.PI);
  ctx.stroke();
  if (r.stone) {
    ctx.fillStyle = r.stone;
    ctx.beginPath();
    ctx.arc(16, 12.5, 2.4, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = r.lit;
    ctx.fillRect(15, 11.5, 1, 1);
  }
}

/** The phial's light, a star's: white at its heart and blue round it. */
const PHIAL = { glass: "#e8f4ff", glow: "#bfe4ff", heart: "#fffbe6", stopper: "#c9a227" };

/**
 * The axis every piece of Narsil lies on in its tile, the same diagonal
 * the swords lie on: a is along the blade from the guard, b across it.
 */
function narsilAxis() {
  const bx = 11, by = 23, tx = 26, ty = 6;
  const len = Math.hypot(tx - bx, ty - by);
  const ux = (tx - bx) / len, uy = (ty - by) / len;
  const px = -uy, py = ux;
  const at = (a: number, b: number): [number, number] => [bx + ux * a + px * b, by + uy * a + py * b];
  return { len, at };
}

/**
 * A length of Narsil's blade from a to b along the axis, half wide: its
 * foot broken off in a jag when broken is set, and its far end a jag too
 * unless pointed, where it tapers to the tip. The steel, a dark edge down
 * one side, and the bright line of the break.
 */
function drawNarsilSteel(
  ctx: Ctx, at: (a: number, b: number) => [number, number],
  a: number, b: number, half: number, broken: boolean, pointed: boolean,
) {
  const pts: [number, number][] = [];
  // Up one side from the foot...
  pts.push(at(a + (broken ? 0.8 : 0), -half));
  if (pointed) {
    pts.push(at(b - 4, -half));
    pts.push(at(b, 0));
    pts.push(at(b - 4, half));
  } else {
    pts.push(at(b, -half));
    pts.push(at(b - 1.2, -half / 3)); // the far break, jagged
    pts.push(at(b + 0.6, half / 3));
    pts.push(at(b - 0.6, half));
  }
  // ...and back down the other, across the foot's jag.
  pts.push(at(a, half));
  if (broken) {
    pts.push(at(a + 1.2, half / 3));
    pts.push(at(a - 0.6, -half / 3));
  }
  ctx.beginPath();
  ctx.moveTo(...pts[0]);
  for (const q of pts.slice(1)) ctx.lineTo(...q);
  ctx.closePath();
  ctx.lineWidth = 1.2;
  ctx.strokeStyle = NARSIL.edge; // a dark rim, so the steel reads on stone
  ctx.stroke();
  ctx.fillStyle = NARSIL.steel;
  ctx.fill();
  ctx.strokeStyle = NARSIL.bright; // the fuller, catching the light
  ctx.lineWidth = 0.8;
  ctx.beginPath();
  ctx.moveTo(...at(a + 1, 0));
  ctx.lineTo(...at(b - (pointed ? 4 : 1.5), 0));
  ctx.stroke();
}

/**
 * Trace a blade from its shoulder at (bx, by) to a point at (tx, ty): sides
 * parallel for most of its length and then tapering to nothing. A stroked
 * line would do for the body of it, but it ends in whatever the line cap
 * leaves — round, and so blunt. This ends in a point.
 */
function bladePath(ctx: Ctx, bx: number, by: number, tx: number, ty: number, half: number) {
  const dx = tx - bx, dy = ty - by;
  const len = Math.hypot(dx, dy) || 1;
  const ux = dx / len, uy = dy / len;
  const px = -uy, py = ux; // square across the blade
  const at = (a: number, b: number): [number, number] => [bx + ux * a + px * b, by + uy * a + py * b];
  const pts = [at(0, -half), at(0, half), at(len * 0.74, half), at(len, 0), at(len * 0.74, -half)];
  ctx.beginPath();
  ctx.moveTo(pts[0][0], pts[0][1]);
  for (const [x, y] of pts.slice(1)) ctx.lineTo(x, y);
  ctx.closePath();
}

// The fallback tunic palette, used only when the server sent no colour;
// characters normally wear the unique colour dealt to them at creation.
const PLAYER_TUNICS = ["#3050b0", "#30a050", "#b08020", "#8040b0", "#c06020", "#2098a0"];

/**
 * What a player is seen wearing and holding. All are item ids (0 for none):
 * the server sends the weapon, shield, armor and helm actually in use with
 * every creature, so a stranger with an axe, a steel shield, chain mail and
 * a horned helmet looks the part before they swing.
 */
export interface Gear {
  weapon: number;
  shield: number;
  armor: number;
  helm: number;
  /**
   * The cloak over the armor: worn beside the four above rather than in
   * place of any of them (2026-10-04: "make the various elf items
   * equippable in addition to other items where it makes sense").
   * Optional, and nought when absent, so that a caller drawing somebody in
   * the four alone need not name it.
   */
  cloak?: number;
  /** The belt at the waist (2026-10-22), 0 or absent for none. */
  belt?: number;
  /**
   * Who made the weapon and the shield in hand, for the outline in the
   * make's colour round each (see MAKE_EDGE): a player's, absent for plain
   * gear and for monsters.
   */
  wmake?: string;
  smake?: string;
  /** The same for the armor, the cloak and the helm worn. */
  amake?: string;
  hmake?: string;
  cmake?: string;
}

export const NO_GEAR: Gear = { weapon: 0, shield: 0, armor: 0, helm: 0, cloak: 0 };

/**
 * Draws a creature into a TILE x (TILE+16) canvas. Creatures are taller than a
 * tile (like Tibia's 32x64 outfits) so heads overlap the tile to the north.
 * dir: 0 n, 1 e, 2 s, 3 w. frame: 0/1/2 walking pose.
 */
/**
 * The dragon's palette. It is the thing at the end of the longest walk on
 * the map, so it is drawn darker and colder than anything else out there:
 * a deep green that the lit ridge picks out against, near-black wings that
 * read as leather rather than as more of the same animal, and horn, eye and
 * the fire in its open jaws in the only warm colours on it. Every shape is
 * edged in `line`, a green so dark it is nearly black, which is what keeps
 * a body this big legible at a tile's size: without the edge the neck ran
 * into the body and the body into the wing (2026-10-23: "Improve dragon
 * graphics").
 */
const DRAGON = {
  scale: "#2f6f3b",
  lit: "#4f9c55",
  dark: "#1b3f24",
  line: "#0c2012",
  belly: "#dccb86",
  bellyDark: "#a4914f",
  web: "#2b2623",
  webLit: "#4a4039",
  horn: "#c6b892",
  claw: "#e4dbc2",
  eye: "#ffb020",
  maw: "#ff6a1a",
  mawHot: "#ffd84a",
  frost: "",
  gems: "",
};

/** A dragon's palette: DRAGON's shape, so the lord can be drawn in its own. */
type Dragonkind = typeof DRAGON;

/**
 * The dragon lord's palette (2026-09-26: "Add a dragon lord at the very
 * top"). It is the dragon drawn again, bigger and crowned, and it has to
 * read as the master of the green ones at a glance from across the peaks:
 * so the scales go the dark red of old blood, nearly black in the shadow,
 * the belly and the horns go to gold, and the eyes and the fire in its
 * jaws burn hotter, white at the heart. Nothing on the plateau is that
 * colour, and the gold is the crown's colour, so the horns and the crown
 * read as one thing: something that rules.
 */
const DRAGON_LORD: Dragonkind = {
  scale: "#7a1f27",
  lit: "#a8333a",
  dark: "#3c0d13",
  line: "#1a0508",
  belly: "#dcaa44",
  bellyDark: "#a07420",
  web: "#1f1113",
  webLit: "#3d1f24",
  horn: "#e3bc3c",
  claw: "#f1e4ba",
  eye: "#ff5a1c",
  maw: "#ff8a1a",
  mawHot: "#fff4c0",
  frost: "",
  gems: "",
};

/**
 * The dragon lord's crown: a gold band with three points and a red stone,
 * sat on the skull between the horns. cx, cy is the middle of the band's
 * bottom edge; w is its width, narrower seen side-on.
 */
function dragonCrown(ctx: Ctx, cx: number, cy: number, w: number) {
  ctx.fillStyle = DRAGON_LORD.horn;
  ctx.fillRect(cx - w / 2, cy - 2.2, w, 2.2);
  for (const t of [0, 0.5, 1]) {
    const x = cx - w / 2 + t * (w - 1.6);
    ctx.beginPath();
    ctx.moveTo(x, cy - 2);
    ctx.lineTo(x + 0.8, cy - 5.5);
    ctx.lineTo(x + 1.6, cy - 2);
    ctx.closePath();
    ctx.fill();
  }
  ctx.strokeStyle = DRAGON_LORD.line;
  ctx.lineWidth = 0.6;
  ctx.strokeRect(cx - w / 2, cy - 2.2, w, 2.2);
  ctx.fillStyle = "#d01a1a";
  ctx.fillRect(cx - 0.8, cy - 1.8, 1.6, 1.4);
}

/**
 * The dragon lord: the dragon, a third as big again about its own feet, in
 * the lord's palette and crowned. Scaled rather than redrawn so that it is
 * plainly the same animal, the one the others are smaller copies of; see
 * BIG_OUTFITS for the room the extra size takes.
 */
function drawDragonLord(ctx: Ctx, dir: number, legOff: number, H: number, eyes: (x: number, y: number, c?: string) => void) {
  ctx.save();
  ctx.translate(16, H);
  ctx.scale(DRAGON_LORD_SCALE, DRAGON_LORD_SCALE);
  ctx.translate(-16, -H);
  drawDragon(ctx, dir, legOff, H, eyes, DRAGON_LORD, true);
  ctx.restore();
}

/** How much bigger the dragon lord is drawn than a dragon. */
const DRAGON_LORD_SCALE = 1.3;

/**
 * The cold-drake's palette (site 12, Belegost, 2026-10-16): a dragon of the
 * north, without the fire. Grey-blue scales with frost on the ridge, a pale
 * belly, eyes the cold blue of the deeps rather than a fire-drake's amber,
 * and the cold of its breath, blue-white, in its jaws where a dragon has
 * fire. It has no wings (see drawColdDrake) but a sail of spines down its
 * back instead, so the web colours are the sail's.
 */
const COLD_DRAKE: Dragonkind = {
  scale: "#5a6f80",
  lit: "#9bb3c4",
  dark: "#2a3744",
  line: "#101820",
  belly: "#dde6ec",
  bellyDark: "#9aacb8",
  web: "#3a4a58",
  webLit: "#55697a",
  horn: "#e2ebf0",
  claw: "#eef3f6",
  eye: "#8fe3ff",
  maw: "#a6e6ff",
  mawHot: "#ffffff",
  frost: "#eef7fb",
  gems: "",
};

/**
 * The fell beast's palette (2026-11-01), the Nazgûl's winged steed: "it
 * was naked, and neither quill nor feather did it bear, and its vast
 * pinions were as webs of hide between horned fingers". The dragon's shape
 * in the grey-black of old hide, its webs a bruised grey-violet, horn and
 * claw a dirty bone, its eyes a sick yellow, and no fire in its throat,
 * only the dark of it.
 */
const FELL_BEAST: Dragonkind = {
  scale: "#3b3740",
  lit: "#5e5866",
  dark: "#211e25",
  line: "#0a090c",
  belly: "#6a6270",
  bellyDark: "#48424e",
  web: "#2c2733",
  webLit: "#4c4456",
  horn: "#a49a86",
  claw: "#cdc4b0",
  eye: "#d8d040",
  maw: "#4a1818",
  mawHot: "#7a2a2a",
  frost: "",
  gems: "",
};

/** The fell beast's outfit (fellBeastOutfit in the game's steeds.go). */
export const FELL_BEAST_OUTFIT = 175;

/** A point in a creature's standard space: x across a tile, y down from its top. */
type Pt = [number, number];

/**
 * Where a fell beast's saddle is, in the standard space with its feet at
 * H: side-on (for a beast facing east; west is the mirror), coming towards
 * you, and going away. Its rider's hips go on it (see fellRiderSprite).
 */
const FELL_SEAT = { side: [19, -27] as Pt, facing: [16, -29] as Pt, away: [16, -27] as Pt };

/** What a fell beast's rider adds to the drawing, at its place in the order. */
type FellRider = (ctx: Ctx, seat: Pt) => void;

/**
 * A fell beast (2026-11-01: "Have Nazgul spawn randomly on steeds, a black
 * horse or winged beast"), redrawn the same day ("rework the fell beast to
 * have no forelegs, and larger more bat like wings"): it stands on its two
 * hind legs alone, as a bat or a pterosaur does, with its wings for arms,
 * held up and out, each an arm to the wrist and four long fingers fanning
 * from it with the hide scalloped between their tips. A long bare neck, a
 * hooked beak and a crest of horn behind it; a whip of a tail. A saddle at
 * the root of its neck, for the Nazgûl.
 *
 * A rider, if given, is drawn where it sits in the order: over the body
 * and the far wing, under the head when the beast comes towards you.
 */
function drawFellBeast(ctx: Ctx, dir: number, legOff: number, H: number, rider?: FellRider) {
  const D = FELL_BEAST;
  const flap = legOff * 1.5; // the wings beat with the step
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  if (dir === 1 || dir === 3) {
    const s = dir === 1 ? 1 : -1;
    const X = (d: number) => 16 + s * (d - 16);
    const P = (x: number, y: number): Pt => [X(x), H + y];
    // The far wing first, higher and further back, in shadow.
    fellWing(ctx, P(14, -27), P(6, -44 - flap), P(10, -64 - flap * 2), [P(-2, -66 - flap), P(-14, -58 - flap), P(-22, -44), P(-20, -30)], P(2, -22), D, false);
    fellTail(ctx, P(3, -20), P(-8, -26), P(-20, -12), 4.5, D);
    dragonLeg(ctx, X(9), H - 16, s, false, -legOff, H, D);
    fellBody(ctx, X(13), H - 21, 11, 6.5, -s * 0.25, D);
    dragonLeg(ctx, X(12), H - 16, s, true, legOff, H, D);
    fellNeck(ctx, P(22, -25), P(30, -29), P(32, -40), D);
    fellHeadSide(ctx, X(33), H - 42, s, D);
    // The near wing, over the body and under the rider.
    fellWing(ctx, P(16, -26), P(9, -41 - flap), P(13, -60 - flap * 2), [P(3, -63 - flap), P(-8, -55 - flap), P(-15, -42), P(-13, -28)], P(5, -21), D, true);
    fellSaddle(ctx, X(FELL_SEAT.side[0]), H + FELL_SEAT.side[1], 7);
    rider?.(ctx, [X(FELL_SEAT.side[0]), H + FELL_SEAT.side[1]]);
    return;
  }
  const L = (x: number, y: number): Pt => [x, H + y];
  const R = (x: number, y: number): Pt => [32 - x, H + y];
  const wings = () => {
    for (const [M, near] of [[L, true], [R, true]] as const) {
      fellWing(ctx, M(11, -28), M(1, -42 - flap), M(-8, -58 - flap * 2), [M(-21, -54 - flap), M(-26, -40), M(-23, -26), M(-14, -17)], M(10, -19), D, near);
    }
  };
  wings();
  if (dir === 2) {
    // Coming towards you: the wings, the body and the legs, the saddle and
    // its rider, and the neck and head in front of them both.
    fellBody(ctx, 16, H - 21, 7.5, 8, 0, D);
    dragonLeg(ctx, 12, H - 15, 0, true, legOff, H, D);
    dragonLeg(ctx, 20, H - 15, 0, true, -legOff, H, D);
    fellSaddle(ctx, 16, H + FELL_SEAT.facing[1], 9);
    rider?.(ctx, [FELL_SEAT.facing[0], H + FELL_SEAT.facing[1]]);
    fellNeck(ctx, L(16, -24), L(16, -30), L(16, -35), D);
    fellHeadFront(ctx, 16, H - 38, D);
    return;
  }
  // Going away: the head beyond the body, then the body, the legs and the
  // tail hanging down behind, and the rider nearest you of all.
  fellNeck(ctx, L(16, -26), L(16, -32), L(16, -36), D);
  fellHeadBack(ctx, 16, H - 38, D);
  fellBody(ctx, 16, H - 21, 7.5, 8, 0, D);
  dragonLeg(ctx, 12, H - 15, 0, true, -legOff, H, D);
  dragonLeg(ctx, 20, H - 15, 0, true, legOff, H, D);
  fellTail(ctx, L(16, -16), L(15, -8), L(19, -1), 4, D);
  fellSaddle(ctx, 16, H + FELL_SEAT.away[1], 9);
  rider?.(ctx, [FELL_SEAT.away[0], H + FELL_SEAT.away[1]]);
}

/**
 * A fell beast's wing, a bat's: the arm from the shoulder to the elbow and
 * on up to the wrist, a hooked thumb there, and four long fingers fanning
 * from it to the tips, the hide between the tips scooped in, and the last
 * of it back to the body at root.
 */
function fellWing(ctx: Ctx, shoulder: Pt, elbow: Pt, wrist: Pt, tips: Pt[], root: Pt, D: Dragonkind, near: boolean) {
  ctx.beginPath();
  ctx.moveTo(...shoulder);
  ctx.lineTo(...elbow);
  ctx.lineTo(...wrist);
  let prev = wrist;
  for (const t of [...tips, root]) {
    // Each scallop bows in towards the wrist, the deeper the longer it is.
    const mx = (prev[0] + t[0]) / 2, my = (prev[1] + t[1]) / 2;
    const k = 0.22;
    ctx.quadraticCurveTo(mx + (wrist[0] - mx) * k, my + (wrist[1] - my) * k, t[0], t[1]);
    prev = t;
  }
  ctx.closePath();
  dragonFill(ctx, D, near ? D.webLit : D.web);
  const limb = near ? D.scale : D.dark;
  for (const [w, col] of [[2.2, D.line], [1.1, limb]] as const) {
    ctx.strokeStyle = col;
    ctx.lineWidth = w;
    ctx.beginPath();
    for (const t of tips) {
      ctx.moveTo(...wrist);
      ctx.lineTo(...t);
    }
    ctx.stroke();
  }
  for (const [w, col] of [[4.2, D.line], [2.8, limb]] as const) {
    ctx.strokeStyle = col;
    ctx.lineWidth = w;
    ctx.beginPath();
    ctx.moveTo(...shoulder);
    ctx.lineTo(...elbow);
    ctx.lineTo(...wrist);
    ctx.stroke();
  }
  // The thumb's hook, on past the wrist.
  const ux = wrist[0] - elbow[0], uy = wrist[1] - elbow[1], n = Math.hypot(ux, uy) || 1;
  ctx.fillStyle = D.claw;
  ctx.beginPath();
  ctx.moveTo(wrist[0] - 1, wrist[1]);
  ctx.lineTo(wrist[0] + (ux / n) * 3.4, wrist[1] + (uy / n) * 3.4 - 0.6);
  ctx.lineTo(wrist[0] + 1, wrist[1] + 0.4);
  ctx.closePath();
  ctx.fill();
}

/** A fell beast's body: a lean barrel of bare hide, lit along the top. */
function fellBody(ctx: Ctx, x: number, y: number, rx: number, ry: number, rot: number, D: Dragonkind) {
  ctx.beginPath();
  ctx.ellipse(x, y, rx, ry, rot, 0, Math.PI * 2);
  dragonFill(ctx, D, D.scale);
  ctx.fillStyle = D.lit;
  ctx.beginPath();
  ctx.ellipse(x, y - ry * 0.45, rx * 0.65, ry * 0.3, rot, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = D.belly;
  ctx.beginPath();
  ctx.ellipse(x, y + ry * 0.4, rx * 0.6, ry * 0.35, rot, 0, Math.PI * 2);
  ctx.fill();
}

/** A tapering length of hide along a curve, edged; a neck or a tail. */
function fellTaper(ctx: Ctx, p0: Pt, c: Pt, p1: Pt, root: number, tip: number, D: Dragonkind) {
  const at = (t: number): Pt => [
    (1 - t) * (1 - t) * p0[0] + 2 * (1 - t) * t * c[0] + t * t * p1[0],
    (1 - t) * (1 - t) * p0[1] + 2 * (1 - t) * t * c[1] + t * t * p1[1],
  ];
  const N = 10;
  ctx.lineCap = "round";
  for (const [extra, col] of [[2, D.line], [0, D.scale]] as const) {
    for (let i = 0; i < N; i++) {
      const a = at(i / N), b = at((i + 1) / N);
      ctx.strokeStyle = col;
      ctx.lineWidth = root + (tip - root) * (i / N) + extra;
      ctx.beginPath();
      ctx.moveTo(...a);
      ctx.lineTo(...b);
      ctx.stroke();
    }
  }
}

/** A fell beast's neck: long, thick at the shoulders, thin at the skull. */
function fellNeck(ctx: Ctx, p0: Pt, c: Pt, p1: Pt, D: Dragonkind) {
  fellTaper(ctx, p0, c, p1, 5, 2.6, D);
}

/** A fell beast's tail: a bare whip, no spade and no spines. */
function fellTail(ctx: Ctx, p0: Pt, c: Pt, p1: Pt, root: number, D: Dragonkind) {
  fellTaper(ctx, p0, c, p1, root, 0.8, D);
}

/** Its head side-on, facing s: a long hooked beak, a crest of horn behind. */
function fellHeadSide(ctx: Ctx, x: number, y: number, s: number, D: Dragonkind) {
  dragonHorn(ctx, x - s * 1.5, y - 1.5, -s * 7, -3.5, 1.3, D);
  ctx.beginPath();
  ctx.ellipse(x, y, 4, 3, 0, 0, Math.PI * 2);
  dragonFill(ctx, D, D.scale, 0.8);
  ctx.beginPath(); // the beak, hooked down at its tip
  ctx.moveTo(x + s * 2.4, y - 1.8);
  ctx.lineTo(x + s * 11, y + 0.6);
  ctx.lineTo(x + s * 11.2, y + 2.8);
  ctx.lineTo(x + s * 9.8, y + 1.8);
  ctx.lineTo(x + s * 2.4, y + 2.4);
  ctx.closePath();
  dragonFill(ctx, D, D.horn, 0.7);
  ctx.strokeStyle = D.line;
  ctx.lineWidth = 0.6;
  ctx.beginPath();
  ctx.moveTo(x + s * 2.8, y + 0.6);
  ctx.lineTo(x + s * 10, y + 1.3);
  ctx.stroke();
  ctx.fillStyle = D.eye;
  ctx.fillRect(x + s * 0.6 - 0.7, y - 1.2, 1.4, 1.1);
}

/** Its head coming towards you: the beak pointing down at you, two eyes, two horns. */
function fellHeadFront(ctx: Ctx, x: number, y: number, D: Dragonkind) {
  dragonHorn(ctx, x - 2, y - 1.5, -3.5, -5, 1.1, D);
  dragonHorn(ctx, x + 2, y - 1.5, 3.5, -5, 1.1, D);
  ctx.beginPath();
  ctx.ellipse(x, y, 3.4, 3, 0, 0, Math.PI * 2);
  dragonFill(ctx, D, D.scale, 0.8);
  ctx.beginPath();
  ctx.moveTo(x - 2.2, y + 0.5);
  ctx.lineTo(x + 2.2, y + 0.5);
  ctx.lineTo(x, y + 7);
  ctx.closePath();
  dragonFill(ctx, D, D.horn, 0.7);
  ctx.fillStyle = D.eye;
  ctx.fillRect(x - 2.4, y - 1.2, 1.3, 1.1);
  ctx.fillRect(x + 1.1, y - 1.2, 1.3, 1.1);
}

/** Its head going away: the back of the skull and the crest. */
function fellHeadBack(ctx: Ctx, x: number, y: number, D: Dragonkind) {
  dragonHorn(ctx, x - 2, y - 1, -3.5, -5, 1.1, D);
  dragonHorn(ctx, x + 2, y - 1, 3.5, -5, 1.1, D);
  ctx.beginPath();
  ctx.ellipse(x, y, 3.2, 2.8, 0, 0, Math.PI * 2);
  dragonFill(ctx, D, D.scale, 0.8);
}

/** The saddle at the root of its neck: black leather, a dull steel edge. */
function fellSaddle(ctx: Ctx, x: number, y: number, w: number) {
  ctx.fillStyle = FELL_TACK.leather;
  ctx.fillRect(x - w / 2, y - 1, w, 3);
  ctx.fillStyle = FELL_TACK.steel;
  ctx.fillRect(x - w / 2, y + 2, w, 0.8);
  ctx.fillStyle = FELL_TACK.leather;
  ctx.fillRect(x - 0.8, y + 2.8, 1.6, 5); // the girth, down its side
}

/** A fell beast's harness: black leather and the dull steel of Minas Morgul. */
const FELL_TACK = { leather: "#151318", steel: "#6a6e78", rein: "#2e2a33" };

/** How big a cold-drake is drawn against a dragon. */
const COLD_DRAKE_SCALE = 0.85;

/** A cold-drake: the dragon drawn smaller, in COLD_DRAKE, without wings. */
function drawColdDrake(ctx: Ctx, dir: number, legOff: number, H: number, eyes: (x: number, y: number, c?: string) => void) {
  ctx.save();
  ctx.translate(16, H);
  ctx.scale(COLD_DRAKE_SCALE, COLD_DRAKE_SCALE);
  ctx.translate(-16, -H);
  drawDragon(ctx, dir, legOff, H, eyes, COLD_DRAKE, false, false);
  ctx.restore();
}

/**
 * Scatha the Worm (2026-11-01): the cold-drake grown great, darker along
 * the back, frost on him, and old gold of the dwarves' hoard stuck in his
 * belly (gems) from the long sleep on it.
 */
const SCATHA: Dragonkind = {
  ...COLD_DRAKE, scale: "#4a5c6a", lit: "#8aa2b4", dark: "#1e2a34", belly: "#cfd9e0", eye: "#c8f4ff", gems: "#f0c850",
};
/** How big Scatha is drawn against a dragon: well over a cold-drake. */
const SCATHA_SCALE = 1.25;

/** Scatha: the cold-drake drawn as big as a dragon lord, wingless. */
function drawScatha(ctx: Ctx, dir: number, legOff: number, H: number, eyes: (x: number, y: number, c?: string) => void) {
  ctx.save();
  ctx.translate(16, H);
  ctx.scale(SCATHA_SCALE, SCATHA_SCALE);
  ctx.translate(-16, -H);
  drawDragon(ctx, dir, legOff, H, eyes, SCATHA, false, false);
  ctx.restore();
}

/** Fram's spear's leaf: steel gone dark, and the char where it went in. */
const FRAM_SPEAR = { blade: "#9aa2a8", char: "#3a3634" };

/**
 * Smaug's palette (2026-10-27: "add smaug as a monster, with no specific
 * spawn yet"). Smaug the Golden: red-gold scales, lit to orange-gold along
 * the back, a gold belly, and the hottest eyes and fire of any dragon. His
 * belly is crusted with the gems of the hoard he slept on (gems, which
 * names the brightest of them; see dragonGems), all but the bare patch in
 * the hollow of his left breast.
 */
const SMAUG: Dragonkind = {
  scale: "#a8401c",
  lit: "#d9772e",
  dark: "#5a1c0e",
  line: "#200a04",
  belly: "#f0c850",
  bellyDark: "#b08a2a",
  web: "#4a1a10",
  webLit: "#7a3418",
  horn: "#f4d27a",
  claw: "#fff0c8",
  eye: "#ffe23a",
  maw: "#ff9a2a",
  mawHot: "#fffbe0",
  frost: "",
  gems: "#f4fbff",
};

/** How much bigger Smaug is drawn than a dragon: the lord's 1.3 and more. */
const SMAUG_SCALE = 1.55;

/** Smaug: the dragon drawn bigger again than the lord, red-gold, jewelled. */
function drawSmaug(ctx: Ctx, dir: number, legOff: number, H: number, eyes: (x: number, y: number, c?: string) => void) {
  ctx.save();
  ctx.translate(16, H);
  ctx.scale(SMAUG_SCALE, SMAUG_SCALE);
  ctx.translate(-16, -H);
  drawDragon(ctx, dir, legOff, H, eyes, SMAUG);
  ctx.restore();
}

/**
 * The gems stuck in a dragon's belly, for the one that has them: specks of
 * diamond, ruby, sapphire and emerald over the belly's ellipse about cx,
 * cy, and where bare is given, a dull patch there with none, the hollow
 * of his left breast that the black arrow found.
 */
function dragonGems(ctx: Ctx, cx: number, cy: number, rx: number, ry: number, D: Dragonkind, bare?: [number, number]) {
  const cols = [D.gems, "#e0303a", "#3aa0e0", "#40c060"];
  const at = [[-0.8, 0.1], [-0.55, -0.4], [-0.35, 0.45], [-0.1, -0.1], [0.1, 0.55], [0.3, -0.45], [0.5, 0.2], [0.75, -0.1], [-0.65, 0.6], [0.6, 0.6], [0.0, -0.6], [-0.3, 0.0]];
  at.forEach(([fx, fy], i) => {
    const x = cx + fx * rx, y = cy + fy * ry;
    if (bare && Math.hypot(x - bare[0], y - bare[1]) < 2.4) return;
    ctx.fillStyle = cols[i % cols.length];
    ctx.fillRect(x - 0.55, y - 0.55, 1.1, 1.1);
  });
  if (bare) {
    ctx.fillStyle = "#7a4a34";
    ctx.beginPath();
    ctx.ellipse(bare[0], bare[1], 1.6, 1.2, 0, 0, Math.PI * 2);
    ctx.fill();
  }
}

/** Fills the path that is open and edges it in the dragon's line colour. */
function dragonFill(ctx: Ctx, D: Dragonkind, col: string, edge = 1) {
  ctx.fillStyle = col;
  ctx.fill();
  ctx.strokeStyle = D.line;
  ctx.lineWidth = edge;
  ctx.stroke();
}

/**
 * One wing: the arm from the shoulder out to the wrist, four fingers
 * fanning back off the wrist, and a membrane scalloped between their tips.
 * The scallops are what make it a wing rather than a triangle — a bat's
 * outline is a row of shallow scoops, and nothing else about the drawing
 * says leather so clearly. The arm is drawn as a limb in the hide's colour
 * over the membrane, thick at the shoulder, with a claw at the wrist, so
 * the wing reads as something the animal holds up rather than a sheet
 * pinned behind it. The near wing is paler than the far one, the way the
 * near side of anything is.
 */
function dragonWing(ctx: Ctx, sx: number, sy: number, tipX: number, tipY: number, drop: number, D: Dragonkind, near: boolean) {
  const fingers = [
    { x: sx + (tipX - sx) * 0.2, y: tipY + drop * 1.0 },
    { x: sx + (tipX - sx) * 0.46, y: tipY + drop * 0.96 },
    { x: sx + (tipX - sx) * 0.7, y: tipY + drop * 0.8 },
    { x: sx + (tipX - sx) * 0.9, y: tipY + drop * 0.5 },
  ];
  ctx.beginPath();
  ctx.moveTo(sx, sy);
  ctx.lineTo(tipX, tipY);
  let prev = { x: tipX, y: tipY };
  for (const f of fingers) {
    ctx.quadraticCurveTo((prev.x + f.x) / 2, (prev.y + f.y) / 2 + drop * 0.28, f.x, f.y);
    prev = f;
  }
  ctx.quadraticCurveTo((prev.x + sx) / 2, (prev.y + sy) / 2 + drop * 0.2, sx, sy);
  ctx.closePath();
  dragonFill(ctx, D, near ? D.webLit : D.web);
  // The fingers, over the membrane they hold up, then the arm over them.
  ctx.lineCap = "round";
  const limb = near ? D.scale : D.dark;
  for (const [w, col] of [[2.4, D.line], [1.2, limb]] as const) {
    ctx.strokeStyle = col;
    ctx.lineWidth = w;
    ctx.beginPath();
    for (const f of fingers) {
      ctx.moveTo(tipX, tipY);
      ctx.lineTo(f.x, f.y);
    }
    ctx.stroke();
  }
  for (const [w, col] of [[4.4, D.line], [3, limb]] as const) {
    ctx.strokeStyle = col;
    ctx.lineWidth = w;
    ctx.beginPath();
    ctx.moveTo(sx, sy);
    ctx.lineTo(tipX, tipY);
    ctx.stroke();
  }
  if (near) {
    ctx.strokeStyle = D.lit;
    ctx.lineWidth = 0.9;
    ctx.beginPath();
    ctx.moveTo(sx + (tipX - sx) * 0.1, sy + (tipY - sy) * 0.1 - 1);
    ctx.lineTo(sx + (tipX - sx) * 0.85, sy + (tipY - sy) * 0.85 - 1);
    ctx.stroke();
  }
  const ux = Math.sign(tipX - sx) || 1; // the wrist's claw, hooked on past the tip
  ctx.fillStyle = D.claw;
  ctx.beginPath();
  ctx.moveTo(tipX - ux * 0.5, tipY - 1.6);
  ctx.lineTo(tipX + ux * 2.6, tipY - 3.4);
  ctx.lineTo(tipX + ux * 0.8, tipY + 0.4);
  ctx.closePath();
  ctx.fill();
}

/**
 * A row of spines down a back or a tail, each one leaning the way it runs.
 * With a `sail` the spines are joined by a web of the wing's colour, the
 * fin a cold-drake carries where a dragon carries wings.
 */
function dragonSpines(ctx: Ctx, from: [number, number], to: [number, number], n: number, size: number, D: Dragonkind = DRAGON, sail = false) {
  const lean = (to[0] - from[0]) / Math.max(1, n * 2);
  const tips: [number, number][] = [];
  for (let i = 0; i < n; i++) {
    const t = (i + 0.5) / n;
    const x = from[0] + (to[0] - from[0]) * t, y = from[1] + (to[1] - from[1]) * t;
    const s = size * (1 - t * 0.5);
    tips.push([x + lean, y - s * 1.9]);
  }
  if (sail) {
    ctx.beginPath();
    ctx.moveTo(from[0], from[1] + 1);
    for (const [x, y] of tips) ctx.lineTo(x, y);
    ctx.lineTo(to[0], to[1] + 1);
    ctx.closePath();
    dragonFill(ctx, D, D.webLit, 0.8);
  }
  for (let i = 0; i < n; i++) {
    const t = (i + 0.5) / n;
    const x = from[0] + (to[0] - from[0]) * t, y = from[1] + (to[1] - from[1]) * t;
    const s = size * (1 - t * 0.5);
    ctx.beginPath();
    ctx.moveTo(x - s, y + 0.5);
    ctx.lineTo(tips[i][0], tips[i][1]);
    ctx.lineTo(x + s, y + 0.5);
    ctx.closePath();
    dragonFill(ctx, D, D.horn, 0.6);
  }
}

/**
 * Rows of scales across a body: small arcs, each the bottom edge of one
 * scale, laid in staggered rows and clipped to the ellipse they cover.
 * Faint, so that at a tile's size they read as a texture on the hide and
 * not as a pattern over it.
 */
function dragonScales(ctx: Ctx, cx: number, cy: number, rx: number, ry: number, D: Dragonkind, rot = 0) {
  ctx.save();
  ctx.translate(cx, cy);
  ctx.rotate(rot);
  ctx.beginPath();
  ctx.ellipse(0, 0, rx, ry, 0, 0, Math.PI * 2);
  ctx.clip();
  ctx.strokeStyle = D.dark;
  ctx.lineWidth = 0.8;
  ctx.globalAlpha = 0.65;
  ctx.beginPath();
  let row = 0;
  for (let y = -ry + 1.5; y < ry * 0.35; y += 2.6, row++) {
    for (let x = -rx + (row % 2) * 1.6; x < rx; x += 3.2) {
      ctx.moveTo(x + 1.4, y);
      ctx.arc(x, y, 1.4, 0, Math.PI);
    }
  }
  ctx.stroke();
  if (D.frost) {
    // The cold-drake's frost: flecks of rime along the top of the hide.
    ctx.globalAlpha = 0.9;
    ctx.fillStyle = D.frost;
    for (let x = -rx + 2; x < rx - 1; x += 3.4) {
      const y = -ry + 1.2 + ((x * 7) % 5) * 0.5;
      ctx.fillRect(x, y, 1.2, 1);
    }
  }
  ctx.restore();
}

/**
 * One leg: a thigh, a shin down to the foot, and three claws. `toe` is the
 * way the claws point, +1 right, -1 left, 0 straight at the viewer; `step`
 * is how far the foot is set forward on this frame of the walk. The near
 * legs are in the hide's own colour with a light on the thigh, the far
 * ones in shadow.
 */
function dragonLeg(ctx: Ctx, x: number, y: number, toe: number, near: boolean, step: number, H: number, D: Dragonkind) {
  const body = near ? D.scale : D.dark;
  const fx = x + toe * 1.5 + step;
  ctx.beginPath(); // the shin, from under the thigh down to the foot
  ctx.moveTo(x - 2.4, y + 1);
  ctx.lineTo(x + 2.4, y + 1);
  ctx.lineTo(fx + 2.2, H - 3);
  ctx.lineTo(fx - 2.2, H - 3);
  ctx.closePath();
  dragonFill(ctx, D, body);
  ctx.beginPath(); // the foot
  ctx.rect(fx - 3, H - 3.5, 6, 3.2);
  dragonFill(ctx, D, body);
  ctx.beginPath(); // the thigh, over the top of the shin
  ctx.ellipse(x, y, 4.2, 5, 0, 0, Math.PI * 2);
  dragonFill(ctx, D, body);
  if (near) {
    ctx.fillStyle = D.lit;
    ctx.beginPath();
    ctx.ellipse(x - 0.8, y - 1.6, 2, 2.2, 0, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.fillStyle = D.claw;
  if (toe) {
    for (let i = 0; i < 3; i++) {
      const cy = H - 3 + i * 1.1;
      ctx.beginPath();
      ctx.moveTo(fx + toe * 2.6, cy - 0.4);
      ctx.lineTo(fx + toe * 5.4, cy + 0.5);
      ctx.lineTo(fx + toe * 2.6, cy + 0.9);
      ctx.closePath();
      ctx.fill();
    }
  } else {
    for (const dx of [-2.2, 0, 2.2]) {
      ctx.beginPath();
      ctx.moveTo(fx + dx - 0.9, H - 2.2);
      ctx.lineTo(fx + dx, H);
      ctx.lineTo(fx + dx + 0.9, H - 2.2);
      ctx.closePath();
      ctx.fill();
    }
  }
}

/**
 * A tail: a tapering length along a curve, thick at the rump and thin at
 * the tip, with a spade on the end and spines along the top. It is drawn
 * as short strokes of falling width, the edge first and the hide over it,
 * which is what gives a taper with a clean outline: one stroke is the same
 * width end to end, and a tail that does not taper is a log.
 */
function dragonTail(ctx: Ctx, p0: [number, number], c: [number, number], p1: [number, number], root: number, D: Dragonkind, spade: [number, number][], spines: number) {
  const at = (t: number): [number, number] => [
    (1 - t) * (1 - t) * p0[0] + 2 * (1 - t) * t * c[0] + t * t * p1[0],
    (1 - t) * (1 - t) * p0[1] + 2 * (1 - t) * t * c[1] + t * t * p1[1],
  ];
  const N = 10;
  ctx.lineCap = "round";
  for (const [extra, col] of [[2, D.line], [0, D.scale]] as const) {
    for (let i = 0; i < N; i++) {
      const a = at(i / N), b = at((i + 1) / N);
      ctx.strokeStyle = col;
      ctx.lineWidth = root * (1 - (i / N) * 0.8) + extra;
      ctx.beginPath();
      ctx.moveTo(a[0], a[1]);
      ctx.lineTo(b[0], b[1]);
      ctx.stroke();
    }
  }
  // The underside in shadow and the top in the light, each a thinner line
  // along its own edge of the tail.
  for (const [off, col, k] of [[1.6, D.dark, 0.4], [-1.8, D.lit, 0.22]] as const) {
    for (let i = 0; i < N - 2; i++) {
      const a = at(i / N), b = at((i + 1) / N);
      ctx.strokeStyle = col;
      ctx.lineWidth = root * (1 - (i / N) * 0.8) * k;
      ctx.beginPath();
      ctx.moveTo(a[0], a[1] + off * (1 - i / N));
      ctx.lineTo(b[0], b[1] + off * (1 - i / N));
      ctx.stroke();
    }
  }
  ctx.beginPath();
  ctx.moveTo(spade[0][0], spade[0][1]);
  for (const [x, y] of spade.slice(1)) ctx.lineTo(x, y);
  ctx.closePath();
  dragonFill(ctx, D, D.dark);
  for (let i = 0; i < spines; i++) {
    const t = 0.12 + (i * 0.6) / spines;
    const [x, y] = at(t), s = 1.9 * (1 - t * 0.5), r = root * (1 - t * 0.8) / 2;
    ctx.beginPath();
    ctx.moveTo(x - s, y - r + 0.6);
    ctx.lineTo(x + (c[0] - p0[0]) * 0.04, y - r - s * 1.6);
    ctx.lineTo(x + s, y - r + 0.6);
    ctx.closePath();
    dragonFill(ctx, D, D.horn, 0.5);
  }
}

/** A tapered horn from (x, y) out to (x + dx, y + dy), bending as it goes. */
function dragonHorn(ctx: Ctx, x: number, y: number, dx: number, dy: number, base: number, D: Dragonkind) {
  const len = Math.hypot(dx, dy) || 1;
  const px = -dy / len, py = dx / len;
  ctx.beginPath();
  ctx.moveTo(x - px * base, y - py * base);
  ctx.quadraticCurveTo(x + dx * 0.5 - px * base * 0.9, y + dy * 0.5 - py * base * 0.9, x + dx, y + dy);
  ctx.quadraticCurveTo(x + dx * 0.45 + px * base * 1.3, y + dy * 0.45 + py * base * 1.3, x + px * base, y + py * base);
  ctx.closePath();
  dragonFill(ctx, D, D.horn, 0.7);
}

/** An eye: a slit pupil in a lit iris, the one point of colour in a face. */
function dragonEye(ctx: Ctx, x: number, y: number, D: Dragonkind) {
  ctx.fillStyle = D.eye;
  ctx.beginPath();
  ctx.ellipse(x, y, 1.9, 1.5, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = D.line;
  ctx.fillRect(x - 0.4, y - 1.3, 0.8, 2.6);
}

/** A row of teeth along y from x0 to x1, pointing down (dir 1) or up (-1). */
function dragonTeeth(ctx: Ctx, x0: number, x1: number, y: number, n: number, dir: number, D: Dragonkind, len = 1.8) {
  ctx.fillStyle = D.claw;
  for (let i = 0; i < n; i++) {
    const x = x0 + ((x1 - x0) * (i + 0.5)) / n;
    ctx.beginPath();
    ctx.moveTo(x - 0.7, y);
    ctx.lineTo(x, y + dir * len);
    ctx.lineTo(x + 0.7, y);
    ctx.closePath();
    ctx.fill();
  }
}

/**
 * The head in profile, facing `side`: a skull with a heavy brow, a long
 * upper jaw and a lower one hanging open under it with the fire showing
 * between the teeth, two horns swept back off the skull and a frill behind
 * the jaw. hx, hy is the middle of the skull.
 */
function dragonHeadSide(ctx: Ctx, hx: number, hy: number, side: number, D: Dragonkind, crowned: boolean) {
  const X = (d: number) => hx + side * d;
  // The horns and the frill first, behind the skull.
  dragonHorn(ctx, X(-3), hy - 3.5, -side * 10, -8.5, 1.9, D);
  dragonHorn(ctx, X(-4.5), hy - 0.5, -side * 7.5, -3.5, 1.3, D);
  ctx.beginPath();
  ctx.moveTo(X(-4), hy - 1);
  ctx.lineTo(X(-10.5), hy + 1.5);
  ctx.lineTo(X(-9), hy + 4.5);
  ctx.lineTo(X(-3), hy + 4);
  ctx.closePath();
  dragonFill(ctx, D, D.dark);
  // The lower jaw, open, and the fire in the mouth between the jaws.
  ctx.beginPath();
  ctx.moveTo(X(0), hy + 1);
  ctx.lineTo(X(11.5), hy + 5);
  ctx.lineTo(X(10.5), hy + 7.5);
  ctx.lineTo(X(-1.5), hy + 5.5);
  ctx.closePath();
  dragonFill(ctx, D, D.dark);
  ctx.fillStyle = D.maw;
  ctx.beginPath();
  ctx.moveTo(X(1), hy + 0.5);
  ctx.lineTo(X(12), hy + 1);
  ctx.lineTo(X(10), hy + 5);
  ctx.closePath();
  ctx.fill();
  ctx.fillStyle = D.mawHot;
  ctx.beginPath();
  ctx.moveTo(X(3), hy + 1.5);
  ctx.lineTo(X(9.5), hy + 1.8);
  ctx.lineTo(X(8.5), hy + 3.8);
  ctx.closePath();
  ctx.fill();
  dragonTeeth(ctx, X(3), X(10.5), hy + 4.4, 3, -1, D, 1.6);
  // The skull and the upper jaw, over the mouth.
  ctx.beginPath();
  ctx.ellipse(hx, hy, 6.2, 5, 0, 0, Math.PI * 2);
  dragonFill(ctx, D, D.scale);
  ctx.beginPath();
  ctx.moveTo(X(-1), hy - 4.2);
  ctx.lineTo(X(9), hy - 3);
  ctx.lineTo(X(13.5), hy - 1);
  ctx.lineTo(X(13), hy + 1);
  ctx.lineTo(X(1), hy + 1.6);
  ctx.closePath();
  dragonFill(ctx, D, D.scale);
  dragonTeeth(ctx, X(3.5), X(12), hy + 0.9, 4, 1, D, 1.7);
  ctx.beginPath(); // the brow, heavy over the eye, which is what gives it its look
  ctx.moveTo(X(-5.5), hy - 2.6);
  ctx.lineTo(X(7.5), hy - 2.4);
  ctx.lineTo(X(4), hy - 4.6);
  ctx.lineTo(X(-2), hy - 5);
  ctx.closePath();
  ctx.fillStyle = D.lit;
  ctx.fill();
  ctx.fillStyle = D.line; // the nostril
  ctx.beginPath();
  ctx.ellipse(X(11.2), hy - 0.6, 0.9, 0.7, 0, 0, Math.PI * 2);
  ctx.fill();
  dragonEye(ctx, X(2.2), hy - 1.2, D);
  if (crowned) dragonCrown(ctx, X(-1), hy - 4.6, 6.5);
}

/**
 * The head seen head-on, carried high over the chest and coming at you:
 * brow, both eyes, the snout and the open jaws under it with the fire in
 * them, and the horns up and out from the skull.
 */
function dragonHeadFront(ctx: Ctx, hy: number, D: Dragonkind, crowned: boolean) {
  dragonHorn(ctx, 16 - 5, hy - 4.5, -8, -9, 1.9, D);
  dragonHorn(ctx, 16 + 5, hy - 4.5, 8, -9, 1.9, D);
  for (const s of [-1, 1]) { // the frills, out behind the jaw
    ctx.beginPath();
    ctx.moveTo(16 + s * 6, hy - 1);
    ctx.lineTo(16 + s * 11, hy + 1.5);
    ctx.lineTo(16 + s * 7, hy + 5);
    ctx.closePath();
    dragonFill(ctx, D, D.dark);
  }
  // The open mouth, lowest of all, then the snout over it.
  ctx.beginPath();
  ctx.ellipse(16, hy + 7.6, 5.2, 3, 0, 0, Math.PI * 2);
  dragonFill(ctx, D, D.maw);
  ctx.fillStyle = D.mawHot;
  ctx.beginPath();
  ctx.ellipse(16, hy + 7.8, 2.6, 1.3, 0, 0, Math.PI * 2);
  ctx.fill();
  dragonTeeth(ctx, 16 - 4.5, 16 + 4.5, hy + 5.4, 4, 1, D, 1.7);
  dragonTeeth(ctx, 16 - 3.2, 16 + 3.2, hy + 10.2, 2, -1, D, 1.5);
  ctx.beginPath();
  ctx.ellipse(16, hy, 7.6, 6, 0, 0, Math.PI * 2);
  dragonFill(ctx, D, D.scale);
  ctx.beginPath(); // the snout, down over the top of the mouth
  ctx.ellipse(16, hy + 3.6, 5.4, 3.2, 0, 0, Math.PI * 2);
  dragonFill(ctx, D, D.scale);
  ctx.beginPath(); // the brow
  ctx.moveTo(16 - 8, hy - 1.5);
  ctx.lineTo(16 + 8, hy - 1.5);
  ctx.lineTo(16 + 5, hy - 6);
  ctx.lineTo(16 - 5, hy - 6);
  ctx.closePath();
  ctx.fillStyle = D.lit;
  ctx.fill();
  ctx.fillStyle = D.line;
  for (const s of [-1, 1]) {
    ctx.beginPath();
    ctx.ellipse(16 + s * 2.2, hy + 2.8, 0.9, 0.7, 0, 0, Math.PI * 2);
    ctx.fill();
  }
  dragonEye(ctx, 16 - 4, hy - 2.2, D);
  dragonEye(ctx, 16 + 4, hy - 2.2, D);
  if (crowned) dragonCrown(ctx, 16, hy - 6.4, 9);
}

/**
 * The dragon itself, in three drawings rather than one. Seen from the side
 * it is a long-bodied beast with its neck curved up and its head carried
 * high, jaws open, the wings raised behind it and the tail sweeping out the
 * other way; head-on it is coming at you, wings out to either side and the
 * jaws lowest of all; from behind it is the same animal with its tail
 * towards you and nothing of its face showing. Each part is edged in the
 * dark line colour, so the neck, the legs and the wings stay separate
 * things at a tile's size. The old drawing was flat blobs — a body the
 * shape of an egg, a neck that ran straight into it, a tail that was one
 * thick stroke — and from the front and the back it was a circle with two
 * horns (2026-10-23: "Improve dragon graphics").
 */
function drawDragon(ctx: Ctx, dir: number, legOff: number, H: number, _eyes: (x: number, y: number, c?: string) => void, D: Dragonkind = DRAGON, crowned = false, wings = true) {
  const side = dir === 1 ? 1 : dir === 3 ? -1 : 0;
  const bob = legOff === 0 ? 0 : 1;
  ctx.lineCap = "round";
  ctx.lineJoin = "round";

  if (side !== 0) {
    // --- in profile ---------------------------------------------------
    const X = (d: number) => 16 + side * d;
    // The far wing first, behind everything, and darker for being the far one.
    if (wings) dragonWing(ctx, X(-6), H - 27, X(-24), H - 55 - legOff * 1.5, 21, D, false);
    // The tail: out behind, tapering, with a spade on the end.
    const tip: [number, number] = [X(-37), H - 7];
    dragonTail(ctx, [X(-10), H - 21], [X(-28), H - 28 + legOff], tip, 7.5, D, [
      [X(-35), tip[1] - 5], [X(-42), tip[1] - 1.5], [X(-36.5), tip[1] + 3.5], [X(-33), tip[1] - 0.5],
    ], 3);
    // The far pair of legs, in shadow and set back.
    dragonLeg(ctx, X(-9), H - 15, -side, false, -legOff, H, D);
    dragonLeg(ctx, X(8), H - 15, side, false, legOff, H, D);
    // The body: a long barrel, a little higher at the shoulders, lit along
    // the top, scaled, and shadowed under the belly plates.
    const rot = -side * 0.1;
    ctx.save();
    ctx.translate(16, H - 20);
    ctx.rotate(rot);
    ctx.beginPath();
    ctx.ellipse(0, 0, 16, 8.5, 0, 0, Math.PI * 2);
    dragonFill(ctx, D, D.scale);
    ctx.save();
    ctx.clip();
    ctx.fillStyle = D.lit;
    ctx.beginPath();
    ctx.ellipse(1, -4.5, 12.5, 3.2, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
    ctx.restore();
    dragonScales(ctx, 16, H - 20, 16, 8.5, D, rot);
    ctx.save();
    ctx.translate(16, H - 20);
    ctx.rotate(rot);
    ctx.beginPath();
    ctx.ellipse(0, 0, 16, 8.5, 0, 0, Math.PI * 2);
    ctx.clip();
    ctx.fillStyle = D.belly;
    ctx.beginPath();
    ctx.ellipse(side * 1, 4.6, 13.5, 4.2, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = D.bellyDark; // the plates across the belly
    ctx.lineWidth = 0.9;
    for (let i = -3; i <= 3; i++) {
      ctx.beginPath();
      ctx.moveTo(side * 1 + i * 3.8, 1.2);
      ctx.lineTo(side * 1 + i * 3.8, 9);
      ctx.stroke();
    }
    // Facing west it is his left side that is towards you, and the bare
    // patch shows at the front of the chest.
    if (D.gems) dragonGems(ctx, side * 1, 4.6, 12.5, 3.4, D, side < 0 ? [side * 10, 3.6] : undefined);
    ctx.restore();
    dragonSpines(ctx, [X(-13), H - 26.5], [X(9), H - 29.5], wings ? 6 : 7, wings ? 2.6 : 4.6, D, !wings);
    // The near pair of legs, in the light and in front of the body.
    dragonLeg(ctx, X(-7), H - 14, -side, true, legOff, H, D);
    dragonLeg(ctx, X(11), H - 14, side, true, -legOff, H, D);
    // The neck curves up out of the shoulders and carries the head high,
    // the throat plated in the belly's colour down its front.
    const hx = X(23), hy = H - 42 + bob;
    const neck = () => {
      ctx.beginPath();
      ctx.moveTo(X(8), H - 24);
      ctx.bezierCurveTo(X(17), H - 25, X(15), H - 40, hx, hy + 1);
    };
    for (const [w, col] of [[9.5, D.line], [7.5, D.scale]] as const) {
      neck();
      ctx.strokeStyle = col;
      ctx.lineWidth = w;
      ctx.stroke();
    }
    ctx.save(); // the throat: the front half of the neck, in belly colour
    neck();
    ctx.lineWidth = 7.5;
    ctx.strokeStyle = D.belly;
    ctx.beginPath();
    ctx.moveTo(X(9), H - 21);
    ctx.bezierCurveTo(X(19), H - 22, X(18), H - 37, X(25), H - 38);
    ctx.lineWidth = 3;
    ctx.stroke();
    ctx.restore();
    ctx.strokeStyle = D.lit; // the ridge of the neck, in the light
    ctx.lineWidth = 1.6;
    ctx.beginPath();
    ctx.moveTo(X(7), H - 27);
    ctx.bezierCurveTo(X(15), H - 28.5, X(13), H - 42, X(20), H - 45);
    ctx.stroke();
    if (!wings) dragonSpines(ctx, [X(11), H - 29], [X(19), H - 43], 4, 2.6, D, true);
    dragonHeadSide(ctx, hx, hy, side, D, crowned);
    // The near wing, over the body and raised: paler than the far one, and
    // reaching further up so the two of them read as a pair.
    if (wings) dragonWing(ctx, X(0), H - 27, X(-18), H - 62 + legOff * 1.5, 23, D, true);
    return;
  }

  // --- head-on, or from behind ----------------------------------------
  const front = dir === 2;
  // Both wings, spread wide. From behind they are the first thing you see
  // and the body is tucked under them; head-on they frame it.
  if (wings) for (const s of [-1, 1]) dragonWing(ctx, 16 + s * 5, H - 30, 16 + s * 30, H - 54 - legOff * s * 1.5, 22, D, !front);
  // Head-on the tail goes away from you, off to one side behind the body,
  // so it is drawn before it. From behind it comes towards you and lies
  // over everything, which is drawn further down.
  if (front) {
    dragonTail(ctx, [16 + 6, H - 24], [16 + 22, H - 28 + legOff], [16 + 29, H - 15], 5, D, [
      [16 + 27, H - 19], [16 + 34, H - 16], [16 + 29, H - 11], [16 + 26, H - 14],
    ], 0);
  }
  // From behind the forelegs are the far pair and peek out past the
  // shoulders; head-on the hind legs are, and peek out past the hips.
  if (front) {
    dragonLeg(ctx, 16 - 13, H - 14, 0, false, legOff * 0.5, H, D);
    dragonLeg(ctx, 16 + 13, H - 14, 0, false, -legOff * 0.5, H, D);
  } else {
    dragonLeg(ctx, 16 - 12, H - 17, 0, false, -legOff * 0.5, H, D);
    dragonLeg(ctx, 16 + 12, H - 17, 0, false, legOff * 0.5, H, D);
  }
  ctx.beginPath(); // the body
  ctx.ellipse(16, H - 19, 13, 10, 0, 0, Math.PI * 2);
  dragonFill(ctx, D, D.scale);
  ctx.save();
  ctx.beginPath();
  ctx.ellipse(16, H - 19, 13, 10, 0, 0, Math.PI * 2);
  ctx.clip();
  ctx.fillStyle = D.lit;
  ctx.beginPath();
  if (front) ctx.ellipse(16, H - 26, 10, 3.6, 0, 0, Math.PI * 2);
  else ctx.ellipse(16, H - 22, 9, 6, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
  dragonScales(ctx, 16, H - 19, 13, 10, D);
  if (front) {
    ctx.save();
    ctx.beginPath();
    ctx.ellipse(16, H - 19, 13, 10, 0, 0, Math.PI * 2);
    ctx.clip();
    ctx.fillStyle = D.belly;
    ctx.beginPath();
    ctx.ellipse(16, H - 13, 9.5, 7.5, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = D.bellyDark; // the plates, curving down the chest
    ctx.lineWidth = 0.9;
    for (const y of [-17, -13.5, -10, -6.5]) {
      ctx.beginPath();
      ctx.arc(16, H + y - 9, 10.5, Math.PI * 0.32, Math.PI * 0.68);
      ctx.stroke();
    }
    // Head-on his left breast is on your right.
    if (D.gems) dragonGems(ctx, 16, H - 12, 8.5, 6, D, [16 + 4.5, H - 16]);
    ctx.restore();
  } else {
    dragonSpines(ctx, [16, H - 29], [16, H - 10], wings ? 5 : 6, wings ? 2.6 : 3.6, D, !wings);
  }
  // The near pair: the forelegs head-on, splayed under the chest with the
  // claws at you; the hind legs from behind.
  if (front) {
    dragonLeg(ctx, 16 - 9, H - 12, 0, true, -legOff * 0.5, H, D);
    dragonLeg(ctx, 16 + 9, H - 12, 0, true, legOff * 0.5, H, D);
  } else {
    dragonLeg(ctx, 16 - 10, H - 12, 0, true, legOff * 0.5, H, D);
    dragonLeg(ctx, 16 + 10, H - 12, 0, true, -legOff * 0.5, H, D);
  }
  // The neck, up from the chest, and the head on it.
  const hy = H - 40 + bob;
  if (!wings && front) dragonSpines(ctx, [16 - 8, H - 28], [16 + 8, H - 28], 5, 3.4, D, true);
  // The neck: a column up from between the shoulders, narrowing towards
  // the head, since it is going away from the viewer as it rises.
  const nw = front ? 3.6 : 2.8; // narrower from behind, where it leans away
  ctx.beginPath();
  ctx.moveTo(16 - 5.5, H - 22);
  ctx.quadraticCurveTo(16 - 5.2, hy + 2, 16 - nw, hy + 1);
  ctx.lineTo(16 + nw, hy + 1);
  ctx.quadraticCurveTo(16 + 5.2, hy + 2, 16 + 5.5, H - 22);
  ctx.closePath();
  dragonFill(ctx, D, D.scale);
  if (front) {
    ctx.strokeStyle = D.belly; // the throat plates
    ctx.lineWidth = 4;
    ctx.beginPath();
    ctx.moveTo(16, H - 25);
    ctx.lineTo(16, hy + 6);
    ctx.stroke();
    ctx.strokeStyle = D.bellyDark;
    ctx.lineWidth = 0.9;
    for (const y of [H - 28, H - 31, H - 34]) {
      ctx.beginPath();
      ctx.moveTo(16 - 2, y);
      ctx.lineTo(16 + 2, y);
      ctx.stroke();
    }
    dragonHeadFront(ctx, hy, D, crowned);
  } else {
    ctx.fillStyle = D.dark; // the neck in its own shadow either side of the ridge
    ctx.globalAlpha = 0.35;
    ctx.fillRect(16 - 5, hy + 1, 2.2, H - 23 - hy);
    ctx.fillRect(16 + 2.8, hy + 1, 2.2, H - 23 - hy);
    ctx.globalAlpha = 1;
    dragonSpines(ctx, [16, H - 29], [16, hy + 1], wings ? 3 : 4, wings ? 2 : 3, D, !wings);
    // The back of the skull between the horns, and nothing of the face
    // but the frills out either side of the jaw.
    dragonHorn(ctx, 16 - 4, hy - 3, -8, -9, 1.9, D);
    dragonHorn(ctx, 16 + 4, hy - 3, 8, -9, 1.9, D);
    for (const s of [-1, 1]) {
      ctx.beginPath();
      ctx.moveTo(16 + s * 5, hy);
      ctx.lineTo(16 + s * 10.5, hy + 2.5);
      ctx.lineTo(16 + s * 6, hy + 5.5);
      ctx.closePath();
      dragonFill(ctx, D, D.dark);
    }
    ctx.beginPath();
    ctx.ellipse(16, hy + 0.5, 7, 5.8, 0, 0, Math.PI * 2);
    dragonFill(ctx, D, D.scale);
    ctx.fillStyle = D.lit;
    ctx.beginPath();
    ctx.ellipse(16, hy - 1, 4.6, 3, 0, 0, Math.PI * 2);
    ctx.fill();
    if (crowned) dragonCrown(ctx, 16, hy - 4.2, 9);
    // The tail, last of all: seen from behind it comes down the screen at
    // you, over the body and the legs both, and ends in the spade at the
    // viewer's feet.
    dragonTail(ctx, [16, H - 17], [16 + 5 + legOff, H - 10], [16 + 3, H - 3], 7, D, [
      [16 - 1.5, H - 6.5], [16 + 9, H - 3], [16 + 2, H], [16 - 1, H - 2.5],
    ], 3);
  }
}

/**
 * The demon's palette. Everything else out in the world is painted in some
 * colour a living thing comes in; this is painted in two that do not belong
 * together — a dried-blood red dark enough to read as black at a glance, and
 * the orange of something still alight inside it, showing through where the
 * skin has split. The fire is the only bright thing on the whole animal, so
 * across a dark cave the eye finds the cracks, the eyes and the claws, and
 * nothing else. That is the shape a player should carry away from the one
 * second they get to look at it before it is on them.
 */
const DEMON = {
  skin: "#6d1a18",
  lit: "#9e2a22",
  dark: "#3b0e0e",
  char: "#190809",
  web: "#240c0e",
  webLit: "#401518",
  horn: "#ded3ba",
  fire: "#ff7a1c",
  ember: "#ffd25e",
  eye: "#ffe45a",
};

/**
 * The fire under the skin, laid down in two passes: a wide dull stroke for
 * the heat either side of a split, and a thin bright one along the middle of
 * it. Two passes is what a glow costs at this size — a blur or a gradient
 * washes out to a smudge by the time it is a few pixels across and stops
 * reading as a crack in anything at all.
 */
function demonCracks(ctx: Ctx, lines: number[][]) {
  ctx.lineCap = "round";
  for (const [w, col] of [[2.6, DEMON.fire], [1, DEMON.ember]] as const) {
    ctx.lineWidth = w;
    ctx.strokeStyle = col;
    ctx.beginPath();
    for (const [x1, y1, x2, y2] of lines) {
      ctx.moveTo(x1, y1);
      ctx.lineTo(x2, y2);
    }
    ctx.stroke();
  }
}

/**
 * One horn, from (x, y) out to (x + dx, y + dy): thick where it leaves the
 * skull, bending away and tapering to a point, with growth rings cut across
 * it. It is a filled shape rather than a stroked line because a stroke is the
 * same width from end to end, and a horn that does not taper is a tusk. The
 * demon wears two of them and they are half of what it is recognised by, so
 * they are worth the extra path.
 */
function demonHorn(ctx: Ctx, x: number, y: number, dx: number, dy: number, base = 2.6, col = DEMON.horn) {
  const len = Math.hypot(dx, dy) || 1;
  const px = -dy / len, py = dx / len; // square across the horn
  ctx.fillStyle = col;
  ctx.beginPath();
  ctx.moveTo(x - px * base, y - py * base);
  // Out along the underside to the point, then back down the upper edge on
  // a tighter curve, which is what puts the bend in it.
  ctx.quadraticCurveTo(x + dx * 0.55 - px * base, y + dy * 0.55 - py * base, x + dx, y + dy);
  ctx.quadraticCurveTo(x + dx * 0.4 + px * base * 1.4, y + dy * 0.4 + py * base * 1.4, x + px * base, y + py * base);
  ctx.closePath();
  ctx.fill();
  ctx.strokeStyle = DEMON.dark;
  ctx.lineWidth = 0.9;
  ctx.beginPath();
  for (const t of [0.3, 0.55]) {
    const cx = x + dx * t, cy = y + dy * t, r = base * (1 - t * 0.7);
    ctx.moveTo(cx - px * r, cy - py * r);
    ctx.lineTo(cx + px * r, cy + py * r);
  }
  ctx.stroke();
}

/**
 * The Balrog's own colours, beside the demon's (DEMON, which its horn and
 * the gear made from it keep). Tolkien's is a shadow before it is a body:
 * "a great shadow, in the middle of which was a dark form, of man-shape
 * maybe, yet greater". So it is drawn nearly black, the red gone out of the
 * skin, and lit only by its own fire — the rim along an edge where the mane
 * throws its light, the cracks, the eyes and the mouth.
 */
const BALROG = {
  body: "#1f0f0e",
  lit: "#3a1712",
  deep: "#0c0607",
  rim: "#b8401a",
  horn: "#2e2622",
  hornLit: "#4d3f36",
  grip: "#120808",
  smoke: "rgba(12,5,6,0.82)",
  smokeVein: "rgba(150,44,20,0.4)",
  smokeRim: "rgba(226,92,30,0.6)",
  eyeGlow: "rgba(255,150,40,0.55)",
};

/**
 * How each of the Balrog's flames stands on its three flame frames, as a
 * nudge in pixels for the tongue (or tatter, or thong) at that place in the
 * list. Its fire turns over standing still as well as walking (see BLAZES),
 * so no two frames agree and none is the one it rests on.
 */
const BALROG_FLICKER: readonly (readonly number[])[] = [
  [0, 0, 0, 0, 0, 0, 0],
  [2, -2, 3, -1, 2, -2, 1],
  [-2, 3, -1, 2, -2, 1, -1],
];

/**
 * One tongue of the Balrog's fire, rising from (x, y) to a tip h above it
 * and lean across, w wide at the root, in the fire elemental's layers
 * (FIRE_ELEMENTAL_LAYERS) from the red rim in to the white heart. Each
 * layer is shorter and narrower about the root, so a tongue is hottest
 * where it leaves the body and red at the tip, as a flame is.
 */
function balrogFlame(ctx: Ctx, x: number, y: number, w: number, h: number, lean: number) {
  for (const [k, col] of FIRE_ELEMENTAL_LAYERS) {
    if (k < 0.3 && h < 10) continue; // a white heart only in the tall ones
    const hw = (w / 2) * k, th = h * (0.3 + 0.7 * k), tip = lean * k;
    ctx.fillStyle = col;
    ctx.beginPath();
    ctx.moveTo(x - hw, y);
    ctx.bezierCurveTo(x - hw * 1.3, y - th * 0.45, x + tip * 0.5 - hw * 0.4, y - th * 0.75, x + tip, y - th);
    ctx.bezierCurveTo(x + tip * 0.5 + hw * 0.4, y - th * 0.75, x + hw * 1.3, y - th * 0.45, x + hw, y);
    ctx.closePath();
    ctx.fill();
  }
}

/**
 * One of the Balrog's wings: "the shadow about it reached out like two vast
 * wings". Not a bat's, which is what the demon had and what the dragon
 * still has (membraneWing), but smoke: a sweep up from the shoulder at
 * (sx, sy) to the tip at (tx, ty), and a trailing edge that comes back
 * down to the waist in tatters, which drift with the flame frame. Faint
 * veins of red run out through it from the shoulder, and the top edge
 * catches the fire, so it reads as smoke with a fire under it rather than
 * as a cloak.
 */
function balrogWing(ctx: Ctx, sx: number, sy: number, tx: number, ty: number, drop: number, flame: number) {
  const f = BALROG_FLICKER[flame % 3];
  const ex = sx + (tx - sx) * 0.12, ey = sy + drop;
  const n = 5;
  const tatters: [number, number][] = [];
  ctx.beginPath();
  ctx.moveTo(sx, sy);
  ctx.quadraticCurveTo(sx + (tx - sx) * 0.35, ty - 4, tx, ty);
  for (let i = 0; i < n; i++) {
    const a = (i + 0.5) / n, b = (i + 1) / n;
    const hang = 5 + (i % 2) * 3 + f[i] * 0.7;
    const px = tx + (ex - tx) * a, py = ty + (ey - ty) * a + hang;
    tatters.push([px, py]);
    ctx.lineTo(px, py);
    ctx.lineTo(tx + (ex - tx) * b, ty + (ey - ty) * b - 1);
  }
  ctx.closePath();
  ctx.fillStyle = BALROG.smoke;
  ctx.fill();
  ctx.lineCap = "round";
  ctx.strokeStyle = BALROG.smokeVein;
  ctx.lineWidth = 1;
  ctx.beginPath();
  for (const [px, py] of tatters.slice(0, 4)) {
    ctx.moveTo(sx, sy);
    ctx.quadraticCurveTo((sx + px) / 2, (sy + py) / 2 - 4, px, py - 3);
  }
  ctx.stroke();
  ctx.strokeStyle = BALROG.smokeRim;
  ctx.lineWidth = 1.2;
  ctx.beginPath();
  ctx.moveTo(sx, sy);
  ctx.quadraticCurveTo(sx + (tx - sx) * 0.35, ty - 4, tx, ty);
  ctx.stroke();
}

/**
 * The sword: "in its right hand was a blade like a stabbing tongue of
 * fire". A black cross-guard and grip in the fist at (x, y), and out along
 * the angle a blade that is a flame drawn long and straight, waved at the
 * edges like a flamberge and in the fire's layers, so the heart of it is
 * white near the hilt and the edges red. The wave runs along it with the
 * flame frame.
 */
function balrogSword(ctx: Ctx, x: number, y: number, ang: number, len: number, flame: number) {
  const f = BALROG_FLICKER[flame % 3];
  ctx.save();
  ctx.translate(x, y);
  ctx.rotate(ang);
  for (const [k, col] of FIRE_ELEMENTAL_LAYERS) {
    const w = 1.9 * k + 0.3, l = (len + f[0] * 0.5) * (0.6 + 0.4 * k);
    const steps = 6;
    ctx.fillStyle = col;
    ctx.beginPath();
    ctx.moveTo(2, -w);
    for (let i = 1; i < steps; i++) {
      const t = i / steps;
      ctx.lineTo(2 + (l - 2) * t, -w * (1 - t * 0.6) + Math.sin(t * 9 + flame * 2.1) * 0.45);
    }
    ctx.lineTo(l, 0);
    for (let i = steps - 1; i >= 1; i--) {
      const t = i / steps;
      ctx.lineTo(2 + (l - 2) * t, w * (1 - t * 0.6) + Math.sin(t * 9 + flame * 2.1 + 1.6) * 0.45);
    }
    ctx.lineTo(2, w);
    ctx.closePath();
    ctx.fill();
  }
  ctx.fillStyle = BALROG.grip;
  ctx.fillRect(-4, -1, 6, 2); // the grip, through the fist
  ctx.fillRect(1, -3.5, 1.8, 7); // the cross-guard
  ctx.restore();
}

/**
 * The whip: "in its left it held a whip of many thongs" (and since
 * 2026-10-12, "Give the balrog a whip and it pulls its target to it"). A
 * black grip in the fist at (x, y) and three lashes of fire hanging from
 * it to the floor at H and trailing along it towards s, each in three
 * passes, dark, fire and ember, the way the cracks in the skin are, so they
 * read as burning rather than as rope. They stir with the flame frame.
 */
function balrogWhip(ctx: Ctx, x: number, y: number, s: number, H: number, flame: number) {
  const f = BALROG_FLICKER[flame % 3];
  ctx.lineCap = "round";
  for (const [w, col] of [[2.2, DEMON.char], [1.3, DEMON.fire], [0.5, DEMON.ember]] as const) {
    ctx.lineWidth = w;
    ctx.strokeStyle = col;
    ctx.beginPath();
    for (let i = 0; i < 3; i++) {
      ctx.moveTo(x, y + 1);
      ctx.bezierCurveTo(
        x + s * (1 + i * 2) + f[i] * 0.4, y + 8,
        x + s * (i * 3) - f[i + 3] * 0.5, H - 3,
        x + s * (5 + i * 6) + f[i + 1] * 0.6, H - 1 - i,
      );
    }
    ctx.stroke();
  }
  ctx.fillStyle = BALROG.grip;
  ctx.fillRect(x - 1.5, y - 2.5, 3, 5);
}

/**
 * The Balrog (outfit 26; "demon" on the server). Drawn from what Tolkien
 * wrote of Durin's Bane on the bridge (2026-10-20: "improve the balrog
 * graphics"): a dark form of man-shape, yet greater, in a shadow that
 * "reached out like two vast wings"; a mane that "kindled, and blazed
 * behind it"; "in its right hand was a blade like a stabbing tongue of fire;
 * in its left it held a whip of many thongs." So: a black body with fire in
 * its cracks, wings of smoke with tattered edges, a mane of flame off the
 * head and shoulders, the sword of fire in the right hand and the
 * many-thonged whip in the left, in three drawings like the dragon — side
 * on, head on, and from behind. It keeps the demon's two horns, which are
 * what its horn drop and the demon helmet and shield are made from, but
 * they are black now, against the fire behind them.
 *
 * The step is legOff, as for everything else; the flame is its own frame,
 * nought to two (see BLAZES), so the fire moves while it stands.
 */
function drawDemon(ctx: Ctx, dir: number, legOff: number, H: number, eyes: (x: number, y: number, c?: string) => void, flame: number) {
  const side = dir === 1 ? 1 : dir === 3 ? -1 : 0;
  const B = BALROG;
  const f = BALROG_FLICKER[flame % 3];
  ctx.lineJoin = "round";
  ctx.lineCap = "round";

  // A leg: a heavy thigh, a shin bent back under it, and a hoof. The
  // colours are passed in, since the far leg of a profile is the same
  // drawing done in shadow.
  const leg = (x: number, off: number, skin: string, shade: string) => {
    ctx.fillStyle = skin;
    ctx.beginPath();
    ctx.ellipse(x, H - 21 + off / 2, 4.8, 6.5, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = shade;
    ctx.fillRect(x - 2, H - 17 + off, 4, 11);
    ctx.fillStyle = B.deep;
    ctx.fillRect(x - 3.2, H - 6 + off, 6.4, 4);
    ctx.fillStyle = DEMON.fire; // the floor scorching under it
    ctx.fillRect(x - 2.5, H - 2.5 + off, 5, 0.8);
  };
  // A fist, black, with the fire showing between the fingers.
  const fist = (x: number, y: number, s = 1) => {
    ctx.fillStyle = B.deep;
    ctx.beginPath();
    ctx.arc(x, y, 3 * s, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = DEMON.fire;
    ctx.fillRect(x - 1.5 * s, y - 0.3, 3 * s, 0.7);
  };
  // An arm, from the shoulder to the hand by way of the elbow.
  const arm = (x1: number, y1: number, cx: number, cy: number, x2: number, y2: number, col: string, w = 4.5) => {
    ctx.strokeStyle = col;
    ctx.lineWidth = w;
    ctx.beginPath();
    ctx.moveTo(x1, y1);
    ctx.quadraticCurveTo(cx, cy, x2, y2);
    ctx.stroke();
  };

  if (side !== 0) {
    // --- in profile -----------------------------------------------------
    // The sword is in the right hand, which is the near one going east
    // and the far one going west; the whip is in the left.
    const hx = 16 + side * 2, hy = H - 48;
    const swordHand = { x: 16 + side * 15, y: H - 33 - legOff / 2 };
    const whipHand = { x: 16 + side * 12, y: H - 26 + legOff / 2 };
    const swordNear = side === 1;
    // The far wing first, then the mane streaming back off the head and
    // shoulders, so the fire is between the two wings.
    balrogWing(ctx, 16 - side * 5, H - 39, 16 - side * 26, H - 62 - legOff, 22, flame);
    leg(16 - side * 5, -legOff, B.body, B.deep);
    const far = () => {
      if (swordNear) {
        arm(16, H - 38, 16 + side * 8, H - 33, whipHand.x, whipHand.y, B.deep, 4);
        balrogWhip(ctx, whipHand.x, whipHand.y, -side, H, flame);
        fist(whipHand.x, whipHand.y, 0.85);
      } else {
        balrogSword(ctx, swordHand.x, swordHand.y, Math.atan2(-1, side * 0.55), 30, flame);
        arm(16, H - 39, 16 + side * 8, H - 30, swordHand.x, swordHand.y, B.deep, 4);
        fist(swordHand.x, swordHand.y, 0.85);
      }
    };
    far();
    // The trunk: shoulders carried forward over a waist nothing like as
    // wide, the stoop of something that leans into a room before it walks
    // into it.
    ctx.fillStyle = B.body;
    ctx.beginPath();
    ctx.moveTo(16 - side * 9, H - 41);
    ctx.lineTo(16 + side * 7, H - 42);
    ctx.lineTo(16 + side * 6, H - 24);
    ctx.lineTo(16 - side * 5, H - 22);
    ctx.closePath();
    ctx.fill();
    ctx.fillStyle = B.lit; // the chest, where the sword lights it
    ctx.beginPath();
    ctx.moveTo(16 + side * 3, H - 42);
    ctx.lineTo(16 + side * 7, H - 42);
    ctx.lineTo(16 + side * 6, H - 26);
    ctx.lineTo(16 + side * 3, H - 27);
    ctx.closePath();
    ctx.fill();
    ctx.strokeStyle = B.rim; // the back, lit by the mane
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(16 - side * 9, H - 40);
    ctx.lineTo(16 - side * 5, H - 23);
    ctx.stroke();
    demonCracks(ctx, [
      [16 - side * 5, H - 38, 16 + side * 1, H - 32],
      [16 + side * 1, H - 32, 16 - side * 2, H - 25],
    ]);
    leg(16 + side * 4, legOff, B.body, B.deep);
    // The near wing, swept back off the shoulder over the far one, so
    // the horns stay the top of the silhouette and the smoke is behind
    // them.
    balrogWing(ctx, 16 - side * 3, H - 40, 16 - side * 21, H - 64 + legOff, 20, flame + 1);
    // The mane, streaming back off the head and down the neck to the
    // shoulders: "its streaming mane kindled, and blazed behind it".
    for (let i = 0; i < 5; i++) {
      const bx = hx - side * (2 + i * 2.2), by = hy - 3 + i * 3;
      balrogFlame(ctx, bx, by + 3, 6.5 - i * 0.4, 15 - i * 1.4 + f[i], -side * (8 + i) + f[i + 1] * 0.5);
    }
    // The head, carried low and thrust forward on a thick neck: a wedge,
    // deep at the back where the horns come out and cut away to a blunt
    // muzzle.
    arm(16 - side * 1, H - 41, 16, H - 43, 16 + side * 2, H - 45, B.body, 7);
    ctx.fillStyle = B.body;
    ctx.beginPath();
    ctx.moveTo(hx - side * 7, hy - 4);
    ctx.lineTo(hx + side * 3, hy - 5);
    ctx.lineTo(hx + side * 7, hy);
    ctx.lineTo(hx + side * 6, hy + 4);
    ctx.lineTo(hx - side * 4, hy + 5);
    ctx.lineTo(hx - side * 7, hy + 1);
    ctx.closePath();
    ctx.fill();
    ctx.fillStyle = B.deep;
    ctx.beginPath(); // the socket, so the eye burns in a hole and not on a cheek
    ctx.moveTo(hx - side * 3, hy - 3);
    ctx.lineTo(hx + side * 5, hy - 1);
    ctx.lineTo(hx + side * 5, hy + 1.5);
    ctx.lineTo(hx - side * 3, hy + 0.5);
    ctx.closePath();
    ctx.fill();
    ctx.fillStyle = DEMON.fire; // the mouth, a slit with the fire in it
    ctx.beginPath();
    ctx.moveTo(hx - side * 3, hy + 3);
    ctx.lineTo(hx + side * 6, hy + 2);
    ctx.lineTo(hx + side * 6, hy + 3.4);
    ctx.lineTo(hx - side * 3, hy + 4.2);
    ctx.closePath();
    ctx.fill();
    demonHorn(ctx, hx - side * 5, hy - 3, -side * 4, -13, 2.2, B.horn); // the far horn
    demonHorn(ctx, hx - side * 3, hy - 4, -side * 8, -12, 2.6, B.hornLit);
    ctx.fillStyle = B.eyeGlow;
    ctx.beginPath();
    ctx.arc(hx + side * 3 + 1, hy - 0.5, 2.2, 0, Math.PI * 2);
    ctx.fill();
    eyes(hx + side * 2, hy - 1.5, DEMON.eye);
    // The near arm last, over everything.
    if (swordNear) {
      arm(16 + side * 4, H - 40, 16 + side * 13, H - 40, swordHand.x, swordHand.y, B.body);
      balrogSword(ctx, swordHand.x, swordHand.y, Math.atan2(-1, side * 0.55), 30, flame);
      fist(swordHand.x, swordHand.y);
    } else {
      arm(16 + side * 4, H - 40, 16 + side * 12, H - 35, whipHand.x, whipHand.y, B.body);
      balrogWhip(ctx, whipHand.x, whipHand.y, -side, H, flame);
      fist(whipHand.x, whipHand.y);
    }
    return;
  }

  // --- head-on, or from behind ------------------------------------------
  const front = dir === 2;
  // The right hand is the viewer's left head-on and their right from
  // behind; the sword is in it, raised, and the whip hangs from the other.
  const ss = front ? -1 : 1;
  const hy = H - 46;
  // Both wings, spread as wide as the sprite has room for: a shadow that
  // is wider than its own tile two and a half times over, so the first
  // thing seen of it is the dark coming.
  for (const s of [-1, 1]) {
    balrogWing(ctx, 16 + s * 7, H - 38, 16 + s * 31, H - 61 - legOff * s, 24, flame + (s > 0 ? 1 : 0));
  }
  // Head-on the mane is behind the head and goes down before it.
  const mane = () => {
    const tongues: [number, number, number][] = [
      [-12, H - 37, 7], [12, H - 37, 7], [-9, hy + 3, 10], [9, hy + 3, 10],
      [-5, hy, 14], [5, hy, 14], [0, hy - 1, 17],
    ];
    tongues.forEach(([dx, by, h], i) => {
      balrogFlame(ctx, 16 + dx, by, dx === 0 ? 8 : 6.5, h + f[i], dx * 0.6 + f[(i + 2) % 7] * 0.6);
    });
  };
  if (front) mane();
  leg(16 - 6, legOff, B.body, B.deep);
  leg(16 + 6, -legOff, B.body, B.deep);
  ctx.fillStyle = B.body;
  ctx.beginPath(); // shoulders far wider than the waist under them
  ctx.moveTo(16 - 12, H - 40);
  ctx.lineTo(16 + 12, H - 40);
  ctx.lineTo(16 + 7, H - 23);
  ctx.lineTo(16 - 7, H - 23);
  ctx.closePath();
  ctx.fill();
  ctx.strokeStyle = B.rim; // both flanks lit by the fire behind
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(16 - 12, H - 39);
  ctx.lineTo(16 - 7, H - 24);
  ctx.moveTo(16 + 12, H - 39);
  ctx.lineTo(16 + 7, H - 24);
  ctx.stroke();
  if (front) {
    ctx.fillStyle = B.lit;
    ctx.beginPath(); // the chest, lit across the top
    ctx.moveTo(16 - 10, H - 39);
    ctx.lineTo(16 + 10, H - 39);
    ctx.lineTo(16 + 7, H - 33);
    ctx.lineTo(16 - 7, H - 33);
    ctx.closePath();
    ctx.fill();
    demonCracks(ctx, [
      [16, H - 36, 16, H - 28],
      [16, H - 28, 16 - 5, H - 24],
      [16, H - 28, 16 + 5, H - 24],
    ]);
  } else {
    ctx.fillStyle = B.deep;
    ctx.fillRect(15, H - 40, 2, 17); // the spine, in shadow between the wings
    demonCracks(ctx, [
      [16 - 8, H - 37, 16 - 2, H - 33],
      [16 + 8, H - 37, 16 + 2, H - 33],
    ]);
  }
  // The arms: the sword arm bent and raised with the blade standing up out
  // of the fist, the whip arm hanging well past the hip. They swing
  // against the legs, so a walking Balrog looks like it is coming rather
  // than sliding.
  const sword = { x: 16 + ss * 16, y: H - 33 + ss * legOff * 0.5 };
  const whip = { x: 16 - ss * 16, y: H - 22 - ss * legOff };
  arm(16 + ss * 10, H - 38, 16 + ss * 19, H - 36, sword.x, sword.y, B.body);
  balrogSword(ctx, sword.x, sword.y, Math.atan2(-1, ss * 0.45), 30, flame);
  fist(sword.x, sword.y);
  arm(16 - ss * 10, H - 38, 16 - ss * 17, H - 34, whip.x, whip.y, B.body);
  balrogWhip(ctx, whip.x, whip.y, -ss, H, flame);
  fist(whip.x, whip.y);
  // The head: sunk between the shoulders rather than set on a neck above
  // them, which is most of why it reads as a brute and not as a tall man.
  ctx.fillStyle = B.body;
  ctx.beginPath();
  ctx.ellipse(16, hy, 7, 6, 0, 0, Math.PI * 2);
  ctx.fill();
  if (front) {
    ctx.fillStyle = B.deep;
    ctx.beginPath(); // the brow, a shelf of shadow the eyes burn out of
    ctx.moveTo(16 - 7, hy - 3);
    ctx.lineTo(16 + 7, hy - 3);
    ctx.lineTo(16 + 6, hy + 1);
    ctx.lineTo(16 - 6, hy + 1);
    ctx.closePath();
    ctx.fill();
    ctx.fillStyle = DEMON.fire; // the jaw, open on the fire inside it
    ctx.beginPath();
    ctx.ellipse(16, hy + 4.5, 3.6, 1.8, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = DEMON.ember;
    ctx.fillRect(16 - 1.5, hy + 4, 3, 1);
    ctx.fillStyle = B.eyeGlow;
    for (const dx of [-2, 3]) {
      ctx.beginPath();
      ctx.arc(16 + dx, hy, 1.7, 0, Math.PI * 2);
      ctx.fill();
    }
  }
  demonHorn(ctx, 16 - 5, hy - 4, -7, -12, 2.6, front ? B.hornLit : B.horn);
  demonHorn(ctx, 16 + 5, hy - 4, 7, -12, 2.6, front ? B.hornLit : B.horn);
  eyes(16, hy - 1, DEMON.eye);
  // From behind, the mane is the back of the head and lies over it.
  if (!front) mane();
}
/**
 * A wolf's colours: the coat, the pale of the belly, throat, muzzle and
 * the insides of the ears, the dark of the saddle, the far legs and the
 * tip of the tail, the nose, and the eye. The wolf and the winter wolf
 * are one drawing and two of these — the bear's own comment says the
 * winter wolf is "the wolf with four colours changed" and it was typed
 * out again in full anyway, so now it is not.
 */
interface WolfPalette {
  fur: string;
  pale: string;
  dark: string;
  nose: string;
  eye: string;
}

const WOLF_GREY: WolfPalette = { fur: "#7d7a72", pale: "#b3ada0", dark: "#46443f", nose: "#1c1b19", eye: "#ffcc33" };
const WOLF_WHITE: WolfPalette = { fur: "#e4ebf0", pale: "#ffffff", dark: "#a9b6c2", nose: "#343a40", eye: "#8fe2ff" };
/**
 * The white wolf of the Fell Winter (2026-11-01): whiter than a winter
 * wolf, its eyes paler, and drawn WHITE_WOLF_SCALE as big. WHITE_WOLF is
 * the same white for its pelt.
 */
const WOLF_FELL: WolfPalette = { fur: "#f4f7fa", pale: "#ffffff", dark: "#b8c4ce", nose: "#2a2e32", eye: "#d8f6ff" };
const WHITE_WOLF = { fur: "#f6f8fa", shade: "#c6d0d8", eye: "#5a8ea8" };
const WHITE_WOLF_SCALE = 1.35;

/**
 * A wolf, in the four facings. It was a grey ellipse with a ball on the
 * front and four sticks under it, and it read as a sheep: what makes a
 * wolf read as a wolf is the silhouette, and a sheep's is a round body on
 * short legs where a wolf's is a lean body on long ones, carried low,
 * with a long muzzle out in front, pricked ears, and a tail as thick as
 * the neck. So those are the parts, in order from the back of the picture
 * to the front: the far legs, the tail, the barrel and chest, the near
 * legs, the neck and head, the ears, the eye.
 *
 * In profile the body is long and shallow and the head is carried at the
 * height of the shoulder rather than above it, which is what says
 * "predator coming" instead of "standing about" — the same argument the
 * bear makes about its hump. Head-on it is narrow through the chest with
 * the muzzle pointing down at you; from behind it is a rump, a hanging
 * tail and two ears, and no face. The pale runs under the throat and
 * belly and along the muzzle, and a darker saddle lies along the back, so
 * the thing has a top and a bottom at the size it is actually played at.
 *
 * Legs swing in opposite pairs on the walk frame like everything else
 * that walks in this world, and the body drops a pixel on the stride.
 */
function drawWolf(ctx: Ctx, p: WolfPalette, dir: number, frame: number, legOff: number, H: number, eyes: (cx: number, cy: number, col: string) => void) {
  const s = dir === 1 ? 1 : dir === 3 ? -1 : 0;
  const bob = frame === 0 ? 0 : 1;
  const leg = (x: number, off: number, w: number, col: string) => {
    ctx.fillStyle = col;
    ctx.fillRect(x - w / 2, H - 13 + off + bob, w, 13 - off - bob);
    // The paw, a shade darker and a shade wider than the leg.
    ctx.fillStyle = p.dark;
    ctx.fillRect(x - w / 2 - 0.5, H - 2.5, w + 1, 2.5);
  };
  if (s) {
    // Profile. Far legs first, dark, then the near pair over the body.
    leg(16 - s * 7, -legOff, 2.5, p.dark);
    leg(16 + s * 8, legOff, 2.5, p.dark);
    // The tail: off the rump, back and down, thick and bushy, dark at
    // the tip. Behind the body so the body laps over its root.
    ctx.strokeStyle = p.fur;
    ctx.lineWidth = 4.5;
    ctx.lineCap = "round";
    ctx.beginPath();
    ctx.moveTo(16 - s * 10, H - 19 + bob);
    ctx.quadraticCurveTo(16 - s * 18, H - 20 + bob, 16 - s * 19.5, H - 9 + bob);
    ctx.stroke();
    ctx.strokeStyle = p.dark;
    ctx.lineWidth = 3.5;
    ctx.beginPath();
    ctx.moveTo(16 - s * 18.8, H - 13 + bob);
    ctx.lineTo(16 - s * 19.5, H - 8 + bob);
    ctx.stroke();
    // The barrel: long, shallow, carried low.
    ctx.fillStyle = p.fur;
    ctx.beginPath();
    ctx.ellipse(16 - s * 1, H - 17 + bob, 12, 5.5, 0, 0, Math.PI * 2);
    ctx.fill();
    // The chest, deeper than the barrel, and the pale under it.
    ctx.beginPath();
    ctx.ellipse(16 + s * 7, H - 17 + bob, 6, 6.5, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = p.pale;
    ctx.beginPath();
    ctx.ellipse(16 + s * 4, H - 13 + bob, 8, 2.5, 0, 0, Math.PI * 2);
    ctx.fill();
    // The saddle along the back.
    ctx.fillStyle = p.dark;
    ctx.beginPath();
    ctx.ellipse(16 - s * 2, H - 21 + bob, 9, 2.2, 0, 0, Math.PI * 2);
    ctx.fill();
    // Near legs, in the coat's colour, over the belly.
    leg(16 - s * 10, legOff, 3, p.fur);
    leg(16 + s * 11, -legOff, 3, p.fur);
    // The neck, up and forward from the shoulder to the head, with the
    // pale of the throat under it.
    ctx.fillStyle = p.fur;
    ctx.beginPath();
    ctx.moveTo(16 + s * 5, H - 23 + bob);
    ctx.lineTo(16 + s * 12, H - 29 + bob);
    ctx.lineTo(16 + s * 15, H - 24 + bob);
    ctx.lineTo(16 + s * 9, H - 15 + bob);
    ctx.closePath();
    ctx.fill();
    ctx.fillStyle = p.pale;
    ctx.beginPath();
    ctx.moveTo(16 + s * 9, H - 16 + bob);
    ctx.lineTo(16 + s * 14, H - 21 + bob);
    ctx.lineTo(16 + s * 12, H - 15 + bob);
    ctx.closePath();
    ctx.fill();
    // The head, and the muzzle out in front of it: long, tapering to the
    // nose, pale along the jaw.
    ctx.fillStyle = p.fur;
    ctx.beginPath();
    ctx.ellipse(16 + s * 13, H - 27 + bob, 4.5, 4, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.beginPath();
    ctx.moveTo(16 + s * 14, H - 30 + bob);
    ctx.lineTo(16 + s * 21, H - 26 + bob);
    ctx.lineTo(16 + s * 20, H - 23 + bob);
    ctx.lineTo(16 + s * 13, H - 23 + bob);
    ctx.closePath();
    ctx.fill();
    ctx.fillStyle = p.pale;
    ctx.beginPath();
    ctx.moveTo(16 + s * 14, H - 24 + bob);
    ctx.lineTo(16 + s * 20, H - 24 + bob);
    ctx.lineTo(16 + s * 19, H - 22.5 + bob);
    ctx.lineTo(16 + s * 13, H - 22.5 + bob);
    ctx.closePath();
    ctx.fill();
    ctx.fillStyle = p.nose;
    ctx.fillRect(16 + s * 20 - 1, H - 27 + bob, 2, 2);
    // Two ears, pricked, the inside pale.
    const ear = (x: number) => {
      ctx.fillStyle = p.fur;
      ctx.beginPath();
      ctx.moveTo(x - 1.5, H - 29 + bob);
      ctx.lineTo(x + 0.5 * s, H - 35 + bob);
      ctx.lineTo(x + 2, H - 29 + bob);
      ctx.closePath();
      ctx.fill();
      ctx.fillStyle = p.pale;
      ctx.beginPath();
      ctx.moveTo(x - 0.5, H - 30 + bob);
      ctx.lineTo(x + 0.5 * s, H - 33 + bob);
      ctx.lineTo(x + 1, H - 30 + bob);
      ctx.closePath();
      ctx.fill();
    };
    ear(16 + s * 10.5);
    ear(16 + s * 13.5);
    // The eye, on the head's near side.
    ctx.fillStyle = p.eye;
    ctx.fillRect(16 + s * 15 - 1, H - 28 + bob, 2, 2);
    ctx.fillStyle = p.nose;
    ctx.fillRect(16 + s * 15 - 1 + (s > 0 ? 1 : 0), H - 28 + bob, 1, 2);
    return;
  }
  if (dir === 2) {
    // Head-on: narrow through the chest, the muzzle pointing down at
    // you, the ears up over the head. Hind legs behind the front pair.
    leg(12, -legOff, 2.5, p.dark);
    leg(20, legOff, 2.5, p.dark);
    ctx.fillStyle = p.fur;
    ctx.beginPath();
    ctx.ellipse(16, H - 17 + bob, 8, 7, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = p.pale;
    ctx.beginPath();
    ctx.ellipse(16, H - 14 + bob, 4.5, 4, 0, 0, Math.PI * 2);
    ctx.fill();
    leg(11, legOff, 3, p.fur);
    leg(21, -legOff, 3, p.fur);
    // The head, wider than it is tall, and the muzzle under the eyes.
    ctx.fillStyle = p.fur;
    ctx.beginPath();
    ctx.ellipse(16, H - 26 + bob, 6.5, 5.5, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = p.pale;
    ctx.beginPath();
    ctx.ellipse(16, H - 22 + bob, 3.5, 3.5, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = p.nose;
    ctx.fillRect(15, H - 24 + bob, 2, 2);
    const earF = (x: number, lean: number) => {
      ctx.fillStyle = p.fur;
      ctx.beginPath();
      ctx.moveTo(x - 2.5, H - 29 + bob);
      ctx.lineTo(x + lean, H - 36 + bob);
      ctx.lineTo(x + 2.5, H - 29 + bob);
      ctx.closePath();
      ctx.fill();
      ctx.fillStyle = p.pale;
      ctx.beginPath();
      ctx.moveTo(x - 1, H - 30 + bob);
      ctx.lineTo(x + lean * 0.7, H - 34 + bob);
      ctx.lineTo(x + 1, H - 30 + bob);
      ctx.closePath();
      ctx.fill();
    };
    earF(11.5, -1);
    earF(20.5, 1);
    eyes(16, H - 28 + bob, p.eye);
    return;
  }
  // From behind: the rump, the tail hanging, two ears, and no face.
  leg(11, legOff, 3, p.fur);
  leg(21, -legOff, 3, p.fur);
  leg(12, -legOff, 2.5, p.dark);
  leg(20, legOff, 2.5, p.dark);
  ctx.fillStyle = p.fur;
  ctx.beginPath();
  ctx.ellipse(16, H - 17 + bob, 8, 7.5, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = p.dark;
  ctx.beginPath();
  ctx.ellipse(16, H - 21 + bob, 6, 2.5, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.strokeStyle = p.fur;
  ctx.lineWidth = 4;
  ctx.lineCap = "round";
  ctx.beginPath();
  ctx.moveTo(16, H - 17 + bob);
  ctx.quadraticCurveTo(17, H - 12 + bob, 15.5, H - 6 + bob);
  ctx.stroke();
  ctx.strokeStyle = p.dark;
  ctx.lineWidth = 3;
  ctx.beginPath();
  ctx.moveTo(15.7, H - 9 + bob);
  ctx.lineTo(15.5, H - 5.5 + bob);
  ctx.stroke();
  ctx.fillStyle = p.fur;
  ctx.beginPath();
  ctx.ellipse(16, H - 26 + bob, 6, 5.5, 0, 0, Math.PI * 2);
  ctx.fill();
  const earB = (x: number, lean: number) => {
    ctx.fillStyle = p.fur;
    ctx.beginPath();
    ctx.moveTo(x - 2.5, H - 29 + bob);
    ctx.lineTo(x + lean, H - 36 + bob);
    ctx.lineTo(x + 2.5, H - 29 + bob);
    ctx.closePath();
    ctx.fill();
    ctx.fillStyle = p.dark;
    ctx.beginPath();
    ctx.moveTo(x - 1, H - 30 + bob);
    ctx.lineTo(x + lean * 0.7, H - 34 + bob);
    ctx.lineTo(x + 1, H - 30 + bob);
    ctx.closePath();
    ctx.fill();
  };
  earB(11.5, -1);
  earB(20.5, 1);
}

/**
 * The siren's palette. Everything about it is chosen against the tile it
 * will always be standing on, which is the only creature in this game of
 * which that is true: water is #2b5ea8, a flat middle blue, so a sea
 * creature drawn in sea colours would be a smudge. The skin is therefore
 * the palest thing in the bestiary and the hair the darkest — the sprite
 * is read as a light shape and a dark shape against one blue, and at the
 * zoom this is actually played at that is the whole of it.
 *
 * The tail keeps the sea colours, and keeps them deliberately: it is under
 * the surface, so it is meant to half disappear. What says "there is more
 * of this below the line" is the ripple rather than the tail.
 */
const SIREN = {
  skin: "#cfe6d8",
  skinLit: "#eef7ef",
  skinDark: "#8fb7a6",
  hair: "#10333a",
  hairLit: "#1d5761",
  tail: "#2f7f6a",
  tailDark: "#1d5150",
  ripple: "#bfe4f2",
  song: "#eaf9ff",
};

/**
 * A siren: half out of the water, and drawn as half out of the water.
 *
 * Every other creature in this world stands on the ground with its feet at
 * the bottom of the box. This one has no feet and never touches ground —
 * see groundFor on the server, which will not let it — so the bottom of
 * the box is not where it ends, it is where it goes under. The drawing is
 * built around that one line: SURFACE, a third of the way up the tile,
 * with the body above it and nothing but a suggestion below.
 *
 * WHAT IT TOOK TO MAKE IT READ, because the first three attempts did not
 * and the reasons are worth keeping. A creature is thirty-two pixels
 * across and is looked at from two tiles away: at that size a sprite is a
 * silhouette and three blocks of colour, and every piece of detail that
 * does not serve one of those four things is noise that closes the shape
 * up into a blob. Three mistakes in order —
 *
 *   - hair drawn over the crown as well as round it, which turns the head
 *     into a hood and the whole thing into an egg with a face in it. The
 *     mass behind now stops at the temples and only a thin fringe crosses
 *     the brow, so the head reads as a head sitting on shoulders.
 *   - a torso as wide as the hair, which leaves no dark edge either side
 *     and no shoulders. It is narrow now and tapers to the waist, and the
 *     hair shows past it on both sides: dark, pale, dark across the body
 *     is the read, and it is the only one that survives being small.
 *   - no colour where the two halves meet. A band of the tail's green at
 *     the waterline does more than anything else in the drawing to say
 *     what this is — skin above, scale below, and the join at the water.
 *
 * The order the pieces go down in is the order they are argued in below.
 * The tail is first and faintest, in the sea's own colours so it reads as
 * something seen through water rather than as a limb. The ripples go on
 * near the end, over the waist, so the body is lost in them; they are the
 * one part that says "floating" without relying on the tile underneath
 * being water, which matters because an admin can summon one anywhere and
 * a sprite sheet is looked at on a white page. The song is last: two arcs
 * standing off the mouth. A siren is singing whenever you can see it —
 * that is its whole attack and its Look line says so — and a creature
 * whose one dangerous thing is invisible until it lands is a creature you
 * cannot learn. The arcs are in the sprite rather than in the attack
 * effect on purpose: the effect says "it just sang at you", and this says
 * "that is a thing that sings". They go with the face for the back view,
 * since a voice is drawn coming out of a mouth and there is no mouth in
 * sight from behind.
 */
function drawSiren(ctx: Ctx, dir: number, legOff: number, H: number, eyes: (x: number, y: number, c?: string) => void) {
  const s = dir === 1 ? 1 : dir === 3 ? -1 : 0; // the side it faces, 0 head-on or away
  const back = dir === 0;
  const bob = legOff / 2; // the surface rising and falling against the body
  const SURFACE = H - 8 + bob;
  const hx = 16 + s * 1.5, hy = SURFACE - 17; // the head

  // UNDER THE WATER: the tail, leaning away from the side it faces so the
  // body reads as turned rather than as twisted, and the fluke off the
  // end of it. Both in the sea's own colours, because they are meant to
  // be half lost in it — what says there is more of this below the line
  // is the ripple, not the tail.
  const lean = s !== 0 ? -s : back ? -1 : 1;
  ctx.fillStyle = SIREN.tailDark;
  ctx.beginPath();
  ctx.moveTo(16 - 3.5, SURFACE - 3);
  ctx.quadraticCurveTo(16 + lean * 6, SURFACE + 2, 16 + lean * 8, H - 3);
  ctx.quadraticCurveTo(16 + lean * 1, SURFACE + 4, 16 + 3.5, SURFACE - 3);
  ctx.closePath();
  ctx.fill();
  ctx.fillStyle = SIREN.tail;
  ctx.beginPath();
  ctx.moveTo(16 + lean * 8, H - 4);
  ctx.lineTo(16 + lean * 14, H - 7);
  ctx.lineTo(16 + lean * 10, H - 1);
  ctx.lineTo(16 + lean * 3, H - 1);
  ctx.closePath();
  ctx.fill();

  // THE HAIR BEHIND, laid down before anything else: narrow over the
  // crown and flaring out past the shoulders to the water. What is left
  // of it once the body is drawn over the middle is a dark edge either
  // side of a pale one, and that edge is the whole silhouette — it is the
  // only part of this drawing that survives being twenty pixels tall.
  ctx.fillStyle = SIREN.hair;
  ctx.beginPath();
  ctx.moveTo(hx - 4.6, hy - 2.4);
  ctx.quadraticCurveTo(16 - 9, hy + 6, 16 - 6.2, SURFACE + 1);
  ctx.lineTo(16 + 6.2, SURFACE + 1);
  ctx.quadraticCurveTo(16 + 9, hy + 6, hx + 4.6, hy - 2.4);
  ctx.closePath();
  ctx.fill();

  // THE BODY above the line: shoulders that are actually shoulders and a
  // waist narrower than they are. The taper is what stops the thing
  // reading as a column with a face on top, which is what a straight
  // torso at this size always does.
  ctx.fillStyle = SIREN.skin;
  ctx.beginPath();
  ctx.moveTo(16 - 5.5, hy + 5);
  ctx.quadraticCurveTo(16, hy + 3.4, 16 + 5.5, hy + 5);
  ctx.lineTo(16 + 3, SURFACE + 1);
  ctx.lineTo(16 - 3, SURFACE + 1);
  ctx.closePath();
  ctx.fill();
  // Arms, hanging against it and set a shade darker so they are arms
  // rather than more torso. They swing with the walk like everything
  // else in this file.
  ctx.fillStyle = SIREN.skinDark;
  ctx.fillRect(16 - 7.4, hy + 5.5 - bob, 2.2, 8);
  ctx.fillRect(16 + 5.2, hy + 5.5 + bob, 2.2, 8);

  // WHERE THE SCALES BEGIN: a band of the tail's own green across the
  // waist, right at the waterline. It is small and it does most of the
  // work of saying what this is — pale skin above, scale below, and the
  // join at the surface.
  ctx.fillStyle = SIREN.tail;
  ctx.beginPath();
  ctx.moveTo(16 - 4, SURFACE - 3.5);
  ctx.lineTo(16 + 4, SURFACE - 3.5);
  ctx.lineTo(16 + 3, SURFACE + 1);
  ctx.lineTo(16 - 3, SURFACE + 1);
  ctx.closePath();
  ctx.fill();

  // TWO LOCKS over the shoulders, falling in front of the body: strands
  // rather than curtains, so the pale middle reads as a body with hair
  // over it rather than as a bib.
  ctx.fillStyle = SIREN.hair;
  for (const side of [-1, 1]) {
    ctx.beginPath();
    ctx.moveTo(hx + side * 4.2, hy + 0.5);
    ctx.quadraticCurveTo(16 + side * 6.2, hy + 8, 16 + side * 4.6, SURFACE - 2);
    ctx.lineTo(16 + side * 3.2, SURFACE - 2);
    ctx.quadraticCurveTo(16 + side * 4.4, hy + 8, hx + side * 2.6, hy + 1.5);
    ctx.closePath();
    ctx.fill();
  }

  // THE HEAD, small: at this size a big one turns the whole sprite into a
  // face, and what has to read first is the shape in the water.
  ctx.fillStyle = SIREN.skinLit;
  ctx.beginPath();
  ctx.arc(hx, hy, 5, 0, Math.PI * 2);
  ctx.fill();

  if (back) {
    // From behind there is nothing but hair: the head goes under it.
    ctx.fillStyle = SIREN.hair;
    ctx.beginPath();
    ctx.ellipse(hx, hy - 0.5, 4.8, 5.2, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = SIREN.hairLit;
    ctx.beginPath();
    ctx.ellipse(hx, hy - 2.6, 2.8, 1.2, 0, 0, Math.PI * 2);
    ctx.fill();
    return;
  }

  // THE FRINGE, over the brow: the top third of the head, so the hair
  // reads as hair rather than as a hood and the face is left under it.
  ctx.fillStyle = SIREN.hair;
  ctx.beginPath();
  ctx.moveTo(hx - 5, hy - 1.6);
  ctx.quadraticCurveTo(hx - 5.4, hy - 6, hx, hy - 5.4);
  ctx.quadraticCurveTo(hx + 5.4, hy - 6, hx + 5, hy - 1.6);
  ctx.quadraticCurveTo(hx + 2.6, hy - 4, hx, hy - 3.6);
  ctx.quadraticCurveTo(hx - 2.6, hy - 4, hx - 5, hy - 1.6);
  ctx.closePath();
  ctx.fill();
  ctx.fillStyle = SIREN.hairLit;
  ctx.beginPath(); // the light along the crown
  ctx.ellipse(hx, hy - 3.6, 2.6, 1.1, 0, 0, Math.PI * 2);
  ctx.fill();

  // THE FACE: eyes where every other creature in this file has them, and
  // a mouth that is open, because a siren is singing whenever you can see
  // it. Both are solid rather than lines — they have to survive being two
  // pixels across.
  eyes(hx, hy - 0.8, "#17484f");
  const mx = hx + s * 1.3, my = hy + 2.6;
  ctx.fillStyle = "#3a1b28";
  ctx.beginPath();
  ctx.ellipse(mx, my, 1.1, 1.4, 0, 0, Math.PI * 2);
  ctx.fill();

  // THE SURFACE: two rings where the body goes in, over the top of it, so
  // the waist is lost in them. They are the part that says "floating"
  // without leaning on the tile underneath being water — an admin can
  // summon one anywhere, and a sprite sheet is looked at on a white page.
  ctx.strokeStyle = SIREN.ripple;
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.ellipse(16, SURFACE + 1.5, 10, 2.8, 0, 0, Math.PI * 2);
  ctx.stroke();
  ctx.beginPath();
  ctx.ellipse(16, SURFACE, 6, 1.8, 0, 0, Math.PI * 2);
  ctx.stroke();

  // AND THE SONG going out of the mouth: two arcs on the side it faces,
  // or spread below the chin when it is looking straight at you. A siren
  // is singing whenever it is on the screen and its whole attack is the
  // song, so a creature whose one dangerous thing was invisible until it
  // landed would be a creature nobody could learn. The attack effect says
  // "it just sang at you"; this says "that is a thing that sings".
  ctx.strokeStyle = SIREN.song;
  ctx.lineWidth = 1;
  for (let i = 1; i <= 2; i++) {
    const r = 2.4 + i * 2.6;
    ctx.beginPath();
    if (s !== 0) ctx.arc(mx, my, r, s > 0 ? -0.75 : Math.PI - 0.75, s > 0 ? 0.75 : Math.PI + 0.75);
    else ctx.arc(mx, my + 0.5, r, 0.6, Math.PI - 0.6);
    ctx.stroke();
  }
}

// The orc and the cyclops, redrawn on 2026-09-24: four drawings were
// proposed ("propose improved orc and cyclops graphics") and these two
// chosen ("i want orc B and cyclops A"): the helmed orc warrior and the
// cyclops brute. They are functions of their own rather than case bodies
// because each is longer than the rest of a case and draws in four
// facings.

type EyesFn = (cx: number, cy: number, col?: string) => void;

/**
 * The bandit (2026-10-01: "implement a bandit monster, put them in dunland
 * and on the border near the north-south road and at the ford of isen"): a
 * man of the Dunland hills in a hooded cloak of mottled green-brown over a
 * belted leather jerkin, a dark scarf drawn up over his mouth and nose, so
 * that all of the face under the hood is a band of shadow and two eyes. He
 * is a man's size and shape, but no player wears a hood, Grubb is half a
 * head shorter with his curls out, and Elrond's robe is long and blue to the
 * ground: the hood's point and the ragged hem of the cloak about his knees
 * are what read as an outlaw at a glance. The cloak hangs behind him from
 * the front and covers him from behind, the hood's point falls back from
 * the crown, and his sword is in the orc's grip (see MONSTER_GRIP).
 */
function drawBandit(ctx: Ctx, dir: number, legOff: number, H: number, eyes: EyesFn) {
  const side = dir === 1 ? 1 : dir === 3 ? -1 : 0;
  const front = dir === 2;
  const back = dir === 0;
  const CLOAK = "#4a5236", CLOAK_D = "#363c27", CLOAK_L = "#5f6845";
  const JERKIN = "#6b4a2c", JERKIN_D = "#4f3520";
  const SKIN = "#c9a07a", SCARF = "#5a2a24", SHADOW = "#1d1a14";
  const swing = legOff / 2;

  // The cloak, hanging from the shoulders to the knees with a ragged hem.
  const cloak = () => {
    ctx.fillStyle = CLOAK;
    ctx.beginPath();
    ctx.moveTo(8, H - 31);
    ctx.lineTo(24, H - 31);
    ctx.lineTo(27, H - 9);
    for (let x = 27; x > 5; x -= 3) {
      ctx.lineTo(x - 1.5, H - 7 - ((x / 3) & 1) * 2);
      ctx.lineTo(x - 3, H - 9);
    }
    ctx.lineTo(5, H - 9);
    ctx.closePath();
    ctx.fill();
    // Its folds, and the light along one edge.
    ctx.fillStyle = CLOAK_D;
    for (const x of [10, 15, 20]) ctx.fillRect(x + side, H - 27, 1, 17);
    ctx.fillStyle = CLOAK_L;
    ctx.fillRect(side >= 0 ? 23 : 8, H - 30, 1, 20);
  };
  if (!back) cloak();

  // Legs: dark breeches and boots.
  ctx.fillStyle = "#3a3128";
  ctx.fillRect(10, H - 13 + legOff, 5, 9);
  ctx.fillRect(17, H - 13 - legOff, 5, 9);
  ctx.fillStyle = "#231c15";
  ctx.fillRect(9 + side, H - 5 + legOff, 6, 5);
  ctx.fillRect(17 + side, H - 5 - legOff, 6, 5);

  if (!back) {
    // The jerkin, laced up the front, belted, with a purse on the belt.
    ctx.fillStyle = JERKIN;
    ctx.fillRect(10, H - 29, 12, 17);
    ctx.fillStyle = JERKIN_D;
    if (front) for (let y = H - 27; y < H - 16; y += 3) ctx.fillRect(15.5, y, 1, 2);
    else ctx.fillRect(side > 0 ? 20 : 11, H - 29, 1, 17);
    ctx.fillStyle = "#2a1f16";
    ctx.fillRect(10, H - 16, 12, 2);
    ctx.fillStyle = "#8a6a3a";
    ctx.fillRect(front ? 18 : 16 + side * 3, H - 15, 3, 3);
  }

  // Arms in the cloak's sleeves, with bare hands.
  const arm = (x: number, dy: number) => {
    ctx.fillStyle = back ? CLOAK_D : CLOAK;
    ctx.fillRect(x, H - 29 + dy, 4, 12);
    ctx.fillStyle = SKIN;
    ctx.fillRect(x, H - 17 + dy, 4, 3);
  };
  arm(5, -swing);
  arm(23, swing);

  if (back) cloak();

  // The hood: round over the head, its point falling back from the crown,
  // and a fold of it over the shoulders.
  const hx = 16 + side * 1.5;
  ctx.fillStyle = CLOAK_D;
  ctx.beginPath();
  ctx.ellipse(16, H - 30, 9, 3, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = CLOAK;
  ctx.beginPath();
  ctx.arc(hx, H - 36, 6.5, 0, Math.PI * 2);
  ctx.fill();
  ctx.beginPath();
  if (side) {
    ctx.moveTo(hx - side * 3, H - 42);
    ctx.lineTo(hx - side * 10, H - 37);
    ctx.lineTo(hx - side * 5, H - 33);
  } else {
    ctx.moveTo(hx - 3.5, H - 41);
    ctx.lineTo(hx, H - 45);
    ctx.lineTo(hx + 3.5, H - 41);
  }
  ctx.closePath();
  ctx.fill();
  ctx.fillStyle = CLOAK_L;
  ctx.fillRect(hx - 4, H - 42, 8, 1);
  if (!back) {
    // The face in the hood's shadow, the scarf over its lower half, and the
    // eyes above it, which are all of him that shows.
    const fx = front ? hx : hx + side * 2.5;
    const fw = front ? 8 : 5;
    ctx.fillStyle = SHADOW;
    ctx.fillRect(fx - fw / 2, H - 39, fw, 7);
    ctx.fillStyle = SKIN;
    ctx.fillRect(fx - fw / 2 + 0.5, H - 38, fw - 1, 2.5);
    ctx.fillStyle = SCARF;
    ctx.fillRect(fx - fw / 2, H - 35, fw, 3.5);
    eyes(front ? hx : hx + side * 0.5, H - 38, "#2a1a0a");
  }
}

// ---------------------------------------------------------------------------
// The content proposal's thirteen kinds (2026-10-01: "Implement the entire
// proposal in the background"). Each is drawn in the same coordinate space
// as everything else — the feet on y=H, the body on x=16 — and the big ones
// are given their room in BIG_OUTFITS.
// ---------------------------------------------------------------------------

/**
 * The crebain: a crow of Dunland, black with a blue sheen on the wing, a
 * heavy beak and a wedge of a tail. It is drawn on the wing, a little off
 * the ground over its own shadow, and the walk frame is its wingbeat the
 * way it is the bat's. In profile it is a long body with the head and beak
 * out in front and one wing raised over its back; head-on and from behind
 * it is both wings spread.
 */
function drawCrebain(ctx: Ctx, dir: number, legOff: number, H: number, eyes: EyesFn) {
  const side = dir === 1 ? 1 : dir === 3 ? -1 : 0;
  const flap = legOff;
  const BLACK = "#1d1b22", WING = "#2b2833", SHEEN = "#3d4a63", BEAK = "#4a4038";
  const cy = H - 18;
  ctx.fillStyle = "rgba(0,0,0,0.22)"; // its shadow on the ground under it
  ctx.beginPath();
  ctx.ellipse(16, H - 2, 6, 1.5, 0, 0, Math.PI * 2);
  ctx.fill();
  if (side) {
    // The far wing, half hidden, then the tail, the body, the near wing.
    ctx.fillStyle = WING;
    ctx.beginPath();
    ctx.moveTo(16 + side * 2, cy - 2);
    ctx.lineTo(16 - side * 2, cy - 10 + flap * 2);
    ctx.lineTo(16 - side * 8, cy - 6 + flap);
    ctx.closePath();
    ctx.fill();
    ctx.fillStyle = BLACK;
    ctx.beginPath(); // the tail, a wedge out behind
    ctx.moveTo(16 - side * 5, cy - 1);
    ctx.lineTo(16 - side * 13, cy - 3);
    ctx.lineTo(16 - side * 13, cy + 3);
    ctx.lineTo(16 - side * 5, cy + 2);
    ctx.closePath();
    ctx.fill();
    ctx.beginPath(); // the body
    ctx.ellipse(16, cy, 7, 4, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.beginPath(); // the head
    ctx.arc(16 + side * 6, cy - 3, 3.2, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = BEAK; // the beak, heavy and down-curved
    ctx.beginPath();
    ctx.moveTo(16 + side * 8.5, cy - 4.5);
    ctx.lineTo(16 + side * 13, cy - 2);
    ctx.lineTo(16 + side * 8.5, cy - 1.5);
    ctx.closePath();
    ctx.fill();
    ctx.fillStyle = WING; // the near wing, raised over the back
    ctx.beginPath();
    ctx.moveTo(16 + side * 3, cy - 1);
    ctx.lineTo(16 - side * 1, cy - 12 - flap * 2);
    ctx.lineTo(16 - side * 6, cy - 13 - flap * 2);
    ctx.lineTo(16 - side * 9, cy - 4 - flap);
    ctx.lineTo(16 - side * 3, cy + 1);
    ctx.closePath();
    ctx.fill();
    ctx.fillStyle = SHEEN;
    ctx.fillRect(16 - side * 4 - 1, cy - 9 - flap * 2, 2, 5);
    ctx.fillStyle = "#e8c040"; // the one eye that shows
    ctx.fillRect(16 + side * 6.5 - 0.5, cy - 4.5, 1.5, 1.5);
    return;
  }
  // Both wings spread, the tips ragged with the long flight feathers.
  for (const s of [-1, 1]) {
    ctx.fillStyle = WING;
    ctx.beginPath();
    ctx.moveTo(16 + s * 3, cy - 3);
    ctx.quadraticCurveTo(16 + s * 9, cy - 9 - flap * 2, 16 + s * 15, cy - 6 - flap * 3);
    for (let i = 0; i < 4; i++) ctx.lineTo(16 + s * (14 - i * 2.5), cy - 2 - flap * (2 - i * 0.5) + (i & 1 ? 2 : 0));
    ctx.lineTo(16 + s * 3, cy + 2);
    ctx.closePath();
    ctx.fill();
    ctx.fillStyle = SHEEN;
    ctx.fillRect(16 + s * 8 - 1, cy - 5 - flap * 1.5, 2, 3);
  }
  ctx.fillStyle = BLACK;
  if (dir === 0) {
    ctx.beginPath(); // the tail fanned under the body
    ctx.moveTo(14, cy + 3);
    ctx.lineTo(12, cy + 9);
    ctx.lineTo(20, cy + 9);
    ctx.lineTo(18, cy + 3);
    ctx.closePath();
    ctx.fill();
  }
  ctx.beginPath();
  ctx.ellipse(16, cy, 4.5, 6, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.beginPath();
  ctx.arc(16, cy - 6, 3.4, 0, Math.PI * 2);
  ctx.fill();
  if (dir === 2) {
    ctx.fillStyle = BEAK;
    ctx.beginPath();
    ctx.moveTo(14.8, cy - 5.5);
    ctx.lineTo(17.2, cy - 5.5);
    ctx.lineTo(16, cy - 1);
    ctx.closePath();
    ctx.fill();
    ctx.fillStyle = "#e8c040";
    ctx.fillRect(13.5, cy - 7.5, 1.5, 1.5);
    ctx.fillRect(17, cy - 7.5, 1.5, 1.5);
  }
  void eyes;
}

/**
 * The orc berserker: an orc gone into the frenzy, stripped to the waist
 * and built like a door, hunched forward with its head low between its
 * shoulders and its jaw open on a roar. No helm: a crest of black hair
 * stands up the middle of a shaved scalp and falls down its back in a mane.
 * Red war paint, a band across the eyes and claw marks down the chest, a
 * baldric across the bare chest, spiked bracers, and a hide kilt under a
 * belt with a skull on it (2026-10-18: "create a new orc berserker
 * graphic"; before then it was a green block with two stripes on it). The
 * orc's green and red eye, so it reads as one of them, but the crest, the
 * bare chest and the red are what nothing else in the family has. Its axe
 * is in its own grip (see MONSTER_GRIP).
 */
function drawOrcBerserker(ctx: Ctx, dir: number, legOff: number, H: number, eyes: EyesFn) {
  const side = dir === 1 ? 1 : dir === 3 ? -1 : 0;
  const back = dir === 0;
  const SKIN = "#4f7d2c", LIT = "#6a9a3c", SHADE = "#3a5e20", DARK = "#24391a";
  const PAINT = "#c0281c", HAIR = "#141210", LEATHER = "#4a2e1a", IRON = "#9aa2aa";
  const swing = legOff / 2;

  // Legs in dark breeches, bound below the knee, feet in wrappings.
  ctx.fillStyle = "#2e241c";
  ctx.fillRect(9, H - 13 + legOff, 6, 10);
  ctx.fillRect(17, H - 13 - legOff, 6, 10);
  ctx.fillStyle = "#5a4630";
  ctx.fillRect(9, H - 7 + legOff, 6, 1);
  ctx.fillRect(17, H - 7 - legOff, 6, 1);
  ctx.fillStyle = "#1a140f";
  ctx.fillRect(8 + side, H - 4 + legOff, 7, 4);
  ctx.fillRect(17 + side, H - 4 - legOff, 7, 4);

  // The arm on the far side, behind the body when seen side on.
  const arm = (x: number, dy: number, far: boolean) => {
    ctx.fillStyle = far ? SHADE : SKIN;
    ctx.fillRect(x, H - 30 + dy, 5, 14);
    ctx.fillStyle = far ? DARK : LIT; // the lit edge of the upper arm
    ctx.fillRect(x + (x < 16 ? 0 : 4), H - 30 + dy, 1, 6);
    ctx.fillStyle = LEATHER; // a spiked bracer
    ctx.fillRect(x, H - 22 + dy, 5, 4);
    ctx.fillStyle = IRON;
    ctx.fillRect(x + (x < 16 ? -1 : 5), H - 21 + dy, 1, 1);
    ctx.fillRect(x + (x < 16 ? -1 : 5), H - 19 + dy, 1, 1);
    ctx.fillStyle = far ? DARK : SHADE; // the fist
    ctx.fillRect(x, H - 18 + dy, 5, 3);
  };
  if (side) arm(side > 0 ? 4 : 23, -swing, true);

  // The kilt of hide, ragged at the hem, under a broad belt.
  ctx.fillStyle = "#6b4a2c";
  ctx.beginPath();
  ctx.moveTo(8, H - 16);
  ctx.lineTo(24, H - 16);
  for (let x = 24; x > 8; x -= 4) {
    ctx.lineTo(x - 1, H - 9);
    ctx.lineTo(x - 3, H - 11);
  }
  ctx.lineTo(8, H - 9);
  ctx.closePath();
  ctx.fill();
  ctx.fillStyle = "#4a3220";
  for (const x of [11, 15, 19]) ctx.fillRect(x + side, H - 15, 1, 4);

  // The torso: a wedge, broad across the shoulders and narrow at the belt,
  // narrower again side on.
  const sw = side ? 9 : 11, ww = 7;
  const tx = 16 + side * 1.5;
  ctx.fillStyle = SKIN;
  ctx.beginPath();
  ctx.moveTo(tx - sw, H - 31);
  ctx.lineTo(tx + sw, H - 31);
  ctx.lineTo(tx + ww, H - 16);
  ctx.lineTo(tx - ww, H - 16);
  ctx.closePath();
  ctx.fill();
  // Shoulders like boulders.
  for (const s of side ? [side] : [-1, 1]) {
    ctx.beginPath();
    ctx.arc(tx + s * (sw - 2), H - 29, 4, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.fillStyle = LIT;
  ctx.fillRect(tx - sw + 1, H - 32, sw * 2 - 2, 1);
  if (back) {
    // The spine and the blades of the back.
    ctx.fillStyle = SHADE;
    ctx.fillRect(15.5, H - 29, 1, 12);
    ctx.fillRect(10, H - 24, 4, 1);
    ctx.fillRect(18, H - 24, 4, 1);
  } else if (side) {
    ctx.fillStyle = SHADE; // the chest's underside and the belly
    ctx.fillRect(tx + side * 2 - 3, H - 24, 6, 1);
  } else {
    // A chest like two slabs and a belly ridged with muscle.
    ctx.fillStyle = SHADE;
    ctx.fillRect(7, H - 24, 18, 1);
    ctx.fillRect(15.5, H - 28, 1, 11);
    ctx.fillRect(12, H - 21, 3, 1);
    ctx.fillRect(17, H - 21, 3, 1);
    ctx.fillRect(12, H - 18.5, 3, 1);
    ctx.fillRect(17, H - 18.5, 3, 1);
  }
  // Claw marks of war paint down the chest, or down the back.
  ctx.fillStyle = PAINT;
  for (let i = 0; i < 3; i++) {
    const x0 = (side ? tx - 3 : 9) + i * (side ? 2.5 : 3);
    ctx.save();
    ctx.translate(x0, H - 30);
    ctx.rotate(0.35);
    ctx.fillRect(0, 0, 1.4, side ? 7 : 9);
    ctx.restore();
  }
  // A baldric from the shoulder to the hip, studded with iron.
  ctx.strokeStyle = LEATHER;
  ctx.lineWidth = 2.5;
  ctx.beginPath();
  if (side) {
    ctx.moveTo(tx - 2, H - 31);
    ctx.lineTo(tx + 1, H - 16);
  } else {
    ctx.moveTo(back ? 23 : 9, H - 31);
    ctx.lineTo(back ? 10 : 22, H - 16);
  }
  ctx.stroke();
  // The belt, and a skull for its buckle.
  ctx.fillStyle = "#24180f";
  ctx.fillRect(8, H - 17, 16, 3);
  if (!back) {
    const bx = side ? tx + side * 4 : 16;
    ctx.fillStyle = "#e4dcc4";
    ctx.fillRect(bx - 1.5, H - 18, 3, 3);
    ctx.fillStyle = "#24180f";
    ctx.fillRect(bx - 1, H - 17, 0.8, 0.8);
    ctx.fillRect(bx + 0.2, H - 17, 0.8, 0.8);
  }

  // The near arms, swinging.
  if (side) arm(side > 0 ? 19 : 8, swing, false);
  else {
    arm(2, -swing, false);
    arm(25, swing, false);
  }

  // The head, low between the shoulders and thrust forward.
  const hx = 16 + side * 3.5;
  const hy = H - 35;
  if (!back) {
    // Ears out to the sides, with an iron ring in them.
    for (const s of side ? [-side] : [-1, 1]) {
      ctx.fillStyle = SKIN;
      ctx.beginPath();
      ctx.moveTo(hx + s * 5, hy - 2);
      ctx.lineTo(hx + s * 9, hy - 5);
      ctx.lineTo(hx + s * 5.5, hy + 1);
      ctx.closePath();
      ctx.fill();
      ctx.fillStyle = IRON;
      ctx.fillRect(hx + s * 6.5 - 0.5, hy + 0.5, 1, 1.5);
    }
  }
  ctx.fillStyle = SKIN;
  ctx.beginPath();
  ctx.arc(hx, hy, 6.5, 0, Math.PI * 2);
  ctx.fill();
  if (!back) {
    // A heavy jaw hanging open on a roar, the tusks standing up out of it.
    const jx = side ? hx + side * 2 - 4 : hx - 5;
    const jw = side ? 8 : 10;
    ctx.fillStyle = SKIN;
    ctx.fillRect(jx, hy + 1, jw, 5);
    ctx.fillStyle = "#3a0d08";
    ctx.fillRect(side ? hx + side * 3 - 2 : hx - 3, hy + 2, side ? 4 : 6, 3);
    ctx.fillStyle = "#efe8d2";
    if (side) ctx.fillRect(hx + side * 4 - 1, hy + 1, 1.5, 3);
    else {
      ctx.fillRect(hx - 4, hy + 0.5, 1.5, 3.5);
      ctx.fillRect(hx + 2.5, hy + 0.5, 1.5, 3.5);
    }
    // The war paint: a red band across the eyes, and the eyes in it.
    ctx.fillStyle = PAINT;
    if (side) ctx.fillRect(hx + side * 1 - 3.5, hy - 3, 7.5, 3);
    else ctx.fillRect(hx - 6, hy - 3, 12, 3);
    ctx.fillStyle = DARK; // a heavy brow over them
    if (side) ctx.fillRect(hx + side * 2 - 2.5, hy - 4, 6, 1);
    else ctx.fillRect(hx - 5, hy - 4, 10, 1);
    eyes(hx, hy - 2.5, "#ffe14a");
  }
  // The crest: a ridge of black hair up the middle of a shaved scalp, in
  // spikes, falling down the back in a mane. Seen from the front it is a
  // narrow brush standing straight up; from the side, a saw blade.
  ctx.fillStyle = HAIR;
  if (side) {
    ctx.beginPath();
    ctx.moveTo(hx + side * 3, hy - 5);
    for (let i = 0; i < 4; i++) {
      const x = hx + side * (3 - i * 3);
      ctx.lineTo(x - side * 0.5, hy - 14 + i * 1.5);
      ctx.lineTo(x - side * 2, hy - 6 + i * 0.5);
    }
    ctx.lineTo(hx - side * 8, hy + 6); // the mane down the back of the neck
    ctx.lineTo(hx - side * 4, hy + 2);
    ctx.lineTo(hx - side * 5, hy - 3);
    ctx.closePath();
    ctx.fill();
  } else {
    ctx.beginPath();
    ctx.moveTo(hx - 2.5, hy - 5);
    ctx.lineTo(hx - 4, hy - 12);
    ctx.lineTo(hx - 1.5, hy - 10);
    ctx.lineTo(hx, hy - 16);
    ctx.lineTo(hx + 1.5, hy - 10);
    ctx.lineTo(hx + 4, hy - 12);
    ctx.lineTo(hx + 2.5, hy - 5);
    ctx.closePath();
    ctx.fill();
    if (back) {
      // The mane, down to the shoulder blades in a tail.
      ctx.beginPath();
      ctx.moveTo(hx - 2, hy - 6);
      ctx.lineTo(hx + 2, hy - 6);
      ctx.lineTo(hx + 2.5, hy + 8);
      ctx.lineTo(hx, hy + 11);
      ctx.lineTo(hx - 2.5, hy + 8);
      ctx.closePath();
      ctx.fill();
    }
  }
}

/**
 * The snaga, a slave-orc of the mountains: an orc a size down, stooped and
 * thin in the arm, bare-headed but for a scruffy topknot, with big bat ears
 * out either side, in a ragged leather jerkin over a rope belt. Grey green
 * where the warrior is a healthy green, but the orc's red eye and a tusk
 * either side, so it reads as one of the family (2026-10-13: it had yellow
 * eyes and a dome of black hair that looked like a hat, and Matt said
 * "change the snaga" to the topknot, red eyes and tusks). Its club is in the
 * orc's grip, drawn a shade under a player's (see MONSTER_GRIP).
 */
function drawSnaga(ctx: Ctx, dir: number, legOff: number, H: number, eyes: EyesFn) {
  const side = dir === 1 ? 1 : dir === 3 ? -1 : 0;
  const back = dir === 0;
  const SKIN = "#6f8058", SHADE = "#55633f", DARK = "#2a3020";
  const RAG = "#5e4a32", RAG_D = "#41321f";
  const swing = legOff / 2;
  // Bandy legs, bare to the knee, and wrapped feet.
  ctx.fillStyle = SHADE;
  ctx.fillRect(11, H - 12 + legOff, 4, 9);
  ctx.fillRect(17, H - 12 - legOff, 4, 9);
  ctx.fillStyle = "#3a2e22";
  ctx.fillRect(10 + side, H - 4 + legOff, 5, 4);
  ctx.fillRect(17 + side, H - 4 - legOff, 5, 4);
  // The ragged jerkin, its hem torn into points.
  ctx.fillStyle = RAG;
  ctx.beginPath();
  ctx.moveTo(9, H - 27);
  ctx.lineTo(23, H - 27);
  ctx.lineTo(23, H - 13);
  for (let x = 23; x > 9; x -= 3.5) {
    ctx.lineTo(x - 1.75, H - 10);
    ctx.lineTo(x - 3.5, H - 13);
  }
  ctx.closePath();
  ctx.fill();
  ctx.fillStyle = RAG_D;
  ctx.fillRect(12 + side, H - 25, 1, 9);
  ctx.fillRect(18 + side, H - 24, 1, 8);
  ctx.fillStyle = "#8a7650"; // the rope belt
  ctx.fillRect(9, H - 16, 14, 1.5);
  // Thin arms, bare.
  ctx.fillStyle = SKIN;
  ctx.fillRect(5, H - 26 - swing, 4, 11);
  ctx.fillRect(23, H - 26 + swing, 4, 11);
  ctx.fillStyle = SHADE;
  ctx.fillRect(5, H - 17 - swing, 4, 2);
  ctx.fillRect(23, H - 17 + swing, 4, 2);
  // The head, thrust forward on a short neck, and the ears.
  const hx = 16 + side * 2.5;
  const hy = H - 32;
  ctx.fillStyle = SKIN;
  for (const s of side ? [-side] : [-1, 1]) {
    ctx.beginPath();
    ctx.moveTo(hx + s * 4, hy - 2);
    ctx.lineTo(hx + s * 10, hy - 6);
    ctx.lineTo(hx + s * 5, hy + 1);
    ctx.closePath();
    ctx.fill();
  }
  ctx.beginPath();
  ctx.arc(hx, hy, 5.5, 0, Math.PI * 2);
  ctx.fill();
  // A scruffy topknot on the crown (the berserker had one until its crest), its
  // strands ragged and of different lengths; the scalp round it is bare.
  const kx = hx - side;
  ctx.fillStyle = "#1e1c18";
  ctx.fillRect(kx - 1.5, hy - 7, 3, 2.5);
  ctx.fillRect(kx - 2, hy - 9, 1.5, 2.5);
  ctx.fillRect(kx - 0.5, hy - 10.5, 1.5, 4);
  ctx.fillRect(kx + 1, hy - 8.5, 1.5, 2);
  if (back) return;
  // A mean little mouth, and a tusk up from either corner of it.
  ctx.fillStyle = DARK;
  ctx.fillRect(side ? hx + side * 2 - 1.5 : hx - 2, hy + 3, 3, 1);
  ctx.fillStyle = "#e6dfc6";
  if (side) ctx.fillRect(hx + side * 3 - 1, hy + 0.5, 2, 3);
  else {
    ctx.fillRect(hx - 3.5, hy + 0.5, 2, 3);
    ctx.fillRect(hx + 1.5, hy + 0.5, 2, 3);
  }
  eyes(hx, hy - 1, "#f22");
}

/** The warg's coat: near black, brindled brown, with a red eye. */
const WOLF_WARG: WolfPalette = { fur: "#4a3f35", pale: "#7a6c5c", dark: "#251f1a", nose: "#0f0d0b", eye: "#ff4a22" };

/** How much bigger a warg is drawn than a wolf. */
const WARG_SCALE = 1.25;

/**
 * The warg: the wolf, a quarter as big again about its feet, in a coat
 * gone near black with a brindle of brown and an eye like an ember. It is
 * the same animal as the wolf, the way the dragon lord is the dragon, and
 * the bigger box in BIG_OUTFITS is the wolf's scaled the same way.
 */
function drawWarg(ctx: Ctx, dir: number, frame: number, legOff: number, H: number, eyes: EyesFn) {
  drawBigWolf(ctx, WOLF_WARG, WARG_SCALE, dir, frame, legOff, H, eyes);
}

/** A werewolf's coat: black over black, with eyes like coals. */
const WOLF_WERE: WolfPalette = { fur: "#26221f", pale: "#4a433d", dark: "#0e0c0b", nose: "#050404", eye: "#ff2a10" };

/** How much bigger a werewolf is drawn than a wolf. */
const WEREWOLF_SCALE = 1.45;

/**
 * A werewolf of Tol-in-Gaurhoth (2026-11-01: Sauron's pack): the warg
 * bigger again and black, its hackles up, a spirit's red eyes in it.
 */
function drawWerewolf(ctx: Ctx, dir: number, frame: number, legOff: number, H: number, eyes: EyesFn) {
  drawBigWolf(ctx, WOLF_WERE, WEREWOLF_SCALE, dir, frame, legOff, H, eyes);
}

/** The wolf drawn bigger, in another coat, with its hackles up: the warg and the werewolf. */
function drawBigWolf(ctx: Ctx, coat: WolfPalette, scale: number, dir: number, frame: number, legOff: number, H: number, eyes: EyesFn) {
  ctx.save();
  ctx.translate(16, H);
  ctx.scale(scale, scale);
  ctx.translate(-16, -H);
  drawWolf(ctx, coat, dir, frame, legOff, H, eyes);
  // The hackles, a ridge of stiff black fur standing up along the neck.
  const s = dir === 1 ? 1 : dir === 3 ? -1 : 0;
  ctx.fillStyle = coat.dark;
  if (s) {
    for (let i = 0; i < 4; i++) {
      const x = 16 + s * (4 + i * 2.2);
      ctx.beginPath();
      ctx.moveTo(x - 1.2, H - 22 - i * 1.2);
      ctx.lineTo(x + s * 0.5, H - 26 - i * 1.4);
      ctx.lineTo(x + 1.2, H - 22 - i * 1.2);
      ctx.closePath();
      ctx.fill();
    }
  } else if (dir === 0) {
    for (const x of [13, 16, 19]) {
      ctx.beginPath();
      ctx.moveTo(x - 1.5, H - 26);
      ctx.lineTo(x, H - 30);
      ctx.lineTo(x + 1.5, H - 26);
      ctx.closePath();
      ctx.fill();
    }
  }
  ctx.restore();
}

/** What a man of the south or the east is wearing: see drawMan. */
interface ManKit {
  skin: string;
  coat: string;
  coatDark: string;
  trim: string;
  legs: string;
  boots: string;
  belt: string;
  /** What is on his head: a wound cloth, his own wild hair, a sea-rag or a helm. */
  head: "turban" | "wild" | "bandana" | "helm";
  headCol: string;
  headCol2: string;
  /** Gold in the ears, or nothing. */
  earring?: string;
  /** Scale in rows down the coat, edged in the trim. */
  scale?: boolean;
  /** A coat that falls to the knee rather than stopping at the belt. */
  long?: boolean;
  /** A beard, for the hill-men. */
  beard?: string;
}

/**
 * The Southron archer: a man of Harad in scarlet to the knee with a gold
 * hem, a scarlet cloth wound round his head, gold in his ears and skin the
 * sun of the south has darkened (the proposal: "A Southron archer in
 * scarlet, gold in his ears").
 */
const SOUTHRON_KIT: ManKit = {
  skin: "#8a5a3a", coat: "#b0281e", coatDark: "#7a1a14", trim: "#e0b040", legs: "#4a2a1c", boots: "#2a1a10",
  belt: "#e0b040", head: "turban", headCol: "#b0281e", headCol2: "#e0b040", earring: "#f0c848", long: true,
};

/**
 * The Dunlending: a wild hill-man of Dunland in a jerkin of hide with the
 * fur still on at the shoulders, his own dark hair loose and a beard to
 * the chest, no shoes worth the name.
 */
const DUNLENDING_KIT: ManKit = {
  skin: "#d0a882", coat: "#6b5034", coatDark: "#4a3622", trim: "#8a7a5c", legs: "#4a3a2a", boots: "#3a2c1e",
  belt: "#2a1f16", head: "wild", headCol: "#3a2a1c", headCol2: "#2a1e14", beard: "#3a2a1c",
};

/**
 * The corsair of Umbar: a sea-robber in a coat of black-blue, a red sash
 * and a red rag knotted round his head, a gold ring in the one ear and
 * boots to the knee for the deck.
 */
const CORSAIR_KIT: ManKit = {
  skin: "#b98a62", coat: "#24324a", coatDark: "#161f30", trim: "#a82020", legs: "#3a3a40", boots: "#1c1612",
  belt: "#a82020", head: "bandana", headCol: "#9a1e1e", headCol2: "#6a1414", earring: "#f0c848",
};

/**
 * The Easterling: a man of Rhûn in lacquered scale, red rows edged in
 * gold, under a tall black-lacquered helm with a gold rim and a plume-less
 * spike (the proposal: "An Easterling in lacquered scale").
 */
const EASTERLING_KIT: ManKit = {
  skin: "#c9a070", coat: "#8a2a1e", coatDark: "#5a1a12", trim: "#d8b040", legs: "#2e2a26", boots: "#1c1814",
  belt: "#2a1f16", head: "helm", headCol: "#2a2220", headCol2: "#d8b040", scale: true, long: true,
};

/**
 * 11. The Variags of Khand (2026-10-16): dark men of the south-east in
 * dark scale over red, bearded, a horse-tail on the helm; the
 * horse-archer in tan leather with his own black hair, gold in his ears;
 * their chieftain in black scale and gold (drawn a head taller); and the
 * merchant of Harad at the oasis, in a long pale robe striped in blue
 * under a white head-cloth.
 */
const VARIAG_AXEMAN_KIT: ManKit = {
  skin: "#7a5236", coat: "#3a2c26", coatDark: "#1e1614", trim: "#b0281e", legs: "#5a2a1c", boots: "#2a1a10",
  belt: "#b0281e", head: "helm", headCol: "#4a3a2a", headCol2: "#b0281e", scale: true, long: true, beard: "#1a1410",
};
const VARIAG_ARCHER_KIT: ManKit = {
  skin: "#7a5236", coat: "#8a6a3a", coatDark: "#5a4422", trim: "#b0281e", legs: "#4a2a1c", boots: "#2a1a10",
  belt: "#b0281e", head: "wild", headCol: "#1a1410", headCol2: "#1a1410", earring: "#f0c848", beard: "#1a1410",
};
const VARIAG_CHIEF_KIT: ManKit = {
  skin: "#6e4a30", coat: "#1c1c20", coatDark: "#0e0e10", trim: "#d8b040", legs: "#3a1a14", boots: "#140e0a",
  belt: "#d8b040", head: "helm", headCol: "#1c1c20", headCol2: "#d8b040", scale: true, long: true, beard: "#141010", earring: "#f0c848",
};
const HARAD_MERCHANT_KIT: ManKit = {
  skin: "#8a5a3a", coat: "#d8cfb8", coatDark: "#2a5a8a", trim: "#2a5a8a", legs: "#c8bca0", boots: "#6a4a2a",
  belt: "#2a5a8a", head: "turban", headCol: "#f0ece0", headCol2: "#2a5a8a", earring: "#f0c848", long: true, beard: "#1a1410",
};

/**
 * A man, for the four peoples of the south and east that came with the
 * content proposal: the bandit's frame (see drawBandit) in each one's own
 * kit, so they are plainly the same sort of thing as one another and as the
 * outlaw of Dunland, and plainly not each other. What tells them apart at a
 * glance is the head — the Southron's wound scarlet, the Dunlending's wild
 * hair and beard, the corsair's red rag, the Easterling's black helm — and
 * the coat. Their weapons are in the bandit's grip (see MONSTER_GRIP).
 */
function drawMan(ctx: Ctx, dir: number, legOff: number, H: number, eyes: EyesFn, k: ManKit) {
  const side = dir === 1 ? 1 : dir === 3 ? -1 : 0;
  const front = dir === 2;
  const back = dir === 0;
  const swing = legOff / 2;
  // Legs and boots.
  ctx.fillStyle = k.legs;
  ctx.fillRect(10, H - 13 + legOff, 5, 9);
  ctx.fillRect(17, H - 13 - legOff, 5, 9);
  ctx.fillStyle = k.boots;
  const bootH = k.head === "bandana" ? 8 : 5; // the corsair's go to the knee
  ctx.fillRect(9 + side, H - bootH + legOff, 6, bootH);
  ctx.fillRect(17 + side, H - bootH - legOff, 6, bootH);
  // The coat: to the belt, or to the knee with a split skirt.
  ctx.fillStyle = k.coat;
  ctx.fillRect(10, H - 29, 12, k.long ? 21 : 17);
  if (k.long) {
    ctx.fillStyle = k.coatDark;
    ctx.fillRect(15.5 + side * 2, H - 16, 1, 8);
    ctx.fillStyle = k.trim;
    ctx.fillRect(10, H - 9, 12, 1);
  }
  if (k.scale) {
    for (let r = 0; r < 5; r++) {
      const y = H - 27 + r * 3.5;
      ctx.fillStyle = k.coatDark;
      ctx.fillRect(10, y, 12, 1);
      ctx.fillStyle = k.trim;
      for (let x = 11 + (r & 1) * 2; x < 22; x += 4) ctx.fillRect(x, y - 1, 1, 1);
    }
  } else if (!back) {
    ctx.fillStyle = k.coatDark;
    if (front) ctx.fillRect(15.5, H - 29, 1, 13);
    else ctx.fillRect(side > 0 ? 20 : 11, H - 29, 1, 13);
  }
  if (k.head === "wild") {
    // The fur at the shoulders of the hide jerkin.
    ctx.fillStyle = "#8a7454";
    ctx.beginPath();
    ctx.ellipse(16, H - 29, 9, 2.5, 0, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.fillStyle = k.belt;
  ctx.fillRect(10, H - 16, 12, 2);
  if (k.head === "bandana" && !back) {
    // The sash's knotted end hanging at the hip.
    ctx.fillRect(front ? 18 : 16 - side * 4, H - 15, 2, 5);
  }
  // Arms in sleeves, with bare hands.
  const arm = (x: number, dy: number) => {
    ctx.fillStyle = back ? k.coatDark : k.coat;
    ctx.fillRect(x, H - 29 + dy, 4, 12);
    if (k.scale) {
      ctx.fillStyle = k.trim;
      ctx.fillRect(x, H - 19 + dy, 4, 1);
    }
    ctx.fillStyle = k.skin;
    ctx.fillRect(x, H - 17 + dy, 4, 3);
  };
  arm(6, -swing);
  arm(22, swing);
  // The head.
  const hx = 16 + side * 1.5;
  const hy = H - 35;
  ctx.fillStyle = k.skin;
  ctx.beginPath();
  ctx.arc(hx, hy, 6, 0, Math.PI * 2);
  ctx.fill();
  if (k.beard && !back) {
    ctx.fillStyle = k.beard;
    const bx = hx + side * 2;
    ctx.beginPath();
    ctx.moveTo(bx - 4, hy + 1);
    ctx.lineTo(bx + 4, hy + 1);
    ctx.lineTo(bx + 2.5, hy + 8);
    ctx.lineTo(bx, hy + 10);
    ctx.lineTo(bx - 2.5, hy + 8);
    ctx.closePath();
    ctx.fill();
  }
  switch (k.head) {
    case "turban": {
      // A cloth wound round the head, a band of gold through it, and the
      // tail of it hanging behind.
      ctx.fillStyle = k.headCol;
      ctx.beginPath();
      ctx.ellipse(hx, hy - 4, 7, 4.5, 0, Math.PI, Math.PI * 2);
      ctx.fill();
      ctx.fillRect(hx - 7, hy - 4, 14, 3);
      ctx.fillStyle = k.headCol2;
      ctx.fillRect(hx - 7, hy - 3, 14, 1);
      ctx.fillStyle = k.headCol;
      if (side) ctx.fillRect(hx - side * 7 - 1, hy - 3, 2, 8);
      else if (back) ctx.fillRect(hx - 1.5, hy - 2, 3, 9);
      break;
    }
    case "wild": {
      // His own hair, long and dark and uncombed.
      ctx.fillStyle = k.headCol;
      ctx.beginPath();
      ctx.arc(hx, hy - 1.5, 6.5, Math.PI, Math.PI * 2);
      ctx.fill();
      if (back) {
        ctx.beginPath();
        ctx.arc(hx, hy, 6.5, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.fillStyle = k.headCol2;
      for (const x of side ? [-side * 5] : [-6.5, 5]) ctx.fillRect(hx + x, hy - 2, 2, 8);
      if (back) ctx.fillRect(hx - 4, hy + 3, 8, 4);
      break;
    }
    case "bandana": {
      // A red rag over the crown, knotted behind with the ends hanging.
      ctx.fillStyle = "#2a1e16"; // dark hair under it
      ctx.beginPath();
      ctx.arc(hx, hy - 1, 6.2, Math.PI, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = k.headCol;
      ctx.beginPath();
      ctx.arc(hx, hy - 2, 6.2, Math.PI, Math.PI * 2);
      ctx.fill();
      ctx.fillRect(hx - 6.2, hy - 2.5, 12.4, 1.5);
      ctx.fillStyle = k.headCol2;
      const kx = side ? hx - side * 6 : back ? hx : -99;
      if (kx > -50) {
        ctx.fillRect(kx - 1, hy - 3, 2, 2);
        ctx.fillRect(kx - (side || 1) * 1 - 0.5, hy - 1, 1.5, 5);
      }
      break;
    }
    case "helm": {
      // A tall lacquered helm, rimmed in gold, with a spike and a neck
      // guard of lames behind.
      ctx.fillStyle = k.headCol;
      ctx.beginPath();
      ctx.moveTo(hx - 6.5, hy - 2);
      ctx.quadraticCurveTo(hx - 6, hy - 9, hx, hy - 10);
      ctx.quadraticCurveTo(hx + 6, hy - 9, hx + 6.5, hy - 2);
      ctx.closePath();
      ctx.fill();
      ctx.beginPath();
      ctx.moveTo(hx - 1.2, hy - 9.5);
      ctx.lineTo(hx, hy - 15);
      ctx.lineTo(hx + 1.2, hy - 9.5);
      ctx.closePath();
      ctx.fill();
      // The neck guard, behind: all of it from the back, its edge in
      // profile.
      if (back) ctx.fillRect(hx - 6.5, hy - 2, 13, 6);
      else if (side) ctx.fillRect(side > 0 ? hx - 6.5 : hx + 3.5, hy - 2, 3, 6);
      ctx.fillStyle = k.headCol2;
      ctx.fillRect(hx - 6.5, hy - 3, 13, 1);
      ctx.fillRect(hx - 0.5, hy - 13, 1, 3);
      break;
    }
  }
  if (back) return;
  if (k.earring) {
    ctx.fillStyle = k.earring;
    if (side) ctx.fillRect(hx - side * 2.5 - 0.5, hy + 1.5, 1.5, 2);
    else {
      ctx.fillRect(hx - 6.5, hy + 1, 1.5, 2);
      ctx.fillRect(hx + 5, hy + 1, 1.5, 2);
    }
  }
  eyes(front ? hx : hx + side * 0.5, hy - 1, "#2a1a0a");
}

/** An orc's colours and what it wears on its head: see drawOrcWarrior. */
interface OrcKit {
  skin: string; shade: string; dark: string;
  scale: string; scaleDark: string; scaleLight: string;
  iron: string; ironLight: string; fur: string; furLight: string;
  skirt: string; skirtDark: string;
  eye: string;
  /** The White Hand of Saruman, painted on the front of the helm. */
  hand?: string;
  /** The Red Eye of Mordor, painted on the front of the helm: the lid's colour and the slit's. */
  redEye?: [string, string];
}

/** The orc's own: green, in dark iron scale and a fur collar. */
const ORC_KIT: OrcKit = {
  skin: "#5a7a32", shade: "#466125", dark: "#263514",
  scale: "#54585e", scaleDark: "#3b3e43", scaleLight: "#7c8289",
  iron: "#666c73", ironLight: "#aab1b8", fur: "#5b4129", furLight: "#7c5c3a",
  skirt: "#7a4428", skirtDark: "#4e2a17", eye: "#f22",
};

/**
 * The Uruk-hai of Isengard: the orc warrior grown big, swart rather than
 * green, in black iron with a collar of black fur and the White Hand of
 * Saruman painted on the front of its helm.
 */
const URUK_KIT: OrcKit = {
  skin: "#3e3a2e", shade: "#2c2920", dark: "#141210",
  scale: "#2c2d31", scaleDark: "#1a1b1e", scaleLight: "#4c4f55",
  iron: "#34363b", ironLight: "#6c7078", fur: "#22201c", furLight: "#3a3630",
  skirt: "#3a2a1c", skirtDark: "#22180f", eye: "#ff3a1a", hand: "#f2f0e8",
};

/** How much bigger an Uruk-hai is drawn than an orc. */
const URUK_SCALE = 1.12;

/**
 * The Uruk-hai: the orc warrior a size up about its feet, in Isengard's
 * black kit (see URUK_KIT). Its grip in MONSTER_GRIP is the orc's scaled
 * the same way, so the scimitar and the White Hand shield are in its fists.
 */
function drawUruk(ctx: Ctx, dir: number, legOff: number, H: number, eyes: EyesFn) {
  ctx.save();
  ctx.translate(16, H);
  ctx.scale(URUK_SCALE, URUK_SCALE);
  ctx.translate(-16, -H);
  drawOrcWarrior(ctx, dir, legOff, H, eyes, URUK_KIT);
  ctx.restore();
}

/**
 * The Black Orc of Mordor (2026-10-04: "Add tougher Black Orcs in Mordor"):
 * one of the black Uruks of Barad-dûr, the orc warrior grown bigger again
 * than the Uruk-hai, skin near black with a grey cast, in black mail and
 * blackened iron, a collar of black hide, and the Red Eye painted on the
 * front of its helm where the Uruk-hai have the White Hand. Its eyes are
 * the red of the Eye.
 */
const BLACK_ORC_KIT: OrcKit = {
  skin: "#2e2c2e", shade: "#1f1d20", dark: "#0b0a0c",
  scale: "#1c1c20", scaleDark: "#0e0e11", scaleLight: "#3a3a42",
  iron: "#232328", ironLight: "#4c4c56", fur: "#141214", furLight: "#26232a",
  skirt: "#2a1414", skirtDark: "#170a0a", eye: "#ff2a10", redEye: ["#c81e0a", "#120404"],
};

/** How much bigger a Black Orc is drawn than an orc: a size over the Uruk-hai. */
const BLACK_ORC_SCALE = 1.18;

/**
 * The Black Orc: the orc warrior a size up again about its feet, bigger
 * than the Uruk-hai as it is tougher, in Mordor's black kit (see
 * BLACK_ORC_KIT). Its grip in MONSTER_GRIP is the orc's scaled the same
 * way, so the scimitar is in its fist.
 */
function drawBlackOrc(ctx: Ctx, dir: number, legOff: number, H: number, eyes: EyesFn) {
  ctx.save();
  ctx.translate(16, H);
  ctx.scale(BLACK_ORC_SCALE, BLACK_ORC_SCALE);
  ctx.translate(-16, -H);
  drawOrcWarrior(ctx, dir, legOff, H, eyes, BLACK_ORC_KIT);
  ctx.restore();
}

/**
 * The orcs of Mount Gundabad (2026-10-15: "Create something at mount
 * gundabad"): each the orc warrior at its own size in its own kit. The
 * Gundabad orc is the mountain-orc of the north, grey as the mountain, in
 * rust-red mail with a collar of grey wolf, yellow-eyed; a size between
 * the Uruk-hai and the Black Orc, as it is on the ladder.
 */
const GUNDABAD_ORC_KIT: OrcKit = {
  skin: "#6c7266", shade: "#555a50", dark: "#272a24",
  scale: "#6a4a34", scaleDark: "#4a3222", scaleLight: "#8e6a4c",
  iron: "#55524e", ironLight: "#8a8580", fur: "#57524a", furLight: "#77716a",
  skirt: "#5a3a24", skirtDark: "#3a2414", eye: "#ffc21a",
};

/**
 * Bolg's bodyguard, "goblins of huge size": the warrior bigger than the
 * Black Orc, in black mail and blackened iron, a collar of black fur and
 * a red skirt, red-eyed.
 */
const BODYGUARD_KIT: OrcKit = {
  skin: "#4c4f48", shade: "#3a3c36", dark: "#181a16",
  scale: "#232326", scaleDark: "#121214", scaleLight: "#44444c",
  iron: "#2c2c30", ironLight: "#5c5c66", fur: "#181616", furLight: "#2c2828",
  skirt: "#5a1410", skirtDark: "#360a08", eye: "#ff4a10",
};

/**
 * Bolg, son of Azog, lord of Gundabad: biggest of them all, pale grey, in
 * black iron, with the white pelt of a great wolf over his shoulders and
 * the pale gold eyes of his father.
 */
const BOLG_KIT: OrcKit = {
  skin: "#8c8e86", shade: "#6c6e66", dark: "#2e302a",
  scale: "#26262a", scaleDark: "#141416", scaleLight: "#4a4a52",
  iron: "#303034", ironLight: "#6a6a74", fur: "#d8d4c6", furLight: "#f2eee4",
  skirt: "#4a1a10", skirtDark: "#2a0c06", eye: "#ffe070",
};

/** How much bigger each of Gundabad's orcs is drawn than an orc. */
const GUNDABAD_ORC_SCALE = 1.14;
const BODYGUARD_SCALE = 1.26;
const BOLG_SCALE = 1.34;

/**
 * One of Gundabad's orcs: the orc warrior scaled about its feet in its kit.
 * Its grip in MONSTER_GRIP is the orc's scaled the same way.
 */
function drawGundabadOrc(ctx: Ctx, dir: number, legOff: number, H: number, eyes: EyesFn, kit: OrcKit, scale: number) {
  ctx.save();
  ctx.translate(16, H);
  ctx.scale(scale, scale);
  ctx.translate(-16, -H);
  drawOrcWarrior(ctx, dir, legOff, H, eyes, kit);
  ctx.restore();
}

/**
 * The stone troll's: brown-grey as weathered rock, lichen in the mottling,
 * a hide of some old sheep for a loincloth, and eyes like a forge.
 */
const STONE_TROLL_HIDE: TrollHide = {
  skin: "#8a7b66", shade: "#5e5243", dark: "#2a241c", light: "#b3a58c", mottle: "#6d7a5a",
  hide: "#9a8a6a", hideDark: "#6a5a40", rope: "#6a5a40", nail: "#d8cfae", tusk: "#efe8d2", eye: "#f0a030",
};

/**
 * The old stone trolls of the Troll-stones (2026-11-01): the stone troll's
 * hide gone greyer and mossed, as rock left out in the weather, with eyes
 * like coals; and the troll-stone one becomes by day, all grey, its eyes
 * shut to stone.
 */
const OLD_TROLL_HIDE: TrollHide = {
  ...STONE_TROLL_HIDE, skin: "#7c7a6c", shade: "#555448", light: "#a6a492", mottle: "#5e7a44", eye: "#ff7a2a",
};
const TROLL_STONE_HIDE: TrollHide = {
  ...STONE_TROLL_HIDE, skin: "#8e8f8a", shade: "#686964", dark: "#3c3e3a", light: "#b2b3ac", mottle: "#7a7c76",
  hide: "#8e8f8a", hideDark: "#686964", rope: "#686964", nail: "#8e8f8a", tusk: "#a2a39c", eye: "#5a5b56",
};
const OLD_TROLL_MOSS = "#5e7a44";
/** How much bigger an old stone troll is drawn than a forest troll. */
const OLD_TROLL_SCALE = 1.3;

/** An old stone troll: the troll drawn bigger, mossed, grey as old rock. */
function drawOldTroll(ctx: Ctx, dir: number, legOff: number, H: number) {
  ctx.save();
  ctx.translate(16, H);
  ctx.scale(OLD_TROLL_SCALE, OLD_TROLL_SCALE);
  ctx.translate(-16, -H);
  drawTroll(ctx, dir, legOff, H, OLD_TROLL_HIDE);
  ctx.restore();
}

/** How much bigger a stone troll is drawn than a forest troll. */
const STONE_TROLL_SCALE = 1.1;

/**
 * The stone troll: the troll a tenth as big again about its feet, between
 * the forest troll and the hill troll as it is on the ladder, in the brown
 * grey of the Trollshaws' rock (see STONE_TROLL_HIDE). Scaled rather than
 * redrawn, as the hill troll is, and its grip is the troll's scaled the
 * same way.
 */
function drawStoneTroll(ctx: Ctx, dir: number, legOff: number, H: number) {
  ctx.save();
  ctx.translate(16, H);
  ctx.scale(STONE_TROLL_SCALE, STONE_TROLL_SCALE);
  ctx.translate(-16, -H);
  drawTroll(ctx, dir, legOff, H, STONE_TROLL_HIDE);
  ctx.restore();
}

/**
 * The huorn: a tree that walks. A thick grey-brown trunk on roots that
 * shift with the step, two boughs for arms reaching out and down, and a
 * crown of dark leaves well over a person's head. From the front and the
 * side a face is in the bark — two knots lit amber and a split for a
 * mouth — and from behind it is only a tree. It is large, and drawn as
 * large (BIG_OUTFITS).
 */
function drawHuorn(ctx: Ctx, dir: number, legOff: number, H: number) {
  const side = dir === 1 ? 1 : dir === 3 ? -1 : 0;
  const back = dir === 0;
  const BARK = "#5e4e3c", BARK_D = "#3e3226", BARK_L = "#7e6c56";
  const LEAF = "#2e4a26", LEAF_D = "#1e3418", LEAF_L = "#46663a";
  // The roots, splayed, shuffling with the walk.
  ctx.strokeStyle = BARK_D;
  ctx.lineCap = "round";
  ctx.lineWidth = 3.5;
  ctx.beginPath();
  for (const [x, dx, off] of [[11, -7, legOff], [14, -3, -legOff], [18, 3, legOff], [21, 7, -legOff]]) {
    ctx.moveTo(x, H - 8);
    ctx.quadraticCurveTo(x + dx / 2, H - 3 + off / 2, x + dx, H - 1 + off / 2);
  }
  ctx.stroke();
  // The boughs: up out of the trunk, then out and down to twig-fingers.
  const bough = (s: number, dy: number) => {
    ctx.strokeStyle = BARK;
    ctx.lineWidth = 4;
    ctx.beginPath();
    ctx.moveTo(16 + s * 5, H - 30);
    ctx.quadraticCurveTo(16 + s * 13, H - 34 + dy, 16 + s * 17, H - 20 + dy);
    ctx.stroke();
    ctx.strokeStyle = BARK_D;
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    for (const [fx, fy] of [[-1, 4], [1, 5], [3, 3]]) {
      ctx.moveTo(16 + s * 17, H - 20 + dy);
      ctx.lineTo(16 + s * (17 + fx), H - 20 + dy + fy);
    }
    ctx.stroke();
  };
  const swing = legOff / 2;
  if (side) bough(-side, swing);
  // The trunk, wider at the foot, ridged with bark.
  ctx.fillStyle = BARK;
  ctx.beginPath();
  ctx.moveTo(9, H - 6);
  ctx.lineTo(11, H - 40);
  ctx.lineTo(21, H - 40);
  ctx.lineTo(23, H - 6);
  ctx.closePath();
  ctx.fill();
  ctx.fillStyle = BARK_D;
  for (const x of [12, 15.5, 19]) ctx.fillRect(x + side * 0.5, H - 38, 1, 30);
  ctx.fillStyle = BARK_L;
  ctx.fillRect(side >= 0 ? 20 : 11, H - 36, 1, 26);
  if (side) bough(side, -swing);
  else {
    bough(-1, -swing);
    bough(1, swing);
  }
  // The crown of leaves, in clumps, well over a person's head.
  for (const [x, y, rx, ry, c] of [
    [16, H - 50, 15, 11, LEAF_D],
    [8, H - 46, 8, 7, LEAF],
    [24, H - 46, 8, 7, LEAF],
    [16, H - 56, 10, 8, LEAF],
    [12, H - 52, 5, 4, LEAF_L],
    [21, H - 54, 4, 3, LEAF_L],
  ] as [number, number, number, number, string][]) {
    ctx.fillStyle = c;
    ctx.beginPath();
    ctx.ellipse(x, y, rx, ry, 0, 0, Math.PI * 2);
    ctx.fill();
  }
  if (back) return;
  // The face in the bark: two knots lit from within, and a mouth.
  const fx = 16 + side * 3;
  ctx.fillStyle = BARK_D;
  ctx.beginPath();
  ctx.ellipse(fx - (side ? 0 : 3), H - 30, 2, 1.6, 0, 0, Math.PI * 2);
  if (!side) ctx.ellipse(fx + 3, H - 30, 2, 1.6, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = "#f0b040";
  ctx.fillRect(fx - (side ? 0.5 : 3.5), H - 30.5, 1.5, 1.2);
  if (!side) ctx.fillRect(fx + 2.5, H - 30.5, 1.5, 1.2);
  ctx.fillStyle = "#1e1810";
  ctx.beginPath();
  ctx.moveTo(fx - (side ? 2 : 4), H - 24);
  ctx.quadraticCurveTo(fx, H - 22 + legOff / 2, fx + (side ? 2 : 4), H - 24);
  ctx.lineTo(fx, H - 23);
  ctx.closePath();
  ctx.fill();
}

/** How much bigger Old Man Willow is drawn than a huorn's frame. */
const WILLOW_SCALE = 1.6;

/**
 * Old Man Willow (2026-10-12: "Add old man willow as a boss"): a huge hoary
 * willow, grey and gnarled, on great roots that grip the bank, with deep
 * cracks down the trunk — the two upper ones for eyes, a dull amber gleam
 * far in, and a long one for a mouth that opens a little as his boughs
 * sway — under a round crown of pale grey-green leaves and the long
 * weeping strands that hang from it nearly to the ground. He never walks,
 * so legOff only stirs the strands. Drawn WILLOW_SCALE times the huorn's
 * frame about the feet, the biggest tree in the world (BIG_OUTFITS).
 */
function drawWillow(ctx: Ctx, dir: number, legOff: number, H: number) {
  const side = dir === 1 ? 1 : dir === 3 ? -1 : 0;
  const back = dir === 0;
  const BARK = "#77756a", BARK_D = "#4a483f", BARK_L = "#9a9888", CRACK = "#17140f";
  const LEAF = "#7f9064", LEAF_D = "#5e6e48", LEAF_L = "#a6b588", STRAND = "#8c9c6c", STRAND_D = "#6a7a50";
  const sway = legOff / 2;
  ctx.save();
  ctx.translate(16, H);
  ctx.scale(WILLOW_SCALE, WILLOW_SCALE);
  ctx.translate(-16, -H);
  // The strands at the back, hanging from the crown's rim.
  const strands = (from: number, to: number, col: string, w: number) => {
    ctx.strokeStyle = col;
    ctx.lineWidth = w;
    ctx.lineCap = "round";
    ctx.beginPath();
    for (let i = from; i <= to; i++) {
      const x = 16 + i * 2.4;
      const top = H - 46 + Math.abs(i) * 0.9;
      const len = 30 - Math.abs(i) * 1.2;
      const s = sway * (i % 2 ? 1 : -0.6);
      ctx.moveTo(x, top);
      ctx.quadraticCurveTo(x + i * 0.9 + s, top + len * 0.5, x + i * 1.3 + s * 1.5, top + len);
    }
    ctx.stroke();
  };
  strands(-8, 8, STRAND_D, 1.4);
  // The roots: great knuckles gripping the bank, out to either side.
  ctx.strokeStyle = BARK_D;
  ctx.lineCap = "round";
  for (const [x, dx, w] of [[10, -9, 3.5], [12, -4, 3], [16, 0, 2.5], [20, 4, 3], [22, 9, 3.5]]) {
    ctx.lineWidth = w;
    ctx.beginPath();
    ctx.moveTo(x, H - 9);
    ctx.quadraticCurveTo(x + dx * 0.4, H - 2, x + dx, H - 0.5);
    ctx.stroke();
  }
  // The trunk: broad, leaning a little, wider at the foot and splitting
  // into two great limbs under the crown.
  ctx.fillStyle = BARK;
  ctx.beginPath();
  ctx.moveTo(7, H - 5);
  ctx.quadraticCurveTo(9, H - 22, 10, H - 36);
  ctx.lineTo(6, H - 46);
  ctx.lineTo(12, H - 44);
  ctx.lineTo(16, H - 38);
  ctx.lineTo(20, H - 45);
  ctx.lineTo(26, H - 46);
  ctx.lineTo(22, H - 36);
  ctx.quadraticCurveTo(23, H - 22, 25, H - 5);
  ctx.closePath();
  ctx.fill();
  // Its ridges and the light down one side.
  ctx.fillStyle = BARK_D;
  for (const x of [10.5, 13.5, 18.5, 21.5]) ctx.fillRect(x + side * 0.6, H - 34, 1, 28);
  ctx.fillStyle = BARK_L;
  ctx.fillRect(side >= 0 ? 22 : 9, H - 32, 1.2, 24);
  // The face in the cracks, from the front and the side; behind, only a
  // crack or two in the bark.
  const fx = 16 + side * 3.5;
  ctx.fillStyle = CRACK;
  if (back) {
    ctx.fillRect(14, H - 30, 1.2, 12);
    ctx.fillRect(19, H - 26, 1, 9);
  } else {
    for (const ex of side ? [0] : [-3.5, 3.5]) {
      ctx.beginPath();
      ctx.moveTo(fx + ex - 2, H - 31);
      ctx.quadraticCurveTo(fx + ex, H - 29.4, fx + ex + 2, H - 31);
      ctx.quadraticCurveTo(fx + ex, H - 27.6, fx + ex - 2, H - 31);
      ctx.fill();
    }
    ctx.fillStyle = "#d8a038";
    for (const ex of side ? [0] : [-3.5, 3.5]) ctx.fillRect(fx + ex - 0.5, H - 30.5, 1.2, 0.9);
    // The mouth: a long split, opening a little as he sways.
    ctx.fillStyle = CRACK;
    ctx.beginPath();
    ctx.moveTo(fx - (side ? 2.5 : 5), H - 21);
    ctx.quadraticCurveTo(fx, H - 18 + Math.abs(legOff) / 2, fx + (side ? 2.5 : 5), H - 21);
    ctx.quadraticCurveTo(fx, H - 20.4, fx - (side ? 2.5 : 5), H - 21);
    ctx.fill();
  }
  // The crown: a round mass of pale, grey-green leaves.
  for (const [x, y, rx, ry, c] of [
    [16, H - 50, 19, 9, LEAF_D],
    [5, H - 47, 9, 6, LEAF],
    [27, H - 47, 9, 6, LEAF],
    [16, H - 55, 13, 7, LEAF],
    [10, H - 54, 6, 4, LEAF_L],
    [22, H - 56, 5, 3, LEAF_L],
  ] as [number, number, number, number, string][]) {
    ctx.fillStyle = c;
    ctx.beginPath();
    ctx.ellipse(x, y, rx, ry, 0, 0, Math.PI * 2);
    ctx.fill();
  }
  // And the strands in front, kept off the face so it shows through.
  strands(-8, -5, STRAND, 1.2);
  strands(5, 8, STRAND, 1.2);
  if (back) strands(-4, 4, STRAND, 1.2);
  ctx.restore();
}

/**
 * What tells one Ringwraith from another: the robe's black and its folds,
 * the mail on the hands, the crown on the brow of the hood — a band of steel
 * with its points, or the Witch-king's helm-crown — and the light where the
 * eyes would be. A Nazgûl's is NAZGUL_KIT; the Witch-king's, WITCH_KING_KIT,
 * since 2026-10-04 ("Add more Nazgul and a boss Witch King").
 */
interface WraithKit {
  robe: string; robeL: string; robeD: string; mail: string;
  /**
   * The crown: a Nazgûl's band of steel with three low points this tall,
   * or "morgul", the Witch-king's helm-crown of Minas Morgul, drawn on the
   * hood as the item is (2026-10-07: "use the helm-crown of minas morgul
   * graphics").
   */
  crown: { steel: string; pointH: number } | "morgul";
  eye: string;
  /** A band of colour at the hem and down the robe's edges, or "" for none. */
  trim: string;
}

const NAZGUL_KIT: WraithKit = {
  robe: "#121116", robeL: "#24222b", robeD: "#07070a", mail: "#6a6e76",
  crown: { steel: "#c8ccd4", pointH: 2 }, eye: "#cfe8ff", trim: "",
};

/**
 * The Witch-king's: the same black a shade blacker, plate-grey gauntlets,
 * the helm-crown of Minas Morgul in place of a crown (2026-10-07: "use the
 * helm-crown of minas morgul graphics") — a faceless mask of black steel
 * filling the hood, a crown of blades fanned off its brow — the
 * Morgul-blade's sick pale green, the light of Minas Morgul, where the eyes
 * would be, and a hem edged in a dried-blood red. Drawn
 * a fifth as big again about the feet (WITCH_KING_SCALE), so he stands over
 * every Nazgûl as he stands over them on the ladder.
 */
const WITCH_KING_KIT: WraithKit = {
  robe: "#0c0b10", robeL: "#1e1c26", robeD: "#040406", mail: "#8a8f98",
  crown: "morgul", eye: "#b8ffc8", trim: "#5a1418",
};

/** How much bigger the Witch-king is drawn than a Nazgûl. */
const WITCH_KING_SCALE = 1.2;

/**
 * The Nazgûl: a tall shape in a black robe and a deep black hood, ragged at
 * the hem and trailing, with nothing in the hood but darkness and two cold
 * points of light, and a pale steel crown on the brow of it. Its hands are
 * mailed in grey; the Morgul-blade is in the bandit's grip, a little lower
 * for the longer arms (see MONSTER_GRIP). The kit is what the Witch-king
 * changes (see drawWitchKing).
 */
/** A fell spirit of Dol Guldur (2026-11-01): the Nazgûl's shape in grey shadow, no crown to speak of, pale points for eyes. */
const FELL_SPIRIT_KIT: WraithKit = {
  robe: "#3a3c44", robeL: "#55585f", robeD: "#24262c", mail: "#3a3c44",
  crown: { steel: "#3a3c44", pointH: 0 }, eye: "#b8c4d0", trim: "",
};

/** The Necromancer (2026-11-01): blacker than the Nine, a hem of dull embers, eyes like coals. */
const NECROMANCER_KIT: WraithKit = {
  robe: "#0c0a0b", robeL: "#1e1a1b", robeD: "#040304", mail: "#2a2224",
  crown: { steel: "#0c0a0b", pointH: 0 }, eye: "#ff7a1a", trim: "#5a1a0e",
};

function drawNazgul(ctx: Ctx, dir: number, legOff: number, H: number, eyes: EyesFn, kit: WraithKit = NAZGUL_KIT) {
  const side = dir === 1 ? 1 : dir === 3 ? -1 : 0;
  const back = dir === 0;
  const ROBE = kit.robe, ROBE_L = kit.robeL, ROBE_D = kit.robeD, MAIL = kit.mail;
  const swing = legOff / 2;
  // The robe, from the shoulders to a ragged hem that drags on the ground.
  const robe = () => {
    ctx.beginPath();
    ctx.moveTo(9, H - 32);
    ctx.lineTo(23, H - 32);
    ctx.lineTo(27, H - 2);
    for (let x = 27; x > 5; x -= 3) {
      ctx.lineTo(x - 1.5, H + ((x / 3) & 1 ? 0 : -2) + legOff / 2);
      ctx.lineTo(x - 3, H - 2);
    }
    ctx.lineTo(5, H - 2);
    ctx.closePath();
  };
  if (kit.trim) {
    // The trim: the robe's outline in the trim's colour, a pixel proud of
    // it at the hem and the edges, so the black laid over it leaves a band.
    ctx.fillStyle = kit.trim;
    ctx.save();
    ctx.translate(16, H);
    ctx.scale(1.08, 1);
    ctx.translate(-16, -H + 1.2);
    robe();
    ctx.fill();
    ctx.restore();
  }
  ctx.fillStyle = ROBE;
  robe();
  ctx.fill();
  ctx.fillStyle = ROBE_L;
  for (const x of [11, 16, 21]) ctx.fillRect(x + side, H - 28, 1, 24);
  ctx.fillStyle = ROBE_D;
  ctx.fillRect(side >= 0 ? 9 : 22, H - 30, 1, 26);
  // The sleeves, wide, and the mailed hands at the ends of them.
  const arm = (x: number, dy: number) => {
    ctx.fillStyle = back ? ROBE_D : ROBE_L;
    ctx.beginPath();
    ctx.moveTo(x + 1, H - 31 + dy);
    ctx.lineTo(x + 5, H - 31 + dy);
    ctx.lineTo(x + 6, H - 18 + dy);
    ctx.lineTo(x - 1, H - 18 + dy);
    ctx.closePath();
    ctx.fill();
    ctx.fillStyle = MAIL;
    ctx.fillRect(x + 0.5, H - 18 + dy, 4, 3);
  };
  arm(5, -swing);
  arm(23, swing);
  // The hood: tall, pointed, deep.
  const hx = 16 + side * 1.5;
  ctx.fillStyle = ROBE;
  ctx.beginPath();
  ctx.moveTo(hx - 8, H - 30);
  ctx.quadraticCurveTo(hx - 8, H - 44, hx - side * 2, H - 47);
  ctx.quadraticCurveTo(hx + 8, H - 44, hx + 8, H - 30);
  ctx.closePath();
  ctx.fill();
  // The crown, a band on the brow of the hood with its points standing up
  // off it; seen from behind, a Nazgûl's low points are lost in the hood.
  // The Witch-king's is the helm-crown of Minas Morgul (2026-10-07): its
  // blades stand high enough to show over the hood from behind, and in
  // front its mask fills the dark of the hood, steel where a face would
  // be, the eye-slits over where the two lights are drawn.
  const fx = hx + side * 2.5;
  const fw = side ? 5 : 9;
  const kitCrown = kit.crown;
  const crown = () => {
    if (kitCrown === "morgul") {
      witchCrownBlades(ctx, fx, H - 41, 1, side
        ? [[-1, -2, 6], [1, 2, 7]]
        : [[-4, -7, 5], [-2, -3, 7], [0, 0, 9], [2, 3, 7], [4, 7, 5]]);
      if (back) return;
      ctx.fillStyle = WITCH_CROWN.steel;
      ctx.beginPath();
      ctx.moveTo(fx - fw / 2, H - 42);
      ctx.lineTo(fx + fw / 2, H - 42);
      ctx.lineTo(fx + fw / 2 - 1, H - 36);
      ctx.lineTo(fx, H - 32);
      ctx.lineTo(fx - fw / 2 + 1, H - 36);
      ctx.closePath();
      ctx.fill();
      ctx.fillStyle = WITCH_CROWN.lit;
      ctx.fillRect(fx - fw / 2, H - 42, fw, 1);
      ctx.fillStyle = WITCH_CROWN.slit;
      ctx.fillRect(fx - fw / 2 + 1, H - 39.5, fw - 2, 2);
      if (!side) ctx.fillRect(fx - 0.5, H - 39.5, 1, 5);
      return;
    }
    ctx.fillStyle = kitCrown.steel;
    ctx.fillRect(fx - fw / 2, H - 42, fw, 1);
    for (const x of side ? [0] : [-3, 0, 3]) ctx.fillRect(fx + x - 0.5, H - 42 - kitCrown.pointH, 1, kitCrown.pointH);
  };
  if (back) {
    if (kitCrown === "morgul") crown();
    return;
  }
  // Nothing in it but dark, the crown on the brow (the Witch-king's mask
  // over the dark), and two lights.
  ctx.fillStyle = ROBE_D;
  ctx.beginPath();
  ctx.ellipse(fx, H - 37, fw / 2, 5.5, 0, 0, Math.PI * 2);
  ctx.fill();
  crown();
  eyes(dir === 2 ? hx : fx - side, H - 38, kit.eye);
}

/**
 * The Witch-king of Angmar (2026-10-04: "Add more Nazgul and a boss Witch
 * King"): a Nazgûl a fifth as big again about its feet, in WITCH_KING_KIT —
 * blacker, in the helm-crown of Minas Morgul since 2026-10-07, green
 * lights behind its slits and a red-edged hem. Scaled rather than redrawn,
 * as the Uruk-hai is the orc warrior, so the grip in MONSTER_GRIP is the
 * Nazgûl's scaled the same way.
 */
function drawWitchKing(ctx: Ctx, dir: number, legOff: number, H: number, eyes: EyesFn) {
  ctx.save();
  ctx.translate(16, H);
  ctx.scale(WITCH_KING_SCALE, WITCH_KING_SCALE);
  ctx.translate(-16, -H);
  drawNazgul(ctx, dir, legOff, H, eyes, WITCH_KING_KIT);
  ctx.restore();
}

/**
 * The barrow-wight: a gaunt shrouded thing wearing its old crown, the
 * shroud ragged at the hem, its arms lost in the sleeves, a grave-grey
 * face and cold eyes. The kit is what the Wight-lord changes.
 */
interface WightKit {
  shroud: string;
  sleeve: string;
  face: string;
  crown: string;
  eye: string;
  /** Points of the crown as tall as the middle one, an iron crown's. */
  tallCrown?: boolean;
}
const WIGHT_KIT: WightKit = { shroud: "#3a4038", sleeve: "#4a524a", face: "#9aa694", crown: "#8a7a3a", eye: "#aef" };

function drawWight(ctx: Ctx, legOff: number, H: number, eyes: EyesFn, kit: WightKit) {
  ctx.fillStyle = kit.shroud;
  ctx.beginPath(); // the shroud, ragged at the hem
  ctx.moveTo(9, H - 25);
  ctx.lineTo(23, H - 25);
  ctx.lineTo(24, H - 6);
  for (let x = 22; x > 8; x -= 3) ctx.lineTo(x, H - 6 - ((x & 1) ? 0 : 3) + legOff / 2);
  ctx.closePath();
  ctx.fill();
  ctx.fillStyle = kit.sleeve;
  ctx.fillRect(8, H - 25 - legOff / 2, 3, 9); // arms lost in the sleeves
  ctx.fillRect(21, H - 25 + legOff / 2, 3, 9);
  ctx.fillStyle = kit.face;
  ctx.beginPath(); // a grave-grey face
  ctx.arc(16, H - 29, 5, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = kit.crown;
  ctx.fillRect(11, H - 34, 10, 2); // the crown
  if (kit.tallCrown) {
    // An iron crown of five tall points.
    for (const x of [11, 13.5, 15, 16.5, 19]) ctx.fillRect(x, H - 38 + Math.abs(x - 15) * 0.5, 2, 4 - Math.abs(x - 15) * 0.5);
  } else {
    ctx.fillRect(12, H - 36, 2, 2);
    ctx.fillRect(15, H - 37, 2, 3);
    ctx.fillRect(18, H - 36, 2, 2);
  }
  eyes(16, H - 30, kit.eye);
}

/**
 * The Wight-lord of Carn Dûm (2026-10-15: "Implement carn dum"): the
 * barrow-wight drawn a quarter as big again about its feet, in a shroud
 * nearly black, a paler, colder face, an iron crown of five points in
 * place of the tarnished gold, and eyes a hard white light. Scaled rather
 * than redrawn, as the Witch-king is the Nazgûl, so its grip in
 * MONSTER_GRIP is the wight's scaled the same way.
 */
const WIGHT_LORD_KIT: WightKit = { shroud: "#1c2020", sleeve: "#2a3030", face: "#b4c2bc", crown: "#4c5258", eye: "#e8f6ff", tallCrown: true };
const WIGHT_LORD_SCALE = 1.25;
function drawWightLord(ctx: Ctx, legOff: number, H: number, eyes: EyesFn) {
  ctx.save();
  ctx.translate(16, H);
  ctx.scale(WIGHT_LORD_SCALE, WIGHT_LORD_SCALE);
  ctx.translate(-16, -H);
  drawWight(ctx, legOff, H, eyes, WIGHT_LORD_KIT);
  ctx.restore();
}

/**
 * The Watcher in the Water (2026-10-08: "when the wrong word is spoken in
 * front of the gate, The Watcher should spawn in the water"): nothing of it
 * but its arms, long and pale green, coming up out of a dark swirl in the
 * pool and curling at the tips, with a faint light along them as the book
 * has it. It is never anything but in the water, so like the siren the
 * bottom of the box is where it goes under. The arms sway with the frame;
 * the ones on the side it faces reach further, towards the bank.
 */
const WATCHER = { arm: "#6f8f62", armDark: "#46613f", armLit: "#a9c98f", sucker: "#d6e6c0", swirl: "#0d1d2a", ripple: "#9cc6d8" };
function drawWatcher(ctx: Ctx, dir: number, legOff: number, H: number) {
  const s = dir === 1 ? 1 : dir === 3 ? -1 : 0;
  const SURFACE = H - 7;
  // The dark under the arms, where the rest of it is.
  ctx.fillStyle = WATCHER.swirl;
  ctx.beginPath();
  ctx.ellipse(16, SURFACE + 1, 15, 5, 0, 0, Math.PI * 2);
  ctx.fill();
  // The arms: root on the water, a bend, and a curl at the tip. The far ones
  // first and darker, the near ones over them.
  const arms: [number, number, number, number][] = [
    // root x, reach out (+ away to the side), height, curl direction
    [9, -10, 30, -1],
    [23, 10, 30, 1],
    [13, -16, 20, -1],
    [19, 16, 20, 1],
    [16, s * 6, 38, s || 1],
  ];
  arms.forEach(([rx, out, tall, curl], i) => {
    const sway = (i % 2 ? legOff : -legOff) * 1.5;
    const toward = s !== 0 && Math.sign(out) === s ? s * 6 : 0; // reach at the bank it faces
    const tx = 16 + out + toward + sway, ty = SURFACE - tall;
    const mx = rx + (out + toward) * 0.3 - sway, my = SURFACE - tall * 0.55;
    ctx.strokeStyle = i < 4 ? WATCHER.armDark : WATCHER.arm;
    ctx.lineCap = "round";
    ctx.lineWidth = i < 4 ? 3.2 : 4;
    ctx.beginPath();
    ctx.moveTo(rx, SURFACE);
    ctx.quadraticCurveTo(mx, my, tx, ty);
    ctx.stroke();
    // The curl at the tip.
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(tx + curl * 2.2, ty, 2.2, Math.PI, Math.PI * 2.4, curl < 0);
    ctx.stroke();
    // The light along it, and its suckers.
    ctx.strokeStyle = WATCHER.armLit;
    ctx.lineWidth = 0.8;
    ctx.beginPath();
    ctx.moveTo(rx - 0.8, SURFACE - 1);
    ctx.quadraticCurveTo(mx - 0.8, my, tx - 0.8, ty + 1);
    ctx.stroke();
    ctx.fillStyle = WATCHER.sucker;
    for (const t of [0.35, 0.6, 0.8]) {
      const u = 1 - t;
      const x = u * u * rx + 2 * u * t * mx + t * t * tx, y = u * u * SURFACE + 2 * u * t * my + t * t * ty;
      ctx.fillRect(x + 0.6, y, 1, 1);
    }
  });
  // Rings on the water round where they come up.
  ctx.strokeStyle = WATCHER.ripple;
  ctx.lineWidth = 0.8;
  ctx.beginPath();
  ctx.ellipse(16, SURFACE + 1, 13 + legOff, 4, 0, Math.PI * 0.1, Math.PI * 0.9);
  ctx.stroke();
}

/**
 * The were-worm (2026-10-09: "Implement the proposal. Replace the gnolls and
 * do the burrow"): a long wingless dragon up out of the ground, nothing of
 * it but the forepart, rearing out of a hole in a ring of thrown-up earth,
 * grey-brown and scaled, with a ridge of plates down its back and a jaw full
 * of teeth. The rest of it is still under, which is where it goes back to
 * when it is hurt (see wereworm.go). It sways with the frame and leans its
 * head the way it faces.
 */
const WEREWORM = { body: "#7a6a52", dark: "#54493a", belly: "#b3a07a", plate: "#3e352a", earth: "#5b4630", hole: "#1b140d", eye: "#e8c64a", tooth: "#efe6cf" };
function drawWereworm(ctx: Ctx, dir: number, legOff: number, H: number) {
  const s = dir === 1 ? 1 : dir === 3 ? -1 : 0;
  const back = dir === 0;
  const GROUND = H - 6;
  // The hole it came out of, and the earth thrown up round it.
  ctx.fillStyle = WEREWORM.earth;
  ctx.beginPath();
  ctx.ellipse(16, GROUND + 1, 17, 6, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = WEREWORM.hole;
  ctx.beginPath();
  ctx.ellipse(16, GROUND, 11, 4, 0, 0, Math.PI * 2);
  ctx.fill();
  // The body: a fat curve up out of the hole to the neck, drawn as discs
  // along it, thick at the ground and thinner at the head.
  const sway = legOff * 1.2;
  const lean = s * 9;
  const x0 = 16, y0 = GROUND, x1 = 16 - s * 6 + sway, y1 = GROUND - 22, x2 = 16 + lean + sway * 0.5, y2 = GROUND - 38;
  const at = (t: number): [number, number] => {
    const u = 1 - t;
    return [u * u * x0 + 2 * u * t * x1 + t * t * x2, u * u * y0 + 2 * u * t * y1 + t * t * y2];
  };
  const N = 14;
  for (let i = 0; i <= N; i++) {
    const t = i / N;
    const [x, y] = at(t);
    const r = 8 - t * 3.5;
    ctx.fillStyle = i % 2 ? WEREWORM.body : WEREWORM.dark;
    ctx.beginPath();
    ctx.arc(x, y, r, 0, Math.PI * 2);
    ctx.fill();
    // The pale belly down the front, which is not seen from behind.
    if (!back) {
      ctx.fillStyle = WEREWORM.belly;
      ctx.beginPath();
      ctx.ellipse(x + s * r * 0.45, y, r * (s ? 0.35 : 0.5), r * 0.55, 0, 0, Math.PI * 2);
      ctx.fill();
    }
  }
  // The plates down its back.
  ctx.fillStyle = WEREWORM.plate;
  for (let i = 2; i < N; i += 3) {
    const [x, y] = at(i / N);
    const r = 8 - (i / N) * 3.5;
    const px = x - (s || 1) * r * 0.8 * (back ? 0 : 1);
    ctx.beginPath();
    ctx.moveTo(px - 2, y + 1);
    ctx.lineTo(px - (s || 0) * 3, y - r - 2);
    ctx.lineTo(px + 2, y + 1);
    ctx.closePath();
    ctx.fill();
  }
  // The head, long and blunt, pushed out the way it faces.
  const hx = x2 + s * 5, hy = y2 - 1;
  ctx.fillStyle = WEREWORM.body;
  ctx.beginPath();
  ctx.ellipse(hx, hy, s ? 8 : 6, 5, 0, 0, Math.PI * 2);
  ctx.fill();
  if (back) return;
  // The jaw, open, and its teeth.
  const jx = hx + s * 4, jy = hy + 3;
  ctx.fillStyle = WEREWORM.hole;
  ctx.beginPath();
  ctx.ellipse(jx, jy, s ? 5 : 4, 2.2, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = WEREWORM.tooth;
  for (let k = -2; k <= 2; k++) {
    ctx.fillRect(jx + k * 1.8 - 0.5, jy - 2.2, 1, 1.6);
  }
  // Its eyes: one in profile, two from the front.
  ctx.fillStyle = WEREWORM.eye;
  if (s) {
    ctx.fillRect(hx + s * 1 - 1, hy - 3, 2, 2);
  } else {
    ctx.fillRect(hx - 3, hy - 3, 2, 2);
    ctx.fillRect(hx + 1, hy - 3, 2, 2);
  }
}

/**
 * Gollum (2026-10-08: "add a gollum creature"): small, thin and crouched
 * low, pale grey-green skin over ribs, a ragged loincloth, a few lank hairs,
 * long arms that hang near the ground, flat bare feet, and two great pale
 * eyes that shine in the dark. Drawn on his own, no bigger than a hobbit
 * bent double.
 */
function drawGollum(ctx: Ctx, dir: number, legOff: number, H: number) {
  const side = dir === 1 ? 1 : dir === 3 ? -1 : 0;
  const back = dir === 0;
  const SKIN = "#9aa38c", SKIN_D = "#7b846e", SKIN_L = "#b4bba6", CLOTH = "#4d4636";
  const hx = 16 + side * 3; // the head pushed forward of the body as he creeps
  // Legs bent under him, and the big flat feet.
  ctx.fillStyle = SKIN_D;
  ctx.fillRect(10, H - 10 + legOff, 4, 8);
  ctx.fillRect(18, H - 10 - legOff, 4, 8);
  ctx.fillStyle = SKIN;
  ctx.fillRect(8 + side * 1.5, H - 3 + legOff * 0.5, 7, 3);
  ctx.fillRect(17 + side * 1.5, H - 3 - legOff * 0.5, 7, 3);
  // The body, hunched: narrow, and lower than a hobbit's.
  ctx.fillStyle = SKIN;
  ctx.beginPath();
  ctx.ellipse(16, H - 15, 6, 6.5, 0, 0, Math.PI * 2);
  ctx.fill();
  if (!back) {
    ctx.fillStyle = SKIN_D; // the ribs
    for (const y of [H - 18, H - 16, H - 14]) ctx.fillRect(12.5, y, 7, 0.8);
  } else {
    ctx.fillStyle = SKIN_D; // the knobbled spine
    for (const y of [H - 20, H - 17, H - 14]) ctx.fillRect(15.5, y, 1.5, 1.5);
  }
  ctx.fillStyle = CLOTH; // the loincloth
  ctx.fillRect(11, H - 11, 10, 3);
  // Long thin arms that hang near the ground, swinging with the step.
  ctx.fillStyle = SKIN_D;
  ctx.fillRect(7, H - 19, 2.5, 13 - legOff);
  ctx.fillRect(22.5, H - 19, 2.5, 13 + legOff);
  ctx.fillStyle = SKIN_L; // the long fingers
  ctx.fillRect(6.5, H - 6 - legOff, 3.5, 2);
  ctx.fillRect(22, H - 6 + legOff, 3.5, 2);
  // The head: big for the body, nearly bald.
  ctx.fillStyle = SKIN;
  ctx.beginPath();
  ctx.ellipse(hx, H - 25, 5.5, 5, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.strokeStyle = "#3a3a30"; // a few lank hairs
  ctx.lineWidth = 0.7;
  ctx.beginPath();
  for (const dx of [-2, 0, 2]) {
    ctx.moveTo(hx + dx, H - 30);
    ctx.lineTo(hx + dx - 1 - side, H - 26);
  }
  ctx.stroke();
  if (back) return;
  // The eyes: great pale lamps, the one thing seen of him in the dark.
  const eye = (x: number) => {
    ctx.fillStyle = "#e8f0c8";
    ctx.beginPath();
    ctx.arc(x, H - 25.5, 1.8, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = "#1a1a10";
    ctx.fillRect(x - 0.5 + side * 0.5, H - 26, 1, 1);
  };
  if (side) eye(hx + side * 2.5);
  else {
    eye(hx - 2.2);
    eye(hx + 2.2);
  }
  ctx.fillStyle = "#4a4a3a"; // a thin mouth
  ctx.fillRect(hx - 1.5 + side * 2, H - 22, 3, 0.8);
}

/**
 * The mûmak of Harad: an elephant as big as a house, grey and wrinkled,
 * with four tusks curving up and out, ears like sails, a trunk that swings
 * with the step, and on its back a war-tower of red-hung timber with a
 * gold-tipped roof. It is huge and drawn so: BIG_OUTFITS gives it more
 * than three tiles of room, and its feet are still on its own tile.
 */
function drawMumak(ctx: Ctx, dir: number, legOff: number, H: number) {
  const side = dir === 1 ? 1 : dir === 3 ? -1 : 0;
  const back = dir === 0;
  const HIDE = "#7d7570", HIDE_D = "#5a534f", HIDE_L = "#9a928c", TUSK = "#efe8d2";
  const WOOD = "#6b4a2c", WOOD_D = "#4a321c", CLOTH = "#a82020", GOLD = "#d8b040";
  const bob = legOff === 0 ? 0 : 1;
  const leg = (x: number, off: number, col: string) => {
    ctx.fillStyle = col;
    ctx.fillRect(x - 4.5, H - 24 + off + bob, 9, 24 - off - bob);
    ctx.fillStyle = HIDE_D;
    ctx.fillRect(x - 5, H - 3, 10, 3);
    ctx.fillStyle = "#d8d0c4"; // the toenails
    for (const t of [-3, 0, 3]) ctx.fillRect(x + t - 0.5, H - 2, 1.5, 1.5);
  };
  // The tower on its back: a box of timber hung with red, a roof over it.
  const tower = (cx: number, by: number) => {
    ctx.fillStyle = CLOTH;
    ctx.fillRect(cx - 14, by - 2, 28, 10); // the saddle-cloth
    ctx.fillStyle = GOLD;
    for (let x = cx - 13; x < cx + 14; x += 4) ctx.fillRect(x, by + 7, 2, 2);
    ctx.fillStyle = WOOD;
    ctx.fillRect(cx - 10, by - 20, 20, 18);
    ctx.fillStyle = WOOD_D;
    for (const x of [-10, -1, 8]) ctx.fillRect(cx + x, by - 20, 2, 18);
    ctx.fillRect(cx - 10, by - 12, 20, 1.5);
    ctx.fillStyle = "#1a1410"; // the loopholes the archers shoot from
    for (const x of [-6, 3]) ctx.fillRect(cx + x, by - 17, 3, 3);
    ctx.fillStyle = CLOTH; // the roof
    ctx.beginPath();
    ctx.moveTo(cx - 13, by - 20);
    ctx.lineTo(cx, by - 30);
    ctx.lineTo(cx + 13, by - 20);
    ctx.closePath();
    ctx.fill();
    ctx.fillStyle = GOLD;
    ctx.fillRect(cx - 1, by - 34, 2, 5);
    ctx.fillRect(cx - 13, by - 21, 26, 1.5);
  };
  if (side) {
    // Profile: the far legs, the tail, the barrel of a body, the near legs,
    // the head with its ear, tusks and trunk, the tower over it all.
    leg(16 - side * 20, -legOff, HIDE_D);
    leg(16 + side * 12, legOff, HIDE_D);
    ctx.strokeStyle = HIDE_D;
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(16 - side * 30, H - 34 + bob);
    ctx.quadraticCurveTo(16 - side * 35, H - 26, 16 - side * 33, H - 18);
    ctx.stroke();
    ctx.fillStyle = HIDE;
    ctx.beginPath();
    ctx.ellipse(16 - side * 6, H - 34 + bob, 26, 17, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = HIDE_L;
    ctx.beginPath();
    ctx.ellipse(16 - side * 6, H - 43 + bob, 20, 5, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = HIDE_D; // the wrinkles
    ctx.lineWidth = 1;
    ctx.beginPath();
    for (const x of [-20, -12, -4, 4]) {
      ctx.moveTo(16 + side * x, H - 30 + bob);
      ctx.quadraticCurveTo(16 + side * (x + 2), H - 25 + bob, 16 + side * x, H - 20 + bob);
    }
    ctx.stroke();
    leg(16 - side * 16, legOff, HIDE);
    leg(16 + side * 8, -legOff, HIDE);
    // The head, the ear over it, and the eye.
    ctx.fillStyle = HIDE;
    ctx.beginPath();
    ctx.ellipse(16 + side * 22, H - 38 + bob, 11, 11, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = HIDE_D;
    ctx.beginPath();
    ctx.ellipse(16 + side * 17, H - 38 + bob, 7, 11, side * 0.15, 0, Math.PI * 2);
    ctx.fill();
    // The trunk, down and curling forward, swinging with the stride.
    ctx.strokeStyle = HIDE;
    ctx.lineWidth = 6;
    ctx.lineCap = "round";
    ctx.beginPath();
    ctx.moveTo(16 + side * 30, H - 34 + bob);
    ctx.quadraticCurveTo(16 + side * 37, H - 22, 16 + side * (33 + legOff), H - 6);
    ctx.stroke();
    // Four tusks, two a side, curving out and up.
    ctx.strokeStyle = TUSK;
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.moveTo(16 + side * 27, H - 30 + bob);
    ctx.quadraticCurveTo(16 + side * 40, H - 26, 16 + side * 44, H - 38);
    ctx.moveTo(16 + side * 25, H - 28 + bob);
    ctx.quadraticCurveTo(16 + side * 34, H - 20, 16 + side * 40, H - 26);
    ctx.stroke();
    ctx.fillStyle = "#1a1410";
    ctx.fillRect(16 + side * 25 - 1, H - 42 + bob, 2, 2);
    tower(16 - side * 6, H - 49 + bob);
    return;
  }
  // Head-on and from behind: the hind legs, the body, the front legs, then
  // the ears, the head with its tusks and trunk, or the rump and tail.
  leg(16 - 13, -legOff, HIDE_D);
  leg(16 + 13, legOff, HIDE_D);
  ctx.fillStyle = HIDE;
  ctx.beginPath();
  ctx.ellipse(16, H - 34 + bob, 24, 17, 0, 0, Math.PI * 2);
  ctx.fill();
  leg(16 - 16, legOff, HIDE);
  leg(16 + 16, -legOff, HIDE);
  if (back) {
    ctx.strokeStyle = HIDE_D;
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(16, H - 30 + bob);
    ctx.quadraticCurveTo(17, H - 22, 16 + legOff, H - 14);
    ctx.stroke();
    ctx.fillStyle = HIDE_D;
    ctx.fillRect(15, H - 34 + bob, 2, 20);
    tower(16, H - 49 + bob);
    return;
  }
  tower(16, H - 49 + bob);
  for (const s of [-1, 1]) {
    ctx.fillStyle = HIDE_D;
    ctx.beginPath();
    ctx.ellipse(16 + s * 17, H - 38 + bob, 10, 14, s * 0.2, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = "#8a6a6a";
    ctx.beginPath();
    ctx.ellipse(16 + s * 17, H - 38 + bob, 6, 10, s * 0.2, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.fillStyle = HIDE;
  ctx.beginPath();
  ctx.ellipse(16, H - 38 + bob, 12, 12, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.strokeStyle = HIDE;
  ctx.lineWidth = 6;
  ctx.lineCap = "round";
  ctx.beginPath();
  ctx.moveTo(16, H - 30 + bob);
  ctx.quadraticCurveTo(16 + legOff * 1.5, H - 16, 16 - legOff, H - 4);
  ctx.stroke();
  ctx.strokeStyle = TUSK;
  ctx.lineWidth = 3;
  ctx.beginPath();
  for (const s of [-1, 1]) {
    ctx.moveTo(16 + s * 5, H - 30 + bob);
    ctx.quadraticCurveTo(16 + s * 14, H - 22, 16 + s * 18, H - 32);
    ctx.moveTo(16 + s * 3, H - 28 + bob);
    ctx.quadraticCurveTo(16 + s * 9, H - 16, 16 + s * 14, H - 22);
  }
  ctx.stroke();
  ctx.fillStyle = "#1a1410";
  ctx.fillRect(10, H - 42 + bob, 2, 2);
  ctx.fillRect(20, H - 42 + bob, 2, 2);
}

/**
 * Shelob's colours, a kit since 2026-10-26 so that Ungoliant can be her
 * drawn bigger and blacker (see UNGOLIANT_KIT). `under` is drawn first,
 * behind the legs, in the scaled frame.
 */
interface ShelobKit {
  legs: string;
  bands: string;
  body: string;
  sheen: string;
  chevrons: string;
  head: string;
  eyes: string;
  fangs: string;
  drip: string;
  under?: (ctx: Ctx, H: number, legOff: number) => void;
}
const SHELOB_KIT: ShelobKit = {
  legs: "#0e0d10", bands: "#4a4a50", body: "#141216", sheen: "#2c2a34", chevrons: "#8a8478",
  head: "#0b0a0d", eyes: "#7cf05a", fangs: "#d8cfc0", drip: "#9ad070",
};

/** How much bigger Shelob is drawn than a giant spider. */
const SHELOB_SCALE = 1.5;

/**
 * Shelob: the giant spider half as big again about its feet, black where
 * the spider is dusky purple, with a bloated abdomen that catches a dull
 * sheen, a pale scar of chevrons down it, joints banded grey rather than
 * tan, and a cluster of green eyes where the spider's are red. The same
 * animal, the way the dragon lord is the dragon, and older.
 */
function drawShelob(ctx: Ctx, dir: number, legOff: number, H: number, kit: ShelobKit = SHELOB_KIT, scale = SHELOB_SCALE) {
  const facingSide = dir === 1 ? 1 : dir === 3 ? -1 : 0;
  ctx.save();
  ctx.translate(16, H);
  ctx.scale(scale, scale);
  ctx.translate(-16, -H);
  kit.under?.(ctx, H, legOff);
  ctx.strokeStyle = kit.legs;
  ctx.lineWidth = 3;
  ctx.lineCap = "round";
  ctx.beginPath(); // eight legs, kneed high above the body
  for (let i = 0; i < 4; i++) {
    const y = H - 34 + i * 7;
    const bend = (i & 1 ? legOff : -legOff) * 1.5;
    ctx.moveTo(16 - 10, y);
    ctx.quadraticCurveTo(16 - 24, y - 14 + bend, 16 - 27, y + 8 + bend);
    ctx.moveTo(16 + 10, y);
    ctx.quadraticCurveTo(16 + 24, y - 14 - bend, 16 + 27, y + 8 - bend);
  }
  ctx.stroke();
  ctx.strokeStyle = kit.bands; // bands at the joints
  ctx.lineWidth = 2;
  ctx.beginPath();
  for (let i = 0; i < 4; i++) {
    const y = H - 34 + i * 7;
    const bend = (i & 1 ? legOff : -legOff) * 1.5;
    ctx.moveTo(16 - 17, y - 7 + bend / 2);
    ctx.lineTo(16 - 21, y - 9 + bend / 2);
    ctx.moveTo(16 + 17, y - 7 - bend / 2);
    ctx.lineTo(16 + 21, y - 9 - bend / 2);
  }
  ctx.stroke();
  ctx.fillStyle = kit.body;
  ctx.beginPath(); // the bloated abdomen
  ctx.ellipse(16, H - 17, 17, 14, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = kit.sheen; // the sheen on it
  ctx.beginPath();
  ctx.ellipse(12, H - 23, 7, 3.5, -0.4, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = kit.chevrons;
  ctx.beginPath(); // the pale chevrons down her back
  for (let i = 0; i < 3; i++) {
    const y = H - 26 + i * 6;
    ctx.moveTo(16, y);
    ctx.lineTo(16 - 6 + i, y + 4);
    ctx.lineTo(16, y + 3);
    ctx.lineTo(16 + 6 - i, y + 4);
    ctx.closePath();
  }
  ctx.fill();
  ctx.fillStyle = kit.head;
  ctx.beginPath(); // the head, thrust the way she faces
  ctx.ellipse(16 + facingSide * 4, H - 36, 10, 8, 0, 0, Math.PI * 2);
  ctx.fill();
  if (dir !== 0) {
    const hx = 16 + facingSide * 4;
    ctx.fillStyle = kit.eyes; // a cluster of eyes
    ctx.fillRect(hx - 5, H - 41, 3, 3);
    ctx.fillRect(hx + 2, H - 41, 3, 3);
    ctx.fillRect(hx - 2, H - 44, 2, 2);
    ctx.fillRect(hx - 7, H - 38, 2, 2);
    ctx.fillRect(hx + 5, H - 38, 2, 2);
    ctx.fillRect(hx - 1, H - 39, 2, 2);
    ctx.fillStyle = kit.fangs; // fangs, parted and dripping
    ctx.beginPath();
    ctx.moveTo(hx - 4, H - 33);
    ctx.lineTo(hx - 2, H - 26 + legOff / 2);
    ctx.lineTo(hx - 1, H - 33);
    ctx.moveTo(hx + 4, H - 33);
    ctx.lineTo(hx + 2, H - 26 - legOff / 2);
    ctx.lineTo(hx + 1, H - 33);
    ctx.closePath();
    ctx.fill();
    ctx.fillStyle = kit.drip;
    ctx.fillRect(hx - 2.5, H - 25 + legOff / 2, 1, 1.5);
  }
  ctx.restore();
}

/**
 * Ungoliant (2026-10-26: "Add ungoliant and Sauron as monsters but no
 * specific spawn"): Shelob drawn bigger again about her feet, black over
 * black, the chevrons down her back a dim violet, her joints banded the
 * same, and a cluster of red eyes; under her the Unlight she weaves, a
 * cloud of dark that no light gets into. The mother, as the dragon lord is
 * the dragon.
 */
const UNGOLIANT_SCALE = 1.85;
const UNGOLIANT_KIT: ShelobKit = {
  legs: "#060508", bands: "#3c2a4c", body: "#09080c", sheen: "#221c2e", chevrons: "#5c3a72",
  head: "#040306", eyes: "#ff3a24", fangs: "#cfc6b4", drip: "#c04040",
  under: (ctx, H, legOff) => {
    // The Unlight: three overlapping clouds of violet-black about her
    // feet, breathing with her walk.
    ctx.fillStyle = "rgba(18, 6, 28, 0.55)";
    ctx.beginPath();
    ctx.ellipse(16, H - 10, 24 + legOff / 2, 8, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = "rgba(10, 2, 16, 0.6)";
    ctx.beginPath();
    ctx.ellipse(8, H - 14, 10, 6 - legOff / 4, -0.3, 0, Math.PI * 2);
    ctx.ellipse(24, H - 14, 10, 6 + legOff / 4, 0.3, 0, Math.PI * 2);
    ctx.fill();
  },
};
function drawUngoliant(ctx: Ctx, dir: number, legOff: number, H: number) {
  drawShelob(ctx, dir, legOff, H, UNGOLIANT_KIT, UNGOLIANT_SCALE);
}

/**
 * The Mirkwood spider (2026-10-27: "implement a mirkwood spider, it's
 * smaller than the existing giant spiders"): the same animal as the giant
 * spider and Shelob, drawn a size down, about a man's height where the
 * giant spider is a horse's; brown where the giant spider is dusky purple,
 * its legs banded ochre and hairy-looking, a pale mottle of chevrons down
 * the back, and a cluster of yellow eyes.
 */
const MIRKWOOD_SPIDER_SCALE = 0.75;
const MIRKWOOD_SPIDER_KIT: ShelobKit = {
  legs: "#241c14", bands: "#8a6a34", body: "#3c3024", sheen: "#56483a", chevrons: "#b09a6a",
  head: "#2a221a", eyes: "#f2c62c", fangs: "#d8cfc0", drip: "#b8c070",
};
function drawMirkwoodSpider(ctx: Ctx, dir: number, legOff: number, H: number) {
  drawShelob(ctx, dir, legOff, H, MIRKWOOD_SPIDER_KIT, MIRKWOOD_SPIDER_SCALE);
}

/**
 * Sauron (2026-10-26: "Add ungoliant and Sauron as monsters but no
 * specific spawn"): the Dark Lord as he came down against the Last
 * Alliance, a man's shape grown a third as big again about his feet, in
 * black plate with spiked pauldrons and a skirt of plates, a helm crowned
 * in five long spikes with the fire of the Eye in its slit, a great
 * flanged mace in his right fist and the One on a finger of his left,
 * glinting gold. Drawn whole here rather than as anybody else's kit.
 */
const SAURON_SCALE = 1.35;
const SAURON = {
  plate: "#1b1b21", plateLit: "#3c3c48", plateDark: "#0b0b0e",
  iron: "#2a2a32", ironLit: "#565664", fire: "#ff7a1a", fireCore: "#ffd060",
  belt: "#4a160a", ring: "#f2c84a",
};
function drawSauron(ctx: Ctx, dir: number, legOff: number, H: number) {
  const side = dir === 1 ? 1 : dir === 3 ? -1 : 0;
  const back = dir === 0;
  // His right hand, the mace's: the viewer's left face on, the right from
  // behind, and the way he faces in profile.
  const maceSide = side || (back ? 1 : -1);
  const swing = legOff / 2;
  ctx.save();
  ctx.translate(16, H);
  ctx.scale(SAURON_SCALE, SAURON_SCALE);
  ctx.translate(-16, -H);
  const mace = () => {
    const hx = 16 + maceSide * 10.5, hy = H - 19 + maceSide * swing;
    const tx = 16 + maceSide * 13, ty = H - 41;
    ctx.strokeStyle = "#2a2018"; // the haft, black wood bound in iron
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.beginPath();
    ctx.moveTo(hx, hy + 2);
    ctx.lineTo(tx, ty);
    ctx.stroke();
    ctx.fillStyle = SAURON.iron; // the head: flanges round a core
    ctx.beginPath();
    ctx.ellipse(tx, ty - 1, 4.2, 5, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = SAURON.ironLit;
    for (const [dx, dy] of [[-5, -1], [5, -1], [0, -7], [-3.5, -5], [3.5, -5], [-3.5, 3], [3.5, 3]]) {
      ctx.beginPath(); // the flanges' points
      ctx.moveTo(tx, ty - 1);
      ctx.lineTo(tx + dx * 1.05 - 0.8, ty + dy);
      ctx.lineTo(tx + dx * 1.05 + 0.8, ty + dy);
      ctx.closePath();
      ctx.fill();
    }
    ctx.fillStyle = SAURON.plateDark;
    ctx.fillRect(tx - 1, ty - 2, 2, 2);
  };
  if (back) mace();
  // Greaves and sabatons.
  ctx.fillStyle = SAURON.plate;
  ctx.fillRect(10.5, H - 14 + legOff, 5, 13);
  ctx.fillRect(16.5, H - 14 - legOff, 5, 13);
  ctx.fillStyle = SAURON.plateLit;
  ctx.fillRect(12, H - 13 + legOff, 1, 9);
  ctx.fillRect(18, H - 13 - legOff, 1, 9);
  ctx.fillStyle = SAURON.plateDark;
  ctx.fillRect(9.5 + (side < 0 ? -1 : 0), H - 2 + legOff, 7, 2);
  ctx.fillRect(15.5 + (side > 0 ? 1 : 0), H - 2 - legOff, 7, 2);
  // The skirt of plates, longest at the middle.
  ctx.fillStyle = SAURON.plate;
  ctx.beginPath();
  ctx.moveTo(9, H - 21);
  ctx.lineTo(23, H - 21);
  ctx.lineTo(25, H - 9);
  ctx.lineTo(16, H - 7);
  ctx.lineTo(7, H - 9);
  ctx.closePath();
  ctx.fill();
  ctx.fillStyle = SAURON.plateDark;
  for (const x of [11, 14, 17, 20, 23]) ctx.fillRect(x - 1, H - 19, 0.8, 10);
  ctx.fillStyle = SAURON.plateLit;
  ctx.fillRect(8, H - 16, 16, 0.8);
  // The breastplate, a ridge down it, and the belt.
  ctx.fillStyle = SAURON.plate;
  ctx.fillRect(9, H - 33, 14, 13);
  ctx.fillStyle = back ? SAURON.plateDark : SAURON.plateLit;
  ctx.fillRect(15.5 + side * 2, H - 32, 1, 11);
  ctx.fillStyle = SAURON.plateDark;
  ctx.fillRect(9, H - 26, 14, 0.8);
  ctx.fillStyle = SAURON.belt;
  ctx.fillRect(9, H - 22, 14, 2);
  // The arms in their vambraces, the gauntlets at the ends.
  const arm = (s: number) => {
    const x = s < 0 ? 4.5 : 23.5, dy = s * swing;
    ctx.fillStyle = SAURON.plate;
    ctx.fillRect(x, H - 31 + dy, 4, 11);
    ctx.fillStyle = SAURON.plateLit;
    ctx.fillRect(x + (s < 0 ? 0.5 : 2.5), H - 30 + dy, 1, 8);
    ctx.fillStyle = SAURON.iron;
    ctx.fillRect(x - 0.5, H - 21 + dy, 5, 4); // the gauntlet
    if (s !== maceSide && !back) {
      ctx.fillStyle = SAURON.ring; // the One, on a finger of his left hand
      ctx.fillRect(x + 1.5, H - 18 + dy, 1.6, 1.4);
    }
  };
  arm(-1);
  arm(1);
  // The pauldrons, with their spikes.
  for (const s of [-1, 1]) {
    const cx = 16 + s * 8.5;
    ctx.fillStyle = SAURON.iron;
    ctx.beginPath();
    ctx.ellipse(cx, H - 32, 5, 3.4, 0, Math.PI, 0);
    ctx.lineTo(cx + 5, H - 30);
    ctx.lineTo(cx - 5, H - 30);
    ctx.closePath();
    ctx.fill();
    ctx.fillStyle = SAURON.ironLit;
    for (const k of [-3, 0, 3]) {
      ctx.beginPath();
      ctx.moveTo(cx + k - 1, H - 34 + Math.abs(k) * 0.4);
      ctx.lineTo(cx + k + s * 1.5, H - 40 + Math.abs(k) * 0.8);
      ctx.lineTo(cx + k + 1, H - 34 + Math.abs(k) * 0.4);
      ctx.closePath();
      ctx.fill();
    }
  }
  // The helm, closed, and its crown of five long spikes.
  const hx = 16 + side * 1;
  ctx.fillStyle = SAURON.plate;
  ctx.beginPath();
  ctx.moveTo(hx - 5.5, H - 33);
  ctx.lineTo(hx - 5.5, H - 42);
  ctx.quadraticCurveTo(hx, H - 46, hx + 5.5, H - 42);
  ctx.lineTo(hx + 5.5, H - 33);
  ctx.lineTo(hx, H - 31);
  ctx.closePath();
  ctx.fill();
  ctx.fillStyle = SAURON.iron;
  for (const k of side ? [-1, 1] : [-4, -2, 0, 2, 4]) {
    const tall = 9 - Math.abs(k) * 1.2;
    ctx.beginPath();
    ctx.moveTo(hx + k - 1, H - 43);
    ctx.lineTo(hx + k * 1.4, H - 43 - tall);
    ctx.lineTo(hx + k + 1, H - 43);
    ctx.closePath();
    ctx.fill();
  }
  ctx.fillStyle = SAURON.ironLit;
  ctx.fillRect(hx - 5.5, H - 43, 11, 1); // the band of the crown
  if (!back) {
    // The slit, and the fire of the Eye behind it.
    const sx = side ? hx + side * 2 : hx;
    const sw = side ? 4 : 8;
    ctx.fillStyle = SAURON.fire;
    ctx.fillRect(sx - sw / 2, H - 39.5, sw, 1.6);
    if (!side) ctx.fillRect(sx - 0.6, H - 39.5, 1.2, 5);
    ctx.fillStyle = SAURON.fireCore;
    ctx.fillRect(sx - (side ? -side : 1), H - 39.2, 1.6, 1);
  }
  if (!back) mace();
  ctx.restore();
}

/**
 * The orc, the helmed warrior: squat and square, planted wide, with no neck
 * to speak of — the head sits straight down in a shaggy fur collar. A
 * spiked iron helm with a nasal and cheek plates, a shirt of dark iron
 * scale, a skirt of leather strips, and a jaw like a shovel with two big
 * tusks. The spike and the collar give it an outline that is neither the
 * berserker's crest nor the troll's round head.
 *
 * The colours are a kit since 2026-10-01 ("Implement the entire proposal in
 * the background"), so that the Uruk-hai can be this warrior in Isengard's
 * black with the White Hand on its helm (see URUK_KIT); the orc's own are
 * ORC_KIT, exactly what was written here before.
 */
function drawOrcWarrior(ctx: Ctx, dir: number, legOff: number, H: number, eyes: EyesFn, kit: OrcKit = ORC_KIT) {
  const side = dir === 1 ? 1 : dir === 3 ? -1 : 0;
  const front = dir === 2;
  const back = dir === 0;
  const SKIN = kit.skin, SHADE = kit.shade, DARK = kit.dark;
  const SCALE = kit.scale, SCALE_D = kit.scaleDark, SCALE_L = kit.scaleLight;
  const IRON = kit.iron, IRON_L = kit.ironLight;
  const FUR = kit.fur, FUR_L = kit.furLight;
  const swing = legOff / 2;

  // Legs set wide, iron-shod.
  ctx.fillStyle = "#2c2721";
  ctx.fillRect(9, H - 13 + legOff, 6, 10);
  ctx.fillRect(17, H - 13 - legOff, 6, 10);
  ctx.fillStyle = "#1c1814";
  ctx.fillRect(8 + side, H - 4 + legOff, 7, 4);
  ctx.fillRect(17 + side, H - 4 - legOff, 7, 4);

  // The scale shirt: a square block, rows of scales picked out in shadow
  // and light.
  ctx.fillStyle = SCALE;
  ctx.fillRect(7, H - 30, 18, 16);
  for (let r = 0; r < 4; r++) {
    const y = H - 27 + r * 4;
    ctx.fillStyle = SCALE_D;
    ctx.fillRect(7, y, 18, 1);
    ctx.fillStyle = SCALE_L;
    for (let x = 8 + (r & 1) * 2; x < 24; x += 4) ctx.fillRect(x, y - 2, 1, 1);
  }
  // A skirt of leather strips under a heavy belt.
  ctx.fillStyle = kit.skirt;
  ctx.fillRect(8, H - 15, 16, 5);
  ctx.fillStyle = kit.skirtDark;
  for (const x of [11.5, 15.5, 19.5]) ctx.fillRect(x, H - 14, 1, 4);
  ctx.fillStyle = "#2a1f16";
  ctx.fillRect(7, H - 16, 18, 2);

  // Arms: thick, bare, with iron bracers.
  const arm = (x: number, dy: number) => {
    ctx.fillStyle = SKIN;
    ctx.fillRect(x, H - 29 + dy, 5, 13);
    ctx.fillStyle = IRON;
    ctx.fillRect(x, H - 22 + dy, 5, 3);
    ctx.fillStyle = IRON_L;
    ctx.fillRect(x, H - 22 + dy, 5, 1);
    ctx.fillStyle = SHADE;
    ctx.fillRect(x, H - 18 + dy, 5, 2);
  };
  arm(3, -swing);
  arm(24, swing);

  // The fur collar over both shoulders, ragged along its lower edge.
  ctx.fillStyle = FUR;
  ctx.beginPath();
  ctx.ellipse(16, H - 30, 13, back ? 5 : 3.5, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.beginPath();
  for (let x = 4; x < 28; x += 4) {
    ctx.moveTo(x, H - 30);
    ctx.lineTo(x + 2, H - (back ? 23 : 25));
    ctx.lineTo(x + 4, H - 30);
  }
  ctx.fill();
  ctx.fillStyle = FUR_L;
  ctx.fillRect(6, H - 33, 20, 1);

  // The head, sitting straight down in the collar.
  const hx = 16 + side * 2;
  ctx.fillStyle = SKIN;
  ctx.beginPath();
  ctx.arc(hx, H - 36, 6.5, 0, Math.PI * 2);
  ctx.fill();
  if (!back) {
    // A shovel of a jaw, with the tusks standing up out of it.
    const jx = side > 0 ? hx - 1 : side < 0 ? hx - 8 : hx - 6;
    ctx.fillStyle = SKIN;
    ctx.fillRect(jx, H - 34, side ? 9 : 12, 5);
    ctx.fillStyle = DARK;
    ctx.fillRect(side ? (side > 0 ? hx + 2 : hx - 7) : hx - 4, H - 31, side ? 5 : 8, 1);
    ctx.fillStyle = "#efe8d2";
    if (side) ctx.fillRect(hx + side * 6 - 1, H - 35, 2, 4);
    else {
      ctx.fillRect(hx - 5, H - 35, 2, 4);
      ctx.fillRect(hx + 3, H - 35, 2, 4);
    }
  }
  // The helm: a dome down to the brow, a lit rim, cheek plates, a nasal
  // and a spike.
  ctx.fillStyle = IRON;
  ctx.beginPath();
  ctx.arc(hx, H - 38, 7.5, Math.PI, 0);
  ctx.fill();
  ctx.fillRect(hx - 7.5, H - 39, 15, 2);
  if (!back) {
    if (side) {
      ctx.fillRect(side > 0 ? hx - 5 : hx + 2, H - 38, 3, 5); // the cheek plate
      ctx.fillRect(hx + side * 6 - 1, H - 38, 2, 4); // the nasal
    } else {
      ctx.fillRect(hx - 7.5, H - 38, 2.5, 5);
      ctx.fillRect(hx + 5, H - 38, 2.5, 5);
      ctx.fillRect(hx - 1, H - 38, 2, 4);
    }
  } else {
    ctx.fillRect(hx - 7.5, H - 38, 15, 4); // the neck guard
  }
  ctx.fillStyle = IRON_L;
  ctx.fillRect(hx - 7.5, H - 39, 15, 1);
  ctx.beginPath();
  ctx.moveTo(hx - 2, H - 45);
  ctx.lineTo(hx, H - 51);
  ctx.lineTo(hx + 2, H - 45);
  ctx.closePath();
  ctx.fill();
  ctx.fillStyle = IRON;
  ctx.fillRect(hx - 2.5, H - 46, 5, 2);
  // The White Hand of Saruman, for the Uruk-hai: an open palm and four
  // fingers painted on the front of the helm.
  if (kit.hand && !back) {
    const px = hx + side * 3;
    ctx.fillStyle = kit.hand;
    ctx.fillRect(px - 1.5, H - 42, 3, 2.5);
    for (const f of side ? [-1, 0.5] : [-1.5, -0.5, 0.5, 1.5]) ctx.fillRect(px + f - 0.25, H - 44.5, 0.75, 2.5);
  }
  // The Red Eye of Mordor, for the Black Orc: a lidless eye in red on the
  // front of the helm, its slit of a pupil black, narrower seen from the
  // side (2026-10-04: "Add tougher Black Orcs in Mordor").
  if (kit.redEye && !back) {
    const px = hx + side * 3;
    const w = side ? 2 : 3.5;
    ctx.fillStyle = kit.redEye[0];
    ctx.beginPath();
    ctx.ellipse(px, H - 42, w, 1.6, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = kit.redEye[1];
    ctx.fillRect(px - 0.4, H - 43.4, 0.8, 2.8);
  }
  eyes(hx, H - 37, kit.eye);
}

/**
 * The cyclops, the brute: a giant in the ape's shape. The shoulders are a
 * tile wide, the head is slung forward and low between them, the arms
 * hang long and swell into fists like clubs, and a pot belly sits over a
 * hide loincloth. One eye, big and pale with a dark pupil, under a brow
 * like a ledge. It sinks onto each step, so the walk reads as heavy.
 */
function drawCyclopsBrute(ctx: Ctx, dir: number, legOff: number, H: number) {
  const side = dir === 1 ? 1 : dir === 3 ? -1 : 0;
  const back = dir === 0;
  const SKIN = "#b98a5c", SHADE = "#946840", LIGHT = "#d2a579", DARK = "#4a2f18";
  const HIDE = "#6e4a29", HIDE_L = "#94703f";
  const bob = legOff === 0 ? 0 : 1; // down onto every step
  const B = H + bob; // the body's own baseline
  const swing = legOff / 2;

  // Legs: short columns with broad flat feet.
  ctx.fillStyle = SHADE;
  ctx.fillRect(7, H - 17 + legOff, 8, 15);
  ctx.fillRect(17, H - 17 - legOff, 8, 15);
  ctx.fillStyle = "#7d5634";
  ctx.fillRect(6 + side * 2, H - 4 + legOff, 10, 4);
  ctx.fillRect(16 + side * 2, H - 4 - legOff, 10, 4);

  // The hump in profile, behind the head.
  if (side) {
    ctx.fillStyle = SKIN;
    ctx.beginPath();
    ctx.arc(16 - side * 6, B - 40, 7, 0, Math.PI * 2);
    ctx.fill();
  }
  // Chest and shoulders: a wedge a whole tile wide at the top.
  ctx.fillStyle = SKIN;
  ctx.beginPath();
  ctx.moveTo(1, B - 42);
  ctx.lineTo(31, B - 42);
  ctx.lineTo(27, B - 22);
  ctx.lineTo(5, B - 22);
  ctx.closePath();
  ctx.fill();
  ctx.beginPath();
  ctx.arc(6, B - 40, 6, 0, Math.PI * 2);
  ctx.arc(26, B - 40, 6, 0, Math.PI * 2);
  ctx.fill();
  // The belly.
  ctx.beginPath();
  ctx.ellipse(16 + side * 3, B - 27, 11, 8, 0, 0, Math.PI * 2);
  ctx.fill();
  if (!back) {
    ctx.fillStyle = SHADE;
    ctx.fillRect(8, B - 33, 7, 1); // under the pecs
    ctx.fillRect(17, B - 33, 7, 1);
    ctx.fillRect(15.5 + side * 3, B - 26, 1, 1); // the navel
    ctx.fillStyle = LIGHT;
    ctx.fillRect(9, B - 38, 5, 2);
  } else {
    ctx.fillStyle = SHADE; // the spine and the shoulder blades
    ctx.fillRect(15.5, B - 36, 1, 10);
    ctx.fillRect(8, B - 36, 1, 5);
    ctx.fillRect(23, B - 36, 1, 5);
  }
  // A hide loincloth with a ragged hem, hung from a thong belt.
  ctx.fillStyle = HIDE;
  ctx.beginPath();
  ctx.moveTo(6, B - 22);
  ctx.lineTo(26, B - 22);
  ctx.lineTo(26, B - 13);
  ctx.lineTo(23, B - 10);
  ctx.lineTo(20, B - 13);
  ctx.lineTo(17, B - 9);
  ctx.lineTo(14, B - 13);
  ctx.lineTo(11, B - 10);
  ctx.lineTo(8, B - 13);
  ctx.lineTo(6, B - 11);
  ctx.closePath();
  ctx.fill();
  ctx.fillStyle = HIDE_L;
  ctx.fillRect(9, B - 19, 2, 2);
  ctx.fillRect(19, B - 17, 2, 2);
  ctx.fillStyle = DARK;
  ctx.fillRect(5, B - 24, 22, 2);

  // Arms: a thick upper arm, a forearm that swells, and a fist like the
  // head of a club, hanging to the knee.
  const arm = (cx: number, dy: number) => {
    ctx.fillStyle = SKIN;
    ctx.fillRect(cx - 3, B - 42 + dy, 6, 11);
    ctx.fillStyle = SHADE; // the crease between the arm and the chest
    ctx.fillRect(cx < 16 ? cx + 3 : cx - 4, B - 38 + dy, 1, 8);
    ctx.fillStyle = SKIN;
    ctx.beginPath();
    ctx.moveTo(cx - 3, B - 32 + dy);
    ctx.lineTo(cx + 3, B - 32 + dy);
    ctx.lineTo(cx + 4.5, B - 24 + dy);
    ctx.lineTo(cx - 4.5, B - 24 + dy);
    ctx.closePath();
    ctx.fill();
    ctx.fillStyle = SHADE;
    ctx.beginPath();
    ctx.arc(cx, B - 22 + dy, 4.5, 0, Math.PI * 2);
    ctx.fill();
  };
  arm(2, -swing);
  arm(30, swing);

  // The head: big, bald, slung forward and low so the shoulders stand
  // round it.
  const hx = 16 + side * 5;
  const hy = B - 47;
  ctx.fillStyle = SKIN;
  ctx.beginPath();
  if (side) ctx.arc(hx - side * 7, hy, 2.5, 0, Math.PI * 2);
  else {
    ctx.arc(hx - 9, hy, 2.5, 0, Math.PI * 2);
    ctx.arc(hx + 9, hy, 2.5, 0, Math.PI * 2);
  }
  ctx.fill();
  // The shadow the head throws on the shoulders it sits between, which is
  // what keeps a skin-coloured head on a skin-coloured body from melting
  // into it.
  ctx.fillStyle = SHADE;
  ctx.beginPath();
  ctx.ellipse(hx, hy + 3, 10.5, 8, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = SKIN;
  ctx.beginPath();
  ctx.arc(hx, hy, 9, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = LIGHT; // light on the dome of the skull
  ctx.fillRect(hx - 3 - side * 2, hy - 8, 5, 2);
  if (back) {
    ctx.fillStyle = SHADE;
    ctx.fillRect(hx - 5, hy + 5, 10, 2); // the fold of the neck
    return;
  }
  // The eye: one, and big — pale, with a dark pupil — under a brow that
  // comes down over the top of it.
  const ex = hx + side * 4;
  const ey = hy - 1;
  ctx.fillStyle = "#f4efe0";
  ctx.beginPath();
  ctx.arc(ex, ey, side ? 3.6 : 5, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = "#8a4a12";
  ctx.beginPath();
  ctx.arc(ex + side * 1.5, ey + 0.5, side ? 2.3 : 2.8, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = "#111";
  ctx.fillRect(ex + side * 1.5 - 1, ey - 0.5, 2, 2);
  ctx.fillStyle = SHADE;
  ctx.beginPath();
  if (side) {
    ctx.moveTo(hx - side * 1, hy - 7);
    ctx.lineTo(hx + side * 10, hy - 5);
    ctx.lineTo(hx + side * 9, hy - 3);
    ctx.lineTo(hx - side * 1, hy - 4);
  } else {
    ctx.moveTo(hx - 8, hy - 7);
    ctx.lineTo(hx + 8, hy - 7);
    ctx.lineTo(hx + 6, hy - 4);
    ctx.lineTo(hx, hy - 3);
    ctx.lineTo(hx - 6, hy - 4);
  }
  ctx.closePath();
  ctx.fill();
  // A wide mouth with a couple of blunt teeth.
  ctx.fillStyle = DARK;
  if (side) ctx.fillRect(side > 0 ? hx + 3 : hx - 8, hy + 5, 5, 1.5);
  else ctx.fillRect(hx - 5, hy + 5, 10, 1.5);
  ctx.fillStyle = "#ece0c0";
  if (side) ctx.fillRect(hx + side * 6 - 1, hy + 4, 2, 1.5);
  else {
    ctx.fillRect(hx - 3, hy + 4, 2, 1.5);
    ctx.fillRect(hx + 2, hy + 4, 2, 1.5);
  }
}

/** A troll's colours: its hide, the loincloth, the tusks and the eyes. */
type TrollHide = {
  skin: string; shade: string; dark: string; light: string; mottle: string;
  hide: string; hideDark: string; rope: string; nail: string; tusk: string; eye: string;
};

/** The troll's own grey green. */
const TROLL_HIDE: TrollHide = {
  skin: "#7f9466", shade: "#58693f", dark: "#2a3320", light: "#a8bb8a", mottle: "#6a7d52",
  hide: "#6e5335", hideDark: "#47341f", rope: "#9a8656", nail: "#d8cfae", tusk: "#ece3c6", eye: "#e8c43a",
};

/**
 * The hill troll's: grey as the rock of the pass it lives on, lichen in the
 * mottling, a darker pelt for a loincloth and eyes like coals. Asked for on
 * 2026-09-28: "make the road east from rivendell go up with several ramp
 * with lots of cyclops, orcs and hill trolls (new monster)".
 */
const HILL_TROLL_HIDE: TrollHide = {
  skin: "#8e887c", shade: "#605a50", dark: "#26231f", light: "#bdb6a8", mottle: "#6f7a5a",
  hide: "#4a3a2a", hideDark: "#2e2319", rope: "#7a6a4c", nail: "#d8cfae", tusk: "#efe8d2", eye: "#e8622a",
};

/**
 * The hill troll: the troll, a fifth as big again about its own feet, in
 * the hill troll's colours. Scaled rather than redrawn, the way the dragon
 * lord is the dragon, so that it is plainly the same animal grown bigger;
 * its grip in MONSTER_GRIP is the troll's scaled the same way, so the club
 * is in the fist that is drawn, and BIG_OUTFITS gives it the room.
 */
function drawHillTroll(ctx: Ctx, dir: number, legOff: number, H: number) {
  ctx.save();
  ctx.translate(16, H);
  ctx.scale(HILL_TROLL_SCALE, HILL_TROLL_SCALE);
  ctx.translate(-16, -H);
  drawTroll(ctx, dir, legOff, H, HILL_TROLL_HIDE);
  ctx.restore();
}

/** How much bigger the hill troll is drawn than a troll. */
const HILL_TROLL_SCALE = 1.2;

/**
 * The troll chief of the Ettenmoors (2026-10-16, the Rangers' camp): the
 * hill troll grown old, drawn 1.4 times the troll about its feet, its grey
 * gone to lichen, and an iron ring through one ear. Scaled rather than
 * redrawn, as the hill troll is the troll, so its grip in MONSTER_GRIP is
 * the troll's scaled the same way and BIG_OUTFITS gives it the room.
 */
const TROLL_CHIEF_HIDE: TrollHide = {
  ...HILL_TROLL_HIDE, skin: "#868a7a", shade: "#585c4e", light: "#b4b8a6", mottle: "#6c8250", eye: "#f08a2a",
};
const TROLL_CHIEF_SCALE = 1.4;
function drawTrollChief(ctx: Ctx, dir: number, legOff: number, H: number) {
  ctx.save();
  ctx.translate(16, H);
  ctx.scale(TROLL_CHIEF_SCALE, TROLL_CHIEF_SCALE);
  ctx.translate(-16, -H);
  drawTroll(ctx, dir, legOff, H, TROLL_CHIEF_HIDE);
  // The ring, where drawTroll sets the near ear on (the left one from in
  // front), hanging from the lobe.
  const s = dir === 1 ? 1 : dir === 3 ? -1 : 0;
  const B = H + (legOff === 0 ? 0 : 1);
  const hx = 16 + s * 6;
  const hy = s ? B - 29 : B - 29.5;
  const d = s ? -s : -1;
  ctx.strokeStyle = TROLL_CHIEF_RING;
  ctx.lineWidth = 1.2;
  ctx.beginPath();
  ctx.ellipse(hx + d * 6, hy + 2, 1.6, 2, 0, 0, Math.PI * 2);
  ctx.stroke();
  ctx.restore();
}

/**
 * The fire elemental's flame, from the outside in: a deep red rim, then
 * orange-red, orange, yellow, and a core near white. Each is the same
 * shape drawn smaller about the base, so the heat sits low in it and the
 * tips are the coolest part, as they are in any fire. Asked for on
 * 2026-09-28: "Add 2 fire elementals in the room with durin's bane".
 */
const FIRE_ELEMENTAL_LAYERS: readonly (readonly [number, string])[] = [
  [1, "#8e1d08"],
  [0.84, "#d9430f"],
  [0.66, "#f5861b"],
  [0.46, "#ffc53a"],
  [0.26, "#fff1b0"],
];

/** The fire elemental's eyes, white-hot, and the dark hollow each sits in. */
const FIRE_ELEMENTAL_EYE = "#fffbe6";
const FIRE_ELEMENTAL_SOCKET = "#5c1003";

/**
 * How the fire elemental's tongues stand on each of the three frames: the
 * head's tip and each arm's, as [sideways, higher]. It flickers on these
 * standing still as well as walking (see FLICKERS), so no two frames
 * agree and none of them is the one it rests on.
 */
const FIRE_ELEMENTAL_TIPS: readonly (readonly [number, number, number, number, number, number])[] = [
  [0, 0, 0, 0, 0, 0],
  [-2, 3, 1, -2, -1, 3],
  [2, -2, -1, 3, 1, -1],
];

/**
 * The outfits drawn as flickering: their frame keeps turning over while
 * they stand still, where everything else rests on frame nought until it
 * walks. A fire does not hold still, and nor does a corpse candle's light
 * (88). See creatureSpriteNow in render.ts.
 */
export const FLICKERS: ReadonlySet<number> = new Set([41, 88]);

/**
 * The outfits whose fire turns over on a clock of its own, apart from their
 * step: the frame they are drawn at is the step (nought to two) plus three
 * times the flame frame (nought to two), so they walk on their legs and
 * burn standing still. The Balrog (2026-10-20: "improve the balrog
 * graphics"), whose mane, sword and whip are fire. See creatureSpriteNow in
 * render.ts.
 */
export const BLAZES: ReadonlySet<number> = new Set([26]);

/** How long each frame of a flicker is held, in milliseconds. */
export const FLICKER_MS = 140;

/**
 * The fire elemental: a living flame, and Durin's Bane's attendants
 * (2026-09-28: "Add 2 fire elementals in the room with durin's bane"). No
 * legs: it stands on a pool of its own fire and rises out of it in a
 * column that narrows at the waist, flares at the shoulders into two arms
 * that bend at the elbow and end in tongues of flame, and carries a round
 * head of fire that runs up into the tallest tongue of all. Two white-hot
 * eyes in dark hollows in the head, and none from behind. In profile the
 * head leans into the way it is going and one arm reaches out in front,
 * the other trailing. Drawn in five layers of the same shape
 * (FIRE_ELEMENTAL_LAYERS), and the tongues move with the frame, which for
 * this outfit turns over standing still too.
 */
function drawFireElemental(ctx: Ctx, dir: number, frame: number, H: number) {
  const side = dir === 1 ? 1 : dir === 3 ? -1 : 0;
  const [hx, hy, lx, ly, rx, ry] = FIRE_ELEMENTAL_TIPS[frame % 3];
  const B = H;
  const lean = side * 3;
  // The shape, once, at whatever size the layer is drawn at.
  const shape = () => {
    // The column: out of the pool, in at the waist, out at the shoulders,
    // and in again at the neck.
    ctx.beginPath();
    ctx.moveTo(16 - 10, B - 2);
    ctx.quadraticCurveTo(16 - 4, B - 11, 16 - 6, B - 20);
    ctx.quadraticCurveTo(16 - 10, B - 29, 16 - 4 + lean, B - 35);
    ctx.lineTo(16 + 4 + lean, B - 35);
    ctx.quadraticCurveTo(16 + 10, B - 29, 16 + 6, B - 20);
    ctx.quadraticCurveTo(16 + 4, B - 11, 16 + 10, B - 2);
    ctx.closePath();
    ctx.fill();
    // The head: a round of flame on the shoulders, running up into the
    // tallest tongue of all.
    ctx.beginPath();
    ctx.moveTo(16 - 5 + lean, B - 36);
    ctx.quadraticCurveTo(16 - 7 + lean, B - 42, 16 - 3 + lean + hx / 3, B - 46);
    ctx.quadraticCurveTo(16 - 1 + lean + hx / 2, B - 50, 16 + lean + hx, B - 56 - hy);
    ctx.quadraticCurveTo(16 + 3 + lean + hx / 2, B - 49, 16 + 4 + lean + hx / 3, B - 46);
    ctx.quadraticCurveTo(16 + 7 + lean, B - 42, 16 + 5 + lean, B - 36);
    ctx.closePath();
    ctx.fill();
    // The arms: out from each shoulder, bent at the elbow, and up into a
    // tongue for a hand. In profile the near one reaches ahead and the
    // far one trails behind, lower.
    const arm = (s: number, reach: number, tx: number, ty: number, drop: number) => {
      ctx.beginPath();
      ctx.moveTo(16 + s * 5, B - 34 + drop);
      ctx.quadraticCurveTo(16 + s * (reach - 3), B - 36 + drop, 16 + s * reach + tx, B - 46 - ty + drop);
      ctx.quadraticCurveTo(16 + s * (reach + 1), B - 33 + drop, 16 + s * (reach - 2), B - 28 + drop);
      ctx.quadraticCurveTo(16 + s * (reach - 6), B - 26 + drop, 16 + s * 5, B - 27 + drop);
      ctx.closePath();
      ctx.fill();
    };
    if (side) {
      arm(-side, 11, rx * -side, ry - 3, 2);
      arm(side, 16, lx * side, ly, 0);
    } else {
      arm(-1, 15, -lx, ly, 0);
      arm(1, 15, rx, ry, 0);
    }
    // Two licks off the hips, and the pool it stands in.
    for (const s of [-1, 1]) {
      ctx.beginPath();
      ctx.moveTo(16 + s * 7, B - 4);
      ctx.quadraticCurveTo(16 + s * 13, B - 9, 16 + s * 12 + (s < 0 ? lx : rx) / 2, B - 17 - (s < 0 ? ry : ly) / 2);
      ctx.quadraticCurveTo(16 + s * 9, B - 10, 16 + s * 4, B - 6);
      ctx.closePath();
      ctx.fill();
    }
    ctx.beginPath();
    ctx.ellipse(16, B - 3, 12, 2.5, 0, 0, Math.PI * 2);
    ctx.fill();
  };
  for (const [k, colour] of FIRE_ELEMENTAL_LAYERS) {
    ctx.save();
    // Each layer shrinks towards the base, a little more across than up,
    // so the core is a narrow heart low in the body.
    ctx.translate(16, B - 3);
    ctx.scale(k * (0.85 + 0.15 * k), k);
    ctx.translate(-16, -(B - 3));
    ctx.fillStyle = colour;
    shape();
    ctx.restore();
  }
  // The eyes: white-hot, each in a dark hollow burnt into the head, and
  // none from behind.
  if (dir === 0) return;
  const eye = (x: number) => {
    ctx.fillStyle = FIRE_ELEMENTAL_SOCKET;
    ctx.fillRect(x - 0.5, B - 42.5, 3, 3);
    ctx.fillStyle = FIRE_ELEMENTAL_EYE;
    ctx.fillRect(x, B - 42, 2, 2);
  };
  if (side) eye(16 + lean + side * 2 - 1);
  else {
    eye(16 - 4);
    eye(16 + 2);
  }
}

/**
 * How the corpse candle's flame stands on each of the three frames, as
 * [sideways, higher] for its tip and [higher] for the whole light, which
 * bobs as it hangs: it flickers standing still (see FLICKERS).
 */
const CORPSE_CANDLE_SWAY: readonly (readonly [number, number, number])[] = [
  [0, 0, 0],
  [-1.5, 2, 1],
  [1.5, -1, -1],
];

/**
 * A corpse candle of the Dead Marshes (2026-10-15: "Put spirit undead in
 * the dead makes marshes"): a dark pool with a dead face looking up out of
 * it, pale and grey-green, and over it the light, a small cold flame in
 * pale green and white with a glow round it, joined to the face by a thin
 * wisp of mist. The face is the dead; the light is what draws you to it.
 * No front or back to a flame, so it is the same from every side but for
 * the face's eyes, which are shut from behind.
 */
function drawCorpseCandle(ctx: Ctx, dir: number, frame: number, H: number) {
  const B = H;
  const [tx, ty, bob] = CORPSE_CANDLE_SWAY[frame % 3];
  // The pool, black water with a rim of the marsh's green.
  ctx.fillStyle = "#26321f";
  ctx.beginPath();
  ctx.ellipse(16, B - 5, 12, 4, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = "#0d1512";
  ctx.beginPath();
  ctx.ellipse(16, B - 5, 10, 3.2, 0, 0, Math.PI * 2);
  ctx.fill();
  // The face under the water, foreshortened, looking up.
  ctx.fillStyle = "rgba(170,190,170,0.8)";
  ctx.beginPath();
  ctx.ellipse(16, B - 5, 4, 2.4, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = "#1a2420";
  if (dir !== 0) {
    ctx.fillRect(13.5, B - 6.5, 1.5, 1.2);
    ctx.fillRect(17, B - 6.5, 1.5, 1.2);
  }
  ctx.fillRect(15, B - 4, 2, 0.8);
  // The wisp from the face up to the light.
  const fy = B - 30 - bob;
  ctx.strokeStyle = "rgba(190,235,220,0.35)";
  ctx.lineWidth = 2;
  ctx.lineCap = "round";
  ctx.beginPath();
  ctx.moveTo(16, B - 7);
  ctx.quadraticCurveTo(16 - tx * 2, B - 16, 16, fy + 6);
  ctx.stroke();
  // The glow, then the flame in three layers, cold at the rim and white
  // at the heart.
  const glow = ctx.createRadialGradient(16, fy, 1, 16, fy, 12);
  glow.addColorStop(0, "rgba(200,255,235,0.55)");
  glow.addColorStop(1, "rgba(120,220,190,0)");
  ctx.fillStyle = glow;
  ctx.beginPath();
  ctx.arc(16, fy, 12, 0, Math.PI * 2);
  ctx.fill();
  for (const [k, colour] of [[1, "#6fcdb2"], [0.7, "#c4f5e2"], [0.4, "#f6fffb"]] as const) {
    ctx.fillStyle = colour;
    ctx.beginPath();
    ctx.moveTo(16 - 4 * k, fy + 3 * k);
    ctx.quadraticCurveTo(16 - 5 * k, fy - 3 * k, 16 + tx * k, fy - (9 + ty) * k);
    ctx.quadraticCurveTo(16 + 5 * k, fy - 3 * k, 16 + 4 * k, fy + 3 * k);
    ctx.quadraticCurveTo(16, fy + 6 * k, 16 - 4 * k, fy + 3 * k);
    ctx.closePath();
    ctx.fill();
  }
}

/**
 * The cave troll's: the slate grey of the deep rock, the green gone out of
 * it for want of a sun, paler on the knuckles, a filthy black rag for a
 * loincloth, and pale eyes that have only ever looked at the dark. Asked
 * for on 2026-09-28: "rename our current trolls as forest trolls, add cave
 * trolls and hill trolls".
 */
const CAVE_TROLL_HIDE: TrollHide = {
  skin: "#6b7466", shade: "#454c42", dark: "#1b1f1a", light: "#98a292", mottle: "#565f50",
  hide: "#35302a", hideDark: "#201c18", rope: "#5c5040", nail: "#c9c2a8", tusk: "#e6dcc0", eye: "#cfe98a",
};

/** How much bigger the cave troll is drawn than a troll: the biggest of the three. */
const CAVE_TROLL_SCALE = 1.3;

/**
 * The cave troll: the troll 1.3 times as big about its own feet, in the
 * cave troll's colours, and two things that are its own. A ridge of stony
 * knobs stands up along its shoulders and down its spine, where the rock
 * it rubs against has grown into it; and there are iron manacles on its
 * wrists with a length of broken chain hanging off one, because the things
 * that live below keep cave trolls on chains. Scaled rather than redrawn,
 * like the hill troll, so that it is plainly a troll; its grip in
 * MONSTER_GRIP is the troll's scaled the same way, so the club is in the
 * fist that is drawn, and BIG_OUTFITS gives it the room.
 */
function drawCaveTroll(ctx: Ctx, dir: number, legOff: number, H: number) {
  ctx.save();
  ctx.translate(16, H);
  ctx.scale(CAVE_TROLL_SCALE, CAVE_TROLL_SCALE);
  ctx.translate(-16, -H);
  drawTroll(ctx, dir, legOff, H, CAVE_TROLL_HIDE);
  const s = dir === 1 ? 1 : dir === 3 ? -1 : 0;
  const back = dir === 0;
  const B = H + (legOff === 0 ? 0 : 1);
  const swing = legOff / 2;
  const knob = (x: number, y: number, r: number) => {
    ctx.fillStyle = CAVE_TROLL_HIDE.dark;
    ctx.beginPath();
    ctx.ellipse(x, y + 0.4, r + 0.5, r + 0.3, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = "#8a8c84";
    ctx.beginPath();
    ctx.ellipse(x, y, r, r, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = "#b2b4ac";
    ctx.fillRect(x - r * 0.6, y - r * 0.7, r * 0.8, 0.8);
  };
  // The knobs: down the spine from behind, along the shoulders otherwise.
  if (back) {
    for (const [y, r] of [[34, 1.6], [30.5, 1.8], [27, 1.7], [23.5, 1.4]]) knob(16, B - y, r);
  } else if (s) {
    for (const [dx, y, r] of [[-6, 33.5, 1.5], [-9, 30.5, 1.7], [-10.5, 27, 1.4]]) knob(16 + s * dx, B - y, r);
  } else {
    for (const [x, y, r] of [[5, 32, 1.4], [8, 34.5, 1.6], [24, 34.5, 1.6], [27, 32, 1.4]]) knob(x, B - y, r);
  }
  // The manacles, where drawTroll puts the wrists: an iron band just above
  // each fist, and a broken chain hanging off the one on the viewer's left
  // (the near one in profile; the far arm is behind the body).
  const grip = MONSTER_GRIP[12];
  const cuff = (hx: number, dy: number, chain: boolean) => {
    const hy = H - grip.y + 1 + dy - 4.5;
    ctx.fillStyle = "#2a2c2e";
    ctx.fillRect(hx - 3.2, hy - 1.2, 6.4, 2.8);
    ctx.fillStyle = "#6c7278";
    ctx.fillRect(hx - 3.2, hy - 1.2, 6.4, 1);
    if (!chain) return;
    ctx.strokeStyle = "#4f5459";
    ctx.lineWidth = 0.9;
    for (let i = 0; i < 3; i++) {
      ctx.beginPath();
      ctx.ellipse(hx - 2.5 - i * 0.6, hy + 3 + i * 2.2, 0.9, 1.3, 0, 0, Math.PI * 2);
      ctx.stroke();
    }
  };
  if (s) cuff(s > 0 ? grip.right : grip.left, s > 0 ? swing : -swing, true);
  else {
    cuff(grip.left, -swing, true);
    cuff(grip.right, swing, false);
  }
  ctx.restore();
}

// The troll, redrawn. Asked for on 2026-09-25: "improve the troll
// graphics". It was a green block with a round head on it — the orc's
// build without the orc's helm — and nothing in the shape said troll.
// This is the thing out of the stories instead: a function of its own,
// like the orc and the cyclops, because it draws in four facings.

/**
 * The troll: hunched and heavy, with the head slung low and forward
 * between shoulders that stand up round it, arms that hang nearly to the
 * ground and end in hands too big for them, and short bowed legs on bare
 * splayed feet. The head is a lumpy thing with a brow like a shelf, a
 * great bulb of a nose, little eyes in the shadow under the brow and an
 * underbite with two tusks coming up out of it. The hide is a grey green,
 * mottled darker and pocked with warts, and the only thing it wears is a
 * ragged loincloth on a rope.
 *
 * The hands are where MONSTER_GRIP says they are, and they swing the way
 * the grip does: the one on the viewer's left rises as the one on the
 * right falls, whichever way the troll faces.
 */
function drawTroll(ctx: Ctx, dir: number, legOff: number, H: number, hide: TrollHide = TROLL_HIDE) {
  const s = dir === 1 ? 1 : dir === 3 ? -1 : 0;
  const back = dir === 0;
  const { skin: SKIN, shade: SHADE, dark: DARK, light: LIGHT, mottle: MOTTLE } = hide;
  const { hide: HIDE, hideDark: HIDE_D, rope: ROPE, nail: NAIL, tusk: TUSK, eye: EYE } = hide;
  const bob = legOff === 0 ? 0 : 1; // it sinks onto every step
  const B = H + bob; // the body's own baseline
  const swing = legOff / 2;
  const grip = MONSTER_GRIP[12];
  const blob = (x: number, y: number, rx: number, ry: number, col: string) => {
    ctx.fillStyle = col;
    ctx.beginPath();
    ctx.ellipse(x, y, rx, ry, 0, 0, Math.PI * 2);
    ctx.fill();
  };
  const dots = (col: string, pts: number[][], ox = 0) => {
    ctx.fillStyle = col;
    for (const [x, y, w = 1] of pts) ctx.fillRect(ox + x, B + y, w, 1);
  };

  // An arm: a heavy shoulder, a bend at the elbow, a forearm that hangs to
  // the knee and a big hand at the end of it, knuckles down.
  const arm = (hx: number, dy: number) => {
    const sx = s ? 16 + s * 1 + (hx < 16 ? -1 : 1) : hx < 16 ? 7 : 25;
    const hy = H - grip.y + 1 + dy;
    const ex = (sx + hx) / 2 + (hx < 16 ? -1.5 : 1.5);
    const ey = (B - 30 + hy) / 2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = SHADE;
    ctx.lineWidth = 6;
    ctx.beginPath();
    ctx.moveTo(sx, B - 30);
    ctx.lineTo(ex, ey);
    ctx.lineTo(hx, hy - 3);
    ctx.stroke();
    ctx.strokeStyle = SKIN;
    ctx.lineWidth = 4;
    ctx.beginPath();
    ctx.moveTo(sx - 0.5, B - 30.5);
    ctx.lineTo(ex - 0.5, ey);
    ctx.lineTo(hx - 0.5, hy - 3);
    ctx.stroke();
    blob(hx, hy, 3.8, 3.4, SHADE);
    blob(hx - 0.3, hy - 0.6, 3.1, 2.7, SKIN);
    ctx.fillStyle = DARK; // the fingers, curled
    ctx.fillRect(hx - 1.5, hy + 1, 1, 2);
    ctx.fillRect(hx + 0.5, hy + 1, 1, 2);
    ctx.fillStyle = LIGHT; // a knuckle catching the light
    ctx.fillRect(hx - 1.5, hy - 2, 2, 1);
  };

  // A leg and its foot: short, thick and bowed, on a bare foot with the
  // toes spread. The planted foot stays on the ground and the other one
  // lifts off it, rather than the whole leg sliding.
  const leg = (x: number, lift: number) => {
    const top = H - 4 - lift;
    ctx.fillStyle = SHADE;
    ctx.fillRect(x, B - 15, 6, top - (B - 15) + 1);
    ctx.fillStyle = SKIN;
    ctx.fillRect(x + (x < 16 ? 0 : 1), B - 15, 5, top - (B - 15) - 1);
    ctx.fillStyle = DARK; // the knee, bowed out
    ctx.fillRect(x < 16 ? x : x + 5, top - 4, 1, 2);
    if (s) {
      // In profile: a long flat foot, the big toe out in front.
      const fx = x + 3 - 4 + s * 2;
      ctx.fillStyle = SHADE;
      ctx.fillRect(fx, top, 9, 4);
      ctx.fillStyle = SKIN;
      ctx.fillRect(fx, top, 9, 2.5);
      ctx.fillStyle = NAIL;
      ctx.fillRect(s > 0 ? fx + 7.5 : fx, top + 2, 1.5, 1);
      return;
    }
    ctx.fillStyle = SHADE;
    ctx.fillRect(x - 1, top, 8, 4);
    ctx.fillStyle = SKIN;
    ctx.fillRect(x - 1, top, 8, 2);
    if (back) {
      ctx.fillStyle = DARK; // the heel
      ctx.fillRect(x + 1, top + 3, 4, 1);
      return;
    }
    // Three fat toes, split and nailed.
    ctx.fillStyle = DARK;
    ctx.fillRect(x + 1.5, top + 2, 1, 2);
    ctx.fillRect(x + 4, top + 2, 1, 2);
    ctx.fillStyle = NAIL;
    for (const tx of [x - 0.5, x + 2.5, x + 5]) ctx.fillRect(tx, top + 3, 1.5, 1);
  };

  // The head: a lumpy thing, set low.
  const hx = 16 + s * 6;
  const hy = s ? B - 29 : B - 29.5;
  const head = () => {
    // Ears: pointed, and cocked up out past the shoulders so they stand
    // against whatever is behind the troll rather than against its arms.
    const ear = (d: number) => {
      ctx.fillStyle = SHADE;
      ctx.beginPath();
      ctx.moveTo(hx + d * 4, hy - 3);
      ctx.lineTo(hx + d * 10.5, hy - 7.5);
      ctx.lineTo(hx + d * 5.5, hy + 1);
      ctx.closePath();
      ctx.fill();
      ctx.fillStyle = SKIN;
      ctx.beginPath();
      ctx.moveTo(hx + d * 5, hy - 2.5);
      ctx.lineTo(hx + d * 9, hy - 6);
      ctx.lineTo(hx + d * 5.5, hy - 0.5);
      ctx.closePath();
      ctx.fill();
    };
    if (s) ear(-s);
    else {
      ear(-1);
      ear(1);
    }
    // The shadow it throws on the shoulders it sits between.
    blob(hx, hy + 2.5, 8, 6.5, SHADE);
    blob(hx, hy, 7.5, 7, SKIN);
    // Lumps on the skull, not in the same place either side.
    if (s) {
      blob(hx - s * 2, hy - 5, 3.2, 3, SKIN);
      blob(hx - s * 5.5, hy - 2, 2.6, 2.6, SKIN);
    } else {
      blob(hx - 3.5, hy - 5, 3.3, 3, SKIN);
      blob(hx + 3, hy - 5.8, 2.7, 2.5, SKIN);
      blob(hx + 5.8, hy - 1.5, 2.2, 2.4, SKIN);
    }
    ctx.fillStyle = LIGHT; // light on the top of the skull
    ctx.fillRect(hx - 4 - s * 2, hy - 8, 3, 1);
    ctx.fillRect(hx + 2 - s * 2, hy - 8, 2, 1);
    // A few lank strands of hair.
    ctx.fillStyle = DARK;
    for (const [dx, dy, l] of [[-2, -10, 3], [0, -10.5, 3], [2, -9.5, 2], [-4, -9, 2]]) {
      ctx.fillRect(hx + dx - s * 2, hy + dy, 1, l);
    }
    // Warts.
    ctx.fillStyle = SHADE;
    ctx.fillRect(hx - 4 - s * 2, hy - 6, 1, 1);
    ctx.fillRect(hx + 4 - s * 2, hy - 4, 1, 1);
    if (back) {
      // From behind: lank hair down the back of the skull, and the fold
      // where the skull sits into the hump.
      ctx.fillStyle = DARK;
      for (const dx of [-4, -1, 2]) ctx.fillRect(hx + dx, hy - 8, 1, 6 + (dx > 0 ? 1 : 0));
      ctx.fillStyle = SHADE;
      ctx.fillRect(hx - 5, hy + 4, 10, 1);
      return;
    }
    if (s) {
      // A little eye, deep in the shadow of the brow.
      ctx.fillStyle = DARK;
      ctx.fillRect(hx + s * 4 - 2, hy - 2, 4, 2.5);
      ctx.fillStyle = EYE;
      ctx.fillRect(hx + s * 4.5 - 0.75, hy - 1.2, 1.5, 1.5);
      // The brow, out over the face like a shelf.
      ctx.fillStyle = SKIN;
      ctx.beginPath();
      ctx.moveTo(hx - s * 1, hy - 6);
      ctx.lineTo(hx + s * 8.5, hy - 4);
      ctx.lineTo(hx + s * 8, hy - 2);
      ctx.lineTo(hx, hy - 2);
      ctx.closePath();
      ctx.fill();
      ctx.fillStyle = LIGHT;
      ctx.fillRect(s > 0 ? hx : hx - 7, hy - 5, 7, 1);
      // The underbite: the jaw juts past the face, the tusk stands up
      // out of it.
      blob(hx + s * 4, hy + 5, 5.5, 3, SHADE);
      blob(hx + s * 4, hy + 4.6, 5, 2.6, SKIN);
      ctx.fillStyle = DARK;
      ctx.fillRect(s > 0 ? hx + 3 : hx - 9, hy + 3, 6, 1.5);
      ctx.fillStyle = TUSK;
      ctx.fillRect(hx + s * 7.5 - 0.75, hy + 0.5, 1.5, 3.5);
      // The nose: a great bulb, hooked out over the mouth.
      blob(hx + s * 7.5, hy + 1, 3, 2.7, SHADE);
      blob(hx + s * 7.8, hy + 0.3, 2.3, 2, LIGHT);
      ctx.fillStyle = DARK;
      ctx.fillRect(hx + s * 7 - 0.5, hy + 2.5, 1.5, 1);
      return;
    }
    // The eyes, little and deep in the shadow of the brow.
    ctx.fillStyle = DARK;
    ctx.fillRect(hx - 6, hy - 2, 12, 2.5);
    ctx.fillStyle = EYE;
    ctx.fillRect(hx - 4.5, hy - 1.2, 1.5, 1.5);
    ctx.fillRect(hx + 3, hy - 1.2, 1.5, 1.5);
    // The brow, a shelf straight across and wider than the face.
    ctx.fillStyle = SKIN;
    ctx.fillRect(hx - 7.5, hy - 4.5, 15, 2.5);
    ctx.fillStyle = LIGHT;
    ctx.fillRect(hx - 6.5, hy - 4.5, 13, 1);
    // The jaw, wider than the face, with the tusks coming up out of it.
    blob(hx, hy + 5, 7, 3, SHADE);
    blob(hx, hy + 4.6, 6.5, 2.6, SKIN);
    ctx.fillStyle = DARK;
    ctx.fillRect(hx - 5, hy + 3.5, 10, 1.5);
    ctx.fillStyle = TUSK;
    ctx.fillRect(hx - 5, hy + 1, 1.5, 3.5);
    ctx.fillRect(hx + 3.5, hy + 1, 1.5, 3.5);
    // The nose, a great bulb hanging over it all.
    blob(hx, hy + 1.5, 3, 2.8, SHADE);
    blob(hx, hy + 0.9, 2.4, 2.1, LIGHT);
    ctx.fillStyle = SKIN;
    ctx.fillRect(hx - 1, hy + 1.5, 2, 1);
    ctx.fillStyle = DARK;
    ctx.fillRect(hx - 2, hy + 3, 1.5, 1);
    ctx.fillRect(hx + 0.5, hy + 3, 1.5, 1);
  };

  // The arm on the far side goes behind everything in profile.
  const leftLift = legOff < 0 ? -legOff : 0;
  const rightLift = legOff > 0 ? legOff : 0;
  if (s) arm(s > 0 ? grip.left : grip.right, s > 0 ? -swing : swing);

  leg(9, leftLift);
  leg(17, rightLift);

  // The body: a hump of shoulders over a hanging belly, each a lit mass
  // inside a shaded rim.
  const masses: number[][] = s
    ? [[16 - s * 3, B - 28, 8.5, 7.5, -s * 0.5, -0.5], [16 + s * 2, B - 19.5, 8.5, 7, 0, -1]]
    : [[16, B - 28, 12, 7, 0, -0.5], [16, B - 19, 9.5, 7.5, 0, -1]];
  for (const [x, y, rx, ry] of masses) blob(x, y, rx, ry, SHADE);
  const lit = () => {
    ctx.beginPath();
    for (const [x, y, rx, ry, dx, dy] of masses) {
      ctx.moveTo(x + dx + rx - 1, y + dy);
      ctx.ellipse(x + dx, y + dy, rx - 1, ry - 1, 0, 0, Math.PI * 2);
    }
  };
  ctx.fillStyle = SKIN;
  lit();
  ctx.fill();
  ctx.fillStyle = LIGHT;
  if (s) ctx.fillRect(16 - s * 5 - 2, B - 34, 4, 1);
  else {
    ctx.fillRect(7, B - 34, 4, 1);
    ctx.fillRect(21, B - 34, 4, 1);
    ctx.fillStyle = SHADE;
    if (back) ctx.fillRect(15.5, B - 31, 1, 13); // the spine down the hump
    else {
      ctx.fillRect(12, B - 24, 8, 1); // the sag of the belly and the navel
      ctx.fillRect(15.5, B - 19, 1, 1);
    }
  }
  // Mottling and warts over the hide: darker blotches, and a few pale
  // warts standing up out of it, kept to the skin they are on.
  ctx.save();
  lit();
  ctx.clip();
  dots(MOTTLE, [[-8, -30, 3], [-7, -29, 2], [4, -31, 2], [5, -30, 3], [-3, -24, 2], [-2, -23, 2],
    [5, -22, 2], [6, -21, 2], [-7, -20, 2], [-6, -19, 1], [1, -17, 2], [9, -27, 2]], 16);
  dots(LIGHT, [[-4, -29], [8, -25], [3, -20], [-6, -23], [0, -27]], 16);
  ctx.restore();

  // A ragged hide loincloth on a rope.
  ctx.fillStyle = HIDE;
  ctx.beginPath();
  const lx = 16 + s * 2;
  ctx.moveTo(lx - 8, B - 15);
  ctx.lineTo(lx + 8, B - 15);
  ctx.lineTo(lx + 7, B - 10);
  ctx.lineTo(lx + 5, B - 12);
  ctx.lineTo(lx + 3, B - 8);
  ctx.lineTo(lx + 1, B - 11);
  ctx.lineTo(lx - 1, B - 7);
  ctx.lineTo(lx - 3, B - 11);
  ctx.lineTo(lx - 5, B - 9);
  ctx.lineTo(lx - 7, B - 12);
  ctx.lineTo(lx - 8, B - 10);
  ctx.closePath();
  ctx.fill();
  ctx.fillStyle = HIDE_D;
  ctx.fillRect(lx - 4, B - 13, 1, 3);
  ctx.fillRect(lx + 3, B - 13, 1, 2);
  ctx.fillStyle = ROPE;
  ctx.fillRect(lx - 8.5, B - 16, 17, 1.5);

  if (back) {
    // From behind, the head sits down in the hump.
    head();
    blob(16, B - 26, 11, 3.5, SKIN);
  }
  if (s) arm(s > 0 ? grip.right : grip.left, s > 0 ? swing : -swing);
  else {
    arm(grip.left, -swing);
    arm(grip.right, swing);
  }
  if (!back) head();
}

// --- the world's people ------------------------------------------------------
//
// The content proposal's eleven NPCs (2026-10-01: "Implement the entire
// proposal in the background"), drawn in the manner of Grubb and Elrond:
// one figure each, plain at a glance and each unlike the others. Two
// hobbits on Grubb's build (drawHobbitFolk), six men and women on one frame
// in their own clothes (drawFolk), Beorn on that frame half as big again
// (drawBeorn), the Lady in white (drawGaladriel) and an Ent (drawTreebeard).

/** What a hobbit of the Shire is wearing: see drawHobbitFolk. */
interface HobbitKit {
  /** Curls: brown on a hobbit in his prime, grey and white on the old. */
  hair: string;
  shirt: string;
  waistcoat: string;
  waistcoatDark: string;
  breeches: string;
  buttons: string;
  /** What is in his hand: the Gaffer's spade, Bilbo's book, Tobold's pipe. */
  holding: "spade" | "book" | "pipe";
}

/** The Gaffer: grey curls, shirtsleeves and an earth-brown waistcoat, a spade. */
const GAFFER_KIT: HobbitKit = {
  hair: "#a8a39a", shirt: "#d8cfb4", waistcoat: "#6a5236", waistcoatDark: "#4a3a26", breeches: "#4f5a3a", buttons: "#b0a080", holding: "spade",
};

/** Bilbo, old: white curls, a red waistcoat with brass buttons, his book. */
const BILBO_KIT: HobbitKit = {
  hair: "#e8e4dc", shirt: "#efe6cc", waistcoat: "#a82828", waistcoatDark: "#7a1c1c", breeches: "#5a4a32", buttons: "#e8c24a", holding: "book",
};

/** Tobold Hornblower of Longbottom (empty-areas site 1, 2026-10-16): brown curls, a leaf-green waistcoat, his long pipe. */
const TOBOLD_KIT: HobbitKit = {
  hair: "#6a4424", shirt: "#e8dcc0", waistcoat: "#4f7a3a", waistcoatDark: "#36562a", breeches: "#6a5236", buttons: "#d8c070", holding: "pipe",
};

/**
 * A hobbit of the Shire: Grubb's build (see drawCreature's case 35) — half
 * a head shorter than a man, big bare hairy feet, curls — in a kit of his
 * own, with something in his hand. The Gaffer leans on a spade; Bilbo has
 * the red book he is writing.
 */
function drawHobbitFolk(ctx: Ctx, dir: number, legOff: number, H: number, eyes: EyesFn, k: HobbitKit) {
  const side = dir === 1 ? 1 : dir === 3 ? -1 : 0;
  const front = dir === 2;
  const back = dir === 0;
  // What he holds goes behind him seen from the back.
  const held = () => {
    const hx = side ? 16 + side * 10 : front ? 5 : 27;
    if (k.holding === "spade") {
      ctx.fillStyle = "#7a5a36"; // the shaft, standing on its blade
      ctx.fillRect(hx - 0.5, H - 26, 1.5, 21);
      ctx.fillStyle = "#5a4026"; // the D of the grip
      ctx.fillRect(hx - 2, H - 28, 5, 2);
      ctx.fillStyle = "#8c8c88"; // the blade, in the earth
      ctx.fillRect(hx - 2.5, H - 6, 6, 5);
      ctx.fillStyle = "#6a5a40";
      ctx.fillRect(hx - 2.5, H - 2, 6, 1);
    } else if (k.holding === "pipe") {
      // A long-stemmed pipe from the hand up to the mouth, its bowl
      // glowing, with a wisp over it (Longbottom, 2026-10-16).
      if (back) return;
      const mx = 16 + side * 4.5, my = H - 27;
      const bx = side ? mx + side * 6 : 21, by = H - 22;
      ctx.strokeStyle = "#6a4a2c";
      ctx.lineWidth = 1.2;
      ctx.beginPath();
      ctx.moveTo(mx, my);
      ctx.lineTo(bx, by);
      ctx.stroke();
      ctx.fillStyle = "#7a4426";
      ctx.fillRect(bx - 1.5, by - 3, 3, 3.5);
      ctx.fillStyle = "#ff8c26";
      ctx.fillRect(bx - 1, by - 3, 2, 1);
      ctx.fillStyle = "rgba(230,230,225,0.6)";
      ctx.fillRect(bx - 0.5, by - 6, 1.5, 2);
      ctx.fillRect(bx + 0.5, by - 9, 1.5, 2);
    } else {
      ctx.fillStyle = "#8a2a1e"; // the red book, under an arm
      ctx.fillRect(hx - 3, H - 20, 6, 7);
      ctx.fillStyle = "#e8dcb8";
      ctx.fillRect(hx - 3 + (side < 0 ? 5 : 0), H - 19.5, 1, 6);
      ctx.fillStyle = "#d8b24a";
      ctx.fillRect(hx - 1.5, H - 17, 3, 1);
    }
  };
  if (back) held();
  ctx.fillStyle = "#8a5a32"; // bare feet, broad
  ctx.fillRect(9, H - 4 + legOff, 6, 3);
  ctx.fillRect(17, H - 4 - legOff, 6, 3);
  ctx.fillStyle = k.hair === "#e8e4dc" ? "#8a7a64" : "#5a3a1e"; // the hair on top of them
  ctx.fillRect(10, H - 5 + legOff, 4, 1);
  ctx.fillRect(18, H - 5 - legOff, 4, 1);
  ctx.fillStyle = k.breeches; // breeches to the knee
  ctx.fillRect(10, H - 12 + legOff, 5, 8);
  ctx.fillRect(17, H - 12 - legOff, 5, 8);
  ctx.fillStyle = k.shirt;
  ctx.fillRect(8, H - 25, 16, 14);
  ctx.fillStyle = back ? k.waistcoatDark : k.waistcoat; // the waistcoat over it
  if (back) ctx.fillRect(8, H - 25, 16, 14);
  else if (side) ctx.fillRect(side > 0 ? 8 : 14, H - 25, 10, 14);
  else {
    ctx.fillRect(8, H - 25, 7, 14);
    ctx.fillRect(17, H - 25, 7, 14);
  }
  if (front) {
    ctx.fillStyle = k.buttons;
    for (const y of [22, 18, 14]) ctx.fillRect(14, H - y, 1, 1);
    for (const y of [22, 18, 14]) ctx.fillRect(17, H - y, 1, 1);
  }
  ctx.fillStyle = "#e0b48a"; // arms, sleeves rolled
  ctx.fillRect(5, H - 24, 3, 10);
  ctx.fillRect(24, H - 24, 3, 10);
  ctx.beginPath(); // a round face, the years in it
  ctx.arc(16 + side * 1.5, H - 30, 6, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = k.hair; // curls
  if (back) {
    ctx.beginPath();
    ctx.arc(16, H - 30, 6.5, 0, Math.PI * 2);
    ctx.fill();
  } else {
    for (const [dx, dy] of [[-5, -3], [-3, -6], [0, -7], [3, -6], [5, -3], [-6, 0], [6, 0]]) {
      ctx.beginPath();
      ctx.arc(16 + side * 1.5 + dx, H - 30 + dy, 2.2, 0, Math.PI * 2);
      ctx.fill();
    }
    eyes(16 + side * 2.5, H - 30, "#2a1a0a");
    if (front) {
      ctx.fillStyle = "#b0705a";
      ctx.fillRect(14, H - 27, 4, 1);
    }
  }
  if (!back) held();
}

/** What one of the big folk is wearing: see drawFolk. */
interface FolkKit {
  skin: string;
  hair: string;
  /** Short, to the shoulders, or down the back. */
  hairLength: "short" | "shoulder" | "long";
  beard?: string;
  coat: string;
  coatDark: string;
  trim: string;
  /** To the belt, to the knee, or to the ground (a dress or a robe). */
  length: "belt" | "knee" | "ground";
  legs: string;
  boots: string;
  /** Boots to the knee rather than to the ankle. */
  tallBoots?: boolean;
  belt: string;
  /** An apron over the front: the innkeeper's and the healer's. */
  apron?: string;
  /** How much wider than a plain man the body is: the innkeeper. */
  girth?: number;
  /** A cloak hanging behind, seen at the shoulders and the sides. */
  cloak?: string;
  /**
   * What is on the head: a Wood-elf's hood, the Elvenking's guard's tall
   * helm, and the king's crown of leaves and berries among them since
   * 2026-10-07.
   */
  hat?: "feather" | "rohan" | "kerchief" | "swan" | "hood" | "elfhelm" | "leaves" | "wizard";
  hatCol?: string;
  hatCol2?: string;
  /** A device on the breast: the swan ship of Dol Amroth. */
  swan?: boolean;
  /** Mail showing in rings. */
  mail?: boolean;
  /** A yew bow and a quiver on the back. */
  bow?: boolean;
  /** Bare arms, and no sleeves. */
  bareArms?: boolean;
  /** An elf's pointed ears, drawn as a player elf's are (see drawPlayer). */
  ears?: boolean;
}

const BARLIMAN_KIT: FolkKit = {
  skin: "#e8a888", hair: "#6a4a2e", hairLength: "short", coat: "#7a5a3a", coatDark: "#5a4028", trim: "#e8dcb8", length: "belt",
  legs: "#4a3a2a", boots: "#2a1e14", belt: "#3a2a1a", apron: "#f0ead8", girth: 3,
};

const BOMBADIL_KIT: FolkKit = {
  skin: "#e8a888", hair: "#6a4a2a", hairLength: "shoulder", beard: "#7a5430", coat: "#2b5ea8", coatDark: "#1e4478", trim: "#e8c020", length: "belt",
  legs: "#5a4a32", boots: "#f0c818", tallBoots: true, belt: "#4a3018", hat: "feather", hatCol: "#5a4a30", hatCol2: "#3a7ad8",
};

const BARD_KIT: FolkKit = {
  skin: "#d8b090", hair: "#1e1a16", hairLength: "shoulder", beard: "#1e1a16", coat: "#2e3e2a", coatDark: "#1e2a1c", trim: "#6a5a3a", length: "knee",
  legs: "#3a3428", boots: "#2a2018", tallBoots: true, belt: "#4a3420", cloak: "#3a3026", bow: true,
};

const EOMER_KIT: FolkKit = {
  skin: "#e6c4a0", hair: "#d8b45a", hairLength: "shoulder", beard: "#c8a04a", coat: "#8a9098", coatDark: "#5e646c", trim: "#d8b040", length: "knee",
  legs: "#3a4a2a", boots: "#4a3420", tallBoots: true, belt: "#6a4a26", cloak: "#2e6a32", hat: "rohan", hatCol: "#b8bcc4", hatCol2: "#f4f4ee", mail: true,
};

const IORETH_KIT: FolkKit = {
  skin: "#e8c8b0", hair: "#c8c4bc", hairLength: "short", coat: "#7a7a80", coatDark: "#5a5a60", trim: "#e8e4dc", length: "ground",
  legs: "#5a5a60", boots: "#3a3430", belt: "#5a4a3a", apron: "#f2f0ea", hat: "kerchief", hatCol: "#f4f2ec", hatCol2: "#d8d4cc",
};

const IMRAHIL_KIT: FolkKit = {
  skin: "#e6d0b8", hair: "#1e1a20", hairLength: "shoulder", coat: "#2a4a8a", coatDark: "#1a325e", trim: "#d8dce4", length: "knee",
  legs: "#2a2e3a", boots: "#1c1a20", tallBoots: true, belt: "#c8ccd4", cloak: "#1a2a52", hat: "swan", hatCol: "#c8ccd4", hatCol2: "#f4f6fa", swan: true,
};

// The sea road's two (2026-10-06: "Add a boat from the Grey Havens to
// somewhere near Minas Tirith per the lore"; see ships.go). Círdan, very
// old and the one bearded elf, in the grey of the Havens to the ground
// with a sea-blue cloak; the Harlond's harbour-master in the black and
// silver of the Citadel.
const CIRDAN_KIT: FolkKit = {
  skin: "#eadccc", hair: "#d8dce2", hairLength: "long", beard: "#e4e6ea", coat: "#9aa0a8", coatDark: "#747a84", trim: "#e8ecf2", length: "ground",
  legs: "#747a84", boots: "#4a4e56", belt: "#c8ccd4", cloak: "#3a5a7a",
};

const HARBOURMASTER_KIT: FolkKit = {
  skin: "#dcb898", hair: "#3a2e24", hairLength: "short", beard: "#3a2e24", coat: "#22242a", coatDark: "#141518", trim: "#c8ccd4", length: "knee",
  legs: "#2a2c32", boots: "#1c1a1a", tallBoots: true, belt: "#8a8e96", cloak: "#1a1c22",
};

// The swan-ships' crew of Dol Amroth (2026-10-08: the Isle of Meneltarma),
// in Imrahil's blue and silver with the swan on the breast, a mariner's
// knee-length coat and sea boots. The captain is weathered, with a short
// salt-grey beard; the ship's master on the isle older, grey-haired and
// long-bearded, in a darker sea-cloak.
const SWANCAPTAIN_KIT: FolkKit = {
  skin: "#c89a74", hair: "#4a4036", hairLength: "short", beard: "#8a8478", coat: "#2c4a7a", coatDark: "#1c3258", trim: "#d8dce4", length: "knee",
  legs: "#2a3442", boots: "#2a2420", tallBoots: true, belt: "#c8ccd4", cloak: "#24406a", swan: true,
};

const SWANMASTER_KIT: FolkKit = {
  skin: "#b88c68", hair: "#a8a8a4", hairLength: "shoulder", beard: "#bcbcb6", coat: "#223a62", coatDark: "#162846", trim: "#c8ccd4", length: "knee",
  legs: "#262e3a", boots: "#24201c", tallBoots: true, belt: "#a8acb4", cloak: "#1a2a44", swan: true,
};

// The dead of the crypt under Sauron's temple (2026-10-08). A King's Man of
// Númenor drowned in his mail, grey-green and running with the sea, under
// a helm with its horsetail gone to weed; a priest's shade in grey robes
// and a hood, drawn three-quarters seen; one of those the altar burned,
// black and bare with the embers still red in its belt; and the High
// Priest in black to the ground under a black hood, trimmed in red.
// Site 3, the Lossoth of Forochel (2026-10-16): their headman in white
// furs, hooded, a broad brown face; and Arvedui's drowned men, the King's
// Man's frame in the dark mail of the North, hooded, frost at the trim.
const LOSSOTH_KIT: FolkKit = {
  skin: "#a87a52", hair: "#1a1612", hairLength: "short", coat: "#e2ded4", coatDark: "#aca698", trim: "#7a6a52", length: "knee",
  legs: "#cfc9bc", boots: "#8a7a62", tallBoots: true, belt: "#6a5238", cloak: "#d6d1c4", hat: "hood", hatCol: "#ece8de", hatCol2: "#b4ad9e", girth: 1,
};

const DROWNED_KIT: FolkKit = {
  skin: "#94a6a0", hair: "#3a4440", hairLength: "shoulder", beard: "#6a7a76", coat: "#5c6c72", coatDark: "#3c4a50", trim: "#c8d8de", length: "knee", mail: true,
  legs: "#2e3a3e", boots: "#262a2a", tallBoots: true, belt: "#4a3e30", cloak: "#2a3a44", hat: "hood", hatCol: "#3a4a54", hatCol2: "#222e36",
};

// 10. Drúwaith Iaur (2026-10-16): the Woses, the Drúedain, short and broad
// and brown as wood, bare-armed and bare-legged in skirts of grass; the
// wild ones with leaves in their hair, the Old Wose grey-headed under a
// cape of bark.
const WOSE_KIT: FolkKit = {
  skin: "#6e4c2e", hair: "#241a12", hairLength: "short", coat: "#5e6e30", coatDark: "#3e4c1e", trim: "#7a6a38", length: "knee",
  legs: "#6a4a2c", boots: "#4a3220", belt: "#3e2c18", bareArms: true, girth: 3, hat: "leaves", hatCol: "#4e6e28", hatCol2: "#7a2a1a",
};

const WOSE_ELDER_KIT: FolkKit = {
  skin: "#7a5838", hair: "#a8a296", hairLength: "shoulder", beard: "#b8b2a6", coat: "#6a7038", coatDark: "#4a5026", trim: "#8a7a48", length: "knee",
  legs: "#745438", boots: "#4e3824", belt: "#3e2c18", bareArms: true, girth: 3, cloak: "#5a4630",
};

const KINGSMAN_KIT: FolkKit = {
  skin: "#8a9a8c", hair: "#2a3230", hairLength: "shoulder", coat: "#6e8478", coatDark: "#46584e", trim: "#b8a868", length: "knee", mail: true,
  legs: "#34403a", boots: "#2a2a26", tallBoots: true, belt: "#5a4a30", cloak: "#2a4a44", hat: "rohan", hatCol: "#7a8e84", hatCol2: "#3a5a50",
};

// The Dead of Dunharrow (2026-10-16, site 7 of the empty areas' proposal),
// on the people's frame and half seen, as the Temple Shade is: a soldier of
// the Men of the Mountains in rotted mail under a hood gone grey, the
// colour of smoke; and their King a size up, darker, in a cloak, with a dim
// crown over the hood (see drawDeadKing).
const DEADMAN_KIT: FolkKit = {
  skin: "#a8b4b0", hair: "#4e5654", hairLength: "shoulder", beard: "#6a7472", coat: "#7c8a86", coatDark: "#545e5c", trim: "#9aa6a2", length: "knee", mail: true,
  legs: "#4e5856", boots: "#3a4240", tallBoots: true, belt: "#5a625e", hat: "hood", hatCol: "#6a7674", hatCol2: "#4a5452",
};

const DEADKING_KIT: FolkKit = {
  skin: "#b8c4c0", hair: "#3a4240", hairLength: "long", beard: "#8a9694", coat: "#4e5a58", coatDark: "#323a38", trim: "#8a9a96", length: "ground", mail: true,
  legs: "#323a38", boots: "#262c2a", belt: "#6a7472", cloak: "#2a3230", hat: "hood", hatCol: "#3e4846", hatCol2: "#262e2c",
};
const DEAD_KING_SCALE = 1.2;
/** How much of the Dead is seen: they are shapes in the dark, not bodies. */
const DEAD_ALPHA = 0.72;

/**
 * The King of the Dead: the Dead Man's frame a fifth as big again about its
 * feet, in DEADKING_KIT, with a crown of seven dim points over the hood and
 * a cold light in it. Scaled rather than redrawn, as the Witch-king is the
 * Nazgûl, so its grip in MONSTER_GRIP is the people's frame's scaled the
 * same way.
 */
function drawDeadKing(ctx: Ctx, dir: number, legOff: number, H: number, eyes: EyesFn) {
  ctx.save();
  ctx.translate(16, H);
  ctx.scale(DEAD_KING_SCALE, DEAD_KING_SCALE);
  ctx.translate(-16, -H);
  drawFolk(ctx, dir, legOff, H, eyes, DEADKING_KIT);
  const side = dir === 1 ? 1 : dir === 3 ? -1 : 0;
  const hx = 16 + side * 1.5, band = H - 43;
  ctx.fillStyle = DEAD_CROWN.iron;
  for (let i = 0; i < 7; i++) {
    const x = hx - 6 + i * 2;
    const up = i % 2 ? 2.5 : 4.5;
    ctx.beginPath();
    ctx.moveTo(x - 1, band);
    ctx.lineTo(x, band - up);
    ctx.lineTo(x + 1, band);
    ctx.closePath();
    ctx.fill();
  }
  ctx.fillRect(hx - 7, band, 14, 2);
  if (dir !== 0) {
    ctx.fillStyle = DEAD_CROWN.glow;
    ctx.fillRect(hx - 0.5, band + 0.5, 1, 1);
  }
  ctx.restore();
}

const TEMPLESHADE_KIT: FolkKit = {
  skin: "#9aa0a8", hair: "#5a5e66", hairLength: "long", coat: "#4a4e58", coatDark: "#30333a", trim: "#c86a2a", length: "ground",
  legs: "#30333a", boots: "#24262c", belt: "#3a3a42", hat: "hood", hatCol: "#3a3e48", hatCol2: "#24262c",
};

const CHARRED_KIT: FolkKit = {
  skin: "#2a221e", hair: "#141010", hairLength: "short", coat: "#3a2a22", coatDark: "#22180f", trim: "#c84a1a", length: "belt",
  legs: "#2a201a", boots: "#1a1410", belt: "#c84a1a", bareArms: true,
};

const HIGHPRIEST_KIT: FolkKit = {
  skin: "#c8b8a8", hair: "#1a1614", hairLength: "long", beard: "#2a2420", coat: "#1a1618", coatDark: "#0e0c0e", trim: "#b02a1a", length: "ground",
  legs: "#141214", boots: "#0e0c0c", belt: "#b02a1a", cloak: "#2a1214", hat: "hood", hatCol: "#1a1618", hatCol2: "#0e0c0e",
};

// The Blue Tower of Rhûn (2026-11-01): the cult's acolyte in faded blue
// under a hood, a bound shade the same shape in blue-black smoke, the
// Remade bare and grey with blue signs, and the Blue Wizard himself in
// deep sea-blue under a wizard's hat, white-bearded.
const ACOLYTE_KIT: FolkKit = {
  skin: "#b08a68", hair: "#1e1a18", hairLength: "shoulder", beard: "#1e1a18", coat: "#3e5a86", coatDark: "#2a3e5e", trim: "#8ab4e8", length: "ground",
  legs: "#2a3448", boots: "#1a1c22", belt: "#8ab4e8", hat: "hood", hatCol: "#3a5280", hatCol2: "#24344e",
};

const BOUNDSHADE_KIT: FolkKit = {
  skin: "#6a7ea8", hair: "#2a3450", hairLength: "long", coat: "#24304e", coatDark: "#141c30", trim: "#6aa0ff", length: "ground",
  legs: "#141c30", boots: "#0e1220", belt: "#24304e", hat: "hood", hatCol: "#1e2842", hatCol2: "#0e1220",
};

const REMADE_KIT: FolkKit = {
  skin: "#8a9096", hair: "#3a3e44", hairLength: "short", coat: "#4a4440", coatDark: "#2e2a26", trim: "#5aa0ff", length: "belt",
  legs: "#3a3430", boots: "#22201c", belt: "#5aa0ff", bareArms: true, girth: 2,
};

const BLUEWIZARD_KIT: FolkKit = {
  skin: "#d8b8a0", hair: "#d8dce2", hairLength: "long", beard: "#e4e8ec", coat: "#1e3e78", coatDark: "#132a56", trim: "#7ab0ff", length: "ground",
  legs: "#132a56", boots: "#141820", belt: "#7ab0ff", cloak: "#16305e", hat: "wizard", hatCol: "#1e3e78", hatCol2: "#132a56",
};

/** The warding stones in the Sanctum (2026-11-01): grey standing stones cut with blue signs. */
const WARD_STONE = { stone: "#5a5e66", dark: "#3a3d44", lit: "#7a7e88", sign: "#6ab0ff", glow: "rgba(90,160,255,0.35)" };

/** A warding stone: a tall grey standing stone, its signs pulsing blue with the walk frame's clock. */
function drawWardStone(ctx: Ctx, H: number) {
  const pulse = 0.6 + 0.4 * Math.sin(performance.now() / 400);
  ctx.fillStyle = "rgba(0,0,0,0.3)"; // its shadow
  ctx.beginPath();
  ctx.ellipse(16, H - 2, 10, 3, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = WARD_STONE.dark;
  ctx.beginPath();
  ctx.moveTo(8, H - 2);
  ctx.lineTo(10, H - 34);
  ctx.quadraticCurveTo(16, H - 40, 22, H - 34);
  ctx.lineTo(24, H - 2);
  ctx.closePath();
  ctx.fill();
  ctx.fillStyle = WARD_STONE.stone; // the lit face
  ctx.beginPath();
  ctx.moveTo(11, H - 3);
  ctx.lineTo(12, H - 33);
  ctx.quadraticCurveTo(16, H - 37, 20, H - 33);
  ctx.lineTo(21, H - 3);
  ctx.closePath();
  ctx.fill();
  ctx.fillStyle = WARD_STONE.lit;
  ctx.fillRect(12, H - 32, 2, 26);
  ctx.globalAlpha *= pulse;
  ctx.fillStyle = WARD_STONE.glow;
  ctx.beginPath();
  ctx.arc(16, H - 20, 9, 0, Math.PI * 2);
  ctx.fill();
  ctx.strokeStyle = WARD_STONE.sign; // the signs: two staves crossed under a star
  ctx.lineWidth = 1.2;
  ctx.beginPath();
  ctx.moveTo(13, H - 12);
  ctx.lineTo(19, H - 22);
  ctx.moveTo(19, H - 12);
  ctx.lineTo(13, H - 22);
  ctx.moveTo(16, H - 30);
  ctx.lineTo(16, H - 24);
  ctx.moveTo(13, H - 27);
  ctx.lineTo(19, H - 27);
  ctx.stroke();
  ctx.globalAlpha /= pulse;
}

// The Wood-elves of Mirkwood and their king (2026-10-07: "propose some
// pathways through the mirkwood forest, and the elf-kings halls, with
// hostile elf monsters", then "Implement it"), each with an elf's pointed
// ears. The archer in a hooded jerkin of leaf-green over brown, hard to
// see among the trees; the warden the other way about, brown under a dark
// green hood, with a spear; the Elvenking's guard in silver mail under a
// green cloak and a tall pointed helm; the king in a robe of deep red and
// gold to the ground with a crown of leaves and red berries on his pale
// gold hair; and Galion, his butler, in wine-dark purple with a cellarer's
// apron, his hair untidy.
// The Ranger of the North at the ford at Tharbad (2026-10-10): weathered
// grey-green, hooded, a beard, a bow on his back and boots to the knee.
const RANGER_KIT: FolkKit = {
  skin: "#c8a07c", hair: "#2a221a", hairLength: "shoulder", beard: "#2e2620", coat: "#4e5a4a", coatDark: "#343e32", trim: "#c8ccd0", length: "knee",
  legs: "#3a3428", boots: "#2a2016", tallBoots: true, belt: "#5a4228", cloak: "#3a463a", hat: "hood", hatCol: "#465246", hatCol2: "#2e382e", bow: true,
};

/**
 * Halbarad of the Dúnedain (2026-10-16, the Rangers' camp): the Ranger's
 * kit darker and more worn, mail under the coat, the hood back off a dark
 * head going grey, a bow on his back, and the silver star at his shoulder
 * (drawn over the kit by drawHalbarad).
 */
const HALBARAD_KIT: FolkKit = {
  skin: "#c09474", hair: "#3a3430", hairLength: "shoulder", beard: "#5a5450", coat: "#46503f", coatDark: "#2e352a", trim: "#9aa0a6", length: "knee",
  legs: "#33302a", boots: "#241c14", tallBoots: true, belt: "#4a3622", cloak: "#5e665c", mail: true, bow: true,
};

/** Halbarad: his kit, and the star of the Dúnedain on the near shoulder. */
function drawHalbarad(ctx: Ctx, dir: number, legOff: number, H: number, eyes: EyesFn) {
  drawFolk(ctx, dir, legOff, H, eyes, HALBARAD_KIT);
  if (dir === 0) return; // from behind the brooch is under the cloak's fold
  const side = dir === 1 ? 1 : dir === 3 ? -1 : 0;
  drawDunedainStar(ctx, side ? 16 - side * 1.5 : 11.5, H - 27, 1.8);
}

/**
 * Elladan son of Elrond (2026-11-01, the Troll-stones): dark-haired and
 * fair, bright mail under a grey cloak, an elf's ears, a long bow on his
 * back.
 */
const ELLADAN_KIT: FolkKit = {
  skin: "#e6c8aa", hair: "#16120e", hairLength: "long", coat: "#5a6670", coatDark: "#3c464e", trim: "#d8dee4", length: "knee",
  legs: "#3a3e42", boots: "#262a2e", tallBoots: true, belt: "#5a4630", cloak: "#7a828a", mail: true, bow: true, ears: true,
};

/**
 * Léod of the Éothéod (2026-11-01, Scatha's lair): a fair-haired horseman
 * of the north, green cloak over a shirt of rings, a gold-trimmed coat,
 * bareheaded.
 */
const LEOD_KIT: FolkKit = {
  skin: "#e2b892", hair: "#e0c060", hairLength: "long", beard: "#d0aa50", coat: "#4e6a34", coatDark: "#344822", trim: "#d4b44a", length: "knee",
  legs: "#5a4a32", boots: "#3a2a1a", tallBoots: true, belt: "#6a4a2a", cloak: "#2e5a2a", mail: true,
};
// --- Site 6: Henneth Annûn (2026-10-16) ---
/** Faramir (outfit 125): the rangers of Ithilien's green and brown, bareheaded, a bow on his back. */
const FARAMIR_KIT: FolkKit = {
  skin: "#d8b090", hair: "#1e1812", hairLength: "shoulder", coat: "#4a5e30", coatDark: "#33421f", trim: "#c9b27a", length: "knee",
  legs: "#4a3a26", boots: "#2e2216", tallBoots: true, belt: "#6a4a2a", cloak: "#5a4a2e", bow: true,
};

// Gamling the Old, who keeps the refuge in the Glittering Caves under
// Helm's Deep (2026-10-22): a Rider of the Westfold grown old, white of
// hair and beard, in mail with the green cloak and the Rohirric helm.
const GAMLING_KIT: FolkKit = {
  skin: "#e2bf9c", hair: "#e8e4dc", hairLength: "shoulder", beard: "#eeeae2", coat: "#7c838a", coatDark: "#555b62", trim: "#c8a248", length: "knee",
  legs: "#3a4a2a", boots: "#4a3420", tallBoots: true, belt: "#6a4a26", cloak: "#2e5a30", hat: "rohan", hatCol: "#a4a8ae", hatCol2: "#e8e4dc", mail: true,
};
// --- end site 6 ---

const WOODELF_ARCHER_KIT: FolkKit = {
  skin: "#ecd4b8", hair: "#8a6236", hairLength: "long", coat: "#4a7a32", coatDark: "#2e5220", trim: "#7a5430", length: "knee",
  legs: "#5a4228", boots: "#3a2814", tallBoots: true, belt: "#6a4622", cloak: "#3a5a26", hat: "hood", hatCol: "#3e6a2a", hatCol2: "#2a481c", ears: true,
};

const WOODELF_WARDEN_KIT: FolkKit = {
  skin: "#e8d0b4", hair: "#5a3a1e", hairLength: "long", coat: "#6e5232", coatDark: "#4a3620", trim: "#3e6a2a", length: "knee",
  legs: "#3e4a28", boots: "#2e2214", tallBoots: true, belt: "#3e6a2a", cloak: "#2a4a22", hat: "hood", hatCol: "#2a4a22", hatCol2: "#1a3214", ears: true,
};

const ELVEN_GUARD_KIT: FolkKit = {
  skin: "#ecd6bc", hair: "#c8a050", hairLength: "long", coat: "#aab2ba", coatDark: "#6a727a", trim: "#2e6a34", length: "knee", mail: true,
  legs: "#2a4a2c", boots: "#3a2a1a", tallBoots: true, belt: "#2e6a34", cloak: "#2a5a2e", hat: "elfhelm", hatCol: "#c8ced6", hatCol2: "#2e6a34", ears: true,
};

const THRANDUIL_KIT: FolkKit = {
  skin: "#f2dec8", hair: "#ecd48a", hairLength: "long", coat: "#8a1c1c", coatDark: "#5a1010", trim: "#e0b040", length: "ground",
  legs: "#5a1010", boots: "#3a2414", belt: "#e0b040", cloak: "#b07a26", hat: "leaves", hatCol: "#4e8a2a", hatCol2: "#d0202a", ears: true,
};

// 9. Dorwinion (2026-10-16): the vintner, a broad brown man of Rhûn in a
// wine-red shirt, bare-armed for the vats, a leather apron and a kerchief.
const VINTNER_KIT: FolkKit = {
  skin: "#b8865e", hair: "#22160e", hairLength: "short", beard: "#2a1c12", coat: "#7a2234", coatDark: "#561626", trim: "#d0a050", length: "belt",
  legs: "#4a3a28", boots: "#3a2616", belt: "#5a3a1e", apron: "#8a5a32", girth: 2, bareArms: true, hat: "kerchief", hatCol: "#6a1e30",
};
// end of 9. Dorwinion

// 5. Rhosgobel (2026-10-16): Radagast the Brown, in earth-brown robes to
// the ground, a long brown beard and a wide-brimmed wizard's hat with a
// little bird on its brim.
const RADAGAST_KIT: FolkKit = {
  skin: "#dcb08a", hair: "#6a5038", hairLength: "shoulder", beard: "#7a5a3a", coat: "#7a5a36", coatDark: "#5a4026", trim: "#a08a5a", length: "ground",
  legs: "#5a4026", boots: "#3a2a18", belt: "#4a6a2a", cloak: "#5e4428", hat: "wizard", hatCol: "#6a4e30", hatCol2: "#c06a3a",
};
// end of 5. Rhosgobel

const GALION_KIT: FolkKit = {
  skin: "#eed4bc", hair: "#b08a52", hairLength: "shoulder", coat: "#5a2e4a", coatDark: "#3e1e32", trim: "#c8a060", length: "knee",
  legs: "#3a2a22", boots: "#2a1e16", belt: "#7a5430", apron: "#d8ccb0", girth: 1, ears: true,
};

/**
 * One of the big folk — a man or a woman — in their own clothes on the one
 * frame: Barliman fat in an apron, Tom in his blue jacket and yellow boots
 * with a feather in his hat, Bard with his bow on his back, Éomer in mail
 * and green under a horsetail helm, Ioreth in grey to the ground and a
 * white headcloth, Imrahil in blue with the swan of his house. What tells
 * them apart at a glance is the head and the coat, as with the men of the
 * south (see drawMan).
 */
function drawFolk(ctx: Ctx, dir: number, legOff: number, H: number, eyes: EyesFn, k: FolkKit) {
  const side = dir === 1 ? 1 : dir === 3 ? -1 : 0;
  const front = dir === 2;
  const back = dir === 0;
  const swing = legOff / 2;
  const g = k.girth ?? 0;
  const hx = 16 + side * 1.5;
  const hy = H - 35;
  // The cloak, behind everything but what is on the back.
  const cloak = () => {
    if (!k.cloak) return;
    ctx.fillStyle = k.cloak;
    ctx.beginPath();
    ctx.moveTo(9, H - 30);
    ctx.lineTo(23, H - 30);
    ctx.lineTo(back ? 25 : 24, H - 6);
    ctx.lineTo(back ? 7 : 8, H - 6);
    ctx.closePath();
    ctx.fill();
  };
  const bow = () => {
    if (!k.bow) return;
    // A yew bow across the back, and the quiver's fletchings over a shoulder.
    ctx.strokeStyle = "#7a4a22";
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.moveTo(7, H - 31);
    ctx.quadraticCurveTo(back ? 14 : 18, H - 19, 25, H - 6);
    ctx.stroke();
    ctx.strokeStyle = "#d8d0b8";
    ctx.lineWidth = 0.6;
    ctx.beginPath();
    ctx.moveTo(7, H - 31);
    ctx.lineTo(25, H - 6);
    ctx.stroke();
    ctx.fillStyle = "#5a3a20";
    ctx.fillRect(back ? 18 : 20, H - 38, 3, back ? 14 : 4);
    ctx.fillStyle = "#e8e0d0";
    ctx.fillRect(back ? 18 : 20, H - 40, 1, 2);
    ctx.fillRect(back ? 20 : 22, H - 40, 1, 2);
  };
  if (!back) {
    cloak();
    if (side) bow();
  }
  // Legs and boots, under a dress or a robe nothing.
  if (k.length !== "ground") {
    ctx.fillStyle = k.legs;
    ctx.fillRect(10, H - 13 + legOff, 5, 9);
    ctx.fillRect(17, H - 13 - legOff, 5, 9);
    ctx.fillStyle = k.boots;
    const bootH = k.tallBoots ? 8 : 5;
    ctx.fillRect(9 + side, H - bootH + legOff, 6, bootH);
    ctx.fillRect(17 + side, H - bootH - legOff, 6, bootH);
  } else {
    ctx.fillStyle = k.boots; // shoes peeping out
    ctx.fillRect(10 + side, H - 2 + legOff / 2, 5, 2);
    ctx.fillRect(17 + side, H - 2 - legOff / 2, 5, 2);
  }
  // The coat, gown or tunic.
  ctx.fillStyle = back ? k.coatDark : k.coat;
  if (k.length === "ground") {
    ctx.beginPath();
    ctx.moveTo(10 - g, H - 29);
    ctx.lineTo(22 + g, H - 29);
    ctx.lineTo(24 + g, H - 2);
    ctx.lineTo(8 - g, H - 2);
    ctx.closePath();
    ctx.fill();
  } else {
    ctx.fillRect(10 - g, H - 29, 12 + 2 * g, k.length === "knee" ? 21 : 15 + g);
  }
  if (k.mail && !back) {
    ctx.fillStyle = k.coatDark;
    for (let y = H - 27; y < H - 9; y += 2) {
      for (let x = 10 + ((y & 2) ? 1 : 0); x < 22; x += 2) ctx.fillRect(x, y, 1, 1);
    }
  }
  if (k.length === "knee") {
    ctx.fillStyle = k.trim;
    ctx.fillRect(10, H - 9, 12, 1);
  }
  if (!back && !k.apron) {
    ctx.fillStyle = k.coatDark;
    if (front) ctx.fillRect(15.5, H - 29, 1, 13);
    else ctx.fillRect(side > 0 ? 20 : 11, H - 29, 1, 13);
  }
  if (k.apron && !back) {
    ctx.fillStyle = k.apron;
    if (front) ctx.fillRect(11 - g / 2, H - 22, 10 + g, k.length === "ground" ? 18 : 12 + g);
    else ctx.fillRect(side > 0 ? 17 + g / 2 : 9 - g, H - 22, 6 + g / 2, k.length === "ground" ? 18 : 12 + g);
  }
  if (k.swan && !back) {
    // The swan of Dol Amroth: white on the blue, wings up.
    const sx = front ? 16 : 16 + side * 2;
    ctx.fillStyle = k.trim;
    ctx.fillRect(sx - 3, H - 22, 6, 2);
    ctx.fillRect(sx - 4, H - 25, 2, 3);
    ctx.fillRect(sx + 2, H - 25, 2, 3);
    ctx.fillRect(sx + 1, H - 24, 1, 3);
    ctx.fillRect(sx + 1, H - 26, 2, 1);
  }
  ctx.fillStyle = k.belt;
  ctx.fillRect(10 - g, H - 16, 12 + 2 * g, 2);
  if (k.trim && k.length === "belt" && !back && !k.apron) {
    ctx.fillStyle = k.trim; // buttons down the front of a jacket
    for (const y of [26, 22, 19]) ctx.fillRect(front ? 15.5 : side > 0 ? 19 : 12, H - y, 1, 1);
  }
  // Arms, sleeved or bare, with hands.
  const arm = (x: number, dy: number) => {
    ctx.fillStyle = k.bareArms ? k.skin : back ? k.coatDark : k.coat;
    ctx.fillRect(x, H - 29 + dy, 4, 12);
    if (k.mail) {
      ctx.fillStyle = k.coatDark;
      ctx.fillRect(x, H - 25 + dy, 4, 1);
      ctx.fillRect(x, H - 21 + dy, 4, 1);
    }
    ctx.fillStyle = k.skin;
    ctx.fillRect(x, H - 17 + dy, 4, 3);
  };
  arm(6 - g, -swing);
  arm(22 + g, swing);
  // The head.
  ctx.fillStyle = k.skin;
  ctx.beginPath();
  ctx.arc(hx, hy, 6, 0, Math.PI * 2);
  ctx.fill();
  // An elf's pointed ears, as a player elf's are drawn and scaled to this
  // smaller head: both from in front and from behind, standing out past
  // the side of the head and up, before the hair that sits over their
  // roots. In profile see below, after the hair.
  if (k.ears && !side) {
    for (const s of [-1, 1] as const) {
      const x = hx + s * 5.2;
      ctx.beginPath();
      ctx.moveTo(x, hy - 2.6);
      ctx.lineTo(x + s * 3.8, hy - 5.6);
      ctx.lineTo(x + s * 1.7, hy + 0.9);
      ctx.lineTo(x, hy + 1.7);
      ctx.fill();
    }
  }
  // Hair.
  ctx.fillStyle = k.hair;
  if (back) {
    ctx.beginPath();
    ctx.arc(hx, hy - 0.5, 6.4, 0, Math.PI * 2);
    ctx.fill();
    if (k.hairLength !== "short") ctx.fillRect(hx - 5.5, hy, 11, k.hairLength === "long" ? 16 : 8);
  } else {
    ctx.beginPath();
    ctx.arc(hx, hy - 1.5, 6.4, Math.PI, Math.PI * 2);
    ctx.fill();
    if (k.hairLength !== "short") {
      for (const x of side ? [-side * 5.5] : [-6.5, 4.5]) ctx.fillRect(hx + x, hy - 2, 2, k.hairLength === "long" ? 16 : 8);
    }
    if (k.hairLength === "short" && k.hat !== "kerchief") {
      // Short hair going thin on top: the innkeeper's.
      ctx.fillStyle = k.skin;
      ctx.fillRect(hx - 2 + side, hy - 7, 4, 2);
    }
  }
  // Side on, the one ear that shows is behind the middle of the head and
  // sweeps back and up through the hair, as a player elf's does (never on
  // the face: see TestAnElfsEarIsBehindItsFaceInProfile).
  if (k.ears && side) {
    const b = -side;
    ctx.fillStyle = k.skin;
    ctx.beginPath();
    ctx.moveTo(hx + b * 0.9, hy - 1.3);
    ctx.lineTo(hx + b * 9, hy - 6);
    ctx.lineTo(hx + b * 4.7, hy + 1.7);
    ctx.lineTo(hx + b * 1.7, hy + 2.1);
    ctx.fill();
    ctx.fillStyle = EAR_SHADE;
    ctx.beginPath();
    ctx.moveTo(hx + b * 2.1, hy - 0.4);
    ctx.lineTo(hx + b * 6.9, hy - 4.3);
    ctx.lineTo(hx + b * 3.9, hy + 0.9);
    ctx.fill();
  }
  if (k.beard && !back) {
    ctx.fillStyle = k.beard;
    const bx = hx + side * 2;
    ctx.beginPath();
    ctx.moveTo(bx - 4, hy + 1);
    ctx.lineTo(bx + 4, hy + 1);
    ctx.lineTo(bx + 3, hy + 6);
    ctx.lineTo(bx, hy + 8);
    ctx.lineTo(bx - 3, hy + 6);
    ctx.closePath();
    ctx.fill();
  }
  switch (k.hat) {
    case "feather": {
      // Tom's battered hat: a tall crown, a wide brim, and a long blue
      // feather stuck in the band.
      ctx.fillStyle = k.hatCol!;
      ctx.fillRect(hx - 9, hy - 6, 18, 2);
      ctx.fillRect(hx - 5, hy - 12, 10, 6);
      ctx.fillStyle = "#3a2e1e";
      ctx.fillRect(hx - 5, hy - 8, 10, 1.5);
      ctx.strokeStyle = k.hatCol2!;
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      const fx = hx + (side ? -side * 4 : 4);
      ctx.moveTo(fx, hy - 8);
      ctx.quadraticCurveTo(fx + (side ? -side * 3 : 4), hy - 14, fx + (side ? -side * 7 : 7), hy - 17);
      ctx.stroke();
      break;
    }
    case "rohan": {
      // A tall steel helm, rimmed in gold, with a white horsetail from the
      // crest streaming down behind.
      ctx.fillStyle = k.hatCol!;
      ctx.beginPath();
      ctx.moveTo(hx - 6.5, hy - 2);
      ctx.quadraticCurveTo(hx - 6, hy - 10, hx, hy - 11);
      ctx.quadraticCurveTo(hx + 6, hy - 10, hx + 6.5, hy - 2);
      ctx.closePath();
      ctx.fill();
      ctx.fillStyle = "#d8b040";
      ctx.fillRect(hx - 6.5, hy - 3, 13, 1);
      if (!back) ctx.fillRect(hx - 0.5 + side * 2, hy - 3, 1, 5); // the nasal
      ctx.fillStyle = k.hatCol2!;
      ctx.fillRect(hx - 1, hy - 13, 2, 3);
      const tx = side ? hx - side * 2 : hx;
      ctx.beginPath();
      ctx.moveTo(tx - 1, hy - 12);
      ctx.quadraticCurveTo(tx - side * 9 - (side ? 0 : 1), hy - 10, tx - side * 8, hy + (back ? 8 : 6));
      ctx.lineTo(tx - side * 6 + 2, hy + 6);
      ctx.quadraticCurveTo(tx - side * 4 + 1, hy - 8, tx + 1, hy - 12);
      ctx.closePath();
      ctx.fill();
      break;
    }
    case "kerchief": {
      // A white headcloth over the grey, tied under the chin.
      ctx.fillStyle = k.hatCol!;
      ctx.beginPath();
      ctx.arc(hx, hy - 1, 7, Math.PI, Math.PI * 2);
      ctx.fill();
      ctx.fillRect(hx - 7, hy - 1, 2.5, 7);
      ctx.fillRect(hx + 4.5, hy - 1, 2.5, 7);
      if (back) ctx.fillRect(hx - 7, hy - 1, 14, 8);
      ctx.fillStyle = k.hatCol2!;
      ctx.fillRect(hx - 7, hy - 2, 14, 1);
      break;
    }
    case "swan": {
      // The helm of Dol Amroth: silver, with a swan's wing swept up on
      // either side.
      ctx.fillStyle = k.hatCol!;
      ctx.beginPath();
      ctx.arc(hx, hy - 2, 6.6, Math.PI, Math.PI * 2);
      ctx.fill();
      ctx.fillRect(hx - 6.6, hy - 3, 13.2, 2);
      ctx.fillStyle = k.hatCol2!;
      const wing = (s: number) => {
        ctx.beginPath();
        ctx.moveTo(hx + s * 5, hy - 4);
        ctx.quadraticCurveTo(hx + s * 10, hy - 8, hx + s * 8, hy - 15);
        ctx.quadraticCurveTo(hx + s * 7, hy - 9, hx + s * 3, hy - 7);
        ctx.closePath();
        ctx.fill();
      };
      if (side) wing(-side);
      else {
        wing(-1);
        wing(1);
      }
      break;
    }
    case "hood": {
      // A Wood-elf's hood: a cowl round the head and down to the
      // shoulders, its point falling behind, the face open in it and the
      // tips of the ears out past its edge.
      ctx.fillStyle = k.hatCol!;
      ctx.beginPath();
      ctx.arc(hx, hy - 1, 6.9, Math.PI, Math.PI * 2);
      ctx.fill();
      if (back) {
        ctx.fillRect(hx - 6.9, hy - 1, 13.8, 8);
      } else if (side) {
        ctx.fillRect(hx - side * 6.9 - (side > 0 ? 0 : 3.5), hy - 1, 3.5, 7);
      } else {
        ctx.fillRect(hx - 6.9, hy - 1, 2, 7);
        ctx.fillRect(hx + 4.9, hy - 1, 2, 7);
      }
      ctx.fillStyle = k.hatCol2!; // the hem round the face, and the point behind
      if (!back) ctx.fillRect(hx - 5 + side * 1.5, hy - 5.5, 10, 1);
      ctx.beginPath();
      const px = side ? hx - side * 6 : hx;
      ctx.moveTo(px - 2, hy - 7);
      ctx.lineTo(px + (side ? -side * 5 : 0), hy - 10 + (side ? 6 : 0));
      ctx.lineTo(px + 2, hy - 7);
      ctx.closePath();
      ctx.fill();
      break;
    }
    case "elfhelm": {
      // The Elvenking's guard's helm: tall, pointed and silver, with a
      // green crest of leaves down its ridge and cheek-guards to the jaw.
      ctx.fillStyle = k.hatCol!;
      ctx.beginPath();
      ctx.moveTo(hx - 6.6, hy - 1);
      ctx.quadraticCurveTo(hx - 6, hy - 10, hx, hy - 15);
      ctx.quadraticCurveTo(hx + 6, hy - 10, hx + 6.6, hy - 1);
      ctx.closePath();
      ctx.fill();
      if (!back) {
        ctx.fillRect(hx - 6.6 + (side > 0 ? 2 : 0), hy - 1, 2, 5);
        if (!side) ctx.fillRect(hx + 4.6, hy - 1, 2, 5);
      }
      ctx.fillStyle = "#8a929c";
      ctx.fillRect(hx - 6.6, hy - 2, 13.2, 1);
      ctx.fillStyle = k.hatCol2!;
      for (let i = 0; i < 4; i++) {
        ctx.beginPath();
        ctx.ellipse(hx + (side ? -side * i * 1.2 : 0), hy - 13 + i * 2.6, 1.1, 1.8, side ? -side * 0.6 : 0, 0, Math.PI * 2);
        ctx.fill();
      }
      break;
    }
    case "leaves": {
      // The Elvenking's crown: leaves of the wood in a ring round his
      // head, berries red among them (in the autumn of The Hobbit he was
      // crowned with berries and red leaves; this is the green of the
      // woodland with the berries kept).
      ctx.fillStyle = k.hatCol!;
      const ring = side ? [-5, -3, -1, 1, 3, 5] : [-6, -4, -2, 0, 2, 4, 6];
      for (const dx of ring) {
        ctx.beginPath();
        ctx.ellipse(hx + dx, hy - 5.5 - (dx === 0 ? 1.5 : Math.abs(dx) < 3 ? 1 : 0), 1.3, 2.4, dx * 0.12, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.fillStyle = k.hatCol2!;
      for (const dx of side ? [-4, 0, 4] : [-5, -1, 3, 6]) {
        ctx.beginPath();
        ctx.arc(hx + dx * 0.9, hy - 4.6, 0.9, 0, Math.PI * 2);
        ctx.fill();
      }
      break;
    }
    // 5. Rhosgobel (2026-10-16).
    case "wizard": {
      // Radagast's hat: a wide brim and a tall, battered crown whose point
      // flops over, and a little bird (a robin: hatCol2 its breast) on the
      // brim.
      ctx.fillStyle = k.hatCol!;
      ctx.beginPath();
      ctx.ellipse(hx, hy - 4, 10, 2.4, 0, 0, Math.PI * 2);
      ctx.fill();
      const lean = side ? -side * 3 : 3;
      ctx.beginPath();
      ctx.moveTo(hx - 5, hy - 5);
      ctx.quadraticCurveTo(hx - 3, hy - 13, hx + lean * 0.6, hy - 16);
      ctx.lineTo(hx + lean * 1.8, hy - 14);
      ctx.quadraticCurveTo(hx + 3, hy - 11, hx + 5, hy - 5);
      ctx.closePath();
      ctx.fill();
      ctx.fillStyle = "#4a3420"; // the band
      ctx.fillRect(hx - 5, hy - 7, 10, 1.5);
      if (!back) {
        const bx = hx + (side ? side * 6 : 6);
        ctx.fillStyle = "#6a5a48"; // the bird's back
        ctx.beginPath();
        ctx.ellipse(bx, hy - 7, 2, 1.5, 0, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = k.hatCol2!; // its red breast
        ctx.beginPath();
        ctx.arc(bx + (side || 1) * 1, hy - 6.6, 1, 0, Math.PI * 2);
        ctx.fill();
      }
      break;
    }
    // end of 5. Rhosgobel
  }
  if (!back) {
    eyes(hx + side * 2, hy - 1, "#2a2a3a");
    if (front && !k.beard) {
      ctx.fillStyle = "#b0705a";
      ctx.fillRect(hx - 1.5, hy + 3, 3, 1);
    }
  }
  if (back) {
    cloak();
    bow();
  } else if (!side) bow();
}

/** How much bigger Beorn is drawn than a man. */
const BEORN_SCALE = 1.35;

/** Beorn's clothes: a coarse woollen tunic to the knee, bare arms and legs. */
const BEORN_KIT: FolkKit = {
  skin: "#c89870", hair: "#141210", hairLength: "shoulder", beard: "#141210", coat: "#6a5a3e", coatDark: "#4a3e2a", trim: "#8a7a5a", length: "knee",
  legs: "#c89870", boots: "#3a2a1a", belt: "#2a2018", bareArms: true,
};

/**
 * Beorn: a man a third as big again about his feet as any other, with a
 * great black beard and black hair, bare arms knotted with muscle, in a
 * woollen tunic to the knee and a bearskin over his shoulders. Scaled, as
 * the hill troll is, and BIG_OUTFITS gives him the room.
 */
function drawBeorn(ctx: Ctx, dir: number, legOff: number, H: number, eyes: EyesFn) {
  const back = dir === 0;
  const side = dir === 1 ? 1 : dir === 3 ? -1 : 0;
  ctx.save();
  ctx.translate(16, H);
  ctx.scale(BEORN_SCALE, BEORN_SCALE);
  ctx.translate(-16, -H);
  drawFolk(ctx, dir, legOff, H, eyes, BEORN_KIT);
  // The beard, bigger than a man's: to the middle of the chest.
  if (!back) {
    ctx.fillStyle = "#141210";
    const bx = 16 + side * 3.5;
    ctx.beginPath();
    ctx.moveTo(bx - 5, H - 34);
    ctx.lineTo(bx + 5, H - 34);
    ctx.lineTo(bx + 4, H - 24);
    ctx.lineTo(bx, H - 20);
    ctx.lineTo(bx - 4, H - 24);
    ctx.closePath();
    ctx.fill();
  }
  // The bearskin over his shoulders, the head of it for a hood behind.
  ctx.fillStyle = "#3a2a1c";
  ctx.beginPath();
  ctx.ellipse(16, H - 29, 10, 3, 0, 0, Math.PI * 2);
  ctx.fill();
  if (back) {
    ctx.fillRect(8, H - 29, 16, 14);
    ctx.fillStyle = "#2a1e14";
    ctx.beginPath();
    ctx.arc(16, H - 30, 4, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.restore();
}

/**
 * Galadriel: the Lady of Lórien, as tall as Elrond and slighter, in a white
 * gown to the ground that flares at the hem, a silver girdle, sleeves that
 * hang wide, golden hair loose to the waist and a fillet of silver on her
 * brow. White and silver and gold, and nothing else in the world is.
 */
function drawGaladriel(ctx: Ctx, dir: number, legOff: number, H: number, eyes: EyesFn) {
  const side = dir === 1 ? 1 : dir === 3 ? -1 : 0;
  const front = dir === 2;
  const back = dir === 0;
  const GOWN = "#f4f4f8", GOWN_D = "#d4d8e4", SILVER = "#c8d0dc", HAIR = "#e8c860", HAIR_D = "#c8a440", SKIN = "#f0e0d0";
  const hx = 16 + side * 1.5;
  const hy = H - 37;
  // Her hair down her back, seen from behind before all else.
  const hairBack = () => {
    ctx.fillStyle = HAIR;
    ctx.beginPath();
    ctx.moveTo(hx - 6, hy);
    ctx.lineTo(hx + 6, hy);
    ctx.lineTo(hx + 6.5, H - 18);
    ctx.quadraticCurveTo(hx, H - 15, hx - 6.5, H - 18);
    ctx.closePath();
    ctx.fill();
    ctx.fillStyle = HAIR_D;
    for (const x of [-3, 0, 3]) ctx.fillRect(hx + x, hy + 4, 1, 12);
  };
  // The gown, flaring to the ground and over the feet.
  ctx.fillStyle = back ? GOWN_D : GOWN;
  ctx.beginPath();
  ctx.moveTo(11, H - 31);
  ctx.lineTo(21, H - 31);
  ctx.lineTo(26, H - 1);
  ctx.quadraticCurveTo(16, H + 1 + legOff / 2, 6, H - 1);
  ctx.closePath();
  ctx.fill();
  ctx.fillStyle = GOWN_D;
  for (const x of [12, 16, 20]) ctx.fillRect(x + side, H - 18, 1, 16);
  ctx.fillStyle = SILVER; // the girdle, its ends hanging
  ctx.fillRect(11, H - 23, 10, 1.5);
  if (!back) ctx.fillRect(front ? 15.5 : 16 + side * 3, H - 22, 1, 8);
  // Sleeves, wide and hanging, with her hands.
  const sleeve = (x: number, dy: number) => {
    ctx.fillStyle = back ? GOWN_D : GOWN;
    ctx.beginPath();
    ctx.moveTo(x + 1, H - 30 + dy);
    ctx.lineTo(x + 4, H - 30 + dy);
    ctx.lineTo(x + 6, H - 15 + dy);
    ctx.lineTo(x - 2, H - 15 + dy);
    ctx.closePath();
    ctx.fill();
    ctx.fillStyle = SILVER;
    ctx.fillRect(x - 2, H - 16 + dy, 8, 1);
    ctx.fillStyle = SKIN;
    ctx.fillRect(x + 0.5, H - 15 + dy, 3, 2.5);
  };
  sleeve(6, legOff * 0.3);
  sleeve(22, -legOff * 0.3);
  if (back) hairBack();
  // The face, and her hair framing it and falling past the shoulders.
  ctx.fillStyle = SKIN;
  ctx.beginPath();
  ctx.arc(hx, hy, 5, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = HAIR;
  ctx.beginPath();
  ctx.arc(hx, hy - 0.5, 5.6, Math.PI, Math.PI * 2);
  ctx.fill();
  if (back) {
    ctx.beginPath();
    ctx.arc(hx, hy, 5.6, 0, Math.PI * 2);
    ctx.fill();
  } else {
    for (const x of side ? [-side * 5] : [-6, 4]) {
      ctx.fillRect(hx + x, hy - 1, 2, 16);
    }
    ctx.fillStyle = HAIR_D;
    for (const x of side ? [-side * 4.5] : [-5.5, 4.5]) ctx.fillRect(hx + x, hy + 3, 1, 11);
  }
  ctx.fillStyle = "#f8fbff"; // the silver fillet, and a white stone in it
  ctx.fillRect(hx - 5.5, hy - 3, 11, 1);
  if (front) {
    ctx.fillStyle = "#e8f4ff";
    ctx.fillRect(hx - 0.5, hy - 3.5, 1.5, 1.5);
  }
  if (!back) eyes(hx + side * 2, hy, "#3a5a8a");
}

/**
 * Treebeard: an Ent, fourteen feet of him, which is two people and more.
 * A man's shape grown of a tree: two legs like trunks with toes like roots,
 * a body of grey-brown bark, long arms with seven twig-fingers, a head
 * with a crown of leafy twigs, and a long grey-green beard of moss and
 * twigs to his middle. His eyes are brown, with a green light in them.
 * Not the huorn, which is a tree that walks (see drawHuorn): he has a face
 * and a beard and the shape of somebody. BIG_OUTFITS gives him the room.
 *
 * The Ents of Fangorn and Quickbeam (2026-10-21: "implement ents") are
 * drawn by the same hand in their own bark and leaves (ENT_LOOK,
 * QUICKBEAM_LOOK): an Ent's beard is shorter than the eldest's, and
 * Quickbeam, a rowan-ent, has none, smooth grey-green bark and rowan
 * berries in his crown.
 */
type EntLook = { bark: string; barkD: string; barkL: string; moss: string; mossD: string; leaf: string; leafL: string; beard: number; berries?: string };
const TREEBEARD_LOOK: EntLook = { bark: "#6a5e4a", barkD: "#4a4032", barkL: "#8a7c64", moss: "#7a8a5a", mossD: "#56663e", leaf: "#3e6a2e", leafL: "#5a8a3e", beard: 1 };
const ENT_LOOK: EntLook = { bark: "#5a4e3c", barkD: "#3c3226", barkL: "#7a6c52", moss: "#6a7a4a", mossD: "#4a5a34", leaf: "#2e5a26", leafL: "#4a7a34", beard: 0.55 };
const QUICKBEAM_LOOK: EntLook = { bark: "#7a7e6a", barkD: "#585c4a", barkL: "#9a9e88", moss: "#7a8a5a", mossD: "#56663e", leaf: "#4a7a36", leafL: "#6a9a46", beard: 0, berries: "#d0402a" };
function drawTreebeard(ctx: Ctx, dir: number, legOff: number, H: number, look: EntLook = TREEBEARD_LOOK) {
  const side = dir === 1 ? 1 : dir === 3 ? -1 : 0;
  const front = dir === 2;
  const back = dir === 0;
  const BARK = look.bark, BARK_D = look.barkD, BARK_L = look.barkL, MOSS = look.moss, MOSS_D = look.mossD, LEAF = look.leaf, LEAF_L = look.leafL;
  const swing = legOff;
  // Legs like trunks, with root toes.
  const leg = (x: number, off: number) => {
    ctx.fillStyle = BARK;
    ctx.fillRect(x - 3.5, H - 26 + off, 7, 24 - off);
    ctx.fillStyle = BARK_D;
    ctx.fillRect(x - 1, H - 24 + off, 1, 20);
    ctx.strokeStyle = BARK_D;
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.beginPath();
    for (const dx of [-4, 0, 4]) {
      ctx.moveTo(x, H - 3);
      ctx.lineTo(x + dx + side * 2, H - 0.5);
    }
    ctx.stroke();
  };
  leg(11.5, swing);
  leg(20.5, -swing);
  // The body: tall, narrowing to the shoulders, ridged with bark.
  ctx.fillStyle = back ? BARK_D : BARK;
  ctx.beginPath();
  ctx.moveTo(8, H - 24);
  ctx.lineTo(10, H - 56);
  ctx.lineTo(22, H - 56);
  ctx.lineTo(24, H - 24);
  ctx.closePath();
  ctx.fill();
  ctx.fillStyle = BARK_D;
  for (const x of [11, 14, 18, 21]) ctx.fillRect(x + side * 0.5, H - 54, 1, 28);
  ctx.fillStyle = BARK_L;
  ctx.fillRect(side >= 0 ? 20 : 11, H - 52, 1, 24);
  // Long arms, hanging nearly to the knee, with twig fingers.
  const arm = (s: number, dy: number) => {
    ctx.strokeStyle = BARK;
    ctx.lineWidth = 4;
    ctx.lineCap = "round";
    ctx.beginPath();
    ctx.moveTo(16 + s * 6, H - 53);
    ctx.quadraticCurveTo(16 + s * 13, H - 46 + dy, 16 + s * 12, H - 26 + dy);
    ctx.stroke();
    ctx.strokeStyle = BARK_D;
    ctx.lineWidth = 1.2;
    ctx.beginPath();
    for (const [fx, fy] of [[-2, 5], [0, 6], [2, 5], [3, 3]]) {
      ctx.moveTo(16 + s * 12, H - 26 + dy);
      ctx.lineTo(16 + s * (12 + fx), H - 26 + dy + fy);
    }
    ctx.stroke();
  };
  arm(-1, -swing / 2);
  arm(1, swing / 2);
  // The head: a knob of bark atop the shoulders, its crown of leafy twigs.
  const hx = 16 + side * 1.5;
  const hy = H - 62;
  ctx.fillStyle = BARK;
  ctx.beginPath();
  ctx.ellipse(hx, hy, 6.5, 8, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.strokeStyle = BARK_D;
  ctx.lineWidth = 1.2;
  ctx.beginPath();
  for (const [dx, dy] of [[-5, -10], [-2, -12], [2, -12], [5, -10], [0, -13]]) {
    ctx.moveTo(hx + dx / 2, hy - 6);
    ctx.lineTo(hx + dx, hy + dy);
  }
  ctx.stroke();
  for (const [dx, dy, r, c] of [[-5, -10, 2.5, LEAF], [5, -10, 2.5, LEAF], [0, -13, 2.8, LEAF_L], [-2, -12, 2, LEAF_L], [2, -12, 2, LEAF]] as [number, number, number, string][]) {
    ctx.fillStyle = c;
    ctx.beginPath();
    ctx.arc(hx + dx, hy + dy, r, 0, Math.PI * 2);
    ctx.fill();
  }
  if (look.berries) {
    ctx.fillStyle = look.berries;
    for (const [dx, dy] of [[-4, -8], [-3, -9], [4, -8], [3, -9.5], [1, -12], [-1, -13]]) ctx.fillRect(hx + dx, hy + dy, 1.2, 1.2);
  }
  if (back) return;
  // The beard: grey-green moss and twigs, long, to his middle (an Ent's
  // as long as look.beard says, Treebeard's the whole of it).
  const bx = hx + side * 2;
  if (look.beard > 0) {
    const by = (y: number) => hy + 2 + (y - (hy + 2)) * look.beard;
    ctx.fillStyle = MOSS;
    ctx.beginPath();
    ctx.moveTo(bx - 5.5, hy + 2);
    ctx.lineTo(bx + 5.5, hy + 2);
    ctx.lineTo(bx + 4, by(H - 40));
    ctx.lineTo(bx + 1, by(H - 34));
    ctx.lineTo(bx - 1, by(H - 37));
    ctx.lineTo(bx - 3, by(H - 33));
    ctx.lineTo(bx - 4.5, by(H - 40));
    ctx.closePath();
    ctx.fill();
    ctx.fillStyle = MOSS_D;
    for (const x of [-3, 0, 3]) ctx.fillRect(bx + x, hy + 4, 1, 12 * look.beard);
  }
  // A big nose of a knot, and the eyes: brown, with the green light.
  ctx.fillStyle = BARK_D;
  ctx.fillRect(hx - 1 + side * 4, hy - 1, 2.5, 4);
  const eye = (x: number) => {
    ctx.fillStyle = "#2a1a0e";
    ctx.fillRect(x - 1, hy - 3, 3, 2.2);
    ctx.fillStyle = "#7ad050";
    ctx.fillRect(x, hy - 2.8, 1, 1);
  };
  if (front) {
    eye(hx - 3.5);
    eye(hx + 2.5);
  } else eye(hx + side * 3);
}

/** The wild hobbit's outfit, and the mustard of its tunic. */
const WILD_HOBBIT = 39;
const WILD_HOBBIT_TUNIC = "#c0922c";

/**
 * Bullroarer Took's outfit, the Took green of his tunic, and how much
 * bigger than a hobbit he is drawn: big enough to ride a horse, so a
 * fifth again, which still leaves him a head under a man.
 */
const BULLROARER = 86;
const BULLROARER_TUNIC = "#2f6a3a";
const BULLROARER_SCALE = 1.2;

function drawCreature(ctx: Ctx, outfit: number, dir: number, frame: number, gear: Gear, color?: string, eye?: string, admin = false, race?: string, hair = 0) {
  const H = TILE + 16; // canvas height; feet at bottom
  const legOff = frame === 1 ? 2 : frame === 2 ? -2 : 0;
  const facingSide = dir === 1 ? 1 : dir === 3 ? -1 : 0;
  const front = dir === 2;

  const eyes = (cx: number, cy: number, col: string = EYE_PLAIN) => {
    if (dir === 0) return; // back of head
    ctx.fillStyle = col;
    if (facingSide) ctx.fillRect(cx + facingSide * 3, cy, 2, 2);
    else {
      ctx.fillRect(cx - 3, cy, 2, 2);
      ctx.fillRect(cx + 2, cy, 2, 2);
    }
  };

  if (outfit < 10) {
    drawPlayer(ctx, outfit, dir, legOff, gear, (cx, cy) => eyes(cx, cy, eye), color, admin, race, eye, hair);
    return;
  }
  // A wild hobbit, out in the fields with a stick and a rock: a hobbit
  // player's figure, size, skin, brown curls and bare feet, in a mustard
  // tunic of its own, holding the stick and the rock as a player holds a
  // weapon and a shield (2026-10-01: "update the monster hobbit appearance
  // to match the player hobbits"). It had a body of its own on Grubb's
  // build, with ginger curls, from 2026-09-28 ("add a hobbit creature with
  // a stick in one hand and a rock in the other").
  if (outfit === WILD_HOBBIT) {
    drawPlayer(ctx, outfit, dir, legOff, gear, (cx, cy) => eyes(cx, cy, eye), color ?? WILD_HOBBIT_TUNIC, false, "hobbit");
    return;
  }
  // Bandobras "Bullroarer" Took (2026-10-10: "What's the famous golfing
  // Hobbit?"): the wild hobbit's figure, a player's, drawn a fifth as big
  // again about his feet, in a Took's green, with what he is seen with
  // coming in as gear: the hobbit saucepan upside down on his head for a
  // helm and the golf club in his hand.
  if (outfit === BULLROARER) {
    ctx.save();
    ctx.translate(16, H);
    ctx.scale(BULLROARER_SCALE, BULLROARER_SCALE);
    ctx.translate(-16, -H);
    drawPlayer(ctx, outfit, dir, legOff, gear, (cx, cy) => eyes(cx, cy, eye), color ?? BULLROARER_TUNIC, false, "hobbit");
    ctx.restore();
    return;
  }
  // A monster that fights with a weapon carries it the way a player does
  // (see drawPlayer): in the right hand, which is the viewer's left coming
  // towards you, south and east, and their right going away, north and
  // west, with the shield on the other arm. Behind the body is the arm
  // further from the camera: both from behind, and in profile the one on
  // the viewer's right, which is the shield arm going east and the weapon
  // arm going west. Where alongside the body the hands are depends on the
  // shape of the beast, so each one that can hold something says where its
  // grip sits.
  //
  // It used to lead with the weapon in both profiles, always in front of
  // the body (2026-10-12: "Check that the main hand is on the correct side
  // of creatures for each direction they can move"): going west that is
  // the left hand, so every armed monster changed hands when it turned
  // round, and its shield was in front of it even on the far arm.
  const weaponSide: -1 | 1 = backView(dir) ? 1 : -1;
  const farArm = (side: -1 | 1) => (facingSide ? side > 0 : dir === 0);
  const holdAt = (leftX: number, rightX: number, y: number) => {
    // Something held in both hands is held across the body and aimed the
    // way it faces, so it goes by the facing rather than by the hand; it
    // is behind the body only when seen from the back.
    const side = twoHandedIds.has(gear.weapon) ? (facingSide !== 0 ? facingSide : front ? -1 : 1) : weaponSide;
    const behind = twoHandedIds.has(gear.weapon) ? dir === 0 : farArm(side);
    return { x: side > 0 ? rightX : leftX, y: y + (side * legOff) / 2, out: side, behind };
  };
  let hand: { x: number; y: number; out: number; behind: boolean } | null = null;
  const grip = MONSTER_GRIP[outfit];
  if (gear.weapon && grip) hand = holdAt(grip.left, grip.right, H - grip.y);
  const monsterCrossed = hand !== null && twoHandedIds.has(gear.weapon);
  // Whatever it is holding is drawn at the size its kind carries it at,
  // scaled about the grip so it stays in the fist rather than sliding out
  // of it.
  const sized = (ax: number, ay: number, draw: () => void, s = grip?.scale ?? 1) => {
    if (s === 1) {
      draw();
      return;
    }
    ctx.save();
    ctx.translate(ax, ay);
    ctx.scale(s, s);
    ctx.translate(-ax, -ay);
    draw();
    ctx.restore();
  };
  // A shield goes on the other arm, which is the other side of the body,
  // the way a player carries the pair. It is drawn at the kind's own scale
  // like everything else it holds, so a kobold's buckler is a kobold's.
  let shieldArm: { x: number; y: number; out: number; behind: boolean } | null = null;
  if (gear.shield && grip) {
    const side = -weaponSide as -1 | 1;
    shieldArm = { x: side > 0 ? grip.right : grip.left, y: H - grip.y + (side * legOff) / 2, out: side, behind: farArm(side) };
  }
  const drawHeld = () => {
    if (shieldArm) {
      sized(shieldArm.x, shieldArm.y, () => drawShield(ctx, gear.shield, shieldArm!.x, shieldArm!.y, shieldArm!.out));
    }
    if (!hand) return;
    if (monsterCrossed) {
      // Walking away from you it goes on the back, the way a player's
      // does: a bow laid across a chest you cannot see is a bow laid
      // behind a back that hides every pixel of it, and a kind that
      // always carries one would be drawn empty-handed for one facing in
      // four. drawSlung measures down from the top of a player's frame,
      // so it is handed this body's own instead — the grip a monster
      // holds things at, the same thirteen above it that drawCrossed
      // works from — and a weapon with no back carry of its own comes
      // out exactly where it was, since both paths then read the one
      // carry table.
      //
      // The size it comes out at is the carry's business and not the
      // kind's, for the giant sword: see carriedScale.
      const s = carriedScale(gear.weapon, grip?.scale ?? 1);
      if (hand.behind) sized(16, hand.y + 3, () => drawSlung(ctx, gear.weapon, hand!.y + 16, false, true), s);
      else sized(16, hand.y + 3, () => drawCrossed(ctx, gear.weapon, 16, hand!.y + 3, hand!.out, facingSide, false, true), s);
    } else sized(hand.x, hand.y, () => drawWeapon(ctx, gear.weapon, hand!.x, hand!.y, hand!.out));
  };
  if (hand?.behind || shieldArm?.behind) drawHeld();
  switch (outfit) {
    case 101: // Longbottom's shed rat (empty-areas site 1): the rat, in Tobold's sheds
    case 10: // rat
      ctx.fillStyle = "#7a5a40";
      ctx.beginPath();
      ctx.ellipse(16, H - 8, 9, 5, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.beginPath();
      ctx.arc(16 + facingSide * 7, H - 10, 4, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = "#e8a0a0";
      ctx.fillRect(14 + facingSide * 7, H - 16, 2, 3);
      ctx.fillRect(17 + facingSide * 7, H - 16, 2, 3);
      ctx.strokeStyle = "#e8a0a0";
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(16 - facingSide * 8, H - 8);
      ctx.quadraticCurveTo(16 - facingSide * 14, H - 4 + legOff, 16 - facingSide * 16, H - 10);
      ctx.stroke();
      eyes(16 + facingSide * 7, H - 11, "#300");
      break;
    case 11: // wolf
      drawWolf(ctx, WOLF_GREY, dir, frame, legOff, H, eyes);
      break;
    case 12: // forest troll: hunched, long in the arm, low in the head (see drawTroll)
      drawTroll(ctx, dir, legOff, H);
      break;
    case 38: // hill troll: the troll, bigger and grey (see drawHillTroll)
      drawHillTroll(ctx, dir, legOff, H);
      break;
    // The Rangers' camp (empty-areas site 2, 2026-10-16).
    case 106: // the troll chief of the Ettenmoors: the hill troll bigger, lichened, a ring in its ear (see drawTrollChief)
      drawTrollChief(ctx, dir, legOff, H);
      break;
    // The northern spots (2026-11-01).
    case 177: // an old stone troll of the Troll-stones: the troll bigger, grey and mossed (see drawOldTroll)
      drawOldTroll(ctx, dir, legOff, H);
      break;
    case 176: // Elladan son of Elrond: grey cloak, bright mail, a bow (see drawFolk)
      drawFolk(ctx, dir, legOff, H, eyes, ELLADAN_KIT);
      break;
    case 178: // Léod of the Éothéod: fair-haired, a green cloak over rings (see drawFolk)
      drawFolk(ctx, dir, legOff, H, eyes, LEOD_KIT);
      break;
    case 105: // Halbarad of the Dúnedain: the Ranger darker, mail, hood back, the star at his shoulder (see drawHalbarad)
      drawHalbarad(ctx, dir, legOff, H, eyes);
      break;
    case 40: // cave troll: the troll, biggest, slate grey, knobbed and chained (see drawCaveTroll)
      drawCaveTroll(ctx, dir, legOff, H);
      break;
    case 13: // orc: the helmed warrior (see drawOrcWarrior)
      drawOrcWarrior(ctx, dir, legOff, H, eyes);
      break;
    case 42: // bandit: a hooded outlaw in a green cloak (see drawBandit)
      drawBandit(ctx, dir, legOff, H, eyes);
      break;
    // The content proposal's kinds (2026-10-01: "Implement the entire
    // proposal in the background").
    case 43: // crebain: a crow of Dunland on the wing (see drawCrebain)
      drawCrebain(ctx, dir, legOff, H, eyes);
      break;
    case 44: // snaga: a slave-orc, small and ragged (see drawSnaga)
      drawSnaga(ctx, dir, legOff, H, eyes);
      break;
    case 45: // warg: the wolf, bigger and near black (see drawWarg)
      drawWarg(ctx, dir, frame, legOff, H, eyes);
      break;
    case 166: // werewolf: the warg bigger again, black and red-eyed (see drawWerewolf)
      drawWerewolf(ctx, dir, frame, legOff, H, eyes);
      break;
    case 46: // Southron archer: a man of Harad in scarlet (see drawMan)
      drawMan(ctx, dir, legOff, H, eyes, SOUTHRON_KIT);
      break;
    case 47: // Dunlending: a wild hill-man in hide (see drawMan)
      drawMan(ctx, dir, legOff, H, eyes, DUNLENDING_KIT);
      break;
    case 48: // corsair of Umbar: a sea-robber with a red rag on his head (see drawMan)
      drawMan(ctx, dir, legOff, H, eyes, CORSAIR_KIT);
      break;
    case 49: // Easterling: a man of Rhûn in lacquered scale (see drawMan)
      drawMan(ctx, dir, legOff, H, eyes, EASTERLING_KIT);
      break;
    case 50: // Uruk-hai: the orc warrior grown big, in black, the White Hand on its helm (see drawUruk)
      drawUruk(ctx, dir, legOff, H, eyes);
      break;
    case 68: // Black Orc: the orc warrior bigger again, near black, the Red Eye on its helm (see drawBlackOrc)
      drawBlackOrc(ctx, dir, legOff, H, eyes);
      break;
    case 95: // Gundabad orc: the orc warrior grey, in rust-red mail (see GUNDABAD_ORC_KIT)
      drawGundabadOrc(ctx, dir, legOff, H, eyes, GUNDABAD_ORC_KIT, GUNDABAD_ORC_SCALE);
      break;
    case 96: // Bolg's bodyguard: a goblin of huge size in black mail (see BODYGUARD_KIT)
      drawGundabadOrc(ctx, dir, legOff, H, eyes, BODYGUARD_KIT, BODYGUARD_SCALE);
      break;
    case 97: // Bolg: the biggest of them, pale grey, a white wolf's pelt over his shoulders (see BOLG_KIT)
      drawGundabadOrc(ctx, dir, legOff, H, eyes, BOLG_KIT, BOLG_SCALE);
      break;
    case 51: // stone troll: the troll a size up, in the grey-brown of rock (see drawStoneTroll)
      drawStoneTroll(ctx, dir, legOff, H);
      break;
    case 52: // huorn: a tree that walks (see drawHuorn)
      drawHuorn(ctx, dir, legOff, H);
      break;
    case 87: // Old Man Willow: a huge grey willow with a face in its cracks (see drawWillow)
      drawWillow(ctx, dir, legOff, H);
      break;
    case 88: // a corpse candle: a cold flame over a dead face in a pool (see drawCorpseCandle)
      drawCorpseCandle(ctx, dir, frame, H);
      break;
    case 53: // Nazgûl: a black hood with nothing in it but two lights (see drawNazgul)
      drawNazgul(ctx, dir, legOff, H, eyes);
      break;
    case 67: // Witch-king: the Nazgûl bigger, in a helm-crown of blades, green-eyed, red at the hem (see drawWitchKing)
      drawWitchKing(ctx, dir, legOff, H, eyes);
      break;
    case 54: // mûmak: an elephant the size of a house with a war-tower on its back (see drawMumak)
      drawMumak(ctx, dir, legOff, H);
      break;
    case 55: // Shelob: the giant spider, bigger, black and green-eyed (see drawShelob)
      drawShelob(ctx, dir, legOff, H);
      break;
    case 162: // Ungoliant: Shelob, bigger again, black over black, red-eyed, in her Unlight (see drawUngoliant)
      drawUngoliant(ctx, dir, legOff, H);
      break;
    case 163: // Sauron: the Dark Lord in black plate, spiked helm, mace and the One (see drawSauron)
      drawSauron(ctx, dir, legOff, H);
      break;
    case 164: // Mirkwood spider: the giant spider a size down, brown and yellow-eyed (see drawMirkwoodSpider)
      drawMirkwoodSpider(ctx, dir, legOff, H);
      break;
    case 34: // gnoll: a hyena on its hind legs, spotted, maned, grinning
      // Asked for on 2026-09-24: "add a new creature called a gnoll that
      // carries a spear". Built on the orc's frame, since it holds its
      // spear in the orc's grip, but lean where the orc is square: a
      // narrow tawny body with dark spots, a stiff mane down the back of
      // the neck, and a long muzzle leading the way, so it reads as an
      // animal walking like a man rather than a man with a dog's head.
      ctx.fillStyle = "#6b5334"; // long digitigrade legs
      ctx.fillRect(11, H - 13 + legOff, 4, 12);
      ctx.fillRect(17, H - 13 - legOff, 4, 12);
      ctx.fillStyle = "#b08a52"; // the lean body
      ctx.fillRect(9, H - 30, 14, 18);
      ctx.fillStyle = "#5a4326"; // spots
      ctx.fillRect(11, H - 27, 2, 2);
      ctx.fillRect(18, H - 24, 2, 2);
      ctx.fillRect(13, H - 19, 2, 2);
      ctx.fillRect(19, H - 16, 2, 2);
      ctx.fillStyle = "#9c7846"; // arms
      ctx.fillRect(5, H - 28, 4, 12);
      ctx.fillRect(23, H - 28, 4, 12);
      ctx.fillStyle = "#5a4326"; // tall rounded ears, set high
      ctx.beginPath();
      ctx.ellipse(12 + facingSide * 2, H - 43, 2, 3.5, -0.2, 0, Math.PI * 2);
      ctx.ellipse(20 + facingSide * 2, H - 43, 2, 3.5, 0.2, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = "#b08a52"; // the head
      ctx.beginPath();
      ctx.arc(16 + facingSide * 2, H - 37, 5.5, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = "#3e2e1a"; // the mane: a dark crest from the crown down the neck
      if (facingSide) ctx.fillRect(16 - facingSide * 4 - 1.5, H - 42, 3, 14);
      else ctx.fillRect(14.5, H - 44, 3, 6);
      if (facingSide) {
        // In profile the muzzle is most of the head: long, sloping, and
        // dark at the end.
        ctx.fillStyle = "#9c7846";
        ctx.beginPath();
        ctx.moveTo(16 + facingSide * 4, H - 39);
        ctx.lineTo(16 + facingSide * 13, H - 35);
        ctx.lineTo(16 + facingSide * 13, H - 32);
        ctx.lineTo(16 + facingSide * 4, H - 32);
        ctx.closePath();
        ctx.fill();
        ctx.fillStyle = "#2a1f14"; // the nose
        ctx.fillRect(16 + facingSide * 12 - 1.5, H - 36, 3, 2.5);
      } else if (front) {
        // Face on, the muzzle is a pale block jutting out below the eyes
        // with the dark nose on top: what says hyena rather than bear.
        ctx.fillStyle = "#d2b27a";
        ctx.fillRect(13, H - 36, 6, 5);
        ctx.fillStyle = "#2a1f14";
        ctx.fillRect(14.5, H - 36, 3, 2);
        ctx.fillStyle = "#eee"; // the grin
        ctx.fillRect(13, H - 32, 6, 1);
      }
      eyes(16 + facingSide * 2, H - 39, "#e8c23a");
      break;
    case 35: // Grubb, the vendor: a hobbit
      // Asked for on 2026-09-24: "make the vendor called Grubb instead.
      // make him an NPC hobbit that wanders in the castle". Half a head
      // shorter than anybody else on the screen, which is most of what
      // says hobbit at this size, and then the three things the books
      // give them: bare feet, big and brown and hairy on top; curly brown
      // hair; and a bright waistcoat over a shirt, buttoned, with a
      // pocket to keep the takings in.
      ctx.fillStyle = "#8a5a32"; // bare feet, broad
      ctx.fillRect(9, H - 4 + legOff, 6, 3);
      ctx.fillRect(17, H - 4 - legOff, 6, 3);
      ctx.fillStyle = "#5a3a1e"; // the hair on top of them
      ctx.fillRect(10, H - 5 + legOff, 4, 1);
      ctx.fillRect(18, H - 5 - legOff, 4, 1);
      ctx.fillStyle = "#6b4a2a"; // breeches to the knee
      ctx.fillRect(10, H - 12 + legOff, 5, 8);
      ctx.fillRect(17, H - 12 - legOff, 5, 8);
      ctx.fillStyle = "#e8dcb8"; // shirt
      ctx.fillRect(8, H - 25, 16, 14);
      ctx.fillStyle = "#3f7a3a"; // the waistcoat, open over it
      ctx.fillRect(8, H - 25, 6, 14);
      ctx.fillRect(18, H - 25, 6, 14);
      ctx.fillStyle = "#d8b24a"; // brass buttons
      ctx.fillRect(13, H - 22, 1, 1);
      ctx.fillRect(13, H - 18, 1, 1);
      ctx.fillRect(13, H - 14, 1, 1);
      ctx.fillStyle = "#2f5e2b"; // a pocket
      ctx.fillRect(19, H - 17, 4, 3);
      ctx.fillStyle = "#e0b48a"; // arms, sleeves rolled
      ctx.fillRect(5, H - 24, 3, 10);
      ctx.fillRect(24, H - 24, 3, 10);
      ctx.beginPath(); // a round face
      ctx.arc(16 + facingSide * 1.5, H - 30, 6, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = "#6a4020"; // curls: a ring of little circles
      for (const [dx, dy] of [[-5, -3], [-3, -6], [0, -7], [3, -6], [5, -3], [-6, 0], [6, 0]]) {
        if (!front && !facingSide) {
          // From behind the curls cover the whole of the head.
          ctx.beginPath();
          ctx.arc(16, H - 30, 6.5, 0, Math.PI * 2);
          ctx.fill();
          break;
        }
        ctx.beginPath();
        ctx.arc(16 + facingSide * 1.5 + dx, H - 30 + dy, 2.2, 0, Math.PI * 2);
        ctx.fill();
      }
      if (front || facingSide) eyes(16 + facingSide * 2.5, H - 30, "#2a1a0a");
      if (front) {
        ctx.fillStyle = "#b0705a"; // a smile: he is selling something
        ctx.fillRect(14, H - 27, 4, 1);
      }
      break;
    case 36: // Elrond, master of Rivendell: an elf lord
      // Asked for on 2026-09-25: "Implement an NPC elrond where Rivendell
      // should be". Taller and slighter than anybody else on the screen,
      // in a long robe that hides the feet, dark hair to the shoulders and
      // a circlet of silver on the brow: what says elf lord at this size.
      ctx.fillStyle = "#3b3552"; // the robe, to the ground
      ctx.beginPath();
      ctx.moveTo(10, H - 30);
      ctx.lineTo(22, H - 30);
      ctx.lineTo(25, H - 1);
      ctx.lineTo(7, H - 1);
      ctx.closePath();
      ctx.fill();
      ctx.fillStyle = "#b9a55a"; // a girdle, and its hem
      ctx.fillRect(10, H - 20, 12, 1.5);
      ctx.fillRect(7, H - 3, 18, 1.5);
      ctx.fillStyle = "#4a4466"; // sleeves
      ctx.fillRect(6, H - 29, 4, 13 + legOff * 0.5);
      ctx.fillRect(22, H - 29, 4, 13 - legOff * 0.5);
      ctx.fillStyle = "#e6d3bd"; // hands and face
      ctx.fillRect(6, H - 16 + legOff * 0.5, 4, 3);
      ctx.fillRect(22, H - 16 - legOff * 0.5, 4, 3);
      ctx.beginPath();
      ctx.arc(16 + facingSide * 1.5, H - 36, 5, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = "#1e1a16"; // dark hair to the shoulders
      if (!front && !facingSide) {
        ctx.beginPath();
        ctx.arc(16, H - 36, 5.5, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillRect(10.5, H - 36, 11, 8);
      } else {
        ctx.fillRect(11 + facingSide * 1.5, H - 41, 10, 3);
        ctx.fillRect(10.5 + facingSide * 1.5 - facingSide * 1, H - 38, 2, 9);
        ctx.fillRect(19.5 + facingSide * 1.5 - facingSide * 1, H - 38, 2, 9);
      }
      ctx.fillStyle = "#e8eef4"; // the silver circlet
      ctx.fillRect(11 + facingSide * 1.5, H - 39, 10, 1);
      if (front || facingSide) eyes(16 + facingSide * 2.5, H - 36, "#3a4f6a");
      break;
    // The content proposal's people (2026-10-01: "Implement the entire
    // proposal in the background"), one outfit each (see people.go).
    case 56: // the Gaffer, Hamfast Gamgee: an old hobbit with a spade (see drawHobbitFolk)
      drawHobbitFolk(ctx, dir, legOff, H, eyes, GAFFER_KIT);
      break;
    case 57: // Bilbo Baggins, old, in his red waistcoat with his book (see drawHobbitFolk)
      drawHobbitFolk(ctx, dir, legOff, H, eyes, BILBO_KIT);
      break;
    // Longbottom (empty-areas site 1, 2026-10-16; see longbottom.go).
    case 100: // Tobold Hornblower: a stout hobbit in a leaf-green waistcoat, his long pipe (see drawHobbitFolk)
      drawHobbitFolk(ctx, dir, legOff, H, eyes, TOBOLD_KIT);
      break;
    case 58: // Barliman Butterbur: fat, in an apron (see drawFolk)
      drawFolk(ctx, dir, legOff, H, eyes, BARLIMAN_KIT);
      break;
    case 59: // Tom Bombadil: blue jacket, yellow boots, a feather in his hat (see drawFolk)
      drawFolk(ctx, dir, legOff, H, eyes, BOMBADIL_KIT);
      break;
    case 60: // Beorn: huge and black-bearded, in a bearskin (see drawBeorn)
      drawBeorn(ctx, dir, legOff, H, eyes);
      break;
    case 61: // Bard the Bowman: dark, in green, his bow on his back (see drawFolk)
      drawFolk(ctx, dir, legOff, H, eyes, BARD_KIT);
      break;
    case 62: // Galadriel: white and silver and gold (see drawGaladriel)
      drawGaladriel(ctx, dir, legOff, H, eyes);
      break;
    case 63: // Treebeard: an Ent (see drawTreebeard)
      drawTreebeard(ctx, dir, legOff, H);
      break;
    case 158: // an Ent of Fangorn: Treebeard's shape in darker bark, a shorter beard (see drawTreebeard)
      drawTreebeard(ctx, dir, legOff, H, ENT_LOOK);
      break;
    case 159: // Quickbeam: a rowan-ent, smooth grey bark, berries in his crown (see drawTreebeard)
      drawTreebeard(ctx, dir, legOff, H, QUICKBEAM_LOOK);
      break;
    case 64: // Éomer: mail, a green cloak, a horsetail helm (see drawFolk)
      drawFolk(ctx, dir, legOff, H, eyes, EOMER_KIT);
      break;
    case 65: // Ioreth: grey to the ground, a white headcloth (see drawFolk)
      drawFolk(ctx, dir, legOff, H, eyes, IORETH_KIT);
      break;
    case 66: // Imrahil: blue and silver, the swan on his breast and his helm (see drawFolk)
      drawFolk(ctx, dir, legOff, H, eyes, IMRAHIL_KIT);
      break;
    case 69: // Círdan the Shipwright: grey to the ground, a silver beard (see drawFolk)
      drawFolk(ctx, dir, legOff, H, eyes, CIRDAN_KIT);
      break;
    case 70: // the Harlond's harbour-master: black and silver (see drawFolk)
      drawFolk(ctx, dir, legOff, H, eyes, HARBOURMASTER_KIT);
      break;
    // The Wood-elves of Mirkwood, their king and his butler (2026-10-07:
    // "propose some pathways through the mirkwood forest, and the
    // elf-kings halls, with hostile elf monsters", then "Implement it").
    case 71: // Wood-elf archer: hooded, leaf-green over brown (see drawFolk)
      drawFolk(ctx, dir, legOff, H, eyes, WOODELF_ARCHER_KIT);
      break;
    case 72: // Wood-elf warden: hooded, brown under dark green (see drawFolk)
      drawFolk(ctx, dir, legOff, H, eyes, WOODELF_WARDEN_KIT);
      break;
    case 73: // Guard of the Elvenking: silver mail, a green cloak, a tall helm (see drawFolk)
      drawFolk(ctx, dir, legOff, H, eyes, ELVEN_GUARD_KIT);
      break;
    case 74: // Thranduil: red and gold to the ground, a crown of leaves and berries (see drawFolk)
      drawFolk(ctx, dir, legOff, H, eyes, THRANDUIL_KIT);
      break;
    case 75: // Galion, the Elvenking's butler: wine-dark, in a cellarer's apron (see drawFolk)
      drawFolk(ctx, dir, legOff, H, eyes, GALION_KIT);
      break;
    case 76: // Gollum: thin, pale and crouched, all eyes (see drawGollum)
      drawGollum(ctx, dir, legOff, H);
      break;
    case 77: // the Watcher in the Water: pale arms out of a dark pool (see drawWatcher)
      drawWatcher(ctx, dir, legOff, H);
      break;
    case 84: // a were-worm: a wingless dragon rearing out of a hole in the ground (see drawWereworm)
      drawWereworm(ctx, dir, legOff, H);
      break;
    case 85: // the Ranger of the North at Tharbad: hooded grey-green, a bow on his back (see drawFolk)
      drawFolk(ctx, dir, legOff, H, eyes, RANGER_KIT);
      break;
    // 9. Dorwinion (2026-10-16): the vintner in his cellars.
    case 140: // the vintner of Dorwinion: wine-red, bare-armed, a leather apron (see drawFolk)
      drawFolk(ctx, dir, legOff, H, eyes, VINTNER_KIT);
      break;
    // end of 9. Dorwinion
    // --- Site 3, the Lossoth of Forochel (2026-10-16).
    case 110: // the headman of the Lossoth: white furs from hood to boots (see drawFolk)
      drawFolk(ctx, dir, legOff, H, eyes, LOSSOTH_KIT);
      break;
    case 111: // a Drowned Dúnadan: sea-dark mail, a sodden hood, frost on him (see drawFolk)
      drawFolk(ctx, dir, legOff, H, eyes, DROWNED_KIT);
      break;
    // --- end of site 3
    // 5. Rhosgobel (2026-10-16): Radagast at home.
    case 120: // Radagast the Brown: earth-brown robes to the ground, a wizard's hat with a robin on it (see drawFolk)
      drawFolk(ctx, dir, legOff, H, eyes, RADAGAST_KIT);
      break;
    // end of 5. Rhosgobel
    // 10. Drúwaith Iaur (2026-10-16).
    case 145: // the Old Wose: broad and brown, grey-headed, a grass skirt and a cape of bark (see drawFolk)
      drawFolk(ctx, dir, legOff, H, eyes, WOSE_ELDER_KIT);
      break;
    case 146: // a Wose: broad and brown, bare-armed, a grass skirt, leaves in the hair (see drawFolk)
      drawFolk(ctx, dir, legOff, H, eyes, WOSE_KIT);
      break;
    // end of 10. Drúwaith Iaur
    // --- Site 6: Henneth Annûn (2026-10-16) ---
    case 125: // Faramir, Captain of Gondor: green and brown, bareheaded, a bow on his back (see drawFolk)
      drawFolk(ctx, dir, legOff, H, eyes, FARAMIR_KIT);
      break;
    case 160: // Gamling the Old, in the Glittering Caves: a white-bearded Rider in mail, the green cloak (see drawFolk)
      drawFolk(ctx, dir, legOff, H, eyes, GAMLING_KIT);
      break;
    // --- end site 6 ---
    // 11. The Variags of Khand (2026-10-16).
    case 150: // the merchant of Harad: a striped robe, a white head-cloth (see drawMan)
      drawMan(ctx, dir, legOff, H, eyes, HARAD_MERCHANT_KIT);
      break;
    case 151: // Variag axeman: dark scale over red, a horse-tail helm (see drawMan)
      drawMan(ctx, dir, legOff, H, eyes, VARIAG_AXEMAN_KIT);
      break;
    case 152: // Variag horse-archer: tan leather, his own black hair, drawn on his horse (see drawMan, riderSprite)
      drawMan(ctx, dir, legOff, H, eyes, VARIAG_ARCHER_KIT);
      break;
    case 153: // chieftain of the Variags: black scale and gold, a head taller (see drawMan)
      ctx.save();
      ctx.translate(16, H);
      ctx.scale(1.15, 1.15);
      ctx.translate(-16, -H);
      drawMan(ctx, dir, legOff, H, eyes, VARIAG_CHIEF_KIT);
      ctx.restore();
      break;
    // end of 11. Khand
    case 78: // a swan-ship captain of Dol Amroth: blue and silver, sea boots (see drawFolk)
      drawFolk(ctx, dir, legOff, H, eyes, SWANCAPTAIN_KIT);
      break;
    case 79: // the swan-ship's master on the Meneltarma: older, grey-bearded (see drawFolk)
      drawFolk(ctx, dir, legOff, H, eyes, SWANMASTER_KIT);
      break;
    // The dead of the crypt under Sauron's temple (2026-10-08).
    case 80: // a Drowned King's Man: sea-green mail and a crested helm (see drawFolk)
      drawFolk(ctx, dir, legOff, H, eyes, KINGSMAN_KIT);
      break;
    case 81: // a Temple Shade: grey robes and a hood, smoke-pale (see drawFolk)
      ctx.globalAlpha *= 0.75;
      drawFolk(ctx, dir, legOff, H, eyes, TEMPLESHADE_KIT);
      ctx.globalAlpha /= 0.75;
      break;
    // The Dead of Dunharrow (2026-10-16), half seen (see DEADMAN_KIT).
    case 130: // a Dead Man of Dunharrow: a grey shape of a man in rotted mail, hooded (see drawFolk)
      ctx.globalAlpha *= DEAD_ALPHA;
      drawFolk(ctx, dir, legOff, H, eyes, DEADMAN_KIT);
      ctx.globalAlpha /= DEAD_ALPHA;
      break;
    case 131: // the King of the Dead: the same a size up, darker, cloaked and crowned (see drawDeadKing)
      ctx.globalAlpha *= DEAD_ALPHA;
      drawDeadKing(ctx, dir, legOff, H, eyes);
      ctx.globalAlpha /= DEAD_ALPHA;
      break;
    case 82: // one of the Charred Dead: black and bare, the embers in it (see drawFolk)
      drawFolk(ctx, dir, legOff, H, eyes, CHARRED_KIT);
      break;
    case 83: // the High Priest of Melkor: black robes to the ground, a red trim (see drawFolk)
      drawFolk(ctx, dir, legOff, H, eyes, HIGHPRIEST_KIT);
      break;
    // The Blue Tower of Rhûn (2026-11-01).
    case 170: // an Acolyte of the Blue: a hooded robe of faded blue (see drawFolk)
      drawFolk(ctx, dir, legOff, H, eyes, ACOLYTE_KIT);
      break;
    case 171: // a Bound Shade: the same shape in blue-black smoke, half seen (see drawFolk)
      ctx.globalAlpha *= 0.7;
      drawFolk(ctx, dir, legOff, H, eyes, BOUNDSHADE_KIT);
      ctx.globalAlpha /= 0.7;
      break;
    case 172: // one of the Remade: bare grey skin with blue signs cut in it (see drawFolk)
      drawFolk(ctx, dir, legOff, H, eyes, REMADE_KIT);
      break;
    case 173: // the Blue Wizard: deep sea-blue robes, a wizard's hat, a white beard (see drawFolk)
      drawFolk(ctx, dir, legOff, H, eyes, BLUEWIZARD_KIT);
      break;
    case 174: // a warding stone: a grey standing stone, its signs pulsing blue (see drawWardStone)
      drawWardStone(ctx, H);
      break;
    // end of the Blue Tower
    // The Pits of Dol Guldur (2026-11-01).
    case 182: // the gaoler of Dol Guldur: a black orc a size bigger, a ring of keys at his belt (see drawBlackOrc)
      ctx.save();
      ctx.translate(16, H);
      ctx.scale(1.12, 1.12);
      ctx.translate(-16, -H);
      drawBlackOrc(ctx, dir, legOff, H, eyes);
      ctx.strokeStyle = "#5a5a62"; // the ring of keys
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.arc(dir === 3 ? 11 : 21, H - 17, 2.2, 0, Math.PI * 2);
      ctx.stroke();
      ctx.fillStyle = "#7a7a84";
      ctx.fillRect(dir === 3 ? 10 : 20, H - 15, 1, 3);
      ctx.fillRect(dir === 3 ? 12 : 22, H - 15, 1, 2);
      ctx.restore();
      break;
    case 183: // a fell spirit: a Nazgûl's shape in grey shadow, half seen, pale points for eyes (see drawNazgul)
      ctx.globalAlpha *= 0.55;
      drawNazgul(ctx, dir, legOff, H, eyes, FELL_SPIRIT_KIT);
      ctx.globalAlpha /= 0.55;
      break;
    case 184: // the Necromancer: a tall hooded shadow, a dull ember hem, two eyes like coals (see drawNazgul)
      ctx.save();
      ctx.translate(16, H);
      ctx.scale(1.15, 1.15);
      ctx.translate(-16, -H);
      drawNazgul(ctx, dir, legOff, H, eyes, NECROMANCER_KIT);
      ctx.restore();
      break;
    // end of Dol Guldur
    case 25: // orc berserker: bare-chested, crested and painted (see drawOrcBerserker)
      drawOrcBerserker(ctx, dir, legOff, H, eyes);
      break;
    case 14: // cyclops: the brute (see drawCyclopsBrute)
      drawCyclopsBrute(ctx, dir, legOff, H);
      break;
    case 16: { // bat: wings that flap with the walk frame
      const flap = legOff; // reuse the gait as a wingbeat
      ctx.fillStyle = "#3a3440";
      ctx.beginPath(); // left wing
      ctx.moveTo(15, H - 16);
      ctx.quadraticCurveTo(6, H - 22 - flap * 2, 2, H - 14 - flap * 3);
      ctx.quadraticCurveTo(8, H - 12, 15, H - 12);
      ctx.closePath();
      ctx.fill();
      ctx.beginPath(); // right wing
      ctx.moveTo(17, H - 16);
      ctx.quadraticCurveTo(26, H - 22 + flap * 2, 30, H - 14 + flap * 3);
      ctx.quadraticCurveTo(24, H - 12, 17, H - 12);
      ctx.closePath();
      ctx.fill();
      ctx.fillStyle = "#4a4450";
      ctx.beginPath(); // body
      ctx.ellipse(16, H - 15, 4, 5, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = "#f33";
      ctx.fillRect(14, H - 18, 1.5, 1.5);
      ctx.fillRect(17, H - 18, 1.5, 1.5);
      break;
    }
    case 18: { // giant spider: far too big for one tile, and drawn so
      ctx.strokeStyle = "#1c1a20";
      ctx.lineWidth = 3;
      ctx.lineCap = "round";
      ctx.beginPath(); // eight thick legs, kneed high above the body
      for (let i = 0; i < 4; i++) {
        const y = H - 34 + i * 7;
        const bend = (i & 1 ? legOff : -legOff) * 1.5;
        ctx.moveTo(16 - 10, y);
        ctx.quadraticCurveTo(16 - 24, y - 14 + bend, 16 - 27, y + 8 + bend);
        ctx.moveTo(16 + 10, y);
        ctx.quadraticCurveTo(16 + 24, y - 14 - bend, 16 + 27, y + 8 - bend);
      }
      ctx.stroke();
      ctx.strokeStyle = "#6a4a28"; // banded joints, Tibia's tan leg stripes
      ctx.lineWidth = 2;
      ctx.beginPath();
      for (let i = 0; i < 4; i++) {
        const y = H - 34 + i * 7;
        const bend = (i & 1 ? legOff : -legOff) * 1.5;
        ctx.moveTo(16 - 17, y - 7 + bend / 2);
        ctx.lineTo(16 - 21, y - 9 + bend / 2);
        ctx.moveTo(16 + 17, y - 7 - bend / 2);
        ctx.lineTo(16 + 21, y - 9 - bend / 2);
      }
      ctx.stroke();
      ctx.fillStyle = "#2a2530";
      ctx.beginPath(); // a huge abdomen, most of the animal
      ctx.ellipse(16, H - 17, 16, 13, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = "#584a30";
      ctx.beginPath(); // pale chevrons down the back
      for (let i = 0; i < 3; i++) {
        const y = H - 26 + i * 6;
        ctx.moveTo(16, y);
        ctx.lineTo(16 - 6 + i, y + 4);
        ctx.lineTo(16, y + 3);
        ctx.lineTo(16 + 6 - i, y + 4);
        ctx.closePath();
      }
      ctx.fill();
      ctx.fillStyle = "#1f1b24";
      ctx.beginPath(); // cephalothorax, thrust the way it faces
      ctx.ellipse(16 + facingSide * 4, H - 36, 10, 8, 0, 0, Math.PI * 2);
      ctx.fill();
      if (dir !== 0) {
        const hx = 16 + facingSide * 4;
        ctx.fillStyle = "#e33"; // a cluster of eyes, lit from within
        ctx.fillRect(hx - 5, H - 41, 3, 3);
        ctx.fillRect(hx + 2, H - 41, 3, 3);
        ctx.fillRect(hx - 2, H - 44, 2, 2);
        ctx.fillRect(hx - 7, H - 38, 2, 2);
        ctx.fillRect(hx + 5, H - 38, 2, 2);
        ctx.fillStyle = "#d8cfc0"; // fangs, parted
        ctx.beginPath();
        ctx.moveTo(hx - 4, H - 33);
        ctx.lineTo(hx - 2, H - 27 + legOff / 2);
        ctx.lineTo(hx - 1, H - 33);
        ctx.moveTo(hx + 4, H - 33);
        ctx.lineTo(hx + 2, H - 27 - legOff / 2);
        ctx.lineTo(hx + 1, H - 33);
        ctx.closePath();
        ctx.fill();
      }
      break;
    }
    case 19: { // bear: a grizzly, built to be the biggest thing on the grass
      // What makes a bear read as a bear rather than as a large dog is the
      // hump: the muscle over the shoulders that a bear digs with, which
      // stands a little higher than its own head.
      //
      // "A little" is doing work there, and it is the correction the ask was
      // for on 2026-09-21: "Make the bear less moose like." It was one.
      // The hump was thrown so far forward that it stood over the skull
      // rather than behind it, the head hung low and well in front of the
      // shoulders on a long horizontal snout, the ears were dots set far
      // back, and the legs were long enough to show daylight under the
      // belly. Those are a moose's tells, every one, and together they
      // beat the brown fur and the claws.
      //
      // What a bear is instead: a round skull carried at the shoulder with
      // a blunt muzzle barely clear of it, eyes low and close over that
      // muzzle, a round ear on top of the head, and short legs under a
      // barrel that nearly touches the grass. The hump is still there and
      // still the first thing you see — it is behind the head now, which
      // is where a grizzly's is. See TestTheBearIsNotAMoose, which holds
      // the five of them so the next edit has to argue with them.
      //
      // The head is still carried below the line of the back, because a
      // silhouette that slopes down towards its mouth is a predator and
      // one that slopes up is a herbivore. What changed is how far below,
      // and how much snout is hanging off the end of it.
      //
      // It was a brown ellipse with a ball on the front of it, a little
      // smaller than the wolf that lives on the same grass, and it looked
      // like a hamster. This is nearly two tiles long and overhangs its
      // own in every facing, which is what the box in BIG_OUTFITS is for.
      const s = facingSide;
      const FUR = "#4a3418", LIT = "#6b4f28", DARK = "#2e2010";
      // Bone rather than white. At the size this is actually played at, a
      // true white row of teeth is the brightest thing on the sprite and
      // wins the eye off the red one, which is the thing that should be
      // looking at you.
      const CLAW = "#ded4bd", MOUTH = "#2a0b0b", TOOTH = "#ded4bd";
      // Legs first, so the body laps over the top of them: four of them,
      // thick as a post, and the near pair a shade darker so the far pair
      // reads as far. They swing in opposite pairs like everything else
      // that walks in this world.
      const leg = (x: number, off: number, w: number, dark: boolean, clawed: boolean) => {
        ctx.fillStyle = dark ? DARK : FUR;
        ctx.fillRect(x - w / 2, H - 11 + off, w, 11 - off);
        if (!clawed) return;
        // Claws on the front paws only, and set into the paw rather than
        // under it: drawn clear of the leg they read as gravel on the
        // grass, and a bear's claws are the part of it you are meant to
        // be looking at.
        ctx.fillStyle = CLAW;
        for (let i = -1; i <= 1; i++) ctx.fillRect(x + i * 2.2 - 0.6, H - 3.5, 1.2, 2.5);
      };
      if (s) {
        leg(16 - s * 10, legOff, 7, true, false);
        leg(16 + s * 9, -legOff, 7, true, true);
        leg(16 - s * 6, -legOff, 6, false, false);
        leg(16 + s * 13, legOff, 6, false, true);
      } else {
        leg(16 - 9, legOff, 7, false, true);
        leg(16 + 9, -legOff, 7, false, true);
      }
      // The barrel. Deep rather than long, so the thing has weight.
      ctx.fillStyle = FUR;
      ctx.beginPath();
      if (s) ctx.ellipse(16 - s * 3, H - 18, 14.5, 10, 0, 0, Math.PI * 2);
      else ctx.ellipse(16, H - 16, 12, 11, 0, 0, Math.PI * 2);
      ctx.fill();
      // The hump over the shoulders, standing above the line of the back.
      ctx.fillStyle = LIT;
      ctx.beginPath();
      if (s) ctx.ellipse(16 + s * 2, H - 26, 8.5, 7, 0, 0, Math.PI * 2);
      else ctx.ellipse(16, H - 25, 11, 7, 0, 0, Math.PI * 2);
      ctx.fill();
      if (dir === 0) {
        // From behind: the hump, the rump and two ears over the top of
        // it, and no face at all. A bear walking away is a wall of fur.
        ctx.fillStyle = DARK;
        ctx.beginPath();
        ctx.ellipse(16, H - 31, 5, 4, 0, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = FUR;
        for (const ex of [-6, 6]) {
          ctx.beginPath();
          ctx.arc(16 + ex, H - 33, 3, 0, Math.PI * 2);
          ctx.fill();
        }
        break;
      }
      // The head: low, forward, and under the hump. In profile it hangs
      // off the front of the shoulders; head on, it fills the chest.
      const hx = 16 + s * 15, hy = H - 21;
      // A shadow where the head meets the shoulder, laid down before the
      // head and left showing as a crescent behind it. Without it the
      // skull is the same brown as the barrel it is set against and the
      // whole animal reads as one loaf with a face on the end — which is
      // most of what was moose-like about it, the rest being the snout.
      // A bear has no neck to speak of, so the break cannot be drawn as
      // one: it has to be drawn as shade.
      if (s) {
        ctx.fillStyle = DARK;
        ctx.beginPath();
        ctx.ellipse(hx - s * 5, hy + 1, 6, 8, 0, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.fillStyle = FUR;
      ctx.beginPath();
      if (s) ctx.ellipse(hx, hy, 8.5, 8, 0, 0, Math.PI * 2);
      else ctx.ellipse(16, H - 16, 9, 8.5, 0, 0, Math.PI * 2);
      ctx.fill();
      // Ears, small and set well back: a big skull with small ears on it
      // is most of what makes a bear look heavy.
      for (const ex of s ? [-s * 4.5] : [-7.5, 7.5]) {
        const ey = (s ? hy : H - 16) - (s ? 6.5 : 7.5);
        const ecx = (s ? hx : 16) + ex;
        ctx.fillStyle = FUR;
        ctx.beginPath();
        ctx.arc(ecx, ey, 3.6, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = DARK;
        ctx.beginPath();
        ctx.arc(ecx, ey + 0.6, 1.9, 0, Math.PI * 2);
        ctx.fill();
      }
      // The muzzle, and the jaws open under it. The dark of an open mouth
      // is the whole of the meanness: a closed one is an animal, an open
      // one is an animal that has decided.
      ctx.fillStyle = LIT;
      ctx.beginPath();
      if (s) ctx.ellipse(hx + s * 5.5, hy + 3, 5.5, 4.5, 0, 0, Math.PI * 2);
      else ctx.ellipse(16, H - 13, 5.5, 4.5, 0, 0, Math.PI * 2);
      ctx.fill();
      // The dark of an open mouth is the whole of the meanness, and it is
      // drawn inside the snout rather than off the end of it: teeth that
      // reach past the muzzle they are set in stop reading as a mouth and
      // start reading as a comb.
      ctx.fillStyle = MOUTH;
      ctx.beginPath();
      if (s) ctx.ellipse(hx + s * 6, hy + 4, 4.2, 2.6, 0, 0, Math.PI * 2);
      else ctx.ellipse(16, H - 12, 4.2, 3, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = TOOTH;
      if (s) {
        // Three up and two down, the lower set offset so the jaws
        // interlock the way a mouth caught open does. Fewer and bigger,
        // because a row of small even teeth is a comb and a few large
        // uneven ones are fangs.
        for (let i = 0; i < 3; i++) ctx.fillRect(hx + s * (3.2 + i * 2.4) - 0.7, hy + 2, 1.4, 2.1);
        for (let i = 0; i < 2; i++) ctx.fillRect(hx + s * (4.4 + i * 2.4) - 0.7, hy + 5, 1.4, 1.9);
      } else {
        for (let i = -2; i <= 2; i++) ctx.fillRect(16 + i * 1.9 - 0.6, H - 14.4, 1.2, 2);
        for (let i = -1; i <= 1; i++) ctx.fillRect(16 + i * 1.9 - 0.6, H - 10.6, 1.2, 1.8);
      }
      // A brow ridge over the eyes, which is the difference between small
      // eyes and mean ones.
      ctx.fillStyle = DARK;
      // Low on the skull and close over the muzzle, which is where a
      // bear's eyes are. Set high they become a browline across the top
      // of the head, and a long head with its eyes at the top of it is
      // a horse or a moose — the same tell as the snout, read the other
      // way up.
      if (s) ctx.fillRect(hx - 3, hy - 3.5, 9, 2.2);
      else ctx.fillRect(16 - 7, H - 21, 14, 2.2);
      eyes(s ? hx + s * 2 : 16, s ? hy - 0.5 : H - 18, "#d8422a");
      break;
    }
    case 102: // Longbottom's shed kobold (empty-areas site 1): the kobold, in Tobold's sheds
    case 21: // kobold: a small hunched dog-faced scrapper, rust-brown
      ctx.fillStyle = "#8a5a3a";
      ctx.fillRect(11, H - 9 + legOff, 4, 8); // short legs
      ctx.fillRect(17, H - 9 - legOff, 4, 8);
      ctx.fillStyle = "#a06a44";
      ctx.beginPath(); // hunched little body
      ctx.ellipse(16, H - 14, 7, 6, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.beginPath(); // an oversized head for the frame
      ctx.arc(16 + facingSide * 3, H - 23, 6, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = "#8a5a3a";
      ctx.beginPath(); // the snout, leading the way
      ctx.ellipse(16 + facingSide * 8, H - 22, 3, 2, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.beginPath(); // tall pointed ears
      ctx.moveTo(11 + facingSide * 3, H - 27);
      ctx.lineTo(13 + facingSide * 3, H - 33);
      ctx.lineTo(15 + facingSide * 3, H - 27);
      ctx.moveTo(17 + facingSide * 3, H - 27);
      ctx.lineTo(19 + facingSide * 3, H - 33);
      ctx.lineTo(21 + facingSide * 3, H - 27);
      ctx.closePath();
      ctx.fill();
      eyes(16 + facingSide * 3, H - 25, "#ffd24a");
      break;
    // The dwarves. Five kinds of them, and they are deliberately one
    // drawing with five sets of numbers rather than five drawings, because
    // that is the argument this file has been making to itself for a while
    // and nobody has written down: the winter wolf is the wolf with four
    // colours changed, the bear is "a wolf's build scaled into a boulder"
    // by its own comment, and both were typed out again in full anyway.
    //
    // So a dwarf is a short wide body, a beard that covers most of it, a
    // helm, and boots. What tells a soldier from a guard from the royal
    // pair is the colour of those four things, how tall the helm sits, and
    // what is laid over the coat or stands off the helm — not the geometry,
    // which is identical. Adding the next kind is a row in the table
    // below, not a case in this switch.
    case 27: // dwarf: broad, bearded, helmed, and shorter than everything
    case 28: // dwarf soldier: the same body in a quilted coat and an iron cap
    case 32: // dwarf guard
    case 29: // dwarf king
    case 30: // dwarf queen
    case 31: // dwarf geomancer
    case 90: // Dáin Ironfoot, lord of the Iron Hills (see people.go)
    case 156: // a dwarf shade of Belegost: a dwarf gone grey and see-through
    case 157: // the Shade of Azaghâl: the same, crowned and masked
    case 181: // the warden of the Wolf-watch, one of Dáin's (see wolfwatch.go)
    case 161: { // Hánar, the stonewright in the Glittering Caves (see aglarond.go)
      const d = DWARVES[outfit];
      const ghost = SHADE_DWARVES.has(outfit);
      if (ghost) ctx.globalAlpha *= SHADE_ALPHA;
      // Boots first, so the legs come out from under the body rather than
      // over it. They are short and set wide: a dwarf is built low.
      ctx.fillStyle = d.boot;
      ctx.fillRect(11, H - 8 + legOff, 5, 7);
      ctx.fillRect(16, H - 8 - legOff, 5, 7);
      // The body, wider than it is tall.
      ctx.fillStyle = d.coat;
      ctx.beginPath();
      ctx.ellipse(16, H - 13, 9, 7, 0, 0, Math.PI * 2);
      ctx.fill();
      // Arms, hanging at the sides and swinging against the legs.
      ctx.fillRect(6, H - 17 - legOff, 4, 9);
      ctx.fillRect(22, H - 17 + legOff, 4, 9);
      // The quilting of a padded coat, for the kind that wears one: rows
      // of stitching across the body, which is what makes the coat read
      // as a gambeson rather than as the plain leather the axeman wears.
      if (d.quilt) {
        ctx.fillStyle = d.quilt;
        for (const y of [H - 17, H - 14, H - 11]) ctx.fillRect(9, y, 14, 1);
        ctx.fillRect(16, H - 19, 1, 11);
      }
      // The metal laid over the coat, when there is any: a pauldron
      // capping each shoulder and a band round the hem. They go on after
      // the arms so the pauldrons sit over them and swing with them, and
      // before the head so the beard falls over the gorget rather than
      // under it. Together they are what says "armoured" at a glance — a
      // coloured coat alone reads as clothing however rich the colour is.
      if (d.trim) {
        ctx.fillStyle = d.trim;
        ctx.fillRect(6, H - 18 - legOff, 4, 3); // the left pauldron
        ctx.fillRect(22, H - 18 + legOff, 4, 3); // the right
        ctx.fillRect(9, H - 9, 14, 2); // the band at the hem
      }
      // The head, mostly hidden between the shoulders.
      ctx.fillStyle = d.skin;
      ctx.beginPath();
      ctx.arc(16 + facingSide * 2, H - 23, 6, 0, Math.PI * 2);
      ctx.fill();
      // The beard: the widest thing on a dwarf, and the reason the head
      // reads as a dwarf's head rather than a short man's. It hangs from
      // the cheekbones to below the chin and spreads as it falls.
      ctx.fillStyle = d.beard;
      ctx.beginPath();
      ctx.moveTo(10 + facingSide * 2, H - 24);
      ctx.lineTo(22 + facingSide * 2, H - 24);
      ctx.lineTo(20 + facingSide * 2, H - 14);
      ctx.lineTo(12 + facingSide * 2, H - 14);
      ctx.closePath();
      ctx.fill();
      // The helm, sitting low on the brow. Its height is the one number
      // that changes the silhouette between the three, so a king reads as
      // a king from across the room.
      ctx.fillStyle = d.helm;
      ctx.fillRect(10 + facingSide * 2, H - 29 - d.crown, 12, 5 + d.crown);
      ctx.fillStyle = d.helmLit;
      ctx.fillRect(10 + facingSide * 2, H - 29 - d.crown, 12, 2);
      // A war-mask over the face, for the lord who wears one: a plate of
      // iron from the helm's rim to the beard's top, which the eyes look
      // out of (they are drawn after it).
      if (d.mask && dir !== 0) {
        ctx.fillStyle = d.mask;
        ctx.fillRect(11 + facingSide * 3, H - 27, 10 - Math.abs(facingSide) * 3, 6);
      }
      // The shape of what rises, when anything does. Points stand up off
      // the top edge in the lit gold, evenly along the crown's width; the
      // stones sit in the band under them, two of them, with a fleck of
      // light on each so they read as cut stone and not as a wound.
      if (d.peaks > 0) {
        const left = 10 + facingSide * 2;
        const top = H - 29 - d.crown;
        const span = 12 / d.peaks;
        ctx.fillStyle = d.helmLit;
        ctx.beginPath();
        for (let i = 0; i < d.peaks; i++) {
          ctx.moveTo(left + i * span, top);
          ctx.lineTo(left + (i + 0.5) * span, top - 3);
          ctx.lineTo(left + (i + 1) * span, top);
        }
        ctx.closePath();
        ctx.fill();
      }
      if (d.jewels) {
        const left = 10 + facingSide * 2;
        const band = H - 29 - d.crown + 2;
        for (const x of [left + 2, left + 8]) {
          ctx.fillStyle = d.jewels;
          ctx.fillRect(x, band, 2, 2);
          ctx.fillStyle = "#ffb0b6";
          ctx.fillRect(x, band, 1, 1);
        }
      }
      // The crest, for the kind that has one: a block of colour standing
      // off the top of the helm, narrower than the helm and taller than
      // the crown any of the others wear. It is drawn after the helm so
      // it sits on it, and it is the one thing that changes this body's
      // outline against a plain dwarf's at the distance a hall is read
      // at — a colour swap can be missed and a silhouette cannot.
      if (d.plume) {
        const left = 10 + facingSide * 2;
        const top = H - 29 - d.crown;
        ctx.fillStyle = d.plume;
        ctx.fillRect(left + 4, top - 5, 4, 5);
        ctx.fillRect(left + 5, top - 7, 2, 2); // the tuft at its peak
      }
      // The gorget, over the beard rather than under it: a collar of the
      // same metal closing the gap between the helm and the coat, which
      // is where a body without one reads as a head resting on a bundle
      // of cloth. It goes on last of the trim for that reason.
      if (d.trim) {
        ctx.fillStyle = d.trim;
        ctx.fillRect(11 + facingSide * 2, H - 20, 10, 2);
      }
      // The eyes sit under the helm's rim, in the gap above the beard.
      eyes(16 + facingSide * 2, H - 26, d.eye);
      // The geomancer's handful of lava, held out at the end of the arm
      // on the side it is facing: a hot core in a cooler crust, lighting
      // the hand that holds it. It is drawn last so it sits over the arm.
      if (d.ember) {
        const hx = facingSide < 0 ? 8 : 24;
        const hy = H - 9 + (facingSide < 0 ? -legOff : legOff);
        ctx.fillStyle = "rgba(214,60,12,0.55)";
        ctx.beginPath();
        ctx.arc(hx, hy, 4, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = d.ember;
        ctx.beginPath();
        ctx.arc(hx, hy, 2.5, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = "#fff6d0";
        ctx.fillRect(hx - 1, hy - 1, 2, 2);
      }
      if (ghost) ctx.globalAlpha /= SHADE_ALPHA;
      break;
    }
    // --- 12. Belegost (2026-10-16).
    case 175: // a fell beast, a Nazgûl's steed: a dragon a little smaller, of naked grey hide (see drawFellBeast)
      drawFellBeast(ctx, dir, legOff, H);
      break;
    case 155: // a cold-drake: a dragon smaller, wingless, grey and frosted (see drawColdDrake)
      drawColdDrake(ctx, dir, legOff, H, eyes);
      break;
    // --- end of 12. Belegost
    case 24: // dragon
      drawDragon(ctx, dir, legOff, H, eyes);
      break;
    case 37: // dragon lord: the dragon, bigger, dark red and gold, crowned
      drawDragonLord(ctx, dir, legOff, H, eyes);
      break;
    case 179: // Scatha the Worm: the cold-drake grown great, gold in his belly (see drawScatha)
      drawScatha(ctx, dir, legOff, H, eyes);
      break;
    case 180: { // the white wolf of the Fell Winter: the winter wolf, bigger and whiter
      ctx.save();
      ctx.translate(16, H);
      ctx.scale(WHITE_WOLF_SCALE, WHITE_WOLF_SCALE);
      ctx.translate(-16, -H);
      drawWolf(ctx, WOLF_FELL, dir, frame, legOff, H, eyes);
      ctx.restore();
      break;
    }
    case 165: // Smaug: the dragon bigger again, red-gold, his belly jewelled (see drawSmaug)
      drawSmaug(ctx, dir, legOff, H, eyes);
      break;
    case 26: // the Balrog: its step in frame % 3 and its flame in frame / 3 (see BLAZES)
      drawDemon(ctx, dir, [0, 2, -2][frame % 3], H, eyes, Math.floor(frame / 3));
      break;
    case 41: // fire elemental: a living flame, flickering (see drawFireElemental)
      drawFireElemental(ctx, dir, frame, H);
      break;
    case 33: // siren: half out of the water, and never out of it
      drawSiren(ctx, dir, legOff, H, eyes);
      break;
    case 22: // winter wolf: a wolf gone white, frost on its breath
      drawWolf(ctx, WOLF_WHITE, dir, frame, legOff, H, eyes);
      if (dir !== 0) {
        ctx.fillStyle = "#bfe4ff"; // the cold hanging off its muzzle
        const s = facingSide;
        if (s) {
          ctx.fillRect(16 + s * 20, H - 25 - (legOff ? 1 : 0), 2, 2);
          ctx.fillRect(16 + s * 23, H - 23, 1.5, 1.5);
        } else {
          ctx.fillRect(13, H - 17, 2, 2);
          ctx.fillRect(18, H - 16, 1.5, 1.5);
        }
      }
      break;
    case 23: // barrow wight: a gaunt shrouded thing wearing its old crown (see drawWight)
      drawWight(ctx, legOff, H, eyes, WIGHT_KIT);
      break;
    case 89: // the Wight-lord of Carn Dûm: the wight taller, blacker, in an iron crown (see drawWightLord)
      drawWightLord(ctx, legOff, H, eyes);
      break;
    case 15: // skeleton
      ctx.fillStyle = "#e2dbc7";
      ctx.fillRect(12, H - 13 + legOff, 3, 12); // leg bones
      ctx.fillRect(18, H - 13 - legOff, 3, 12);
      ctx.fillRect(14, H - 24, 5, 12); // spine
      for (let i = 0; i < 4; i++) {
        // Ribs, narrowing towards the waist.
        const w = 12 - i * 2;
        ctx.fillRect(16 - w / 2, H - 23 + i * 3, w, 2);
      }
      ctx.fillRect(9, H - 25 - legOff / 2, 3, 11); // arms
      ctx.fillRect(21, H - 25 + legOff / 2, 3, 11);
      ctx.fillStyle = "#f2ecdb";
      ctx.beginPath();
      ctx.arc(16, H - 31, 6, 0, Math.PI * 2); // skull
      ctx.fill();
      ctx.fillRect(13, H - 29, 6, 4); // jaw
      if (dir !== 0) {
        ctx.fillStyle = "#1a1a1a";
        if (facingSide) {
          ctx.fillRect(16 + facingSide * 2, H - 33, 3, 3);
        } else {
          ctx.fillRect(12, H - 33, 3, 3);
          ctx.fillRect(17, H - 33, 3, 3);
        }
      }
      break;
    default:
      ctx.fillStyle = "#f0f";
      ctx.fillRect(8, H - 24, 16, 22);
  }
  if ((hand && !hand.behind) || (shieldArm && !shieldArm.behind)) {
    drawHeld();
  }
}

/**
 * Player: legs, tunic, arms, head, and then whatever they are wearing and
 * holding on top. The tunic colour is the character's identity (picked from
 * the name), so armor is a cuirass that leaves the hem showing rather than a
 * coat that hides it: two players in chain mail still tell apart.
 */
/**
 * Whether this facing shows a character's back rather than their front.
 *
 * Four facings, two camps: north and west are going away from you, south
 * and east are coming towards you. It is the one fact the armor and the
 * hands both have to agree about, so it is asked once here rather than
 * spelled out in each of them — a cuirass showing its neckline on a back,
 * or a sword changing hands without the character turning round, is what
 * two copies of this drifting apart looks like.
 *
 * West being a back view and east a front view is a choice rather than a
 * fact about geometry: a profile strictly shows neither, and the opposite
 * pairing would draw just as well. Splitting them this way gives each camp
 * one straight-on facing and one profile, so both faces of the armor get
 * equal use, and it is what makes the weapon change sides exactly once
 * going round the compass instead of three times.
 */
function backView(dir: number): boolean {
  return dir === 0 || dir === 3;
}

function drawPlayer(
  ctx: Ctx,
  outfit: number,
  dir: number,
  legOff: number,
  gear: Gear,
  eyes: (cx: number, cy: number) => void,
  color?: string,
  admin = false,
  race?: string,
  eye?: string,
  hair = 0,
) {
  const H = TILE + 16;
  const facingSide = dir === 1 ? 1 : dir === 3 ? -1 : 0;
  const front = dir === 2;
  // The race's build: the whole figure stretched about the point between
  // the feet, so a hobbit is small and a dwarf short and broad. What they
  // hold is not (2026-11-02: "don't scale items when carried by dwarves or
  // hobbits, but do make sure to position them correctly in the hands"):
  // it goes where their hands are, and is drawn there at its own size
  // (see fullSize). What they wear is their size, as it is on them.
  const look = raceLook(race, hair);
  ctx.save();
  unbuild = null;
  if (look.sx !== 1 || look.sy !== 1) {
    const before = typeof ctx.getTransform === "function" ? ctx.getTransform() : undefined;
    ctx.translate(16, H);
    ctx.scale(look.sx, look.sy);
    ctx.translate(-16, -H);
    if (before && isAffine(before)) unbuild = undoStretch(before, look.sx, look.sy);
  }
  // A piece in a hand, drawn full size about the hand holding it.
  const held = (c: Ctx, x: number, y: number, draw: () => void) => {
    c.save();
    fullSize(c, x, y);
    draw();
    c.restore();
  };
  // An arm on one side of the body, viewer's left (-1) or right (+1). Arms
  // swing with the legs, so the hand does too, and whatever is held tilts
  // away from the body, so a blade never crosses the face.
  const arm = (side: -1 | 1) => ({
    x: side > 0 ? 24.5 + (facingSide > 0 ? 1 : 0) : 6.5 - (facingSide < 0 ? 1 : 0),
    y: H - 16 + (side * legOff) / 2,
    out: side,
  });
  // Handedness never changes: the weapon is in the right hand and the shield
  // on the left arm whichever way the player faces. What changes is which
  // side of the picture that hand comes out on, and it changes exactly once
  // going round the compass — on the turn from seeing somebody's front to
  // seeing their back, because a back view mirrors them. Coming towards
  // you, south and east, the weapon is on the viewer's left; going away,
  // north and west, it is on their right, and the shield is opposite it
  // both times.
  //
  // Every facing but the front used to put the weapon on the right, which
  // made it change sides three times in a circuit. Walking a character
  // round a square read as the sword being thrown from hand to hand at
  // every corner — and worse, walking east and then south flipped it while
  // the character never turned away from you at all.
  //
  // Which camp a profile belongs to is the same question the armor asks
  // about its collar, and it is answered in one place for both: backView.
  let weaponHand = arm(backView(dir) ? 1 : -1);
  const shieldArm = arm(backView(dir) ? -1 : 1);
  // Which of the two is hidden by the body follows from the side it is on
  // rather than being listed per facing: in profile it is the arm further
  // from the camera, and from behind it is both. The camera is to the
  // south, so walking east somebody shows it their right side and walking
  // west their left — and the camps above put the right hand on the
  // viewer's left going east and the left hand there going west, so in
  // either profile the near arm is the one on the viewer's left. Deriving
  // it means the hand and its occlusion cannot disagree, which is exactly
  // what they would do if the sides above were changed and a list of
  // facings here was not.
  //
  // It used to hide whichever arm trailed (2026-10-12: "Check that the main
  // hand is on the correct side of creatures for each direction they can
  // move"). That is the far arm going west, but going east it is the sword
  // arm, the near one: the sword went behind the body and the shield in
  // front of it, and everybody walking east read as left-handed.
  const behind = (side: -1 | 1) => (facingSide ? side > 0 : dir === 0);
  let weaponBehind = behind(weaponHand.out as -1 | 1);
  const shieldBehind = behind(shieldArm.out as -1 | 1);
  // A ranged weapon is aimed the way you face, so in profile it sits in
  // the leading hand, in front of the body — never trailing behind it.
  // The bow takes both hands anyway, so the shield is never on that arm.
  if ((gear.weapon === 21 || gear.weapon === 62 || gear.weapon === CROSSBOW_ID) && facingSide) {
    weaponHand = arm(facingSide as -1 | 1);
    weaponBehind = false;
  }
  // Anything needing both hands is held across the body, in front of it —
  // except from behind, where it goes on the back like anything else you
  // are walking away from.
  const bothHands = twoHandedIds.has(gear.weapon);
  if (bothHands) weaponBehind = dir === 0;
  // A hobbit hauls a giant sword along the ground rather than holding it
  // up: see DRAGGED.
  const drag = look === RACE_LOOKS.hobbit;
  // Holding a two-hander in front, the hands are up on it rather than
  // hanging at the sides: the arms stop at the elbow, and the forearms and
  // fists go on over the weapon once it is drawn (see markGrip).
  const holding = bothHands && !weaponBehind && typeof ctx.getTransform === "function";

  // Whatever is on the far side goes down first, so the body and the arm
  // cover it; anything trailing is pushed out so it still shows past the
  // shoulder instead of being swallowed by the body. How far it has to go
  // depends on what is in the way: in profile the body is a narrow edge,
  // from behind it is the whole of somebody's back.
  // A rock is all hand. Everything else held is long enough that pushing
  // it clear still leaves the haft crossing the fist; push a rock and the
  // whole of it leaves the hand, which is what made it look like a stone
  // hanging in the air beside somebody rather than one they are holding.
  //
  // The same push whichever way you face. The back view used to get more of
  // one, on the reasoning that a back is wider to clear than a flank — but
  // the body is drawn out of the same rectangles in every facing, so there
  // was nothing extra to clear, and all the extra bought was a sword
  // floating a visible gap away from the fist supposedly holding it.
  const push = gear.weapon === 34 ? 0 : 2;
  if (weaponBehind) {
    if (bothHands) ringedPiece(ctx, gear.weapon, gear.wmake, (c) => drawSlung(c, gear.weapon, H, drag, false));
    else ringedPiece(ctx, gear.weapon, gear.wmake, (c) => held(c, weaponHand.x + push * weaponHand.out, weaponHand.y, () => drawWeapon(c, gear.weapon, weaponHand.x + push * weaponHand.out, weaponHand.y, weaponHand.out)));
  }
  if (shieldBehind) ringedPiece(ctx, gear.shield, gear.smake, (c) => held(c, shieldArm.x + push * shieldArm.out, shieldArm.y, () => drawShield(c, gear.shield, shieldArm.x + push * shieldArm.out, shieldArm.y, shieldArm.out)));

  // An admin's shirt is their uniform rather than their dealt colour. They
  // keep that colour everywhere it says who they are — their name, their
  // damage numbers, their blip on the minimap — and give it up only on the
  // one square of cloth where the uniform has to be read instead.
  const tunic = admin ? ADMIN_SHIRT : color || PLAYER_TUNICS[outfit % PLAYER_TUNICS.length];
  ctx.fillStyle = admin ? ADMIN_TROUSERS : "#3a2a1a";
  ctx.fillRect(11, H - 12 + legOff, 4, 10);
  ctx.fillRect(17, H - 12 - legOff, 4, 10);
  ctx.fillStyle = tunic;
  ctx.fillRect(9, H - 28, 14, 17);
  // The arms. The one on the side being faced stands a pixel further out
  // than the other, which is what gives a profile its near shoulder — but
  // it is pushed out rather than moved out: the rect grows by the pixel it
  // gains, so its inner edge still meets the tunic at 9 and 23. Moving it
  // left a one-pixel column of whatever was behind the character showing
  // between the arm and the body, in the direction faced and no other,
  // which read as a seam down one side of everybody walking east or west.
  const lean = (side: -1 | 1) => (facingSide === side ? 1 : 0)
  // A hobbit goes barefoot: big feet at the ends of the trousers.
  if (look.bareFeet) {
    ctx.fillStyle = look.skin;
    ctx.fillRect(10 - (facingSide < 0 ? 1 : 0), H - 3 + legOff, 6, 3);
    ctx.fillRect(16 + (facingSide > 0 ? 1 : 0), H - 3 - legOff, 6, 3);
  }
  ctx.fillStyle = look.skin;
  const armLen = holding ? 6 : 10;
  ctx.fillRect(6 - lean(-1), H - 26 - legOff / 2, 3 + lean(-1), armLen);
  ctx.fillRect(23, H - 26 + legOff / 2, 3 + lean(1), armLen);
  ringedPiece(ctx, gear.armor, gear.amake, (c) => drawWornArmor(c, gear.armor, dir, legOff));
  // The belt goes on over the armor's hem and under the cloak (2026-10-22).
  drawWornBelt(ctx, gear.belt ?? 0, dir);
  // The cloak goes on over the armor (2026-10-04: "make the various elf
  // items equippable in addition to other items where it makes sense").
  ringedPiece(ctx, gear.cloak ?? 0, gear.cmake, (c) => drawWornCloak(c, gear.cloak ?? 0, dir, legOff));
  // An elf's long hair falls behind the head to the shoulders, so it goes
  // down before the head except from behind, where it is what you see.
  if (look.longHair && dir !== 0) drawLongHair(ctx, look, H);
  ctx.fillStyle = look.skin;
  ctx.beginPath();
  ctx.arc(16, H - 34, 7, 0, Math.PI * 2);
  ctx.fill();
  // Pointed ears, standing out past the side of the head and up: both of
  // them from in front and from behind. In profile see below, after the
  // hair.
  if (look.ears && !facingSide) {
    ctx.fillStyle = look.skin;
    for (const side of [-1, 1] as const) {
      const x = 16 + side * 6;
      ctx.beginPath();
      ctx.moveTo(x, H - 37);
      ctx.lineTo(x + side * 4.5, H - 40.5);
      ctx.lineTo(x + side * 2, H - 33);
      ctx.lineTo(x, H - 32);
      ctx.fill();
    }
  }
  ctx.fillStyle = look.hair;
  ctx.beginPath();
  ctx.arc(16, H - 36, 7, Math.PI, Math.PI * 2);
  ctx.fill();
  // A hobbit's curls: bumps along the top of the hair. Not under a helm
  // (2026-10-01: "don't show hobbit hair if wearing a helmet"): they stand
  // out past where any helm sits, and a ring of curls round the iron read
  // as hair growing through it. The cap of hair under them is the helm's
  // to cover, as anybody else's is. The Elendilmir is a band rather than a
  // cap, so they show round it.
  if (look.curls && (!gear.helm || gear.helm === 88)) {
    for (const [cx, cy] of [[9.5, -35], [11, -39], [14.5, -41], [18, -41], [21, -39], [22.5, -35]]) {
      ctx.beginPath();
      ctx.arc(cx, H + cy, 2.6, 0, Math.PI * 2);
      ctx.fill();
    }
  }
  if (look.longHair && dir === 0) drawLongHair(ctx, look, H);
  // Side on, the one ear that shows is on the side of the head, a little
  // behind the middle of it, and it sweeps back and up past the back of
  // the head through the hair. It used to be drawn on the side being
  // faced, which put it out in front of the face beside the eye (2026-10-01:
  // "when walking west or east, the elf's ears are rendering on their face
  // instead of where it should be"). It goes on after the hair, which it
  // pokes out of, with a shadow down its inside so it reads against the
  // cheek it starts on.
  if (look.ears && facingSide) {
    const back = -facingSide;
    ctx.fillStyle = look.skin;
    ctx.beginPath();
    ctx.moveTo(16 + back * 1, H - 35.5);
    ctx.lineTo(16 + back * 10.5, H - 41);
    ctx.lineTo(16 + back * 5.5, H - 32);
    ctx.lineTo(16 + back * 2, H - 31.5);
    ctx.fill();
    ctx.fillStyle = EAR_SHADE;
    ctx.beginPath();
    ctx.moveTo(16 + back * 2.5, H - 34.5);
    ctx.lineTo(16 + back * 8, H - 39);
    ctx.lineTo(16 + back * 4.5, H - 33);
    ctx.fill();
  }
  // A crown is worn on the head rather than in place of it, so the hair
  // goes down first and the gold sits on it.
  if (admin) drawAdminCrown(ctx, H);
  // A dwarf's beard, from the chin down over the chest: seen from the
  // front, and out ahead of the face in profile, never from behind.
  if (look.beard && dir !== 0) {
    const off = facingSide * 3;
    ctx.fillStyle = look.beard;
    ctx.beginPath();
    ctx.moveTo(9.5 + off, H - 34);
    ctx.lineTo(11.5 + off, H - 31.5);
    ctx.lineTo(20.5 + off, H - 31.5);
    ctx.lineTo(22.5 + off, H - 34);
    ctx.lineTo(22 + off, H - 25);
    ctx.lineTo(16 + off, H - 18);
    ctx.lineTo(10 + off, H - 25);
    ctx.fill();
  }
  // The helm goes on after the head, since it is on it: over the hair,
  // with the rim above the eyes so the face still shows under it.
  // A helm whose mask covers the face draws the eyes into its own slits
  // (see helmIsTheEyes): a lock's red when they have one, and otherwise
  // the mask's own dark, whatever colour the eyes behind it are.
  const masked = helmIsTheEyes(gear.helm, dir);
  ringedPiece(ctx, gear.helm, gear.hmake, (c) => drawWornHelm(c, gear.helm, dir, eye === EYE_SKULL ? eye : undefined));

  // Whatever is on the near side goes on top of the arm holding it.
  if (!weaponBehind) {
    if (bothHands && holding) {
      gripMarks = [];
      const back = invertAffine(ctx.getTransform());
      try {
        ringedPiece(ctx, gear.weapon, gear.wmake, (c) => drawCrossed(c, gear.weapon, 16, H - 13 + legOff / 2, facingSide || 1, facingSide, drag, false));
      } finally {
        const marks = gripMarks.map((m) => applyAffine(back, m.x, m.y));
        gripMarks = null;
        drawHolds(ctx, marks, look.skin, H, legOff, lean);
      }
    } else if (bothHands) ringedPiece(ctx, gear.weapon, gear.wmake, (c) => drawCrossed(c, gear.weapon, 16, H - 13 + legOff / 2, facingSide || 1, facingSide, drag, false));
    else ringedPiece(ctx, gear.weapon, gear.wmake, (c) => held(c, weaponHand.x, weaponHand.y, () => drawWeapon(c, gear.weapon, weaponHand.x, weaponHand.y, weaponHand.out)));
  }
  if (!shieldBehind) ringedPiece(ctx, gear.shield, gear.smake, (c) => held(c, shieldArm.x, shieldArm.y, () => drawShield(c, gear.shield, shieldArm.x, shieldArm.y, shieldArm.out)));
  // And the eyes go on last of all, so nothing worn or held hides them
  // (2026-10-26: "make sure no equipment conceals the player's eyes"):
  // their colour says things, a protection zone lock's red for one, and
  // the war-mask of Belegost, the Elendilmir's light and the top corner of
  // a tall shield held in front all covered one or both. A helm that
  // covers the face has its eyes in its slits instead.
  if ((front || facingSide) && !masked) eyes(16, H - 35);
  unbuild = null;
  ctx.restore();
}

/**
 * The two hands on a two-hander held in front: a forearm from each elbow
 * to its hold, and a fist over the weapon there. The left arm takes the
 * hold further left, so the forearms never cross each other. A weapon that
 * marked no holds is left as it was drawn, hands and all.
 */
function drawHolds(
  ctx: Ctx,
  marks: Point[],
  skin: string,
  H: number,
  legOff: number,
  lean: (side: -1 | 1) => number,
) {
  if (marks.length < 2) return;
  const [a, b] = marks[0].x <= marks[1].x ? [marks[0], marks[1]] : [marks[1], marks[0]];
  const elbows = [
    { x: 7.5 - lean(-1) / 2, y: H - 21 - legOff / 2, to: a },
    { x: 24.5 + lean(1) / 2, y: H - 21 + legOff / 2, to: b },
  ];
  // Filled rather than stroked, as the arms are: a stroke here would be
  // one more line among the weapon's own to anything reading them.
  const forearm = (e: (typeof elbows)[number], half: number) => {
    const dx = e.to.x - e.x, dy = e.to.y - e.y;
    const len = Math.hypot(dx, dy) || 1;
    const nx = (-dy / len) * half, ny = (dx / len) * half;
    ctx.beginPath();
    ctx.moveTo(e.x + nx, e.y + ny);
    ctx.lineTo(e.to.x + nx, e.to.y + ny);
    ctx.lineTo(e.to.x - nx, e.to.y - ny);
    ctx.lineTo(e.x - nx, e.y - ny);
    ctx.closePath();
    ctx.fill();
  };
  ctx.save();
  for (const e of elbows) {
    ctx.fillStyle = FIST_EDGE;
    forearm(e, 2);
    ctx.fillStyle = skin;
    forearm(e, 1.5);
  }
  for (const e of elbows) {
    ctx.fillStyle = FIST_EDGE;
    ctx.beginPath();
    ctx.arc(e.to.x, e.to.y, 2.4, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = skin;
    ctx.beginPath();
    ctx.arc(e.to.x, e.to.y, 1.8, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.restore();
}

/** The line round a fist and a forearm on a weapon: knuckles in shadow. */
const FIST_EDGE = "rgba(60, 35, 20, 0.55)";

/**
 * How each race looks (2026-09-29: "Include a sample picture with each race
 * when you choose"). The chooser draws its pictures through drawPlayer with
 * these, so what it shows is the game's own drawing of the body. A man is
 * exactly how every player was drawn before there were races.
 */
export interface RaceLook {
  key: string;
  /** Stretch across and up, about the point between the feet. */
  sx: number;
  sy: number;
  skin: string;
  hair: string;
  /** A dwarf's beard colour; empty for none. */
  beard: string;
  /** The eyes, when nothing is turning them red. */
  eye: string;
  ears: boolean;
  longHair: boolean;
  curls: boolean;
  bareFeet: boolean;
}

/** The inside of an elf's ear in profile: the skin in shadow. */
const EAR_SHADE = "#d4b29c";

export const RACE_LOOKS: Record<"man" | "elf" | "dwarf" | "hobbit", RaceLook> = {
  man: { key: "man", sx: 1, sy: 1, skin: "#e0b090", hair: "#4a2a10", beard: "", eye: "#3a2416", ears: false, longHair: false, curls: false, bareFeet: false },
  elf: { key: "elf", sx: 0.94, sy: 1, skin: "#f2d8c4", hair: "#3a2a1e", beard: "", eye: "#45525e", ears: true, longHair: true, curls: false, bareFeet: false },
  dwarf: { key: "dwarf", sx: 1.14, sy: 0.82, skin: "#d8a47c", hair: "#3e2614", beard: "#4a2e18", eye: "#3a2416", ears: false, longHair: false, curls: false, bareFeet: false },
  hobbit: { key: "hobbit", sx: 0.8, sy: 0.74, skin: "#e6b890", hair: "#5a3210", beard: "", eye: "#3a2416", ears: false, longHair: false, curls: true, bareFeet: true },
};

/**
 * One way a race's hair can go: its share of the race in hundredths, the
 * hair, the beard to match on a race that has one, and the eyes.
 */
interface HairShade {
  share: number;
  hair: string;
  beard?: string;
  eye: string;
}

/**
 * The hair and eyes Tolkien gives each people, and how many of a race have
 * which (2026-10-27: "lets fix the hair and eye colors according to
 * tolkien lore"; the comparison is the doc "Hair and eye colours vs
 * Tolkien"). The first of each is the race's own look in RACE_LOOKS, its
 * commonest, and what anybody the server gives no hair is drawn with.
 *
 * Elves are dark-haired and grey-eyed, "save in the golden house of
 * Finarfin" (LotR, Appendix F): dark for the Noldor and most Sindar,
 * golden for Vanyarin blood, silver for the kin of Thingol and Celeborn,
 * and a few of the red-brown of Nerdanel's kin that Maedhros and the
 * twins had. Men are brown for Bree and most of Bëor's folk (brown eyes
 * going with it), black and grey-eyed for the Dúnedain, flaxen and
 * blue-eyed for Hador's line and the Rohirrim. Hobbits are brown, darker
 * for the Harfoots and Stoors, fairer for the Fallohides. Dwarves have no
 * colour of their own in the books, only Thorin's company's spread: the
 * yellow is Fíli's and Kíli's.
 *
 * The eyes are dark shades of their colour, not the colour itself: two
 * pixels of light grey or sky blue would wash into the skin, and a lock's
 * red has to stand out from all of them.
 */
const HAIR_SPREADS: Record<keyof typeof RACE_LOOKS, HairShade[]> = {
  elf: [
    { share: 35, hair: "#3a2a1e", eye: "#45525e" }, // dark brown: Noldor, Sindar
    { share: 25, hair: "#1c1a1e", eye: "#45525e" }, // black
    { share: 20, hair: "#e6d27a", eye: "#45525e" }, // golden: Vanyar, Finarfin's house
    { share: 15, hair: "#d4d8dc", eye: "#45525e" }, // silver: Thingol's and Celeborn's kin
    { share: 5, hair: "#8a3a1c", eye: "#45525e" }, // red-brown: Nerdanel's kin
  ],
  man: [
    { share: 45, hair: "#4a2a10", eye: "#3a2416" }, // brown: Bree, Bëor's folk
    { share: 25, hair: "#1e1a18", eye: "#45525e" }, // black, grey-eyed: the Dúnedain
    { share: 30, hair: "#d8b45a", eye: "#2c4878" }, // flaxen, blue-eyed: Hador's line, Rohan
  ],
  hobbit: [
    { share: 50, hair: "#5a3210", eye: "#3a2416" }, // brown: Harfoots
    { share: 25, hair: "#3a2210", eye: "#3a2416" }, // dark brown: Stoors
    { share: 25, hair: "#9a6a30", eye: "#3a2416" }, // fair: Fallohides
  ],
  dwarf: [
    { share: 30, hair: "#3e2614", beard: "#4a2e18", eye: "#3a2416" }, // dark brown
    { share: 30, hair: "#1e1a18", beard: "#26201c", eye: "#3a2416" }, // black
    { share: 25, hair: "#7a3814", beard: "#8a4418", eye: "#3a2416" }, // red-brown
    { share: 15, hair: "#b08a3a", beard: "#c09a48", eye: "#3a2416" }, // yellow: Fíli and Kíli
  ],
};

/**
 * Every race's looks, one for each of its shades, the first of them its
 * own entry in RACE_LOOKS: made once, so the same hair is the same look
 * and a sprite's cache key can be built from its key.
 */
const RACE_SHADES = Object.fromEntries(
  (Object.keys(HAIR_SPREADS) as (keyof typeof RACE_LOOKS)[]).map((race) => [
    race,
    HAIR_SPREADS[race].map((h, i): RaceLook =>
      i === 0 ? RACE_LOOKS[race] : { ...RACE_LOOKS[race], key: race + i, hair: h.hair, beard: h.beard ?? "", eye: h.eye },
    ),
  ]),
) as Record<keyof typeof RACE_LOOKS, RaceLook[]>;

/**
 * The look for a race by its key; a man for anything else, no race
 * included. `hair` is where the character's name puts them in their race's
 * spread, 1 to 100, from the server (see hairOf in race.go); without one
 * they are drawn with their race's commonest.
 */
export function raceLook(race?: string, hair = 0): RaceLook {
  if (!race || !Object.prototype.hasOwnProperty.call(RACE_LOOKS, race)) return RACE_LOOKS.man;
  const r = race as keyof typeof RACE_LOOKS;
  let left = hair;
  const spread = HAIR_SPREADS[r];
  for (let i = 0; i < spread.length; i++) {
    left -= spread[i].share;
    if (left <= 0) return RACE_SHADES[r][i];
  }
  return RACE_LOOKS[r];
}

/** An elf's long hair, from the crown down behind the shoulders. */
function drawLongHair(ctx: Ctx, look: RaceLook, H: number) {
  ctx.fillStyle = look.hair;
  ctx.fillRect(9.5, H - 37, 13, 12);
}

/**
 * A shield on the arm at (hx, hy), its face towards the viewer, leaning
 * `out` (+1 right, -1 left) away from the body. The same colours and marks
 * as the ground sprites, so what you picked up is what you are seen behind:
 * the wooden shield is planks with an iron rim and boss, the steel one a
 * bright kite with a cross of ribs.
 */
function drawShield(ctx: Ctx, id: number, hx: number, hy: number, out: number) {
  if (id === 34 || id === 78) {
    // A rock, in the off hand: the hobbit's (2026-09-28: "add a hobbit
    // creature with a stick in one hand and a rock in the other"), and the
    // hill troll's boulder, the same thing bigger (2026-09-28: "give hill
    // trolls a big boulder (scaled up rock) in one hand"). What a monster
    // holds in its other hand comes as its shield, and a rock there is
    // drawn exactly as one in the weapon hand is.
    drawWeapon(ctx, id, hx, hy, out);
    return;
  }
  if (id === 21) {
    // A bow, in the off hand: the bandit's (2026-10-20: "give bandit ... a
    // bow in the other hand"), which it shoots from two tiles off and puts
    // by for its sword when you come up beside it. Drawn as a bow is in the
    // weapon hand.
    drawWeapon(ctx, id, hx, hy, out);
    return;
  }
  if (id === 90) {
    // The phial of Galadriel, held up in the off hand where a shield goes
    // (2026-10-09: "the phial of galadriel works like a shield that emits
    // light"), in a halo of its own light.
    const cx = hx + out * 2, cy = hy - 5;
    ctx.fillStyle = PHIAL.glow;
    ctx.beginPath();
    ctx.arc(cx, cy, 5, 0, Math.PI * 2);
    ctx.fill();
    drawPhial(ctx, cx, cy, 0.5);
    return;
  }
  if (id === 210) {
    // The Arkenstone, held up in the off hand where a shield goes
    // (2026-11-01), in a halo of its own light, as the phial is.
    drawArkenstone(ctx, hx + out * 2, hy - 5, 0.55);
    return;
  }
  if (id === 171) {
    // The wizard's staff, upright in the off hand where a shield goes
    // (2026-10-19: "add wizard's staff. it's an offhand item"), taller
    // than its bearer, the glint in its head over their shoulder.
    const cx = hx + out * 2.5, top = hy - 20, bottom = hy + 9;
    ctx.strokeStyle = WIZARD_STAFF.wood;
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.beginPath();
    ctx.moveTo(cx, bottom);
    ctx.lineTo(cx, top);
    ctx.stroke();
    ctx.fillStyle = WIZARD_STAFF.knot;
    ctx.beginPath();
    ctx.arc(cx, top - 1, 2.6, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = WIZARD_STAFF.glint;
    ctx.beginPath();
    ctx.arc(cx, top - 1, 1.1, 0, Math.PI * 2);
    ctx.fill();
    return;
  }
  if (id === 119) {
    // A torch, held up in the off hand where a shield goes (2026-10-16:
    // "Implement torches"), leaning out the way the hand is.
    drawTorch(ctx, hx + out * 2, hy + 4, 0.75, out * 0.25);
    return;
  }
  if (id === 37) {
    // A buckler is held rather than strapped on: a small disc on the fist,
    // which is why it covers so little and why a kobold can carry one.
    const cx = hx + out * 2, cy = hy - 4;
    ctx.fillStyle = "#8a6a46";
    ctx.beginPath();
    ctx.arc(cx, cy, 6, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = "#5a3a1e";
    ctx.lineWidth = 1.5;
    ctx.stroke();
    ctx.fillStyle = "#8d949c";
    ctx.beginPath();
    ctx.arc(cx, cy, 1.8, 0, Math.PI * 2);
    ctx.fill();
    return;
  }
  if (id === 38) {
    // The tower shield, held: the same slab, and it covers the carrier
    // from shoulder to shin — which is the whole idea, and why a dragon is
    // the only thing that has one to give up.
    // Big enough to read as a tower shield and no bigger: drawn to its
    // full height it hid the character behind it, and a sprite you cannot
    // pick out of a crowd is worse than an inch of realism.
    const cx = hx + out * 2.5, top = hy - 17, bottom = hy + 6;
    ctx.fillStyle = "#7a5a34";
    ctx.beginPath();
    ctx.moveTo(cx - 7, top);
    ctx.lineTo(cx + 7, top);
    ctx.lineTo(cx + 7, bottom - 4);
    ctx.quadraticCurveTo(cx + 7, bottom, cx, bottom);
    ctx.quadraticCurveTo(cx - 7, bottom, cx - 7, bottom - 4);
    ctx.closePath();
    ctx.fill();
    ctx.fillStyle = "#6f767e";
    ctx.fillRect(cx - 7, top + 3, 14, 2.5);
    ctx.fillRect(cx - 7, bottom - 8, 14, 2.5);
    ctx.fillRect(cx - 1, top, 2, bottom - top - 2);
    ctx.strokeStyle = "#4a4f55";
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.moveTo(cx - 7, top);
    ctx.lineTo(cx + 7, top);
    ctx.lineTo(cx + 7, bottom - 4);
    ctx.quadraticCurveTo(cx + 7, bottom, cx, bottom);
    ctx.quadraticCurveTo(cx - 7, bottom, cx - 7, bottom - 4);
    ctx.closePath();
    ctx.stroke();
    return;
  }
  if (id === 44) {
    // The demon shield, held. At this size the two lit eyes are the whole of
    // what reads, and they are the right thing to read: a character coming
    // the other way with two embers behind their arm is carrying the best
    // shield in the world, and that is worth knowing from a tile away.
    const cx = hx + out * 2.5, top = hy - 16, bottom = hy + 6;
    const w = 7.5;
    const face = () => {
      ctx.beginPath();
      ctx.moveTo(cx - w, top);
      ctx.lineTo(cx + w, top);
      ctx.lineTo(cx + w, top + 8);
      ctx.quadraticCurveTo(cx + w, bottom - 1, cx, bottom);
      ctx.quadraticCurveTo(cx - w, bottom - 1, cx - w, top + 8);
      ctx.closePath();
    };
    demonHorn(ctx, cx - w + 2, top + 1.5, -4.5, -7, 2);
    demonHorn(ctx, cx + w - 2, top + 1.5, 4.5, -7, 2);
    ctx.fillStyle = "#2b1013";
    face();
    ctx.fill();
    ctx.fillStyle = "#3f181c";
    ctx.fillRect(cx - 5.5, top + 3, 11, 5); // the brow
    ctx.fillStyle = DEMON.fire;
    ctx.fillRect(cx - 5, top + 4, 4, 2.5);
    ctx.fillRect(cx + 1, top + 4, 4, 2.5);
    ctx.fillStyle = DEMON.ember;
    ctx.fillRect(cx - 4.2, top + 4.7, 1.6, 1);
    ctx.fillRect(cx + 1.8, top + 4.7, 1.6, 1);
    ctx.fillStyle = "#150708";
    ctx.fillRect(cx - 4.5, top + 10, 9, 4); // the mouth and the teeth in it
    ctx.fillStyle = DEMON.horn;
    for (const dx of [-4.5, -2.2, 0.1, 2.4]) ctx.fillRect(cx + dx, top + 10, 1.4, 4);
    ctx.strokeStyle = "#6a564a";
    ctx.lineWidth = 1.5;
    face();
    ctx.stroke();
    return;
  }
  if (id === 89) {
    // The Uruk-hai shield, held: the steel shield's shape, taller and
    // black, with the White Hand on it, which is what is seen from a tile
    // away and the reason Éomer wants them (2026-10-01).
    const cx = hx + out * 2.5, top = hy - 16, bottom = hy + 6;
    const w = 7;
    const outline = () => {
      ctx.beginPath();
      ctx.moveTo(cx - w, top);
      ctx.lineTo(cx + w, top);
      ctx.lineTo(cx + w, top + 10);
      ctx.quadraticCurveTo(cx + w, bottom - 1, cx, bottom);
      ctx.quadraticCurveTo(cx - w, bottom - 1, cx - w, top + 10);
      ctx.closePath();
    };
    ctx.fillStyle = URUK.shield;
    outline();
    ctx.fill();
    drawWhiteHand(ctx, cx, top + 9, 0.8);
    ctx.strokeStyle = URUK.rim;
    ctx.lineWidth = 1.5;
    outline();
    ctx.stroke();
    return;
  }
  if (id === 184) {
    // The shield of Arthedain, held (2026-10-22): the kite on the arm,
    // blue with its silver rim, the stars a fleck of white at this size.
    drawArthedainShield(ctx, hx + out * 2.5, hy - 15, hy + 6, 7);
    return;
  }
  if (id === 191) {
    // The mithril shield, held (2026-10-27): the kite on the arm, bright
    // silver, the stars a fleck of white at this size.
    drawMithrilShield(ctx, hx + out * 2.5, hy - 15, hy + 6, 7);
    return;
  }
  if (id === 117) {
    // The Haradrim spiked shield, held (2026-10-09): the round shield on
    // the arm, its spikes reading at a tile away the way the Balrog
    // shield's eyes do.
    drawHaradShield(ctx, hx + out * 2.5, hy - 5, 7);
    return;
  }
  if (id !== 22 && id !== 23) return;
  // A shield the size a shield should be. It used to be drawn narrow so it
  // would not run off the edge of a one-tile sprite; the box is wider than
  // the tile now, so it can overhang the way it does on a real arm.
  const cx = hx + out * 2, top = hy - 14, bottom = hy + 6;
  const w = 7.5; // half width
  const outline = () => {
    ctx.beginPath();
    ctx.moveTo(cx - w, top);
    ctx.lineTo(cx + w, top);
    ctx.lineTo(cx + w, top + 7);
    ctx.quadraticCurveTo(cx + w, bottom - 1, cx, bottom);
    ctx.quadraticCurveTo(cx - w, bottom - 1, cx - w, top + 7);
    ctx.closePath();
  };
  if (id === 22) {
    ctx.fillStyle = "#8a5a32";
    outline();
    ctx.fill();
    ctx.strokeStyle = "#5a3a1e";
    ctx.lineWidth = 1;
    ctx.beginPath(); // planks
    for (const x of [cx - 2.5, cx + 2.5]) {
      ctx.moveTo(x, top + 1);
      ctx.lineTo(x, bottom - 2);
    }
    ctx.stroke();
    ctx.strokeStyle = "#5c6166";
    ctx.lineWidth = 1.5;
    outline(); // iron rim
    ctx.stroke();
    ctx.fillStyle = "#8d949c";
    ctx.beginPath(); // boss
    ctx.arc(cx, top + 6, 1.8, 0, Math.PI * 2);
    ctx.fill();
  } else {
    ctx.fillStyle = "#b6bcc4";
    outline();
    ctx.fill();
    ctx.fillStyle = "#e2e8ee";
    ctx.fillRect(cx - 3.5, top + 2, 1.5, 7); // a highlight down one side
    ctx.strokeStyle = "#5a6068";
    ctx.lineWidth = 1.5;
    outline();
    ctx.stroke();
    ctx.strokeStyle = "#8d949c";
    ctx.lineWidth = 1;
    ctx.beginPath(); // a cross of ribs
    ctx.moveTo(cx, top + 1.5);
    ctx.lineTo(cx, bottom - 2);
    ctx.moveTo(cx - w + 1, top + 5);
    ctx.lineTo(cx + w - 1, top + 5);
    ctx.stroke();
  }
}

/**
 * Armor worn over the tunic. Each piece is drawn with the same colours and
 * marks as its ground sprite, so what you picked up is what you are seen in:
 * leather is brown hide with stitched seams, chain mail a grey lattice of
 * rings with sleeves, plate bright steel with pauldrons and a waist band.
 * The cuirass covers the chest and leaves the tunic's hem showing.
 */
function drawWornArmor(ctx: Ctx, id: number, dir: number, legOff: number) {
  const H = TILE + 16;
  const top = H - 27, bottom = H - 15; // chest, from the collar to above the hem
  const chest = (body: string) => {
    ctx.fillStyle = body;
    ctx.beginPath();
    ctx.moveTo(9, top);
    ctx.lineTo(23, top);
    ctx.lineTo(23, bottom - 2);
    ctx.quadraticCurveTo(16, bottom + 2, 9, bottom - 2);
    ctx.closePath();
    ctx.fill();
  };
  // The neck opening is the one mark that says which way round a cuirass
  // is being seen: from the front it is a notch under the chin, and from
  // behind the collar runs straight across. Which facings count as behind
  // is backView's business rather than this function's, so that the armor
  // and the hands cannot end up disagreeing about which way somebody is
  // facing — a back with a neckline on it, wearing its sword in the hand
  // that only a front view puts there, is what that disagreement looks
  // like.
  const collar = (shade: string) => {
    if (backView(dir)) return;
    ctx.fillStyle = shade;
    ctx.beginPath();
    ctx.moveTo(13, top);
    ctx.lineTo(19, top);
    ctx.lineTo(16, top + 4);
    ctx.closePath();
    ctx.fill();
  };
  switch (id) {
    case 8: // leather armor
      chest("#8a5a32");
      ctx.strokeStyle = "#5a3a1e";
      ctx.lineWidth = 1;
      ctx.setLineDash([2, 2]);
      ctx.beginPath();
      ctx.moveTo(11.5, top + 3);
      ctx.lineTo(11.5, bottom - 2);
      ctx.moveTo(20.5, top + 3);
      ctx.lineTo(20.5, bottom - 2);
      ctx.stroke();
      ctx.setLineDash([]);
      collar("#5a3a1e");
      ctx.fillStyle = "#a97245";
      ctx.fillRect(10, top + 4, 1, 7);
      break;
    case 9: // chain mail
      chest("#8e949a");
      // Sleeves down to the elbow, following the swing of the arms.
      ctx.fillStyle = "#8e949a";
      ctx.fillRect(6, H - 26 - legOff / 2, 3, 5);
      ctx.fillRect(23, H - 26 + legOff / 2, 3, 5);
      ctx.fillStyle = "#5f666c";
      for (let y = top + 2; y < bottom; y += 3) {
        for (let x = 10 + (((y - top) / 3) & 1); x < 23; x += 3) ctx.fillRect(x, y, 1, 1);
      }
      for (let y = 0; y < 5; y += 2) {
        ctx.fillRect(7, H - 25 - legOff / 2 + y, 1, 1);
        ctx.fillRect(24, H - 25 + legOff / 2 + y, 1, 1);
      }
      collar("#4d5257");
      ctx.fillStyle = "#b9c0c6";
      ctx.fillRect(10, top + 3, 1, 8);
      break;
    case 10: // plate armor
      chest("#b6bcc4");
      ctx.fillStyle = "#8d949c";
      ctx.fillRect(9, bottom - 4, 14, 2); // waist band
      ctx.beginPath(); // pauldrons, sitting out over the shoulders
      ctx.ellipse(9, top + 1, 4, 3, 0, 0, Math.PI * 2);
      ctx.ellipse(23, top + 1, 4, 3, 0, 0, Math.PI * 2);
      ctx.fill();
      collar("#5a6068");
      ctx.fillStyle = "#e2e8ee";
      ctx.fillRect(11, top + 3, 2, 7);
      break;
    case 192: // mithril plate: plate's harness in mithril (2026-10-27)
      chest(MITHRIL.body);
      ctx.fillStyle = MITHRIL.ring;
      ctx.fillRect(9, bottom - 4, 14, 2); // waist band
      ctx.fillRect(15, top + 2, 2, bottom - top - 6); // the join down the breastplate
      ctx.beginPath(); // pauldrons, sitting out over the shoulders
      ctx.ellipse(9, top + 1, 4, 3, 0, 0, Math.PI * 2);
      ctx.ellipse(23, top + 1, 4, 3, 0, 0, Math.PI * 2);
      ctx.fill();
      collar(MITHRIL.shade);
      ctx.fillStyle = MITHRIL.light;
      ctx.fillRect(11, top + 3, 2, 7);
      ctx.fillStyle = MITHRIL.sheen;
      ctx.fillRect(19, top + 3, 1, 5);
      break;
    case 39: // carapace: a spider's back, worn the way it grew
      chest("#2f2a33");
      ctx.fillStyle = "#3d3644"; // the plates' own banding
      ctx.fillRect(9, top + 3, 14, 2);
      ctx.fillRect(9, top + 7, 14, 2);
      ctx.fillStyle = "#8f7f96"; // the pale join down the spine
      ctx.fillRect(15, top, 2, bottom - top);
      ctx.strokeStyle = "#0f0d12"; // and the hairs
      ctx.lineWidth = 1;
      ctx.beginPath();
      for (const [x, y] of [[11, top + 6], [21, top + 6], [12, bottom - 3], [20, bottom - 3]] as const) {
        ctx.moveTo(x, y);
        ctx.lineTo(x + (x < 16 ? -3 : 3), y - 3);
      }
      ctx.stroke();
      collar("#171419");
      break;
    // The rare finds' suits (2026-09-26, "Implement them all"), each in
    // the colours of its ground sprite. The wolfskin cloak was a third,
    // and the spidersilk and the elven cloak were here with it, until
    // they went over the armor in drawWornCloak (2026-10-04); the wolfskin
    // left the game on 2026-10-07.
    case 60: // mithril shirt: fine silver rings, sleeved like chain
      chest(MITHRIL.body);
      ctx.fillStyle = MITHRIL.body;
      ctx.fillRect(6, H - 26 - legOff / 2, 3, 4);
      ctx.fillRect(23, H - 26 + legOff / 2, 3, 4);
      ctx.fillStyle = MITHRIL.ring;
      for (let y = top + 2; y < bottom; y += 2) {
        for (let x = 10 + (((y - top) >> 1) & 1); x < 23; x += 2) ctx.fillRect(x, y, 1, 1);
      }
      collar(MITHRIL.shade);
      ctx.fillStyle = MITHRIL.sheen;
      ctx.fillRect(19, top + 3, 1, 7);
      ctx.fillStyle = MITHRIL.light;
      ctx.fillRect(10, top + 3, 1, 8);
      break;
    case 67: // dragon-scale mail: rows of red scale, gold rivets, pauldrons
      chest(DRAGON_SCALE.body);
      ctx.strokeStyle = DRAGON_SCALE.edge;
      ctx.lineWidth = 1;
      for (let y = top + 3; y < bottom - 1; y += 3) {
        ctx.beginPath();
        for (let x = 9 + (((y - top) / 3) & 1) * 1.5; x < 22; x += 3) {
          ctx.moveTo(x, y);
          ctx.quadraticCurveTo(x + 1.5, y + 2, x + 3, y);
        }
        ctx.stroke();
      }
      ctx.fillStyle = DRAGON_SCALE.light; // pauldrons of bigger scale
      ctx.beginPath();
      ctx.ellipse(9, top + 1, 3.5, 2.5, 0, 0, Math.PI * 2);
      ctx.ellipse(23, top + 1, 3.5, 2.5, 0, 0, Math.PI * 2);
      ctx.fill();
      collar(DRAGON_SCALE.shade);
      ctx.fillStyle = DRAGON_SCALE.gold;
      for (const y of [top + 4, top + 8]) ctx.fillRect(20, y, 1.5, 1.5);
      break;
    // The content proposal's suit (2026-10-01, "Implement the entire
    // proposal in the background"), in the colours of its ground sprite.
    case 85: // Easterling scale mail: rows of red scale, gold down the front and at the shoulders
      chest(EASTERLING.body);
      ctx.strokeStyle = EASTERLING.edge;
      ctx.lineWidth = 1;
      for (let y = top + 3; y < bottom - 1; y += 3) {
        ctx.beginPath();
        for (let x = 9 + (((y - top) / 3) & 1) * 1.5; x < 22; x += 3) {
          ctx.moveTo(x, y);
          ctx.quadraticCurveTo(x + 1.5, y + 2, x + 3, y);
        }
        ctx.stroke();
      }
      ctx.fillStyle = EASTERLING.light;
      ctx.beginPath();
      ctx.ellipse(9, top + 1, 3.5, 2.5, 0, 0, Math.PI * 2);
      ctx.ellipse(23, top + 1, 3.5, 2.5, 0, 0, Math.PI * 2);
      ctx.fill();
      collar(EASTERLING.shade);
      ctx.fillStyle = EASTERLING.gold;
      if (!backView(dir)) ctx.fillRect(15, top + 4, 2, bottom - top - 6);
      else ctx.fillRect(9, bottom - 4, 14, 1.5);
      break;
  }
}

/**
 * A cloak worn over whatever armor is on, the armor drawn first (2026-10-04:
 * "make the various elf items equippable in addition to other items where
 * it makes sense"). Before that day each cloak was drawn as a suit, a
 * chest-piece of cloth in drawWornArmor; now it hangs from the shoulders
 * and the suit shows where a cloak leaves a body showing. From the front
 * that is down the middle: a mantle over the shoulders, the clasp at the
 * throat and the two edges falling either side of the chest, with the
 * mail between them. From behind it is the whole of somebody's back,
 * which is what a cloak is mostly seen as. In profile it falls down the
 * back from the shoulder, and the armor shows in front of it.
 *
 * Each in the colours of its ground sprite, with its hood lying back: the
 * silk pale blue-grey with a green clasp and the elven cloak grey-green
 * with a gold leaf at the throat, both long to the calves and flaring. The wolfskin's white fur was a third until it
 * left the game on 2026-10-07 ("remove the wolfskin cloak from the game.").
 */
/** The belts of Lórien, worn and in the bag: the band, its shade and the clasp's two colours. */
const BELTS: Record<number, { band: string; shade: string; clasp: string; core: string }> = {
  185: { band: "#c8cdd4", shade: "#7d848e", clasp: "#f2c43a", core: "#fff1a8" }, // silver belt of Lórien
  186: { band: "#e0b23c", shade: "#94701c", clasp: "#fff1a8", core: "#c98a1a" }, // golden belt
};

/**
 * A belt worn at the waist (2026-10-22: "Show what a belt could look like
 * on a player in the game", then "do it"): a band across the armor's hem,
 * and its clasp in front, or at the side being faced in profile. From
 * behind only the band shows. The silver belt's clasp is a golden flower,
 * as the Lady gave the hobbits; the golden belt's a square buckle.
 */
function drawWornBelt(ctx: Ctx, id: number, dir: number) {
  const pal = BELTS[id];
  if (!pal) return;
  const H = TILE + 16;
  const y = H - 15;
  const facingSide = dir === 1 ? 1 : dir === 3 ? -1 : 0;
  ctx.fillStyle = pal.band;
  ctx.fillRect(9, y, 14, 2);
  ctx.fillStyle = pal.shade;
  ctx.fillRect(9, y + 2, 14, 0.75);
  if (dir === 0) {
    ctx.fillRect(12, y, 1, 2); // a link or two along the back
    ctx.fillRect(19, y, 1, 2);
    return;
  }
  drawBeltClasp(ctx, id, facingSide ? 16 + facingSide * 4 : 16, y + 1, 1);
}

/** A belt's clasp at x, y, k times its worn size: the flower or the buckle. */
function drawBeltClasp(ctx: Ctx, id: number, x: number, y: number, k: number) {
  const pal = BELTS[id];
  ctx.fillStyle = pal.clasp;
  if (id === 185) {
    for (const [dx, dy] of [[-1.4, 0], [1.4, 0], [0, -1.4], [0, 1.4]]) {
      ctx.beginPath();
      ctx.arc(x + dx * k, y + dy * k, 1.2 * k, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.fillStyle = pal.core;
    ctx.fillRect(x - 0.5 * k, y - 0.5 * k, k, k);
  } else {
    ctx.fillRect(x - 2 * k, y - 2 * k, 4 * k, 4 * k);
    ctx.fillStyle = pal.core;
    ctx.fillRect(x - k, y - k, 2 * k, 2 * k);
  }
}

function drawWornCloak(ctx: Ctx, id: number, dir: number, legOff: number) {
  const pal =
    id === 31
      ? { body: SILK_CLOAK.body, shade: SILK_CLOAK.lining, fold: SILK_CLOAK.fold, light: SILK_CLOAK.fold, clasp: SILK_CLOAK.clasp }
      : id === 84
        ? { body: ELVEN_CLOAK.body, shade: ELVEN_CLOAK.shade, fold: ELVEN_CLOAK.fold, light: ELVEN_CLOAK.light, clasp: ELVEN_CLOAK.leaf }
        : id === 129 // the Lossoth's fur cloak (site 3, 2026-10-16)
          ? { body: FUR_CLOAK.body, shade: FUR_CLOAK.shade, fold: FUR_CLOAK.fold, light: FUR_CLOAK.light, clasp: FUR_CLOAK.toggle }
        : id === 136 // 5. Rhosgobel (2026-10-16): Radagast's brown cloak
          ? { body: BROWN_CLOAK.body, shade: BROWN_CLOAK.shade, fold: BROWN_CLOAK.fold, light: BROWN_CLOAK.light, clasp: BROWN_CLOAK.feather }
        : id === 139 // the star of the Dúnedain (2026-10-16): Ranger grey, the silver star for a clasp
          ? { body: DUNEDAIN_CLOAK.body, shade: DUNEDAIN_CLOAK.shade, fold: DUNEDAIN_CLOAK.fold, light: DUNEDAIN_CLOAK.light, clasp: DUNEDAIN_CLOAK.star }
        : id === 196 // the Blue Tower of Rhûn (2026-11-01): the Blue Wizard's robe, a silver clasp
          ? { body: BLUE_ROBE.body, shade: BLUE_ROBE.shade, fold: BLUE_ROBE.fold, light: BLUE_ROBE.light, clasp: BLUE_ROBE.clasp }
          : null;
  if (!pal) return;
  const H = TILE + 16;
  // The shoulders, and a hem at the calves, flaring there.
  const top = H - 27, hem = H - 6, flare = 2.5;
  const facingSide = dir === 1 ? 1 : dir === 3 ? -1 : 0;
  const folds = (xs: number[]) => {
    ctx.strokeStyle = pal.fold;
    ctx.lineWidth = 1;
    ctx.beginPath();
    for (const x of xs) {
      ctx.moveTo(x, top + 3);
      ctx.quadraticCurveTo(x - 1, (top + hem) / 2, x, hem - 1);
    }
    ctx.stroke();
  };
  ctx.fillStyle = pal.body;
  if (dir === 0) {
    // From behind: the whole back, wider at the hem than the shoulders.
    ctx.beginPath();
    ctx.moveTo(8, top - 1);
    ctx.lineTo(24, top - 1);
    ctx.lineTo(25.5 + flare, hem);
    ctx.lineTo(6.5 - flare, hem);
    ctx.closePath();
    ctx.fill();
    folds([11, 16, 21]);
    ctx.fillStyle = pal.shade;
    ctx.fillRect(6.5 - flare, hem - 1, 19 + 2 * flare, 1);
  } else if (facingSide) {
    // In profile: down the back from the shoulder, swinging a little with
    // the stride, and a mantle over the near shoulder.
    const back = -facingSide;
    ctx.beginPath();
    ctx.moveTo(16, top - 1);
    ctx.lineTo(16 + back * 8, top);
    ctx.lineTo(16 + back * (10 + flare + Math.abs(legOff) / 2), hem);
    ctx.lineTo(16 + back * 3, hem);
    ctx.closePath();
    ctx.fill();
    ctx.fillRect(9, top - 1, 14, 3);
    folds([16 + back * 6]);
    ctx.fillStyle = pal.clasp;
    ctx.fillRect(16 + facingSide * 3, top, 2, 2);
  } else {
    // From the front: the mantle over both shoulders, and the two edges
    // falling past the arms' inside either side of the chest, with the
    // armor between them.
    ctx.beginPath();
    ctx.ellipse(9.5, top + 1, 4, 3, 0, 0, Math.PI * 2);
    ctx.ellipse(22.5, top + 1, 4, 3, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.beginPath();
    ctx.moveTo(8, top);
    ctx.lineTo(11, top);
    ctx.lineTo(11, hem);
    ctx.lineTo(8 - flare, hem);
    ctx.closePath();
    ctx.moveTo(21, top);
    ctx.lineTo(24, top);
    ctx.lineTo(24 + flare, hem);
    ctx.lineTo(21, hem);
    ctx.closePath();
    ctx.fill();
    ctx.fillStyle = pal.shade;
    ctx.fillRect(10.5, top + 2, 0.8, hem - top - 2);
    ctx.fillRect(20.7, top + 2, 0.8, hem - top - 2);
    ctx.fillRect(13, top - 0.5, 6, 1); // the cord across the throat
    ctx.fillStyle = pal.clasp;
    ctx.fillRect(15, top - 1, 2, 2);
  }
  // What sits round the neck: the hood lying back, on both since the silk
  // was given one with its new ground sprite (2026-10-10). (The wolfskin's
  // ruff was the other, until it left the game on 2026-10-07.)
  {
    ctx.fillStyle = pal.light;
    ctx.beginPath();
    ctx.ellipse(16 - facingSide * 3, top, facingSide ? 4 : 6, 2, 0, 0, Math.PI * 2);
    ctx.fill();
    if (dir !== 0) {
      ctx.fillStyle = pal.clasp; // the brooch: a leaf of Lórien, or the silk's green clasp
      ctx.fillRect(15 + facingSide * 2, top + 1, 2, 2);
    }
  }
}

/**
 * The Elendilmir worn as a helm: a fillet of mithril with the white star on
 * it alight. It was bound on the brow under a helm from 2026-10-04 to
 * 2026-10-09, and is the helm again since ("remove the separate brow and
 * light slots"). No dome: a band, which shows the hair over it from every
 * side. The star is on the brow, so it is seen from the front and the
 * side; from behind there is the band and its light, which is seen from
 * every side because it is a light.
 */
function drawElendilmir(ctx: Ctx, dir: number) {
  const H = TILE + 16;
  const cx = 16, top = H - 36; // the hairline, as drawWornHelm has it
  const facingSide = dir === 1 ? 1 : dir === 3 ? -1 : 0;
  ctx.fillStyle = ELENDILMIR.glow;
  if (!backView(dir)) {
    ctx.beginPath();
    ctx.arc(cx + facingSide * 6, top - 1, 3.2, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.fillStyle = ELENDILMIR.band;
  ctx.fillRect(cx - 7.5, top - 2, 15, 1.5);
  ctx.fillStyle = ELENDILMIR.gem; // the mithril's own light along the band
  ctx.fillRect(cx - 6, top - 2, 12, 0.6);
  if (!backView(dir)) {
    const gx = cx + facingSide * 6, gy = top - 1.8;
    ctx.beginPath();
    ctx.moveTo(gx, gy - 2.2);
    ctx.lineTo(gx + 1.3, gy + 0.4);
    ctx.lineTo(gx, gy + 2.6);
    ctx.lineTo(gx - 1.3, gy + 0.4);
    ctx.closePath();
    ctx.fill();
  }
}

/**
 * The crown somebody who runs the place wears. It sits on the head every
 * other character has — a circle of radius 7 centred at (16, H - 34), with
 * the hairline at H - 36 — resting on the hair rather than replacing it,
 * the way a crown does.
 *
 * A band with three points off it, the middle one taller. Three because at
 * this size five is a comb and one is a horn; taller in the middle because
 * that is the silhouette that says crown even when it is eight pixels of
 * gold. The band is a little narrower than the head so the hair shows at
 * the temples on either side, which is what keeps it a thing worn on
 * somebody rather than a shape stuck to the top of them.
 *
 * The band's lower edge is drawn in the darker gold, so the crown has a
 * thickness instead of reading as a flat sticker, and the same shade rims
 * the points. It shows from every facing: from behind it is the back of
 * the band and the points over it, which is exactly when you most need to
 * know who you are looking at.
 */
function drawAdminCrown(ctx: Ctx, H: number) {
  // The band sits at the top of the skull rather than at the hairline. The
  // head is a circle of radius 7 centred at (16, H - 34), so its crown is
  // exactly H - 41 and that is where the band goes: with its lower shade
  // the crown occupies H - 41 to H - 37, which leaves two clear pixels
  // above the eyes at H - 35. Drawn at the hairline it came down over them,
  // which read as a visor rather than as a crown and cost the face the one
  // feature that says which way somebody is looking.
  const cx = 16, band = H - 41;
  // The points first, so the band's lower shade laps over their feet.
  ctx.fillStyle = ADMIN_CROWN;
  for (const [x, up] of [[-5, 3], [0, 5], [5, 3]] as [number, number][]) {
    ctx.beginPath();
    ctx.moveTo(cx + x - 2, band + 1);
    ctx.lineTo(cx + x, band - up);
    ctx.lineTo(cx + x + 2, band + 1);
    ctx.closePath();
    ctx.fill();
  }
  ctx.fillStyle = ADMIN_CROWN;
  ctx.fillRect(cx - 7, band, 14, 3);
  ctx.fillStyle = ADMIN_CROWN_SHADE;
  ctx.fillRect(cx - 7, band + 3, 14, 1);
}

/**
 * A helm worn on the head. It is drawn the way worn armor is, with the
 * colours and marks of its ground sprite, but it sits on the head rather
 * than the torso: the head is a circle of radius 7 centred at (16, H - 34)
 * with the hair as the upper half of one a little higher, so the cap is a
 * dome over exactly that, with a rim that stops above the eyes at H - 35.
 * The horns rise from the sides of the dome, so they show from behind as
 * well as in front — a horned helmet seen from behind is still a horned
 * helmet, and somebody walking away from you in one should look like it.
 * In profile the two horns are one behind the other, so the far one is
 * drawn first and darker, the way the demon's own are.
 */
/**
 * Whether the helm's mask covers the wearer's face from this facing, so
 * that its eye-slits or lights stand for the wearer's eyes: the war-mask
 * of Belegost and the Witch-king's crown, from anywhere but behind.
 */
function helmIsTheEyes(id: number, dir: number): boolean {
  return (id === 142 || id === 101) && dir !== 0;
}

function drawWornHelm(ctx: Ctx, id: number, dir: number, glare?: string) {
  const H = TILE + 16;
  const cx = 16, top = H - 36; // the centre of the dome's base, level with the hairline
  const facingSide = dir === 1 ? 1 : dir === 3 ? -1 : 0;
  const front = dir === 2;
  switch (id) {
    case 88: // Elendilmir
      drawElendilmir(ctx, dir);
      return;
    case 47: { // demon helmet
      // From the front and from behind the horns go on first, so the iron
      // covers their roots: they rise from either side of the dome and
      // sweep outwards, and everything past the dome's edge shows.
      if (!facingSide) {
        demonHorn(ctx, cx - 5, top - 3.5, -4.5, -7, 2);
        demonHorn(ctx, cx + 5, top - 3.5, 4.5, -7, 2);
      }
      ctx.fillStyle = "#2b1013";
      ctx.beginPath();
      ctx.arc(cx, top, 7.5, Math.PI, Math.PI * 2);
      ctx.closePath();
      ctx.fill();
      // In profile they go on after it, since their roots are on the
      // crown and a horn drawn under the dome is a horn mostly inside it.
      // The far one stands nearer the back of the skull and upright, the
      // near one further forward and swept back, which is what keeps the
      // two tips apart: side on, a pair of horns one directly behind the
      // other is one horn. The far one is in shadow, the way the demon's is —
      // a horn-coloured shadow rather than the demon's own dark, which
      // against the iron of the cap is no horn at all.
      if (facingSide) {
        demonHorn(ctx, cx - facingSide * 4.5, top - 4.5, facingSide * 1, -7, 1.8, "#8f8672");
        demonHorn(ctx, cx + facingSide * 1, top - 5.5, -facingSide * 6.5, -6, 2.1);
        ctx.fillStyle = "#2b1013";
      }
      // The rim is the two rows under the dome, and it ends a row above
      // the eyes: a rim that reached them took the top off every eye in
      // the world and left a helmeted face looking half asleep.
      ctx.fillRect(cx - 7.5, top - 1, 15, 2);
      // A cheek guard hangs from the rim on each side of the face, and the
      // nasal comes down between the eyes — a single pixel wide, since the
      // eyes sit three apart and a wider bar joins one of them; from
      // behind there is neither, only the iron over the back of the skull.
      if (front) {
        ctx.fillRect(cx - 6, top + 1, 2, 5);
        ctx.fillRect(cx + 4, top + 1, 2, 5);
        ctx.fillRect(cx, top + 1, 1, 4);
      } else if (facingSide) {
        ctx.fillRect(cx + facingSide * 5, top + 1, 2, 5);
      }
      ctx.fillStyle = "#3f181c"; // the brow ridge, raised out of the iron
      ctx.fillRect(cx - 5, top - 3, 10, 1.5);
      ctx.fillStyle = "#6a564a"; // the old iron of the rim's lower edge
      ctx.fillRect(cx - 7.5, top, 15, 1);
      ctx.fillStyle = DEMON.fire; // and the fire at the roots of the horns
      if (facingSide) ctx.fillRect(cx + facingSide * 0.5 - 1, top - 5, 2, 1.5);
      else {
        ctx.fillRect(cx - 6, top - 4, 2, 1.5);
        ctx.fillRect(cx + 4, top - 4, 2, 1.5);
      }
      break;
    }
    case 61: { // dwarven helm (2026-09-26, "Implement them all")
      // The same dome over the same head as the demon's, without the
      // horns: iron, a bronze band at the brow and a bronze crest over the
      // top from front to back, which is what shows from behind.
      ctx.fillStyle = DWARF_HELM.iron;
      ctx.beginPath();
      ctx.arc(cx, top, 7.5, Math.PI, Math.PI * 2);
      ctx.closePath();
      ctx.fill();
      ctx.fillRect(cx - 7.5, top - 1, 15, 2); // the rim, a row above the eyes
      if (front) {
        ctx.fillRect(cx - 6, top + 1, 2, 5); // cheek guards
        ctx.fillRect(cx + 4, top + 1, 2, 5);
        ctx.fillRect(cx, top + 1, 1, 4); // and the nasal
      } else if (facingSide) {
        ctx.fillRect(cx + facingSide * 5, top + 1, 2, 5);
      }
      ctx.fillStyle = DWARF_HELM.lit;
      ctx.fillRect(cx - 5, top - 5, 3, 2);
      ctx.fillStyle = DWARF_HELM.bronze;
      ctx.fillRect(cx - 7.5, top - 2, 15, 1.5); // the band
      if (facingSide) ctx.fillRect(cx - 6, top - 8, 12, 1.5); // the crest, seen side on
      else ctx.fillRect(cx - 0.75, top - 8, 1.5, 6); // or end on
      ctx.fillStyle = DWARF_HELM.rim;
      ctx.fillRect(cx - 7.5, top, 15, 1);
      break;
    }
    // The content proposal's three (2026-10-01, "Implement the entire
    // proposal in the background"): the same dome over the same head, each
    // with what it is known by.
    // A wild hobbit's saucepan (2026-10-09), worn upside down: a copper
    // drum on the head and the handle out over an ear, or out behind
    // from the side.
    case 116: {
      ctx.fillStyle = SAUCEPAN.handle;
      if (facingSide) ctx.fillRect(facingSide > 0 ? cx - 15 : cx + 7, top - 5, 8, 2);
      else ctx.fillRect(cx + 7, top - 5, 7, 2);
      ctx.fillStyle = SAUCEPAN.copper;
      ctx.fillRect(cx - 7, top - 7, 14, 7);
      ctx.fillStyle = SAUCEPAN.dark;
      ctx.fillRect(cx - 8, top - 1, 16, 2); // the rim, a row above the eyes
      ctx.fillStyle = SAUCEPAN.lit;
      ctx.fillRect(cx - 5, top - 6, 2, 4);
      break;
    }
    // --- 12. Belegost (2026-10-16): the war-mask, worn. An iron cap, and
    // from the front the grim face-plate over the wearer's own, with its
    // beard of rings; from the side its edge, and from behind only the cap.
    case 142: {
      ctx.fillStyle = BELEGOST_MASK.dark;
      ctx.beginPath();
      ctx.arc(cx, top, 7.5, Math.PI, Math.PI * 2);
      ctx.closePath();
      ctx.fill();
      ctx.fillStyle = BELEGOST_MASK.lit;
      ctx.fillRect(cx - 7.5, top - 1, 15, 2);
      ctx.fillStyle = BELEGOST_MASK.gold;
      ctx.fillRect(cx - 1, top - 6, 2, 4);
      // The plate covers the wearer's eyes, so its eye-slits are their
      // eyes: dark, and the wearer's own colour when that says something,
      // a protection zone lock's red (2026-10-26: "For the war mask and
      // Witch King helm, it's easy enough to replace the existing eyes
      // with red"). Side on, the plate's edge has its slit too.
      if (front) {
        ctx.fillStyle = BELEGOST_MASK.iron;
        ctx.fillRect(cx - 6, top + 1, 12, 7);
        ctx.fillStyle = glare ?? BELEGOST_MASK.slit;
        ctx.fillRect(cx - 4, top + 3, 3, 1);
        ctx.fillRect(cx + 1, top + 3, 3, 1);
        ctx.fillStyle = BELEGOST_MASK.ring;
        ctx.fillRect(cx - 5, top + 8, 10, 2);
        ctx.fillRect(cx - 3, top + 10, 6, 2);
      } else if (facingSide) {
        const px = cx + facingSide * 4;
        ctx.fillStyle = BELEGOST_MASK.iron;
        ctx.fillRect(px, top + 1, 3, 7);
        ctx.fillStyle = glare ?? BELEGOST_MASK.slit;
        ctx.fillRect(px + (facingSide > 0 ? 1 : 0), top + 3, 2, 1);
      }
      break;
    }
    // --- end of 12. Belegost
    case 112: { // helm of the King's Men: a sea-green dome with a high crest from brow to nape
      ctx.fillStyle = KINGSMEN_HELM.steel;
      ctx.beginPath();
      ctx.moveTo(cx - 7.5, top);
      ctx.quadraticCurveTo(cx - 7.5, top - 8, cx, top - 9);
      ctx.quadraticCurveTo(cx + 7.5, top - 8, cx + 7.5, top);
      ctx.closePath();
      ctx.fill();
      ctx.fillStyle = KINGSMEN_HELM.rim;
      ctx.fillRect(cx - 7.5, top - 1, 15, 2);
      if (front) ctx.fillRect(cx, top + 1, 1, 4); // the nasal
      ctx.fillStyle = KINGSMEN_HELM.lit;
      ctx.fillRect(cx - 5, top - 6, 2, 3);
      ctx.fillStyle = KINGSMEN_HELM.crest;
      if (facingSide) ctx.fillRect(cx - 6, top - 12, 12, 3); // the crest seen along its length
      else ctx.fillRect(cx - 1, top - 13, 2, 5); // and end on
      break;
    }
    case 86: { // Rohirric helm: steel, a gold band, and a white horsetail streaming back
      // The horsetail first, so the dome covers its root: it falls behind
      // the head from the crest, down the back, and from the side it
      // streams out behind whichever way the head is turned.
      if (facingSide) drawHorsetail(ctx, cx, top - 9, cx - facingSide * 12, top - 9, cx - facingSide * 11, top + 9, 4);
      else if (front) drawHorsetail(ctx, cx + 2, top - 9, cx + 11, top - 8, cx + 9, top + 8, 3.5);
      ctx.fillStyle = ROHAN_HELM.steel;
      ctx.beginPath();
      ctx.moveTo(cx - 7.5, top);
      ctx.quadraticCurveTo(cx - 7.5, top - 8, cx, top - 9);
      ctx.quadraticCurveTo(cx + 7.5, top - 8, cx + 7.5, top);
      ctx.closePath();
      ctx.fill();
      ctx.fillRect(cx - 7.5, top - 1, 15, 2); // the rim, a row above the eyes
      if (front) ctx.fillRect(cx, top + 1, 1, 4); // the nasal
      ctx.fillStyle = ROHAN_HELM.lit;
      ctx.fillRect(cx - 5, top - 6, 2, 3);
      ctx.fillStyle = ROHAN_HELM.gold;
      ctx.fillRect(cx - 7.5, top - 2, 15, 1.5);
      ctx.fillStyle = ROHAN_HELM.rim;
      ctx.fillRect(cx - 7.5, top, 15, 1);
      // And over the dome, from behind, the whole tail down the back of the
      // head and neck; from the side, where it leaves the crest, so it
      // reads as coming off the helm.
      if (dir === 0) drawHorsetail(ctx, cx, top - 7, cx + 1, top + 2, cx, top + 12, 5);
      else if (facingSide) {
        ctx.fillStyle = ROHAN_HELM.gold; // the knob at the crest that holds it
        ctx.fillRect(cx - 1, top - 10, 2, 2);
      }
      break;
    }
    case 87: { // helm of Dol Amroth: a silver dome, the swan-knights' blue band, a wing either side
      for (const side of [-1, 1]) {
        if (facingSide && side !== -facingSide) continue; // side on, the far wing is behind the head
        ctx.fillStyle = DOL_AMROTH.wing;
        ctx.beginPath();
        ctx.moveTo(cx + side * 6, top - 3);
        ctx.quadraticCurveTo(cx + side * 12, top - 6, cx + side * 11, top - 13);
        ctx.quadraticCurveTo(cx + side * 9, top - 7, cx + side * 4, top - 6);
        ctx.closePath();
        ctx.fill();
      }
      ctx.fillStyle = DOL_AMROTH.steel;
      ctx.beginPath();
      ctx.arc(cx, top, 7.5, Math.PI, Math.PI * 2);
      ctx.closePath();
      ctx.fill();
      ctx.fillRect(cx - 7.5, top - 1, 15, 2);
      if (front) {
        ctx.fillRect(cx - 6, top + 1, 2, 5); // cheek guards
        ctx.fillRect(cx + 4, top + 1, 2, 5);
      } else if (facingSide) {
        ctx.fillRect(cx + facingSide * 5, top + 1, 2, 5);
      }
      ctx.fillStyle = DOL_AMROTH.lit;
      ctx.fillRect(cx - 5, top - 5, 3, 2);
      ctx.fillStyle = DOL_AMROTH.blue;
      ctx.fillRect(cx - 7.5, top - 2, 15, 1.5); // the band
      if (front) ctx.fillRect(cx, top + 1, 1, 4); // and the nasal, enamelled
      ctx.fillStyle = DOL_AMROTH.rim;
      ctx.fillRect(cx - 7.5, top, 15, 1);
      break;
    }
    case 149: { // Baldor's helm: the Rohirric helm's shape, a broad gold band set with garnets, a grey horsetail
      ctx.strokeStyle = BALDOR_HELM.tail;
      ctx.lineWidth = 2;
      ctx.lineCap = "round";
      ctx.beginPath();
      if (facingSide) {
        ctx.moveTo(cx, top - 8);
        ctx.quadraticCurveTo(cx - facingSide * 8, top - 7, cx - facingSide * 10, top + 3);
      } else {
        ctx.moveTo(cx, top - 8);
        ctx.quadraticCurveTo(cx + 3, top - 3, cx + (front ? 7 : 2), top + (front ? 1 : 6));
      }
      ctx.stroke();
      ctx.fillStyle = BALDOR_HELM.steel;
      ctx.beginPath();
      ctx.moveTo(cx - 7.5, top);
      ctx.quadraticCurveTo(cx - 7.5, top - 8, cx, top - 9);
      ctx.quadraticCurveTo(cx + 7.5, top - 8, cx + 7.5, top);
      ctx.closePath();
      ctx.fill();
      ctx.fillRect(cx - 7.5, top - 1, 15, 2); // the rim, a row above the eyes
      if (front) ctx.fillRect(cx, top + 1, 1, 4); // the nasal
      ctx.fillStyle = BALDOR_HELM.lit;
      ctx.fillRect(cx - 5, top - 6, 2, 3);
      ctx.fillStyle = BALDOR_HELM.gold;
      ctx.fillRect(cx - 7.5, top - 3, 15, 2.5);
      ctx.fillStyle = BALDOR_HELM.garnet;
      if (front) for (const x of [-5, -0.5, 4]) ctx.fillRect(cx + x, top - 2.5, 1.5, 1.5);
      else if (facingSide) ctx.fillRect(cx + facingSide * 3, top - 2.5, 1.5, 1.5);
      ctx.fillStyle = BALDOR_HELM.rim;
      ctx.fillRect(cx - 7.5, top, 15, 1);
      break;
    }
    case 150: { // crown of the King of the Dead: a dim black crown of tall points round the brow
      ctx.fillStyle = DEAD_CROWN.iron;
      const points = facingSide ? [-6, -3, 0, 3, 6] : [-6, -4, -2, 0, 2, 4, 6];
      points.forEach((x, i) => {
        const up = i % 2 ? 3 : 5;
        ctx.beginPath();
        ctx.moveTo(cx + x - 1.2, top - 3);
        ctx.lineTo(cx + x, top - 3 - up);
        ctx.lineTo(cx + x + 1.2, top - 3);
        ctx.closePath();
        ctx.fill();
      });
      ctx.fillRect(cx - 7.5, top - 4, 15, 3); // the band
      ctx.fillStyle = DEAD_CROWN.lit;
      ctx.fillRect(cx - 7.5, top - 4, 15, 0.8);
      ctx.fillStyle = DEAD_CROWN.glow;
      if (front) for (const x of [-4, 0, 4]) ctx.fillRect(cx + x - 0.5, top - 3, 1, 1);
      else if (facingSide) ctx.fillRect(cx + facingSide * 3 - 0.5, top - 3, 1, 1);
      break;
    }
    case 101: { // Witch-king's crown (2026-10-07): a helm over the whole head, faceless, a crown of blades off the brow
      // The helm-crown of Minas Morgul ("use the helm-crown of minas morgul
      // graphics"). Unlike the Elendilmir it is a helm, so it covers the
      // head as the others do and the face as none of them does: dome and
      // mask down to a point below the chin, with nothing of the wearer
      // showing but two green lights in the slits. The blades stand up all
      // the way round, five fanned from the front and behind, three side on.
      witchCrownBlades(ctx, cx, top - 3, 1.1, facingSide
        ? [[-3, -4, 8], [1, 2, 9], [4, 7, 7]]
        : [[-6, -9, 6], [-3, -5, 8], [0, 0, 10], [3, 5, 8], [6, 9, 6]]);
      ctx.fillStyle = WITCH_CROWN.steel;
      ctx.beginPath();
      ctx.arc(cx, top + 1, 7.5, Math.PI, Math.PI * 2);
      ctx.lineTo(cx + 7.5, top + 6);
      ctx.lineTo(cx, top + 10);
      ctx.lineTo(cx - 7.5, top + 6);
      ctx.closePath();
      ctx.fill();
      ctx.fillStyle = WITCH_CROWN.lit; // the brow band
      ctx.fillRect(cx - 7.5, top - 3, 15, 1.5);
      if (dir === 0) {
        // From behind, the back of the helm and a seam down it.
        ctx.fillStyle = WITCH_CROWN.dark;
        ctx.fillRect(cx - 0.5, top - 1, 1, 9);
      } else if (facingSide) {
        // Side on, one slit and one light, at the front of the face. The
        // lights are the wearer's eyes, which the mask covers, so when the
        // wearer's colour says something, a protection zone lock's red, the
        // whole slit burns in it (2026-10-26: "For the war mask and Witch
        // King helm, it's easy enough to replace the existing eyes with
        // red", then "The eyes should turn fully red for the masks").
        ctx.fillStyle = glare ?? WITCH_CROWN.slit;
        ctx.fillRect(cx + facingSide * 2.5 - 2, top + 1, 5, 2);
        if (!glare) {
          ctx.fillStyle = WITCH_CROWN.eye;
          ctx.fillRect(cx + facingSide * 4, top + 1.5, 1, 1);
        }
      } else {
        // From the front, the T, and a light either side of its stem; red,
        // the crossbar either side of the stem is.
        ctx.fillStyle = WITCH_CROWN.slit;
        ctx.fillRect(cx - 1, top + 1, 2, 6);
        if (glare) {
          ctx.fillStyle = glare;
          ctx.fillRect(cx - 5, top + 1, 4, 2);
          ctx.fillRect(cx + 1, top + 1, 4, 2);
        } else {
          ctx.fillRect(cx - 5, top + 1, 10, 2);
          ctx.fillStyle = WITCH_CROWN.eye;
          ctx.fillRect(cx - 3.5, top + 1.5, 1, 1);
          ctx.fillRect(cx + 2.5, top + 1.5, 1, 1);
        }
      }
      break;
    }
  }
}

/**
 * A weapon held at (hx, hy), leaning `out` (+1 right,
 * -1 left) away from the body. Same colours and shapes as the ground sprites:
 * a club is a thick brown stick with a heavier end, a sword a bright blade
 * over a crossguard, an axe a wooden haft with a steel head on the outside.
 * A knuckle of skin is painted over the grip afterwards, so the weapon
 * reads as held by the arm it swings with rather than floating beside it.
 */
/**
 * Item ids that need both hands, as the server defines them. The sprites
 * draw from the item id alone, so the ids are handed over once the item
 * definitions arrive rather than written out here a second time.
 */
let twoHandedIds = new Set<number>();

export function setTwoHandedItems(ids: Iterable<number>) {
  twoHandedIds = new Set(ids);
  creatureCache.clear(); // anything already drawn was drawn one-handed
  itemCache.clear(); // and any two-hander's icon unmirrored (see mirrored)
}

/**
 * The two-handers that are swung rather than aimed are mirrored, in the
 * bag, on the ground and in the hands (2026-10-10: "Mirror 2 handed melee
 * weapons so they are angled the other way. When being held, swap the
 * arms so the left hand is closest to the hilt"). Every one-hander lies
 * point up and to the right, and a two-hander now lies the other way, so
 * which kind a thing is shows at a glance in a row of them.
 *
 * Held, the mirror is the whole carry turned over about the middle of the
 * body. The grip marks go over with it, and drawHolds still gives each arm
 * the hold on its own side, so the hands swap ends: the left hand takes
 * the end of the hilt and the right the hold by the guard, whichever way
 * the body faces, the way a right-handed swordsman holds a long blade.
 *
 * The bows, the crossbow and the fishing rod take both hands too, but they
 * are aimed or held up the way they are for reasons of their own, and stay
 * as they were.
 */
function mirrored(id: number): boolean {
  return twoHandedIds.has(id) && !AIMED_TWO_HANDERS.has(id);
}

/** Mirror whatever is drawn next about the upright line x = cx. */
function mirrorAbout(ctx: Ctx, cx: number) {
  ctx.translate(2 * cx, 0);
  ctx.scale(-1, 1);
}

/**
 * Where the hands go on a weapon that takes both (2026-10-08: "make it so
 * two handed weapons appear to have parts of the weapon in each hand so
 * it's clear they're being used with two hands"). Each two-hander marks
 * its two holds with markGrip as it is drawn, in its own coordinates, so a
 * hold is on the haft or the grip however the weapon has been turned,
 * scaled or carried to get there. While nothing is listening the marks
 * cost nothing; drawPlayer listens, and puts a fist on each mark with a
 * forearm reaching to it from each arm.
 */
let gripMarks: Point[] | null = null;

/**
 * The race's stretch, taken back off: set by drawPlayer while it draws a
 * dwarf or a hobbit, as the change to the canvas that undoes their build
 * (see undoStretch), and null otherwise. A piece they hold is placed in
 * their stretched hands and then drawn through fullSize, so it comes out
 * at the size it is in anybody's (2026-11-02).
 */
let unbuild: Affine | null = null;

/** What undoes a stretch of sx by sy laid on top of the transform `before`. */
function undoStretch(before: Affine, sx: number, sy: number): Affine {
  // Read out by name: a DOMMatrix keeps them on its prototype, so a spread
  // of one is empty.
  const lin = { a: before.a, b: before.b, c: before.c, d: before.d, e: 0, f: 0 };
  return mulAffine(mulAffine(lin, { a: 1 / sx, b: 0, c: 0, d: 1 / sy, e: 0, f: 0 }), invertAffine(lin));
}

/** Whether a transform read off a canvas is one: a stand-in canvas may hand back anything. */
function isAffine(m: Affine): boolean {
  return [m.a, m.b, m.c, m.d, m.e, m.f].every(Number.isFinite);
}

function mulAffine(m: Affine, n: Affine): Affine {
  return {
    a: m.a * n.a + m.c * n.b, b: m.b * n.a + m.d * n.b,
    c: m.a * n.c + m.c * n.d, d: m.b * n.c + m.d * n.d,
    e: m.a * n.e + m.c * n.f + m.e, f: m.b * n.e + m.d * n.f + m.f,
  };
}

/**
 * Takes the carrier's build back off the canvas about the point (x, y),
 * which stays where it is: a held piece drawn after it is its own size
 * however short or broad the hand it is in. Nothing while no build is set,
 * so a monster's carry and a man's are untouched. The caller saves and
 * restores round it.
 */
function fullSize(ctx: Ctx, x = 0, y = 0) {
  if (!unbuild || typeof ctx.getTransform !== "function") return;
  const m = ctx.getTransform();
  if (!isAffine(m)) return;
  const p = applyAffine(m, x, y);
  const t = mulAffine(unbuild, { a: m.a, b: m.b, c: m.c, d: m.d, e: 0, f: 0 });
  ctx.setTransform(t.a, t.b, t.c, t.d, p.x - (t.a * x + t.c * y), p.y - (t.b * x + t.d * y));
}

type Point = { x: number; y: number };
type Affine = { a: number; b: number; c: number; d: number; e: number; f: number };

function markGrip(ctx: Ctx, x: number, y: number) {
  if (gripMarks) gripMarks.push(applyAffine(ctx.getTransform(), x, y));
}

function applyAffine(m: Affine, x: number, y: number): Point {
  return { x: m.a * x + m.c * y + m.e, y: m.b * x + m.d * y + m.f };
}

/** The inverse of a canvas transform, worked out rather than asked for. */
function invertAffine(m: Affine): Affine {
  const det = m.a * m.d - m.b * m.c;
  return {
    a: m.d / det, b: -m.b / det, c: -m.c / det, d: m.a / det,
    e: (m.c * m.f - m.d * m.e) / det, f: (m.b * m.e - m.a * m.f) / det,
  };
}

/**
 * How a weapon that takes both hands is carried, for the ones that are not
 * simply held out at arm's length: `turn`, the angle the whole thing lies
 * at, and where on the body the point it turns about sits — `out` back from
 * the centre line towards the near hip, and `up` above the feet. Every
 * weapon is drawn upright, point up, and turns about its guard or its grip,
 * so `turn` is how far off vertical it ends up and `out` goes negative when
 * that point sits past the centre line rather than short of it.
 *
 * It is one table because the carry has to be one carry. The same entry
 * places the weapon in front of the body and behind it, so nothing about
 * it changes as somebody turns around: what you see walking away from you
 * is the end of the same thing you saw them holding.
 */
const CARRY: Record<number, { out: number; up: number; turn: number }> = {
  // The axe is carried level: haft out past one side of the body, head out
  // past the other, which is what says it is a long thing being carried
  // rather than a shape stuck to a shoulder.
  6: { out: 2, up: 21, turn: 1.35 },
  // The giant sword is held rather than laid across anything: the grip
  // across the chest between the two hands, and the blade up and away over
  // the shoulder at about fifty-five degrees off the horizontal, which is
  // what 0.45 comes to once the lean every weapon is drawn with is added
  // to it.
  //
  // `up` is the height the hands are at. `out` is negative because the
  // guard is not where the hands are: the blade leans one way off the
  // centre line and the grip below it leans the other, so the point the
  // sword turns about ends up past the centre rather than short of it,
  // with the hands halfway down the grip from there.
  //
  // Both numbers grew with GIANT_SWORD_SCALE, and had to. The sword is
  // drawn half again as big about the point it turns on, so everything
  // measured from that point is half again as far away from it: the grip
  // hangs lower, the blade stands taller, and the edge that faces the head
  // lies half again as far off the axis between them.
  //
  // `up` is twenty-two rather than twenty-one because the middle of the
  // longer grip has to land at the height the hands are; a guard left where
  // it was would have put the hands a finger's width up the blade. `out` is
  // nine rather than five because that wider edge no longer passed outside
  // the head at five — it took a bite out of the side of the face, which is
  // the same thing that put it there in the first place, and a cyclops's
  // head is bigger than yours and wanted the extra again.
  43: { out: -9, up: 22, turn: 0.45 },
  // Andúril is carried as the giant sword is (2026-10-08: "anduril needs
  // to be much larger, it's now two-handed"): the grip across the chest in
  // both hands and the blade up over the shoulder. It is a smaller sword
  // and stands a little straighter, so the guard sits nearer the middle.
  77: { out: -6, up: 21, turn: 0.4 },
  // Orcrist is carried as Andúril is (2026-10-17: "orcrist looks too small
  // to be 2 handed"): the same length of sword in the same two hands.
  110: { out: -6, up: 21, turn: 0.4 },
  // The spear is held up the way the swords are (2026-10-10: "Hold the
  // spear and staff higher up so the position of the arms is similar to
  // other weapons"): four higher than the diagonal everything else with no
  // entry here lies at, and in towards the middle, so the butt ends at the
  // belt and the hands are where they are on the giant sword's grip.
  40: { out: -4, up: 16, turn: 0.6 },
  // The Uruk-hai pike (2026-10-18) was held as the spear is, until it
  // was stood up nearer upright and made longer (2026-10-26: "The pike
  // should be held more upright and longer"): a pike is grounded and held
  // standing, not levelled like a spear, so it leans only a little out
  // from the hip and its point stands well over the head (see PIKE_RISE).
  170: { out: -4, up: 16, turn: 0.2 },
  // The black rod is the same length as the spear and is held as it is
  // (2026-10-10: "The black rod and the spear are not held consistently").
  113: { out: -4, up: 16, turn: 0.6 },
  // And the Blue Wizard's staff as the rod is (2026-11-01).
  195: { out: -4, up: 16, turn: 0.6 },
};

/**
 * The carries a hobbit uses in place of CARRY's (2026-10-12: "Show hobbits
 * carrying giant sword dragging it on the ground"). The giant sword is as
 * long as a person is tall, so on a hobbit it is longer than the hobbit:
 * held up over the shoulder it read as a hobbit holding a man's sword, and
 * what a hobbit can actually do with a cyclops's blade is haul it. So the
 * grip is held low across the hips in both hands and the blade slopes down
 * and back from it to the ground, with its point resting there beside the
 * feet. The blade goes out the side it stands over in the ordinary carry,
 * so it trails behind in profile, and the one entry places it in front of
 * the body and behind it, as CARRY's do.
 *
 * `out` and `up` are in the hobbit's own frame, before its build shrinks
 * the figure about the feet, so the ground is where it is for everybody
 * and the grip is where the hobbit's hanging arms end. The blade is not
 * shrunk with the hobbit (2026-11-02: "don't scale items when carried by
 * dwarves or hobbits"), so it is the sword at its full held size, and the
 * turn lays it a little under the level, the slope that brings a blade
 * that long down from the hands to the soles of the feet: the edge of the
 * point is on the ground and not past the bottom or the side of the
 * picture, where it would be cut off square.
 */
const DRAGGED: Record<number, { out: number; up: number; turn: number }> = {
  43: { out: -1, up: 14, turn: 1.62 },
};

/**
 * The giant sword, which is the one weapon in the world drawn to a size of
 * its own rather than to the size of the hand holding it.
 *
 * Everything else held is drawn for a person and then scaled by whatever is
 * holding it, so a cyclops's axe is a cyclops's axe and a kobold's club is a
 * kobold's club. That is right for a kind's own gear and wrong for this,
 * because this is not the cyclops's gear: it is a thing the cyclops carries
 * and you can pick up off it, and a sword that shrank by a third when it
 * changed hands would be a different sword. It is also the biggest thing
 * anybody swings — as long as you are tall, and made for a hand twice the
 * size of yours — and at a person's scale it read as an ordinary sword with
 * a long handle.
 *
 * The size is stated here, and there are two of them, because the two pairs
 * of hands are not the same pair. A cyclops is half again the size of you
 * and the sword was forged for it; in its fist the blade is the full
 * GIANT_SWORD_SCALE, which is as far as it can go and still be a sword
 * somebody is carrying rather than scenery they are standing next to —
 * past that the point leaves the tile above as well as the two either side.
 *
 * In a person's hands it is drawn at GIANT_SWORD_SCALE_HELD, which is
 * exactly halfway between the size an ordinary weapon is drawn at and the
 * size the cyclops swings. Something forged for a bigger hand should look
 * like a burden in yours — plainly the largest thing anybody carries, and
 * plainly not the thing the cyclops is carrying either. At the cyclops's
 * own size it stopped reading as a person holding a sword and started
 * reading as a person standing in front of one.
 *
 * Halfway is a number rather than a taste: one is what every other weapon
 * is drawn at, and the midpoint of that and the cyclops's is the one size
 * that is as much bigger than a sword as it is smaller than the cyclops's.
 */
const GIANT_SWORD = 43;
const GIANT_SWORD_SCALE = 1.5;
const GIANT_SWORD_SCALE_HELD = (1 + GIANT_SWORD_SCALE) / 2;

/**
 * How much bigger a bow in somebody's hands is than it was first drawn.
 * Asked for on 2026-09-24: "make the bow a bit bigger". A fifth is a bit:
 * enough to see the difference, and not so much that the upper limb
 * climbs past the top of the head of whoever is holding it.
 */
const BOW_SCALE = 1.2;

/**
 * The elven longbow is taller than the bow again (2026-09-26, "Implement
 * them all"): a longbow that stood no higher than a hunting bow in the
 * hand would be a hunting bow in a different colour.
 */
const LONGBOW_SCALE = 1.35;

/**
 * Draw a carried weapon into the pose the caller has already turned the
 * canvas into — the giant sword at whichever of its two sizes the carrier
 * swings, everything else at the size it was drawn for. Both carries go
 * through here, so front and back cannot come out at different sizes, and
 * so does a monster's, so the only thing that can make the sword bigger is
 * being the cyclops rather than being drawn down a different path.
 */
function drawCarried(ctx: Ctx, id: number, out: number, big = false) {
  fullSize(ctx);
  const k = id === GIANT_SWORD ? (big ? GIANT_SWORD_SCALE : GIANT_SWORD_SCALE_HELD) : id === ANDURIL_ID || id === ORCRIST_ID ? ANDURIL_SCALE_HELD : 1;
  if (k !== 1) ctx.scale(k, k);
  drawWeapon(ctx, id, 0, 0, out);
}

/**
 * Andúril in the hands, a fifth as big again as it was drawn (2026-10-08:
 * "anduril needs to be much larger, it's now two-handed"): with its longer
 * grip and blade that is about half as long again as it was in one hand,
 * and still a little short of the giant sword in yours, which is the
 * bigger and cruder thing.
 */
const ANDURIL_ID = 77;
const ANDURIL_SCALE_HELD = 1.15;
/** Orcrist, held at Andúril's size since it took both hands the same way. */
const ORCRIST_ID = 110;

/**
 * How big a carrier draws what it is holding. A kind's own scale is right
 * for everything but the giant sword, which has one of its own: laying the
 * cyclops's on top of it would make the sword on the cyclops half again as
 * big as the same sword in your hands.
 */
function carriedScale(id: number, carrier: number): number {
  return id === GIANT_SWORD ? 1 : carrier;
}

/**
 * The same weapon slung on the back: what there is to see of somebody
 * walking away from you. It is drawn before the body, so the body covers
 * the middle of it, and it is placed so the head stands clear of the
 * shoulder — an axe carried across the chest is a head at chest height,
 * which from behind is a head hidden by a back.
 *
 * The haft is given extra length below the grip. None of it may ever show,
 * since the body is over it, but a head at one shoulder with nothing under
 * it reads as an axe floating there rather than as one being carried, and
 * the couple of pixels between the head and the shoulder are what say which.
 *
 * Only weapons whose back carry differs from their front one are in here.
 * Anything in CARRY with no entry here is slung at its carry exactly, which
 * is how the giant sword keeps one shape through all four facings.
 */
const SLUNG: Record<number, { x: number; y: number; turn: number }> = {
  // The axe rides a little higher on the back than across the chest: the
  // back is the whole of somebody, where the chest is half of it, and the
  // head needs the extra to stand clear of the shoulder.
  6: { x: 16, y: 25, turn: 1.35 },
  // A bow is carried the way a bow is carried, upright over the shoulder:
  // laid level it would be a stave three tiles wide.
  //
  // It sits as high on the back as it does across the chest. It used to
  // be hung seven pixels higher than that, which from behind put the top
  // of it well over the head; asked for on 2026-09-24: "when walking north
  // keep the approximate position, it currently sticks up a lot more".
  // Hung that much lower, the bottom of the bow reaches down past the hem
  // of the tunic, so it stands two pixels further right than it did, where
  // the leg on that side hides the lower nock rather than leaving it
  // showing in the gap between the two legs.
  21: { x: 20, y: 11, turn: 0.5 },
  // A spear goes over the shoulder too, and stands a little straighter:
  // it is longer than the bow and would otherwise sweep the whole tile.
  40: { x: 18, y: 16, turn: 0.4 },
  // The black rod over the shoulder as the spear is, rather than laid
  // level across the back at the carry everything without an entry gets.
  113: { x: 18, y: 16, turn: 0.4 },
  195: { x: 18, y: 16, turn: 0.4 }, // the Blue Wizard's staff, as the rod (2026-11-01)
  // The elven longbow over the shoulder the way the bow is, and the
  // geomancer's staff the way the spear is (2026-09-26, "Implement them
  // all").
  62: { x: 20, y: 11, turn: 0.5 },
  66: { x: 18, y: 16, turn: 0.4 },
  // No crossbow here: walking away it is held out ahead rather than slung
  // (2026-09-28, see drawCrossbowAimed).
};

/** The crossbow's item id (2026-09-27): see drawCrossbowAimed. */
const CROSSBOW_ID = 69;

/**
 * The crossbow in somebody's hands, which is the one two-handed thing that
 * is aimed rather than carried: in profile it is held level at the shoulder
 * and points the way they face, the prod out in front — the way the bow is
 * led in the facing direction rather than laid across the body. (cx, cy)
 * is where drawCrossed is handed a weapon, thirteen above the feet on a
 * player.
 *
 * Facing north or south it is held out at an angle, asked for on
 * 2026-09-28: "when walking north and south, have the player hold the
 * crossbow out at an angle". From the front it used to point straight at
 * you, which showed nothing but the prod square across the chest, and from
 * behind it was slung over the shoulder like the bow. Now the stock is at
 * the weapon hand and the crossbow leans out diagonally, an eighth of a
 * turn off the vertical, with the prod leading the way they face: down and
 * out on the viewer's left coming towards you, up and out past the right
 * shoulder going away (`away`), where it is ahead of a body that hides the
 * stock and leaves the prod showing. It is the same drawing at the same
 * size from the same grip both ways, so it does not change as they turn,
 * in every frame of the walk.
 */
function drawCrossbowAimed(ctx: Ctx, cx: number, cy: number, aim: number, away = false) {
  if (aim === 0) {
    // The weapon hand's side: the viewer's left from the front, their
    // right from behind, as for everything held (see drawPlayer).
    const side = away ? 1 : -1;
    ctx.save();
    ctx.translate(cx + side * 6, cy - 9);
    fullSize(ctx); // its own size in a small hand (see fullSize)
    // The upright drawing's nose turned an eighth of a turn out from
    // straight up going away, and three eighths round from it (an eighth
    // off straight down) coming towards you. `side` as the drawing's `out`
    // hangs the lever under the stock both ways.
    ctx.rotate(away ? Math.PI / 4 : (-3 * Math.PI) / 4);
    drawWeapon(ctx, CROSSBOW_ID, 0, 0, side);
    ctx.restore();
    return;
  }
  // In profile: the upright drawing turned a quarter over about the grip,
  // so the nose points along `aim`.
  ctx.save();
  ctx.translate(cx + aim * 3, cy - 11);
  fullSize(ctx);
  ctx.rotate(aim * Math.PI / 2);
  drawWeapon(ctx, CROSSBOW_ID, 0, 0, aim);
  ctx.restore();
}

/** The fishing rod's item id: see drawRod. */
const FISHING_ROD_ID = 24;

/**
 * The fishing rod's colours in the hand. The rod is a dark edge with a
 * bright cane core down the middle of it, because no one colour can do
 * both things it has to: every race's skin is a pale warm tan and the
 * grass and the water it is held over are dark, so a colour light enough
 * to show against the ground is lost against the hands and one dark
 * enough to show against the hands is lost against the water. The edge
 * stands out from any skin (the way the elven longbow's darker stave
 * does, 2026-10-01) and the golden cane from the grass and the water,
 * and the sprite's own outline does the rest. The float is red over
 * white, the way a float is painted to be seen from the bank.
 */
const ROD = {
  edge: "#24160a",
  cane: "#ffd84a",
  grip: "#5b2f1a",
  reel: "#c9ced3",
  line: "#f2f2f2",
  float: "#e02828",
  floatLow: "#ffffff",
};

/**
 * How long the rod is from the hands to the tip, in pixels: long enough
 * that the tip stands ten pixels over the top of the head, half a tile
 * over the top of the tile the player stands on.
 */
const ROD_LENGTH = 41;
/** How far the line hangs below the tip before the float. */
const ROD_LINE = 12;

/**
 * The height of the box a player holding the fishing rod is drawn in. The
 * player's own box has six pixels over the top of the standard space and
 * the rod's tip, upright from the front, is about six over it, a pixel
 * more on the up-swing of the walk; then half the rod's width for its
 * rounded end and a pixel for the outline. Eleven covers all of that, so
 * the tip is never cut square at the edge of the picture.
 */
const ROD_BOX_H = TILE + 16 + 11;

/**
 * The fishing rod held up in both hands at (hx, hy), standing nearly
 * straight and leaning `lean` radians off upright (positive to the
 * viewer's right), with the line hanging straight down from the tip and a
 * float on the end of it. Asked for on 2026-10-01: "make the fishing rod
 * more erect and obvious when wielded". It used to be laid across the body
 * at a diagonal like a bow, a thin pale stroke the colour of the hands
 * holding it that never came up past the shoulders.
 *
 * It is worked out point by point rather than drawn upright and turned,
 * because the line hangs from the tip and a line hangs straight down
 * whatever angle the rod is at.
 */
function drawRod(ctx: Ctx, hx: number, hy: number, lean: number) {
  const ux = Math.sin(lean), uy = -Math.cos(lean); // up the rod
  const along = (d: number) => [hx + ux * d, hy + uy * d] as const;
  // The tip bows a little the way it leans, under the line's weight.
  const bow = Math.sign(lean) * 1.5;
  const [bx, by] = along(-4); // the butt, a hand's width under the hands
  markGrip(ctx, ...along(-2)); // the hands, one over the other on the grip
  markGrip(ctx, ...along(3));
  const [gx, gy] = along(5); // the top of the grip
  const [kx, ky] = along(ROD_LENGTH * 0.75);
  const [ex, ey] = along(ROD_LENGTH);
  const tx = ex + Math.cos(lean) * bow, ty = ey + Math.abs(Math.sin(lean)) * bow;
  ctx.save();
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  // The dark edge, butt to tip.
  ctx.strokeStyle = ROD.edge;
  ctx.lineWidth = 3.2;
  ctx.beginPath();
  ctx.moveTo(bx, by);
  ctx.lineTo(gx, gy);
  ctx.quadraticCurveTo(kx, ky, tx, ty);
  ctx.stroke();
  // The grip, thicker, where the hands are.
  ctx.strokeStyle = ROD.grip;
  ctx.lineWidth = 4.4;
  ctx.beginPath();
  ctx.moveTo(bx, by);
  ctx.lineTo(gx, gy);
  ctx.stroke();
  // The cane, bright down the middle from the grip to the tip.
  ctx.strokeStyle = ROD.cane;
  ctx.lineWidth = 1.4;
  ctx.beginPath();
  ctx.moveTo(gx, gy);
  ctx.quadraticCurveTo(kx, ky, tx, ty);
  ctx.stroke();
  // The reel, under the rod just above the hands, on the side away from
  // the lean.
  const side = lean < 0 ? 1 : -1;
  const [rx, ry] = along(1);
  ctx.fillStyle = ROD.edge;
  ctx.beginPath();
  ctx.arc(rx + side * 2.6 * Math.cos(lean), ry + side * 2.6 * Math.sin(lean), 2.3, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = ROD.reel;
  ctx.beginPath();
  ctx.arc(rx + side * 2.6 * Math.cos(lean), ry + side * 2.6 * Math.sin(lean), 1.3, 0, Math.PI * 2);
  ctx.fill();
  // The line, straight down from the tip, and the float on the end of it.
  ctx.strokeStyle = ROD.line;
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(tx, ty);
  ctx.lineTo(tx, ty + ROD_LINE);
  ctx.stroke();
  const fy = ty + ROD_LINE + 1.8;
  ctx.fillStyle = ROD.floatLow;
  ctx.beginPath();
  ctx.arc(tx, fy, 2.2, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = ROD.float;
  ctx.beginPath();
  ctx.arc(tx, fy, 2.2, Math.PI, Math.PI * 2);
  ctx.fill();
  ctx.restore();
}

/**
 * The fishing rod in somebody's hands, in each facing. (cx, cy) is where
 * drawCrossed is handed a weapon, thirteen above the feet on a player; the
 * hands are a pixel above that. In profile it is held just ahead of the
 * body and leans out the way they face, over the water. From the front
 * and from behind it stands up on the weapon hand's side, leaning a
 * little out past the shoulder so it clears the head, and from behind
 * (`away`) it is drawn before the body, which hides its butt.
 */
function drawRodHeld(ctx: Ctx, cx: number, cy: number, aim: number, away = false) {
  if (aim !== 0) {
    drawRod(ctx, cx + aim * 3, cy - 1, aim * 0.34);
    return;
  }
  // The weapon hand's side: the viewer's left from the front, their
  // right from behind, as for everything held (see drawPlayer).
  const side = away ? 1 : -1;
  drawRod(ctx, cx + side * 5, cy - 1, side * 0.24);
}

/**
 * The two-handed things that are held upright in front exactly as they are
 * slung on the back, rather than laid lower across the body: the same
 * height, the same place over the right shoulder and the same lean in
 * every facing. Asked for on 2026-09-27: "Raise the height of the
 * geomancer staff for all orientations to match the height and position
 * it's currently shown when walking north". It lay across the chest from
 * every other side, its lit stone down by the shoulder rather than up over
 * the head where a staff is carried.
 */
const STANDS: Record<number, true> = { 66: true };

/** The two-handers that are not mirrored: see mirrored. */
const AIMED_TWO_HANDERS = new Set([21, 62, CROSSBOW_ID, FISHING_ROD_ID]);

function drawSlung(ctx: Ctx, id: number, H: number, drag = false, big = false) {
  // The crossbow is never slung: walking away it is held out ahead, at an
  // angle (2026-09-28: "when walking north and south, have the player hold
  // the crossbow out at an angle"). It stays on this path, which is drawn
  // before the body, because ahead of somebody walking away is behind them
  // in the picture. H - 13 is where drawCrossed is handed it.
  if (id === CROSSBOW_ID) {
    drawCrossbowAimed(ctx, 16, H - 13, 0, true);
    return;
  }
  // Nor is the fishing rod: it is held up ahead (2026-10-01: "make the
  // fishing rod more erect and obvious when wielded"), see drawRodHeld.
  if (id === FISHING_ROD_ID) {
    ctx.save();
    fullSize(ctx, 16, H - 14);
    drawRodHeld(ctx, 16, H - 13, 0, true);
    ctx.restore();
    return;
  }
  // A dragged sword is low behind the body, not slung over it: see DRAGGED.
  const dragged = drag ? DRAGGED[id] : undefined;
  const carry = dragged ?? CARRY[id];
  const at =
    (dragged ? undefined : SLUNG[id]) ??
    (carry ? { x: 16 - carry.out, y: carry.up, turn: carry.turn } : { x: 16, y: 25, turn: 1.35 });
  ctx.save();
  if (mirrored(id)) mirrorAbout(ctx, 16);
  ctx.translate(at.x, H - at.y);
  ctx.rotate(at.turn);
  drawCarried(ctx, id, 1, big);
  ctx.restore();
}

/**
 * A weapon that takes both hands, carried across the body rather than held
 * out at arm's length. Nothing grips it — no sprite draws hands, and
 * everything held simply floats where a hand would be — so what says two
 * hands is the length of the thing and where across the body it lies.
 *
 * The ones with an entry in CARRY are placed from it, which is the same
 * placing the back gets — drawn in front of the body rather than behind
 * it, so the weapon does not change shape as somebody turns around. The
 * rest lie across the chest at a diagonal, which suits a bow or a spear,
 * both of which are drawn nearer upright to begin with.
 */
function drawCrossed(ctx: Ctx, id: number, cx: number, cy: number, lean: number, aim: number, drag = false, big = false) {
  // The crossbow is aimed rather than laid across anybody: see
  // drawCrossbowAimed. `aim` is the facing across the picture: nought
  // from the front, one way or the other in profile. It is the argument
  // before `big` so that the size stays the last thing every call says
  // (see TestTheGiantSwordIsTwoSizesAndTheHeldOneIsHalfway).
  if (id === CROSSBOW_ID) {
    drawCrossbowAimed(ctx, cx, cy, aim);
    return;
  }
  // The fishing rod is held up nearly straight rather than laid across
  // anybody: see drawRodHeld.
  if (id === FISHING_ROD_ID) {
    ctx.save();
    fullSize(ctx, cx, cy - 1);
    drawRodHeld(ctx, cx, cy, aim);
    ctx.restore();
    return;
  }
  const side = lean < 0 ? -1 : 1;
  const carry = (drag ? DRAGGED[id] : undefined) ?? CARRY[id];
  ctx.save();
  if (mirrored(id)) mirrorAbout(ctx, cx);
  if (STANDS[id]) {
    // Stood upright where it stands on the back, in every facing: see
    // STANDS. cy arrives thirteen above the feet, as below.
    const at = SLUNG[id];
    ctx.translate(cx - 16 + at.x, cy + 13 - at.y);
    ctx.rotate(at.turn);
    drawCarried(ctx, id, 1, big);
    ctx.restore();
    return;
  }
  if (carry) {
    // cy arrives thirteen above the feet and `up` is measured from the
    // feet too, so the difference between them is what the weapon drops.
    ctx.translate(cx - side * carry.out, cy + 13 - carry.up);
    ctx.rotate(side * carry.turn);
  } else {
    ctx.translate(cx - side * 7, cy + 1);
    ctx.rotate(side * 0.6);
  }
  drawCarried(ctx, id, side, big);
  ctx.restore();
}

function drawWeapon(ctx: Ctx, id: number, hx: number, hy: number, out: number) {
  ctx.lineCap = "round";
  switch (id) {
    case 4: // club
      ctx.strokeStyle = "#6b4a2a";
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.moveTo(hx, hy + 2);
      ctx.lineTo(hx + 3 * out, hy - 13);
      ctx.stroke();
      ctx.lineWidth = 5;
      ctx.beginPath();
      ctx.moveTo(hx + 2 * out, hy - 9);
      ctx.lineTo(hx + 3 * out, hy - 14);
      ctx.stroke();
      break;
    case 24:
      // fishing rod: held up in both hands, nearly straight and leaning out
      // the way the hand is, with the line hanging from the tip. Asked for
      // on 2026-09-24: "clicking the fishing rod should equip it (2
      // handed)", and stood up on 2026-10-01: "make the fishing rod more
      // erect and obvious when wielded". A rod held two-handed comes in
      // through drawRodHeld; this is the same rod in one hand, for
      // whatever draws it before it knows the rod takes two.
      drawRod(ctx, hx, hy, out * 0.34);
      break;
    case 155: { // pick: up from the fist, the head across the top, its point out the way the hand is
      // A haft as long as a one-handed weapon's and a head square across
      // its top, built on the haft's own axes as the axe's is, so it reads
      // as a pick and not a lump on a stick (2026-10-24: "Fix the pick
      // graphics"). It was a short thin haft with the head bent off one
      // side.
      const ux = 0.2 * out, uy = -0.98;
      drawPick(ctx, (a, b) => [hx + ux * a - uy * out * b, hy + 2 + uy * a + ux * out * b], 17, 2, 0.75);
      break;
    }
    case 204: { // Iron Hills mattock (2026-11-01): the pick drawn longer and heavier, in both hands
      const ux = 0.2 * out, uy = -0.98;
      drawPick(ctx, (a, b) => [hx + ux * a - uy * out * b, hy + 6 + uy * a + ux * out * b], 23, 2.6, 1);
      markGrip(ctx, hx + ux * -2, hy + 6 + uy * -2);
      break;
    }
    case 118: { // golf club: up from the fist, the iron head turned out at the top
      ctx.strokeStyle = GOLF_CLUB.shaft;
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.moveTo(hx, hy + 2);
      ctx.lineTo(hx + 3 * out, hy - 15);
      ctx.stroke();
      ctx.strokeStyle = GOLF_CLUB.grip;
      ctx.lineWidth = 2.5;
      ctx.beginPath();
      ctx.moveTo(hx, hy + 2);
      ctx.lineTo(hx + 1 * out, hy - 3);
      ctx.stroke();
      ctx.fillStyle = GOLF_CLUB.iron;
      ctx.beginPath();
      ctx.moveTo(hx + 2.5 * out, hy - 13);
      ctx.lineTo(hx + 3.5 * out, hy - 16);
      ctx.lineTo(hx + 8 * out, hy - 15);
      ctx.quadraticCurveTo(hx + 8.5 * out, hy - 12.5, hx + 6.5 * out, hy - 12);
      ctx.closePath();
      ctx.fill();
      break;
    }
    case 33: // stick: held like a club, because that is what it is for
      ctx.strokeStyle = "#6b4a2a";
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(hx, hy + 2);
      ctx.lineTo(hx + 2 * out, hy - 14);
      ctx.stroke();
      break;
    case 34: // rock: a lump in the fist, ready to go
      // Sat on the fist, not out past it. Everything else held is long and
      // hangs from the hand, so an offset that reads as a haft leaving the
      // fist reads here as a stone floating in the air beside somebody.
      // Half a rock's width clear of the hand is all it takes to show which
      // hand it is in without letting go of it.
      ctx.fillStyle = "#7d7a74";
      ctx.beginPath();
      ctx.arc(hx + 2 * out, hy - 2, 3, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = "#9a968e"; // the lit facet, as on the ground sprite
      ctx.fillRect(hx + 2 * out - 2, hy - 4, 2, 2);
      break;
    case 78: // boulder: the hill troll's, a head-sized rock on its fist
      // The rock in the fist again, two and a half times over (2026-09-28:
      // "give hill trolls a big boulder (scaled up rock) in one hand"). It
      // sits on top of the hand rather than beside it, held up ready to go,
      // since a stone that size is carried on the palm and not in the grip.
      ctx.fillStyle = "#7d7a74";
      ctx.beginPath();
      ctx.arc(hx + 3 * out, hy - 6, 7.5, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = "#9a968e"; // the lit facet, as on the ground sprite
      ctx.fillRect(hx + 3 * out - 5, hy - 11, 5, 4);
      ctx.fillStyle = "#5e5b56"; // and the shaded one underneath
      ctx.fillRect(hx + 3 * out, hy - 3, 5, 3);
      break;
    case 5: // sword
    case 42: // fire sword: the same shape again, burning
    case 32: { // bright sword: the same shape, lit, on a gold hilt
      // Held, the whole weapon leans away from the body — and leans as one
      // thing. It is drawn upright in a frame tilted by that lean, so the
      // grip, the guard and the blade share an axis; drawing the hilt
      // straight and the blade at an angle put a kink at the guard, which
      // showed most on the bright sword, whose glow traces its edges.
      const lit = id === 32, burning = id === 42;
      ctx.save();
      ctx.translate(hx, hy);
      ctx.rotate(0.16 * out);
      ctx.strokeStyle = lit ? "#c9a227" : burning ? "#3a3330" : "#7a5a2a";
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(-3, -3);
      ctx.lineTo(3, -3); // crossguard
      ctx.moveTo(0, 2);
      ctx.lineTo(0, -3); // grip, with the hand round it
      ctx.stroke();
      if (burning) {
        // The same three passes the ground sprite uses, down the upright
        // blade: red haze, orange, and a pale core.
        for (const [half, color, over] of [[2.6, "#8c1f08", 2], [1.8, "#e0641a", 0], [1.1, "#ffc24a", 0], [0.6, "#fff0c0", 0]] as const) {
          ctx.fillStyle = color;
          bladePath(ctx, 0, -4, 0, -20 - over, half);
          ctx.fill();
        }
      } else if (lit) {
        ctx.fillStyle = BRIGHT_GLOW; // the glow, laid down under the blade
        bladePath(ctx, 0, -4, 0, -21, 2.2);
        ctx.fill();
        ctx.fillStyle = BRIGHT_BLADE;
        bladePath(ctx, 0, -4, 0, -20, 1.2);
        ctx.fill();
      } else {
        ctx.strokeStyle = "#cfd4d8";
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(0, -4);
        ctx.lineTo(0, -18);
        ctx.stroke();
      }
      ctx.restore();
      break;
    }
    // The rare finds' four (2026-09-26, "Implement them all"), each in the
    // colours of its ground sprite.
    case 64: { // spider-fang dagger: short, the fang curving off the fist
      ctx.save();
      ctx.translate(hx, hy);
      ctx.rotate(0.16 * out);
      ctx.strokeStyle = SPIDER_FANG.hilt;
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(0, 2);
      ctx.lineTo(0, -3); // the grip
      ctx.stroke();
      ctx.strokeStyle = SPIDER_FANG.guard;
      ctx.beginPath();
      ctx.moveTo(-2.5, -3);
      ctx.lineTo(2.5, -3);
      ctx.stroke();
      ctx.fillStyle = SPIDER_FANG.fang;
      ctx.beginPath();
      ctx.moveTo(-1.4, -4);
      ctx.quadraticCurveTo(-1, -9, 2.5 * out, -13);
      ctx.quadraticCurveTo(1.5, -8, 1.4, -4);
      ctx.closePath();
      ctx.fill();
      ctx.fillStyle = SPIDER_FANG.venom;
      ctx.beginPath();
      ctx.arc(2.5 * out, -12, 1.1, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
      break;
    }
    case 65: { // Sting: held like a sword, a hand shorter, blue at the edges
      ctx.save();
      ctx.translate(hx, hy);
      ctx.rotate(0.16 * out);
      ctx.strokeStyle = STING.hilt;
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(-2.5, -3);
      ctx.lineTo(2.5, -3); // crossguard
      ctx.moveTo(0, 2);
      ctx.lineTo(0, -3); // grip
      ctx.stroke();
      ctx.fillStyle = STING.glow;
      bladePath(ctx, 0, -4, 0, -17, 1.8);
      ctx.fill();
      ctx.fillStyle = STING.blade;
      bladePath(ctx, 0, -4, 0, -16, 0.9);
      ctx.fill();
      ctx.restore();
      break;
    }
    // The content proposal's five (2026-10-01, "Implement the entire
    // proposal in the background"), each held the way a sword is: upright
    // in a frame tilted by the lean, so grip, guard and blade share an axis.
    case 183: { // Herugrim: held like a sword, its gold guard over the hand
      ctx.save();
      ctx.translate(hx, hy);
      ctx.rotate(0.16 * out);
      ctx.strokeStyle = HERUGRIM.grip;
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(0, 2);
      ctx.lineTo(0, -3);
      ctx.stroke();
      ctx.strokeStyle = HERUGRIM.gold;
      ctx.beginPath();
      ctx.moveTo(-3.5, -3);
      ctx.lineTo(3.5, -3);
      ctx.stroke();
      ctx.fillStyle = HERUGRIM.edge;
      bladePath(ctx, 0, -4, 0, -22, 1.8);
      ctx.fill();
      ctx.fillStyle = HERUGRIM.blade;
      bladePath(ctx, 0, -4, 0, -21, 1);
      ctx.fill();
      ctx.restore();
      break;
    }
    case 79: { // Glamdring: held like a sword, the blue light down its blade
      ctx.save();
      ctx.translate(hx, hy);
      ctx.rotate(0.16 * out);
      ctx.strokeStyle = GLAMDRING.grip;
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(0, 2);
      ctx.lineTo(0, -3);
      ctx.stroke();
      ctx.strokeStyle = GLAMDRING.hilt;
      ctx.beginPath();
      ctx.moveTo(-3.5, -3);
      ctx.lineTo(3.5, -3);
      ctx.stroke();
      ctx.fillStyle = GLAMDRING.glow;
      bladePath(ctx, 0, -4, 0, -22, 2.2);
      ctx.fill();
      ctx.fillStyle = GLAMDRING.blade;
      bladePath(ctx, 0, -4, 0, -21, 1);
      ctx.fill();
      ctx.restore();
      break;
    }
    case 113: { // black rod: held upright, the fire in its head over the hand
      const bx = hx - 2 * out, by = hy + 8;
      const tx = hx + 4 * out, ty = hy - 20;
      ctx.strokeStyle = BLACK_ROD.iron;
      ctx.lineWidth = 2.2;
      ctx.beginPath();
      ctx.moveTo(bx, by);
      ctx.lineTo(tx, ty);
      ctx.stroke();
      ctx.fillStyle = BLACK_ROD.glow;
      ctx.beginPath();
      ctx.arc(tx, ty, 3.2, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = BLACK_ROD.ember;
      ctx.beginPath();
      ctx.arc(tx, ty, 2, 0, Math.PI * 2);
      ctx.fill();
      // Held as the spear is, by the butt and a forearm up from it
      // (2026-10-10: "The black rod and the spear are not held
      // consistently"): the same two places on the body as the spear's.
      markGrip(ctx, bx + (tx - bx) * 0.09, by + (ty - by) * 0.09);
      markGrip(ctx, bx + (tx - bx) * 0.36, by + (ty - by) * 0.36);
      break;
    }
    case 195: { // staff of the Ithryn Luin: held upright as the rod is, blue fire in its head over the hand
      const bx = hx - 2 * out, by = hy + 8;
      const tx = hx + 4 * out, ty = hy - 20;
      ctx.strokeStyle = BLUE_STAFF.wood;
      ctx.lineWidth = 2.2;
      ctx.beginPath();
      ctx.moveTo(bx, by);
      ctx.lineTo(tx, ty);
      ctx.stroke();
      ctx.fillStyle = BLUE_STAFF.glow;
      ctx.beginPath();
      ctx.arc(tx, ty, 3.2, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = BLUE_STAFF.fire;
      ctx.beginPath();
      ctx.arc(tx, ty, 2, 0, Math.PI * 2);
      ctx.fill();
      markGrip(ctx, bx + (tx - bx) * 0.09, by + (ty - by) * 0.09);
      markGrip(ctx, bx + (tx - bx) * 0.36, by + (ty - by) * 0.36);
      break;
    }
    case 110: { // Orcrist: held in both hands, a broad blade with the blue light down it
      // Two-handed, so carried as Andúril is (2026-10-17: "orcrist looks
      // too small to be 2 handed"): on one axis, a grip long enough for
      // both hands under the guard, at Andúril's size (ANDURIL_SCALE_HELD)
      // and across the body the way it is (see CARRY). Broader in the
      // blade than Andúril, as it is on the ground, with the guard swept
      // down and the grip bound in silver.
      const ux = 0.157 * out, uy = -0.987; // along the blade, toward the point
      const px = 0.987 * out, py = 0.157; // square across it
      const at = (a: number, b: number): [number, number] =>
        [hx + ux * a + px * b, hy + uy * a + py * b];
      ctx.lineCap = "butt";
      ctx.fillStyle = ORCRIST.glow;
      bladePath(ctx, ...at(0, 0), ...at(27, 0), 3.4);
      ctx.fill();
      ctx.fillStyle = ORCRIST.blade;
      bladePath(ctx, ...at(0, 0), ...at(26, 0), 2);
      ctx.fill();
      ctx.strokeStyle = ORCRIST.fuller;
      ctx.lineWidth = 0.7;
      ctx.beginPath();
      ctx.moveTo(...at(1.5, 0));
      ctx.lineTo(...at(19, 0));
      ctx.stroke();
      ctx.strokeStyle = ORCRIST.grip; // the grip, long enough for both hands
      ctx.lineWidth = 2.5;
      ctx.beginPath();
      ctx.moveTo(...at(-1, 0));
      ctx.lineTo(...at(-9, 0));
      ctx.stroke();
      ctx.strokeStyle = ORCRIST.binding;
      ctx.lineWidth = 0.7;
      ctx.beginPath();
      for (const a of [-4.5, -6]) {
        ctx.moveTo(...at(a, -1.3));
        ctx.lineTo(...at(a - 0.7, 1.3));
      }
      ctx.stroke();
      ctx.strokeStyle = ORCRIST.hilt; // the guard, its ends swept down
      ctx.lineWidth = 2.2;
      ctx.lineCap = "round";
      ctx.beginPath();
      ctx.moveTo(...at(-2, -5.5));
      ctx.lineTo(...at(0, -4));
      ctx.lineTo(...at(0, 4));
      ctx.lineTo(...at(-2, 5.5));
      ctx.stroke();
      ctx.fillStyle = ORCRIST.hilt; // the pommel, with a green stone in it
      ctx.beginPath();
      ctx.arc(...at(-10.5, 0), 2.2, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = ORCRIST.gem;
      ctx.beginPath();
      ctx.arc(...at(-10.5, 0), 1.1, 0, Math.PI * 2);
      ctx.fill();
      markGrip(ctx, ...at(-2.5, 0)); // a hand under the guard
      markGrip(ctx, ...at(-7.5, 0)); // and one over the pommel
      break;
    }
    case 80: { // barrow-blade: a hand shorter than a sword, the runes down it
      ctx.save();
      ctx.translate(hx, hy);
      ctx.rotate(0.16 * out);
      ctx.strokeStyle = BARROW_BLADE.hilt;
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(0, 2);
      ctx.lineTo(0, -3);
      ctx.stroke();
      ctx.strokeStyle = BARROW_BLADE.guard;
      ctx.beginPath();
      ctx.moveTo(-2.5, -3);
      ctx.lineTo(2.5, -3);
      ctx.stroke();
      ctx.fillStyle = BARROW_BLADE.blade;
      bladePath(ctx, 0, -4, 0, -17, 1.2);
      ctx.fill();
      ctx.fillStyle = BARROW_BLADE.rune;
      for (const y of [-7, -10, -13]) ctx.fillRect(-0.5, y, 1, 1);
      ctx.restore();
      break;
    }
    case 81: { // Uruk-hai scimitar: short and broad, the black blade curving out
      ctx.save();
      ctx.translate(hx, hy);
      ctx.rotate(0.16 * out);
      ctx.strokeStyle = URUK.grip;
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(0, 2);
      ctx.lineTo(0, -3);
      ctx.moveTo(-3, -3);
      ctx.lineTo(3, -3);
      ctx.stroke();
      ctx.fillStyle = URUK.iron;
      ctx.beginPath();
      ctx.moveTo(-1.5, -4);
      ctx.quadraticCurveTo(-2, -12, 2 * out, -17);
      ctx.quadraticCurveTo(4 * out, -10, 1.8, -4);
      ctx.closePath();
      ctx.fill();
      ctx.strokeStyle = URUK.edge;
      ctx.lineWidth = 0.8;
      ctx.beginPath();
      ctx.moveTo(2 * out, -17);
      ctx.quadraticCurveTo(4 * out, -10, 1.8, -4);
      ctx.stroke();
      ctx.restore();
      break;
    }
    case 82: { // Umbar cutlass: a curved blade over a brass shell guard
      ctx.save();
      ctx.translate(hx, hy);
      ctx.rotate(0.16 * out);
      ctx.strokeStyle = CUTLASS.wrap;
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(0, 2);
      ctx.lineTo(0, -3);
      ctx.stroke();
      ctx.strokeStyle = CUTLASS.guard;
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.arc(0, -1, 3, Math.PI, Math.PI * 2);
      ctx.stroke();
      ctx.fillStyle = CUTLASS.blade;
      ctx.beginPath();
      ctx.moveTo(-1.2, -4);
      ctx.quadraticCurveTo(-1, -12, 3 * out, -18);
      ctx.quadraticCurveTo(2.5 * out, -10, 1.4, -4);
      ctx.closePath();
      ctx.fill();
      ctx.restore();
      break;
    }
    case 83: { // Morgul-blade: a thin pale knife, smoking
      ctx.save();
      ctx.translate(hx, hy);
      ctx.rotate(0.16 * out);
      ctx.strokeStyle = MORGUL.hilt;
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(0, 2);
      ctx.lineTo(0, -3);
      ctx.moveTo(-2.5, -3);
      ctx.lineTo(2.5, -3);
      ctx.stroke();
      ctx.fillStyle = MORGUL.haze;
      bladePath(ctx, 0, -4, 0, -15, 1.8);
      ctx.fill();
      ctx.fillStyle = MORGUL.blade;
      bladePath(ctx, 0, -4, 0, -14, 0.8);
      ctx.fill();
      ctx.restore();
      break;
    }
    case 77: { // Andúril: held in both hands, the blade alight, a hilt of gold
      // A two-hander since 2026-10-08 ("make anduril 2 handed and have 10
      // attack", then "anduril needs to be much larger, it's now
      // two-handed"): built the way the giant sword is, on one axis, with
      // a grip long enough for both hands under the guard, and carried the
      // way it is (see CARRY), at a size of its own (ANDURIL_SCALE_HELD).
      // A narrower blade than the giant sword's, and alight.
      const ux = 0.157 * out, uy = -0.987; // along the blade, toward the point
      const px = 0.987 * out, py = 0.157; // square across it
      const at = (a: number, b: number): [number, number] =>
        [hx + ux * a + px * b, hy + uy * a + py * b];
      ctx.lineCap = "butt";
      ctx.fillStyle = ANDURIL.glow; // the moon's light round the blade
      bladePath(ctx, ...at(0, 0), ...at(27, 0), 2.8);
      ctx.fill();
      ctx.fillStyle = ANDURIL.flame; // the sun's down one edge
      bladePath(ctx, ...at(0, 0.7), ...at(24, 0.7), 1.2);
      ctx.fill();
      ctx.fillStyle = ANDURIL.blade;
      bladePath(ctx, ...at(0, 0), ...at(26, 0), 1.2);
      ctx.fill();
      ctx.strokeStyle = NARSIL.grip; // the grip, long enough for both hands
      ctx.lineWidth = 2.5;
      ctx.beginPath();
      ctx.moveTo(...at(-1, 0));
      ctx.lineTo(...at(-9, 0));
      ctx.stroke();
      ctx.strokeStyle = ANDURIL.hilt; // the guard, its ends turned up
      ctx.lineWidth = 2.2;
      ctx.lineCap = "round";
      ctx.beginPath();
      ctx.moveTo(...at(1.5, -5));
      ctx.lineTo(...at(0, -4));
      ctx.lineTo(...at(0, 4));
      ctx.lineTo(...at(1.5, 5));
      ctx.stroke();
      ctx.fillStyle = ANDURIL.hilt; // the pommel, with a red stone in it
      ctx.beginPath();
      ctx.arc(...at(-10.5, 0), 2.2, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = ANDURIL.gem;
      ctx.beginPath();
      ctx.arc(...at(-10.5, 0), 1, 0, Math.PI * 2);
      ctx.fill();
      markGrip(ctx, ...at(-2.5, 0)); // a hand under the guard
      markGrip(ctx, ...at(-7.5, 0)); // and one over the pommel
      break;
    }
    case 62: {
      // The elven longbow, carried the way the bow is but taller, in dark
      // gold-grained wood that stands out against the hand holding it (see
      // LONGBOW), with its tips turned back.
      const mid = hy - 12;
      const nockTop = mid - 14 * LONGBOW_SCALE;
      const nockBot = mid + 14 * LONGBOW_SCALE;
      ctx.strokeStyle = LONGBOW.wood;
      ctx.lineWidth = 2 * BOW_SCALE;
      ctx.beginPath();
      ctx.moveTo(hx - 2 * out, nockBot + 1);
      ctx.lineTo(hx, nockBot);
      ctx.quadraticCurveTo(hx + 9 * LONGBOW_SCALE * out, mid, hx, nockTop);
      ctx.lineTo(hx - 2 * out, nockTop - 1);
      ctx.stroke();
      ctx.strokeStyle = LONGBOW.string;
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(hx, nockBot);
      ctx.lineTo(hx, nockTop);
      ctx.stroke();
      ctx.fillStyle = LONGBOW.grip;
      ctx.fillRect(hx + 5 * LONGBOW_SCALE * out - 1.5, mid - 2.5, 3, 5);
      markGrip(ctx, hx + 5 * LONGBOW_SCALE * out, mid); // one hand on the grip
      markGrip(ctx, hx, mid + 1); // and one on the string
      break;
    }
    case 66: { // geomancer's staff: held upright, the lit stone over the head
      const bx = hx - 2 * out, by = hy + 8; // the butt, down by the hip
      const tx = hx + 5 * out, ty = hy - 22; // the head
      ctx.strokeStyle = GEO_STAFF.stone;
      ctx.lineWidth = 2.5;
      ctx.beginPath();
      ctx.moveTo(bx, by);
      ctx.lineTo(tx, ty);
      ctx.stroke();
      ctx.strokeStyle = GEO_STAFF.vein;
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(bx + (tx - bx) * 0.55, by + (ty - by) * 0.55);
      ctx.lineTo(bx + (tx - bx) * 0.7, by + (ty - by) * 0.7);
      ctx.stroke();
      ctx.fillStyle = GEO_STAFF.stone;
      ctx.beginPath();
      ctx.arc(tx, ty, 3.5, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = GEO_STAFF.vein;
      ctx.beginPath();
      ctx.arc(tx, ty, 1.7, 0, Math.PI * 2);
      ctx.fill();
      // Both hands low on it, so the staff stands up out of them and the
      // arms come in to the middle of the chest as they do on a sword
      // (2026-10-10: "Hold the spear and staff higher up so the position of
      // the arms is similar to other weapons").
      markGrip(ctx, bx + (tx - bx) * 0.1, by + (ty - by) * 0.1);
      markGrip(ctx, bx + (tx - bx) * 0.3, by + (ty - by) * 0.3);
      break;
    }
    case 40: { // spear: held levelled, butt low and the point out past the head
      const bx = hx - 2 * out, by = hy + 8; // the butt, down by the hip
      const nx = hx + 5 * out, ny = hy - 17; // where the wood ends
      const tx = hx + 7 * out, ty = hy - 25; // the point
      ctx.strokeStyle = "#8a6a46";
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(bx, by);
      ctx.lineTo(nx, ny);
      ctx.stroke();
      ctx.strokeStyle = "#5c6166"; // the binding where steel meets wood
      ctx.lineWidth = 1.2;
      ctx.beginPath();
      ctx.moveTo(nx - 2 * out, ny + 2);
      ctx.lineTo(nx + 2 * out, ny + 1);
      ctx.stroke();
      ctx.fillStyle = "#cfd4d8";
      bladePath(ctx, nx, ny, tx, ty, 2);
      ctx.fill();
      // A hand by the butt and one up the shaft, a forearm's length apart.
      markGrip(ctx, bx + (nx - bx) * 0.1, by + (ny - by) * 0.1);
      markGrip(ctx, bx + (nx - bx) * 0.4, by + (ny - by) * 0.4);
      break;
    }
    case 193: { // mithril spear (2026-10-27): held as the spear is, the leaf longer and bright
      const bx = hx - 2 * out, by = hy + 8;
      const nx = hx + 5 * out, ny = hy - 17;
      const tx = hx + 7.5 * out, ty = hy - 26;
      ctx.strokeStyle = MITHRIL_SPEAR.shaft;
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(bx, by);
      ctx.lineTo(nx, ny);
      ctx.stroke();
      ctx.strokeStyle = MITHRIL.shade;
      ctx.lineWidth = 1.2;
      ctx.beginPath();
      ctx.moveTo(nx - 2 * out, ny + 2);
      ctx.lineTo(nx + 2 * out, ny + 1);
      ctx.stroke();
      ctx.fillStyle = MITHRIL.light;
      bladePath(ctx, nx, ny, tx, ty, 2.2);
      ctx.fill();
      markGrip(ctx, bx + (nx - bx) * 0.1, by + (ny - by) * 0.1);
      markGrip(ctx, bx + (nx - bx) * 0.4, by + (ny - by) * 0.4);
      break;
    }
    case 202: { // Fram's spear (2026-11-01): held as the spear is, its leaf blackened
      const bx = hx - 2 * out, by = hy + 8;
      const nx = hx + 5 * out, ny = hy - 17;
      const tx = hx + 7 * out, ty = hy - 25;
      ctx.strokeStyle = "#8a6a46";
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(bx, by);
      ctx.lineTo(nx, ny);
      ctx.stroke();
      ctx.strokeStyle = "#c9a227";
      ctx.lineWidth = 1.2;
      ctx.beginPath();
      ctx.moveTo(nx - 2 * out, ny + 2);
      ctx.lineTo(nx + 2 * out, ny + 1);
      ctx.stroke();
      ctx.fillStyle = FRAM_SPEAR.blade;
      bladePath(ctx, nx, ny, tx, ty, 2);
      ctx.fill();
      markGrip(ctx, bx + (nx - bx) * 0.1, by + (ny - by) * 0.1);
      markGrip(ctx, bx + (nx - bx) * 0.4, by + (ny - by) * 0.4);
      break;
    }
    case 170: { // Uruk-hai pike: stood nearly upright, a long dark shaft and a narrow point
      // Drawn straight up the carry's axis, so the carry's turn alone is
      // how far it leans (2026-10-26: "held more upright and longer").
      const bx = hx, by = hy + 10; // the butt, down by the knee
      const nx = hx + 0.5 * out, ny = hy - PIKE_RISE + 9; // where the wood ends
      const tx = hx + 0.7 * out, ty = hy - PIKE_RISE; // the point
      ctx.strokeStyle = PIKE_SHAFT;
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(bx, by);
      ctx.lineTo(nx, ny);
      ctx.stroke();
      ctx.strokeStyle = URUK.rim; // the lugs where steel meets wood
      ctx.lineWidth = 1.2;
      ctx.beginPath();
      ctx.moveTo(nx - 2.5 * out, ny + 2);
      ctx.lineTo(nx + 2.5 * out, ny + 1);
      ctx.stroke();
      ctx.fillStyle = URUK.edge;
      bladePath(ctx, nx, ny, tx, ty, 1.4);
      ctx.fill();
      markGrip(ctx, bx + (nx - bx) * 0.1, by + (ny - by) * 0.1);
      markGrip(ctx, bx + (nx - bx) * 0.4, by + (ny - by) * 0.4);
      break;
    }
    case 43: { // giant sword: held in both hands, blade up over the shoulder
      // Drawn upright, point up, like everything else held; where it ends
      // up pointing is the carry's business (see CARRY). What is this
      // weapon's own is the length either side of the guard — a long grip
      // below it for the second hand, and twenty-six of blade above — and
      // that every part of it sits on one axis, or the sword has a kink
      // where the guard is.
      const ux = 0.157 * out, uy = -0.987; // along the blade, toward the point
      const px = 0.987 * out, py = 0.157; // square across it
      const at = (a: number, b: number): [number, number] =>
        [hx + ux * a + px * b, hy + uy * a + py * b];
      ctx.lineCap = "butt";
      ctx.fillStyle = "#6b7178"; // the dull edge
      bladePath(ctx, ...at(0, 0), ...at(26, 0), 3);
      ctx.fill();
      ctx.fillStyle = "#c3cad1"; // and the bright face inside it
      bladePath(ctx, ...at(0, 0), ...at(25, 0), 2);
      ctx.fill();
      ctx.strokeStyle = "#4a3a22";
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.moveTo(...at(0, -5)); // the guard, square across the blade
      ctx.lineTo(...at(0, 5));
      ctx.moveTo(...at(-1, 0)); // the grip, long enough for both hands
      ctx.lineTo(...at(-9, 0));
      ctx.stroke();
      ctx.fillStyle = "#4a3a22";
      ctx.beginPath();
      ctx.arc(...at(-10, 0), 2, 0, Math.PI * 2); // the pommel
      ctx.fill();
      markGrip(ctx, ...at(-2.5, 0)); // a hand under the guard
      markGrip(ctx, ...at(-7.5, 0)); // and one over the pommel
      break;
    }
    case 6: { // axe: a double-bitted battle axe on a long haft
      // The haft runs the whole length of the weapon, butt to head, rather
      // than stopping at the grip: an axe carried across the body shows all
      // of itself, and a haft that ended where the hands are read as a
      // head on a stub. The grip is marked by the binding rather than by
      // where the wood stops.
      const ux = 0.157 * out, uy = -0.987; // along the haft, toward the head
      const px = 0.987 * out, py = 0.157; // square across it
      const along = (a: number, b: number): [number, number] =>
        [hx + ux * a + px * b, hy + uy * a + py * b];
      ctx.strokeStyle = "#6b4a2a";
      ctx.lineWidth = 3;
      ctx.lineCap = "round";
      ctx.beginPath();
      // Twelve below the grip and twenty-one above it: as much haft as
      // fits under a carrier's hip without the butt dragging on the floor
      // when the whole thing is carried across the body.
      ctx.moveTo(...along(-12, 0));
      ctx.lineTo(...along(21, 0));
      ctx.stroke();
      ctx.strokeStyle = "#3b2a17";
      ctx.lineWidth = 4;
      ctx.beginPath(); // leather bound where the hands go
      ctx.moveTo(...along(-1, 0));
      ctx.lineTo(...along(5, 0));
      ctx.stroke();
      markGrip(ctx, ...along(-5, 0)); // a hand down the haft
      markGrip(ctx, ...along(4, 0)); // and one up on the binding
      // The head: a bit either side of the haft, near the top. It is built
      // on the haft's own axes rather than on the canvas's, so the bits sit
      // square on the wood however the whole thing is turned — an axe at an
      // angle with an upright head has a kink in it where the two meet.
      const hc = 15; // how far up the haft the head is centred
      const at = (a: number, b: number): [number, number] => along(hc + a, b);
      for (const side of [1, -1]) {
        ctx.fillStyle = "#b8bec4";
        ctx.beginPath();
        ctx.moveTo(...at(4, 0));
        ctx.quadraticCurveTo(...at(6, side * 8), ...at(0, side * 7));
        ctx.quadraticCurveTo(...at(-6, side * 8), ...at(-4, 0));
        ctx.closePath();
        ctx.fill();
        ctx.strokeStyle = "#eef2f5"; // the ground edge, bright along the curve
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.moveTo(...at(3.5, side * 6.5));
        ctx.quadraticCurveTo(...at(0, side * 8.5), ...at(-3.5, side * 6.5));
        ctx.stroke();
      }
      ctx.fillStyle = "#6b7076"; // the socket the haft passes through
      ctx.beginPath();
      ctx.moveTo(...at(5, 2));
      ctx.lineTo(...at(5, -2));
      ctx.lineTo(...at(-5, -2));
      ctx.lineTo(...at(-5, 2));
      ctx.closePath();
      ctx.fill();
      break;
    }
    case 69: {
      // The crossbow, drawn upright the way every weapon is — nose up, the
      // grip at (hx, hy) — and turned into place by whoever holds it: level
      // and pointing the way they face in profile, out at an angle facing
      // north or south (see drawCrossbowAimed). The stock
      // runs from the butt below the grip to the nose above it, the prod
      // goes square across the nose bowed forward, and the string is drawn
      // back to the nut with a bolt lying in the groove.
      const butt = hy + 7, nose = hy - 15, nut = hy - 6;
      ctx.strokeStyle = CROSSBOW.string;
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(hx - 9, nose + 2);
      ctx.lineTo(hx, nut);
      ctx.lineTo(hx + 9, nose + 2);
      ctx.stroke();
      ctx.strokeStyle = CROSSBOW.stock;
      ctx.lineWidth = 3.5;
      ctx.beginPath();
      ctx.moveTo(hx, butt);
      ctx.lineTo(hx, nose);
      ctx.stroke();
      // The bolt in the groove, its iron head just past the nose.
      ctx.strokeStyle = "#3e2a1a";
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.moveTo(hx + out * 0.5, nut);
      ctx.lineTo(hx + out * 0.5, nose - 1);
      ctx.stroke();
      ctx.fillStyle = "#8d949c";
      ctx.beginPath();
      ctx.moveTo(hx + out * 0.5, nose - 4);
      ctx.lineTo(hx + out * 0.5 - 1.6, nose - 1);
      ctx.lineTo(hx + out * 0.5 + 1.6, nose - 1);
      ctx.closePath();
      ctx.fill();
      // The lever, hanging under the stock behind the grip.
      ctx.strokeStyle = CROSSBOW.iron;
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.moveTo(hx + out * 1.5, hy - 1);
      ctx.lineTo(hx + out * 4, hy + 3);
      ctx.stroke();
      // The prod, bowed forward past the nose, on a dark edge so it reads
      // against grey armour.
      for (const [colour, width] of [[CROSSBOW.iron, 4], [CROSSBOW.prod, 2.5]] as const) {
        ctx.strokeStyle = colour;
        ctx.lineWidth = width;
        ctx.beginPath();
        ctx.moveTo(hx - 9, nose + 2);
        ctx.quadraticCurveTo(hx, nose - 4, hx + 9, nose + 2);
        ctx.stroke();
      }
      markGrip(ctx, hx, hy + 1); // one hand on the grip
      markGrip(ctx, hx, hy - 9); // and one under the stock
      break;
    }
    case 21: {
      // A bow is a big thing, and carried across the body it should read as
      // one: the upper limb stands above the shoulder and the lower one
      // reaches the far side of the waist, so the stave crosses the whole
      // chest rather than sitting on it. The stave bows away from the body
      // and the string stays straight between the nocks.
      //
      // It is BOW_SCALE times the size it was first drawn at, grown about
      // the middle of the stave, where a bow is held, so it reaches further
      // both ways rather than only upwards. Asked for on 2026-09-24: "make
      // the bow a bit bigger".
      const mid = hy - 12;
      const nockTop = mid - 14 * BOW_SCALE;
      const nockBot = mid + 14 * BOW_SCALE;
      ctx.strokeStyle = "#8a5a2a";
      ctx.lineWidth = 2 * BOW_SCALE;
      ctx.beginPath();
      ctx.moveTo(hx, nockBot);
      ctx.quadraticCurveTo(hx + 9 * BOW_SCALE * out, mid, hx, nockTop);
      ctx.stroke();
      ctx.strokeStyle = "#e8e0c8";
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(hx, nockBot);
      ctx.lineTo(hx, nockTop);
      ctx.stroke();
      markGrip(ctx, hx + 4.5 * BOW_SCALE * out, mid); // one hand on the stave
      markGrip(ctx, hx, mid + 1); // and one on the string
      break;
    }
  }
  ctx.lineCap = "butt";
}

const creatureCache = new Map<string, HTMLCanvasElement>();
/**
 * Draws one piece a player wears or holds, with draw, and when the piece
 * is of a make, a ring in the make's colour round the piece itself, before
 * it goes on the figure (2026-10-27: "the outline should be drawn on the
 * original item, then occluded as normal ... draw it onto the shield, then
 * draw the shield onto the player (or behind or whatever is appropriate
 * for the facing)"). The piece is drawn on a sheet of its own in the same
 * place, ringed there, and the sheet laid on the figure where the piece
 * would have gone, so whatever is drawn after it covers its ring as it
 * covers the piece. Until then the ring went round the difference the
 * piece made to the finished figure, which moved with whatever else was
 * near it.
 */
function ringedPiece(ctx: Ctx, id: number, make: string | undefined, draw: (c: Ctx) => void) {
  const edge = id ? makeEdge(make) : undefined;
  const cv = ctx.canvas as HTMLCanvasElement | undefined;
  if (!edge || !cv || typeof ctx.getTransform !== "function") {
    draw(ctx);
    return;
  }
  const w = cv.width, h = cv.height;
  const [piece, pctx] = canvas(w, h);
  pctx.setTransform(ctx.getTransform());
  draw(pctx);
  const [line, lctx] = canvas(w, h);
  lctx.drawImage(piece, 0, 0);
  lctx.globalCompositeOperation = "source-in";
  lctx.fillStyle = edge;
  lctx.fillRect(0, 0, w, h);
  ctx.save();
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1]]) ctx.drawImage(line, dx, dy);
  ctx.drawImage(piece, 0, 0);
  ctx.restore();
}

export const CREATURE_H = TILE + 16;

/**
 * Sprite boxes wider or taller than the standard one. Nothing is drawn to
 * fit inside a tile: the renderer centres whatever it is given on the tile
 * with the feet at its bottom edge, so a big body, a raised shield or a
 * swung axe simply overhangs its neighbours the way it should.
 *
 * Every sprite is still drawn in the same coordinate space — the body on
 * the centre line at x=16, the feet at y=CREATURE_H — and a bigger box is
 * margin around that, added by translating before the drawing starts. So a
 * sprite can be given more room without touching a line of its drawing.
 */
const BIG_OUTFITS: Record<number, { w: number; h: number }> = {
  // The wolves: nose to tail tip is forty-three wide in profile with the
  // brush of the tail, a little over the tile either end, and no taller
  // than an ordinary box. Forty-eight leaves the outline's pixel and a
  // little over; forty-four cut the tip of the tail, and the wolf test
  // measures the drawing so that it cannot creep past its box again.
  11: { w: 48, h: CREATURE_H },
  22: { w: 48, h: CREATURE_H },
  // The content proposal's big ones (2026-10-01: "Implement the entire
  // proposal in the background"). The warg is the wolf drawn a quarter as
  // big again about its feet, so its box is the wolf's scaled the same way;
  // the stone troll is the hill troll's box a little smaller; the huorn is
  // a tree with its crown well over a tile and a half up; the mûmak spans
  // three tiles and a half nose to tail with its tower over two tiles up;
  // Shelob is the giant spider's box half as big again.
  45: { w: 62, h: CREATURE_H + 12 }, // warg
  166: { w: 72, h: CREATURE_H + 18 }, // werewolf: the wolf's box at 1.45
  50: { w: 60, h: CREATURE_H + 12 }, // Uruk-hai: the armed box, and room over the helm's spike
  68: { w: 62, h: CREATURE_H + 16 }, // Black Orc (2026-10-04): the Uruk-hai's box, a size up
  95: { w: 60, h: CREATURE_H + 14 }, // Gundabad orc (2026-10-15): between the Uruk-hai's box and the Black Orc's
  96: { w: 66, h: CREATURE_H + 20 }, // Bolg's bodyguard: the Black Orc's box, a size up
  97: { w: 70, h: CREATURE_H + 24 }, // Bolg: a size up again
  51: { w: 68, h: CREATURE_H + 6 }, // stone troll
  177: { w: 80, h: CREATURE_H + 14 }, // old stone troll (2026-11-01): the stone troll's box, a size up
  179: { w: 112, h: CREATURE_H + 18 }, // Scatha (2026-11-01): the cold-drake drawn SCATHA_SCALE as big, wingless
  180: { w: 66, h: CREATURE_H + 10 }, // the white wolf (2026-11-01): the winter wolf's box, WHITE_WOLF_SCALE over
  52: { w: 56, h: 82 }, // huorn
  87: { w: 104, h: 112 }, // Old Man Willow (2026-10-12): the huorn's frame 1.6 times over, strands and all
  54: { w: 112, h: 104 }, // mûmak
  55: { w: 88, h: 92 }, // Shelob
  // Ungoliant and Sauron (2026-10-26): Shelob's box scaled to her bigger
  // drawing, the Unlight about her feet included; Sauron a man a third as
  // big again, his crown's spikes and his mace's head well over a tile up.
  162: { w: 112, h: 118 }, // Ungoliant
  163: { w: 72, h: CREATURE_H + 34 }, // Sauron
  // The Mirkwood spider (2026-10-27): the giant spider's legs at three
  // quarters of their reach, so a little wider than a tile.
  164: { w: 46, h: CREATURE_H }, // Mirkwood spider
  // The Witch-king (2026-10-04): the Nazgûl a fifth as big again, the
  // points of his crown over the top of the hood, so the armed box a little
  // taller than the Uruk-hai's.
  67: { w: 60, h: CREATURE_H + 16 }, // Witch-king
  // The Wight-lord of Carn Dûm (2026-10-15): the wight a quarter as big
  // again, the points of its iron crown over a tile up.
  89: { w: 60, h: CREATURE_H + 16 }, // Wight-lord
  // The King of the Dead (2026-10-16): the people's frame a fifth as big
  // again, the points of his crown over a tile up.
  131: { w: 60, h: CREATURE_H + 14 }, // King of the Dead
  77: { w: 64, h: CREATURE_H + 10 }, // the Watcher in the Water: arms reaching out over the banks
  84: { w: 48, h: CREATURE_H + 14 }, // a were-worm: its earth a tile across and more, its head well up
  18: { w: 56, h: 64 }, // giant spider
  // The cyclops brute: shoulders a tile wide and a head over a person.
  14: { w: 60, h: 66 },
  // The hill troll (2026-09-28): the troll a fifth as big again, its hands
  // out at the tile's edges and a club a size up in each, so the box is the
  // armed one widened for the club's reach and a little taller.
  38: { w: 72, h: CREATURE_H + 10 },
  // The cave troll (2026-09-28): the troll 1.3 times as big, its hands a
  // little past the tile's edges and a club half as big again in each, so
  // its box is the hill troll's widened and heightened to match.
  40: { w: 80, h: CREATURE_H + 14 }, // cave troll
  // The troll chief of the Ettenmoors (2026-10-16): the troll 1.4 times as
  // big, its club and boulder further out again.
  106: { w: 88, h: CREATURE_H + 18 }, // troll chief
  // Bear: nearly two tiles nose to rump in profile, and it overhangs its
  // own tile in every facing. A quadruped wants width rather than the
  // height a demon wants, so this is barely taller than an ordinary box
  // and half again as wide.
  19: { w: 54, h: 50 },
  // Dragon: wings out, tail sweeping behind and the snout out past the
  // shoulders, which in profile spans nearly three tiles end to end.
  24: { w: 88, h: 72 },
  // Dragon lord: the dragon drawn a third as big again about its feet
  // (DRAGON_LORD_SCALE), so its box is the dragon's scaled the same way
  // and a little over, the crown included.
  37: { w: 116, h: 92 },
  // Smaug (2026-10-27): the dragon drawn SMAUG_SCALE as big about its
  // feet, so the dragon's box scaled the same way.
  165: { w: 140, h: 112 },
  // Cold-drake (2026-10-21): the dragon drawn smaller about its feet
  // (COLD_DRAKE_SCALE), so its box is the dragon's width scaled the same
  // way; it has no wings to stand up over it, so it is no taller than an
  // ordinary box. Without one it was cut to a tile, its head and tail gone
  // whichever way it faced.
  155: { w: 76, h: CREATURE_H + 6 },
  // The fell beast (2026-11-01): its wings up over its head and out past
  // the tile either side, its tail and its beak out further side-on.
  175: { w: 84, h: 72 },
  // Demon: standing upright, so it wants height rather than the dragon's
  // length — horns over the top of the tile to the north and a wingspan
  // half again as wide as the body under it.
  // Since 2026-10-20 its wings are smoke and reach further, and its mane
  // and its sword of fire stand well over the horns.
  26: { w: 80, h: 76 },
  // Fire elemental (2026-09-28): its head's tongue stands a little over a
  // tile and a half, higher on a flicker, and its arms reach out past the
  // tile either side.
  41: { w: 52, h: 66 },
  // The content proposal's people (2026-10-01): Beorn a man drawn a third
  // as big again (BEORN_SCALE), and Treebeard an Ent two people tall with
  // his arms hanging out from his sides. Tom's feather, Éomer's horsetail
  // and Imrahil's swan wings stand a little over a man's head.
  60: { w: 44, h: 62 }, // Beorn
  63: { w: 44, h: 84 }, // Treebeard
  158: { w: 44, h: 84 }, // an Ent (2026-10-21): Treebeard's frame
  159: { w: 44, h: 84 }, // Quickbeam: the same
  59: { w: 36, h: CREATURE_H + 6 }, // Tom Bombadil
  64: { w: 36, h: CREATURE_H + 4 }, // Éomer
  66: { w: 36, h: CREATURE_H + 6 }, // Imrahil
};

/**
 * The box a player gets: room either side for a shield and a weapon, and a
 * little over the head, since an axe carried across the body puts its blade
 * above the shoulder.
 */
const PLAYER_BOX = { w: 60, h: CREATURE_H + 6 };

/**
 * The box a monster with something in its hands gets, which is the player's
 * for the same reason. A weapon is held out from the body — that is what
 * makes it read as held rather than as part of the animal — and a tile is
 * only as wide as the body, so a kobold's club, a troll's stick and an
 * orc's sword all ran off the edge of their own sprite and were cut square.
 * Overhanging a neighbouring tile is what the drawing wants and what the
 * renderer already allows: it places every sprite by its own width.
 */
const ARMED_BOX = { w: 60, h: CREATURE_H + 6 };

/**
 * The room a giant sword needs, which is more than any other carry: it is
 * drawn half again as big as everything else (see GIANT_SWORD_SCALE) and
 * stood on end, so its point ends up `rise` above the hands holding it and
 * more than half a tile out past the shoulder. Neither fits in the box a
 * weapon usually gets, and a point that does not fit is a point cut off
 * square at the edge of the picture — which is what a cyclops's sword was,
 * in all four facings, for as long as the cyclops has carried one.
 *
 * Both numbers follow from the drawing rather than from the screen: how
 * long the blade is, the angle the carry lays it at, and the size it is
 * drawn at. A test works the same two out of those and will not have them
 * fall short.
 *
 * The extra goes to whoever is actually holding one and to nobody else.
 * Growing every sprite's box instead would move every sprite in the world
 * by a pixel or two, for a weapon almost none of them will ever pick up.
 * That is safe to key on the weapon because the weapon is already part of
 * the sprite's cache key, so the box and what is drawn in it cannot come
 * apart.
 */
const GIANT_SWORD_BOX = { w: 68, rise: 32 };

/**
 * How high above the feet the hands of a two-handed carry sit on this body,
 * which is where the box that has to hold the weapon starts measuring from.
 *
 * A player's are at the carry's own height. A monster's carry hangs off its
 * own grip instead: drawCreature hands drawCrossed that grip three above
 * the fist, where a player's arrives thirteen above the feet, so a monster
 * carries the whole thing (grip.y - 16) higher up its body than you carry
 * yours — ten pixels, on a cyclops — and the point rides up with it.
 */
function carryHeight(outfit: number, id: number): number {
  const up = CARRY[id]?.up ?? 0;
  const grip = MONSTER_GRIP[outfit];
  return outfit >= 10 && grip ? up + grip.y - 16 : up;
}

/**
 * The plain dark eye of anybody drawn without eyes of their own: monsters
 * on a person's frame. Every player's eyes were this, at every level, from
 * 2026-10-23 ("set all eyes to just black again for now. we want to use
 * eye color to signal other things instead") until their people's own
 * came in on 2026-10-27 (see HAIR_SPREADS). Being protection zone locked
 * is the one thing that turns them a colour that means something
 * (2026-10-24: "Eyes should be red if you are protection zone locked").
 */
const EYE_PLAIN = "#111111";
/**
 * The eyes of a player who is protection zone locked. It is `#f22` — the red an orc and a
 * demon look out of the dark with, and the red this client already outlines
 * anything that is a fight in — written out at full length so it can be
 * compared against EYE_PLAIN.
 * Reusing that red rather than inventing one is most of the point: the
 * world has one colour that means "this will hurt you", and somebody who
 * has just swung at a stranger has joined it.
 *
 */
const EYE_SKULL = "#ff2222";

/**
 * What somebody who runs the place wears: a gold crown, a white shirt and
 * red trousers.
 *
 * Every piece of it is something no player can be dealt. Colours come off
 * a wheel at one fixed saturation and lightness (see color.go) with the
 * whole red band cut out of it, so no character in the world wears white
 * and none wears red at all — the red is reserved because red is what
 * monsters hit in. An admin is therefore never somebody who happened to be
 * given a similar colour, which is the same trick the skulled eye plays
 * with a red brighter than any player can reach.
 *
 * Three pieces rather than one because one is a coincidence at 32 pixels:
 * a crown alone reads as a yellow smudge from behind, and trousers alone
 * read as trousers. Head, body and legs together read as a uniform from
 * every side, and a white shirt with red trousers is a uniform whatever
 * the crown is doing.
 */
const ADMIN_CROWN = "#f0c419";
const ADMIN_CROWN_SHADE = "#a8850c";
const ADMIN_SHIRT = "#f2f4f7";
const ADMIN_TROUSERS = "#c01c1c";

export function creatureSprite(
  outfit: number,
  dir: number,
  frame: number,
  gear: Gear = NO_GEAR,
  color?: string,
  level = 1,
  locked = false,
  admin = false,
  race?: string,
  hair = 0,
): HTMLCanvasElement {
  // Monsters wear nothing, though some carry a bow, so they share one
  // entry per pose and weapon whatever else the caller passes.
  if (outfit >= 10) {
    // Monsters wear nothing, though some carry a weapon and a shield, and
    // one wears a helm: Bullroarer Took's saucepan (2026-10-10).
    gear = { weapon: gear.weapon, shield: gear.shield, armor: 0, helm: gear.helm, cloak: 0 };
    color = undefined;
  }
  // A made piece a player wears or holds is ringed in its make's colour
  // (2026-10-21: "when wielding an item with a make, can we display that
  // differently too?"; 2026-10-22, for what is worn), the ring drawn round
  // the piece before it goes on the figure: see ringedPiece. The makes are
  // in the key below, since they are in the picture.
  const makes = outfit < 10 ? [gear.wmake, gear.smake, gear.amake, gear.hmake, gear.cmake].map((m) => m ?? "").join(",") : "";
  // The race is in the picture, and so is where the character's name puts
  // them in their race's hair (2026-10-27): see raceLook. A man is the
  // picture a player with no race already is.
  const look = outfit < 10 ? raceLook(race, hair) : RACE_LOOKS.man;
  // A player's eyes are their people's (2026-10-27: "lets fix the hair and
  // eye colors according to tolkien lore"), dark shades of grey, blue and
  // brown, and a protection zone lock burns them red (2026-10-24). Between
  // 2026-10-23 ("set all eyes to just black again for now. we want to use
  // eye color to signal other things instead") and then they were all the
  // plain dark. It goes through this one value rather than through
  // a branch somewhere down in the drawing, and that is deliberate — the
  // cache key below is built out of `eye`, so putting the lock here means
  // gaining or losing one is already a different key and already a redrawn
  // sprite, with nothing to remember. Monsters keep their own eyes whatever
  // is passed, which is the same guard as before rather than a bet that a
  // monster is never locked.
  const eye = outfit < 10 ? (locked ? EYE_SKULL : look.eye) : undefined;
  // Everything the sprite is drawn from is in the key, the helm included:
  // a key that said nothing about it would hand the first player drawn in
  // one their horns and everybody after them the same picture.
  // The hat and the trousers are part of the picture, so they are part of
  // the key. A key that said nothing about them would draw the first
  // character of an outfit and hand everybody after them the same picture
  // — which for this would mean an admin's hat on a stranger, or a player
  // drawn bare-headed because an admin of the same outfit came first.
  // The race and its hair are in it for the same reason, but only a look
  // that differs from a man's, the picture a player with no race already is.
  // The cloak, the light carried and the fillet on the brow too
  // (2026-10-04), for the helm's reason.
  const key = `${outfit}:${dir}:${frame}:${gear.weapon}:${gear.shield}:${gear.armor}:${gear.helm}:${gear.cloak ?? 0}:${gear.belt ?? 0}:${color ?? ""}:${eye ?? ""}:${admin ? "a" : ""}${look === RACE_LOOKS.man ? "" : ":" + look.key}${makes.replace(/,/g, "") ? ":" + makes : ""}`;
  let c = creatureCache.get(key);
  if (!c) {
    let dims =
      BIG_OUTFITS[outfit] ??
      (outfit < 10
        ? PLAYER_BOX
        : (gear.weapon || gear.shield) && MONSTER_GRIP[outfit]
          ? ARMED_BOX
          : { w: TILE, h: CREATURE_H });
    // A giant sword sticks out of any of those. It is only given the room
    // if it is going to be drawn at all, which for a monster means one with
    // somewhere to hold it.
    // Andúril, carried the same way since it took both hands (2026-10-08),
    // is given the same room, which is more than it needs.
    // So is Orcrist, carried as Andúril is.
    if ((gear.weapon === GIANT_SWORD || gear.weapon === ANDURIL_ID || gear.weapon === ORCRIST_ID) && (outfit < 10 || MONSTER_GRIP[outfit])) {
      // A pixel over the rise for the walk: arms swing with the legs, so
      // the whole carry rides half a leg-swing higher in two frames of the
      // three.
      const point = carryHeight(outfit, GIANT_SWORD) + GIANT_SWORD_BOX.rise + 1;
      dims = {
        w: Math.max(dims.w, GIANT_SWORD_BOX.w),
        // Two above the point, not one: the outline pass stamps the
        // silhouette a pixel out in every direction, and the point itself
        // is a taper rather than a square end, so it arrives with a row of
        // half-lit pixels of its own.
        h: Math.max(dims.h, point + 2),
      };
    }
    // A fishing rod stands up over the head (2026-10-01: "make the fishing
    // rod more erect and obvious when wielded"), and its tip goes higher
    // than anything else a player carries but the giant sword: see
    // ROD_BOX_H.
    if (gear.weapon === FISHING_ROD_ID && outfit < 10) dims = { w: dims.w, h: Math.max(dims.h, ROD_BOX_H) };
    // The pike's point stands over the head too (2026-10-26), and on a
    // monster rides up with its grip, as the giant sword's does, and grows
    // with the kind's own scale: a pixel over the rise for the walk and two
    // for the outline and the taper.
    if (gear.weapon === PIKE_ID && (outfit < 10 || MONSTER_GRIP[outfit])) {
      const k = outfit >= 10 ? MONSTER_GRIP[outfit].scale : 1;
      const point = Math.ceil(carryHeight(outfit, PIKE_ID) + PIKE_RISE * k + 3);
      dims = { w: dims.w, h: Math.max(dims.h, point) };
    }
    const [raw, ctx] = canvas(dims.w, dims.h);
    // Centre the standard coordinate space in whatever box was asked for.
    ctx.translate((dims.w - TILE) / 2, dims.h - CREATURE_H);
    drawCreature(ctx, outfit, dir, frame, gear, color, eye, admin, race, hair);
    // Outlined like the items, so a player in steel reads against the
    // castle's flagstones, and the weapon in hand against anything.
    c = outlined(raw, dims.w, dims.h, false);
    creatureCache.set(key, c);
  }
  return c;
}

/**
 * A horse of the Mark (2026-10-12: "Add mounts, they can be acquired from
 * Edoras for a quest"): a bay with a black mane and tail and a white blaze,
 * under a green saddle-cloth edged in gold, the colours of Rohan.
 */
const HORSE_OF_THE_MARK = {
  coat: "#8b5a2b",
  shade: "#6a4220",
  mane: "#2b1c12",
  hoof: "#26201b",
  muzzle: "#4e3221",
  blaze: "#ece4d4",
  eye: "#120c08",
  cloth: "#2f6a3a",
  trim: "#c9a43c",
  saddle: "#5a3418",
};
/** A horse's colours, so a Nazgûl's can be drawn in its own. */
type HorseLook = typeof HORSE_OF_THE_MARK;

/**
 * A Nazgûl's black horse (2026-11-01: "Have Nazgul spawn randomly on
 * steeds, a black horse or winged beast"): black from mane to hoof, no
 * blaze, eyes red as coals, under a black saddle-cloth edged in the dull
 * steel of Minas Morgul.
 */
const BLACK_HORSE: HorseLook = {
  coat: "#2a2630",
  shade: "#141217",
  mane: "#050506",
  hoof: "#3a3640",
  muzzle: "#36323c",
  blaze: "#2a2630",
  eye: "#e0281c",
  cloth: "#0b0a0d",
  trim: "#5a5e66",
  saddle: "#26232a",
};

/** A Nazgûl's legs down its steed's sides: the black of its robe. */
const NAZGUL_LEGS = "#141218";

/** The row of the horse's back the rider sits on, feet at CREATURE_H. */
const SADDLE_Y = 20;

/**
 * The horse in two layers, in the standard space (feet at CREATURE_H):
 * "back" is all of it that goes under the rider, and "front" what goes
 * over them — the rider's own legs down its sides and, coming towards
 * you, its head. Its legs walk with the frame; the rider sits still. It
 * is bigger than a tile, as a horse is bigger than a man: a body as long
 * as the tile is wide, and a back as high as a man's waist and a half.
 */
function drawHorse(ctx: Ctx, dir: number, frame: number, layer: "back" | "front", trousers: string, HORSE: HorseLook = HORSE_OF_THE_MARK) {
  const H = CREATURE_H;
  const step = frame === 1 ? 2 : frame === 2 ? -2 : 0;
  const top = SADDLE_Y;
  const rect = (col: string, x: number, y: number, w: number, h: number) => {
    ctx.fillStyle = col;
    ctx.fillRect(x, y, w, h);
  };
  // A leg from the body down to its hoof, slanted by its swing.
  const leg = (col: string, x: number, swing: number, from = H - 14) => {
    rect(col, x + Math.round(swing / 2), from, 3, H - 7 - from);
    rect(col, x + swing, H - 7, 3, 5);
    rect(HORSE.hoof, x + swing, H - 2, 3, 2);
  };
  // A rider's leg hanging down the horse's side, boot at the bottom.
  const riderLeg = (x: number) => {
    rect(trousers, x, top + 1, 3, 9);
    rect("#2a1c12", x, top + 9, 4, 2);
  };
  // The saddle-cloth down both flanks, seen from in front or behind.
  const flankCloth = () => {
    rect(HORSE.cloth, 5, top - 1, 4, 9);
    rect(HORSE.cloth, 23, top - 1, 4, 9);
    rect(HORSE.trim, 5, top + 8, 4, 1);
    rect(HORSE.trim, 23, top + 8, 4, 1);
    rect(HORSE.saddle, 8, top - 2, 16, 2);
  };
  if (dir === 1 || dir === 3) {
    ctx.save();
    // Drawn facing east; west is the same horse in a mirror.
    if (dir === 3) {
      ctx.translate(TILE, 0);
      ctx.scale(-1, 1);
    }
    if (layer === "back") {
      // The far legs first, in shadow, then the tail, the body, the near
      // legs, the neck and head, and the saddle on top.
      leg(HORSE.shade, 1, -step);
      leg(HORSE.shade, 21, step);
      rect(HORSE.mane, -5, top + 1, 3, 4);
      rect(HORSE.mane, -6, top + 4, 3, 13);
      rect(HORSE.coat, -3, top + 1, 31, 12);
      rect(HORSE.coat, -2, top, 28, 1);
      rect(HORSE.coat, -2, top + 13, 28, 1);
      rect(HORSE.shade, 0, top + 12, 24, 2);
      leg(HORSE.coat, -2, step);
      leg(HORSE.coat, 24, -step);
      // The neck rising forward, and the long head off the top of it.
      rect(HORSE.coat, 22, top - 5, 8, 9);
      rect(HORSE.coat, 25, top - 10, 7, 6);
      rect(HORSE.coat, 28, top - 14, 7, 5);
      rect(HORSE.coat, 29, top - 15, 9, 6);
      rect(HORSE.muzzle, 36, top - 13, 3, 5);
      rect(HORSE.coat, 29, top - 18, 2, 3);
      rect(HORSE.shade, 32, top - 17, 2, 2);
      rect(HORSE.eye, 32, top - 13, 2, 1);
      rect(HORSE.blaze, 34, top - 15, 2, 2);
      rect(HORSE.mane, 21, top - 5, 2, 8);
      rect(HORSE.mane, 23, top - 10, 2, 6);
      rect(HORSE.mane, 26, top - 15, 2, 6);
      rect(HORSE.cloth, 7, top - 1, 13, 9);
      rect(HORSE.trim, 7, top + 8, 13, 1);
      rect(HORSE.saddle, 8, top - 2, 11, 2);
    } else {
      // The near leg, the thigh along the saddle and the shin down the
      // flank to the stirrup.
      rect(trousers, 14, top, 6, 3);
      rect(trousers, 17, top + 2, 3, 8);
      rect("#2a1c12", 17, top + 9, 5, 2);
    }
    ctx.restore();
    return;
  }
  if (layer === "back") {
    if (dir === 2) {
      // Coming towards you: its chest, its forelegs and the hind legs
      // between them.
      leg(HORSE.shade, 12, -step, H - 12);
      leg(HORSE.shade, 17, step, H - 12);
      rect(HORSE.coat, 7, top, 18, 14);
      rect(HORSE.coat, 8, top - 1, 16, 1);
      rect(HORSE.shade, 9, top + 13, 14, 1);
      leg(HORSE.coat, 8, step);
      leg(HORSE.coat, 21, -step);
    } else {
      // Going away: its rump, its hind legs and its tail.
      leg(HORSE.shade, 12, step, H - 12);
      leg(HORSE.shade, 17, -step, H - 12);
      rect(HORSE.coat, 7, top, 18, 14);
      rect(HORSE.coat, 8, top - 1, 16, 1);
      rect(HORSE.shade, 7, top + 2, 2, 11);
      rect(HORSE.shade, 23, top + 2, 2, 11);
      leg(HORSE.coat, 8, -step);
      leg(HORSE.coat, 21, step);
      rect(HORSE.mane, 14, top + 1, 4, 15);
      rect(HORSE.mane, 15, top + 16, 2, 4);
    }
    flankCloth();
    return;
  }
  riderLeg(3);
  riderLeg(26);
  if (dir === 2) {
    // The long face in front of the rider, ears up, a white blaze down it.
    rect(HORSE.coat, 12, top - 3, 8, 17);
    rect(HORSE.coat, 13, top - 4, 6, 1);
    rect(HORSE.muzzle, 12, top + 11, 8, 4);
    rect(HORSE.eye, 14, top + 13, 1, 1);
    rect(HORSE.eye, 17, top + 13, 1, 1);
    rect(HORSE.coat, 11, top - 7, 3, 4);
    rect(HORSE.coat, 18, top - 7, 3, 4);
    rect(HORSE.mane, 14, top - 5, 4, 3);
    rect(HORSE.blaze, 15, top - 1, 2, 11);
    rect(HORSE.eye, 12, top + 1, 1, 2);
    rect(HORSE.eye, 19, top + 1, 1, 2);
  }
}

/**
 * A player on a horse: the figure creatureSprite draws, standing still
 * and lifted onto the saddle, its legs taken off below it and drawn down
 * the horse's sides instead, between the two layers of the horse. The
 * box is the rider's own, taller by the lift, and the feet that stand on
 * the tile are the horse's.
 */
export function riderSprite(
  outfit: number,
  dir: number,
  frame: number,
  gear: Gear = NO_GEAR,
  color?: string,
  level = 1,
  locked = false,
  admin = false,
  race?: string,
  hair = 0,
  steed?: string,
): HTMLCanvasElement {
  const rider = creatureSprite(outfit, dir, 0, gear, color, level, locked, admin, race, hair);
  // A Nazgûl on a fell beast is drawn on its back (see fellRiderSprite).
  if (steed === "fell") return fellRiderSprite(rider, dir, frame);
  // Kept with the rider's picture, which creatureSprite already caches by
  // everything it is drawn from, under the horse's step, and the horse's
  // colours: a black horse is a Nazgûl's.
  const maps = steed === "black" ? blackRiderMaps : riderMaps;
  let byFrame = maps.get(rider);
  if (!byFrame) maps.set(rider, (byFrame = new Map()));
  const cached = byFrame.get(frame);
  if (cached) return cached;
  // Hips on the saddle: they are twelve rows up a man and fewer up the
  // short peoples, who are drawn squashed about their feet.
  const look = raceLook(race);
  const rise = Math.round(CREATURE_H - SADDLE_Y - 12 * look.sy);
  const W = Math.max(rider.width, 60);
  const Hh = rider.height + rise;
  const ox = (W - TILE) / 2;
  const top = Hh - CREATURE_H;
  const layer = (which: "back" | "front") => {
    const [raw, ctx] = canvas(W, Hh);
    ctx.translate(ox, top);
    if (steed === "black") drawHorse(ctx, dir, frame, which, NAZGUL_LEGS, BLACK_HORSE);
    else drawHorse(ctx, dir, frame, which, admin ? ADMIN_TROUSERS : "#3a2a1a");
    return outlined(raw, W, Hh, false);
  };
  const [out, ctx] = canvas(W, Hh);
  ctx.drawImage(layer("back"), 0, 0);
  // The rider lifted by the rise, with what was below the saddle of their
  // legs cleared away: their arms, and whatever is in their hands, still
  // hang over the horse.
  const [lifted, lctx] = canvas(W, Hh);
  lctx.drawImage(rider, (W - rider.width) / 2, 0);
  lctx.clearRect(ox + 8, top + SADDLE_Y + 1, 16, Hh);
  ctx.drawImage(lifted, 0, 0);
  ctx.drawImage(layer("front"), 0, 0);
  byFrame.set(frame, out);
  return out;
}

/** The mounted pictures of each rider's picture, by the horse's step. */
const riderMaps = new WeakMap<HTMLCanvasElement, Map<number, HTMLCanvasElement>>();
/** The same on a Nazgûl's black horse. */
const blackRiderMaps = new WeakMap<HTMLCanvasElement, Map<number, HTMLCanvasElement>>();
/** And on a fell beast. */
const fellRiderMaps = new WeakMap<HTMLCanvasElement, Map<string, HTMLCanvasElement>>();

/**
 * A Nazgûl on its fell beast (2026-11-01; redrawn the same day: "Improve
 * how the Nazgul are drawn while mounted on them"). The beast is drawn as
 * it is on its own (see drawFellBeast), and its rider in the middle of it,
 * where it sits in the order: in the saddle at the root of the neck, over
 * the near wing side-on and under the head when the beast comes towards
 * you. The rider's figure is cut off at the hips, and its robe hangs down
 * the beast's flanks in their place, ragged at the hem, with the reins in
 * its hands side-on.
 */
function fellRiderSprite(rider: HTMLCanvasElement, dir: number, frame: number): HTMLCanvasElement {
  let byKey = fellRiderMaps.get(rider);
  if (!byKey) fellRiderMaps.set(rider, (byKey = new Map()));
  const key = `${dir}:${frame}`;
  const cached = byKey.get(key);
  if (cached) return cached;
  const H = CREATURE_H;
  const box = BIG_OUTFITS[FELL_BEAST_OUTFIT];
  // The rider's hips, in its own standard space: a man's are twelve rows
  // up from his feet. Its picture is centred on that space as the beast's is.
  const hipY = H - 12;
  const rox = (rider.width - TILE) / 2, roy = rider.height - H;
  // Room over the beast's own box for a tall rider's head or crown.
  const seatTop = Math.min(FELL_SEAT.side[1], FELL_SEAT.facing[1], FELL_SEAT.away[1]) + H;
  const over = Math.max(0, roy + hipY - seatTop - (box.h - H) + 2);
  const W = Math.max(box.w, rider.width + 8), Hh = box.h + over;
  const [cut, cctx] = canvas(rider.width, rider.height);
  cctx.drawImage(rider, 0, 0);
  cctx.clearRect(0, roy + hipY + 1, rider.width, rider.height);
  const [raw, ctx] = canvas(W, Hh);
  ctx.translate((W - TILE) / 2, Hh - H);
  const s = dir === 1 ? 1 : dir === 3 ? -1 : 0;
  drawFellBeast(ctx, dir, frame === 1 ? 2 : frame === 2 ? -2 : 0, H, (c, [sx, sy]) => {
    // The robe down the flanks first, then the figure over its top.
    const robe = (x: number, back: number) => {
      c.beginPath();
      c.moveTo(x - 2.5, sy - 1);
      c.lineTo(x + 2.5, sy - 1);
      c.lineTo(x + 2 + back * 0.3, sy + 8);
      c.lineTo(x + 0.6 + back * 0.5, sy + 6.6);
      c.lineTo(x - 0.8 + back * 0.8, sy + 9);
      c.lineTo(x - 2 + back, sy + 7);
      c.lineTo(x - 3 + back * 1.2, sy + 8.4);
      c.closePath();
      c.fillStyle = NAZGUL_LEGS;
      c.fill();
      c.strokeStyle = "#2c2832";
      c.lineWidth = 0.6;
      c.stroke();
    };
    if (s) robe(sx + s * 1.5, -s * 3);
    else for (const dx of [-6.5, 6.5]) robe(sx + dx, 0);
    c.drawImage(cut, sx - 16 - rox, sy - hipY - roy);
    if (s) {
      // The reins, from the hands to the beak.
      c.strokeStyle = FELL_TACK.rein;
      c.lineWidth = 0.8;
      c.beginPath();
      c.moveTo(sx + s * 4, sy - 5);
      c.quadraticCurveTo(sx + s * 9, sy - 2, 16 + s * 19, H - 42);
      c.stroke();
    }
  });
  const out = outlined(raw, W, Hh, false);
  byKey.set(key, out);
  return out;
}


/**
 * How far down its own picture a sprite's topmost painted pixel is: the
 * row of its head, its horns or the point of whatever it is holding up,
 * counted from the top edge of the canvas.
 *
 * A sprite's box is not its body. Everything is drawn in one coordinate
 * space with the feet at the bottom and a fixed height over them, and a
 * box only ever adds margin round that — so a rat and a wolf come back in
 * exactly the same 32x48 canvas as a bare-headed orc, with the rat filling
 * the bottom third of it and the orc nearly all of it. Anything that wants
 * to know where a creature's head actually is therefore cannot ask how
 * tall the canvas is; it has to look at what was painted in it.
 *
 * So: read the pixels. The obvious cheaper alternative is a table of head
 * heights, one number per outfit, and it is the wrong shape for this
 * codebase — every creature here is drawn procedurally by a function that
 * somebody will lengthen a horn or raise a shield in, and a table is a
 * second statement of the same fact that nothing keeps honest. A number
 * measured off the picture is right by construction, including for the
 * combinations nobody would think to put in a table: the same cyclops
 * measures differently with a giant sword on end in its fist.
 *
 * Any pixel at all counts, rather than one over some threshold of opacity.
 * The sprites are hard fills, and every one of them is run through
 * `outlined`, which stamps the silhouette a pixel out in each direction at
 * 0.8 alpha — so the topmost row is a deliberate, solidly visible outline
 * and not a stray fringe off an antialiased arc. A threshold would be a
 * number to defend with nothing to defend it with.
 *
 * Measured once per sprite and remembered against the canvas itself. The
 * sprites are cached, so this is once per distinct picture in the world
 * and never once per frame; keying the memo on the canvas rather than on
 * the sprite's own cache key means the answer cannot be handed to a
 * different picture, and means anything holding a canvas can ask without
 * knowing how it was made.
 */
const inkTopCache = new WeakMap<HTMLCanvasElement, number>();
export function inkTop(spr: HTMLCanvasElement): number {
  const known = inkTopCache.get(spr);
  if (known !== undefined) return known;
  const d = spr.getContext("2d")!.getImageData(0, 0, spr.width, spr.height).data;
  // A sprite with nothing in it answers zero, which is the top of its own
  // box: a caller placing something above the picture then places it above
  // the whole box, which is where everything sat before any of this was
  // measured. Answering the height instead would drop a label to the feet
  // of a creature that is merely mid-redraw.
  let top = 0;
  scan: for (let y = 0; y < spr.height; y++) {
    for (let x = 0; x < spr.width; x++) {
      if (d[(y * spr.width + x) * 4 + 3] > 0) {
        top = y;
        break scan;
      }
    }
  }
  inkTopCache.set(spr, top);
  return top;
}

/**
 * inkTop over the middle `w` columns of the sprite only: the topmost row
 * with any ink directly under a bar `w` wide centred on it, which is where
 * a creature's health bar sits (2026-10-20: "show health bar as close to
 * the top of a creatures head as possible"). A spear tip or a wing beside
 * the head is not under the bar, so it does not push the bar up off the
 * head; anything that is under it does, so the bar never covers the
 * creature. A sprite with nothing under the span answers inkTop.
 */
const inkTopUnderCache = new WeakMap<HTMLCanvasElement, Map<number, number>>();
export function inkTopUnder(spr: HTMLCanvasElement, w: number): number {
  let byW = inkTopUnderCache.get(spr);
  if (!byW) inkTopUnderCache.set(spr, (byW = new Map()));
  const known = byW.get(w);
  if (known !== undefined) return known;
  const x0 = Math.max(0, Math.round((spr.width - w) / 2)), x1 = Math.min(spr.width, x0 + w);
  let top = -1;
  scan: for (let y = 0; y < spr.height; y++) {
    for (let x = x0; x < x1; x++) {
      if (inkAt(spr, x, y)) {
        top = y;
        break scan;
      }
    }
  }
  if (top < 0) top = inkTop(spr);
  byW.set(w, top);
  return top;
}

/**
 * Whether the sprite has ink at (x, y) of its own box, read from its
 * pixels once and kept. The renderer asks it of the tall ones (see
 * render.ts, where a tall creature hides whoever stands behind it), whose
 * crowns and strands are mostly air.
 */
const inkMaskCache = new WeakMap<HTMLCanvasElement, Uint8ClampedArray>();
export function inkAt(spr: HTMLCanvasElement, x: number, y: number): boolean {
  if (x < 0 || y < 0 || x >= spr.width || y >= spr.height) return false;
  let d = inkMaskCache.get(spr);
  if (!d) {
    d = spr.getContext("2d")!.getImageData(0, 0, spr.width, spr.height).data;
    inkMaskCache.set(spr, d);
  }
  return d[(Math.floor(y) * spr.width + Math.floor(x)) * 4 + 3] > 0;
}

/**
 * The mark beside the name of a player with a kill's skull timer running
 * (2026-10-24: "A skull should appear only if you have the timer running
 * from killing a player"): a small white skull, drawn rather
 * than borrowed from the item sprite, which is a whole tile and far too big
 * to sit beside a name. Centred on (x, y) and sized by w, so a nameplate can
 * place it without knowing how it is made.
 */
export function drawSkullMark(ctx: CanvasRenderingContext2D, x: number, y: number, w: number) {
  const u = w / 10; // the drawing below is ten units across
  ctx.save();
  ctx.translate(x, y);
  ctx.scale(u, u);
  // A dark rim first, so the mark reads against pale ground as well as dark.
  ctx.fillStyle = "rgba(0,0,0,0.55)";
  ctx.beginPath();
  ctx.arc(0, -1, 5.2, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillRect(-3.2, 1.2, 6.4, 4);
  ctx.fillStyle = "#f2efe6";
  ctx.beginPath();
  ctx.arc(0, -1, 4.4, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillRect(-2.6, 1.4, 5.2, 3);
  ctx.fillStyle = "#1a1a1a";
  ctx.beginPath(); // two sockets, round rather than square: it is small
  ctx.arc(-1.8, -1.4, 1.5, 0, Math.PI * 2);
  ctx.arc(1.8, -1.4, 1.5, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillRect(-0.5, 0.4, 1, 1.4); // the nose
  ctx.fillRect(-1.6, 2.2, 0.8, 2); // and the teeth
  ctx.fillRect(-0.4, 2.2, 0.8, 2);
  ctx.fillRect(0.8, 2.2, 0.8, 2);
  ctx.restore();
}

/**
 * A fish in the water, seen from above: a small dark shape with a pale
 * belly-flash and a tail, drawn a good deal smaller than the fish you put
 * in your bag. It is under the surface rather than on it, so it is dim and
 * blue-shifted — what you see is a shadow with a glint, which is what a
 * fish in a lake looks like from the bank.
 *
 * The wag is the whole of the animation: the tail swings with the frame, so
 * a still fish still reads as a live one.
 */
const fishCache = new Map<string, HTMLCanvasElement>();

export function fishSprite(dir: number, frame: number): HTMLCanvasElement {
  const key = `${dir}:${frame}`;
  let c = fishCache.get(key);
  if (c) return c;
  const [raw, ctx] = canvas(TILE, TILE);
  const cx = 16, cy = 16;
  // Facing: east (+1) or west (-1); a fish swimming north or south is drawn
  // in profile anyway, since a fish seen end-on is a dot.
  const out = dir === 3 ? -1 : 1;
  const wag = frame === 1 ? 2 : frame === 2 ? -2 : 0;
  ctx.fillStyle = "#20486b";
  ctx.beginPath(); // the body
  ctx.ellipse(cx, cy, 6, 3, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.beginPath(); // the tail, wagging
  ctx.moveTo(cx - out * 5, cy);
  ctx.lineTo(cx - out * 10, cy - 3 + wag);
  ctx.lineTo(cx - out * 10, cy + 3 + wag);
  ctx.closePath();
  ctx.fill();
  ctx.fillStyle = "#3d7aa8"; // the lit back
  ctx.beginPath();
  ctx.ellipse(cx, cy - 1, 5, 1.5, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = "#9fd0ef"; // the belly catching the light
  ctx.beginPath();
  ctx.ellipse(cx + out, cy + 1.5, 3, 1, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = "#0d2338";
  ctx.fillRect(cx + out * 4, cy - 1, 1.2, 1.2); // the eye
  c = raw;
  fishCache.set(key, c);
  return c;
}
